// OptimalAvailability Studio - FINAL PRODUCTION VERSION
// Comprehensive reliability engineering desktop application
// Uses imported CoreComponents and calculation engines

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';

import {
  Play,
  Square,
  ChevronDown,
  ChevronRight,
  Plus,
  Settings,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Trash2,
  Copy,
  Download,
  Upload,
  Home,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  BarChart3,
  DollarSign,
  Info,
  X,
  Eye,
  EyeOff,
  Grid3x3,
  File,
  FolderOpen,
  Save,
  RotateCcw,
  RotateCw,
  BookOpen,
  Filter,
  Layers,
  Package,
  Users,
  Clock,
  Zap,
  Wrench,
  Briefcase,
  Target,
  GitBranch,
  Calendar,
  Shield,
  ShieldCheck,
  Activity,
  Link2,
  RefreshCw,
  Search
} from 'lucide-react';

// Import core components from CoreComponents.jsx
import {
  MenuBar,
  Toolbar,
  TreeSection,
  DataGrid,
  PropertyInspector,
  TopNav,
  SubBar,
  useUndoRedo,
  createDemoData,
  createBlankProject,
  COLORS,
  TREE_SECTIONS,
  TREE_GROUPS,
  COLUMN_DEFINITIONS
} from './components/CoreComponents';

// Import calculation engines
import { SimulationEngine } from './engine/simulation';
import { FaultTreeEngine } from './engine/faultTreeEngine';
import { WeibullAnalysis } from './engine/weibullEngine';
// import { LCCEngine } from './engine/lccEngine'; // Replaced by LifecycleAssessmentView inline calculations
import { ProcessReliabilityEngine } from './engine/processReliability';
import { ALTEngine } from './engine/altEngine';
import { ReportDesigner } from './engine/reportDesigner';
import { ExcelIO } from './engine/excelIO';
import { RCMEngine } from './engine/rcmEngine';
import { OptimalSparesAPI, analyseSpare, calculateROP, classifyCriticality, weibullDemandForecast, DEFAULT_STOCKING_POLICY, getZScore } from './engine/sparesEngine';
import { searchLibrary, loadEquipmentTemplate, applyTemplateToProject, getCategories, getLibraryStats, applySFG20Template } from './engine/failureLibraryEngine';
import { runMonteCarlo, runSensitivity, analyseResilience, generateRAMReportData } from './engine/ramSimulationEngine';
import { OptimalIntegrityAPI, isIntegrityRelated, classifyIntegrityModes, categorizeIntegrityMode, enrichWithIntegrityData, DAMAGE_MECHANISM_MAP } from './engine/integrityEngine';
import { EventTreeEngine } from './engine/eventTreeEngine';
import { CriticalityEngine } from './engine/criticalityEngine';
import { XMLImport } from './engine/xmlImport';
import SAPConnector, { SAPLiveConnector, SAPWriteBack } from './engine/sapConnector';
import MaximoConnector, { MaximoLiveConnector, MaximoWriteBack } from './engine/maximoConnector';
import IntervalOptimization from './engine/intervalOptimization';
import { MarkovEngine } from './engine/markovEngine';
import EnterpriseManager from './engine/enterpriseManager';
import LibraryManager from './engine/libraryManager';
import DocxExport from './engine/docxExport';
import MdbImport from './engine/mdbImport';
import WorkInstructionGenerator from './engine/workInstructions';

// Import recharts for data visualization
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ComposedChart,
  ReferenceLine
} from 'recharts';

// ============================================================================
// RELIABILITY ENGINEERING DATA CONSTANTS
// ============================================================================

const MIL217_BASE_RATES = {
  resistor: { low_stress: 0.0000001, high_stress: 0.0000005 },
  capacitor: { low_stress: 0.0000002, high_stress: 0.0000008 },
  inductor: { low_stress: 0.0000001, high_stress: 0.0000004 },
  ic_digital: { low_stress: 0.00000005, high_stress: 0.0000003 },
  ic_analog: { low_stress: 0.00000008, high_stress: 0.0000004 },
  connector: { low_stress: 0.0000003, high_stress: 0.0000012 },
  relay: { low_stress: 0.000002, high_stress: 0.000008 },
  pcb: { low_stress: 0.0000001, high_stress: 0.0000005 }
};

const PI_FACTORS = {
  temperature: (junction_temp, ref_temp = 25) => {
    const k = 0.000125;
    return Math.exp(k * (junction_temp - ref_temp));
  },
  environment: {
    GF: 1.0,
    GM: 2.0,
    MP: 4.0,
    MFF: 5.0,
    SW: 8.0,
    AIC: 18.0
  },
  quality: {
    JANTXV: 0.25,
    JANTX: 0.5,
    JAN: 1.0,
    industrial: 2.0,
    commercial: 5.0
  },
  stress: (actual_stress, max_rating) => Math.pow(actual_stress / max_rating, 2)
};

const SEVERITY_LEVELS = {
  'Catastrophic': { value: 4, color: '#d32f2f' },
  'Hazardous': { value: 3, color: '#f57c00' },
  'Major': { value: 2, color: '#fbc02d' },
  'Minor': { value: 1, color: '#388e3c' },
  'No Effect': { value: 0, color: '#757575' }
};

// ============================================================================
// RELIABILITY ENGINEERING CALCULATION FUNCTIONS
// ============================================================================

const calculatePartFailureRate = (part) => {
  const base = MIL217_BASE_RATES[part.type]?.high_stress || 0.0000001;
  const pi_temp = PI_FACTORS.temperature(part.temp, 25);
  const pi_env = PI_FACTORS.environment[part.environment] || 1.0;
  const pi_q = PI_FACTORS.quality[part.quality] || 1.0;
  const pi_s = PI_FACTORS.stress(part.stress_ratio, 1.0);

  return base * pi_temp * pi_env * pi_q * pi_s * part.quantity;
};

const calculateAssemblyMetrics = (parts) => {
  const partRates = parts.map(p => ({
    ...p,
    failure_rate: calculatePartFailureRate(p),
    mtbf_hours: 1 / calculatePartFailureRate(p)
  }));

  const total_rate = partRates.reduce((sum, p) => sum + p.failure_rate, 0);
  const system_mtbf = total_rate > 0 ? 1 / total_rate : 999999;

  return { partRates, total_rate, system_mtbf };
};

const calculateDuaneGrowth = (data) => {
  if (data.length < 2) return { growth_rate: 0, current_mtbf: 0 };

  const log_t = data.map(d => Math.log(d.test_time));
  const log_mtbf = data.map(d => Math.log(d.test_time / d.cum_failures));

  const n = data.length;
  const sum_log_t = log_t.reduce((a, b) => a + b, 0);
  const sum_log_mtbf = log_mtbf.reduce((a, b) => a + b, 0);
  const sum_log_t_mtbf = log_t.reduce((sum, lt, i) => sum + lt * log_mtbf[i], 0);
  const sum_log_t2 = log_t.reduce((sum, lt) => sum + lt * lt, 0);

  const alpha = (n * sum_log_t_mtbf - sum_log_t * sum_log_mtbf) / (n * sum_log_t2 - sum_log_t * sum_log_t);
  const ln_a = (sum_log_mtbf - alpha * sum_log_t) / n;
  const a = Math.exp(ln_a);

  const last_data = data[data.length - 1];
  const current_mtbf = a * Math.pow(last_data.test_time, alpha);

  return { growth_rate: alpha, a, current_mtbf };
};

// ============================================================================
// BRAND HELPERS
// ============================================================================
const OptimalTrendBrand = ({ size = 14, color, trendColor }) => (
  <span style={{ fontSize: size + 'px', fontWeight: '700', letterSpacing: '0.3px' }}>
    <span style={{ color: color || '#002F65' }}>Optimal</span>
    <sup style={{ fontSize: Math.round(size * 0.6) + 'px', fontWeight: '800', color: trendColor || color || '#00A8CB', verticalAlign: 'super', letterSpacing: '0.5px' }}>{'\u00ae'}TREND</sup>
  </span>
);

// ============================================================================
// CURRENCY CONFIGURATION
// ============================================================================
const CURRENCIES = {
  USD: { symbol: '$', code: 'USD', name: 'US Dollar', locale: 'en-US', rateToUSD: 1.0 },
  GBP: { symbol: '£', code: 'GBP', name: 'British Pound', locale: 'en-GB', rateToUSD: 1.27 },
  EUR: { symbol: '€', code: 'EUR', name: 'Euro', locale: 'de-DE', rateToUSD: 1.09 },
  ZAR: { symbol: 'R', code: 'ZAR', name: 'South African Rand', locale: 'en-ZA', rateToUSD: 0.054 },
  AED: { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham', locale: 'ar-AE', rateToUSD: 0.272 },
  SAR: { symbol: '﷼', code: 'SAR', name: 'Saudi Riyal', locale: 'ar-SA', rateToUSD: 0.267 },
  AUD: { symbol: 'A$', code: 'AUD', name: 'Australian Dollar', locale: 'en-AU', rateToUSD: 0.65 },
  CAD: { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar', locale: 'en-CA', rateToUSD: 0.73 },
  NOK: { symbol: 'kr', code: 'NOK', name: 'Norwegian Krone', locale: 'nb-NO', rateToUSD: 0.093 },
  SGD: { symbol: 'S$', code: 'SGD', name: 'Singapore Dollar', locale: 'en-SG', rateToUSD: 0.75 }
};

// Convert amount between currencies
function convertCurrency(amount, fromCode, toCode) {
  if (fromCode === toCode) return amount;
  const from = CURRENCIES[fromCode] || CURRENCIES.USD;
  const to = CURRENCIES[toCode] || CURRENCIES.USD;
  const amountInUSD = amount * from.rateToUSD;
  return amountInUSD / to.rateToUSD;
}

function formatCurrency(amount, currencyCode = 'USD', compact = false) {
  const curr = CURRENCIES[currencyCode] || CURRENCIES.USD;
  const num = parseFloat(amount) || 0;
  if (compact) {
    if (Math.abs(num) >= 1000000) return `${curr.symbol}${(num / 1000000).toFixed(2)}M`;
    if (Math.abs(num) >= 1000) return `${curr.symbol}${(num / 1000).toFixed(0)}K`;
  }
  return `${curr.symbol}${num.toLocaleString(curr.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatCurrencyFull(amount, currencyCode = 'USD') {
  const curr = CURRENCIES[currencyCode] || CURRENCIES.USD;
  const num = parseFloat(amount) || 0;
  return `${curr.symbol}${num.toLocaleString(curr.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ============================================================================
// HIERARCHY STANDARDS
// ============================================================================
const HIERARCHY_STANDARDS = {
  iso14224: {
    name: 'ISO 14224',
    levels: [
      { level: 0, label: 'Industry', prefix: 'IND', color: '#1A237E' },
      { level: 1, label: 'Business Category', prefix: 'BUS', color: '#283593' },
      { level: 2, label: 'Installation', prefix: 'INS', color: '#303F9F' },
      { level: 3, label: 'Plant/Unit', prefix: 'PLT', color: '#3949AB' },
      { level: 4, label: 'Section/System', prefix: 'SEC', color: '#3F51B5' },
      { level: 5, label: 'Equipment Unit', prefix: 'EQU', color: '#5C6BC0' },
      { level: 6, label: 'Subunit', prefix: 'SUB', color: '#7986CB' },
      { level: 7, label: 'Maintainable Item', prefix: 'MI', color: '#9FA8DA' },
      { level: 8, label: 'Component/Part', prefix: 'CMP', color: '#C5CAE9' },
    ]
  },
  rdspp: {
    name: 'RDS-PP (Power)',
    levels: [
      { level: 0, label: 'Power Plant', prefix: 'PP', color: '#B71C1C' },
      { level: 1, label: 'Unit/Block', prefix: 'UB', color: '#C62828' },
      { level: 2, label: 'Main System', prefix: 'MS', color: '#D32F2F' },
      { level: 3, label: 'Subsystem', prefix: 'SS', color: '#E53935' },
      { level: 4, label: 'Main Component', prefix: 'MC', color: '#F44336' },
      { level: 5, label: 'Subcomponent', prefix: 'SC', color: '#EF5350' },
      { level: 6, label: 'Part', prefix: 'PT', color: '#E57373' },
    ]
  },
  isa95: {
    name: 'ISA-95',
    levels: [
      { level: 0, label: 'Enterprise', prefix: 'ENT', color: '#1B5E20' },
      { level: 1, label: 'Site', prefix: 'SIT', color: '#2E7D32' },
      { level: 2, label: 'Area', prefix: 'ARE', color: '#388E3C' },
      { level: 3, label: 'Work Centre', prefix: 'WCE', color: '#43A047' },
      { level: 4, label: 'Work Unit', prefix: 'WUN', color: '#4CAF50' },
      { level: 5, label: 'Equipment Module', prefix: 'EQM', color: '#66BB6A' },
      { level: 6, label: 'Control Module', prefix: 'CTL', color: '#81C784' },
    ]
  }
};

// ============================================================================
// MODULE WORKFLOW DEFINITIONS
// Each module defines its sequential workflow steps. Steps reference tab IDs
// from the existing tab system. 'requires' lists step indices that must have
// data before this step unlocks. 'domainGroups' lists which TREE_GROUP ids are
// relevant when this step is active. 'assetTypes' controls show/hide per asset.
// ============================================================================
const MODULE_WORKFLOWS = {
  optimalav: {
    label: 'Reliability & Availability',
    steps: [
      { id: 'grid', label: 'Asset Data', icon: '📋', requires: [], domainGroups: ['assetHierarchy', 'analysis', 'strategy', 'resources'], assetTypes: 'all', rollUp: true },
      { id: 'modelling', label: 'System Model', icon: '🔗', requires: [0], domainGroups: ['modelling'], assetTypes: 'all', rollUp: true },
      { id: 'spares', label: 'Resources', icon: '📦', requires: [0], domainGroups: ['resources'], assetTypes: ['equipments'], rollUp: true },
      { id: 'connectors', label: 'ERP Connectors', icon: '🔌', requires: [], domainGroups: ['assetHierarchy'], assetTypes: 'all', rollUp: false },
      { id: 'markov', label: 'Markov Analysis', icon: '🔀', requires: [0, 1], domainGroups: ['modelling'], assetTypes: ['equipments'], rollUp: false },
      { id: 'results', label: 'Results', icon: '📊', requires: [0, 1], domainGroups: ['analysis', 'strategy'], assetTypes: 'all', rollUp: true },
      { id: 'optimization', label: 'Optimisation', icon: '🎯', requires: [0, 1, 5], domainGroups: ['strategy', 'commercial'], assetTypes: 'all', rollUp: true }
    ]
  },
  weibull: {
    label: 'Weibull Analysis',
    steps: [
      { id: 'grid', label: 'Failure Data', icon: '📋', requires: [], domainGroups: ['assetHierarchy', 'analysis'], assetTypes: 'all', rollUp: true },
      { id: 'weibull', label: 'Distribution Analysis', icon: '📈', requires: [0], domainGroups: ['analysis'], assetTypes: ['equipments'], rollUp: false },
      { id: 'alt', label: 'ALT Testing', icon: '🔬', requires: [0], domainGroups: ['analysis'], assetTypes: ['equipments'], rollUp: false }
    ]
  },
  rcm: {
    label: 'Reliability-Centred Maintenance',
    steps: [
      { id: 'grid', label: 'Asset & Failure Data', icon: '📋', requires: [], domainGroups: ['assetHierarchy', 'analysis'], assetTypes: 'all', rollUp: true },
      { id: 'rcm2', label: 'RCM Decision Logic', icon: '🔧', requires: [0], domainGroups: ['analysis', 'strategy'], assetTypes: ['equipments'], rollUp: false },
      { id: 'heatmap', label: 'Criticality Matrix', icon: '🟥', requires: [0], domainGroups: ['analysis'], assetTypes: 'all', rollUp: true },
      { id: 'optimalrcm', label: 'Analytics & Reporting', icon: '📊', requires: [0, 1], domainGroups: ['analysis', 'strategy'], assetTypes: 'all', rollUp: true }
    ]
  },
  lcc: {
    label: 'Lifecycle Assessment',
    steps: [
      { id: 'lcc', label: 'Lifecycle Assessment', icon: '💰', requires: [], domainGroups: ['assetHierarchy', 'commercial'], assetTypes: 'all', rollUp: true }
    ]
  },
  process: {
    label: 'Process Reliability',
    steps: [
      { id: 'grid', label: 'Process Data', icon: '📋', requires: [], domainGroups: ['assetHierarchy', 'analysis'], assetTypes: 'all', rollUp: true },
      { id: 'process', label: 'Process Analysis', icon: '🏭', requires: [0], domainGroups: ['analysis', 'modelling'], assetTypes: 'all', rollUp: true }
    ]
  },
  reliability: {
    label: 'Reliability Engineering',
    steps: [
      { id: 'prediction', label: 'Prediction (MIL-217F)', icon: '🔮', requires: [], domainGroups: ['assetHierarchy', 'analysis'], assetTypes: ['equipments'], rollUp: false },
      { id: 'fmecaworksheet', label: 'FMECA Worksheet', icon: '📝', requires: [], domainGroups: ['analysis'], assetTypes: ['equipments'], rollUp: false },
      { id: 'growth', label: 'Reliability Growth', icon: '📈', requires: [], domainGroups: ['analysis'], assetTypes: ['equipments'], rollUp: false },
      { id: 'safety', label: 'Safety Assessment', icon: '🛡', requires: [], domainGroups: ['analysis', 'modelling'], assetTypes: 'all', rollUp: true },
      { id: 'allocation', label: 'Allocation', icon: '⚖', requires: [], domainGroups: ['assetHierarchy', 'modelling'], assetTypes: 'all', rollUp: true }
    ]
  },
  enterprise: {
    label: 'Enterprise Manager',
    steps: [
      { id: 'enterprise', label: 'Enterprise Manager', icon: '🌍', requires: [], domainGroups: ['assetHierarchy', 'analysis', 'strategy', 'resources', 'commercial'], assetTypes: 'all', rollUp: true }
    ]
  }
};

// ============================================================================
// RBD EDITOR - RELIABILITY BLOCK DIAGRAM
// ============================================================================
function RBDEditor({
  blocks = [],
  connections = [],
  onUpdateBlock,
  onAddBlock,
  onDeleteBlock,
  onAddConnection,
  onDeleteConnection,
  selectedBlockId,
  onSelectBlock,
  selectedConnectionId,
  onSelectConnection,
  zoom = 1,
  onZoomChange,
  showGrid = false,
  onToggleGrid,
  currentPage = 0
}) {
  const svgRef = useRef(null);
  const [draggingBlock, setDraggingBlock] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [drawingConnection, setDrawingConnection] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const BLOCK_WIDTH = 100;
  const BLOCK_HEIGHT = 54;
  const GRID_SIZE = 20;

  // ===== RBD AUTO-LAYOUT ALGORITHM =====
  // Sugiyama-style layered layout for RBD diagrams
  const autoLayout = useCallback(() => {
    if (blocks.length === 0) return;

    // Build adjacency from connections
    const adj = new Map();
    const inDeg = new Map();
    blocks.forEach(b => { adj.set(b.id, []); inDeg.set(b.id, 0); });
    connections.forEach(c => {
      if (c.from !== 'START' && c.to !== 'END' && adj.has(c.from)) {
        adj.get(c.from).push(c.to);
        inDeg.set(c.to, (inDeg.get(c.to) || 0) + 1);
      }
    });

    // Topological sort to assign layers
    const layers = [];
    const layerMap = new Map();
    const queue = [];
    blocks.forEach(b => { if ((inDeg.get(b.id) || 0) === 0) queue.push(b.id); });

    while (queue.length > 0) {
      const layer = [...queue];
      layers.push(layer);
      layer.forEach(id => layerMap.set(id, layers.length - 1));
      queue.length = 0;
      layer.forEach(id => {
        (adj.get(id) || []).forEach(child => {
          inDeg.set(child, inDeg.get(child) - 1);
          if (inDeg.get(child) === 0) queue.push(child);
        });
      });
    }

    // Assign any unplaced blocks to layer 0
    blocks.forEach(b => {
      if (!layerMap.has(b.id)) {
        if (!layers[0]) layers[0] = [];
        layers[0].push(b.id);
        layerMap.set(b.id, 0);
      }
    });

    // Position blocks: horizontal layers, vertical ordering within layers
    const H_SPACING = 170;
    const V_SPACING = 90;
    const START_X = 100;
    const START_Y = 60;

    layers.forEach((layer, layerIdx) => {
      const layerHeight = layer.length * V_SPACING;
      const startY = START_Y + Math.max(0, (3 * V_SPACING - layerHeight) / 2);
      layer.forEach((blockId, posIdx) => {
        const x = Math.round((START_X + layerIdx * H_SPACING) / GRID_SIZE) * GRID_SIZE;
        const y = Math.round((startY + posIdx * V_SPACING) / GRID_SIZE) * GRID_SIZE;
        onUpdateBlock(blockId, { x, y });
      });
    });
  }, [blocks, connections, onUpdateBlock, GRID_SIZE]);

  const getBlockColor = (block) => {
    if (block.blockType === 'subdiagram') return '#00A8CB';  // Teal — sub-diagram reference
    if (block.blockType === 'multistate') return '#7B61FF';  // Purple — multi-state block
    if (block.state === 'operating') return COLORS.GOLD;
    if (block.state === 'failed') return COLORS.NAVY;
    if (block.state === 'standby') return '#6698C2';
    if (block.state === 'degraded') return '#E9C46A';
    return COLORS.TEXT_DIM;
  };

  const getBlockTypeLabel = (type) => {
    const labels = { series: 'S', parallel: 'P', kofn: 'K', standby: 'SB', subdiagram: 'SUB', multistate: 'MS' };
    return labels[type] || 'C';
  };

  const handleMouseDown = (e) => {
    if (e.button === 2) return;

    if (e.button === 1) {
      setIsPanning(true);
      return;
    }

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const x = (e.clientX - rect.left - panOffset.x) / zoom;
    const y = (e.clientY - rect.top - panOffset.y) / zoom;

    const clickedBlock = blocks.find(
      (b) => x >= b.x && x <= b.x + BLOCK_WIDTH && y >= b.y && y <= b.y + BLOCK_HEIGHT
    );

    if (clickedBlock) {
      onSelectBlock(clickedBlock.id);
      setDraggingBlock(clickedBlock.id);
      setDragStart({ x: e.clientX, y: e.clientY, blockX: clickedBlock.x, blockY: clickedBlock.y });
    } else {
      onSelectBlock(null);
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setPanOffset({ x: panOffset.x + e.movementX, y: panOffset.y + e.movementY });
      return;
    }

    if (!draggingBlock || !dragStart) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    const newX = Math.round((dragStart.blockX + deltaX / zoom) / GRID_SIZE) * GRID_SIZE;
    const newY = Math.round((dragStart.blockY + deltaY / zoom) / GRID_SIZE) * GRID_SIZE;

    onUpdateBlock(draggingBlock, { x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setDraggingBlock(null);
    setDragStart(null);
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const newZoom = Math.max(0.5, Math.min(3, zoom - e.deltaY * 0.001));
    onZoomChange(newZoom);
  };

  const pageBlocks = blocks.filter(b => (b.page || 1) === (currentPage + 1));

  return (
    <div data-tour="rbd-canvas" style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ display: 'flex', gap: '8px', padding: '8px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
        <button
          onClick={() => onAddBlock({ id: `RBD-${Date.now()}`, x: 100, y: 100, blockType: 'series', state: 'operating', description: 'Block', page: currentPage + 1, mtbf: 0, mttr: 0, failureRate: 0, distribution: 'exponential', beta: null, eta: null, equipmentId: null, faultTreeGateId: null, markovModelId: null, capacity: 100, lognormalSigma: 0.8, repairDistribution: 'lognormal', repairSigma: 0.6, logisticDelay: 0, pmInterval: 0, pmDuration: 0, pmLabel: '', deferredEffect: 0, redundantGroup: '', kRequired: 1, dataSource: 'Optimal Reference Library', equipmentType: '', costPerHour: 0, shutdownStart: null, shutdownDuration: 0, shutdownLabel: '', beanUpTime: 0, mtbfActual: 0, mttrActual: 0, distributionActual: 'exponential', betaActual: null, etaActual: null, dataSourceActual: '' })}
          style={{ padding: '6px 12px', backgroundColor: COLORS.NAVY, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px' }}
        >
          Add Block
        </button>
        <button
          onClick={() => onAddBlock({ id: `SUB-${Date.now()}`, x: 200, y: 100, blockType: 'subdiagram', state: 'operating', description: 'Sub-System', page: currentPage + 1, subDiagramId: null, subBlocks: [], subConnections: [] })}
          style={{ padding: '6px 12px', backgroundColor: '#00A8CB', color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px' }}
          title="Add a sub-diagram block that contains its own RBD"
        >
          Sub-Diagram
        </button>
        <button
          onClick={() => onAddBlock({ id: `MS-${Date.now()}`, x: 300, y: 100, blockType: 'multistate', state: 'operating', description: 'Multi-State', page: currentPage + 1, states: [{ name: 'Full', capacity: 100, probability: 0.85 }, { name: 'Degraded', capacity: 60, probability: 0.10 }, { name: 'Failed', capacity: 0, probability: 0.05 }] })}
          style={{ padding: '6px 12px', backgroundColor: '#7B61FF', color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px' }}
          title="Add a multi-state block with degraded performance levels"
        >
          Multi-State
        </button>
        <button
          onClick={() => selectedBlockId && onDeleteBlock(selectedBlockId)}
          style={{ padding: '6px 12px', backgroundColor: COLORS.PANEL_BG, color: COLORS.NAVY, border: `1px solid ${COLORS.NAVY}`, cursor: 'pointer', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}
        >
          Delete
        </button>
        <button
          onClick={autoLayout}
          style={{ padding: '6px 12px', backgroundColor: COLORS.GOLD, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}
          title="Auto-arrange blocks using Sugiyama layered layout"
        >
          Auto Layout
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: COLORS.TEXT, fontSize: '12px' }}>
          <input type="checkbox" checked={showGrid} onChange={onToggleGrid} />
          Grid
        </label>
        <span style={{ marginLeft: 'auto', color: COLORS.TEXT_DIM, fontSize: '12px' }}>Zoom: {(zoom * 100).toFixed(0)}%</span>
      </div>

      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{
          flex: 1,
          minHeight: '400px',
          backgroundColor: COLORS.LIGHT_BG,
          cursor: draggingBlock ? 'grabbing' : 'grab',
          display: 'block'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onContextMenu={(e) => e.preventDefault()}
      >
        <g transform={`translate(${panOffset.x}, ${panOffset.y}) scale(${zoom})`}>
          {showGrid && (
            <>
              <defs>
                <pattern id="grid" width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse">
                  <circle cx={GRID_SIZE / 2} cy={GRID_SIZE / 2} r="0.5" fill={COLORS.BORDER} />
                </pattern>
              </defs>
              <rect width="4000" height="2000" fill="url(#grid)" />
            </>
          )}

          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
              <polygon points="0 0, 10 3, 0 6" fill={COLORS.GOLD} />
            </marker>
            <marker id="arrowhead-error" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
              <polygon points="0 0, 10 3, 0 6" fill={COLORS.ERROR} />
            </marker>
          </defs>

          {/* START Node */}
          <circle cx="40" cy="100" r="12" fill={COLORS.GOLD} stroke={COLORS.BORDER} strokeWidth="2" />
          <text x="40" y="105" textAnchor="middle" fill={COLORS.TEXT} fontSize="10" fontWeight="bold">S</text>

          {/* Render connections */}
          {connections.map((conn, idx) => {
            const fromBlock = conn.from === 'START' ? { x: 28, y: 88 } : pageBlocks.find((b) => b.id === conn.from);
            const toBlock = conn.to === 'END' ? { x: 1960, y: 88 } : pageBlocks.find((b) => b.id === conn.to);
            if (!fromBlock || !toBlock) return null;

            const x1 = conn.from === 'START' ? fromBlock.x : fromBlock.x + BLOCK_WIDTH;
            const y1 = conn.from === 'START' ? fromBlock.y : fromBlock.y + BLOCK_HEIGHT / 2;
            const x2 = conn.to === 'END' ? toBlock.x : toBlock.x;
            const y2 = conn.to === 'END' ? toBlock.y : toBlock.y + BLOCK_HEIGHT / 2;

            return (
              <line
                key={idx}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={selectedConnectionId === idx ? COLORS.GOLD : COLORS.BORDER}
                strokeWidth={selectedConnectionId === idx ? 3 : 2}
                markerEnd="url(#arrowhead)"
                style={{ cursor: 'pointer' }}
                onClick={() => onSelectConnection?.(idx)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, connectionId: idx });
                }}
              />
            );
          })}

          {/* Drawing connection in progress */}
          {drawingConnection && (
            <line
              x1={drawingConnection.x1}
              y1={drawingConnection.y1}
              x2={drawingConnection.x2}
              y2={drawingConnection.y2}
              stroke={COLORS.INFO}
              strokeWidth="2"
              strokeDasharray="5,5"
              markerEnd="url(#arrowhead)"
            />
          )}

          {/* Regular blocks */}
          {pageBlocks.map((block) => (
            <g key={block.id}>
              {/* Sub-diagram: double-bordered rectangle */}
              {block.blockType === 'subdiagram' ? (
                <>
                  <rect x={block.x} y={block.y} width={BLOCK_WIDTH + 10} height={BLOCK_HEIGHT + 6}
                    fill="none" stroke={selectedBlockId === block.id ? COLORS.GOLD : '#00A8CB'} strokeWidth="1.5" rx="6" strokeDasharray="4,2" />
                  <rect x={block.x + 3} y={block.y + 3} width={BLOCK_WIDTH + 4} height={BLOCK_HEIGHT}
                    fill={getBlockColor(block)} stroke={selectedBlockId === block.id ? COLORS.GOLD : COLORS.BORDER}
                    strokeWidth={selectedBlockId === block.id ? 3 : 2} rx="4" style={{ cursor: 'pointer' }}
                    onClick={() => onSelectBlock(block.id)} />
                  <text x={block.x + (BLOCK_WIDTH + 10) / 2} y={block.y + BLOCK_HEIGHT / 2 - 4} textAnchor="middle"
                    fill="#FFFFFF" fontSize="10" fontWeight="bold" style={{ pointerEvents: 'none' }}>
                    {block.description?.substring(0, 10) || 'Sub-System'}
                  </text>
                  <text x={block.x + (BLOCK_WIDTH + 10) / 2} y={block.y + BLOCK_HEIGHT / 2 + 10} textAnchor="middle"
                    fill="rgba(255,255,255,0.7)" fontSize="8" style={{ pointerEvents: 'none' }}>
                    ↳ {(block.subBlocks || []).length} blocks
                  </text>
                </>
              ) : block.blockType === 'multistate' ? (
                /* Multi-state: segmented block showing capacity levels */
                <>
                  <rect x={block.x} y={block.y} width={BLOCK_WIDTH} height={BLOCK_HEIGHT + 12}
                    fill={getBlockColor(block)} stroke={selectedBlockId === block.id ? COLORS.GOLD : COLORS.BORDER}
                    strokeWidth={selectedBlockId === block.id ? 3 : 2} rx="4" style={{ cursor: 'pointer' }}
                    onClick={() => onSelectBlock(block.id)} />
                  <text x={block.x + BLOCK_WIDTH / 2} y={block.y + 13} textAnchor="middle"
                    fill="#FFFFFF" fontSize="10" fontWeight="bold" style={{ pointerEvents: 'none' }}>
                    {block.description?.substring(0, 8) || 'MS'}
                  </text>
                  {(block.states || []).slice(0, 3).map((st, si) => (
                    <g key={si}>
                      <rect x={block.x + 4} y={block.y + 18 + si * 13} width={(BLOCK_WIDTH - 8) * (st.capacity / 100)} height={10}
                        fill={st.capacity > 50 ? 'rgba(255,255,255,0.4)' : st.capacity > 0 ? 'rgba(255,200,50,0.5)' : 'rgba(255,80,80,0.5)'}
                        rx="2" />
                      <text x={block.x + BLOCK_WIDTH - 6} y={block.y + 26 + si * 13} textAnchor="end"
                        fill="rgba(255,255,255,0.8)" fontSize="7" style={{ pointerEvents: 'none' }}>
                        {st.name} {st.capacity}%
                      </text>
                    </g>
                  ))}
                </>
              ) : (
                /* Standard block */
                <>
              <rect
                x={block.x}
                y={block.y}
                width={BLOCK_WIDTH}
                height={BLOCK_HEIGHT}
                fill={getBlockColor(block)}
                stroke={selectedBlockId === block.id ? COLORS.GOLD : COLORS.BORDER}
                strokeWidth={selectedBlockId === block.id ? 3 : 2}
                rx="4"
                style={{ cursor: 'pointer' }}
                onClick={() => onSelectBlock(block.id)}
              />
              <text
                x={block.x + BLOCK_WIDTH / 2}
                y={block.y + BLOCK_HEIGHT / 2 - 8}
                textAnchor="middle"
                fill="#FFFFFF"
                fontSize="11"
                fontWeight="bold"
                style={{ pointerEvents: 'none' }}
              >
                {block.description?.substring(0, 12) || block.id.substring(0, 12)}
              </text>
              <text
                x={block.x + BLOCK_WIDTH / 2}
                y={block.y + BLOCK_HEIGHT / 2 + 8}
                textAnchor="middle"
                fill="rgba(255,255,255,0.75)"
                fontSize="9"
                style={{ pointerEvents: 'none' }}
              >
                {getBlockTypeLabel(block.blockType)}
              </text>
                </>
              )}
              {/* Right edge hotspot for connection drawing */}
              <rect
                x={block.x + BLOCK_WIDTH - 6}
                y={block.y + BLOCK_HEIGHT / 2 - 6}
                width="12"
                height="12"
                fill="transparent"
                stroke="none"
                style={{ cursor: 'crosshair' }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDrawingConnection({
                    fromId: block.id,
                    x1: block.x + BLOCK_WIDTH,
                    y1: block.y + BLOCK_HEIGHT / 2,
                    x2: block.x + BLOCK_WIDTH,
                    y2: block.y + BLOCK_HEIGHT / 2
                  });
                }}
              />
            </g>
          ))}

          {/* END Node */}
          <circle cx="1960" cy="100" r="12" fill={COLORS.NAVY} stroke={COLORS.BORDER} strokeWidth="2" />
          <text x="1960" y="105" textAnchor="middle" fill="#FFFFFF" fontSize="10" fontWeight="bold">E</text>
        </g>
      </svg>
    </div>
  );
}

// ============================================================================
// FAULT TREE EDITOR - INTERACTIVE FT DIAGRAM
// ============================================================================
function FaultTreeEditor({
  gates = [],
  primaryEvents = [],
  onUpdateGate,
  onAddGate,
  onDeleteGate,
  selectedGateId,
  onSelectGate,
  zoom = 1,
  onZoomChange,
  cutSets = [],
  importanceMeasures = [],
  projectData,
  setProjectData
}) {
  const svgRef = useRef(null);
  const [panOffset, setPanOffset] = useState({ x: 100, y: 50 });
  const [highlightedCutSet, setHighlightedCutSet] = useState(null);
  const [showCutSets, setShowCutSets] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null); // {type: 'gate'|'event', id: string}
  const [showProps, setShowProps] = useState(true);

  // Normalize gate inputs: ensure inputs is always an array of child IDs
  const normalizeInputs = (gate) => {
    if (Array.isArray(gate.inputs)) return gate.inputs;
    return [];
  };

  // Build parent-child relationships from gates
  // gates have inputs[] arrays containing child gate/event IDs
  const getGateInputs = (gate) => normalizeInputs(gate);

  // Build set of highlighted event IDs from selected cut set
  const highlightedEvents = useMemo(() => {
    if (highlightedCutSet === null || !cutSets[highlightedCutSet]) return new Set();
    const cs = cutSets[highlightedCutSet];
    return new Set(Array.isArray(cs) ? cs.flat() : [cs]);
  }, [highlightedCutSet, cutSets]);

  const getImportanceColor = (eventId) => {
    if (!importanceMeasures || importanceMeasures.length === 0) return null;
    const imp = importanceMeasures.find(m => m.id === eventId);
    if (!imp) return null;
    const fv = imp.fussellVesely || 0;
    if (fv > 0.5) return COLORS.NAVY;
    if (fv > 0.1) return '#6698C2';
    return COLORS.GOLD;
  };

  // Tree layout
  const calculateTreeLayout = () => {
    const positions = {};
    const visited = new Set();

    const getSubtreeWidth = (nodeId) => {
      if (visited.has(nodeId)) return 1;
      const gate = gates.find(g => g.id === nodeId);
      if (!gate) return 1;
      const children = getGateInputs(gate);
      if (children.length === 0) return 1;
      return children.reduce((sum, cid) => sum + getSubtreeWidth(cid), 0);
    };

    const layoutNode = (nodeId, level, xCenter) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      const gate = gates.find(g => g.id === nodeId);
      if (!gate) return;

      const y = level * 140 + 60;
      positions[nodeId] = { x: xCenter, y, type: 'gate' };

      const children = getGateInputs(gate);
      if (children.length === 0) return;

      const spacing = 120;
      const totalWidth = children.reduce((sum, cid) => {
        const w = getSubtreeWidth(cid);
        return sum + w * spacing;
      }, 0);

      let currentX = xCenter - totalWidth / 2;
      children.forEach(childId => {
        const childWidth = getSubtreeWidth(childId) * spacing;
        const childCenter = currentX + childWidth / 2;

        const childGate = gates.find(g => g.id === childId);
        if (childGate) {
          layoutNode(childId, level + 1, childCenter);
        } else {
          positions[childId] = { x: childCenter, y: y + 140, type: 'event' };
        }
        currentX += childWidth;
      });
    };

    // Find root gate (first gate, or one with no parent)
    const childIds = new Set();
    gates.forEach(g => getGateInputs(g).forEach(id => childIds.add(id)));
    const rootGate = gates.find(g => !childIds.has(g.id)) || gates[0];
    if (rootGate) {
      layoutNode(rootGate.id, 0, 400);
    }

    // Position any orphan gates not in tree
    let orphanX = 50;
    gates.forEach(g => {
      if (!positions[g.id]) {
        positions[g.id] = { x: orphanX, y: 400, type: 'gate' };
        orphanX += 120;
      }
    });

    // Position orphan events
    primaryEvents.forEach(e => {
      if (!positions[e.id]) {
        positions[e.id] = { x: orphanX, y: 400, type: 'event' };
        orphanX += 80;
      }
    });

    return positions;
  };

  const positions = calculateTreeLayout();

  const renderGateSymbol = (gateType, x, y, size = 35) => {
    switch (gateType) {
      case 'AND':
        return (
          <g>
            <path d={`M ${x - size} ${y} L ${x + size} ${y} Q ${x + size} ${y - size} ${x} ${y - size} Q ${x - size} ${y - size} ${x - size} ${y}`}
              fill={COLORS.BLUE} stroke={COLORS.BORDER} strokeWidth="2" />
            <text x={x} y={y - 5} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">AND</text>
          </g>
        );
      case 'OR':
        return (
          <g>
            <path d={`M ${x - size} ${y - size / 2} L ${x} ${y + size / 2} L ${x + size} ${y - size / 2} Q ${x + size} ${y - size} ${x} ${y - size - 8} Q ${x - size} ${y - size} ${x - size} ${y - size / 2}`}
              fill={COLORS.BLUE} stroke={COLORS.BORDER} strokeWidth="2" />
            <text x={x} y={y - 8} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">OR</text>
          </g>
        );
      case 'VOTING':
        return (
          <g>
            <path d={`M ${x - size} ${y} L ${x + size} ${y} Q ${x + size} ${y - size} ${x} ${y - size} Q ${x - size} ${y - size} ${x - size} ${y}`}
              fill={COLORS.INFO} stroke={COLORS.BORDER} strokeWidth="2" />
            <text x={x} y={y - 8} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">VOTE</text>
          </g>
        );
      default:
        return <circle cx={x} cy={y} r={size} fill={COLORS.BLUE} stroke={COLORS.BORDER} strokeWidth="2" />;
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const newZoom = Math.max(0.3, Math.min(3, zoom - e.deltaY * 0.001));
    onZoomChange(newZoom);
  };

  // Add a new OR gate with auto ID
  const handleAddGate = (gateType = 'OR') => {
    const nextNum = gates.length + 1;
    const id = `GT-${String(nextNum).padStart(3, '0')}`;
    const newGate = { id, description: `${gateType} Gate ${nextNum}`, gateType, inputs: [], kValue: null, nValue: null };
    onAddGate(newGate);
    setSelectedNode({ type: 'gate', id });
  };

  // Add a new primary event
  const handleAddEvent = () => {
    const nextNum = primaryEvents.length + 1;
    const id = `PE-${String(nextNum).padStart(3, '0')}`;
    const newEvent = { id, description: `Event ${nextNum}`, eventType: 'Basic', probability: 0.01, failureModel: null, commonCauseGroup: null, betaFactor: null, rcmCauseId: null, weibullSetId: null, equipmentId: null };
    if (setProjectData) {
      setProjectData(prev => ({ ...prev, primaryevents: [...(prev.primaryevents || []), newEvent] }));
    }
    setSelectedNode({ type: 'event', id });
  };

  // Detect if adding childId under parentGateId would create a cycle
  const wouldCreateCycle = (parentGateId, childId) => {
    if (parentGateId === childId) return true;
    const visited = new Set();
    const traverse = (gateId) => {
      if (visited.has(gateId)) return false;
      visited.add(gateId);
      const gate = gates.find(g => g.id === gateId);
      if (!gate) return false;
      const inputs = Array.isArray(gate.inputs) ? gate.inputs : [];
      for (const inputId of inputs) {
        if (inputId === parentGateId) return true; // found cycle back to parent
        if (traverse(inputId)) return true;
      }
      return false;
    };
    return traverse(childId);
  };

  // Connect: add a child (gate or event) to a parent gate's inputs
  const handleConnect = (parentGateId, childId) => {
    if (!setProjectData) return;
    if (parentGateId === childId) {
      alert('Cannot connect a gate to itself.');
      return;
    }
    // Check for circular reference
    if (wouldCreateCycle(parentGateId, childId)) {
      alert('Cannot connect: this would create a circular reference in the fault tree.');
      return;
    }
    setProjectData(prev => {
      const newGates = (prev.gates || []).map(g => {
        if (g.id !== parentGateId) return g;
        const currentInputs = Array.isArray(g.inputs) ? g.inputs : [];
        if (currentInputs.includes(childId)) return g; // already connected
        return { ...g, inputs: [...currentInputs, childId] };
      });
      return { ...prev, gates: newGates };
    });
  };

  // Disconnect: remove a child from a gate's inputs
  const handleDisconnect = (parentGateId, childId) => {
    if (!setProjectData) return;
    setProjectData(prev => {
      const newGates = (prev.gates || []).map(g => {
        if (g.id !== parentGateId) return g;
        const currentInputs = Array.isArray(g.inputs) ? g.inputs : [];
        return { ...g, inputs: currentInputs.filter(id => id !== childId) };
      });
      return { ...prev, gates: newGates };
    });
  };

  // Update a gate property
  const handleUpdateGateProp = (gateId, key, value) => {
    if (onUpdateGate) onUpdateGate(gateId, { [key]: value });
  };

  // Update an event property (with validation)
  const handleUpdateEvent = (eventId, key, value) => {
    if (!setProjectData) return;
    // Validate probability range
    if (key === 'probability') {
      const numVal = parseFloat(value);
      if (isNaN(numVal) || numVal < 0 || numVal > 1) return; // silently reject invalid probability
    }
    setProjectData(prev => ({
      ...prev,
      primaryevents: (prev.primaryevents || []).map(e => e.id === eventId ? { ...e, [key]: value } : e)
    }));
  };

  // Delete event
  const handleDeleteEvent = (eventId) => {
    if (!setProjectData) return;
    // Remove from all gate inputs and from primaryevents
    setProjectData(prev => ({
      ...prev,
      primaryevents: (prev.primaryevents || []).filter(e => e.id !== eventId),
      gates: (prev.gates || []).map(g => ({
        ...g,
        inputs: Array.isArray(g.inputs) ? g.inputs.filter(id => id !== eventId) : g.inputs
      }))
    }));
    if (selectedNode?.id === eventId) setSelectedNode(null);
  };

  // Delete gate (with confirmation for root gate)
  const handleDeleteGateLocal = (gateId) => {
    // Check if this is the root gate
    const childIds = new Set();
    gates.forEach(g => (Array.isArray(g.inputs) ? g.inputs : []).forEach(id => childIds.add(id)));
    const isRoot = !childIds.has(gateId);
    if (isRoot && gates.length > 1) {
      if (!window.confirm('This is the top-level (root) gate. Deleting it will disconnect all child nodes. Continue?')) return;
    }
    if (setProjectData) {
      setProjectData(prev => ({
        ...prev,
        gates: (prev.gates || []).filter(g => g.id !== gateId).map(g => ({
          ...g,
          inputs: Array.isArray(g.inputs) ? g.inputs.filter(id => id !== gateId) : g.inputs
        }))
      }));
    }
    if (selectedNode?.id === gateId) setSelectedNode(null);
  };

  // Get the selected node details
  const selectedGate = selectedNode?.type === 'gate' ? gates.find(g => g.id === selectedNode.id) : null;
  const selectedEvent = selectedNode?.type === 'event' ? primaryEvents.find(e => e.id === selectedNode.id) : null;

  // All IDs for connect dropdown
  const allIds = [...gates.map(g => ({ id: g.id, label: `${g.id} (${g.gateType} Gate)`, type: 'gate' })), ...primaryEvents.map(e => ({ id: e.id, label: `${e.id} (${e.description})`, type: 'event' }))];

  const inputStyle = { backgroundColor: '#fff', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '5px 7px', fontSize: '11px', width: '100%', boxSizing: 'border-box' };
  const btnSmall = { padding: '4px 10px', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '500' };

  return (
    <div data-tour="ft-canvas" style={{ display: 'flex', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Main canvas area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', gap: '6px', padding: '8px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => handleAddGate('OR')} style={{ ...btnSmall, backgroundColor: COLORS.NAVY, color: '#fff' }}>+ OR Gate</button>
          <button onClick={() => handleAddGate('AND')} style={{ ...btnSmall, backgroundColor: COLORS.BLUE, color: '#fff' }}>+ AND Gate</button>
          <button onClick={() => handleAddGate('VOTING')} style={{ ...btnSmall, backgroundColor: COLORS.INFO, color: '#fff' }}>+ Voting Gate</button>
          <div style={{ width: '1px', height: '20px', backgroundColor: COLORS.BORDER }} />
          <button onClick={handleAddEvent} style={{ ...btnSmall, backgroundColor: COLORS.SUCCESS, color: '#fff' }}>+ Basic Event</button>
          {cutSets.length > 0 && (
            <>
              <div style={{ width: '1px', height: '20px', backgroundColor: COLORS.BORDER }} />
              <button onClick={() => setShowCutSets(!showCutSets)} style={{ ...btnSmall, backgroundColor: showCutSets ? COLORS.GOLD : COLORS.BORDER, color: showCutSets ? '#000' : COLORS.TEXT }}>
                {showCutSets ? 'Hide' : 'Show'} Cut Sets ({cutSets.length})
              </button>
              {showCutSets && (
                <select value={highlightedCutSet ?? ''} onChange={(e) => setHighlightedCutSet(e.target.value === '' ? null : parseInt(e.target.value))}
                  style={{ padding: '4px 8px', backgroundColor: COLORS.LIGHT_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '11px' }}>
                  <option value="">All Cut Sets</option>
                  {cutSets.map((cs, idx) => <option key={idx} value={idx}>Cut Set {idx + 1}: &#123;{Array.isArray(cs) ? cs.flat().join(', ') : cs}&#125;</option>)}
                </select>
              )}
            </>
          )}
          <span style={{ marginLeft: 'auto', color: COLORS.TEXT_DIM, fontSize: '11px' }}>
            {gates.length} gates, {primaryEvents.length} events | Zoom: {(zoom * 100).toFixed(0)}%
          </span>
          <button onClick={() => setShowProps(!showProps)} style={{ ...btnSmall, backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}` }}>
            {showProps ? 'Hide' : 'Show'} Properties
          </button>
        </div>

        {/* SVG Canvas */}
        <svg ref={svgRef} style={{ flex: 1, backgroundColor: '#f8f9fc', cursor: 'grab' }} onWheel={handleWheel}>
          <g transform={`translate(${panOffset.x}, ${panOffset.y}) scale(${zoom})`}>
            {/* Connecting lines */}
            {gates.map((gate) => {
              const gatePos = positions[gate.id];
              if (!gatePos) return null;
              return getGateInputs(gate).map((inputId) => {
                const inputPos = positions[inputId];
                if (!inputPos) return null;
                return (
                  <line key={`line-${gate.id}-${inputId}`}
                    x1={gatePos.x} y1={gatePos.y + 30}
                    x2={inputPos.x} y2={inputPos.type === 'gate' ? inputPos.y - 30 : inputPos.y - 22}
                    stroke={COLORS.BORDER} strokeWidth="2" />
                );
              });
            })}

            {/* Gate nodes */}
            {gates.map((gate) => {
              const pos = positions[gate.id];
              if (!pos) return null;
              const isSelected = selectedNode?.id === gate.id;
              return (
                <g key={gate.id} onClick={() => setSelectedNode({ type: 'gate', id: gate.id })} style={{ cursor: 'pointer' }}>
                  {isSelected && <circle cx={pos.x} cy={pos.y - 5} r="48" fill="none" stroke={COLORS.GOLD} strokeWidth="2" strokeDasharray="4 2" />}
                  {renderGateSymbol(gate.gateType, pos.x, pos.y, 35)}
                  <text x={pos.x} y={pos.y + 30} textAnchor="middle" fill={COLORS.TEXT} fontSize="9" fontWeight="bold">{gate.id}</text>
                  <text x={pos.x} y={pos.y + 42} textAnchor="middle" fill={COLORS.TEXT_DIM} fontSize="8">{(gate.description || '').substring(0, 20)}</text>
                </g>
              );
            })}

            {/* Primary event nodes */}
            {primaryEvents.map((event) => {
              const pos = positions[event.id];
              if (!pos) return null;
              const isSelected = selectedNode?.id === event.id;
              const isHighlighted = highlightedEvents.has(event.id);
              const importanceColor = getImportanceColor(event.id);
              const fillColor = isHighlighted ? COLORS.ERROR : (importanceColor || COLORS.SUCCESS);
              const strokeColor = isSelected ? COLORS.GOLD : (isHighlighted ? COLORS.ERROR : COLORS.BORDER);

              return (
                <g key={event.id} onClick={() => setSelectedNode({ type: 'event', id: event.id })} style={{ cursor: 'pointer' }}>
                  {isHighlighted && (
                    <circle cx={pos.x} cy={pos.y} r="28" fill="none" stroke={COLORS.ERROR} strokeWidth="1" opacity="0.5">
                      <animate attributeName="r" values="24;30;24" dur="1.5s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle cx={pos.x} cy={pos.y} r="22" fill={fillColor} stroke={strokeColor} strokeWidth={isSelected ? 3 : 2} />
                  <text x={pos.x} y={pos.y - 4} textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">
                    {(event.description || event.id).substring(0, 8)}
                  </text>
                  <text x={pos.x} y={pos.y + 7} textAnchor="middle" fill="#fff" fontSize="7">
                    P={event.probability || 0}
                  </text>
                  <text x={pos.x} y={pos.y + 36} textAnchor="middle" fill={COLORS.TEXT_DIM} fontSize="8">{event.id}</text>
                </g>
              );
            })}

            {/* Empty state */}
            {gates.length === 0 && primaryEvents.length === 0 && (
              <text x="400" y="200" textAnchor="middle" fill={COLORS.TEXT_DIM} fontSize="14">
                Click "+ OR Gate" or "+ AND Gate" to start building your fault tree
              </text>
            )}
          </g>
        </svg>
      </div>

      {/* Properties panel */}
      {showProps && (
        <div style={{ width: '280px', borderLeft: `1px solid ${COLORS.BORDER}`, backgroundColor: '#fff', overflow: 'auto', fontSize: '12px' }}>
          <div style={{ padding: '10px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
            <h4 style={{ margin: 0, color: COLORS.TEXT, fontSize: '13px' }}>Properties</h4>
          </div>

          {selectedGate && (
            <div style={{ padding: '10px' }}>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Gate ID</label>
                <input value={selectedGate.id} disabled style={{ ...inputStyle, backgroundColor: '#f5f5f5' }} />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Description</label>
                <input value={selectedGate.description || ''} onChange={e => handleUpdateGateProp(selectedGate.id, 'description', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Gate Type</label>
                <select value={selectedGate.gateType} onChange={e => handleUpdateGateProp(selectedGate.id, 'gateType', e.target.value)} style={inputStyle}>
                  <option value="OR">OR</option>
                  <option value="AND">AND</option>
                  <option value="VOTING">VOTING (k-of-n)</option>
                </select>
              </div>
              {selectedGate.gateType === 'VOTING' && (
                <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>k (required)</label>
                    <input type="number" value={selectedGate.kValue || 2} min="1" onChange={e => handleUpdateGateProp(selectedGate.id, 'kValue', parseInt(e.target.value))} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>n (total)</label>
                    <input type="number" value={selectedGate.nValue || 3} min="1" onChange={e => handleUpdateGateProp(selectedGate.id, 'nValue', parseInt(e.target.value))} style={inputStyle} />
                  </div>
                </div>
              )}

              {/* Current inputs */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Inputs (children)</label>
                {getGateInputs(selectedGate).length === 0 ? (
                  <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px', fontStyle: 'italic', padding: '4px 0' }}>No inputs connected</div>
                ) : (
                  getGateInputs(selectedGate).map(inputId => {
                    const childGate = gates.find(g => g.id === inputId);
                    const childEvent = primaryEvents.find(e => e.id === inputId);
                    const label = childGate ? `${inputId} (${childGate.gateType} Gate)` : childEvent ? `${inputId} (${childEvent.description})` : inputId;
                    return (
                      <div key={inputId} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 0', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                        <span style={{ flex: 1, fontSize: '11px' }}>{label}</span>
                        <button onClick={() => handleDisconnect(selectedGate.id, inputId)} style={{ ...btnSmall, backgroundColor: '#fde8e8', color: COLORS.ERROR, padding: '2px 6px', fontSize: '10px' }}>×</button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Add input dropdown */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Add Input</label>
                <select onChange={e => { if (e.target.value) { handleConnect(selectedGate.id, e.target.value); e.target.value = ''; } }} style={inputStyle} defaultValue="">
                  <option value="">-- Select gate or event --</option>
                  {allIds.filter(item => item.id !== selectedGate.id && !getGateInputs(selectedGate).includes(item.id)).map(item => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </div>

              <button onClick={() => { if (window.confirm(`Delete gate ${selectedGate.id}?`)) handleDeleteGateLocal(selectedGate.id); }}
                style={{ ...btnSmall, backgroundColor: '#fde8e8', color: COLORS.ERROR, width: '100%', padding: '6px', border: `1px solid ${COLORS.ERROR}` }}>
                Delete Gate
              </button>
            </div>
          )}

          {selectedEvent && (
            <div style={{ padding: '10px' }}>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Event ID</label>
                <input value={selectedEvent.id} disabled style={{ ...inputStyle, backgroundColor: '#f5f5f5' }} />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Description</label>
                <input value={selectedEvent.description || ''} onChange={e => handleUpdateEvent(selectedEvent.id, 'description', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Event Type</label>
                <select value={selectedEvent.eventType || 'Basic'} onChange={e => handleUpdateEvent(selectedEvent.id, 'eventType', e.target.value)} style={inputStyle}>
                  <option value="Basic">Basic Event</option>
                  <option value="House">House Event</option>
                  <option value="Undeveloped">Undeveloped Event</option>
                </select>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Probability</label>
                <input type="number" value={selectedEvent.probability || 0} min="0" max="1" step="0.001"
                  onChange={e => handleUpdateEvent(selectedEvent.id, 'probability', parseFloat(e.target.value) || 0)} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Common Cause Group</label>
                <input value={selectedEvent.commonCauseGroup || ''} onChange={e => handleUpdateEvent(selectedEvent.id, 'commonCauseGroup', e.target.value)} style={inputStyle} placeholder="e.g. CCG-001" />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Beta Factor (CCF)</label>
                <input type="number" value={selectedEvent.betaFactor || 0} min="0" max="1" step="0.01"
                  onChange={e => handleUpdateEvent(selectedEvent.id, 'betaFactor', parseFloat(e.target.value) || 0)} style={inputStyle} />
              </div>

              <button onClick={() => { if (window.confirm(`Delete event ${selectedEvent.id}?`)) handleDeleteEvent(selectedEvent.id); }}
                style={{ ...btnSmall, backgroundColor: '#fde8e8', color: COLORS.ERROR, width: '100%', padding: '6px', border: `1px solid ${COLORS.ERROR}` }}>
                Delete Event
              </button>
            </div>
          )}

          {!selectedGate && !selectedEvent && (
            <div style={{ padding: '16px', color: COLORS.TEXT_DIM, fontSize: '11px', textAlign: 'center' }}>
              <p>Click a gate or event on the diagram to view and edit its properties.</p>
              <p style={{ marginTop: '8px' }}>To build a tree: add gates and events, then select a gate and use "Add Input" to connect children to it.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EVENT TREE EDITOR - FULL PROBABILITY PROPAGATION
// ============================================================================
function EventTreeEditor({ eventTrees = [], onUpdate, projectData, setProjectData }) {
  const [ieFrequency, setIeFrequency] = useState(0.1);
  const [ieName, setIeName] = useState('');
  const [functionalEvents, setFunctionalEvents] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [hasRun, setHasRun] = useState(false);
  const [activeTab, setActiveTab] = useState('diagram');
  const [error, setError] = useState(null);

  const inputStyle = { backgroundColor: '#fff', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '5px 7px', fontSize: '11px', width: '100%', boxSizing: 'border-box' };
  const btnSmall = { padding: '4px 10px', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '500' };

  // FTA integration — available gates for linking
  const availableGates = (projectData?.gates || []).map(g => ({ id: g.id, name: g.name || g.description || g.id }));
  const availablePrimaryEvents = (projectData?.primaryevents || []).map(e => ({ id: e.id, name: e.name || e.description || e.id, probability: e.probability || 0.01 }));

  // Add a functional event (branching point)
  const handleAddBranch = () => {
    const nextNum = functionalEvents.length + 1;
    setFunctionalEvents(prev => [...prev, { id: `FE${nextNum}`, name: `Safety System ${nextNum}`, successProbability: 0.95, linkedFTGate: null, linkedPrimaryEvent: null }]);
  };

  // Remove a functional event
  const handleRemoveBranch = (idx) => {
    setFunctionalEvents(prev => prev.filter((_, i) => i !== idx));
    setSequences([]);
    setHasRun(false);
  };

  // Update a functional event (with validation)
  const handleUpdateBranch = (idx, key, value) => {
    if (key === 'successProbability') {
      const numVal = parseFloat(value);
      if (isNaN(numVal)) return;
      // Clamp to [0, 1]
      value = Math.max(0, Math.min(1, numVal));
    }
    setFunctionalEvents(prev => prev.map((fe, i) => i === idx ? { ...fe, [key]: value } : fe));
  };

  // Run propagation manually (straightforward binary tree calculation)
  const handleRunPropagation = () => {
    setError(null);
    if (functionalEvents.length === 0) {
      setError('Add at least one functional event (branching point).');
      return;
    }

    // Validate initiating event frequency
    if (ieFrequency <= 0 || !isFinite(ieFrequency)) {
      setError('Initiating event frequency must be a positive number.');
      return;
    }

    // Validate all functional events
    for (let i = 0; i < functionalEvents.length; i++) {
      const fe = functionalEvents[i];
      if (!fe.name || !fe.name.trim()) {
        setError(`Branching event ${i + 1} has no name. Please enter a name.`);
        return;
      }
      const p = parseFloat(fe.successProbability);
      if (isNaN(p) || p < 0 || p > 1) {
        setError(`"${fe.name}" has an invalid success probability. Must be between 0 and 1.`);
        return;
      }
    }

    // Guard against too many branching events (2^n memory limit)
    if (functionalEvents.length > 15) {
      setError(`Too many branching events (${functionalEvents.length}). Maximum is 15 to avoid ${Math.pow(2, functionalEvents.length).toLocaleString()} sequences.`);
      return;
    }

    const allSequences = [];
    const numSeq = Math.pow(2, functionalEvents.length);

    for (let i = 0; i < numSeq; i++) {
      let freq = ieFrequency;
      const path = [];
      let failCount = 0;

      for (let j = 0; j < functionalEvents.length; j++) {
        const fe = functionalEvents[j];
        const bit = (i >> (functionalEvents.length - 1 - j)) & 1; // 0 = success, 1 = failure
        const pSuccess = fe.successProbability;

        if (bit === 0) {
          freq *= pSuccess;
          path.push({ event: fe.name, outcome: 'Success', prob: pSuccess });
        } else {
          freq *= (1 - pSuccess);
          path.push({ event: fe.name, outcome: 'Failure', prob: 1 - pSuccess });
          failCount++;
        }
      }

      // Categorise consequence by number of failures
      let consequence = 'Safe';
      if (failCount === 1) consequence = 'Minor Incident';
      else if (failCount === 2) consequence = 'Major Incident';
      else if (failCount >= 3) consequence = 'Catastrophic';

      allSequences.push({ id: i + 1, path, frequency: freq, consequence, failCount });
    }

    setSequences(allSequences);
    setHasRun(true);
  };

  // Load a demo scenario
  const handleLoadDemo = () => {
    setIeName('Loss of Coolant');
    setIeFrequency(0.1);
    setFunctionalEvents([
      { id: 'FE1', name: 'Emergency Shutdown', successProbability: 0.95 },
      { id: 'FE2', name: 'Backup Pump Starts', successProbability: 0.90 },
      { id: 'FE3', name: 'Operator Intervention', successProbability: 0.85 }
    ]);
    setSequences([]);
    setHasRun(false);
  };

  // Verify: all frequencies should sum to initiating event frequency
  const totalFreq = sequences.reduce((sum, s) => sum + s.frequency, 0);

  // Build diagram data
  const diagramWidth = 120 + functionalEvents.length * 140 + 200;
  const diagramHeight = Math.max(400, Math.pow(2, functionalEvents.length) * 40 + 100);

  // Draw the event tree diagram
  const renderDiagram = () => {
    if (functionalEvents.length === 0) {
      return <text x="300" y="200" textAnchor="middle" fill={COLORS.TEXT_DIM} fontSize="14">Add functional events to build the tree</text>;
    }

    const elements = [];
    const headerY = 30;
    const treeStartY = 80;
    const colWidth = 140;
    const startX = 80;

    // Headers
    elements.push(
      <g key="ie-header">
        <rect x={startX - 40} y={headerY - 15} width="80" height="28" fill={COLORS.GOLD} rx="4" />
        <text x={startX} y={headerY + 3} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">{ieName.substring(0, 12)}</text>
      </g>
    );

    functionalEvents.forEach((fe, i) => {
      const x = startX + (i + 1) * colWidth;
      elements.push(
        <g key={`header-${i}`}>
          <rect x={x - 50} y={headerY - 15} width="100" height="28" fill={COLORS.NAVY} rx="4" />
          <text x={x} y={headerY - 1} textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">{fe.name.substring(0, 14)}</text>
          <text x={x} y={headerY + 10} textAnchor="middle" fill={COLORS.GOLD} fontSize="7">P(S)={fe.successProbability}</text>
        </g>
      );
    });

    // Outcome column header
    const outcomeX = startX + (functionalEvents.length + 1) * colWidth;
    elements.push(
      <g key="outcome-header">
        <rect x={outcomeX - 50} y={headerY - 15} width="100" height="28" fill={COLORS.BORDER} rx="4" />
        <text x={outcomeX} y={headerY + 3} textAnchor="middle" fill={COLORS.TEXT} fontSize="9" fontWeight="bold">Outcome</text>
      </g>
    );

    // Draw branches recursively
    const numLeaves = Math.pow(2, functionalEvents.length);
    const leafSpacing = Math.max(30, Math.min(50, (diagramHeight - 120) / numLeaves));
    const totalTreeHeight = numLeaves * leafSpacing;
    const treeBaseY = treeStartY + 20;

    const drawBranch = (level, yCenter, ySpan, seqPrefix) => {
      if (level >= functionalEvents.length) return;

      const x1 = startX + level * colWidth;
      const x2 = startX + (level + 1) * colWidth;
      const halfSpan = ySpan / 4;

      const yUp = yCenter - halfSpan;
      const yDown = yCenter + halfSpan;

      // Success branch (up)
      elements.push(
        <line key={`s-${level}-${seqPrefix}`} x1={x1} y1={yCenter} x2={x2} y2={yUp}
          stroke={COLORS.SUCCESS} strokeWidth="2" />
      );
      // Failure branch (down)
      elements.push(
        <line key={`f-${level}-${seqPrefix}`} x1={x1} y1={yCenter} x2={x2} y2={yDown}
          stroke={COLORS.ERROR} strokeWidth="1.5" strokeDasharray="4 2" />
      );

      // Branch point dot
      elements.push(
        <circle key={`dot-${level}-${seqPrefix}`} cx={x1} cy={yCenter} r="4" fill={COLORS.BLUE} stroke={COLORS.BORDER} strokeWidth="1" />
      );

      drawBranch(level + 1, yUp, ySpan / 2, seqPrefix + '0');
      drawBranch(level + 1, yDown, ySpan / 2, seqPrefix + '1');
    };

    const centerY = treeBaseY + totalTreeHeight / 2;
    // IE circle
    elements.push(
      <g key="ie-node">
        <circle cx={startX} cy={centerY} r="16" fill={COLORS.GOLD} stroke={COLORS.BORDER} strokeWidth="2" />
        <text x={startX} y={centerY + 4} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">IE</text>
        <text x={startX} y={centerY + 22} textAnchor="middle" fill={COLORS.TEXT_DIM} fontSize="8">f={ieFrequency}/yr</text>
      </g>
    );

    drawBranch(0, centerY, totalTreeHeight, '');

    // End state labels (if analysis has been run)
    if (hasRun && sequences.length > 0) {
      const endX = startX + functionalEvents.length * colWidth;
      sequences.forEach((seq, i) => {
        // Calculate y position for this sequence
        let y = treeBaseY;
        let span = totalTreeHeight;
        seq.path.forEach(step => {
          const halfSpan = span / 4;
          if (step.outcome === 'Success') {
            y = y + span / 2 - halfSpan;
          } else {
            y = y + span / 2 + halfSpan;
          }
          span = span / 2;
        });

        const freqText = seq.frequency >= 0.001 ? seq.frequency.toFixed(4) : seq.frequency.toExponential(2);
        const color = seq.consequence === 'Safe' ? COLORS.SUCCESS : seq.consequence === 'Minor Incident' ? COLORS.WARNING : COLORS.ERROR;

        elements.push(
          <g key={`end-${i}`}>
            <circle cx={endX} cy={y} r="3" fill={color} />
            <text x={endX + 10} y={y + 3} fill={COLORS.TEXT} fontSize="8" fontWeight="bold">{freqText}/yr</text>
            <text x={endX + 80} y={y + 3} fill={color} fontSize="7">{seq.consequence}</text>
          </g>
        );
      });
    }

    return elements;
  };

  return (
    <div style={{ display: 'flex', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Left panel: Event Tree Configuration */}
      <div style={{ width: '280px', borderRight: `1px solid ${COLORS.BORDER}`, overflow: 'auto', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
          <h4 style={{ margin: 0, color: COLORS.TEXT, fontSize: '13px' }}>Event Tree Setup</h4>
        </div>

        {/* Initiating Event */}
        <div style={{ padding: '10px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
          <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px', fontWeight: '600' }}>Initiating Event</label>
          <input value={ieName} onChange={e => setIeName(e.target.value)} style={{ ...inputStyle, marginBottom: '6px' }} placeholder="Event name..." />
          <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>Frequency (per year)</label>
          <input type="number" value={ieFrequency} onChange={e => setIeFrequency(parseFloat(e.target.value) || 0)} style={inputStyle} min="0" step="0.01" />
        </div>

        {/* Functional Events */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ padding: '8px 10px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ fontSize: '10px', color: COLORS.TEXT_DIM, fontWeight: '600' }}>Branching Events ({functionalEvents.length})</label>
            <button onClick={handleAddBranch} style={{ ...btnSmall, backgroundColor: COLORS.BLUE, color: '#fff', fontSize: '10px' }}>+ Add</button>
          </div>
          {functionalEvents.map((fe, i) => (
            <div key={i} style={{ padding: '8px 10px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM, fontWeight: '600', minWidth: '20px' }}>{i + 1}.</span>
                <input value={fe.name} onChange={e => handleUpdateBranch(i, 'name', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => handleRemoveBranch(i)} style={{ ...btnSmall, backgroundColor: '#fde8e8', color: COLORS.ERROR, padding: '2px 6px', fontSize: '10px' }}>×</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '24px' }}>
                <label style={{ fontSize: '9px', color: COLORS.TEXT_DIM, whiteSpace: 'nowrap' }}>P(Success):</label>
                <input type="number" value={fe.successProbability} min="0" max="1" step="0.01"
                  onChange={e => handleUpdateBranch(i, 'successProbability', parseFloat(e.target.value) || 0)}
                  style={{ ...inputStyle, width: '70px' }} />
                <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>P(Fail): {(1 - fe.successProbability).toFixed(3)}</span>
              </div>
              {/* FTA Integration — link branch to fault tree gate or primary event */}
              {availableGates.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '24px', marginTop: '3px' }}>
                  <label style={{ fontSize: '9px', color: COLORS.TEAL, whiteSpace: 'nowrap', fontWeight: 600 }}>FTA Link:</label>
                  <select value={fe.linkedFTGate || ''} onChange={e => {
                    handleUpdateBranch(i, 'linkedFTGate', e.target.value || null);
                    // If a gate is linked, auto-compute failure probability from FTA
                    if (e.target.value && projectData?.gates) {
                      const gatesMap = {};
                      (projectData.gates || []).forEach(g => { gatesMap[g.id] = { type: g.type || 'OR', inputs: g.inputs || [] }; });
                      const eventsMap = {};
                      (projectData.primaryevents || []).forEach(ev => { eventsMap[ev.id] = { type: ev.type || 'Basic', probability: ev.probability || 0.01 }; });
                      try {
                        const ftEng = new FaultTreeEngine(gatesMap, eventsMap, {});
                        const ftRes = ftEng.analyze(e.target.value, 8760);
                        if (ftRes?.topEventProbability != null) {
                          handleUpdateBranch(i, 'successProbability', Math.max(0, 1 - ftRes.topEventProbability));
                        }
                      } catch (_) { /* ignore FTA errors */ }
                    }
                  }} style={{ ...inputStyle, width: '140px', fontSize: '9px', borderColor: COLORS.TEAL }}>
                    <option value="">— No link —</option>
                    {availableGates.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                  {fe.linkedFTGate && <span style={{ fontSize: '8px', color: COLORS.TEAL, fontWeight: 600 }}>P auto-linked from FTA</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Controls */}
        <div style={{ padding: '10px', borderTop: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
          <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '6px' }}>
            {functionalEvents.length} branches → {Math.pow(2, functionalEvents.length)} outcome sequences
          </div>
          <button onClick={handleRunPropagation} style={{ ...btnSmall, backgroundColor: COLORS.GOLD, color: '#fff', width: '100%', padding: '8px', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>
            ▶ Run Propagation
          </button>
          <button onClick={handleLoadDemo} style={{ ...btnSmall, backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, width: '100%', padding: '6px', fontSize: '11px' }}>
            Load Demo Scenario
          </button>
        </div>
      </div>

      {/* Right panel: Results */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Tab bar */}
        <div style={{ padding: '6px 12px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '4px', alignItems: 'center' }}>
          {['diagram', 'sequences', 'summary'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ ...btnSmall, backgroundColor: activeTab === tab ? COLORS.BLUE : 'transparent', color: activeTab === tab ? '#fff' : COLORS.TEXT, padding: '5px 12px' }}>
              {tab === 'diagram' ? 'Diagram' : tab === 'sequences' ? 'Sequences' : 'Summary'}
            </button>
          ))}
          {hasRun && (
            <span style={{ marginLeft: 'auto', fontSize: '10px', color: totalFreq > 0 ? COLORS.SUCCESS : COLORS.TEXT_DIM }}>
              Sum of frequencies: {totalFreq.toFixed(6)}/yr {Math.abs(totalFreq - ieFrequency) < 0.0001 ? '✓' : `(expected ${ieFrequency})`}
            </span>
          )}
        </div>

        {error && (
          <div style={{ padding: '10px 12px', backgroundColor: '#fde8e8', borderBottom: `1px solid ${COLORS.ERROR}`, color: COLORS.ERROR, fontSize: '12px' }}>{error}</div>
        )}

        <div style={{ flex: 1, overflow: 'auto' }}>
          {activeTab === 'diagram' && (
            <svg style={{ width: Math.max(800, diagramWidth), height: Math.max(400, diagramHeight), backgroundColor: '#f8f9fc' }}>
              {renderDiagram()}
            </svg>
          )}

          {activeTab === 'sequences' && (
            <div style={{ padding: '12px' }}>
              {hasRun && sequences.length > 0 ? (
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.PANEL_BG }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}`, color: COLORS.TEXT }}>#</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}`, color: COLORS.TEXT }}>Path</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}`, color: COLORS.TEXT }}>Frequency</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}`, color: COLORS.TEXT }}>% of Total</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}`, color: COLORS.TEXT }}>Consequence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sequences.sort((a, b) => b.frequency - a.frequency).map((seq) => {
                      const pct = (seq.frequency / ieFrequency * 100);
                      const color = seq.consequence === 'Safe' ? COLORS.SUCCESS : seq.consequence === 'Minor Incident' ? COLORS.WARNING : COLORS.ERROR;
                      return (
                        <tr key={seq.id} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                          <td style={{ padding: '6px 8px', color: COLORS.TEXT_DIM }}>{seq.id}</td>
                          <td style={{ padding: '6px 8px', fontSize: '10px' }}>
                            {seq.path.map((s, i) => (
                              <span key={i} style={{ color: s.outcome === 'Success' ? COLORS.SUCCESS : COLORS.ERROR, marginRight: '4px' }}>
                                {s.event.substring(0, 10)}: {s.outcome}{i < seq.path.length - 1 ? ' → ' : ''}
                              </span>
                            ))}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', color: COLORS.TEXT }}>
                            {seq.frequency >= 0.001 ? seq.frequency.toFixed(4) : seq.frequency.toExponential(3)}/yr
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: COLORS.TEXT }}>{pct.toFixed(2)}%</td>
                          <td style={{ padding: '6px 8px', color, fontWeight: '600' }}>{seq.consequence}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', textAlign: 'center', marginTop: '40px' }}>Run propagation to see sequence data</p>
              )}
            </div>
          )}

          {activeTab === 'summary' && (
            <div style={{ padding: '12px' }}>
              {hasRun && sequences.length > 0 ? (
                <>
                  {/* Category summary cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                    {['Safe', 'Minor Incident', 'Major Incident', 'Catastrophic'].map(cat => {
                      const catSeqs = sequences.filter(s => s.consequence === cat);
                      const catFreq = catSeqs.reduce((sum, s) => sum + s.frequency, 0);
                      const color = cat === 'Safe' ? COLORS.SUCCESS : cat === 'Minor Incident' ? COLORS.WARNING : cat === 'Major Incident' ? '#e67e22' : COLORS.ERROR;
                      return (
                        <div key={cat} style={{ backgroundColor: '#fff', padding: '14px', borderRadius: '6px', border: `1px solid ${COLORS.BORDER}`, textAlign: 'center' }}>
                          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>{cat}</div>
                          <div style={{ fontSize: '18px', fontWeight: 'bold', color, fontFamily: 'monospace' }}>
                            {catFreq >= 0.001 ? catFreq.toFixed(4) : catFreq.toExponential(2)}
                          </div>
                          <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>
                            {catSeqs.length} sequence{catSeqs.length !== 1 ? 's' : ''} ({(catFreq / ieFrequency * 100).toFixed(1)}%)
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Verification */}
                  <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', border: `1px solid ${COLORS.BORDER}` }}>
                    <h4 style={{ margin: '0 0 8px 0', color: COLORS.TEXT, fontSize: '12px' }}>Verification</h4>
                    <div style={{ fontSize: '11px', color: COLORS.TEXT }}>
                      <div>Initiating event frequency: <strong>{ieFrequency}/yr</strong></div>
                      <div>Sum of all sequence frequencies: <strong>{totalFreq.toFixed(6)}/yr</strong></div>
                      <div style={{ color: Math.abs(totalFreq - ieFrequency) < 0.0001 ? COLORS.SUCCESS : COLORS.ERROR, fontWeight: 'bold', marginTop: '4px' }}>
                        {Math.abs(totalFreq - ieFrequency) < 0.0001 ? '✓ Frequencies sum correctly — conservation verified' : '✗ Sum does not match initiating event frequency'}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', textAlign: 'center', marginTop: '40px' }}>Run propagation to see summary</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RESULTS PANEL
// ============================================================================
function ResultsPanel({ simResults, simConfig, cutSets, weibullData }) {
  const [activeTab, setActiveTab] = useState('summary');

  const TabButton = ({ tab, label, icon: Icon }) => (
    <button
      onClick={() => setActiveTab(tab)}
      style={{
        padding: '8px 12px',
        backgroundColor: activeTab === tab ? COLORS.BLUE : 'transparent',
        color: activeTab === tab ? '#FFFFFF' : COLORS.TEXT,
        border: 'none',
        cursor: 'pointer',
        fontSize: '12px',
        borderBottom: activeTab === tab ? `3px solid ${COLORS.GOLD}` : 'none'
      }}
    >
      {Icon && <Icon size={14} style={{ marginRight: '4px', display: 'inline' }} />}
      {label}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
        <TabButton tab="summary" label="Summary" icon={CheckCircle} />
        <TabButton tab="availability" label="Availability" icon={TrendingUp} />
        <TabButton tab="costs" label="Costs" icon={DollarSign} />
        <TabButton tab="components" label="Components" icon={BarChart3} />
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {activeTab === 'summary' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, marginTop: 0 }}>Simulation Results</h3>
            {simResults ? (
              <div>
                <div data-tour="results-kpi" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ backgroundColor: COLORS.PANEL_BG, padding: '12px', borderRadius: '4px', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>System Availability</div>
                    <div style={{ color: COLORS.SUCCESS, fontSize: '18px', fontWeight: 'bold' }}>
                      {simResults.systemAvailability}%
                    </div>
                    {simResults.confidenceBounds && (
                      <div style={{ color: COLORS.TEXT_DIM, fontSize: '10px', marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${COLORS.BORDER}` }}>
                        95% CI: {simResults.confidenceBounds.availabilityLower?.toFixed(2)}% – {simResults.confidenceBounds.availabilityUpper?.toFixed(2)}%
                      </div>
                    )}
                  </div>
                  <div style={{ backgroundColor: COLORS.PANEL_BG, padding: '12px', borderRadius: '4px', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>MTBF</div>
                    <div style={{ color: COLORS.INFO, fontSize: '18px', fontWeight: 'bold' }}>
                      {simResults.mtbf} h
                    </div>
                  </div>
                  <div style={{ backgroundColor: COLORS.PANEL_BG, padding: '12px', borderRadius: '4px', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>MTTR</div>
                    <div style={{ color: COLORS.WARNING, fontSize: '18px', fontWeight: 'bold' }}>
                      {simResults.mttr} h
                    </div>
                  </div>
                  <div style={{ backgroundColor: COLORS.PANEL_BG, padding: '12px', borderRadius: '4px', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Total Cost</div>
                    <div style={{ color: COLORS.GOLD, fontSize: '18px', fontWeight: 'bold' }}>
                      {formatCurrency(simResults.totalCost, simConfig?.currency)}
                    </div>
                    {simResults.confidenceBounds && (
                      <div style={{ color: COLORS.TEXT_DIM, fontSize: '10px', marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${COLORS.BORDER}` }}>
                        90% CI: {formatCurrency(simResults.confidenceBounds.costLower, simConfig?.currency)} – {formatCurrency(simResults.confidenceBounds.costUpper, simConfig?.currency)}
                      </div>
                    )}
                  </div>
                </div>
                <PFCurveVisualization />
              </div>
            ) : (
              <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Run simulation to see results</div>
            )}
          </div>
        )}

        {activeTab === 'availability' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, marginTop: 0 }}>Availability Profile</h3>
            {simResults?.profileData && simResults.profileData.length > 0 ? (
              <div data-tour="results-chart">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={simResults.profileData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid stroke={COLORS.BORDER} />
                  <XAxis dataKey="interval" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }} />
                  <YAxis yAxisId="left" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }} />
                  <YAxis yAxisId="right" orientation="right" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }} />
                  <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="availability"
                    stroke={COLORS.GOLD}
                    strokeWidth={2}
                    dot={{ fill: COLORS.GOLD, r: 3 }}
                    name="Availability"
                    yAxisId="left"
                  />
                  <Line
                    type="monotone"
                    dataKey="cost"
                    stroke={COLORS.BLUE}
                    strokeWidth={2}
                    dot={{ fill: COLORS.BLUE, r: 3 }}
                    name="Cost"
                    yAxisId="right"
                  />
                </LineChart>
              </ResponsiveContainer>
              </div>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>No profile data available</p>
            )}
          </div>
        )}

        {activeTab === 'costs' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, marginTop: 0 }}>Cost Breakdown</h3>
            {simResults?.costBreakdown ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: 'Labor', value: simResults.costBreakdown.labor || 0 },
                    { name: 'Spares', value: simResults.costBreakdown.spares || 0 },
                    { name: 'Consequence', value: simResults.costBreakdown.consequence || 0 },
                    { name: 'PM', value: simResults.costBreakdown.pm || 0 },
                    { name: 'Downtime', value: simResults.costBreakdown.downtime || 0 }
                  ]}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid stroke={COLORS.BORDER} />
                  <XAxis dataKey="name" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }} />
                  <YAxis stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }} />
                  <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                  <Bar dataKey="value" fill={COLORS.GOLD} name="Cost ($)" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>No cost data available</p>
            )}
          </div>
        )}

        {activeTab === 'components' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, marginTop: 0 }}>Component Results</h3>
            {simResults?.componentResults?.length > 0 ? (
              <>
                {/* Importance Ranking Chart */}
                {simResults.componentResults.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ color: COLORS.TEXT, fontSize: '12px', margin: '0 0 8px 0' }}>Importance Ranking</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart
                        data={simResults.componentResults
                          .sort((a, b) => (b.availabilityImpact || 0) - (a.availabilityImpact || 0))
                          .slice(0, 8)
                        }
                        layout="vertical"
                        margin={{ top: 5, right: 20, left: 120, bottom: 5 }}
                      >
                        <CartesianGrid stroke={COLORS.BORDER} />
                        <XAxis type="number" stroke={COLORS.TEXT_DIM} style={{ fontSize: '9px' }} />
                        <YAxis dataKey="name" type="category" stroke={COLORS.TEXT_DIM} style={{ fontSize: '9px' }} width={110} />
                        <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                        <Bar dataKey="availabilityImpact" fill={COLORS.GOLD} name="Impact" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Components Table */}
                <h4 style={{ color: COLORS.TEXT, fontSize: '12px', margin: '12px 0 8px 0' }}>All Components</h4>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG, position: 'sticky', top: 0 }}>
                        <th style={{ color: COLORS.TEXT, textAlign: 'left', padding: '6px' }}>Component</th>
                        <th style={{ color: COLORS.TEXT, textAlign: 'right', padding: '6px' }}>Availability</th>
                        <th style={{ color: COLORS.TEXT, textAlign: 'right', padding: '6px' }}>Impact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simResults.componentResults.map((comp) => (
                        <tr key={comp.id} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                          <td style={{ color: COLORS.TEXT, padding: '6px' }}>{comp.name}</td>
                          <td style={{ color: COLORS.SUCCESS, textAlign: 'right', padding: '6px' }}>{((comp.availability || 0) * 100).toFixed(2)}%</td>
                          <td style={{ color: COLORS.GOLD, textAlign: 'right', padding: '6px' }}>{(comp.availabilityImpact || 0).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>No results available</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// WEIBULL PLOT VIEW
// ============================================================================
// G18: Universal module export utility — generates branded Optimal® HTML reports from any module
function exportModuleReport(title, headers, rows, metadata = {}) {
  const now = new Date().toISOString().split('T')[0];
  let html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title>
  <style>body{font-family:'Helvetica Neue',Arial,sans-serif;color:#0D1117;max-width:1000px;margin:0 auto;padding:40px;}
  h1{color:#002F65;font-family:Georgia,serif;border-bottom:2px solid #00A8CB;padding-bottom:12px;}
  h2{color:#002F65;margin-top:24px;}
  table{width:100%;border-collapse:collapse;margin:16px 0;font-size:12px;}
  th{background:#002F65;color:#fff;padding:8px 12px;text-align:left;}
  td{padding:8px 12px;border-bottom:1px solid #E5E7EB;}tr:nth-child(even) td{background:#F8F9FA;}
  .meta{display:flex;gap:32px;margin:16px 0;flex-wrap:wrap;}.meta-item{font-size:12px;color:#6B7280;}
  .meta-val{font-size:18px;font-weight:700;color:#002F65;}
  .footer{margin-top:40px;padding-top:16px;border-top:1px solid #D8D3CC;font-size:11px;color:#6B7280;display:flex;justify-content:space-between;}
  @media print{body{padding:20mm;font-size:10px;}}</style></head><body>
  <div style="font-size:11px;color:#00A8CB;letter-spacing:2px;text-transform:uppercase;margin-bottom:8px;">Optimal Asset Management SA</div>
  <h1>${title}</h1><p style="color:#6B7280;font-size:13px;">Generated ${now}</p>`;
  if (Object.keys(metadata).length > 0) {
    html += '<div class="meta">' + Object.entries(metadata).map(([k,v]) => `<div class="meta-item"><div class="meta-val">${v}</div>${k}</div>`).join('') + '</div>';
  }
  html += '<table><tr>' + headers.map(h => `<th>${h}</th>`).join('') + '</tr>';
  rows.forEach(row => { html += '<tr>' + row.map(c => `<td>${c ?? '—'}</td>`).join('') + '</tr>'; });
  html += `</table><div class="footer"><span>www.optimal.world</span><span>OptimalAvailability Studio</span></div></body></html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `Optimal-${title.replace(/[^a-zA-Z0-9]/g, '-')}-${now}.html`; a.click();
  URL.revokeObjectURL(url);
}

function WeibullPlotView({ weibullData: initialData, selectedSet, weibullSets = [], projectData, setProjectData }) {
  const [weibullData, setWeibullData] = useState(initialData);
  const [dataEntries, setDataEntries] = useState([]);
  const [newTime, setNewTime] = useState('');
  const [newIsFailed, setNewIsFailed] = useState(true);
  const [bulkInput, setBulkInput] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [analysisMethod, setAnalysisMethod] = useState('MRR');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [dataSetName, setDataSetName] = useState('New Analysis');
  const [activeView, setActiveView] = useState('input'); // 'input' or 'saved'

  // Allow user to switch between saved Weibull sets
  const handleSetChange = (e) => {
    const set = weibullSets.find(s => s.id === e.target.value);
    if (set) {
      setWeibullData({ beta: set.beta, eta: set.eta, r2: set.rSquared || set.r2, name: set.description });
    }
  };

  // Add a single data point
  const handleAddPoint = () => {
    const val = parseFloat(newTime);
    if (isNaN(val) || val <= 0) {
      setAnalysisError('Enter a positive number for time.');
      return;
    }
    setDataEntries(prev => [...prev, { time: val, isFailed: newIsFailed, id: Date.now() }]);
    setNewTime('');
    setAnalysisError(null);
  };

  // Handle Enter key in the time input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAddPoint();
  };

  // Remove a data point
  const handleRemovePoint = (id) => {
    setDataEntries(prev => prev.filter(d => d.id !== id));
  };

  // Parse bulk input (comma, space, newline separated)
  const handleBulkAdd = () => {
    const values = bulkInput.split(/[\s,;]+/).filter(v => v.trim() !== '');
    const newPoints = [];
    const errors = [];
    values.forEach((v, i) => {
      // Support "100s" for suspended, plain number for failure
      const isSuspended = v.toLowerCase().endsWith('s');
      const num = parseFloat(isSuspended ? v.slice(0, -1) : v);
      if (isNaN(num) || num <= 0) {
        errors.push(`"${v}" at position ${i + 1}`);
      } else {
        newPoints.push({ time: num, isFailed: !isSuspended, id: Date.now() + i });
      }
    });
    if (newPoints.length > 0) {
      setDataEntries(prev => [...prev, ...newPoints]);
      setBulkInput('');
      setShowBulkInput(false);
    }
    if (errors.length > 0) {
      setAnalysisError(`Skipped invalid values: ${errors.join(', ')}`);
    } else {
      setAnalysisError(null);
    }
  };

  // Clear all data
  const handleClearData = () => {
    if (dataEntries.length > 0 && !window.confirm('Clear all data points?')) return;
    setDataEntries([]);
    setAnalysisResult(null);
    setAnalysisError(null);
  };

  // Load sample data
  const handleLoadSample = () => {
    const sampleData = [
      { time: 100, isFailed: true }, { time: 200, isFailed: true },
      { time: 350, isFailed: true }, { time: 500, isFailed: true },
      { time: 650, isFailed: true }, { time: 800, isFailed: true },
      { time: 1050, isFailed: true }, { time: 1400, isFailed: true },
      { time: 1900, isFailed: true }, { time: 2500, isFailed: true },
      { time: 750, isFailed: false }, { time: 1200, isFailed: false }
    ].map((d, i) => ({ ...d, id: Date.now() + i }));
    setDataEntries(sampleData);
    setDataSetName('Sample Bearing Data');
    setAnalysisError(null);
  };

  // Run Weibull analysis
  const handleRunAnalysis = () => {
    setAnalysisError(null);
    const failures = dataEntries.filter(d => d.isFailed);
    const suspensions = dataEntries.filter(d => !d.isFailed);

    if (failures.length < 2) {
      setAnalysisError('Need at least 2 failure data points to run Weibull analysis.');
      return;
    }

    // Check for all-identical failure times (causes regression to fail)
    const uniqueTimes = new Set(failures.map(d => d.time));
    if (uniqueTimes.size === 1) {
      setAnalysisError('All failure times are identical. Weibull analysis requires variation in the data.');
      return;
    }

    // Check for extremely large values that could cause overflow
    const maxVal = Math.max(...failures.map(d => d.time));
    if (maxVal > 1e12) {
      setAnalysisError('Time values are extremely large (>1 trillion). Please check your data units.');
      return;
    }

    try {
      const wa = new WeibullAnalysis(
        failures.map(d => ({ time: d.time, isFailed: true })),
        suspensions.map(d => ({ time: d.time, isFailed: false }))
      );

      let result;
      if (analysisMethod === 'MRR') {
        result = wa.medianRankRegression();
      } else {
        result = wa.maximumLikelihood();
      }

      // Generate plot data from actual results
      const beta = result.beta;
      const eta = result.eta;
      const maxT = Math.max(...dataEntries.map(d => d.time)) * 1.5;

      const curveData = [];
      for (let t = 0; t <= maxT; t += maxT / 100) {
        const F = 1 - Math.exp(-Math.pow(t / eta, beta));
        curveData.push({ time: Math.round(t), cdf: F * 100 });
      }

      // Actual failure data points with median rank positions
      const sortedFailures = [...failures].sort((a, b) => a.time - b.time);
      const n = dataEntries.length;
      const scatterData = sortedFailures.map((d, i) => {
        const medianRank = ((i + 1) - 0.3) / (n + 0.4); // Bernard's approximation
        return { time: d.time, cdf: medianRank * 100 };
      });

      // Suspension markers
      const suspensionMarkers = suspensions.map(d => {
        const F = 1 - Math.exp(-Math.pow(d.time / eta, beta));
        return { time: d.time, cdf: F * 100 };
      });

      setAnalysisResult({ ...result, curveData, scatterData, suspensionMarkers });
      setWeibullData({ beta: result.beta, eta: result.eta, r2: result.rSquared, name: dataSetName });
    } catch (e) {
      setAnalysisError(e.message);
    }
  };

  // Save results to project
  const handleSaveToProject = () => {
    if (!analysisResult || !setProjectData) return;
    if (!dataSetName.trim()) {
      setAnalysisError('Please enter a dataset name before saving.');
      return;
    }
    // Guard against NaN/Infinity in results
    if (!isFinite(analysisResult.beta) || !isFinite(analysisResult.eta)) {
      setAnalysisError('Analysis produced invalid results. Check your data and try again.');
      return;
    }
    const newSet = {
      id: `WB-${String((projectData?.weibullsets || []).length + 1).padStart(3, '0')}`,
      description: dataSetName.trim(),
      failureData: String(dataEntries.filter(d => d.isFailed).length),
      suspensionData: String(dataEntries.filter(d => !d.isFailed).length),
      estimationMethod: analysisMethod === 'MRR' ? 'Rank Regression' : 'MLE',
      beta: parseFloat(analysisResult.beta.toFixed(4)),
      eta: parseFloat(analysisResult.eta.toFixed(2)),
      rSquared: parseFloat(analysisResult.rSquared.toFixed(4)),
      mttf: parseFloat((analysisResult.estimatedMTTF || 0).toFixed(0)),
      rawData: dataEntries.map(d => ({ time: d.time, isFailed: d.isFailed }))
    };
    setProjectData(prev => {
      const updated = { ...prev, weibullsets: [...(prev.weibullsets || []), newSet] };
      // G01: Also write β/η/MTTF to the linked equipment record so other modules can access it
      if (newSet.equipmentId) {
        updated.equipments = (updated.equipments || []).map(eq =>
          eq.id === newSet.equipmentId ? { ...eq, weibullBeta: newSet.beta, weibullEta: newSet.eta, weibullMTTF: newSet.mttf, failureRate: newSet.mttf > 0 ? 1 / newSet.mttf : 0, weibullSetId: newSet.id } : eq
        );
      }
      return updated;
    });
    setAnalysisError(null);
    alert(`Saved as ${newSet.id} — ${dataSetName.trim()}`);
  };

  // Failure pattern interpretation
  const getFailurePattern = (beta) => {
    if (!beta) return '';
    if (beta < 0.95) return 'Early-Life (Infant Mortality) — β < 1 indicates decreasing failure rate. Consider burn-in testing or quality screening.';
    if (beta <= 1.05) return 'Random Failures — β ≈ 1 indicates constant failure rate (exponential). Condition monitoring is recommended.';
    if (beta <= 2.5) return 'Early Wear-Out — β between 1 and 2.5 indicates gradually increasing failure rate. Time-based preventive maintenance is effective.';
    return 'Rapid Wear-Out — β > 2.5 indicates strongly increasing failure rate. Scheduled replacement before characteristic life is critical.';
  };

  // Generate plot data for saved sets view
  const generateSavedPlot = () => {
    if (!weibullData) return null;
    const beta = weibullData.beta || 2.0;
    const eta = weibullData.eta || 1000;
    const curveData = [];
    const maxT = 2 * eta;
    for (let t = 0; t <= maxT; t += maxT / 100) {
      const F = 1 - Math.exp(-Math.pow(t / eta, beta));
      curveData.push({ time: Math.round(t), cdf: F * 100 });
    }
    return curveData;
  };

  const failureCount = dataEntries.filter(d => d.isFailed).length;
  const suspensionCount = dataEntries.filter(d => !d.isFailed).length;

  const inputStyle = { backgroundColor: '#fff', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '6px 8px', fontSize: '12px' };
  const btnStyle = { backgroundColor: COLORS.BLUE, color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' };
  const btnSecStyle = { ...btnStyle, backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}` };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Header with view toggle */}
      <div style={{ padding: '8px 12px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <h3 style={{ margin: 0, color: COLORS.TEXT, fontSize: '14px' }}>Weibull Analysis</h3>
        <div style={{ display: 'flex', gap: '2px', backgroundColor: COLORS.LIGHT_BG, borderRadius: '4px', padding: '2px' }}>
          <button onClick={() => setActiveView('input')} style={{ ...btnSecStyle, padding: '4px 12px', backgroundColor: activeView === 'input' ? COLORS.BLUE : 'transparent', color: activeView === 'input' ? '#fff' : COLORS.TEXT, border: 'none', fontSize: '11px' }}>
            Data Input & Analysis
          </button>
          <button onClick={() => setActiveView('saved')} style={{ ...btnSecStyle, padding: '4px 12px', backgroundColor: activeView === 'saved' ? COLORS.BLUE : 'transparent', color: activeView === 'saved' ? '#fff' : COLORS.TEXT, border: 'none', fontSize: '11px' }}>
            Saved Datasets ({weibullSets.length})
          </button>
        </div>
      </div>

      {activeView === 'input' ? (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left panel: Data Entry */}
          <div style={{ width: '320px', borderRight: `1px solid ${COLORS.BORDER}`, display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
            {/* Dataset name */}
            <div style={{ padding: '10px 12px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <label style={{ display: 'block', fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Dataset Name</label>
              <input value={dataSetName} onChange={e => setDataSetName(e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
            </div>

            {/* Add single point */}
            <div style={{ padding: '10px 12px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="number" placeholder="Time to failure..." value={newTime}
                  onChange={e => setNewTime(e.target.value)} onKeyDown={handleKeyDown}
                  style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                  min="0" step="any"
                />
                <select value={newIsFailed} onChange={e => setNewIsFailed(e.target.value === 'true')} style={{ ...inputStyle, width: '90px' }}>
                  <option value="true">Failed</option>
                  <option value="false">Suspended</option>
                </select>
                <button onClick={handleAddPoint} style={{ ...btnStyle, padding: '6px 10px' }}>Add</button>
              </div>
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                <button onClick={() => setShowBulkInput(!showBulkInput)} style={{ ...btnSecStyle, flex: 1, fontSize: '11px', padding: '4px' }}>
                  {showBulkInput ? 'Hide Bulk Input' : 'Bulk Input'}
                </button>
                <button onClick={handleLoadSample} style={{ ...btnSecStyle, flex: 1, fontSize: '11px', padding: '4px' }}>
                  Load Sample
                </button>
                <button onClick={handleClearData} style={{ ...btnSecStyle, flex: 1, fontSize: '11px', padding: '4px', color: COLORS.ERROR }}>
                  Clear All
                </button>
              </div>
            </div>

            {/* Bulk input area */}
            {showBulkInput && (
              <div style={{ padding: '10px 12px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: '#f8f9fa' }}>
                <label style={{ display: 'block', fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>
                  Paste failure times (comma/space/newline separated). Add "s" suffix for suspended, e.g. "750s"
                </label>
                <textarea
                  value={bulkInput} onChange={e => setBulkInput(e.target.value)}
                  placeholder="100, 200, 350, 500, 750s, 800, 1200s"
                  style={{ ...inputStyle, width: '100%', height: '60px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace' }}
                />
                <button onClick={handleBulkAdd} style={{ ...btnStyle, marginTop: '4px', width: '100%' }}>Add All</button>
              </div>
            )}

            {/* Data table */}
            <div style={{ flex: 1, overflow: 'auto', fontSize: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.PANEL_BG, position: 'sticky', top: 0 }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM, fontSize: '11px' }}>#</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM, fontSize: '11px' }}>Time</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM, fontSize: '11px' }}>Type</th>
                    <th style={{ padding: '6px 4px', textAlign: 'center', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM, fontSize: '11px', width: '30px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {dataEntries.sort((a, b) => a.time - b.time).map((d, i) => (
                    <tr key={d.id} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                      <td style={{ padding: '4px 8px', color: COLORS.TEXT_DIM }}>{i + 1}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{d.time.toLocaleString()}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <span style={{ padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: '500', backgroundColor: d.isFailed ? '#fde8e8' : '#e8f5e8', color: d.isFailed ? COLORS.ERROR : COLORS.SUCCESS }}>
                          {d.isFailed ? 'Failed' : 'Suspended'}
                        </span>
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <button onClick={() => handleRemovePoint(d.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.TEXT_DIM, fontSize: '14px', padding: '0 4px' }} title="Remove">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {dataEntries.length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>
                  No data entered yet. Add failure times above, use Bulk Input, or click Load Sample.
                </div>
              )}
            </div>

            {/* Summary and analysis controls */}
            <div style={{ padding: '10px 12px', borderTop: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
              <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '8px' }}>
                {failureCount} failures, {suspensionCount} suspensions — {dataEntries.length} total
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select value={analysisMethod} onChange={e => setAnalysisMethod(e.target.value)} style={{ ...inputStyle, width: '100px' }}>
                  <option value="MRR">MRR</option>
                  <option value="MLE">MLE</option>
                </select>
                <button onClick={handleRunAnalysis} style={{ ...btnStyle, flex: 1, backgroundColor: COLORS.BLUE, padding: '8px' }}>
                  ▶ Analyse ({analysisMethod})
                </button>
              </div>
            </div>
          </div>

          {/* Right panel: Results */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', padding: '12px' }}>
            {analysisError && (
              <div style={{ padding: '10px', backgroundColor: '#fde8e8', border: `1px solid ${COLORS.ERROR}`, borderRadius: '4px', color: COLORS.ERROR, fontSize: '12px', marginBottom: '12px' }}>
                {analysisError}
              </div>
            )}

            {analysisResult ? (
              <>
                {/* Parameters display */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>Shape (β)</div>
                    <div style={{ color: COLORS.GOLD, fontSize: '22px', fontWeight: 'bold' }}>{analysisResult.beta.toFixed(3)}</div>
                  </div>
                  <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>Scale (η)</div>
                    <div style={{ color: COLORS.INFO, fontSize: '22px', fontWeight: 'bold' }}>{analysisResult.eta.toFixed(0)}</div>
                  </div>
                  <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>R² Goodness</div>
                    <div style={{ color: analysisResult.rSquared > 0.9 ? COLORS.SUCCESS : COLORS.WARNING, fontSize: '22px', fontWeight: 'bold' }}>{analysisResult.rSquared.toFixed(4)}</div>
                  </div>
                  <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>MTTF</div>
                    <div style={{ color: COLORS.TEXT, fontSize: '22px', fontWeight: 'bold' }}>{(analysisResult.estimatedMTTF || 0).toFixed(0)}</div>
                  </div>
                </div>

                {/* Failure pattern interpretation */}
                <div style={{ backgroundColor: '#f0f7ff', border: `1px solid ${COLORS.BLUE}33`, borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', fontSize: '12px', color: COLORS.TEXT }}>
                  <strong style={{ color: COLORS.BLUE }}>Failure Pattern: </strong>
                  {getFailurePattern(analysisResult.beta)}
                </div>

                {/* Weibull Plot */}
                <div style={{ backgroundColor: '#fff', borderRadius: '6px', border: `1px solid ${COLORS.BORDER}`, padding: '12px', marginBottom: '12px' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: COLORS.TEXT, fontSize: '13px' }}>Weibull Probability Plot</h4>
                  <ResponsiveContainer width="100%" height={320}>
                    <ComposedChart data={analysisResult.curveData} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
                      <CartesianGrid stroke={COLORS.BORDER} strokeDasharray="3 3" />
                      <XAxis dataKey="time" type="number" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }}
                        label={{ value: 'Time (hours)', position: 'insideBottom', offset: -10, fill: COLORS.TEXT_DIM }} />
                      <YAxis stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }} domain={[0, 100]}
                        label={{ value: 'Unreliability F(t) %', angle: -90, position: 'insideLeft', fill: COLORS.TEXT_DIM }} />
                      <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                      <Line type="monotone" dataKey="cdf" stroke={COLORS.BLUE} strokeWidth={2} dot={false} name="Weibull Fit" isAnimationActive={false} />
                      <Scatter name="Failure Data" data={analysisResult.scatterData} dataKey="cdf" fill={COLORS.GOLD} shape="circle" />
                      {analysisResult.suspensionMarkers.length > 0 && (
                        <Scatter name="Suspended" data={analysisResult.suspensionMarkers} dataKey="cdf" fill={COLORS.SUCCESS} shape="triangle" />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Save button */}
                <button onClick={handleSaveToProject} style={{ ...btnStyle, backgroundColor: COLORS.SUCCESS, padding: '10px', fontSize: '13px', width: '100%' }}>
                  Save to Project as Weibull Set
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.TEXT_DIM }}>
                <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.3 }}>📊</div>
                <p style={{ fontSize: '14px', margin: '0 0 6px 0' }}>Enter failure data and click Analyse</p>
                <p style={{ fontSize: '12px', margin: 0 }}>Results with Weibull plot, parameters, and failure pattern interpretation will appear here</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Saved Datasets View */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '12px', overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, color: COLORS.TEXT, fontSize: '13px' }}>Saved Weibull Datasets</h4>
            {weibullSets.length > 0 && (
              <select onChange={handleSetChange} defaultValue={weibullSets[0]?.id} style={{ ...inputStyle }}>
                {weibullSets.map(s => <option key={s.id} value={s.id}>{s.id} — {s.description}</option>)}
              </select>
            )}
          </div>
          {weibullData && generateSavedPlot() ? (
            <>
              <div style={{ backgroundColor: '#fff', borderRadius: '6px', border: `1px solid ${COLORS.BORDER}`, padding: '12px', marginBottom: '12px' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={generateSavedPlot()} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid stroke={COLORS.BORDER} />
                    <XAxis dataKey="time" type="number" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }}
                      label={{ value: 'Time (hours)', position: 'insideBottom', offset: -5, fill: COLORS.TEXT_DIM }} />
                    <YAxis stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }} domain={[0, 100]}
                      label={{ value: 'CDF (%)', angle: -90, position: 'insideLeft', fill: COLORS.TEXT_DIM }} />
                    <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                    <Line type="monotone" dataKey="cdf" stroke={COLORS.BLUE} strokeWidth={2} dot={false} name="Weibull CDF" isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                  <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Shape (β)</div>
                  <div style={{ color: COLORS.GOLD, fontSize: '20px', fontWeight: 'bold' }}>{(weibullData.beta || 0).toFixed(3)}</div>
                </div>
                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                  <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Scale (η)</div>
                  <div style={{ color: COLORS.INFO, fontSize: '20px', fontWeight: 'bold' }}>{(weibullData.eta || 0).toFixed(0)}</div>
                </div>
                <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                  <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Goodness (R²)</div>
                  <div style={{ color: COLORS.SUCCESS, fontSize: '20px', fontWeight: 'bold' }}>{(weibullData.r2 || 0).toFixed(3)}</div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>No saved Weibull datasets. Run an analysis and save the results.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// RCM WORKSHEET
// ============================================================================
function RCMWorksheet({ rcmCauses = [], onUpdate, onAddRow, onDeleteRow }) {
  const [sortBy, setSortBy] = useState('rpn-desc');
  const [editingCell, setEditingCell] = useState(null);

  const calculateRPN = (s, o, d) => (s || 1) * (o || 1) * (d || 1);

  const getRPNColor = (rpn) => {
    if (rpn > 200) return COLORS.ERROR;
    if (rpn >= 100) return COLORS.WARNING;
    return COLORS.SUCCESS;
  };

  const sortedRows = [...rcmCauses].sort((a, b) => {
    if (sortBy === 'rpn-desc') {
      return calculateRPN(b.severity, b.occurrence, b.detection) - calculateRPN(a.severity, a.occurrence, a.detection);
    }
    if (sortBy === 'rpn-asc') {
      return calculateRPN(a.severity, a.occurrence, a.detection) - calculateRPN(b.severity, b.occurrence, b.detection);
    }
    return 0;
  });

  const handleCellEdit = (rowId, field, value) => {
    onUpdate?.(rowId, { [field]: parseInt(value, 10) || 0 });
    setEditingCell(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '8px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={onAddRow}
          style={{ padding: '6px 12px', backgroundColor: COLORS.NAVY, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px' }}
        >
          Add Row
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: COLORS.TEXT, fontSize: '11px', marginLeft: 'auto' }}>
          Sort by RPN:
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              backgroundColor: COLORS.LIGHT_BG,
              color: COLORS.TEXT,
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: '4px',
              padding: '4px',
              fontSize: '11px',
              cursor: 'pointer'
            }}
          >
            <option value="rpn-desc">Highest First</option>
            <option value="rpn-asc">Lowest First</option>
          </select>
        </label>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, position: 'sticky', top: 0 }}>
              <th style={{ padding: '6px', textAlign: 'left', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}`, width: '200px' }}>Failure Mode</th>
              <th style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}`, width: '50px' }}>S (1-10)</th>
              <th style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}`, width: '50px' }}>O (1-10)</th>
              <th style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}`, width: '50px' }}>D (1-10)</th>
              <th style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, width: '60px', fontWeight: 'bold' }}>RPN</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => {
              const rpn = calculateRPN(row.severity, row.occurrence, row.detection);
              return (
                <tr
                  key={row.id}
                  style={{
                    backgroundColor: idx % 2 === 0 ? COLORS.PANEL_BG : COLORS.LIGHT_BG,
                    borderBottom: `1px solid ${COLORS.BORDER}`
                  }}
                >
                  <td style={{ padding: '6px', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>
                    {row.failureMode}
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>
                    {editingCell?.id === row.id && editingCell?.field === 'severity' ? (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={row.severity || 1}
                        onChange={(e) => handleCellEdit(row.id, 'severity', e.target.value)}
                        autoFocus
                        style={{
                          width: '30px',
                          padding: '2px',
                          backgroundColor: COLORS.LIGHT_BG,
                          color: COLORS.TEXT,
                          border: `1px solid ${COLORS.GOLD}`,
                          borderRadius: '2px',
                          textAlign: 'center'
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => setEditingCell({ id: row.id, field: 'severity' })}
                        style={{ cursor: 'pointer', padding: '2px', borderRadius: '2px', userSelect: 'none' }}
                      >
                        {row.severity || 1}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>
                    {editingCell?.id === row.id && editingCell?.field === 'occurrence' ? (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={row.occurrence || 1}
                        onChange={(e) => handleCellEdit(row.id, 'occurrence', e.target.value)}
                        autoFocus
                        style={{
                          width: '30px',
                          padding: '2px',
                          backgroundColor: COLORS.LIGHT_BG,
                          color: COLORS.TEXT,
                          border: `1px solid ${COLORS.GOLD}`,
                          borderRadius: '2px',
                          textAlign: 'center'
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => setEditingCell({ id: row.id, field: 'occurrence' })}
                        style={{ cursor: 'pointer', padding: '2px', borderRadius: '2px', userSelect: 'none' }}
                      >
                        {row.occurrence || 1}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>
                    {editingCell?.id === row.id && editingCell?.field === 'detection' ? (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={row.detection || 1}
                        onChange={(e) => handleCellEdit(row.id, 'detection', e.target.value)}
                        autoFocus
                        style={{
                          width: '30px',
                          padding: '2px',
                          backgroundColor: COLORS.LIGHT_BG,
                          color: COLORS.TEXT,
                          border: `1px solid ${COLORS.GOLD}`,
                          borderRadius: '2px',
                          textAlign: 'center'
                        }}
                      />
                    ) : (
                      <div
                        onClick={() => setEditingCell({ id: row.id, field: 'detection' })}
                        style={{ cursor: 'pointer', padding: '2px', borderRadius: '2px', userSelect: 'none' }}
                      >
                        {row.detection || 1}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      padding: '6px',
                      textAlign: 'center',
                      color: getRPNColor(rpn),
                      fontWeight: 'bold',
                      backgroundColor: rpn > 200 ? 'rgba(255, 107, 107, 0.1)' : rpn >= 100 ? 'rgba(255, 193, 7, 0.1)' : 'transparent'
                    }}
                  >
                    {rpn}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// LIFECYCLE ASSESSMENT VIEW
// ISO 55001 / ISO 15686-5 / IEC 60300-3-3 / ISO 13381-1 aligned
// ============================================================================
function LifecycleAssessmentView({ costNodes = [], simConfig, projectData, setProjectData }) {
  // ---- Active assessment ----
  const assessments = projectData?.lifecycleAssessments || [];
  const [activeId, setActiveId] = useState(null);
  const active = assessments.find(a => a.id === activeId) || null;
  const [activeTab, setActiveTab] = useState('profile');

  // ---- Condition score converters (1-5 ↔ 0-100) ----
  const scoreToHealth = (s) => Math.max(0, Math.min(100, Math.round(100 - (s - 1) * 25)));
  const healthToScore = (h) => Math.max(1, Math.min(5, Math.round(1 + (100 - h) / 25)));

  // ---- Currency ----
  const [currency, setCurrency] = useState(simConfig?.currency || 'USD');

  // ---- Shared styles ----
  const inputStyle = { backgroundColor: '#fff', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '6px 8px', fontSize: '12px', width: '100%', boxSizing: 'border-box' };
  const btnPrimary = { backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' };
  const btnSec = { backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer' };
  const cardStyle = { backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' };
  const labelStyle = { display: 'block', fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '3px' };
  const statCard = (label, value, unit, color) => (
    <div style={cardStyle}>
      <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>{label}</div>
      <div style={{ color: color || COLORS.TEAL, fontSize: '22px', fontWeight: 'bold' }}>{value}</div>
      {unit && <div style={{ color: COLORS.TEXT_DIM, fontSize: '10px', marginTop: '2px' }}>{unit}</div>}
    </div>
  );

  // ---- Save helper ----
  const updateAssessment = (id, updater) => {
    setProjectData(prev => ({
      ...prev,
      lifecycleAssessments: (prev.lifecycleAssessments || []).map(a =>
        a.id === id ? (typeof updater === 'function' ? updater(a) : { ...a, ...updater }) : a
      )
    }));
  };

  // ---- Create new assessment ----
  const handleNewAssessment = () => {
    const equipments = projectData?.equipments || [];
    const newA = {
      id: Date.now(),
      name: `Assessment ${assessments.length + 1}`,
      equipmentId: equipments.length > 0 ? equipments[0].id : '',
      assetProfile: {
        installDate: new Date(Date.now() - 10 * 365.25 * 24 * 3600000).toISOString().slice(0, 10),
        designLife: 25,
        replacementCost: 500000,
        salvageValue: 25000,
        criticalityRating: 'High',
        assetType: 'rotating'
      },
      conditionHistory: [],
      costData: {
        annualMaintenance: 15000, // G10: Consider auto-populating from sum of scheduledtasks[].cost for linked equipment
        autoMaintenanceCost: null, // Will be populated from RCM/PM tasks when available
        annualOperating: 25000,
        annualDowntime: 10000,
        overhaulCost: 150000,
        overhaulLifeExtension: 10,
        disposalCost: 30000,
        upgradeCost: 600000,
        upgradeDesignLife: 30,
        pmCostMultiplier: 1.3,
        pmLifeMultiplier: 1.5
      },
      analysisParams: {
        discountRate: 8,
        escalationRate: 3,
        analysisHorizon: 30
      },
      weibullOverride: null,
      options: null,
      costProfiles: null,
      monteCarloResults: null,
      recommendation: null
    };
    setProjectData(prev => ({
      ...prev,
      lifecycleAssessments: [...(prev.lifecycleAssessments || []), newA]
    }));
    setActiveId(newA.id);
    setActiveTab('profile');
  };

  const handleDeleteAssessment = (id) => {
    setProjectData(prev => ({
      ...prev,
      lifecycleAssessments: (prev.lifecycleAssessments || []).filter(a => a.id !== id)
    }));
    if (activeId === id) setActiveId(null);
  };

  // ============================================================================
  // RUL CALCULATION ENGINE
  // ============================================================================
  const calculateRUL = (assessment) => {
    const prof = assessment.assetProfile;
    const installDate = new Date(prof.installDate);
    const currentAge = (Date.now() - installDate.getTime()) / (365.25 * 24 * 3600000);
    const designLife = prof.designLife || 25;

    // --- Weibull-based RUL (ISO 13381-1) ---
    let weibullRUL = null;
    const wb = assessment.weibullOverride;
    if (wb && wb.beta > 0 && wb.eta > 0) {
      const ageHours = currentAge * 8760;
      // Target absolute R = 0.368 (characteristic life, R = 1/e ≈ 36.8%)
      // This is the industry-standard threshold per IEC 61649 / ISO 13381-1
      const targetR = 0.368;
      const t_target = wb.eta * Math.pow(-Math.log(targetR), 1 / wb.beta);
      weibullRUL = Math.max(0, (t_target - ageHours) / 8760);
    }

    // --- Condition-based RUL ---
    let conditionRUL = null;
    const history = assessment.conditionHistory || [];
    const failureThreshold = 20; // health index at functional failure
    if (history.length >= 2) {
      const sorted = [...history].sort((a, b) => new Date(a.date) - new Date(b.date));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const h0 = first.healthIndex;
      const h1 = last.healthIndex;
      const dt = (new Date(last.date) - new Date(first.date)) / (365.25 * 24 * 3600000);

      // Linear degradation (2-3 points, or always as one estimate)
      let linearRUL = null;
      if (dt > 0 && h0 > h1) {
        const degradeRate = (h0 - h1) / dt;
        linearRUL = Math.max(0, (h1 - failureThreshold) / degradeRate);
      }

      // Exponential degradation (≥4 points: fit h(t) = A × e^(-λt))
      // Uses log-linear regression: ln(h) = ln(A) - λt
      let exponentialRUL = null;
      if (history.length >= 4 && h1 > failureThreshold) {
        const t0 = new Date(first.date).getTime();
        const points = sorted.filter(p => p.healthIndex > 0).map(p => ({
          t: (new Date(p.date).getTime() - t0) / (365.25 * 24 * 3600000),
          lnH: Math.log(Math.max(1, p.healthIndex))
        }));
        if (points.length >= 4) {
          const n = points.length;
          const sumT = points.reduce((s, p) => s + p.t, 0);
          const sumLnH = points.reduce((s, p) => s + p.lnH, 0);
          const sumTLnH = points.reduce((s, p) => s + p.t * p.lnH, 0);
          const sumT2 = points.reduce((s, p) => s + p.t * p.t, 0);
          const denom = n * sumT2 - sumT * sumT;
          if (Math.abs(denom) > 1e-10) {
            const lambda = -(n * sumTLnH - sumT * sumLnH) / denom;
            const lnA = (sumLnH - (-lambda) * sumT) / n;
            // Only use if decay rate is positive (health decreasing)
            if (lambda > 0) {
              // Solve A × e^(-λt) = threshold for t, then RUL = t - currentAge
              const tFromFirst = (new Date(last.date).getTime() - t0) / (365.25 * 24 * 3600000);
              const tFailure = (lnA - Math.log(failureThreshold)) / lambda;
              exponentialRUL = Math.max(0, tFailure - tFromFirst);
            }
          }
        }
      }

      // Take conservative (minimum) of available condition estimates
      if (linearRUL !== null && exponentialRUL !== null) {
        conditionRUL = Math.min(linearRUL, exponentialRUL);
      } else {
        conditionRUL = linearRUL; // linear is always available when we reach here
      }
    }

    // --- Combined RUL ---
    let combinedRUL;
    if (weibullRUL !== null && conditionRUL !== null) {
      combinedRUL = Math.min(weibullRUL, conditionRUL);
    } else if (weibullRUL !== null) {
      combinedRUL = weibullRUL;
    } else if (conditionRUL !== null) {
      combinedRUL = conditionRUL;
    } else {
      combinedRUL = Math.max(0, designLife - currentAge);
    }

    // Current health index
    let currentHealth = 100;
    if (history.length > 0) {
      const latest = [...history].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      currentHealth = latest.healthIndex;
    } else {
      currentHealth = Math.max(0, Math.round(100 * (1 - currentAge / designLife)));
    }

    return { currentAge, designLife, weibullRUL, conditionRUL, combinedRUL, currentHealth };
  };

  // ============================================================================
  // LIFECYCLE OPTIONS CALCULATOR
  // ============================================================================
  const calculateLifecycleOptions = (assessment) => {
    const rul = calculateRUL(assessment);
    const cost = assessment.costData;
    const params = assessment.analysisParams;
    const rate = params.discountRate / 100;
    const esc = params.escalationRate / 100;
    const horizon = params.analysisHorizon;

    const npvStream = (annualCost, years, capex = 0) => {
      let npv = capex;
      for (let y = 1; y <= years; y++) {
        npv += (annualCost * Math.pow(1 + esc, y)) / Math.pow(1 + rate, y);
      }
      return npv;
    };
    const eac = (npv, years) => {
      if (rate < 1e-10) return npv / years;
      return npv * (rate * Math.pow(1 + rate, years)) / (Math.pow(1 + rate, years) - 1);
    };

    const annualTotal = cost.annualMaintenance + cost.annualOperating + cost.annualDowntime;
    const remainingYears = Math.max(1, Math.round(rul.combinedRUL));

    // --- Option 1: Continue as-is ---
    const opt1Years = Math.min(remainingYears, horizon);
    const opt1NPV = npvStream(annualTotal, opt1Years) + (cost.disposalCost / Math.pow(1 + rate, opt1Years));
    const opt1EAC = eac(opt1NPV, opt1Years);

    // --- Option 2: Major overhaul ---
    const overhaulYear = Math.max(1, Math.round(remainingYears * 0.7));
    const postOverhaulYears = cost.overhaulLifeExtension;
    const totalOverhaulLife = overhaulYear + postOverhaulYears;
    const opt2NPV = npvStream(annualTotal, overhaulYear)
      + (cost.overhaulCost / Math.pow(1 + rate, overhaulYear))
      + npvStream(annualTotal * 0.7, postOverhaulYears, 0) / Math.pow(1 + rate, overhaulYear)
      + (cost.disposalCost / Math.pow(1 + rate, totalOverhaulLife));
    const opt2EAC = eac(opt2NPV, totalOverhaulLife);

    // --- Option 3: Replace ---
    const replaceYear = Math.max(1, Math.round(remainingYears * 0.8));
    const newAssetLife = cost.upgradeDesignLife || assessment.assetProfile.designLife;
    const totalReplaceLife = replaceYear + newAssetLife;
    const replCost = cost.upgradeCost || assessment.assetProfile.replacementCost;
    const opt3NPV = npvStream(annualTotal, replaceYear)
      + (replCost / Math.pow(1 + rate, replaceYear))
      - (assessment.assetProfile.salvageValue / Math.pow(1 + rate, replaceYear))
      + npvStream(annualTotal * 0.5, newAssetLife, 0) / Math.pow(1 + rate, replaceYear)
      + (cost.disposalCost / Math.pow(1 + rate, totalReplaceLife));
    const opt3EAC = eac(opt3NPV, Math.min(totalReplaceLife, horizon));

    // --- Option 4: Decommission (one-time cost, not annualised) ---
    const opt4NPV = cost.disposalCost - assessment.assetProfile.salvageValue * 0.5;
    const opt4EAC = null; // one-time cost — not comparable as an annual figure

    // --- Option 5: Revised maintenance strategy (user-configurable multipliers) ---
    const pmCostMult = cost.pmCostMultiplier || 1.3;
    const pmLifeMult = cost.pmLifeMultiplier || 1.5;
    const revisedAnnual = annualTotal * pmCostMult;
    const extendedLife = Math.round(remainingYears * pmLifeMult);
    const opt5NPV = npvStream(revisedAnnual, Math.min(extendedLife, horizon))
      + (cost.disposalCost / Math.pow(1 + rate, Math.min(extendedLife, horizon)));
    const opt5EAC = eac(opt5NPV, Math.min(extendedLife, horizon));

    // Build cost profiles for chart
    const buildProfile = (label, annCost, years, events = []) => {
      const profile = [];
      let cum = 0;
      for (let y = 0; y <= Math.min(years, horizon); y++) {
        const annual = y === 0 ? 0 : annCost * Math.pow(1 + esc, y);
        const eventCost = events.filter(e => e.year === y).reduce((s, e) => s + e.cost, 0);
        cum += annual + eventCost;
        profile.push({ year: y, cost: Math.round(cum), label });
      }
      return profile;
    };

    const options = [
      { id: 'continue', name: 'Continue as-is', npv: opt1NPV, eac: opt1EAC, life: opt1Years, risk: 'High', description: 'Maintain current strategy until end of remaining useful life' },
      { id: 'overhaul', name: 'Major overhaul', npv: opt2NPV, eac: opt2EAC, life: totalOverhaulLife, risk: 'Medium', description: `Major refurbishment at year ${overhaulYear}, extending life by ${postOverhaulYears} years` },
      { id: 'replace', name: 'Replace / upgrade', npv: opt3NPV, eac: opt3EAC, life: Math.min(totalReplaceLife, horizon), risk: 'Low', description: `Replace at year ${replaceYear} with ${newAssetLife}-year design life asset` },
      { id: 'decommission', name: 'Decommission', npv: opt4NPV, eac: null, life: 0, risk: 'N/A', description: 'Dispose of asset immediately, recover salvage value', oneTime: true },
      { id: 'revised', name: 'Revised maintenance strategy', npv: opt5NPV, eac: opt5EAC, life: Math.min(extendedLife, horizon), risk: 'Medium-Low', description: `Enhanced PdM/CBM to extend life to ~${extendedLife} years with ${Math.round((pmCostMult - 1) * 100)}% higher maintenance investment (${Math.round((pmLifeMult - 1) * 100)}% life extension)` }
    ];

    // Optimal route = lowest EAC excluding decommission
    const viable = options.filter(o => o.id !== 'decommission');
    const optimal = viable.reduce((best, o) => o.eac < best.eac ? o : best, viable[0]);

    // Cost profiles for timing chart
    const profiles = [
      ...buildProfile('Continue', annualTotal, opt1Years),
      ...buildProfile('Overhaul', annualTotal, totalOverhaulLife, [{ year: overhaulYear, cost: cost.overhaulCost }]),
      ...buildProfile('Replace', annualTotal, Math.min(totalReplaceLife, horizon), [{ year: replaceYear, cost: replCost }]),
      ...buildProfile('Revised PM', revisedAnnual, Math.min(extendedLife, horizon))
    ];

    return { options, optimal, rul, profiles, annualTotal };
  };

  // ============================================================================
  // MONTE CARLO SIMULATION
  // ============================================================================
  const runMonteCarlo = (assessment, iterations = 5000) => {
    const cost = assessment.costData;
    const params = assessment.analysisParams;
    const rate = params.discountRate / 100;
    const esc = params.escalationRate / 100;
    const annualTotal = cost.annualMaintenance + cost.annualOperating + cost.annualDowntime;

    const randNormal = (mean, stddev) => {
      const u1 = Math.random();
      const u2 = Math.random();
      return mean + stddev * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    };

    const simOption = (label, baseCost, baseLife, capexYear, capex) => {
      const results = [];
      for (let i = 0; i < iterations; i++) {
        // G17: Use Weibull-calibrated variance when equipment data available
        const weibullBeta = assessment.weibullOverride?.beta || null;
        const lifeCV = weibullBeta ? Math.sqrt(Math.pow(1 + 2/weibullBeta, 1) - Math.pow(1 + 1/weibullBeta, 2)) / (1 + 1/weibullBeta) : 0.15;
        const costCV = (assessment.costData?.historicalVariance || 0.2);
        const life = Math.max(1, Math.round(randNormal(baseLife, baseLife * Math.min(lifeCV, 0.5))));
        const annual = Math.max(0, randNormal(baseCost, baseCost * costCV));
        let npv = 0;
        for (let y = 1; y <= life; y++) {
          npv += (annual * Math.pow(1 + esc, y)) / Math.pow(1 + rate, y);
          if (capexYear && y === capexYear) {
            npv += randNormal(capex, capex * 0.1) / Math.pow(1 + rate, y);
          }
        }
        npv += cost.disposalCost / Math.pow(1 + rate, life);
        results.push(npv);
      }
      results.sort((a, b) => a - b);
      return {
        label,
        p10: results[Math.floor(iterations * 0.1)],
        p50: results[Math.floor(iterations * 0.5)],
        p90: results[Math.floor(iterations * 0.9)],
        mean: results.reduce((s, v) => s + v, 0) / iterations,
        min: results[0],
        max: results[results.length - 1],
        distribution: Array.from({ length: 20 }, (_, i) => {
          const lo = results[0] + (results[results.length - 1] - results[0]) * i / 20;
          const hi = results[0] + (results[results.length - 1] - results[0]) * (i + 1) / 20;
          return { range: `${(lo / 1000).toFixed(0)}k`, count: results.filter(v => v >= lo && v < hi).length, lo, hi };
        })
      };
    };

    const rul = calculateRUL(assessment);
    const remainYrs = Math.max(2, Math.round(rul.combinedRUL));

    return [
      simOption('Continue as-is', annualTotal, remainYrs, null, 0),
      simOption('Major overhaul', annualTotal * 0.85, remainYrs + cost.overhaulLifeExtension, Math.round(remainYrs * 0.7), cost.overhaulCost),
      simOption('Replace / upgrade', annualTotal * 0.5, remainYrs + (cost.upgradeDesignLife || 25), Math.round(remainYrs * 0.8), cost.upgradeCost || assessment.assetProfile.replacementCost),
      simOption('Revised PM', annualTotal * (cost.pmCostMultiplier || 1.3), Math.round(remainYrs * (cost.pmLifeMultiplier || 1.5)), null, 0)
    ];
  };

  // ============================================================================
  // HANDLE RUN ANALYSIS
  // ============================================================================
  const handleRunAnalysis = () => {
    if (!active) return;
    const result = calculateLifecycleOptions(active);
    updateAssessment(active.id, { options: result.options, recommendation: result.optimal, costProfiles: result.profiles });
  };

  const handleRunMonteCarlo = () => {
    if (!active) return;
    const mcResults = runMonteCarlo(active);
    updateAssessment(active.id, { monteCarloResults: mcResults });
  };

  // ============================================================================
  // RENDER: ASSESSMENT LIST (no active)
  // ============================================================================
  if (!active) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
        <div style={{ padding: '12px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: COLORS.TEXT, fontSize: '14px' }}>Lifecycle Assessment — ISO 55001 / IEC 60300-3-3</h3>
          <button onClick={handleNewAssessment} style={btnPrimary}>+ New Assessment</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {assessments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 24px', color: COLORS.TEXT_DIM }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No lifecycle assessments</div>
              <div style={{ fontSize: '13px', marginBottom: '24px' }}>Create an assessment to evaluate remaining useful life, compare lifecycle options, and determine the optimal route for your assets.</div>
              <button onClick={handleNewAssessment} style={btnPrimary}>+ New Assessment</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
              {assessments.map(a => {
                const equip = (projectData?.equipments || []).find(e => e.id === a.equipmentId);
                const rul = calculateRUL(a);
                return (
                  <div key={a.id} style={{ ...cardStyle, cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                    onClick={() => { setActiveId(a.id); setActiveTab('profile'); }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: COLORS.TEXT, fontSize: '14px' }}>{a.name}</div>
                        <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>{equip?.description || 'No equipment linked'}</div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); handleDeleteAssessment(a.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d32f2f', fontSize: '16px' }}>×</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '11px' }}>
                      <div><span style={{ color: COLORS.TEXT_DIM }}>Age:</span> <span style={{ fontWeight: '600' }}>{rul.currentAge.toFixed(1)} yrs</span></div>
                      <div><span style={{ color: COLORS.TEXT_DIM }}>RUL:</span> <span style={{ fontWeight: '600', color: rul.combinedRUL < 3 ? '#d32f2f' : COLORS.TEAL }}>{rul.combinedRUL.toFixed(1)} yrs</span></div>
                      <div><span style={{ color: COLORS.TEXT_DIM }}>Health:</span> <span style={{ fontWeight: '600' }}>{rul.currentHealth}%</span></div>
                    </div>
                    {a.recommendation && (
                      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: COLORS.TEAL_LIGHT, borderRadius: '4px', fontSize: '11px', color: COLORS.NAVY }}>
                        Optimal: <span style={{ fontWeight: '600' }}>{a.recommendation.name}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: ACTIVE ASSESSMENT
  // ============================================================================
  const equip = (projectData?.equipments || []).find(e => e.id === active.equipmentId);
  const rul = calculateRUL(active);
  const equipments = projectData?.equipments || [];
  const weibullSets = projectData?.weibullsets || [];
  // ---- Step readiness checks ----
  const stepReadiness = {
    profile: {
      ready: !!(active.equipmentId && active.assetProfile.installDate && active.assetProfile.designLife > 0 && active.assetProfile.replacementCost > 0),
      issues: [
        ...(!active.equipmentId ? ['Link an equipment item'] : []),
        ...(!active.assetProfile.installDate ? ['Set installation date'] : []),
        ...(active.assetProfile.designLife <= 0 ? ['Set design life'] : []),
        ...(active.assetProfile.replacementCost <= 0 ? ['Set replacement cost'] : [])
      ],
      guidance: 'Define the asset identity, type, criticality, and lifecycle parameters. Link to an equipment record from the register if available.'
    },
    condition: {
      ready: (active.conditionHistory || []).length >= 2,
      issues: [
        ...((active.conditionHistory || []).length === 0 ? ['Add at least 2 condition assessments for degradation trending'] : []),
        ...((active.conditionHistory || []).length === 1 ? ['Add 1 more assessment — degradation trending needs ≥2 data points'] : [])
      ],
      guidance: 'Record periodic condition inspections. Score 1 (failed) to 5 (as new) or use the 0-100 health index. Two or more entries enable degradation trend analysis.'
    },
    costs: {
      ready: (active.costData.annualMaintenance > 0 || active.costData.annualOperating > 0) && active.costData.overhaulCost > 0,
      issues: [
        ...(active.costData.annualMaintenance <= 0 && active.costData.annualOperating <= 0 ? ['Enter annual maintenance or operating costs'] : []),
        ...(active.costData.overhaulCost <= 0 ? ['Enter major overhaul cost'] : []),
        ...(active.costData.disposalCost <= 0 ? ['Consider entering disposal cost (optional)'] : [])
      ],
      guidance: 'Enter annual recurring costs (maintenance, operations, downtime) and one-off lifecycle event costs (overhaul, disposal, upgrade). Set the discount rate to match your WACC.'
    },
    rul: {
      ready: true, // RUL always calculates (age-based fallback)
      issues: [
        ...(rul.weibullRUL === null ? ['Link Weibull parameters for statistical RUL (optional)'] : []),
        ...(rul.conditionRUL === null ? ['Add condition data for condition-based RUL (optional)'] : [])
      ],
      guidance: 'Review the remaining useful life estimates. The combined RUL uses the most conservative method available. More data sources improve accuracy.'
    },
    options: {
      ready: !!active.options,
      issues: [
        ...(!active.options ? ['Click "Run Lifecycle Analysis" to evaluate all five lifecycle options'] : [])
      ],
      guidance: 'Compare Continue as-is, Major overhaul, Replace/upgrade, Decommission, and Revised PM strategy. The optimal route is determined by lowest Equivalent Annual Cost (EAC).'
    },
    montecarlo: {
      ready: !!active.monteCarloResults,
      issues: [
        ...(!active.monteCarloResults ? ['Click "Run Monte Carlo" to simulate cost uncertainty'] : [])
      ],
      guidance: 'Monte Carlo simulation (5,000 iterations) quantifies cost uncertainty. P10/P50/P90 confidence bands show optimistic, expected, and conservative lifecycle cost outcomes.'
    }
  };

  const tabs = [
    { id: 'profile', label: 'Asset Profile', step: 1 },
    { id: 'condition', label: 'Condition History', step: 2 },
    { id: 'costs', label: 'Cost Data', step: 3 },
    { id: 'rul', label: 'RUL Analysis', step: 4 },
    { id: 'options', label: 'Lifecycle Options', step: 5 },
    { id: 'montecarlo', label: 'Monte Carlo', step: 6 }
  ];
  const currentStepIdx = tabs.findIndex(t => t.id === activeTab);
  const prevTab = currentStepIdx > 0 ? tabs[currentStepIdx - 1] : null;
  const nextTab = currentStepIdx < tabs.length - 1 ? tabs[currentStepIdx + 1] : null;
  const completedSteps = tabs.filter(t => stepReadiness[t.id]?.ready).length;

  const fieldGroup = (label, children) => (
    <div style={{ marginBottom: '10px' }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  const updateField = (path, value) => {
    updateAssessment(active.id, a => {
      const parts = path.split('.');
      const updated = { ...a };
      let obj = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return updated;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Header */}
      <div style={{ padding: '8px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => setActiveId(null)} style={{ ...btnSec, padding: '4px 10px', fontSize: '11px' }}>← Back</button>
        <input value={active.name} onChange={e => updateAssessment(active.id, { name: e.target.value })}
          style={{ ...inputStyle, width: '240px', fontWeight: '600', fontSize: '14px' }} />
        <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>{equip?.description || 'No equipment linked'}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          <label style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Currency:</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} style={{ ...inputStyle, width: '120px' }}>
            {Object.entries(CURRENCIES).map(([code, c]) => (
              <option key={code} value={code}>{c.symbol} {c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', padding: '8px 16px', backgroundColor: '#fff', borderBottom: `1px solid ${COLORS.BORDER}` }}>
        {statCard('Current Age', `${rul.currentAge.toFixed(1)} yrs`, `Design life: ${active.assetProfile.designLife} yrs`)}
        {statCard('Health Index', `${rul.currentHealth}/100`, `Score: ${healthToScore(rul.currentHealth)}/5`)}
        {statCard('Remaining Life', `${rul.combinedRUL.toFixed(1)} yrs`, rul.weibullRUL !== null ? 'Combined estimate' : rul.conditionRUL !== null ? 'Condition-based' : 'Age-based', rul.combinedRUL < 3 ? '#d32f2f' : COLORS.TEAL)}
        {statCard('Annual Cost', formatCurrency(active.costData.annualMaintenance + active.costData.annualOperating + active.costData.annualDowntime, currency), 'Maint + Ops + Downtime')}
        {statCard('Optimal Route', active.recommendation ? active.recommendation.name : '—', active.recommendation ? `EAC: ${formatCurrency(active.recommendation.eac, currency)}/yr` : 'Run analysis', COLORS.NAVY)}
      </div>

      {/* Step progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '6px 16px', backgroundColor: '#fff', borderBottom: `1px solid ${COLORS.BORDER}`, gap: '4px' }}>
        <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginRight: '8px', whiteSpace: 'nowrap' }}>{completedSteps}/{tabs.length} complete</span>
        {tabs.map((t, i) => {
          const ready = stepReadiness[t.id]?.ready;
          const isCurrent = t.id === activeTab;
          return (
            <React.Fragment key={t.id}>
              <div onClick={() => setActiveTab(t.id)} style={{ width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: ready ? COLORS.TEAL : isCurrent ? COLORS.NAVY : COLORS.LIGHT_BG,
                color: ready || isCurrent ? '#fff' : COLORS.TEXT_DIM,
                fontSize: '10px', fontWeight: '600', cursor: 'pointer', flexShrink: 0, border: isCurrent ? `2px solid ${COLORS.GOLD}` : 'none',
                transition: 'all 0.2s' }}>
                {ready ? '✓' : t.step}
              </div>
              {i < tabs.length - 1 && (
                <div style={{ flex: 1, height: '2px', backgroundColor: ready ? COLORS.TEAL : COLORS.BORDER, transition: 'background-color 0.3s' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
        {tabs.map(t => {
          const ready = stepReadiness[t.id]?.ready;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ flex: 1, padding: '10px', backgroundColor: activeTab === t.id ? COLORS.NAVY : 'transparent',
                color: activeTab === t.id ? '#fff' : COLORS.TEXT_DIM, border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: activeTab === t.id ? '600' : '400',
                position: 'relative' }}>
              {t.label}
              {ready && activeTab !== t.id && <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: COLORS.TEAL, marginLeft: '4px', verticalAlign: 'middle' }} />}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>

        {/* Guidance banner */}
        <div style={{ marginBottom: '12px', padding: '10px 14px', backgroundColor: stepReadiness[activeTab]?.ready ? '#D1FAE5' : '#EFF6FF',
          borderRadius: '6px', borderLeft: `4px solid ${stepReadiness[activeTab]?.ready ? COLORS.TEAL : '#3B82F6'}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#1E3A5F', fontWeight: '600', marginBottom: '2px' }}>
              Step {currentStepIdx + 1} of {tabs.length}: {tabs[currentStepIdx]?.label}
              {stepReadiness[activeTab]?.ready && <span style={{ color: COLORS.TEAL, marginLeft: '8px' }}>✓ Complete</span>}
            </div>
            <div style={{ fontSize: '11px', color: '#4B5563' }}>{stepReadiness[activeTab]?.guidance}</div>
            {!stepReadiness[activeTab]?.ready && stepReadiness[activeTab]?.issues?.length > 0 && (
              <div style={{ fontSize: '11px', color: '#92400E', marginTop: '4px' }}>
                To do: {stepReadiness[activeTab].issues.join(' · ')}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '16px' }}>
            {prevTab && (
              <button onClick={() => setActiveTab(prevTab.id)} style={{ ...btnSec, padding: '5px 12px', fontSize: '11px' }}>
                ← {prevTab.label}
              </button>
            )}
            {nextTab && (
              <button onClick={() => setActiveTab(nextTab.id)}
                style={{ ...(stepReadiness[activeTab]?.ready ? btnPrimary : btnSec), padding: '5px 12px', fontSize: '11px' }}>
                {nextTab.label} →
              </button>
            )}
          </div>
        </div>

        {/* ==================== ASSET PROFILE TAB ==================== */}
        {activeTab === 'profile' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 16px 0', color: COLORS.NAVY, fontSize: '13px' }}>Asset Details</h4>
              {fieldGroup('Linked Equipment', <select value={active.equipmentId} onChange={e => updateAssessment(active.id, { equipmentId: e.target.value })} style={inputStyle}>
                <option value="">— Select equipment —</option>
                {equipments.map(eq => <option key={eq.id} value={eq.id}>{eq.id} — {eq.description}</option>)}
              </select>)}
              {fieldGroup('Assessment Name', <input value={active.name} onChange={e => updateAssessment(active.id, { name: e.target.value })} style={inputStyle} />)}
              {fieldGroup('Asset Type', <select value={active.assetProfile.assetType} onChange={e => updateField('assetProfile.assetType', e.target.value)} style={inputStyle}>
                <option value="rotating">Rotating equipment</option>
                <option value="static">Static / structural</option>
                <option value="electrical">Electrical</option>
                <option value="instrumentation">Instrumentation</option>
              </select>)}
              {fieldGroup('Criticality Rating', <select value={active.assetProfile.criticalityRating} onChange={e => updateField('assetProfile.criticalityRating', e.target.value)} style={inputStyle}>
                {['Critical', 'High', 'Medium', 'Low'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>)}
            </div>
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 16px 0', color: COLORS.NAVY, fontSize: '13px' }}>Lifecycle Parameters</h4>
              {fieldGroup('Installation Date', <input type="date" value={active.assetProfile.installDate} onChange={e => updateField('assetProfile.installDate', e.target.value)} style={inputStyle} />)}
              {fieldGroup('Design Life (years)', <input type="number" value={active.assetProfile.designLife} onChange={e => updateField('assetProfile.designLife', Number(e.target.value) || 0)} style={inputStyle} min="1" max="100" />)}
              {fieldGroup(`Replacement Cost (${currency})`, <input type="number" value={active.assetProfile.replacementCost} onChange={e => updateField('assetProfile.replacementCost', Number(e.target.value) || 0)} style={inputStyle} min="0" step="1000" />)}
              {fieldGroup(`Salvage Value (${currency})`, <input type="number" value={active.assetProfile.salvageValue} onChange={e => updateField('assetProfile.salvageValue', Number(e.target.value) || 0)} style={inputStyle} min="0" step="500" />)}
            </div>
            <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
              <h4 style={{ margin: '0 0 16px 0', color: COLORS.NAVY, fontSize: '13px' }}>Weibull Parameters (from failure data)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                {fieldGroup('Link from Weibull Set', <select value={active.weibullOverride ? 'custom' : ''} onChange={e => {
                  if (e.target.value && e.target.value !== 'custom') {
                    const ws = weibullSets.find(w => w.id === e.target.value);
                    if (ws) updateAssessment(active.id, { weibullOverride: { beta: ws.beta || 2.0, eta: ws.eta || 50000, source: ws.id } });
                  }
                }} style={inputStyle}>
                  <option value="">— Select Weibull set —</option>
                  {weibullSets.map(ws => <option key={ws.id} value={ws.id}>{ws.description || ws.id}</option>)}
                  {active.weibullOverride && <option value="custom">Custom values</option>}
                </select>)}
                {fieldGroup('Shape (β)', <input type="number" step="0.1" value={active.weibullOverride?.beta || ''} onChange={e => updateAssessment(active.id, { weibullOverride: { ...(active.weibullOverride || {}), beta: Number(e.target.value) || 0, eta: active.weibullOverride?.eta || 50000 } })} style={inputStyle} placeholder="e.g. 2.5" />)}
                {fieldGroup('Scale (η) hours', <input type="number" value={active.weibullOverride?.eta || ''} onChange={e => updateAssessment(active.id, { weibullOverride: { ...(active.weibullOverride || {}), eta: Number(e.target.value) || 0, beta: active.weibullOverride?.beta || 2.0 } })} style={inputStyle} placeholder="e.g. 50000" />)}
              </div>
            </div>
          </div>
        )}

        {/* ==================== CONDITION HISTORY TAB ==================== */}
        {activeTab === 'condition' && (() => {
          const history = active.conditionHistory || [];
          const sorted = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, color: COLORS.NAVY, fontSize: '13px' }}>Condition Assessment History (1-5 score / 0-100 health index)</h4>
                <button onClick={() => {
                  const entry = { id: Date.now(), date: new Date().toISOString().slice(0, 10), score: 3, healthIndex: 50, inspector: '', notes: '' };
                  updateAssessment(active.id, a => ({ ...a, conditionHistory: [...(a.conditionHistory || []), entry] }));
                }} style={btnPrimary}>+ Add Assessment</button>
              </div>
              {sorted.length === 0 ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: '48px', color: COLORS.TEXT_DIM }}>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>No condition assessments recorded</div>
                  <div style={{ fontSize: '13px' }}>Add inspection data to enable condition-based RUL estimation. At least 2 assessments are needed for degradation trending.</div>
                </div>
              ) : (
                <>
                  <div style={cardStyle}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: COLORS.LIGHT_BG }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}` }}>Date</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: `2px solid ${COLORS.BORDER}` }}>Score (1-5)</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: `2px solid ${COLORS.BORDER}` }}>Health Index (0-100)</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}` }}>Inspector</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}` }}>Notes</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: `2px solid ${COLORS.BORDER}`, width: '80px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((entry, idx) => (
                          <tr key={entry.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG }}>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                              <input type="date" value={entry.date} onChange={e => {
                                updateAssessment(active.id, a => ({ ...a, conditionHistory: a.conditionHistory.map(c => c.id === entry.id ? { ...c, date: e.target.value } : c) }));
                              }} style={{ ...inputStyle, width: '130px' }} />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                              <select value={entry.score} onChange={e => {
                                const newScore = Number(e.target.value);
                                updateAssessment(active.id, a => ({ ...a, conditionHistory: a.conditionHistory.map(c => c.id === entry.id ? { ...c, score: newScore, healthIndex: scoreToHealth(newScore) } : c) }));
                              }} style={{ ...inputStyle, width: '60px', textAlign: 'center' }}>
                                {[1, 2, 3, 4, 5].map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                              <input type="number" min="0" max="100" value={entry.healthIndex} onChange={e => {
                                const hi = Number(e.target.value);
                                updateAssessment(active.id, a => ({ ...a, conditionHistory: a.conditionHistory.map(c => c.id === entry.id ? { ...c, healthIndex: hi, score: healthToScore(hi) } : c) }));
                              }} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} />
                            </td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                              <input value={entry.inspector} onChange={e => {
                                updateAssessment(active.id, a => ({ ...a, conditionHistory: a.conditionHistory.map(c => c.id === entry.id ? { ...c, inspector: e.target.value } : c) }));
                              }} placeholder="Inspector" style={{ ...inputStyle, width: '120px' }} />
                            </td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                              <input value={entry.notes} onChange={e => {
                                updateAssessment(active.id, a => ({ ...a, conditionHistory: a.conditionHistory.map(c => c.id === entry.id ? { ...c, notes: e.target.value } : c) }));
                              }} placeholder="Notes" style={inputStyle} />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                              <button onClick={() => updateAssessment(active.id, a => ({ ...a, conditionHistory: a.conditionHistory.filter(c => c.id !== entry.id) }))}
                                style={{ background: 'transparent', color: '#d32f2f', border: 'none', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {sorted.length >= 2 && (
                    <div style={cardStyle}>
                      <h4 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '12px' }}>Degradation Trend</h4>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={[...sorted].reverse().map(c => ({ date: c.date, health: c.healthIndex }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" style={{ fontSize: '10px' }} />
                          <YAxis domain={[0, 100]} style={{ fontSize: '10px' }} label={{ value: 'Health Index', angle: -90, position: 'insideLeft' }} />
                          <Tooltip />
                          <ReferenceLine y={20} stroke="#d32f2f" strokeDasharray="5 5" label={{ value: 'Failure threshold', fill: '#d32f2f', fontSize: 10 }} />
                          <Line type="monotone" dataKey="health" stroke={COLORS.TEAL} strokeWidth={2} dot={{ fill: COLORS.TEAL, r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* ==================== COST DATA TAB ==================== */}
        {activeTab === 'costs' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 16px 0', color: COLORS.NAVY, fontSize: '13px' }}>Annual Operating Costs ({currency})</h4>
              {fieldGroup('Annual Maintenance Cost', <input type="number" value={active.costData.annualMaintenance} onChange={e => updateField('costData.annualMaintenance', Number(e.target.value) || 0)} style={inputStyle} min="0" step="500" />)}
              {fieldGroup('Annual Operating Cost', <input type="number" value={active.costData.annualOperating} onChange={e => updateField('costData.annualOperating', Number(e.target.value) || 0)} style={inputStyle} min="0" step="500" />)}
              {fieldGroup('Annual Downtime Cost', <input type="number" value={active.costData.annualDowntime} onChange={e => updateField('costData.annualDowntime', Number(e.target.value) || 0)} style={inputStyle} min="0" step="500" />)}
            </div>
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 16px 0', color: COLORS.NAVY, fontSize: '13px' }}>Lifecycle Event Costs ({currency})</h4>
              {fieldGroup('Major Overhaul Cost', <input type="number" value={active.costData.overhaulCost} onChange={e => updateField('costData.overhaulCost', Number(e.target.value) || 0)} style={inputStyle} min="0" step="1000" />)}
              {fieldGroup('Life Extension from Overhaul (years)', <input type="number" value={active.costData.overhaulLifeExtension} onChange={e => updateField('costData.overhaulLifeExtension', Number(e.target.value) || 0)} style={inputStyle} min="0" max="50" />)}
              {fieldGroup('Disposal / Decommissioning Cost', <input type="number" value={active.costData.disposalCost} onChange={e => updateField('costData.disposalCost', Number(e.target.value) || 0)} style={inputStyle} min="0" step="500" />)}
              {fieldGroup('Upgrade / New Asset Cost', <input type="number" value={active.costData.upgradeCost} onChange={e => updateField('costData.upgradeCost', Number(e.target.value) || 0)} style={inputStyle} min="0" step="1000" />)}
              {fieldGroup('New Asset Design Life (years)', <input type="number" value={active.costData.upgradeDesignLife} onChange={e => updateField('costData.upgradeDesignLife', Number(e.target.value) || 0)} style={inputStyle} min="1" max="100" />)}
            </div>
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 16px 0', color: COLORS.NAVY, fontSize: '13px' }}>Revised PM Strategy Parameters</h4>
              <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '12px' }}>These multipliers control Option 5 (Revised Maintenance Strategy). Adjust based on the asset class and expected PdM/CBM effectiveness.</div>
              {fieldGroup('Maintenance Cost Multiplier (e.g. 1.3 = +30%)', <input type="number" step="0.05" min="1.0" max="3.0" value={active.costData.pmCostMultiplier || 1.3} onChange={e => updateField('costData.pmCostMultiplier', Math.max(1, Number(e.target.value) || 1.3))} style={inputStyle} />)}
              {fieldGroup('Life Extension Multiplier (e.g. 1.5 = +50%)', <input type="number" step="0.05" min="1.0" max="5.0" value={active.costData.pmLifeMultiplier || 1.5} onChange={e => updateField('costData.pmLifeMultiplier', Math.max(1, Number(e.target.value) || 1.5))} style={inputStyle} />)}
            </div>
            <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
              <h4 style={{ margin: '0 0 16px 0', color: COLORS.NAVY, fontSize: '13px' }}>Analysis Parameters</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                {fieldGroup('Discount Rate (%)', <input type="number" value={active.analysisParams.discountRate} onChange={e => updateField('analysisParams.discountRate', Number(e.target.value) || 0)} style={inputStyle} min="0" max="30" step="0.5" />)}
                {fieldGroup('Cost Escalation Rate (%)', <input type="number" value={active.analysisParams.escalationRate} onChange={e => updateField('analysisParams.escalationRate', Number(e.target.value) || 0)} style={inputStyle} min="0" max="20" step="0.5" />)}
                {fieldGroup('Analysis Horizon (years)', <input type="number" value={active.analysisParams.analysisHorizon} onChange={e => updateField('analysisParams.analysisHorizon', Number(e.target.value) || 1)} style={inputStyle} min="1" max="100" />)}
              </div>
            </div>
            {/* Show linked cost nodes */}
            {costNodes.length > 0 && (
              <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
                <h4 style={{ margin: '0 0 8px 0', color: COLORS.TEXT, fontSize: '12px' }}>Project Cost Nodes (from data grid)</h4>
                <div style={{ fontSize: '11px' }}>
                  {costNodes.map(node => (
                    <div key={node.id} style={{ padding: '4px 0', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }}>
                      {node.description}: <span style={{ color: COLORS.GOLD, fontWeight: 'bold' }}>{formatCurrency(node.annualCost, currency)}/yr</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== RUL ANALYSIS TAB ==================== */}
        {activeTab === 'rul' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              {statCard('Age-based RUL', `${Math.max(0, active.assetProfile.designLife - rul.currentAge).toFixed(1)} yrs`, 'Design life − current age')}
              {statCard('Weibull RUL', rul.weibullRUL !== null ? `${rul.weibullRUL.toFixed(1)} yrs` : '—', rul.weibullRUL !== null ? `β=${active.weibullOverride.beta}, η=${active.weibullOverride.eta} · R=0.368 target` : 'No Weibull params set')}
              {statCard('Condition RUL', rul.conditionRUL !== null ? `${rul.conditionRUL.toFixed(1)} yrs` : '—', rul.conditionRUL !== null ? ((active.conditionHistory || []).length >= 4 ? 'Min of linear + exponential fit' : 'Linear degradation trend') : 'Need ≥2 condition assessments')}
              {statCard('Combined RUL', `${rul.combinedRUL.toFixed(1)} yrs`, 'Conservative (minimum) of available methods', rul.combinedRUL < 3 ? '#d32f2f' : COLORS.TEAL)}
            </div>

            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 12px 0', color: COLORS.NAVY, fontSize: '13px' }}>Asset Lifecycle Position</h4>
              <div style={{ position: 'relative', height: '60px', backgroundColor: COLORS.LIGHT_BG, borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(100, (rul.currentAge / active.assetProfile.designLife) * 100)}%`, backgroundColor: rul.currentAge > active.assetProfile.designLife * 0.8 ? '#f57c00' : COLORS.TEAL, opacity: 0.3, transition: 'width 0.5s' }} />
                <div style={{ position: 'absolute', left: `${Math.min(98, (rul.currentAge / active.assetProfile.designLife) * 100)}%`, top: 0, height: '100%', width: '3px', backgroundColor: COLORS.NAVY }} />
                <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', fontSize: '13px', fontWeight: '600', color: COLORS.TEXT }}>
                  {rul.currentAge.toFixed(1)} / {active.assetProfile.designLife} years ({((rul.currentAge / active.assetProfile.designLife) * 100).toFixed(0)}% through design life)
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: COLORS.TEXT_DIM }}>
                <span>Install: {active.assetProfile.installDate}</span>
                <span>Today</span>
                <span>End of design life</span>
              </div>
            </div>

            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 12px 0', color: COLORS.NAVY, fontSize: '13px' }}>RUL Methodology — ISO 13381-1 / IEC 61649</h4>
              <div style={{ fontSize: '12px', color: COLORS.TEXT, lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 8px 0' }}><strong>Combined approach:</strong> The remaining useful life estimate uses the most conservative (lowest) value from all available estimation methods.</p>
                <p style={{ margin: '0 0 8px 0' }}><strong>Weibull-based:</strong> Uses the fitted β (shape) and η (scale) parameters to estimate the time at which reliability drops to R = 0.368 (characteristic life, 1/e). This is the standard threshold per IEC 61649, representing the age at which ~63.2% of the population has failed. Applicable when failure data is available from the equipment register or fleet-wide data.</p>
                <p style={{ margin: '0 0 8px 0' }}><strong>Condition-based (linear):</strong> Projects the degradation trend from condition assessment history to the functional failure threshold (health index = 20/100). Uses first-to-last linear interpolation. Available with ≥2 condition assessments.</p>
                <p style={{ margin: '0 0 8px 0' }}><strong>Condition-based (exponential):</strong> When ≥4 condition data points are available, fits an exponential decay curve h(t) = A·e<sup>-λt</sup> using log-linear regression. Projects to the failure threshold using the fitted decay rate. The conservative (minimum) of linear and exponential estimates is used.</p>
                <p style={{ margin: 0 }}><strong>Age-based:</strong> Simple remaining life from design life minus current age. Used as fallback when no failure or condition data is available.</p>
              </div>
            </div>
          </div>
        )}

        {/* ==================== LIFECYCLE OPTIONS TAB ==================== */}
        {activeTab === 'options' && (() => {
          const result = active.options ? { options: active.options, optimal: active.recommendation, rul, profiles: active.costProfiles || [], annualTotal: active.costData.annualMaintenance + active.costData.annualOperating + active.costData.annualDowntime } : null;
          const riskColors = { 'Low': '#065F46', 'Medium-Low': '#047857', 'Medium': '#92400E', 'High': '#991B1B', 'N/A': COLORS.TEXT_DIM };
          const riskBgs = { 'Low': '#D1FAE5', 'Medium-Low': '#D1FAE5', 'Medium': '#FEF3C7', 'High': '#FEE2E2', 'N/A': COLORS.LIGHT_BG };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, color: COLORS.NAVY, fontSize: '14px' }}>Lifecycle Decision Options — ISO 15686-5 / IEC 60300-3-3</h4>
                <button onClick={handleRunAnalysis} style={btnPrimary}>Run Lifecycle Analysis</button>
              </div>

              {!result ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: '48px', color: COLORS.TEXT_DIM }}>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>No analysis results</div>
                  <div style={{ fontSize: '13px' }}>Complete the Asset Profile, Condition History, and Cost Data tabs, then click "Run Lifecycle Analysis" to evaluate all five options.</div>
                </div>
              ) : (
                <>
                  {/* Option cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                    {result.options.map(opt => {
                      const isOptimal = result.optimal && result.optimal.id === opt.id;
                      return (
                        <div key={opt.id} style={{ ...cardStyle, borderColor: isOptimal ? COLORS.TEAL : COLORS.BORDER, borderWidth: isOptimal ? '2px' : '1px', position: 'relative' }}>
                          {isOptimal && <div style={{ position: 'absolute', top: '-1px', right: '12px', backgroundColor: COLORS.TEAL, color: '#fff', fontSize: '10px', fontWeight: '600', padding: '2px 10px', borderRadius: '0 0 4px 4px' }}>OPTIMAL</div>}
                          <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.NAVY, marginBottom: '8px' }}>{opt.name}</div>
                          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '12px' }}>{opt.description}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                            <div><span style={{ color: COLORS.TEXT_DIM }}>NPV:</span> <span style={{ fontWeight: '600' }}>{formatCurrency(opt.npv, currency, true)}</span></div>
                            <div><span style={{ color: COLORS.TEXT_DIM }}>EAC:</span> <span style={{ fontWeight: '600' }}>{opt.eac !== null ? `${formatCurrency(opt.eac, currency)}/yr` : 'One-time'}</span></div>
                            <div><span style={{ color: COLORS.TEXT_DIM }}>Life:</span> <span style={{ fontWeight: '600' }}>{opt.life > 0 ? `${opt.life} years` : 'Immediate'}</span></div>
                            <div><span style={{ color: COLORS.TEXT_DIM }}>Risk:</span> <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', backgroundColor: riskBgs[opt.risk] || COLORS.LIGHT_BG, color: riskColors[opt.risk] || COLORS.TEXT }}>{opt.risk}</span></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* EAC comparison chart */}
                  <div style={cardStyle}>
                    <h4 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '12px' }}>Equivalent Annual Cost Comparison</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={result.options.filter(o => o.id !== 'decommission')}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" style={{ fontSize: '10px' }} />
                        <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                        <Tooltip formatter={v => formatCurrency(v, currency)} />
                        <Bar dataKey="eac" name="EAC (per year)">
                          {result.options.filter(o => o.id !== 'decommission').map(o => (
                            <Cell key={o.id} fill={result.optimal && result.optimal.id === o.id ? COLORS.TEAL : COLORS.NAVY} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* NPV comparison */}
                  <div style={cardStyle}>
                    <h4 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '12px' }}>Net Present Value Comparison</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={result.options} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                        <YAxis type="category" dataKey="name" width={160} style={{ fontSize: '11px' }} />
                        <Tooltip formatter={v => formatCurrency(v, currency)} />
                        <Bar dataKey="npv" name="Total NPV" fill={COLORS.GOLD} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Cumulative cost profile — optimal timing chart */}
                  {result.profiles && result.profiles.length > 0 && (() => {
                    // Pivot profiles into year-indexed rows for multi-line chart
                    const labels = [...new Set(result.profiles.map(p => p.label))];
                    const profileColors = { 'Continue': '#991B1B', 'Overhaul': '#92400E', 'Replace': '#065F46', 'Revised PM': '#1E40AF' };
                    const maxYear = Math.max(...result.profiles.map(p => p.year));
                    const yearData = [];
                    for (let y = 0; y <= maxYear; y++) {
                      const row = { year: y };
                      labels.forEach(lbl => {
                        const match = result.profiles.find(p => p.label === lbl && p.year === y);
                        if (match) row[lbl] = match.cost;
                      });
                      yearData.push(row);
                    }
                    return (
                      <div style={cardStyle}>
                        <h4 style={{ margin: '0 0 4px 0', color: COLORS.TEXT, fontSize: '12px' }}>Cumulative Lifecycle Cost — Optimal Timing Chart</h4>
                        <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '12px' }}>Crossover points show where one strategy becomes cheaper than another over time. Steeper curves indicate higher annual spend.</div>
                        <ResponsiveContainer width="100%" height={350}>
                          <LineChart data={yearData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5, fontSize: 11 }} />
                            <YAxis tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                              label={{ value: `Cumulative Cost (${currency})`, angle: -90, position: 'insideLeft', fontSize: 10 }} />
                            <Tooltip formatter={v => formatCurrency(v, currency)} labelFormatter={y => `Year ${y}`} />
                            <Legend />
                            {labels.map(lbl => (
                              <Line key={lbl} type="monotone" dataKey={lbl} stroke={profileColors[lbl] || COLORS.TEXT_DIM} strokeWidth={2}
                                dot={false} connectNulls />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          );
        })()}

        {/* ==================== MONTE CARLO TAB ==================== */}
        {activeTab === 'montecarlo' && (() => {
          const mc = active.monteCarloResults;
          const mcColors = [COLORS.TEAL, COLORS.NAVY, COLORS.GOLD, '#7b1fa2'];
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, color: COLORS.NAVY, fontSize: '14px' }}>Monte Carlo Cost Simulation — 5,000 iterations</h4>
                <button onClick={handleRunMonteCarlo} style={btnPrimary}>Run Monte Carlo</button>
              </div>

              {!mc ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: '48px', color: COLORS.TEXT_DIM }}>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>No simulation results</div>
                  <div style={{ fontSize: '13px' }}>Click "Run Monte Carlo" to simulate cost uncertainty across four lifecycle options (Continue, Overhaul, Replace, Revised PM). Decommission is excluded as it represents a one-time deterministic cost with no variability to simulate. Uses normal distributions for annual costs (σ=20%), asset life (σ=15%), and CAPEX events (σ=10%).</div>
                </div>
              ) : (
                <>
                  {/* P10/P50/P90 summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${mc.length}, 1fr)`, gap: '12px' }}>
                    {mc.map((opt, i) => (
                      <div key={opt.label} style={{ ...cardStyle, borderTop: `3px solid ${mcColors[i]}` }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.NAVY, marginBottom: '8px' }}>{opt.label}</div>
                        <div style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div><span style={{ color: COLORS.TEXT_DIM }}>P10 (optimistic):</span> <span style={{ fontWeight: '600' }}>{formatCurrency(opt.p10, currency, true)}</span></div>
                          <div><span style={{ color: COLORS.TEXT_DIM }}>P50 (median):</span> <span style={{ fontWeight: '600', color: COLORS.NAVY }}>{formatCurrency(opt.p50, currency, true)}</span></div>
                          <div><span style={{ color: COLORS.TEXT_DIM }}>P90 (conservative):</span> <span style={{ fontWeight: '600' }}>{formatCurrency(opt.p90, currency, true)}</span></div>
                          <div style={{ marginTop: '4px', borderTop: `1px solid ${COLORS.BORDER}`, paddingTop: '4px' }}><span style={{ color: COLORS.TEXT_DIM }}>Mean:</span> <span style={{ fontWeight: '600' }}>{formatCurrency(opt.mean, currency, true)}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Distribution chart */}
                  <div style={cardStyle}>
                    <h4 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '12px' }}>Cost Distribution Comparison (P50 median)</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={mc.map((opt, i) => ({ name: opt.label, P10: opt.p10, P50: opt.p50, P90: opt.p90 }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" style={{ fontSize: '10px' }} />
                        <YAxis tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                        <Tooltip formatter={v => formatCurrency(v, currency)} />
                        <Legend />
                        <Bar dataKey="P10" fill="#A7F3D0" name="P10 (optimistic)" />
                        <Bar dataKey="P50" fill={COLORS.TEAL} name="P50 (median)" />
                        <Bar dataKey="P90" fill={COLORS.NAVY} name="P90 (conservative)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Individual distributions */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {mc.map((opt, i) => (
                      <div key={opt.label} style={cardStyle}>
                        <h4 style={{ margin: '0 0 8px 0', color: COLORS.TEXT, fontSize: '12px' }}>{opt.label} — Cost Distribution</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={opt.distribution}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="range" style={{ fontSize: '9px' }} />
                            <YAxis style={{ fontSize: '9px' }} />
                            <Tooltip />
                            <Bar dataKey="count" fill={mcColors[i]} name="Frequency" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ==================== WORKFLOW COMPLETION / BOTTOM NAV ==================== */}
        <div style={{ marginTop: '16px', padding: '12px 14px', backgroundColor: '#fff', borderRadius: '6px', border: `1px solid ${COLORS.BORDER}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>
            {completedSteps === tabs.length ? (
              <span style={{ color: COLORS.TEAL, fontWeight: '600' }}>✓ All {tabs.length} steps complete — assessment is ready for review and reporting</span>
            ) : (
              <span>{completedSteps}/{tabs.length} workflow steps complete · {tabs.filter(t => !stepReadiness[t.id]?.ready).map(t => t.label).join(', ')} remaining</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {prevTab && (
              <button onClick={() => setActiveTab(prevTab.id)} style={{ ...btnSec, padding: '6px 14px', fontSize: '11px' }}>
                ← {prevTab.label}
              </button>
            )}
            {nextTab && (
              <button onClick={() => setActiveTab(nextTab.id)}
                style={{ ...(stepReadiness[activeTab]?.ready ? btnPrimary : btnSec), padding: '6px 14px', fontSize: '11px' }}>
                {nextTab.label} →
              </button>
            )}
            {!nextTab && completedSteps === tabs.length && (
              <button onClick={() => setActiveId(null)} style={{ ...btnPrimary, padding: '6px 14px', fontSize: '11px' }}>
                ✓ Back to Assessment List
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// ============================================================================
// PROCESS RELIABILITY VIEW
// ============================================================================
function ProcessReliabilityView({ data, results, onRunAnalysis }) {
  const [activeTab, setActiveTab] = useState('summary');

  const handleRun = () => {
    try {
      const config = {
        analysisStart: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
        analysisEnd: new Date().toISOString(),
        timeUnit: 'hours',
      };
      const engine = new ProcessReliabilityEngine(data, config);
      const analysisResults = engine.analyze();
      onRunAnalysis?.(analysisResults);
    } catch (err) {
      console.error('Process Reliability error:', err.message);
      alert('Process Reliability analysis failed: ' + err.message + '\n\nPlease check your equipment and failure model data.');
    }
  };

  const oeeData = results?.oee || null;

  const topLosses = results?.topLosses || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '12px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={handleRun}
          style={{ padding: '6px 12px', backgroundColor: COLORS.GOLD, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}
        >
          Run Analysis
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM }}>
          {results ? 'Analysis complete' : 'Ready to analyze'}
        </div>
      </div>

      <div style={{ display: 'flex', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
        {['summary', 'losses', 'mtbf', 'recommendations'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: activeTab === tab ? COLORS.BLUE : 'transparent',
              color: activeTab === tab ? '#FFFFFF' : COLORS.TEXT,
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              borderBottom: activeTab === tab ? `2px solid ${COLORS.GOLD}` : 'none'
            }}
          >
            {tab === 'summary' && 'OEE Summary'}
            {tab === 'losses' && 'Top Losses'}
            {tab === 'mtbf' && 'Equipment'}
            {tab === 'recommendations' && 'Recommendations'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {activeTab === 'summary' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 16px 0' }}>OEE Summary</h3>
            {!oeeData ? (
              <div style={{ padding: '20px', textAlign: 'center', color: COLORS.TEXT_DIM, backgroundColor: COLORS.PANEL_BG, borderRadius: '6px', border: `1px solid ${COLORS.BORDER}` }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Run analysis to see OEE results</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                  {[
                    { label: 'Availability', value: oeeData.availability },
                    { label: 'Performance', value: oeeData.performance },
                    { label: 'Quality', value: oeeData.quality },
                    { label: 'OEE', value: oeeData.overall }
                  ].map(card => (
                    <div key={card.label} style={{ backgroundColor: COLORS.PANEL_BG, padding: '12px', borderRadius: '6px', textAlign: 'center', border: `1px solid ${COLORS.BORDER}` }}>
                      <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '8px' }}>{card.label}</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.GOLD, marginBottom: '8px' }}>{card.value.toFixed(1)}%</div>
                      <div style={{ height: '4px', backgroundColor: COLORS.BORDER, borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${card.value}%`, backgroundColor: card.value >= 85 ? COLORS.SUCCESS : card.value >= 70 ? COLORS.WARNING : COLORS.ERROR }} />
                      </div>
                    </div>
                  ))}
                </div>

                <h4 style={{ color: COLORS.TEXT, margin: '16px 0 8px 0', fontSize: '13px' }}>World-Class Comparison</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={[
                    { name: 'Current OEE', value: oeeData.overall },
                    { name: 'World-Class Target', value: 85 }
                  ]}>
                    <CartesianGrid stroke={COLORS.BORDER} />
                    <XAxis dataKey="name" stroke={COLORS.TEXT_DIM} style={{ fontSize: '11px' }} />
                    <YAxis stroke={COLORS.TEXT_DIM} style={{ fontSize: '11px' }} domain={[0, 100]} />
                    <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                    <Bar dataKey="value" fill={COLORS.GOLD} name="OEE %" />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
        )}

        {activeTab === 'losses' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 16px 0' }}>Top Losses (Pareto)</h3>
            {topLosses.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: COLORS.TEXT_DIM, backgroundColor: COLORS.PANEL_BG, borderRadius: '6px', border: `1px solid ${COLORS.BORDER}` }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Run analysis to see loss data</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topLosses} layout="vertical">
                  <CartesianGrid stroke={COLORS.BORDER} />
                  <XAxis type="number" stroke={COLORS.TEXT_DIM} style={{ fontSize: '11px' }} />
                  <YAxis dataKey="name" type="category" stroke={COLORS.TEXT_DIM} style={{ fontSize: '11px' }} width={120} />
                  <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                  <Bar dataKey="value" fill={COLORS.GOLD} name="Hours" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        {activeTab === 'mtbf' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 12px 0' }}>Equipment MTBF/MTTR</h3>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Equipment</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>MTBF (hrs)</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>MTTR (hrs)</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>Availability</th>
                </tr>
              </thead>
              <tbody>
                {results?.equipmentMetrics?.map((eq, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: idx % 2 === 0 ? COLORS.LIGHT_BG : 'transparent' }}>
                    <td style={{ padding: '8px', color: COLORS.TEXT }}>{eq.name}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>{eq.mtbf?.toFixed(0)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>{eq.mttr?.toFixed(2)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: COLORS.SUCCESS, fontWeight: 'bold' }}>{(eq.availability * 100).toFixed(1)}%</td>
                  </tr>
                )) || (
                  <tr><td colSpan="4" style={{ padding: '8px', color: COLORS.TEXT_DIM }}>No equipment data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 12px 0' }}>Improvement Recommendations</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {results?.recommendations?.map((rec, idx) => (
                <div key={idx} style={{ backgroundColor: COLORS.PANEL_BG, padding: '10px', borderRadius: '4px', borderLeft: `3px solid ${COLORS.GOLD}`, color: COLORS.TEXT, fontSize: '12px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>{rec.title}</div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>{rec.description}</div>
                  {rec.impact && <div style={{ fontSize: '10px', marginTop: '4px', color: COLORS.SUCCESS }}>Impact: +{rec.impact}% OEE</div>}
                </div>
              )) || (
                <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Run analysis to get recommendations</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// ALT VIEW
// ============================================================================
function ALTView({ data, results, onRunAnalysis }) {
  const [activeTab, setActiveTab] = useState('testData');
  const [stressModel, setStressModel] = useState('arrhenius');
  const [normalStress, setNormalStress] = useState(40);
  const [stressUnit, setStressUnit] = useState('°C');
  const [testData, setTestData] = useState(results?.testData || []);
  const [error, setError] = useState(null);

  const inputStyle = { backgroundColor: '#fff', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '5px 7px', fontSize: '11px', width: '100%', boxSizing: 'border-box' };
  const btnSmall = { padding: '4px 10px', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '500' };

  // Add a new data row
  const handleAddRow = () => {
    setTestData(prev => [...prev, { stressLevel: '', failureTime: '', isSuspended: false, id: Date.now() }]);
  };

  // Update a row
  const handleUpdateRow = (id, key, value) => {
    setTestData(prev => prev.map(r => r.id === id ? { ...r, [key]: value } : r));
  };

  // Remove a row
  const handleRemoveRow = (id) => {
    setTestData(prev => prev.filter(r => r.id !== id));
  };

  // Load sample data
  const handleLoadSample = () => {
    setStressModel('arrhenius');
    setNormalStress(40);
    setStressUnit('°C');
    setTestData([
      { stressLevel: 120, failureTime: 500, isSuspended: false, id: 1 },
      { stressLevel: 120, failureTime: 600, isSuspended: false, id: 2 },
      { stressLevel: 120, failureTime: 750, isSuspended: false, id: 3 },
      { stressLevel: 120, failureTime: 900, isSuspended: true, id: 4 },
      { stressLevel: 150, failureTime: 150, isSuspended: false, id: 5 },
      { stressLevel: 150, failureTime: 200, isSuspended: false, id: 6 },
      { stressLevel: 150, failureTime: 280, isSuspended: false, id: 7 },
      { stressLevel: 180, failureTime: 50, isSuspended: false, id: 8 },
      { stressLevel: 180, failureTime: 80, isSuspended: false, id: 9 },
      { stressLevel: 180, failureTime: 110, isSuspended: false, id: 10 }
    ]);
    setError(null);
  };

  const handleRun = () => {
    setError(null);

    // Validate normal stress
    if (!normalStress || normalStress <= 0 || !isFinite(normalStress)) {
      setError('Normal use stress must be a positive number.');
      return;
    }

    const validRows = testData.filter(r => r.stressLevel > 0 && r.failureTime > 0);
    if (validRows.length < 3) {
      setError('Enter at least 3 data points with valid stress levels and failure times.');
      return;
    }

    // Check for rows with zero or negative values that might confuse users
    const invalidRows = testData.filter(r => (r.stressLevel !== '' && r.stressLevel <= 0) || (r.failureTime !== '' && r.failureTime <= 0));
    if (invalidRows.length > 0) {
      setError(`${invalidRows.length} row(s) have zero or negative values and were excluded. Stress and time must be positive.`);
    }

    try {
      const grouped = {};
      validRows.forEach(row => {
        const key = row.stressLevel;
        if (!grouped[key]) grouped[key] = { stressLevel: parseFloat(key), failureTimes: [], suspensionTimes: [], unit: 'hours' };
        if (row.isSuspended) {
          grouped[key].suspensionTimes.push(parseFloat(row.failureTime));
        } else {
          grouped[key].failureTimes.push(parseFloat(row.failureTime));
        }
      });
      const formattedData = Object.values(grouped);

      if (formattedData.length < 2) {
        setError('Need data at least 2 different stress levels.');
        return;
      }

      // Warn if normal stress is higher than test stress levels (unusual for ALT)
      const minTestStress = Math.min(...formattedData.map(d => d.stressLevel));
      if (stressModel === 'arrhenius' && normalStress >= minTestStress) {
        // Not an error, but note it
        console.warn('[ALT] Normal stress >= lowest test stress — results may not represent acceleration.');
      }

      const config = {
        stressModel: stressModel.toLowerCase().replace(/[^a-z_]/g, ''),
        normalStress: parseFloat(normalStress),
        confidenceLevel: 0.9,
        useStress: stressUnit === '°C' ? 'temperature' : 'mechanical',
      };

      const engine = new ALTEngine(formattedData, config);
      const analysisResults = engine.analyze();
      onRunAnalysis?.({ ...analysisResults, testData: validRows });
    } catch (err) {
      console.error('ALT Analysis error:', err.message);
      alert('Accelerated Life Testing analysis failed: ' + err.message + '\n\nPlease check your test data entries.');
    }
  };

  // Group test data by stress level for the plot
  const stressGroups = {};
  testData.filter(r => r.stressLevel > 0 && r.failureTime > 0).forEach(r => {
    const sl = parseFloat(r.stressLevel);
    if (!stressGroups[sl]) stressGroups[sl] = [];
    stressGroups[sl].push(parseFloat(r.failureTime));
  });
  const plotData = Object.entries(stressGroups).map(([sl, times]) => ({
    stressLevel: parseFloat(sl),
    avgLife: times.reduce((a, b) => a + b, 0) / times.length,
    minLife: Math.min(...times),
    maxLife: Math.max(...times),
    count: times.length
  })).sort((a, b) => a.stressLevel - b.stressLevel);

  return (
    <div style={{ display: 'flex', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Left panel: Configuration and Data Entry */}
      <div style={{ width: '320px', borderRight: `1px solid ${COLORS.BORDER}`, display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
        {/* Config */}
        <div style={{ padding: '10px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
          <h4 style={{ margin: '0 0 8px 0', color: COLORS.TEXT, fontSize: '13px' }}>ALT Configuration</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>Stress Model</label>
              <select value={stressModel} onChange={e => setStressModel(e.target.value)} style={inputStyle}>
                <option value="arrhenius">Arrhenius (Temperature)</option>
                <option value="inversePower">Inverse Power Law</option>
                <option value="eyring">Eyring</option>
                <option value="tempHumidity">Temp-Humidity</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>Stress Unit</label>
              <select value={stressUnit} onChange={e => setStressUnit(e.target.value)} style={inputStyle}>
                <option value="°C">°C (Temperature)</option>
                <option value="V">V (Voltage)</option>
                <option value="MPa">MPa (Pressure)</option>
                <option value="RPM">RPM (Speed)</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '6px' }}>
            <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>Normal Use Stress ({stressUnit})</label>
            <input type="number" value={normalStress} onChange={e => setNormalStress(parseFloat(e.target.value) || 0)} style={inputStyle} />
          </div>
        </div>

        {/* Data entry header */}
        <div style={{ padding: '8px 10px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '11px', color: COLORS.TEXT, fontWeight: '600', flex: 1 }}>Test Data ({testData.length} rows)</span>
          <button onClick={handleAddRow} style={{ ...btnSmall, backgroundColor: COLORS.BLUE, color: '#fff', fontSize: '10px' }}>+ Row</button>
          <button onClick={handleLoadSample} style={{ ...btnSmall, backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, fontSize: '10px' }}>Sample</button>
          <button onClick={() => { if (testData.length === 0 || window.confirm('Clear all test data?')) { setTestData([]); setError(null); } }} style={{ ...btnSmall, backgroundColor: '#fde8e8', color: COLORS.ERROR, fontSize: '10px' }}>Clear</button>
        </div>

        {/* Data table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.PANEL_BG, position: 'sticky', top: 0 }}>
                <th style={{ padding: '5px 6px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM, fontSize: '10px' }}>Stress ({stressUnit})</th>
                <th style={{ padding: '5px 6px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM, fontSize: '10px' }}>Time (hrs)</th>
                <th style={{ padding: '5px 6px', textAlign: 'center', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM, fontSize: '10px' }}>Type</th>
                <th style={{ padding: '5px 2px', borderBottom: `1px solid ${COLORS.BORDER}`, width: '24px' }}></th>
              </tr>
            </thead>
            <tbody>
              {testData.map((row) => (
                <tr key={row.id} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <td style={{ padding: '3px 4px' }}>
                    <input type="number" value={row.stressLevel} onChange={e => handleUpdateRow(row.id, 'stressLevel', parseFloat(e.target.value) || 0)}
                      style={{ ...inputStyle, padding: '3px 5px' }} min="0" step="1" />
                  </td>
                  <td style={{ padding: '3px 4px' }}>
                    <input type="number" value={row.failureTime} onChange={e => handleUpdateRow(row.id, 'failureTime', parseFloat(e.target.value) || 0)}
                      style={{ ...inputStyle, padding: '3px 5px' }} min="0" step="10" />
                  </td>
                  <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                    <select value={row.isSuspended ? 'suspended' : 'failed'} onChange={e => handleUpdateRow(row.id, 'isSuspended', e.target.value === 'suspended')}
                      style={{ ...inputStyle, padding: '2px 3px', fontSize: '10px', width: 'auto' }}>
                      <option value="failed">F</option>
                      <option value="suspended">S</option>
                    </select>
                  </td>
                  <td style={{ padding: '3px 2px', textAlign: 'center' }}>
                    <button onClick={() => handleRemoveRow(row.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.TEXT_DIM, fontSize: '13px' }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {testData.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '11px' }}>
              No test data. Click "+ Row" to add data points or "Sample" to load example data.
            </div>
          )}
        </div>

        {/* Run button */}
        <div style={{ padding: '10px', borderTop: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
          <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '6px' }}>
            {Object.keys(stressGroups).length} stress levels, {testData.filter(r => !r.isSuspended && r.failureTime > 0).length} failures, {testData.filter(r => r.isSuspended).length} suspensions
          </div>
          <button onClick={handleRun} style={{ ...btnSmall, backgroundColor: COLORS.GOLD, color: '#fff', width: '100%', padding: '8px', fontSize: '12px', fontWeight: '600' }}>
            ▶ Run ALT Analysis
          </button>
        </div>
      </div>

      {/* Right panel: Results */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
          {['plot', 'results'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ flex: 1, padding: '8px', backgroundColor: activeTab === tab ? COLORS.BLUE : 'transparent', color: activeTab === tab ? '#fff' : COLORS.TEXT, border: 'none', cursor: 'pointer', fontSize: '12px', borderBottom: activeTab === tab ? `2px solid ${COLORS.GOLD}` : 'none' }}>
              {tab === 'plot' ? 'Life vs Stress Plot' : 'Results'}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ padding: '10px 12px', backgroundColor: '#fde8e8', borderBottom: `1px solid ${COLORS.ERROR}`, color: COLORS.ERROR, fontSize: '12px' }}>{error}</div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          {activeTab === 'plot' && (
            <div>
              <h4 style={{ color: COLORS.TEXT, margin: '0 0 12px 0', fontSize: '13px' }}>Life vs Stress ({stressUnit})</h4>
              {plotData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={plotData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                    <CartesianGrid stroke={COLORS.BORDER} strokeDasharray="3 3" />
                    <XAxis dataKey="stressLevel" type="number" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }}
                      label={{ value: `Stress Level (${stressUnit})`, position: 'insideBottom', offset: -10, fill: COLORS.TEXT_DIM }} />
                    <YAxis dataKey="avgLife" type="number" stroke={COLORS.TEXT_DIM} style={{ fontSize: '10px' }}
                      label={{ value: 'Life (hours)', angle: -90, position: 'insideLeft', fill: COLORS.TEXT_DIM }} />
                    <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }}
                      formatter={(v, name) => [typeof v === 'number' ? v.toFixed(0) + ' hrs' : v, name]} />
                    <Scatter name="Avg Life" dataKey="avgLife" fill={COLORS.GOLD} />
                    <Line type="monotone" dataKey="avgLife" stroke={COLORS.BLUE} strokeWidth={2} dot={false} name="Trend" />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', textAlign: 'center', marginTop: '40px' }}>Enter test data to see the plot</p>
              )}
              {plotData.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <h4 style={{ color: COLORS.TEXT, fontSize: '12px', margin: '0 0 8px 0' }}>Stress Level Summary</h4>
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: COLORS.PANEL_BG }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}` }}>Stress ({stressUnit})</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}` }}>Count</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}` }}>Avg Life</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}` }}>Min</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}` }}>Max</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plotData.map(sg => (
                        <tr key={sg.stressLevel} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                          <td style={{ padding: '6px 8px', fontWeight: 'bold' }}>{sg.stressLevel}{stressUnit}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{sg.count}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{sg.avgLife.toFixed(0)} hrs</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{sg.minLife.toFixed(0)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{sg.maxLife.toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'results' && (
            <div>
              <h4 style={{ color: COLORS.TEXT, margin: '0 0 12px 0', fontSize: '13px' }}>Analysis Results</h4>
              {results ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '14px' }}>
                    {[
                      { label: `MTTF at Normal Stress (${normalStress}${stressUnit})`, value: `${(results.mttf || results.normalLifeEstimate || 0).toFixed(0)} hrs`, color: COLORS.GOLD },
                      { label: 'B10 Life', value: `${(results.b10 || 0).toFixed(0)} hrs`, color: COLORS.BLUE },
                      { label: 'Shape Parameter (β)', value: (results.beta || 0).toFixed(3), color: COLORS.INFO },
                      { label: 'Scale Parameter (η)', value: `${(results.eta || 0).toFixed(0)} hrs`, color: COLORS.SUCCESS }
                    ].map(item => (
                      <div key={item.label} style={{ backgroundColor: '#fff', padding: '14px', borderRadius: '6px', border: `1px solid ${COLORS.BORDER}` }}>
                        <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>{item.label}</div>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: item.color }}>{item.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Acceleration factors */}
                  {results.accelerationFactors && results.accelerationFactors.length > 0 && (
                    <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '6px', border: `1px solid ${COLORS.BORDER}`, marginBottom: '14px' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: COLORS.TEXT, fontSize: '12px' }}>Acceleration Factors</h4>
                      <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: COLORS.PANEL_BG }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `2px solid ${COLORS.BORDER}` }}>Stress Level</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}` }}>Accel. Factor</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `2px solid ${COLORS.BORDER}` }}>Median Life</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.accelerationFactors.map((af, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                              <td style={{ padding: '6px 8px' }}>{af.stressLevel}{stressUnit}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'bold', color: COLORS.GOLD }}>{af.factor}×</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{typeof af.medianLife === 'number' ? af.medianLife.toFixed(0) : af.medianLife} hrs</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Confidence bounds */}
                  {results.confidenceBounds && (
                    <div style={{ backgroundColor: '#f0f7ff', padding: '12px', borderRadius: '6px', border: `1px solid ${COLORS.BLUE}33` }}>
                      <h4 style={{ margin: '0 0 6px 0', color: COLORS.BLUE, fontSize: '12px' }}>{results.confidenceBounds.level || 90}% Confidence Bounds</h4>
                      <div style={{ fontSize: '12px', color: COLORS.TEXT }}>
                        Normal life estimate: <strong>{(results.confidenceBounds.lower || 0).toLocaleString()}</strong> to <strong>{(results.confidenceBounds.upper || 0).toLocaleString()}</strong> hours
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: COLORS.TEXT_DIM, marginTop: '40px' }}>
                  <div style={{ fontSize: '40px', marginBottom: '8px', opacity: 0.3 }}>🔬</div>
                  <p style={{ fontSize: '13px' }}>Enter test data and click Run ALT Analysis</p>
                  <p style={{ fontSize: '11px' }}>Results will show estimated life at normal stress, Weibull parameters, and acceleration factors</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// UNIFIED RCM2 ANALYSIS — Single guided wizard with internal phases
// Phases: Scope → Functions (Q1) → Func.Failures (Q2) → FMEA (Q3-Q5) →
//         Decisions (Q6-Q7) → Deliverables (Worksheet/PM/Defaults/Cost)
// ============================================================================
function UnifiedRCM2Analysis({ projectData, setProjectData, scopedProjectData }) {
  const [activePhase, setActivePhase] = useState('scope');
  const [libraryOpen, setLibraryOpen] = useState(false);

  const PHASES = [
    { id: 'scope', label: 'Phase 0', title: 'Scope and Classification', icon: Target },
    { id: 'functions', label: 'Q1', title: 'Functions', icon: Layers },
    { id: 'failures', label: 'Q2', title: 'Functional Failures', icon: AlertTriangle },
    { id: 'fmea', label: 'Q3–Q5', title: 'FMEA and Consequences', icon: Grid3x3 },
    { id: 'decisions', label: 'Q6–Q7', title: 'Task Decisions', icon: GitBranch },
    { id: 'deliverables', label: 'Outputs', title: 'Deliverables', icon: File }
  ];

  const [deliverableTab, setDeliverableTab] = useState('worksheet');

  const phaseIndex = PHASES.findIndex(p => p.id === activePhase);

  // Compute progress indicators
  const fnCount = (projectData.rcmFunctions || []).length;
  const ffCount = (projectData.rcmFunctionalFailures || []).length;
  const fmCount = (projectData.rcmcauses || []).length;
  const studyCount = (projectData.rcmStudies || []).length;
  const phaseCounts = [studyCount, fnCount, ffCount, fmCount, fmCount, fmCount];

  // Build enriched causes for criticality (G03: persist enrichment back to rcmcauses)
  const enrichedCauses = useMemo(() => {
    const pd = scopedProjectData || projectData;
    const fmMap = {};
    (pd.failuremodels || []).forEach(fm => { fmMap[fm.id] = fm; });
    const enriched = (pd.rcmcauses || []).map(cause => {
      const fm = fmMap[cause.failureModel] || fmMap[cause.failureModeId] || {};
      return { ...cause, lambda: cause.lambda || fm.lambda || (fm.mttf ? 1 / fm.mttf : null), mttf: cause.mttf || fm.mttf || fm.eta, beta: cause.beta || fm.beta, eta: cause.eta || fm.eta, distribution: cause.distribution || fm.distribution };
    });
    // G03: Auto-persist enriched fields back to rcmcauses in projectData
    const needsUpdate = enriched.some(ec => {
      const orig = (projectData.rcmcauses || []).find(c => c.id === ec.id);
      return orig && (!orig.lambda && ec.lambda) || (!orig.beta && ec.beta) || (!orig.mttf && ec.mttf);
    });
    if (needsUpdate) {
      setTimeout(() => {
        setProjectData(prev => ({
          ...prev,
          rcmcauses: (prev.rcmcauses || []).map(c => {
            const ec = enriched.find(e => e.id === c.id);
            if (ec) return { ...c, lambda: ec.lambda || c.lambda, beta: ec.beta || c.beta, eta: ec.eta || c.eta, mttf: ec.mttf || c.mttf, distribution: ec.distribution || c.distribution };
            return c;
          })
        }));
      }, 0);
    }
    return enriched;
  }, [projectData, scopedProjectData]);

  // G02: Export RCM task recommendations to scheduledtasks
  const exportRCMTasksToSchedule = useCallback(() => {
    const pd = scopedProjectData || projectData;
    const causes = pd.rcmcauses || [];
    const existingRCMTasks = (pd.scheduledtasks || []).filter(t => t.source === 'RCM');
    const newTasks = causes
      .filter(c => c.recommendation && c.recommendation !== 'Run-to-Failure')
      .filter(c => !existingRCMTasks.some(t => t.rcmCauseId === c.id))
      .map(c => ({
        id: 'ST-RCM-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
        source: 'RCM',
        rcmCauseId: c.id,
        equipment: c.equipment || c.equipmentId,
        equipmentId: c.equipment || c.equipmentId,
        failureModel: c.failureModel || c.failureModeId,
        description: c.recommendation + ': ' + (c.description || c.failureMode || ''),
        taskType: c.recommendation,
        interval: c.taskInterval || null,
        craft: c.taskCraft || 'Mechanical',
        estimatedDuration: c.taskDuration || 2,
        spareRequirement: c.spareRequirement || null
      }));
    if (newTasks.length > 0) {
      setProjectData(prev => ({
        ...prev,
        scheduledtasks: [...(prev.scheduledtasks || []), ...newTasks]
      }));
      alert(`Exported ${newTasks.length} RCM task recommendations to Scheduled Tasks`);
    } else {
      alert('No new RCM tasks to export (all already synced or no recommendations)');
    }
  }, [projectData, scopedProjectData, setProjectData]);

  return (
    <div style={{ display: 'flex', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Left: Phase Rail */}
      <div style={{ width: '200px', backgroundColor: COLORS.PANEL_BG, borderRight: `1px solid ${COLORS.BORDER}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '16px 14px 12px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Workflow</div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginTop: '2px' }}>Seven-question process</div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {PHASES.map((phase, idx) => {
            const Icon = phase.icon;
            const isActive = activePhase === phase.id;
            const isDone = phaseCounts[idx] > 0;
            return (
              <button key={phase.id} onClick={() => setActivePhase(phase.id)} style={{
                width: '100%', border: 'none', cursor: 'pointer', padding: '10px 14px',
                display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left',
                backgroundColor: isActive ? '#EFF6FF' : 'transparent',
                borderLeft: isActive ? '3px solid #002F65' : '3px solid transparent',
                transition: 'all 0.15s ease'
              }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '4px',
                  backgroundColor: isActive ? '#002F65' : isDone ? '#F0FDF4' : '#F3F4F6',
                  color: isActive ? '#fff' : isDone ? '#15803D' : '#9CA3AF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isDone && !isActive ? <CheckCircle size={14} /> : <Icon size={14} />}
                </div>
                <div>
                  <div style={{ fontSize: '9px', fontWeight: '700', color: isActive ? '#002F65' : COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{phase.label}</div>
                  <div style={{ fontSize: '11px', fontWeight: isActive ? '600' : '400', color: isActive ? '#002F65' : COLORS.TEXT }}>{phase.title}</div>
                  {isDone && <div style={{ fontSize: '9px', color: '#15803D', marginTop: '1px' }}>{phaseCounts[idx]} items</div>}
                </div>
              </button>
            );
          })}
        </div>
        {/* Bottom: Library access */}
        <div style={{ borderTop: `1px solid ${COLORS.BORDER}`, padding: '8px' }}>
          <button onClick={() => setLibraryOpen(!libraryOpen)} style={{ width: '100%', padding: '8px 12px', backgroundColor: libraryOpen ? '#EFF6FF' : 'transparent', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: '600', color: COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <BookOpen size={12} /> Component Library
          </button>
        </div>
      </div>

      {/* Centre: Phase Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Phase header bar */}
        <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => { const prev = Math.max(0, phaseIndex - 1); setActivePhase(PHASES[prev].id); }} disabled={phaseIndex === 0} style={{ padding: '4px 10px', backgroundColor: phaseIndex > 0 ? 'transparent' : '#F3F4F6', color: phaseIndex > 0 ? COLORS.TEXT : COLORS.TEXT_DIM, border: `1px solid ${phaseIndex > 0 ? COLORS.BORDER : '#E5E7EB'}`, borderRadius: '3px', fontSize: '10px', cursor: phaseIndex > 0 ? 'pointer' : 'not-allowed', textTransform: 'uppercase' }}>Previous</button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Step {phaseIndex + 1} of {PHASES.length}</span>
          </div>
          <button onClick={() => { const next = Math.min(PHASES.length - 1, phaseIndex + 1); setActivePhase(PHASES[next].id); }} disabled={phaseIndex === PHASES.length - 1} style={{ padding: '4px 10px', backgroundColor: phaseIndex < PHASES.length - 1 ? '#002F65' : '#F3F4F6', color: phaseIndex < PHASES.length - 1 ? '#fff' : COLORS.TEXT_DIM, border: 'none', borderRadius: '3px', fontSize: '10px', cursor: phaseIndex < PHASES.length - 1 ? 'pointer' : 'not-allowed', textTransform: 'uppercase', fontWeight: '600' }}>Next</button>
        </div>

        {/* Phase content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {activePhase === 'scope' && <RCM2StudyWizard projectData={projectData} setProjectData={setProjectData} onStartAnalysis={() => setActivePhase('functions')} />}
          {activePhase === 'functions' && <RCM2FunctionsView projectData={projectData} setProjectData={setProjectData} />}
          {activePhase === 'failures' && <RCM2FunctionalFailuresView projectData={projectData} setProjectData={setProjectData} />}
          {activePhase === 'fmea' && <RCM2GuidedWalkthrough projectData={projectData} setProjectData={setProjectData} />}
          {activePhase === 'decisions' && <RCMDecisionTreeView rcmCauses={enrichedCauses} />}
          {activePhase === 'deliverables' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ display: 'flex', padding: '0', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                {[
                  { id: 'worksheet', label: 'Full Worksheet' },
                  { id: 'criticality', label: 'Criticality Matrix' },
                  { id: 'costbenefit', label: 'Cost-Benefit' },
                  { id: 'pmschedule', label: 'PM Schedule' },
                  { id: 'defaultactions', label: 'Default Actions' },
                  { id: 'workinstructions', label: 'Work Instructions' }
                ].map(dt => (
                  <button key={dt.id} onClick={() => setDeliverableTab(dt.id)} style={{
                    padding: '8px 14px', fontSize: '11px', border: 'none', cursor: 'pointer',
                    backgroundColor: deliverableTab === dt.id ? COLORS.TEAL_LIGHT : 'transparent',
                    color: deliverableTab === dt.id ? COLORS.TEAL : COLORS.TEXT,
                    borderBottom: deliverableTab === dt.id ? `2px solid ${COLORS.TEAL}` : '2px solid transparent',
                    fontWeight: deliverableTab === dt.id ? '600' : '400'
                  }}>{dt.label}</button>
                ))}
              </div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                {deliverableTab === 'worksheet' && <RCM2FullWorksheet projectData={projectData} />}
                {deliverableTab === 'criticality' && <CriticalityMatrixView failureModes={enrichedCauses} consequences={(scopedProjectData || projectData).consequences || []} />}
                {deliverableTab === 'costbenefit' && <CostBenefitOptimisationView projectData={projectData} />}
                {deliverableTab === 'pmschedule' && <PMScheduleView projectData={projectData} />}
                {deliverableTab === 'defaultactions' && <DefaultActionRegisterView projectData={projectData} setProjectData={setProjectData} />}
                {deliverableTab === 'workinstructions' && <WorkInstructionsView projectData={scopedProjectData || projectData} />}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: Library Drawer (context-sensitive) */}
      {libraryOpen && (
        <div style={{ width: '320px', borderLeft: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Component Library</div>
            <button onClick={() => setLibraryOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.TEXT_DIM, padding: '2px' }}><X size={14} /></button>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <LibraryView projectData={projectData} setProjectData={setProjectData} />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LIBRARY DRAWER — Context-sensitive library panel for use across modules
// ============================================================================
function LibraryDrawer({ open, onClose, projectData, setProjectData }) {
  if (!open) return null;
  return (
    <div style={{ width: '320px', borderLeft: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG, display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'relative' }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Component Library</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.TEXT_DIM, padding: '2px' }}><X size={14} /></button>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <LibraryView projectData={projectData} setProjectData={setProjectData} />
      </div>
    </div>
  );
}

// ============================================================================
// RCM2 FUNCTIONS VIEW (Q1 — Moubray)
// ============================================================================
const getSuggestionSourceLabel = (suggestion, fallback = 'OptimalAI') => {
  const raw = String(suggestion?.source || suggestion?.dataSource || fallback || 'OptimalAI');
  if (/sfg20/i.test(raw)) return raw;
  if (/iso\s*14224/i.test(raw)) return raw;
  if (/library|reference/i.test(raw)) return raw;
  if (/optimalai/i.test(raw)) return raw;
  return raw;
};

const SuggestionSourceBadge = ({ suggestion, fallback }) => (
  <span title={`Suggestion source: ${getSuggestionSourceLabel(suggestion, fallback)}`} style={{
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    padding: '1px 5px',
    border: `1px solid ${COLORS.TEAL}40`,
    borderRadius: '2px',
    backgroundColor: '#EAFBFF',
    color: COLORS.NAVY,
    fontSize: '8px',
    fontWeight: '700',
    lineHeight: '1.3',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
  }}>
    {getSuggestionSourceLabel(suggestion, fallback)}
  </span>
);

const getSuggestionStableId = (suggestion, idx = 0) => String(
  suggestion?.id || suggestion?.description || suggestion?.failureMode || suggestion?.mechanism || `suggestion-${idx}`
);

const getAuthToken = () => sessionStorage.getItem('oav_token') || localStorage.getItem('oav_token') || '';

const requestOptimalAIRanking = async ({ workflowStepId, sectionId, equipmentType, itemContext, candidates }) => {
  const token = getAuthToken();
  const safeCandidates = (candidates || []).slice(0, 24).map((candidate, idx) => ({
    ...candidate,
    id: getSuggestionStableId(candidate, idx),
  }));
  const resp = await fetch('/api/v1/ai/suggestions/rank', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      workflow_id: 'rcm2',
      workflow_step_id: workflowStepId,
      section_id: sectionId,
      equipment_type: equipmentType || '',
      item_context: itemContext || {},
      candidates: safeCandidates,
    }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `OptimalAI request failed (${resp.status})`);
  }
  return resp.json();
};

const rankSuggestionsForDisplay = (suggestions, aiResult) => {
  if (!aiResult?.ranked_ids?.length) return suggestions;
  const order = new Map(aiResult.ranked_ids.map((id, idx) => [String(id), idx]));
  return [...suggestions].sort((a, b) => {
    const aRank = order.has(String(a.id)) ? order.get(String(a.id)) : 9999;
    const bRank = order.has(String(b.id)) ? order.get(String(b.id)) : 9999;
    return aRank - bRank;
  });
};

const getAIExplanation = (suggestion, aiResult) => {
  const id = String(suggestion?.id || '');
  return aiResult?.explanation_by_id?.[id] || '';
};

const getAIEnrichment = (suggestion, aiResult) => {
  const id = String(suggestion?.id || '');
  return aiResult?.enrichment_by_id?.[id] || null;
};

const SuggestionAIExplanation = ({ suggestion, aiResult }) => {
  const explanation = getAIExplanation(suggestion, aiResult);
  const enrichment = getAIEnrichment(suggestion, aiResult);
  if (!explanation && !enrichment) return null;
  return (
    <div style={{ marginTop: '3px', padding: '4px 6px', backgroundColor: '#F8FBFC', border: `1px solid ${COLORS.BORDER}`, borderRadius: '2px', fontSize: '9px', color: COLORS.TEXT_DIM, lineHeight: '1.35' }}>
      <span style={{ fontWeight: '700', color: COLORS.NAVY }}>OptimalAI:</span> {explanation}
      {enrichment?.confidence != null && <span style={{ marginLeft: '6px', color: COLORS.TEAL, fontWeight: '700' }}>Confidence {Math.round(enrichment.confidence * 100)}%</span>}
      {enrichment?.caution && <div style={{ marginTop: '2px', color: COLORS.WARNING }}>{enrichment.caution}</div>}
    </div>
  );
};

function RCM2FunctionsView({ projectData, setProjectData }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [aiRankings, setAiRankings] = useState({});
  const [aiLoadingKey, setAiLoadingKey] = useState(null);

  const functions = projectData.rcmFunctions || [];
  const equipments = projectData.equipments || [];
  const rcmTickedEquipmentIds = projectData.rcmScopeSelection?.equipmentIds || [];
  const tickedEquipments = equipments.filter(eq => rcmTickedEquipmentIds.includes(eq.id));
  const functionSuggestionGroups = tickedEquipments
    .map(eq => ({
      equipment: eq,
      suggestions: getSuggestions(eq.type || eq.equipmentType || eq.description || eq.name || '', 'rcmFunctions', eq.id, functions)
        .slice(0, 5)
        .map(s => ({ ...s, assetId: eq.id, type: (s.type || 'primary').toLowerCase() }))
    }))
    .filter(group => group.suggestions.length > 0);

  const handleAdd = () => {
    const newFn = {
      id: `FN-${Date.now()}`,
      assetId: equipments[0]?.id || '',
      type: 'primary',
      description: '',
      performanceStandard: '',
      unit: '',
      requiredValue: '',
      operatingContext: ''
    };
    setProjectData(prev => ({ ...prev, rcmFunctions: [...(prev.rcmFunctions || []), newFn] }));
    setEditingId(newFn.id);
    setEditForm(newFn);
  };

  const handleSave = () => {
    setProjectData(prev => ({
      ...prev,
      rcmFunctions: (prev.rcmFunctions || []).map(f => f.id === editingId ? { ...f, ...editForm } : f)
    }));
    setEditingId(null);
    setEditForm({});
  };

  const handleDelete = (id) => {
    setProjectData(prev => ({ ...prev, rcmFunctions: (prev.rcmFunctions || []).filter(f => f.id !== id) }));
  };

  const acceptFunctionSuggestion = (suggestion) => {
    const newFn = {
      ...suggestion,
      id: `FN-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      assetId: suggestion.assetId || suggestion.equipmentId || '',
      type: (suggestion.type || 'primary').toLowerCase(),
      performanceStandard: suggestion.performanceStandard || '',
      unit: suggestion.unit || '',
      requiredValue: suggestion.requiredValue || '',
      operatingContext: suggestion.operatingContext || '',
    };
    setProjectData(prev => ({ ...prev, rcmFunctions: [...(prev.rcmFunctions || []), newFn] }));
  };

  const acceptAllFunctionSuggestions = (suggestions) => {
    const base = Date.now();
    const newFunctions = suggestions.map((suggestion, idx) => ({
      ...suggestion,
      id: `FN-${base}-${idx}`,
      assetId: suggestion.assetId || suggestion.equipmentId || '',
      type: (suggestion.type || 'primary').toLowerCase(),
      performanceStandard: suggestion.performanceStandard || '',
      unit: suggestion.unit || '',
      requiredValue: suggestion.requiredValue || '',
      operatingContext: suggestion.operatingContext || '',
    }));
    setProjectData(prev => ({ ...prev, rcmFunctions: [...(prev.rcmFunctions || []), ...newFunctions] }));
  };

  const getAssetName = (id) => {
    const eq = equipments.find(e => e.id === id);
    return eq ? (eq.description || eq.id) : id || 'Unassigned';
  };

  const handleEnhanceFunctionSuggestions = async (group) => {
    const key = group.equipment.id;
    setAiLoadingKey(key);
    try {
      const aiResult = await requestOptimalAIRanking({
        workflowStepId: 'Q1-functions',
        sectionId: 'rcmFunctions',
        equipmentType: group.equipment.type || group.equipment.equipmentType || group.equipment.description || group.equipment.name || '',
        itemContext: { equipment: group.equipment, scopeEquipmentIds: rcmTickedEquipmentIds },
        candidates: group.suggestions,
      });
      setAiRankings(prev => ({ ...prev, [key]: aiResult }));
    } catch (err) {
      setAiRankings(prev => ({ ...prev, [key]: { ranked_ids: [], explanation_by_id: {}, enrichment_by_id: {}, error: err.message } }));
    } finally {
      setAiLoadingKey(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Question 1</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Functions and Performance Standards</div>
        <div style={{ flex: 1 }} />
        <button onClick={handleAdd} style={{ padding: '6px 14px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Add Function</button>
      </div>
      <div style={{ padding: '12px 16px', backgroundColor: '#FAFBFD', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', color: COLORS.TEXT_DIM, lineHeight: '1.5' }}>
        Define what each asset is expected to do in its present operating context. Each function must include a quantified performance standard where possible (e.g., "Pump coolant at minimum 500 L/min at 3 bar").
      </div>
      <div style={{ margin: '12px', padding: '12px', backgroundColor: '#FAFBFD', border: `1px solid ${COLORS.BORDER}`, borderLeft: `3px solid ${COLORS.TEAL}`, borderRadius: '4px' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: COLORS.NAVY, fontFamily: 'Georgia, serif', marginBottom: '6px' }}>Contextual Function Suggestions</div>
        <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, lineHeight: '1.4', marginBottom: '8px' }}>Based on equipment selected in RCM scope. Sources: ISO 14224 / Equipment Library / SFG20 / OptimalAI.</div>
        {functionSuggestionGroups.length === 0 ? (
          <div style={{ padding: '8px', fontSize: '10px', color: COLORS.TEXT_DIM, backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>No function suggestions available for the current RCM scope.</div>
        ) : functionSuggestionGroups.map(group => (
          <div key={group.equipment.id} style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: COLORS.TEXT }}>{group.equipment.description || group.equipment.name || group.equipment.id}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button onClick={() => handleEnhanceFunctionSuggestions(group)} disabled={aiLoadingKey === group.equipment.id} style={{ padding: '2px 7px', fontSize: '8px', fontWeight: '700', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '2px', cursor: aiLoadingKey === group.equipment.id ? 'wait' : 'pointer', textTransform: 'uppercase' }}>{aiLoadingKey === group.equipment.id ? 'Thinking' : 'OptimalAI LLM'}</button>
                <button onClick={() => acceptAllFunctionSuggestions(group.suggestions)} style={{ padding: '2px 7px', fontSize: '8px', fontWeight: '700', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '2px', cursor: 'pointer', textTransform: 'uppercase' }}>Accept All</button>
              </div>
            </div>
            {aiRankings[group.equipment.id]?.error && <div style={{ marginBottom: '4px', fontSize: '9px', color: COLORS.WARNING }}>{aiRankings[group.equipment.id].error}</div>}
            {rankSuggestionsForDisplay(group.suggestions, aiRankings[group.equipment.id]).map(suggestion => (
              <div key={suggestion.id || suggestion.description} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', padding: '4px 5px', marginBottom: '3px', backgroundColor: COLORS.LIGHT_BG, borderRadius: '2px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: '10px', color: COLORS.TEXT, lineHeight: '1.35' }}>{suggestion.description}</div>
                    <SuggestionSourceBadge suggestion={suggestion} fallback="OptimalAI" />
                  </div>
                  {suggestion.performanceStandard && <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, marginTop: '1px', lineHeight: '1.3' }}>{suggestion.performanceStandard}</div>}
                  <SuggestionAIExplanation suggestion={suggestion} aiResult={aiRankings[group.equipment.id]} />
                </div>
                <button onClick={() => acceptFunctionSuggestion(suggestion)} style={{ padding: '1px 6px', fontSize: '8px', fontWeight: '600', border: `1px solid ${COLORS.TEAL}40`, borderRadius: '2px', backgroundColor: 'transparent', color: COLORS.TEAL, cursor: 'pointer' }}>+</button>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {functions.length === 0 ? (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '8px' }}>No functions defined</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '20px' }}>Define the functions and performance standards for each asset in your hierarchy before proceeding to Functional Failures.</div>
            <button onClick={handleAdd} style={{ padding: '8px 20px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Define First Function</button>
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `2px solid ${COLORS.BORDER}` }}>
                {['Asset', 'Type', 'Function Description', 'Performance Standard', 'Unit', 'Required Value', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.TEXT, fontWeight: '600', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {functions.map((fn, idx) => (
                editingId === fn.id ? (
                  <tr key={fn.id} style={{ backgroundColor: '#F0F7FF', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '6px' }}>
                      <select value={editForm.assetId || ''} onChange={e => setEditForm(p => ({ ...p, assetId: e.target.value }))} style={{ width: '100%', padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
                        <option value="">Select asset...</option>
                        {equipments.map(eq => <option key={eq.id} value={eq.id}>{eq.description || eq.id}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '6px' }}>
                      <select value={editForm.type || 'primary'} onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))} style={{ padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                        <option value="protective">Protective</option>
                        <option value="informational">Informational</option>
                        <option value="superfluous">Superfluous</option>
                      </select>
                    </td>
                    <td style={{ padding: '6px' }}><input value={editForm.description || ''} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g., Transfer coolant from sump to heat exchanger" style={{ width: '100%', padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></td>
                    <td style={{ padding: '6px' }}><input value={editForm.performanceStandard || ''} onChange={e => setEditForm(p => ({ ...p, performanceStandard: e.target.value }))} placeholder="e.g., Minimum 500 L/min at 3 bar" style={{ width: '100%', padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></td>
                    <td style={{ padding: '6px' }}><input value={editForm.unit || ''} onChange={e => setEditForm(p => ({ ...p, unit: e.target.value }))} placeholder="L/min" style={{ width: '60px', padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></td>
                    <td style={{ padding: '6px' }}><input value={editForm.requiredValue || ''} onChange={e => setEditForm(p => ({ ...p, requiredValue: e.target.value }))} placeholder="500" style={{ width: '60px', padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></td>
                    <td style={{ padding: '6px', display: 'flex', gap: '4px' }}>
                      <button onClick={handleSave} style={{ padding: '4px 10px', backgroundColor: COLORS.SUCCESS, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Save</button>
                      <button onClick={() => { setEditingId(null); setEditForm({}); }} style={{ padding: '4px 10px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={fn.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '8px 10px', color: COLORS.TEXT }}>{getAssetName(fn.assetId)}</td>
                    <td style={{ padding: '8px 10px' }}><span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', backgroundColor: fn.type === 'primary' ? '#EFF6FF' : fn.type === 'protective' ? '#FEF2F2' : '#F0FDF4', color: fn.type === 'primary' ? '#1D4ED8' : fn.type === 'protective' ? '#DC2626' : '#15803D' }}>{fn.type}</span></td>
                    <td style={{ padding: '8px 10px', color: COLORS.TEXT, maxWidth: '300px' }}>{fn.description || '—'}</td>
                    <td style={{ padding: '8px 10px', color: COLORS.INFO, fontStyle: fn.performanceStandard ? 'normal' : 'italic' }}>{fn.performanceStandard || 'Not specified'}</td>
                    <td style={{ padding: '8px 10px', color: COLORS.TEXT_DIM }}>{fn.unit || '—'}</td>
                    <td style={{ padding: '8px 10px', color: COLORS.TEXT, fontWeight: '600' }}>{fn.requiredValue || '—'}</td>
                    <td style={{ padding: '8px 10px', display: 'flex', gap: '4px' }}>
                      <button onClick={() => { setEditingId(fn.id); setEditForm({ ...fn }); }} style={{ padding: '3px 8px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => handleDelete(fn.id)} style={{ padding: '3px 8px', backgroundColor: 'transparent', color: COLORS.ERROR, border: `1px solid rgba(239,68,68,0.2)`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Remove</button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ padding: '8px 16px', backgroundColor: COLORS.PANEL_BG, borderTop: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '16px', fontSize: '10px', color: COLORS.TEXT_DIM }}>
        <span>Total: {functions.length} functions</span>
        <span>Primary: {functions.filter(f => f.type === 'primary').length}</span>
        <span>Secondary: {functions.filter(f => f.type === 'secondary').length}</span>
        <span>Protective: {functions.filter(f => f.type === 'protective').length}</span>
      </div>
    </div>
  );
}

// ============================================================================
// RCM2 FUNCTIONAL FAILURES VIEW (Q2 — Moubray)
// ============================================================================
function RCM2FunctionalFailuresView({ projectData, setProjectData }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [filterFunctionId, setFilterFunctionId] = useState('all');
  const [aiRanking, setAiRanking] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const functionalFailures = projectData.rcmFunctionalFailures || [];
  const functions = projectData.rcmFunctions || [];
  const equipments = projectData.equipments || [];
  const rcmTickedEquipmentIds = projectData.rcmScopeSelection?.equipmentIds || [];
  const scopedFunctions = functions.filter(fn => rcmTickedEquipmentIds.includes(fn.assetId || fn.equipmentId || fn.equipment));
  const functionalFailureSuggestions = scopedFunctions.slice(0, 6).flatMap((fn, fnIndex) => {
    const fnText = String(fn.description || '').trim().replace(/\.$/, '');
    if (!fnText) return [];
    const lowerFn = fnText.charAt(0).toLowerCase() + fnText.slice(1);
    const context = fn.performanceStandard || fn.requiredValue || '';
    return [
      { id: `FFS-${fn.id || fnIndex}-total`, functionId: fn.id, failureType: 'total', description: `Unable to ${lowerFn}`, context, source: 'OptimalAI / RCM2 logic' },
      { id: `FFS-${fn.id || fnIndex}-partial`, functionId: fn.id, failureType: 'partial', description: `Partially unable to ${lowerFn}`, context, source: 'OptimalAI / RCM2 logic' },
      { id: `FFS-${fn.id || fnIndex}-erratic`, functionId: fn.id, failureType: 'erratic', description: `Intermittently fails to ${lowerFn}`, context, source: 'OptimalAI / RCM2 logic' },
    ];
  }).filter(suggestion => !functionalFailures.some(ff =>
    ff.functionId === suggestion.functionId &&
    String(ff.description || '').toLowerCase() === suggestion.description.toLowerCase()
  ));

  const filtered = filterFunctionId === 'all' ? functionalFailures : functionalFailures.filter(ff => ff.functionId === filterFunctionId);

  const handleAdd = () => {
    const newFF = {
      id: `FF-${Date.now()}`,
      functionId: filterFunctionId !== 'all' ? filterFunctionId : (functions[0]?.id || ''),
      description: '',
      failureType: 'total',
      context: ''
    };
    setProjectData(prev => ({ ...prev, rcmFunctionalFailures: [...(prev.rcmFunctionalFailures || []), newFF] }));
    setEditingId(newFF.id);
    setEditForm(newFF);
  };

  const handleSave = () => {
    setProjectData(prev => ({
      ...prev,
      rcmFunctionalFailures: (prev.rcmFunctionalFailures || []).map(ff => ff.id === editingId ? { ...ff, ...editForm } : ff)
    }));
    setEditingId(null);
  };

  const handleDelete = (id) => {
    setProjectData(prev => ({ ...prev, rcmFunctionalFailures: (prev.rcmFunctionalFailures || []).filter(ff => ff.id !== id) }));
  };

  const acceptFunctionalFailureSuggestion = (suggestion) => {
    const newFF = {
      ...suggestion,
      id: `FF-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    };
    setProjectData(prev => ({ ...prev, rcmFunctionalFailures: [...(prev.rcmFunctionalFailures || []), newFF] }));
  };

  const acceptAllFunctionalFailureSuggestions = () => {
    const base = Date.now();
    const newFailures = functionalFailureSuggestions.map((suggestion, idx) => ({
      ...suggestion,
      id: `FF-${base}-${idx}`,
    }));
    setProjectData(prev => ({ ...prev, rcmFunctionalFailures: [...(prev.rcmFunctionalFailures || []), ...newFailures] }));
  };

  const getFunctionDesc = (id) => {
    const fn = functions.find(f => f.id === id);
    return fn ? fn.description : id || 'Unlinked';
  };

  const handleEnhanceFunctionalFailures = async () => {
    setAiLoading(true);
    try {
      const aiResult = await requestOptimalAIRanking({
        workflowStepId: 'Q2-functional-failures',
        sectionId: 'rcmFunctionalFailures',
        equipmentType: rcmTickedEquipmentIds.map(id => equipments.find(eq => eq.id === id)?.type || equipments.find(eq => eq.id === id)?.description || id).join(', '),
        itemContext: { scopeEquipmentIds: rcmTickedEquipmentIds, functions: scopedFunctions.slice(0, 12) },
        candidates: functionalFailureSuggestions,
      });
      setAiRanking(aiResult);
    } catch (err) {
      setAiRanking({ ranked_ids: [], explanation_by_id: {}, enrichment_by_id: {}, error: err.message });
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Question 2</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Functional Failures</div>
        <div style={{ flex: 1 }} />
        <select value={filterFunctionId} onChange={e => setFilterFunctionId(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', backgroundColor: '#fff' }}>
          <option value="all">All Functions</option>
          {functions.map(fn => <option key={fn.id} value={fn.id}>{fn.description ? fn.description.substring(0, 50) : fn.id}</option>)}
        </select>
        <button onClick={handleAdd} style={{ padding: '6px 14px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Add Functional Failure</button>
      </div>
      <div style={{ padding: '12px 16px', backgroundColor: '#FAFBFD', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', color: COLORS.TEXT_DIM, lineHeight: '1.5' }}>
        For each function, identify all the ways it can fail. A functional failure is any state in which the asset cannot fulfil a function to the standard of performance acceptable to the user. Types: Total loss, Partial loss, Over-performance, or Erratic performance.
      </div>
      <div style={{ margin: '12px', padding: '12px', backgroundColor: '#FAFBFD', border: `1px solid ${COLORS.BORDER}`, borderLeft: `3px solid ${COLORS.TEAL}`, borderRadius: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: COLORS.NAVY, fontFamily: 'Georgia, serif' }}>Contextual Functional Failure Suggestions</div>
          {functionalFailureSuggestions.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button onClick={handleEnhanceFunctionalFailures} disabled={aiLoading} style={{ padding: '2px 7px', fontSize: '8px', fontWeight: '700', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '2px', cursor: aiLoading ? 'wait' : 'pointer', textTransform: 'uppercase' }}>{aiLoading ? 'Thinking' : 'OptimalAI LLM'}</button>
              <button onClick={acceptAllFunctionalFailureSuggestions} style={{ padding: '2px 7px', fontSize: '8px', fontWeight: '700', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '2px', cursor: 'pointer', textTransform: 'uppercase' }}>Accept All</button>
            </div>
          )}
        </div>
        <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, lineHeight: '1.4', marginBottom: '8px' }}>Derived from Q1 functions carried forward for the equipment selected in RCM scope. Sources: ISO 14224 / Equipment Library / SFG20 / OptimalAI.</div>
        {aiRanking?.error && <div style={{ marginBottom: '4px', fontSize: '9px', color: COLORS.WARNING }}>{aiRanking.error}</div>}
        {functionalFailureSuggestions.length === 0 ? (
          <div style={{ padding: '8px', fontSize: '10px', color: COLORS.TEXT_DIM, backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>No functional failure suggestions available. Confirm RCM scope and Q1 functions first.</div>
        ) : rankSuggestionsForDisplay(functionalFailureSuggestions.slice(0, 12), aiRanking).map(suggestion => (
          <div key={suggestion.id} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', padding: '4px 5px', marginBottom: '3px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '2px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: '10px', color: COLORS.TEXT, lineHeight: '1.35' }}>{suggestion.description}</div>
                <SuggestionSourceBadge suggestion={suggestion} fallback="OptimalAI" />
              </div>
              <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, marginTop: '1px', lineHeight: '1.3' }}>{getFunctionDesc(suggestion.functionId)}{suggestion.context ? ` | ${suggestion.context}` : ''}</div>
              <SuggestionAIExplanation suggestion={suggestion} aiResult={aiRanking} />
            </div>
            <button onClick={() => acceptFunctionalFailureSuggestion(suggestion)} style={{ padding: '1px 6px', fontSize: '8px', fontWeight: '600', border: `1px solid ${COLORS.TEAL}40`, borderRadius: '2px', backgroundColor: 'transparent', color: COLORS.TEAL, cursor: 'pointer' }}>+</button>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '8px' }}>No functional failures defined</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '20px' }}>{functions.length === 0 ? 'Define functions first (Q1), then identify how each function can fail.' : 'Identify the ways each function can fail to meet its performance standard.'}</div>
            {functions.length > 0 && <button onClick={handleAdd} style={{ padding: '8px 20px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textTransform: 'uppercase' }}>Add First Functional Failure</button>}
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `2px solid ${COLORS.BORDER}` }}>
                {['Parent Function', 'Failure Type', 'Functional Failure Description', 'Linked Failure Modes', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.TEXT, fontWeight: '600', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((ff, idx) => {
                const linkedFMs = (projectData.rcmcauses || []).filter(c => c.functionalFailureId === ff.id).length + (projectData.failuremodels || []).filter(fm => fm.functionalFailureId === ff.id).length;
                return editingId === ff.id ? (
                  <tr key={ff.id} style={{ backgroundColor: '#F0F7FF', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '6px' }}>
                      <select value={editForm.functionId || ''} onChange={e => setEditForm(p => ({ ...p, functionId: e.target.value }))} style={{ width: '100%', padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
                        <option value="">Select function...</option>
                        {functions.map(fn => <option key={fn.id} value={fn.id}>{fn.description ? fn.description.substring(0, 60) : fn.id}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '6px' }}>
                      <select value={editForm.failureType || 'total'} onChange={e => setEditForm(p => ({ ...p, failureType: e.target.value }))} style={{ padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
                        <option value="total">Total Loss</option>
                        <option value="partial">Partial Loss</option>
                        <option value="over">Over-performance</option>
                        <option value="erratic">Erratic Performance</option>
                        <option value="degraded">Degraded Performance</option>
                      </select>
                    </td>
                    <td style={{ padding: '6px' }}><input value={editForm.description || ''} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g., Unable to transfer any coolant" style={{ width: '100%', padding: '4px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></td>
                    <td style={{ padding: '6px', color: COLORS.TEXT_DIM }}>{linkedFMs}</td>
                    <td style={{ padding: '6px', display: 'flex', gap: '4px' }}>
                      <button onClick={handleSave} style={{ padding: '4px 10px', backgroundColor: COLORS.SUCCESS, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditingId(null)} style={{ padding: '4px 10px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={ff.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '8px 10px', color: COLORS.INFO, fontSize: '10px', maxWidth: '200px' }}>{getFunctionDesc(ff.functionId)}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', backgroundColor: ff.failureType === 'total' ? '#FEE2E2' : ff.failureType === 'partial' ? '#FFF7ED' : '#EFF6FF', color: ff.failureType === 'total' ? '#DC2626' : ff.failureType === 'partial' ? '#C2410C' : '#1D4ED8' }}>{ff.failureType}</span>
                    </td>
                    <td style={{ padding: '8px 10px', color: COLORS.TEXT }}>{ff.description || '—'}</td>
                    <td style={{ padding: '8px 10px', color: linkedFMs > 0 ? COLORS.SUCCESS : COLORS.TEXT_DIM, fontWeight: '600' }}>{linkedFMs}</td>
                    <td style={{ padding: '8px 10px', display: 'flex', gap: '4px' }}>
                      <button onClick={() => { setEditingId(ff.id); setEditForm({ ...ff }); }} style={{ padding: '3px 8px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => handleDelete(ff.id)} style={{ padding: '3px 8px', backgroundColor: 'transparent', color: COLORS.ERROR, border: `1px solid rgba(239,68,68,0.2)`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ padding: '8px 16px', backgroundColor: COLORS.PANEL_BG, borderTop: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '16px', fontSize: '10px', color: COLORS.TEXT_DIM }}>
        <span>Total: {functionalFailures.length} functional failures</span>
        <span>Total Loss: {functionalFailures.filter(f => f.failureType === 'total').length}</span>
        <span>Partial: {functionalFailures.filter(f => f.failureType === 'partial').length}</span>
      </div>
    </div>
  );
}

// ============================================================================
// RCM2 STUDY WIZARD — PHASE 0 (Scope & Maintainable Item Classification)
// ============================================================================
function RCM2StudyWizard({ projectData, setProjectData, onStartAnalysis }) {
  const [phase, setPhase] = useState('scope'); // scope | classify | review | start
  const [selectedScope, setSelectedScope] = useState(() => projectData.rcmScopeSelection?.equipmentIds || []);
  const [classifications, setClassifications] = useState({});
  const engine = useMemo(() => new RCMEngine(), []);
  const equipments = projectData.equipments || [];

  useEffect(() => {
    const savedScope = projectData.rcmScopeSelection?.equipmentIds || [];
    setSelectedScope(prev => {
      const same = prev.length === savedScope.length && prev.every(id => savedScope.includes(id));
      return same ? prev : savedScope;
    });
  }, [projectData.rcmScopeSelection?.equipmentIds]);

  useEffect(() => {
    setProjectData(prev => {
      const current = prev.rcmScopeSelection?.equipmentIds || [];
      const same = current.length === selectedScope.length && current.every(id => selectedScope.includes(id));
      if (same) return prev;
      return {
        ...prev,
        rcmScopeSelection: {
          ...(prev.rcmScopeSelection || {}),
          equipmentIds: selectedScope,
        },
      };
    });
  }, [selectedScope, setProjectData]);

  const handleClassifyAll = () => {
    const results = {};
    equipments.filter(eq => selectedScope.includes(eq.id)).forEach(eq => {
      results[eq.id] = engine.classifyMaintainableItem(eq);
    });
    setClassifications(results);
    setPhase('review');
  };

  const handleOverrideClassification = (eqId, newClass) => {
    setClassifications(prev => ({
      ...prev,
      [eqId]: { ...prev[eqId], classification: newClass, rcmRequired: newClass !== 'run_to_failure', reason: 'Manual override by analyst' }
    }));
  };

  const handleStartStudy = () => {
    const rcmItems = Object.entries(classifications).filter(([_, c]) => c.rcmRequired).map(([id]) => id);
    const activeEquipmentId = rcmItems[0] || selectedScope[0] || '';
    const rcmStudy = {
      id: `RCM-STUDY-${Date.now()}`,
      name: `RCM Study — ${new Date().toLocaleDateString()}`,
      createdDate: new Date().toISOString(),
      scope: selectedScope,
      classifications: classifications,
      rcmItems,
      status: 'in_progress',
      currentItemIndex: 0,
      activeEquipmentId,
      completedItems: []
    };
    setProjectData(prev => ({
      ...prev,
      rcmScopeSelection: {
        ...(prev.rcmScopeSelection || {}),
        equipmentIds: selectedScope,
        activeEquipmentId,
      },
      rcmStudies: [...(prev.rcmStudies || []), rcmStudy]
    }));
    onStartAnalysis?.(rcmStudy);
  };

  const rcmItemCount = Object.values(classifications).filter(c => c.rcmRequired).length;
  const rtfItemCount = Object.values(classifications).filter(c => !c.rcmRequired).length;

  const PHASE_LABELS = { scope: 'Define Scope', classify: 'Classify Items', review: 'Review & Confirm', start: 'Begin Analysis' };
  const classColors = { critical: '#DC2626', significant: '#F59E0B', economic: '#3B82F6', run_to_failure: '#6B7280' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Phase 0</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Study Wizard — Scope and Classification</div>
      </div>
      {/* Phase progress bar */}
      <div style={{ display: 'flex', padding: '12px 16px', backgroundColor: '#FAFBFD', borderBottom: `1px solid ${COLORS.BORDER}`, gap: '4px' }}>
        {Object.entries(PHASE_LABELS).map(([key, label], idx) => (
          <div key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700',
              backgroundColor: phase === key ? COLORS.NAVY : Object.keys(PHASE_LABELS).indexOf(phase) > idx ? COLORS.SUCCESS : '#E5E7EB',
              color: phase === key || Object.keys(PHASE_LABELS).indexOf(phase) > idx ? '#fff' : COLORS.TEXT_DIM
            }}>{idx + 1}</div>
            <span style={{ fontSize: '10px', fontWeight: phase === key ? '700' : '400', color: phase === key ? COLORS.NAVY : COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</span>
            {idx < 3 && <div style={{ flex: 1, height: '1px', backgroundColor: Object.keys(PHASE_LABELS).indexOf(phase) > idx ? COLORS.SUCCESS : '#E5E7EB', marginLeft: '4px' }} />}
          </div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {phase === 'scope' && (
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '12px' }}>Select Equipment for RCM Analysis</div>
            <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Select the assets within the hierarchy that should be assessed for maintainability. Each selected item will be classified to determine whether full RCM analysis is warranted.</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button onClick={() => setSelectedScope(equipments.map(e => e.id))} style={{ padding: '4px 12px', fontSize: '10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', backgroundColor: '#fff', cursor: 'pointer', color: COLORS.TEXT }}>Select All</button>
              <button onClick={() => setSelectedScope([])} style={{ padding: '4px 12px', fontSize: '10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', backgroundColor: '#fff', cursor: 'pointer', color: COLORS.TEXT }}>Clear</button>
            </div>
            <div style={{ maxHeight: '400px', overflow: 'auto', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px' }}>
              {equipments.map((eq, idx) => (
                <div key={eq.id} onClick={() => setSelectedScope(prev => prev.includes(eq.id) ? prev.filter(id => id !== eq.id) : [...prev, eq.id])} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', cursor: 'pointer',
                  backgroundColor: selectedScope.includes(eq.id) ? '#EFF6FF' : idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG,
                  borderBottom: `1px solid ${COLORS.BORDER}`
                }}>
                  <div style={{ width: '16px', height: '16px', borderRadius: '3px', border: `2px solid ${selectedScope.includes(eq.id) ? COLORS.NAVY : COLORS.BORDER}`, backgroundColor: selectedScope.includes(eq.id) ? COLORS.NAVY : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {selectedScope.includes(eq.id) && <span style={{ color: '#fff', fontSize: '10px', fontWeight: '700' }}>&#10003;</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{eq.description || eq.id}</div>
                    <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{eq.id} | {eq.type || 'Equipment'}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => { if (selectedScope.length > 0) setPhase('classify'); }} disabled={selectedScope.length === 0} style={{ padding: '8px 24px', backgroundColor: selectedScope.length > 0 ? COLORS.NAVY : '#CBD5E1', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: selectedScope.length > 0 ? 'pointer' : 'not-allowed', textTransform: 'uppercase' }}>Next: Classify Items</button>
            </div>
          </div>
        )}

        {phase === 'classify' && (
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '8px' }}>Maintainable Item Classification</div>
            <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>{selectedScope.length} items selected. The engine will classify each based on safety, environmental, and operational criticality to determine whether full RCM analysis is warranted.</div>
            <button onClick={handleClassifyAll} style={{ padding: '8px 24px', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textTransform: 'uppercase', marginBottom: '16px' }}>Classify All Items</button>
            {Object.keys(classifications).length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button onClick={() => setPhase('review')} style={{ padding: '8px 24px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textTransform: 'uppercase' }}>Next: Review</button>
              </div>
            )}
          </div>
        )}

        {phase === 'review' && (
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '12px' }}>Review Classification Results</div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              {[
                { label: 'RCM Required', count: rcmItemCount, color: COLORS.NAVY },
                { label: 'Run to Failure', count: rtfItemCount, color: '#6B7280' },
                { label: 'Total Scope', count: selectedScope.length, color: COLORS.TEAL }
              ].map(kpi => (
                <div key={kpi.label} style={{ flex: 1, padding: '12px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', borderTop: `3px solid ${kpi.color}` }}>
                  <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{kpi.label}</div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>{kpi.count}</div>
                </div>
              ))}
            </div>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `2px solid ${COLORS.BORDER}` }}>
                  {['Equipment', 'Classification', 'RCM Required', 'Reason', 'Override'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.TEXT, fontWeight: '600', fontSize: '10px', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedScope.map((eqId, idx) => {
                  const eq = equipments.find(e => e.id === eqId);
                  const cls = classifications[eqId] || {};
                  return (
                    <tr key={eqId} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                      <td style={{ padding: '8px 10px', color: COLORS.TEXT }}>{eq?.description || eqId}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: classColors[cls.classification] || '#6B7280', backgroundColor: (classColors[cls.classification] || '#6B7280') + '18' }}>{cls.classification || '—'}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: cls.rcmRequired ? COLORS.SUCCESS : COLORS.TEXT_DIM, fontWeight: '600' }}>{cls.rcmRequired ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '8px 10px', color: COLORS.TEXT_DIM, fontSize: '10px' }}>{cls.reason || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <select value={cls.classification || ''} onChange={e => handleOverrideClassification(eqId, e.target.value)} style={{ padding: '3px 6px', fontSize: '10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
                          <option value="critical">Critical</option>
                          <option value="significant">Significant</option>
                          <option value="economic">Economic</option>
                          <option value="run_to_failure">Run to Failure</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setPhase('scope')} style={{ padding: '8px 20px', backgroundColor: 'transparent', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px', cursor: 'pointer', textTransform: 'uppercase' }}>Back</button>
              <button onClick={handleStartStudy} disabled={rcmItemCount === 0} style={{ padding: '8px 24px', backgroundColor: rcmItemCount > 0 ? COLORS.NAVY : '#CBD5E1', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: rcmItemCount > 0 ? 'pointer' : 'not-allowed', textTransform: 'uppercase' }}>Start RCM Analysis ({rcmItemCount} items)</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// RCM2 GUIDED 7-QUESTION WALKTHROUGH
// ============================================================================
function RCM2GuidedWalkthrough({ projectData, setProjectData }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [walkData, setWalkData] = useState({ functions: [], functionalFailures: [], failureModes: [], failureEffects: [], consequences: [], tasks: [], defaultActions: [] });
  const [addingItem, setAddingItem] = useState(false);
  const [newItemForm, setNewItemForm] = useState({});
  const [aiFailureModeRanking, setAiFailureModeRanking] = useState(null);
  const [aiFailureModeLoading, setAiFailureModeLoading] = useState(false);
  const engine = useMemo(() => new RCMEngine(), []);

  const equipments = projectData.equipments || [];
  const functions = projectData.rcmFunctions || [];
  const functionalFailures = projectData.rcmFunctionalFailures || [];
  const rcmScopeIds = projectData.rcmScopeSelection?.equipmentIds || [];
  const rcmActiveEquipmentId = projectData.rcmScopeSelection?.activeEquipmentId || projectData.rcmStudies?.find(study => study.status === 'in_progress')?.activeEquipmentId || '';
  const scopedEquipments = rcmScopeIds.length > 0
    ? rcmScopeIds.map(id => equipments.find(eq => eq.id === id)).filter(Boolean)
    : equipments;

  const STEPS = RCMEngine.RCM2_QUESTIONS || [
    { id: 'Q1', title: 'Functions', question: 'What are the functions and associated desired standards of performance of the asset?' },
    { id: 'Q2', title: 'Functional Failures', question: 'In what ways can it fail to fulfil its functions?' },
    { id: 'Q3', title: 'Failure Modes', question: 'What causes each functional failure?' },
    { id: 'Q4', title: 'Failure Effects', question: 'What happens when each failure occurs?' },
    { id: 'Q5', title: 'Consequences', question: 'In what way does each failure matter?' },
    { id: 'Q6', title: 'Proactive Tasks', question: 'What should be done to predict or prevent each failure?' },
    { id: 'Q7', title: 'Default Actions', question: 'What should be done if a suitable proactive task cannot be found?' }
  ];

  const handleSelectEquipment = (eq) => {
    setSelectedEquipment(eq);
    setProjectData(prev => ({
      ...prev,
      rcmScopeSelection: {
        ...(prev.rcmScopeSelection || {}),
        activeEquipmentId: eq.id,
      },
    }));
    // Load existing data for this equipment
    const eqFunctions = functions.filter(f => f.assetId === eq.id);
    const eqFFs = functionalFailures.filter(ff => eqFunctions.some(fn => fn.id === ff.functionId));
    const eqFMs = (projectData.rcmcauses || []).filter(c => c.equipmentId === eq.id);
    setWalkData({
      functions: eqFunctions,
      functionalFailures: eqFFs,
      failureModes: eqFMs,
      failureEffects: eqFMs.map(fm => ({ id: fm.id, effect: fm.failureEffect || '' })),
      consequences: eqFMs.map(fm => ({ id: fm.id, type: fm.consequenceType || '', classification: engine.classifyConsequence(fm) })),
      tasks: eqFMs.map(fm => ({ id: fm.id, taskType: engine.recommendTask(fm, engine.classifyConsequence(fm)), interval: fm.taskInterval || '' })),
      defaultActions: eqFMs.filter(fm => { const t = engine.recommendTask(fm, engine.classifyConsequence(fm)); return t === 'RUN_TO_FAILURE'; }).map(fm => ({ id: fm.id, action: 'no_scheduled_maintenance', justification: '' }))
    });
    setCurrentStep(0);
  };

  useEffect(() => {
    if (scopedEquipments.length === 0) return;
    if (selectedEquipment && scopedEquipments.some(eq => eq.id === selectedEquipment.id)) return;
    const activeEquipment = scopedEquipments.find(eq => eq.id === rcmActiveEquipmentId);
    const equipmentWithFailures = scopedEquipments.find(eq => {
      const eqFunctions = functions.filter(f => f.assetId === eq.id);
      return functionalFailures.some(ff => eqFunctions.some(fn => fn.id === ff.functionId));
    });
    handleSelectEquipment(activeEquipment || equipmentWithFailures || scopedEquipments[0]);
  }, [selectedEquipment, scopedEquipments, rcmActiveEquipmentId, functions, functionalFailures]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddFunction = () => {
    const fn = { id: `FN-${Date.now()}`, assetId: selectedEquipment.id, type: newItemForm.type || 'primary', description: newItemForm.description || '', performanceStandard: newItemForm.performanceStandard || '', unit: newItemForm.unit || '', requiredValue: newItemForm.requiredValue || '' };
    setProjectData(prev => ({ ...prev, rcmFunctions: [...(prev.rcmFunctions || []), fn] }));
    setWalkData(prev => ({ ...prev, functions: [...prev.functions, fn] }));
    setAddingItem(false);
    setNewItemForm({});
  };

  const handleAddFF = () => {
    const ff = { id: `FF-${Date.now()}`, functionId: newItemForm.functionId || '', failureType: newItemForm.failureType || 'total', description: newItemForm.description || '' };
    setProjectData(prev => ({ ...prev, rcmFunctionalFailures: [...(prev.rcmFunctionalFailures || []), ff] }));
    setWalkData(prev => ({ ...prev, functionalFailures: [...prev.functionalFailures, ff] }));
    setAddingItem(false);
    setNewItemForm({});
  };

  const handleAddFM = () => {
    const fm = { id: `FM-${Date.now()}`, equipmentId: selectedEquipment.id, functionalFailureId: newItemForm.functionalFailureId || '', failureMode: newItemForm.failureMode || '', failureEffect: newItemForm.failureEffect || '', severity: parseInt(newItemForm.severity) || 5, occurrence: parseInt(newItemForm.occurrence) || 3, detection: parseInt(newItemForm.detection) || 5, isEvident: newItemForm.isEvident !== 'false', affectsSafety: newItemForm.affectsSafety === 'true', affectsEnvironment: newItemForm.affectsEnvironment === 'true', affectsOperations: newItemForm.affectsOperations === 'true' };
    setProjectData(prev => ({ ...prev, rcmcauses: [...(prev.rcmcauses || []), fm] }));
    const consequence = engine.classifyConsequence(fm);
    const task = engine.recommendTask(fm, consequence);
    setWalkData(prev => ({
      ...prev,
      failureModes: [...prev.failureModes, fm],
      failureEffects: [...prev.failureEffects, { id: fm.id, effect: fm.failureEffect }],
      consequences: [...prev.consequences, { id: fm.id, type: consequence, classification: consequence }],
      tasks: [...prev.tasks, { id: fm.id, taskType: task, interval: '' }],
      ...(task === 'RUN_TO_FAILURE' ? { defaultActions: [...prev.defaultActions, { id: fm.id, action: 'no_scheduled_maintenance', justification: '' }] } : {})
    }));
    setAddingItem(false);
    setNewItemForm({});
  };

  const failureModeSuggestions = selectedEquipment
    ? getSuggestions(
        selectedEquipment.type || selectedEquipment.equipmentType || selectedEquipment.description || selectedEquipment.name || '',
        'rcmcauses',
        selectedEquipment.id,
        walkData.failureModes
      ).slice(0, 8)
    : [];

  const acceptFailureModeSuggestion = (suggestion, functionalFailureId) => {
    const fm = {
      id: `FM-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      equipmentId: selectedEquipment.id,
      functionalFailureId: functionalFailureId || walkData.functionalFailures[0]?.id || '',
      failureMode: suggestion.description || suggestion.failureMode || suggestion.mechanism || '',
      failureEffect: suggestion.consequence || suggestion.failureEffect || suggestion.effect || '',
      severity: parseInt(suggestion.severity) || 5,
      occurrence: parseInt(suggestion.occurrence) || 3,
      detection: parseInt(suggestion.detection) || 5,
      mechanism: suggestion.mechanism || '',
      cause: suggestion.cause || '',
      detectionMethod: suggestion.detectionMethod || '',
      isEvident: true,
      affectsSafety: /safety|fire|explosion|injury/i.test(`${suggestion.description || ''} ${suggestion.consequence || ''}`),
      affectsEnvironment: /environment|leak|release|emission|spill/i.test(`${suggestion.description || ''} ${suggestion.consequence || ''}`),
      affectsOperations: true,
      source: suggestion.source || 'Contextual failure mode suggestion',
    };
    const consequence = engine.classifyConsequence(fm);
    const task = engine.recommendTask(fm, consequence);
    setProjectData(prev => ({ ...prev, rcmcauses: [...(prev.rcmcauses || []), fm] }));
    setWalkData(prev => ({
      ...prev,
      failureModes: [...prev.failureModes, fm],
      failureEffects: [...prev.failureEffects, { id: fm.id, effect: fm.failureEffect }],
      consequences: [...prev.consequences, { id: fm.id, type: consequence, classification: consequence }],
      tasks: [...prev.tasks, { id: fm.id, taskType: task, interval: '' }],
      ...(task === 'RUN_TO_FAILURE' ? { defaultActions: [...prev.defaultActions, { id: fm.id, action: 'no_scheduled_maintenance', justification: '' }] } : {})
    }));
  };

  const acceptAllFailureModeSuggestions = () => {
    const targetFF = walkData.functionalFailures[0]?.id || '';
    failureModeSuggestions.forEach(suggestion => acceptFailureModeSuggestion(suggestion, targetFF));
  };

  const handleEnhanceFailureModeSuggestions = async () => {
    if (!selectedEquipment || failureModeSuggestions.length === 0) return;
    setAiFailureModeLoading(true);
    try {
      const aiResult = await requestOptimalAIRanking({
        workflowStepId: 'Q3-failure-modes',
        sectionId: 'rcmcauses',
        equipmentType: selectedEquipment.type || selectedEquipment.equipmentType || selectedEquipment.description || selectedEquipment.name || '',
        itemContext: {
          equipment: selectedEquipment,
          functions: walkData.functions,
          functionalFailures: walkData.functionalFailures,
        },
        candidates: failureModeSuggestions,
      });
      setAiFailureModeRanking(aiResult);
    } catch (err) {
      setAiFailureModeRanking({ ranked_ids: [], explanation_by_id: {}, enrichment_by_id: {}, error: err.message });
    } finally {
      setAiFailureModeLoading(false);
    }
  };

  const stepDataCounts = [walkData.functions.length, walkData.functionalFailures.length, walkData.failureModes.length, walkData.failureEffects.filter(fe => fe.effect).length, walkData.consequences.length, walkData.tasks.length, walkData.defaultActions.length];

  if (!selectedEquipment) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
        <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Guided Analysis</div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Seven-Question Walkthrough</div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Select an equipment item to walk through the complete RCM seven-question process.</div>
          <div style={{ maxHeight: '500px', overflow: 'auto', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px' }}>
            {scopedEquipments.map((eq, idx) => {
              const fnCount = functions.filter(f => f.assetId === eq.id).length;
              return (
                <div key={eq.id} onClick={() => handleSelectEquipment(eq)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', cursor: 'pointer', backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}`, transition: 'background-color 0.15s' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '4px', backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Package size={14} color={COLORS.NAVY} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{eq.description || eq.id}</div>
                    <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{eq.id}</div>
                  </div>
                  <div style={{ fontSize: '10px', color: fnCount > 0 ? COLORS.SUCCESS : COLORS.TEXT_DIM }}>{fnCount > 0 ? `${fnCount} functions defined` : 'Not started'}</div>
                  <ChevronRight size={14} color={COLORS.TEXT_DIM} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => setSelectedEquipment(null)} style={{ padding: '4px 8px', backgroundColor: 'transparent', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', cursor: 'pointer', fontSize: '10px', color: COLORS.TEXT_DIM }}>Back</button>
        <div>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>Analysing: {selectedEquipment.description || selectedEquipment.id}</div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Q{currentStep + 1} — {STEPS[currentStep]?.title}</div>
        </div>
      </div>

      {/* Step navigation */}
      <div style={{ display: 'flex', padding: '8px 16px', backgroundColor: '#FAFBFD', borderBottom: `1px solid ${COLORS.BORDER}`, gap: '2px' }}>
        {STEPS.map((step, idx) => (
          <div key={step.id} onClick={() => setCurrentStep(idx)} style={{
            flex: 1, padding: '6px 4px', textAlign: 'center', cursor: 'pointer', borderRadius: '3px',
            backgroundColor: currentStep === idx ? COLORS.NAVY : stepDataCounts[idx] > 0 ? COLORS.SUCCESS + '20' : 'transparent',
            border: currentStep === idx ? 'none' : `1px solid ${stepDataCounts[idx] > 0 ? COLORS.SUCCESS + '40' : COLORS.BORDER}`
          }}>
            <div style={{ fontSize: '9px', fontWeight: '700', color: currentStep === idx ? '#fff' : stepDataCounts[idx] > 0 ? COLORS.SUCCESS : COLORS.TEXT_DIM }}>Q{idx + 1}</div>
            <div style={{ fontSize: '8px', color: currentStep === idx ? 'rgba(255,255,255,0.8)' : COLORS.TEXT_DIM, marginTop: '2px' }}>{step.title}</div>
            {stepDataCounts[idx] > 0 && <div style={{ fontSize: '8px', fontWeight: '600', color: currentStep === idx ? '#fff' : COLORS.SUCCESS, marginTop: '1px' }}>{stepDataCounts[idx]}</div>}
          </div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{ fontSize: '12px', color: COLORS.TEXT, fontWeight: '500', marginBottom: '4px', fontFamily: 'Georgia, serif' }}>{STEPS[currentStep]?.question}</div>
        <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>
          {currentStep === 0 && 'Define primary and secondary functions with quantified performance standards.'}
          {currentStep === 1 && 'For each function, identify all the ways it can fail to meet its performance standard.'}
          {currentStep === 2 && 'For each functional failure, identify the failure modes (causes) that are reasonably likely to occur.'}
          {currentStep === 3 && 'Describe what happens when each failure mode occurs, including evidence and consequences.'}
          {currentStep === 4 && 'Classify each failure by consequence: Hidden, Safety/Environmental, Operational, or Non-Operational.'}
          {currentStep === 5 && 'Select the most appropriate proactive task for each failure mode based on the decision tree.'}
          {currentStep === 6 && 'For failure modes where no proactive task is applicable, determine the default action.'}
        </div>

        {/* Q1: Functions */}
        {currentStep === 0 && (
          <div>
            {walkData.functions.map((fn, idx) => (
              <div key={fn.id} style={{ padding: '10px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '8px', borderLeft: `3px solid ${fn.type === 'primary' ? COLORS.NAVY : COLORS.TEAL}` }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: fn.type === 'primary' ? COLORS.NAVY : COLORS.TEAL }}>{fn.type}</span>
                  <span style={{ fontSize: '12px', color: COLORS.TEXT }}>{fn.description}</span>
                </div>
                {fn.performanceStandard && <div style={{ fontSize: '11px', color: COLORS.INFO, marginTop: '4px' }}>Standard: {fn.performanceStandard} {fn.requiredValue && `(${fn.requiredValue} ${fn.unit || ''})`}</div>}
              </div>
            ))}
            {addingItem ? (
              <div style={{ padding: '12px', backgroundColor: '#F0F7FF', border: `1px solid ${COLORS.NAVY}40`, borderRadius: '4px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <select value={newItemForm.type || 'primary'} onChange={e => setNewItemForm(p => ({ ...p, type: e.target.value }))} style={{ padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
                    <option value="primary">Primary</option><option value="secondary">Secondary</option><option value="protective">Protective</option>
                  </select>
                  <input placeholder="Unit (e.g., L/min)" value={newItemForm.unit || ''} onChange={e => setNewItemForm(p => ({ ...p, unit: e.target.value }))} style={{ padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} />
                </div>
                <input placeholder="Function description" value={newItemForm.description || ''} onChange={e => setNewItemForm(p => ({ ...p, description: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }} />
                <input placeholder="Performance standard" value={newItemForm.performanceStandard || ''} onChange={e => setNewItemForm(p => ({ ...p, performanceStandard: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }} />
                <input placeholder="Required value" value={newItemForm.requiredValue || ''} onChange={e => setNewItemForm(p => ({ ...p, requiredValue: e.target.value }))} style={{ width: '50%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }} />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={handleAddFunction} style={{ padding: '6px 14px', backgroundColor: COLORS.SUCCESS, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Save</button>
                  <button onClick={() => { setAddingItem(false); setNewItemForm({}); }} style={{ padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingItem(true)} style={{ padding: '6px 14px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', textTransform: 'uppercase', fontWeight: '600' }}>Add Function</button>
            )}
          </div>
        )}

        {/* Q2: Functional Failures */}
        {currentStep === 1 && (
          <div>
            {walkData.functionalFailures.map((ff) => {
              const parentFn = walkData.functions.find(f => f.id === ff.functionId);
              return (
                <div key={ff.id} style={{ padding: '10px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '8px', borderLeft: `3px solid ${ff.failureType === 'total' ? COLORS.ERROR : COLORS.WARNING}` }}>
                  <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Function: {parentFn?.description || ff.functionId}</div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', padding: '1px 6px', borderRadius: '2px', backgroundColor: ff.failureType === 'total' ? '#FEE2E2' : '#FFF7ED', color: ff.failureType === 'total' ? '#DC2626' : '#C2410C' }}>{ff.failureType}</span>
                    <span style={{ fontSize: '12px', color: COLORS.TEXT }}>{ff.description}</span>
                  </div>
                </div>
              );
            })}
            {addingItem ? (
              <div style={{ padding: '12px', backgroundColor: '#F0F7FF', border: `1px solid ${COLORS.NAVY}40`, borderRadius: '4px' }}>
                <select value={newItemForm.functionId || ''} onChange={e => setNewItemForm(p => ({ ...p, functionId: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }}>
                  <option value="">Select parent function...</option>
                  {walkData.functions.map(fn => <option key={fn.id} value={fn.id}>{fn.description}</option>)}
                </select>
                <select value={newItemForm.failureType || 'total'} onChange={e => setNewItemForm(p => ({ ...p, failureType: e.target.value }))} style={{ padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }}>
                  <option value="total">Total Loss</option><option value="partial">Partial Loss</option><option value="over">Over-performance</option><option value="erratic">Erratic</option>
                </select>
                <input placeholder="Functional failure description" value={newItemForm.description || ''} onChange={e => setNewItemForm(p => ({ ...p, description: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }} />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={handleAddFF} style={{ padding: '6px 14px', backgroundColor: COLORS.SUCCESS, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Save</button>
                  <button onClick={() => { setAddingItem(false); setNewItemForm({}); }} style={{ padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingItem(true)} disabled={walkData.functions.length === 0} style={{ padding: '6px 14px', backgroundColor: walkData.functions.length > 0 ? COLORS.NAVY : '#CBD5E1', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', cursor: walkData.functions.length > 0 ? 'pointer' : 'not-allowed', textTransform: 'uppercase', fontWeight: '600' }}>Add Functional Failure</button>
            )}
          </div>
        )}

        {/* Q3: Failure Modes */}
        {currentStep === 2 && (
          <div>
            <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#FAFBFD', border: `1px solid ${COLORS.BORDER}`, borderLeft: `3px solid ${COLORS.TEAL}`, borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: COLORS.NAVY, fontFamily: 'Georgia, serif' }}>Contextual Failure Mode Suggestions</div>
                {failureModeSuggestions.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button onClick={handleEnhanceFailureModeSuggestions} disabled={aiFailureModeLoading} style={{ padding: '2px 7px', fontSize: '8px', fontWeight: '700', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '2px', cursor: aiFailureModeLoading ? 'wait' : 'pointer', textTransform: 'uppercase' }}>{aiFailureModeLoading ? 'Thinking' : 'OptimalAI LLM'}</button>
                    <button onClick={acceptAllFailureModeSuggestions} style={{ padding: '2px 7px', fontSize: '8px', fontWeight: '700', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '2px', cursor: 'pointer', textTransform: 'uppercase' }}>Accept All</button>
                  </div>
                )}
              </div>
              <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, lineHeight: '1.4', marginBottom: '8px' }}>Based on the equipment carried forward from RCM scope and the selected functional failures. Sources: ISO 14224 / Equipment Library / SFG20 / OptimalAI.</div>
              {aiFailureModeRanking?.error && <div style={{ marginBottom: '4px', fontSize: '9px', color: COLORS.WARNING }}>{aiFailureModeRanking.error}</div>}
              {failureModeSuggestions.length === 0 ? (
                <div style={{ padding: '8px', fontSize: '10px', color: COLORS.TEXT_DIM, backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>No failure mode suggestions available for this equipment.</div>
              ) : rankSuggestionsForDisplay(failureModeSuggestions, aiFailureModeRanking).map(suggestion => (
                <div key={suggestion.id || suggestion.description} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', padding: '4px 5px', marginBottom: '3px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '2px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                      <div style={{ flex: 1, minWidth: 0, fontSize: '10px', color: COLORS.TEXT, lineHeight: '1.35' }}>{suggestion.description || suggestion.failureMode || suggestion.mechanism}</div>
                      <SuggestionSourceBadge suggestion={suggestion} fallback="OptimalAI" />
                    </div>
                    {(suggestion.mechanism || suggestion.cause || suggestion.detectionMethod) && <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, marginTop: '1px', lineHeight: '1.3' }}>{suggestion.mechanism || suggestion.cause || suggestion.detectionMethod}</div>}
                    <SuggestionAIExplanation suggestion={suggestion} aiResult={aiFailureModeRanking} />
                  </div>
                  <button onClick={() => acceptFailureModeSuggestion(suggestion, walkData.functionalFailures[0]?.id || '')} style={{ padding: '1px 6px', fontSize: '8px', fontWeight: '600', border: `1px solid ${COLORS.TEAL}40`, borderRadius: '2px', backgroundColor: 'transparent', color: COLORS.TEAL, cursor: 'pointer' }}>+</button>
                </div>
              ))}
            </div>
            {walkData.failureModes.map(fm => (
              <div key={fm.id} style={{ padding: '10px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '8px', borderLeft: `3px solid ${COLORS.GOLD}` }}>
                <div style={{ fontSize: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{fm.failureMode}</div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '10px', color: COLORS.TEXT_DIM }}>
                  <span>S: {fm.severity}</span><span>O: {fm.occurrence}</span><span>D: {fm.detection}</span>
                  <span style={{ fontWeight: '600', color: (fm.severity * fm.occurrence * fm.detection) > 200 ? COLORS.ERROR : COLORS.SUCCESS }}>RPN: {fm.severity * fm.occurrence * fm.detection}</span>
                </div>
              </div>
            ))}
            {addingItem ? (
              <div style={{ padding: '12px', backgroundColor: '#F0F7FF', border: `1px solid ${COLORS.NAVY}40`, borderRadius: '4px' }}>
                <select value={newItemForm.functionalFailureId || ''} onChange={e => setNewItemForm(p => ({ ...p, functionalFailureId: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }}>
                  <option value="">Select functional failure...</option>
                  {walkData.functionalFailures.map(ff => <option key={ff.id} value={ff.id}>{ff.description}</option>)}
                </select>
                <input placeholder="Failure mode description" value={newItemForm.failureMode || ''} onChange={e => setNewItemForm(p => ({ ...p, failureMode: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }} />
                <input placeholder="Failure effect (what happens?)" value={newItemForm.failureEffect || ''} onChange={e => setNewItemForm(p => ({ ...p, failureEffect: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' }}>
                  <div><label style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Severity (1-10)</label><input type="number" min="1" max="10" value={newItemForm.severity || 5} onChange={e => setNewItemForm(p => ({ ...p, severity: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></div>
                  <div><label style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Occurrence (1-10)</label><input type="number" min="1" max="10" value={newItemForm.occurrence || 3} onChange={e => setNewItemForm(p => ({ ...p, occurrence: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></div>
                  <div><label style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Detection (1-10)</label><input type="number" min="1" max="10" value={newItemForm.detection || 5} onChange={e => setNewItemForm(p => ({ ...p, detection: e.target.value }))} style={{ width: '100%', padding: '6px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '8px' }}>
                  <label style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={newItemForm.isEvident !== 'false'} onChange={e => setNewItemForm(p => ({ ...p, isEvident: e.target.checked ? 'true' : 'false' }))} /> Evident</label>
                  <label style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={newItemForm.affectsSafety === 'true'} onChange={e => setNewItemForm(p => ({ ...p, affectsSafety: e.target.checked ? 'true' : 'false' }))} /> Safety</label>
                  <label style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={newItemForm.affectsEnvironment === 'true'} onChange={e => setNewItemForm(p => ({ ...p, affectsEnvironment: e.target.checked ? 'true' : 'false' }))} /> Environmental</label>
                  <label style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}><input type="checkbox" checked={newItemForm.affectsOperations === 'true'} onChange={e => setNewItemForm(p => ({ ...p, affectsOperations: e.target.checked ? 'true' : 'false' }))} /> Operational</label>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={handleAddFM} style={{ padding: '6px 14px', backgroundColor: COLORS.SUCCESS, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Save</button>
                  <button onClick={() => { setAddingItem(false); setNewItemForm({}); }} style={{ padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingItem(true)} disabled={walkData.functionalFailures.length === 0} style={{ padding: '6px 14px', backgroundColor: walkData.functionalFailures.length > 0 ? COLORS.NAVY : '#CBD5E1', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', cursor: walkData.functionalFailures.length > 0 ? 'pointer' : 'not-allowed', textTransform: 'uppercase', fontWeight: '600' }}>Add Failure Mode</button>
            )}
          </div>
        )}

        {/* Q4: Failure Effects */}
        {currentStep === 3 && (
          <div>
            {walkData.failureModes.map(fm => (
              <div key={fm.id} style={{ padding: '10px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>Mode: {fm.failureMode}</div>
                <div style={{ fontSize: '12px', color: COLORS.TEXT }}>{fm.failureEffect || 'No failure effect described'}</div>
              </div>
            ))}
            {walkData.failureModes.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>No failure modes defined yet. Complete Q3 first.</div>}
          </div>
        )}

        {/* Q5: Consequences */}
        {currentStep === 4 && (
          <div>
            {walkData.consequences.map((c, idx) => {
              const fm = walkData.failureModes.find(f => f.id === c.id);
              const consColors = { HIDDEN: '#F59E0B', SAFETY: '#EF4444', ENVIRONMENTAL: '#10B981', OPERATIONAL: '#3B82F6', NON_OPERATIONAL: '#6B7280' };
              return (
                <div key={c.id} style={{ padding: '10px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '8px', borderLeft: `3px solid ${consColors[c.classification] || '#6B7280'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '12px', color: COLORS.TEXT }}>{fm?.failureMode || c.id}</div>
                    <span style={{ padding: '2px 10px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', backgroundColor: (consColors[c.classification] || '#6B7280') + '18', color: consColors[c.classification] || '#6B7280' }}>{c.classification}</span>
                  </div>
                </div>
              );
            })}
            {walkData.consequences.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>No failure modes to classify. Complete Q3 first.</div>}
          </div>
        )}

        {/* Q6: Proactive Tasks */}
        {currentStep === 5 && (
          <div>
            {walkData.tasks.map(t => {
              const fm = walkData.failureModes.find(f => f.id === t.id);
              const taskLabels = { 'ON_CONDITION_SAFETY': 'On-Condition (Safety)', 'ON_CONDITION_ENVIRONMENTAL': 'On-Condition (Environmental)', 'SCHEDULED_RESTORATION': 'Scheduled Restoration', 'SCHEDULED_DISCARD': 'Scheduled Discard', 'FAILURE_FINDING': 'Failure-Finding', 'RUN_TO_FAILURE': 'Run to Failure', 'ON_CONDITION_PREDICTIVE': 'Predictive/CBM' };
              return (
                <div key={t.id} style={{ padding: '10px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '8px', borderLeft: `3px solid ${t.taskType === 'RUN_TO_FAILURE' ? COLORS.TEXT_DIM : COLORS.SUCCESS}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '12px', color: COLORS.TEXT }}>{fm?.failureMode || t.id}</div>
                    <span style={{ padding: '2px 10px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', backgroundColor: t.taskType === 'RUN_TO_FAILURE' ? '#F3F4F6' : '#F0FDF4', color: t.taskType === 'RUN_TO_FAILURE' ? '#6B7280' : '#15803D' }}>{taskLabels[t.taskType] || t.taskType}</span>
                  </div>
                </div>
              );
            })}
            {walkData.tasks.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>No failure modes analysed. Complete Q3 first.</div>}
          </div>
        )}

        {/* Q7: Default Actions */}
        {currentStep === 6 && (
          <div>
            {walkData.defaultActions.length > 0 ? walkData.defaultActions.map(da => {
              const fm = walkData.failureModes.find(f => f.id === da.id);
              return (
                <div key={da.id} style={{ padding: '10px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '8px', borderLeft: `3px solid ${COLORS.WARNING}` }}>
                  <div style={{ fontSize: '12px', color: COLORS.TEXT, marginBottom: '4px' }}>{fm?.failureMode || da.id}</div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Default Action: {da.action === 'no_scheduled_maintenance' ? 'No Scheduled Maintenance — accept risk of failure' : da.action === 'redesign_mandatory' ? 'Redesign is mandatory' : da.action}</div>
                </div>
              );
            }) : (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.SUCCESS, fontFamily: 'Georgia, serif', marginBottom: '8px' }}>All failure modes have proactive tasks assigned</div>
                <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM }}>No default actions are required — every failure mode has been addressed by a scheduled or on-condition task.</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderTop: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => setCurrentStep(s => Math.max(0, s - 1))} disabled={currentStep === 0} style={{ padding: '6px 16px', backgroundColor: currentStep > 0 ? 'transparent' : '#E5E7EB', color: currentStep > 0 ? COLORS.TEXT : COLORS.TEXT_DIM, border: `1px solid ${currentStep > 0 ? COLORS.BORDER : '#E5E7EB'}`, borderRadius: '3px', fontSize: '11px', cursor: currentStep > 0 ? 'pointer' : 'not-allowed', textTransform: 'uppercase' }}>Previous</button>
        <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Step {currentStep + 1} of 7</span>
        <button onClick={() => setCurrentStep(s => Math.min(6, s + 1))} disabled={currentStep === 6} style={{ padding: '6px 16px', backgroundColor: currentStep < 6 ? COLORS.NAVY : '#E5E7EB', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', cursor: currentStep < 6 ? 'pointer' : 'not-allowed', textTransform: 'uppercase', fontWeight: '600' }}>Next</button>
      </div>
    </div>
  );
}

// ============================================================================
// PM SCHEDULE BUILDER
// ============================================================================
function PMScheduleView({ projectData }) {
  const [schedule, setSchedule] = useState([]);
  const [view, setView] = useState('groups'); // groups | calendar | summary
  const engine = useMemo(() => new RCMEngine(), []);

  const handleBuildSchedule = () => {
    const tasks = (projectData.scheduledtasks || []).concat(
      (projectData.rcmcauses || []).map(cause => {
        const consequence = engine.classifyConsequence(cause);
        const taskType = engine.recommendTask(cause, consequence);
        return {
          id: cause.id,
          name: cause.failureMode || cause.description,
          craft: cause.craft || 'Mechanical',
          interval: cause.taskInterval || (taskType.includes('CONDITION') ? 'Monthly' : taskType === 'SCHEDULED_RESTORATION' ? 'Quarterly' : taskType === 'SCHEDULED_DISCARD' ? 'Annually' : 'N/A'),
          taskType: taskType,
          estimatedDuration: cause.estimatedDuration || '2',
          equipmentId: cause.equipmentId
        };
      }).filter(t => t.taskType !== 'RUN_TO_FAILURE')
    );
    const groups = engine.buildPMSchedule(tasks);
    setSchedule(groups);
  };

  const totalTasks = schedule.reduce((sum, g) => sum + g.tasks.length, 0);
  const craftSummary = {};
  schedule.forEach(g => { craftSummary[g.craft] = (craftSummary[g.craft] || 0) + g.tasks.length; });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Deliverable</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Preventive Maintenance Schedule</div>
        <div style={{ flex: 1 }} />
        <button onClick={handleBuildSchedule} style={{ padding: '6px 14px', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textTransform: 'uppercase' }}>Build Schedule</button>
        {['groups', 'summary'].map(v => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '4px 10px', fontSize: '10px', borderRadius: '3px', cursor: 'pointer', backgroundColor: view === v ? COLORS.NAVY : 'transparent', color: view === v ? '#fff' : COLORS.TEXT, border: view === v ? 'none' : `1px solid ${COLORS.BORDER}`, textTransform: 'uppercase' }}>{v}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {schedule.length === 0 ? (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '8px' }}>No schedule built</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM }}>Click "Build Schedule" to package RCM task recommendations into a grouped PM schedule by craft and interval.</div>
          </div>
        ) : view === 'groups' ? (
          schedule.map(group => (
            <div key={group.id} style={{ marginBottom: '16px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT }}>{group.craft}</span>
                  <span style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginLeft: '8px' }}>{group.interval}</span>
                </div>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{group.tasks.length} tasks | Est. {group.totalEstimatedHours.toFixed(1)}h</div>
              </div>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <tbody>
                  {group.tasks.map((task, idx) => (
                    <tr key={task.id || idx} style={{ borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG }}>
                      <td style={{ padding: '6px 14px', color: COLORS.TEXT }}>{task.name || task.failureMode || task.id}</td>
                      <td style={{ padding: '6px 14px', color: COLORS.TEXT_DIM, width: '120px' }}>{task.estimatedDuration}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', borderTop: `3px solid ${COLORS.NAVY}` }}>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>Total Task Groups</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>{schedule.length}</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', borderTop: `3px solid ${COLORS.TEAL}` }}>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>Total Tasks</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>{totalTasks}</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', borderTop: `3px solid ${COLORS.GOLD}` }}>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>Total Est. Hours</div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>{schedule.reduce((s, g) => s + g.totalEstimatedHours, 0).toFixed(1)}</div>
              </div>
            </div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '8px', fontFamily: 'Georgia, serif' }}>By Craft</div>
            {Object.entries(craftSummary).map(([craft, count]) => (
              <div key={craft} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: COLORS.TEXT }}>{craft}</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.NAVY }}>{count} tasks</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// DEFAULT ACTION REGISTER (Moubray RCM2 Q7)
// ============================================================================
function DefaultActionRegisterView({ projectData, setProjectData }) {
  const [editingId, setEditingId] = useState(null);
  const engine = useMemo(() => new RCMEngine(), []);

  const defaultActions = projectData.rcmDefaultActions || [];
  const rcmCauses = projectData.rcmcauses || [];

  const handleGenerateRegister = () => {
    const newActions = rcmCauses.map(cause => {
      const consequence = engine.classifyConsequence(cause);
      const task = engine.recommendTask(cause, consequence);
      if (task === 'RUN_TO_FAILURE' || task === 'FAILURE_FINDING') {
        const isSafetyHidden = consequence === 'HIDDEN' && cause.affectsSafety;
        return {
          id: `DA-${cause.id}`,
          failureModeId: cause.id,
          failureMode: cause.failureMode || cause.description,
          consequence: consequence,
          recommendedTask: task,
          defaultAction: isSafetyHidden ? 'redesign_mandatory' : task === 'FAILURE_FINDING' ? 'failure_finding_task' : 'no_scheduled_maintenance',
          justification: '',
          signedOff: false,
          signedOffBy: '',
          signedOffDate: null,
          redesignRequired: isSafetyHidden,
          status: 'pending'
        };
      }
      return null;
    }).filter(Boolean);
    setProjectData(prev => ({ ...prev, rcmDefaultActions: newActions }));
  };

  const handleUpdate = (id, updates) => {
    setProjectData(prev => ({
      ...prev,
      rcmDefaultActions: (prev.rcmDefaultActions || []).map(da => da.id === id ? { ...da, ...updates } : da)
    }));
  };

  const handleSignOff = (id) => {
    handleUpdate(id, { signedOff: true, signedOffDate: new Date().toISOString(), signedOffBy: 'Analyst', status: 'approved' });
  };

  const actionLabels = { 'redesign_mandatory': 'Redesign Mandatory', 'redesign_desirable': 'Redesign Desirable', 'no_scheduled_maintenance': 'No Scheduled Maintenance', 'failure_finding_task': 'Failure-Finding Task', 'combination': 'Combination of Tasks', 'change_procedures': 'Change Operating Procedures', 'training': 'Training' };
  const actionColors = { 'redesign_mandatory': '#DC2626', 'redesign_desirable': '#F59E0B', 'no_scheduled_maintenance': '#6B7280', 'failure_finding_task': '#3B82F6', 'combination': '#8B5CF6', 'change_procedures': '#10B981', 'training': '#06B6D4' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Question 7</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Default Action Register</div>
        <div style={{ flex: 1 }} />
        <button onClick={handleGenerateRegister} style={{ padding: '6px 14px', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textTransform: 'uppercase' }}>Generate Register</button>
      </div>
      <div style={{ padding: '12px 16px', backgroundColor: '#FAFBFD', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', color: COLORS.TEXT_DIM, lineHeight: '1.5' }}>
        This register captures all failure modes where no proactive maintenance task is applicable or worth doing. Each entry requires a documented default action and formal sign-off by the RCM review team.
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {defaultActions.length === 0 ? (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '8px' }}>Default Action Register Empty</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Click "Generate Register" to identify failure modes requiring default actions from the RCM analysis.</div>
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `2px solid ${COLORS.BORDER}` }}>
                {['Failure Mode', 'Consequence', 'Default Action', 'Redesign?', 'Justification', 'Status', 'Sign-Off'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.TEXT, fontWeight: '600', fontSize: '10px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {defaultActions.map((da, idx) => (
                <tr key={da.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <td style={{ padding: '8px 10px', color: COLORS.TEXT }}>{da.failureMode}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', backgroundColor: da.consequence === 'HIDDEN' ? '#FEF3C7' : da.consequence === 'SAFETY' ? '#FEE2E2' : '#EFF6FF', color: da.consequence === 'HIDDEN' ? '#A16207' : da.consequence === 'SAFETY' ? '#DC2626' : '#1D4ED8' }}>{da.consequence}</span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <select value={da.defaultAction} onChange={e => handleUpdate(da.id, { defaultAction: e.target.value })} style={{ padding: '3px 6px', fontSize: '10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', color: actionColors[da.defaultAction] || COLORS.TEXT }}>
                      {Object.entries(actionLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    {da.redesignRequired ? <span style={{ color: COLORS.ERROR, fontWeight: '600', fontSize: '10px' }}>Required</span> : <span style={{ color: COLORS.TEXT_DIM, fontSize: '10px' }}>No</span>}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <input value={da.justification || ''} onChange={e => handleUpdate(da.id, { justification: e.target.value })} placeholder="Justification..." style={{ width: '100%', padding: '3px 6px', fontSize: '10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }} />
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ fontSize: '10px', fontWeight: '600', color: da.status === 'approved' ? COLORS.SUCCESS : COLORS.WARNING }}>{da.status === 'approved' ? 'Approved' : 'Pending'}</span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {da.signedOff ? (
                      <span style={{ fontSize: '10px', color: COLORS.SUCCESS }}>Signed {new Date(da.signedOffDate).toLocaleDateString()}</span>
                    ) : (
                      <button onClick={() => handleSignOff(da.id)} style={{ padding: '3px 10px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '10px', cursor: 'pointer' }}>Sign Off</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ padding: '8px 16px', backgroundColor: COLORS.PANEL_BG, borderTop: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '16px', fontSize: '10px', color: COLORS.TEXT_DIM }}>
        <span>Total: {defaultActions.length}</span>
        <span>Redesign Required: {defaultActions.filter(d => d.redesignRequired).length}</span>
        <span>Signed Off: {defaultActions.filter(d => d.signedOff).length}</span>
        <span>Pending: {defaultActions.filter(d => !d.signedOff).length}</span>
      </div>
    </div>
  );
}

// ============================================================================
// FULL RCM2 WORKSHEET (Moubray Format with Function/FF Columns)
// ============================================================================
function RCM2FullWorksheet({ projectData }) {
  const [sortField, setSortField] = useState('');
  const engine = useMemo(() => new RCMEngine(), []);

  const functions = projectData.rcmFunctions || [];
  const functionalFailures = projectData.rcmFunctionalFailures || [];
  const failureModes = projectData.rcmcauses || [];
  const equipments = projectData.equipments || [];

  // Build full worksheet rows: Function → FF → FM → Effect → Consequence → Task → Default Action
  const rows = failureModes.map(fm => {
    const ff = functionalFailures.find(f => f.id === fm.functionalFailureId) || {};
    const fn = functions.find(f => f.id === ff.functionId) || {};
    const eq = equipments.find(e => e.id === fm.equipmentId) || {};
    const consequence = engine.classifyConsequence(fm);
    const task = engine.recommendTask(fm, consequence);
    const rpn = (fm.severity || 1) * (fm.occurrence || 1) * (fm.detection || 1);
    return {
      equipment: eq.description || eq.id || '—',
      function: fn.description || '—',
      functionType: fn.type || '—',
      performanceStandard: fn.performanceStandard || '—',
      functionalFailure: ff.description || '—',
      failureType: ff.failureType || '—',
      failureMode: fm.failureMode || fm.description || '—',
      failureEffect: fm.failureEffect || '—',
      consequence: consequence,
      rpn: rpn,
      severity: fm.severity || 1,
      occurrence: fm.occurrence || 1,
      detection: fm.detection || 1,
      taskType: task,
      taskInterval: fm.taskInterval || '—',
      defaultAction: task === 'RUN_TO_FAILURE' ? 'No Scheduled Maintenance' : '—'
    };
  });

  const consColors = { HIDDEN: '#F59E0B', SAFETY: '#EF4444', ENVIRONMENTAL: '#10B981', OPERATIONAL: '#3B82F6', NON_OPERATIONAL: '#6B7280' };
  const taskLabels = { 'ON_CONDITION_SAFETY': 'On-Condition (Safety)', 'ON_CONDITION_ENVIRONMENTAL': 'On-Condition (Environmental)', 'SCHEDULED_RESTORATION': 'Scheduled Restoration', 'SCHEDULED_DISCARD': 'Scheduled Discard', 'FAILURE_FINDING': 'Failure-Finding', 'RUN_TO_FAILURE': 'Run to Failure', 'ON_CONDITION_PREDICTIVE': 'Predictive/CBM' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Deliverable</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Full RCM Worksheet</div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{rows.length} rows</span>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse', minWidth: '1400px' }}>
          <thead>
            <tr style={{ backgroundColor: COLORS.NAVY, position: 'sticky', top: 0, zIndex: 2 }}>
              {['Equipment', 'Function', 'Perf. Standard', 'Functional Failure', 'Failure Mode', 'Failure Effect', 'S', 'O', 'D', 'RPN', 'Consequence', 'Recommended Task', 'Interval', 'Default Action'].map(h => (
                <th key={h} style={{ padding: '8px 6px', textAlign: 'left', color: '#fff', fontWeight: '600', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.03em', borderRight: '1px solid rgba(255,255,255,0.1)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={14} style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>No data. Define Functions (Q1), Functional Failures (Q2), and Failure Modes (Q3) to populate the worksheet.</td></tr>
            ) : rows.map((row, idx) => (
              <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <td style={{ padding: '6px', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>{row.equipment}</td>
                <td style={{ padding: '6px', color: COLORS.TEXT, maxWidth: '180px', borderRight: `1px solid ${COLORS.BORDER}` }}>{row.function}</td>
                <td style={{ padding: '6px', color: COLORS.INFO, fontSize: '9px', borderRight: `1px solid ${COLORS.BORDER}` }}>{row.performanceStandard}</td>
                <td style={{ padding: '6px', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>{row.functionalFailure}</td>
                <td style={{ padding: '6px', color: COLORS.TEXT, fontWeight: '500', borderRight: `1px solid ${COLORS.BORDER}` }}>{row.failureMode}</td>
                <td style={{ padding: '6px', color: COLORS.TEXT_DIM, fontSize: '9px', maxWidth: '160px', borderRight: `1px solid ${COLORS.BORDER}` }}>{row.failureEffect}</td>
                <td style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>{row.severity}</td>
                <td style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>{row.occurrence}</td>
                <td style={{ padding: '6px', textAlign: 'center', color: COLORS.TEXT, borderRight: `1px solid ${COLORS.BORDER}` }}>{row.detection}</td>
                <td style={{ padding: '6px', textAlign: 'center', fontWeight: '700', color: row.rpn > 200 ? COLORS.ERROR : row.rpn >= 100 ? COLORS.WARNING : COLORS.SUCCESS, borderRight: `1px solid ${COLORS.BORDER}` }}>{row.rpn}</td>
                <td style={{ padding: '6px', borderRight: `1px solid ${COLORS.BORDER}` }}>
                  <span style={{ padding: '1px 6px', borderRadius: '2px', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', backgroundColor: (consColors[row.consequence] || '#6B7280') + '18', color: consColors[row.consequence] || '#6B7280' }}>{row.consequence}</span>
                </td>
                <td style={{ padding: '6px', color: row.taskType === 'RUN_TO_FAILURE' ? COLORS.TEXT_DIM : COLORS.SUCCESS, fontWeight: '500', borderRight: `1px solid ${COLORS.BORDER}` }}>{taskLabels[row.taskType] || row.taskType}</td>
                <td style={{ padding: '6px', color: COLORS.TEXT_DIM, borderRight: `1px solid ${COLORS.BORDER}` }}>{row.taskInterval}</td>
                <td style={{ padding: '6px', color: row.defaultAction !== '—' ? COLORS.WARNING : COLORS.TEXT_DIM }}>{row.defaultAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// COST-BENEFIT OPTIMISATION VIEW
// ============================================================================
function CostBenefitOptimisationView({ projectData }) {
  const [analyses, setAnalyses] = useState([]);
  const [selectedFM, setSelectedFM] = useState(null);
  const engine = useMemo(() => new RCMEngine(), []);

  const rcmCauses = projectData.rcmcauses || [];

  const handleAnalyseAll = () => {
    const results = rcmCauses.map(fm => {
      const strategies = engine.compareStrategies(fm);
      const costImpact = engine.calculateCostImpact(fm);
      return { ...fm, strategies, costImpact, recommended: strategies.recommended, savings: strategies.savings };
    });
    setAnalyses(results);
  };

  const totalSavings = analyses.reduce((sum, a) => sum + (a.savings || 0), 0);
  const totalCurrentCost = analyses.reduce((sum, a) => sum + (a.costImpact?.totalAnnualCost || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '10px 16px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.TEXT_DIM }}>RCM Decision Support</div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>Cost-Benefit Optimisation</div>
        <div style={{ flex: 1 }} />
        <button onClick={handleAnalyseAll} style={{ padding: '6px 14px', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textTransform: 'uppercase' }}>Analyse All ({rcmCauses.length})</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {analyses.length === 0 ? (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '8px' }}>Cost-Benefit Analysis</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Compare maintenance strategies (Run-to-Failure, Preventive, Predictive/CBM, Redesign) for each failure mode and identify the most cost-effective approach.</div>
            <button onClick={handleAnalyseAll} style={{ padding: '8px 20px', backgroundColor: COLORS.TEAL, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', textTransform: 'uppercase', fontWeight: '600' }}>Run Analysis</button>
          </div>
        ) : (
          <>
            {/* Summary KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', borderTop: `3px solid ${COLORS.NAVY}` }}>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>Current Annual Cost</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>{formatCurrency(totalCurrentCost)}</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', borderTop: `3px solid ${COLORS.SUCCESS}` }}>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>Potential Savings</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.SUCCESS, fontFamily: 'Georgia, serif' }}>{formatCurrency(totalSavings)}</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', borderTop: `3px solid ${COLORS.GOLD}` }}>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>Failure Modes Analysed</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.TEXT, fontFamily: 'Georgia, serif' }}>{analyses.length}</div>
              </div>
            </div>

            {/* Comparison table */}
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `2px solid ${COLORS.BORDER}` }}>
                  {['Failure Mode', 'Run-to-Failure', 'Preventive', 'Predictive/CBM', 'Redesign', 'Recommended', 'Savings'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.TEXT, fontWeight: '600', fontSize: '10px', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analyses.map((a, idx) => {
                  const strats = a.strategies.strategies || [];
                  const costs = {};
                  strats.forEach(s => { costs[s.strategy] = s.totalAnnualCost; });
                  const minCost = Math.min(...strats.map(s => s.totalAnnualCost));
                  return (
                    <tr key={a.id || idx} onClick={() => setSelectedFM(a)} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}`, cursor: 'pointer' }}>
                      <td style={{ padding: '8px 10px', color: COLORS.TEXT, fontWeight: '500' }}>{a.failureMode || a.description || a.name || a.id}</td>
                      {['Run to Failure', 'Preventive Maintenance', 'Predictive/CBM', 'Redesign'].map(strat => {
                        const cost = costs[strat] || 0;
                        const isBest = Math.abs(cost - minCost) < 1;
                        return (
                          <td key={strat} style={{ padding: '8px 10px', color: isBest ? COLORS.SUCCESS : COLORS.TEXT, fontWeight: isBest ? '700' : '400', backgroundColor: isBest ? '#F0FDF4' : 'transparent' }}>{formatCurrency(cost)}</td>
                        );
                      })}
                      <td style={{ padding: '8px 10px', color: COLORS.TEAL, fontWeight: '600' }}>{a.recommended}</td>
                      <td style={{ padding: '8px 10px', color: a.savings > 0 ? COLORS.SUCCESS : COLORS.TEXT_DIM, fontWeight: '600' }}>{formatCurrency(a.savings)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Detail panel for selected FM */}
            {selectedFM && (
              <div style={{ backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '16px', borderTop: `3px solid ${COLORS.TEAL}` }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: 'Georgia, serif', marginBottom: '12px' }}>Strategy Comparison: {selectedFM.failureMode || selectedFM.name}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                  {(selectedFM.strategies.strategies || []).map(s => (
                    <div key={s.strategy} style={{ padding: '12px', border: `1px solid ${s.strategy === selectedFM.recommended ? COLORS.SUCCESS : COLORS.BORDER}`, borderRadius: '4px', backgroundColor: s.strategy === selectedFM.recommended ? '#F0FDF4' : '#fff' }}>
                      <div style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', color: s.strategy === selectedFM.recommended ? COLORS.SUCCESS : COLORS.TEXT_DIM, marginBottom: '8px' }}>{s.strategy}</div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Maintenance Cost</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT }}>{formatCurrency(s.annualMaintenanceCost)}</div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginTop: '6px' }}>Failure Cost</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT }}>{formatCurrency(s.annualFailureCost)}</div>
                      <div style={{ borderTop: `1px solid ${COLORS.BORDER}`, marginTop: '8px', paddingTop: '8px' }}>
                        <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Total Annual</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: s.strategy === selectedFM.recommended ? COLORS.SUCCESS : COLORS.TEXT }}>{formatCurrency(s.totalAnnualCost)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CRITICALITY MATRIX VIEW
// ============================================================================
function OptimalRCMView({ rcmCauses = [] }) {
  const [engine] = useState(() => new RCMEngine());
  const predictions = useMemo(() => engine.generateCausePredictions(rcmCauses), [rcmCauses, engine]);
  const systemProfile = useMemo(() => engine.generateSystemCostProfile(rcmCauses), [rcmCauses, engine]);

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        {[
          { label: 'Total Annual Cost', value: formatCurrency(systemProfile.totalAnnualCost || 0, 'USD'), color: '#002F65' },
          { label: 'System Availability', value: `${((systemProfile.systemAvailability || 0) * 100).toFixed(2)}%`, color: COLORS.BLUE },
          { label: 'Failure Modes', value: rcmCauses.length, color: COLORS.GOLD },
          { label: 'Top Contributors', value: (systemProfile.topContributors || []).length, color: '#00A8CB' }
        ].map((card, i) => (
          <div key={i} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', borderLeft: `4px solid ${card.color}` }}>
            <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>{card.label}</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: COLORS.TEXT }}>{card.value}</div>
          </div>
        ))}
      </div>
      <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '14px', fontWeight: '600', color: COLORS.TEXT }}>
          OptimalRCM Cause Predictions — Cost-Benefit Analysis
        </div>
        <div style={{ overflow: 'auto', maxHeight: '400px' }}>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, position: 'sticky', top: 0 }}>
                {['Failure Mode', 'RPN', 'Availability', 'Annual Cost', 'Corrective', 'Preventive', 'Downtime', 'Strategy', 'Savings'].map(h => (
                  <th key={h} style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT_DIM, fontWeight: '600' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {predictions.map((p, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{p.name || p.id}</td>
                  <td style={{ padding: '8px', color: p.rpn > 200 ? COLORS.ERROR : p.rpn >= 100 ? COLORS.WARNING : COLORS.SUCCESS, fontWeight: '600' }}>{p.rpn}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{(p.availability * 100).toFixed(2)}%</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT, fontWeight: '600' }}>{formatCurrency(p.annualCost)}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{formatCurrency(p.costBreakdown?.annualCorrectiveCost || 0)}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{formatCurrency(p.costBreakdown?.annualPreventiveCost || 0)}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{formatCurrency(p.costBreakdown?.annualDowntimeCost || 0)}</td>
                  <td style={{ padding: '8px', color: '#00A8CB', fontWeight: '600' }}>{p.recommendedStrategy}</td>
                  <td style={{ padding: '8px', color: COLORS.GOLD }}>{formatCurrency(p.potentialSavings || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Cost Profile Chart */}
      <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', marginTop: '16px' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '12px' }}>System Cost Profile</div>
        <div style={{ display: 'flex', height: '200px', alignItems: 'flex-end', gap: '4px' }}>
          {(systemProfile.costProfile || []).map((p, i) => {
            const maxCost = Math.max(...(systemProfile.costProfile || []).map(x => x.cumulativeCost || 1));
            const height = maxCost > 0 ? (p.cumulativeCost / maxCost) * 180 : 0;
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '100%', height: `${height}px`, backgroundColor: COLORS.BLUE, borderRadius: '2px 2px 0 0', minHeight: '2px' }} />
                <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, marginTop: '4px' }}>M{p.interval}</div>
              </div>
            );
          })}
        </div>
      </div>
      <PFCurveVisualization />
    </div>
  );
}

function CriticalityMatrixView({ failureModes = [], consequences = [], onUpdate }) {
  const [matrixData, setMatrixData] = useState(null);
  const [paretoData, setParetoData] = useState(null);
  const [activeTab, setActiveTab] = useState('matrix');
  const [criticityMode, setCriticityMode] = useState('standard'); // 'standard' or '4-axis'
  const [axisWeights, setAxisWeights] = useState({ safety: 40, operational: 30, environmental: 15, cost: 15 });

  const engine = useMemo(() => new CriticalityEngine(), []);

  const handleAnalyze = useCallback(() => {
    const matrix = engine.buildCriticalityMatrix(failureModes, consequences);
    const pareto = engine.getParetoCurveData(failureModes);
    const ranked = engine.rankByRPN(failureModes);
    setMatrixData({ matrix, ranked });
    setParetoData(pareto);
  }, [engine, failureModes, consequences]);

  const SEVERITY_LABELS = ['I - Catastrophic', 'II - Critical', 'III - Marginal', 'IV - Negligible'];
  const PROB_LABELS = ['A - Frequent', 'B - Probable', 'C - Occasional', 'D - Remote', 'E - Unlikely'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '8px 12px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={handleAnalyze}
          style={{ padding: '6px 14px', backgroundColor: COLORS.TEAL, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}
        >
          Build Matrix
        </button>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', borderLeft: `1px solid ${COLORS.BORDER}`, paddingLeft: '8px' }}>
          <select value={criticityMode} onChange={(e) => setCriticityMode(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
            <option value="standard">Standard (Severity/Occurrence)</option>
            <option value="4-axis">4-Axis (S/O/E/C)</option>
          </select>
        </div>
        <div style={{ flex: 1 }} />
        {['matrix', 'pareto', 'ranking', '4-axis'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 10px', fontSize: '11px', cursor: 'pointer', borderRadius: '4px',
              backgroundColor: activeTab === tab ? COLORS.TEAL : 'transparent',
              border: activeTab === tab ? 'none' : `1px solid ${COLORS.BORDER}`,
              color: activeTab === tab ? '#FFFFFF' : COLORS.TEXT
            }}
          >
            {tab === 'matrix' ? 'Criticality Matrix' : tab === 'pareto' ? 'Pareto Chart' : tab === 'ranking' ? 'RPN Ranking' : '4-Axis View'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {activeTab === 'matrix' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 16px 0' }}>MIL-STD-1629A Criticality Matrix</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '140px repeat(4, 1fr)', gap: '2px', maxWidth: '800px' }}>
              {/* Header row */}
              <div style={{ padding: '8px', fontSize: '10px', color: COLORS.TEXT_DIM }} />
              {SEVERITY_LABELS.map(s => (
                <div key={s} style={{ padding: '10px 8px', textAlign: 'center', fontSize: '10px', color: '#FFFFFF', backgroundColor: '#002F65', borderRadius: '3px', fontWeight: '600', letterSpacing: '0.02em' }}>
                  {s}
                </div>
              ))}
              {/* Matrix rows — MIL-STD-1629A risk mapping */}
              {PROB_LABELS.map((prob, pIdx) => {
                // Standard 5x4 risk matrix: row=probability(0=Frequent..4=Unlikely), col=severity(0=Catastrophic..3=Negligible)
                const RISK_MAP = [
                  // Catastrophic   Critical       Marginal       Negligible
                  ['Unacceptable', 'Unacceptable', 'Undesirable', 'Review'     ],  // Frequent
                  ['Unacceptable', 'Unacceptable', 'Undesirable', 'Review'     ],  // Probable
                  ['Unacceptable', 'Undesirable',  'Review',      'Acceptable' ],  // Occasional
                  ['Undesirable',  'Review',       'Review',      'Acceptable' ],  // Remote
                  ['Review',       'Review',       'Acceptable',  'Acceptable' ],  // Unlikely
                ];
                const ZONE_COLORS = {
                  'Unacceptable': { bg: '#FEE2E2', border: '#EF4444', text: '#B91C1C' },
                  'Undesirable':  { bg: '#FFF7ED', border: '#F97316', text: '#C2410C' },
                  'Review':       { bg: '#FEFCE8', border: '#EAB308', text: '#A16207' },
                  'Acceptable':   { bg: '#F0FDF4', border: '#22C55E', text: '#15803D' },
                };
                return (
                <React.Fragment key={prob}>
                  <div style={{ padding: '10px 8px', fontSize: '10px', color: '#FFFFFF', backgroundColor: '#002F65', borderRadius: '3px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                    {prob}
                  </div>
                  {[0, 1, 2, 3].map(sIdx => {
                    const zone = RISK_MAP[pIdx][sIdx];
                    const zc = ZONE_COLORS[zone];
                    const count = matrixData?.matrix?.[pIdx]?.[sIdx]?.count || 0;
                    return (
                      <div key={sIdx} style={{
                        padding: '12px', textAlign: 'center', backgroundColor: zc.bg,
                        border: `2px solid ${zc.border}`, borderRadius: '4px', minHeight: '40px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: zc.text }}>{count}</div>
                        <div style={{ fontSize: '8px', color: zc.text, textTransform: 'uppercase', opacity: 0.7 }}>{zone}</div>
                      </div>
                    );
                  })}
                </React.Fragment>
                );
              })}
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px', fontSize: '11px' }}>
              {[
                { label: 'Unacceptable', bg: '#FEE2E2', border: '#EF4444' },
                { label: 'Undesirable', bg: '#FFF7ED', border: '#F97316' },
                { label: 'Review', bg: '#FEFCE8', border: '#EAB308' },
                { label: 'Acceptable', bg: '#F0FDF4', border: '#22C55E' }
              ].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '14px', height: '14px', backgroundColor: l.bg, border: `2px solid ${l.border}`, borderRadius: '2px' }} />
                  <span style={{ color: COLORS.TEXT }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'pareto' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 16px 0' }}>RPN Pareto Analysis</h3>
            {paretoData && paretoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={paretoData.slice(0, 15)} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                  <CartesianGrid stroke={COLORS.BORDER} />
                  <XAxis dataKey="name" stroke={COLORS.TEXT_DIM} style={{ fontSize: '9px' }} angle={-45} textAnchor="end" />
                  <YAxis yAxisId="left" stroke={COLORS.TEXT_DIM} style={{ fontSize: '9px' }} />
                  <YAxis yAxisId="right" orientation="right" stroke={COLORS.TEXT_DIM} style={{ fontSize: '9px' }} />
                  <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                  <Bar dataKey="rpn" fill={COLORS.GOLD} name="RPN" yAxisId="left" />
                  <Line type="monotone" dataKey="cumulativePercent" stroke={COLORS.NAVY} strokeWidth={2} name="Cumulative %" yAxisId="right" />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Run analysis to see Pareto data</p>
            )}
          </div>
        )}

        {activeTab === 'ranking' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 12px 0' }}>RPN Ranking</h3>
            {matrixData?.ranked ? (
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Rank</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Failure Mode</th>
                    <th style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>S</th>
                    <th style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>O</th>
                    <th style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>D</th>
                    <th style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT, fontWeight: 'bold' }}>RPN</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Risk Level</th>
                  </tr>
                </thead>
                <tbody>
                  {matrixData.ranked.map((fm, idx) => {
                    const rpn = (fm.severity || 1) * (fm.occurrence || 1) * (fm.detection || 1);
                    const riskColor = rpn > 200 ? COLORS.ERROR : rpn >= 100 ? COLORS.WARNING : COLORS.SUCCESS;
                    return (
                      <tr key={fm.id || idx} style={{ borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: idx % 2 === 0 ? COLORS.LIGHT_BG : 'transparent' }}>
                        <td style={{ padding: '8px', color: COLORS.TEXT_DIM, fontWeight: 'bold' }}>{idx + 1}</td>
                        <td style={{ padding: '8px', color: COLORS.TEXT }}>{fm.failureMode || fm.description || 'Unknown'}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>{fm.severity || 1}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>{fm.occurrence || 1}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>{fm.detection || 1}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: riskColor, fontWeight: 'bold' }}>{rpn}</td>
                        <td style={{ padding: '8px', color: riskColor }}>{rpn > 200 ? 'HIGH' : rpn >= 100 ? 'MEDIUM' : 'LOW'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Run analysis to see rankings</p>
            )}
          </div>
        )}

        {activeTab === '4-axis' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 16px 0' }}>4-Axis Criticality Assessment (Safety/Operational/Environmental/Cost)</h3>

            {/* Axis Weight Configuration */}
            <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '10px' }}>Axis Weighting</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                {[
                  { key: 'safety', label: 'Safety (S)', color: '#EF4444' },
                  { key: 'operational', label: 'Operational (O)', color: '#3B82F6' },
                  { key: 'environmental', label: 'Environmental (E)', color: '#10B981' },
                  { key: 'cost', label: 'Cost (C)', color: '#F59E0B' }
                ].map(axis => (
                  <div key={axis.key}>
                    <label style={{ fontSize: '11px', color: COLORS.TEXT_DIM, display: 'block', marginBottom: '6px' }}>{axis.label}</label>
                    <input type="range" min="0" max="100" value={axisWeights[axis.key]} onChange={(e) => setAxisWeights(prev => ({ ...prev, [axis.key]: parseInt(e.target.value) }))} style={{ width: '100%' }} />
                    <div style={{ fontSize: '12px', fontWeight: '600', color: axis.color, marginTop: '4px', textAlign: 'center' }}>{axisWeights[axis.key]}%</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '8px', fontSize: '10px', color: COLORS.TEXT_DIM, textAlign: 'right' }}>Total: {Object.values(axisWeights).reduce((a, b) => a + b, 0)}%</div>
            </div>

            {/* 4-Axis Contribution Breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {matrixData?.ranked?.slice(0, 4).map((fm, idx) => {
                const safetyScore = fm.severity || 1;
                const operationalScore = fm.occurrence || 1;
                const envScore = Math.random() * 10; // Placeholder
                const costScore = Math.random() * 10; // Placeholder
                const weightedScore = (safetyScore * axisWeights.safety / 100) + (operationalScore * axisWeights.operational / 100) + (envScore * axisWeights.environmental / 100) + (costScore * axisWeights.cost / 100);
                return (
                  <div key={fm.id || idx} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '8px' }}>{fm.failureMode || fm.description || 'Failure Mode ' + (idx + 1)}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '10px', marginBottom: '8px' }}>
                      <div><span style={{ color: '#EF4444' }}>S:</span> {safetyScore.toFixed(1)}</div>
                      <div><span style={{ color: '#3B82F6' }}>O:</span> {operationalScore.toFixed(1)}</div>
                      <div><span style={{ color: '#10B981' }}>E:</span> {envScore.toFixed(1)}</div>
                      <div><span style={{ color: '#F59E0B' }}>C:</span> {costScore.toFixed(1)}</div>
                    </div>
                    <div style={{ paddingTop: '8px', borderTop: `1px solid ${COLORS.BORDER}`, fontSize: '11px', fontWeight: '600', color: COLORS.GOLD }}>Weighted: {weightedScore.toFixed(1)}</div>
                  </div>
                );
              })}
            </div>

            {!matrixData?.ranked || matrixData.ranked.length === 0 && (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Run matrix analysis to see 4-axis contributions</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// RCM DECISION TREE VIEW (Enhanced with RCMEngine)
// ============================================================================
function RCMDecisionTreeView({ rcmCauses = [], onUpdate }) {
  const [decisions, setDecisions] = useState(null);
  const [selectedCause, setSelectedCause] = useState(null);
  const [decisionPath, setDecisionPath] = useState(null);
  const [activeTab, setActiveTab] = useState('worksheet');

  const engine = useMemo(() => new RCMEngine(), []);

  const handleEvaluateAll = useCallback(() => {
    const results = engine.evaluateAll(rcmCauses);
    setDecisions(results);
  }, [engine, rcmCauses]);

  const handleEvaluateSingle = useCallback((cause) => {
    setSelectedCause(cause);
    const path = engine.getDecisionPath(cause);
    setDecisionPath(path);
    setActiveTab('decisions');
  }, [engine]);

  const taskDistribution = useMemo(() => {
    if (!decisions) return null;
    return engine.getTaskDistribution(decisions);
  }, [engine, decisions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ padding: '8px 12px', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={handleEvaluateAll}
          style={{ padding: '6px 12px', backgroundColor: COLORS.GOLD, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}
        >
          Evaluate All ({rcmCauses.length})
        </button>
        <div style={{ flex: 1 }} />
        {['worksheet', 'decisions', 'distribution'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 10px', fontSize: '11px', border: 'none', cursor: 'pointer', borderRadius: '4px',
              backgroundColor: activeTab === tab ? COLORS.BLUE : 'transparent',
              color: activeTab === tab ? '#FFFFFF' : COLORS.TEXT
            }}
          >
            {tab === 'worksheet' ? 'RCM Worksheet' : tab === 'decisions' ? 'Decision Path' : 'Task Distribution'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
        {activeTab === 'worksheet' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 12px 0' }}>RCM Analysis Worksheet</h3>
            {decisions ? (
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Failure Mode</th>
                    <th style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>RPN</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Consequence</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Task Type</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Recommendation</th>
                    <th style={{ padding: '8px', textAlign: 'center', color: COLORS.TEXT }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {decisions.map((d, idx) => {
                    const taskColor = d.taskType === 'Redesign' ? COLORS.ERROR : d.taskType === 'Run-to-Failure' ? COLORS.TEXT_DIM : COLORS.SUCCESS;
                    return (
                      <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: idx % 2 === 0 ? COLORS.LIGHT_BG : 'transparent' }}>
                        <td style={{ padding: '8px', color: COLORS.TEXT }}>{d.failureMode || rcmCauses[idx]?.failureMode || 'Unknown'}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: d.rpn > 200 ? COLORS.ERROR : d.rpn >= 100 ? COLORS.WARNING : COLORS.SUCCESS, fontWeight: 'bold' }}>
                          {d.rpn || 'N/A'}
                        </td>
                        <td style={{ padding: '8px', color: COLORS.INFO }}>{d.consequence || 'N/A'}</td>
                        <td style={{ padding: '8px', color: taskColor, fontWeight: '600' }}>{d.taskType || 'N/A'}</td>
                        <td style={{ padding: '8px', color: COLORS.TEXT_DIM, fontSize: '10px' }}>{d.recommendation || ''}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleEvaluateSingle(rcmCauses[idx])}
                            style={{ padding: '2px 8px', backgroundColor: COLORS.NAVY, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '2px', fontSize: '10px' }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Click "Evaluate All" to run RCM decision analysis on all failure modes</p>
            )}
          </div>
        )}

        {activeTab === 'decisions' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 12px 0' }}>Decision Tree Path</h3>
            {decisionPath ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ padding: '8px', backgroundColor: COLORS.PANEL_BG, borderRadius: '4px', border: `1px solid ${COLORS.GOLD}`, fontSize: '12px' }}>
                  <strong style={{ color: COLORS.GOLD }}>Failure Mode:</strong> <span style={{ color: COLORS.TEXT }}>{selectedCause?.failureMode || 'Unknown'}</span>
                </div>
                {decisionPath.questions?.map((q, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: COLORS.PANEL_BG, borderRadius: '4px', borderLeft: `3px solid ${COLORS.BLUE}` }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: COLORS.GOLD, minWidth: '24px' }}>{idx + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: COLORS.TEXT }}>{q.question}</div>
                      <div style={{ fontSize: '11px', color: q.answer ? COLORS.SUCCESS : COLORS.ERROR, fontWeight: '600', marginTop: '2px' }}>
                        Answer: {q.answer ? 'YES' : 'NO'}
                      </div>
                    </div>
                  </div>
                ))}
                <div style={{ padding: '12px', backgroundColor: COLORS.GOLD + '22', borderRadius: '4px', border: `2px solid ${COLORS.GOLD}` }}>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Recommended Task</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.GOLD }}>{decisionPath.taskType}</div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT, marginTop: '4px' }}>{decisionPath.recommendation}</div>
                </div>
              </div>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Select a failure mode from the worksheet to see its decision path</p>
            )}
          </div>
        )}

        {activeTab === 'distribution' && (
          <div>
            <h3 style={{ color: COLORS.TEXT, margin: '0 0 16px 0' }}>Task Type Distribution</h3>
            {taskDistribution ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(taskDistribution).map(([name, count]) => ({ name, value: count }))}
                      cx="50%" cy="50%"
                      outerRadius={100} innerRadius={40}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {Object.entries(taskDistribution).map(([_name, _count], idx) => (
                        <Cell key={idx} fill={[COLORS.GOLD, COLORS.BLUE, COLORS.SUCCESS, COLORS.WARNING, COLORS.ERROR, COLORS.INFO][idx % 6]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
                  {Object.entries(taskDistribution).map(([task, count]) => (
                    <div key={task} style={{ padding: '8px', backgroundColor: COLORS.PANEL_BG, borderRadius: '4px', border: `1px solid ${COLORS.BORDER}`, textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{task}</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.GOLD }}>{count}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px' }}>Run evaluation to see task distribution</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// BATCH SIMULATION DIALOG
// ============================================================================
function BatchSimDialog({ visible, onClose, onRun, projectData }) {
  const [scenarios, setScenarios] = useState([
    { id: 'base', name: 'Base Case', config: {} },
    { id: 'opt1', name: 'Optimization 1', config: {} }
  ]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [batchResults, setBatchResults] = useState(null);

  const handleRunAll = async () => {
    setRunning(true);
    setProgress(0);

    const engine = new SimulationEngine(projectData, {
      iterations: 5000,
      missionTime: 8760,
      seed: null,
      discountRate: 10
    });

    const results = await engine.runBatch(scenarios, (progressVal) => {
      setProgress(progressVal);
    });

    setBatchResults(results);
    setRunning(false);
    onRun?.(results);
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: COLORS.PANEL_BG,
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${COLORS.BORDER}`,
        minWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        color: COLORS.TEXT
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ marginTop: 0 }}>Batch Simulation</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.TEXT, cursor: 'pointer', fontSize: '20px' }}>×</button>
        </div>

        {!batchResults ? (
          <>
            <h3 style={{ fontSize: '13px', color: COLORS.TEXT }}>Scenarios</h3>
            <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Scenario</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Config</th>
                    <th style={{ padding: '8px', width: '50px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {scenarios.map((scenario) => (
                    <tr key={scenario.id} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                      <td style={{ padding: '8px', color: COLORS.TEXT }}>{scenario.name}</td>
                      <td style={{ padding: '8px', color: COLORS.TEXT_DIM, fontSize: '10px' }}>{JSON.stringify(scenario.config) || 'Default'}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <button
                          onClick={() => setScenarios(scenarios.filter(s => s.id !== scenario.id))}
                          style={{ padding: '2px 6px', backgroundColor: COLORS.ERROR, color: '#fff', border: 'none', cursor: 'pointer', borderRadius: '2px', fontSize: '10px' }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {running ? (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', marginBottom: '8px' }}>Running: {progress}%</div>
                <div style={{ height: '8px', backgroundColor: COLORS.BORDER, borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progress}%`, backgroundColor: COLORS.GOLD, transition: 'width 0.2s' }} />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleRunAll}
                  style={{ padding: '8px 16px', backgroundColor: COLORS.GOLD, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontWeight: '600' }}
                >
                  Run All
                </button>
                <button
                  onClick={onClose}
                  style={{ padding: '8px 16px', backgroundColor: COLORS.BORDER, color: COLORS.TEXT, border: 'none', cursor: 'pointer', borderRadius: '4px' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <h3 style={{ fontSize: '13px', color: COLORS.TEXT, marginBottom: '12px' }}>Results Comparison</h3>
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <th style={{ padding: '8px', textAlign: 'left', color: COLORS.TEXT }}>Scenario</th>
                    <th style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>Availability %</th>
                    <th style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>Cost $</th>
                    <th style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>MTBF hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResults.scenarios?.map((result, idx) => (
                    <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: idx % 2 === 0 ? COLORS.LIGHT_BG : 'transparent' }}>
                      <td style={{ padding: '8px', color: COLORS.TEXT }}>{scenarios[idx]?.name}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: COLORS.SUCCESS, fontWeight: 'bold' }}>{(result.availability * 100).toFixed(2)}%</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>$ {result.cost?.toFixed(0)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: COLORS.TEXT }}>{result.mtbf?.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={onClose}
              style={{ marginTop: '16px', padding: '8px 16px', backgroundColor: COLORS.GOLD, color: '#FFFFFF', border: 'none', cursor: 'pointer', borderRadius: '4px', fontWeight: '600' }}
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// REPORT GENERATION DIALOG
// ============================================================================
function ReportDialog({ visible, onClose, projectData, simResults }) {
  const [reportType, setReportType] = useState('availability');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeData, setIncludeData] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('quick'); // 'quick' or 'browse'

  const reportTemplates = {
    AvSim: [
      { id: 'av-summary', label: 'Availability Summary', desc: 'System availability metrics with confidence intervals' },
      { id: 'av-profile', label: 'Availability Profile', desc: 'Time-based availability trends over mission life' },
      { id: 'av-capacity', label: 'Capacity Analysis', desc: 'Production throughput & capacity-weighted availability' },
      { id: 'av-components', label: 'Component Rankings', desc: 'Equipment criticality & importance measures' }
    ],
    RCMCost: [
      { id: 'rcm-analysis', label: 'RCM Analysis', desc: 'Full FMEA & RCM decision logic output' },
      { id: 'rcm-tasks', label: 'Task Package', desc: 'Maintenance task definitions & schedules' },
      { id: 'rcm-criticality', label: '4-Axis Criticality', desc: 'Safety/Operational/Environmental/Cost assessment' }
    ],
    LCC: [
      { id: 'lcc-scenario', label: 'LCC Scenario', desc: 'Repair vs Replace vs Redesign analysis' },
      { id: 'lcc-sensitivity', label: 'Sensitivity Analysis', desc: 'Parameter sensitivity & tornado chart' },
      { id: 'lcc-investment', label: 'Investment Justification', desc: 'NPV/IRR analysis for CAPEX' }
    ],
    Enterprise: [
      { id: 'ent-fleet', label: 'Fleet Summary', desc: 'Multi-site fleet reliability overview' },
      { id: 'ent-benchmark', label: 'Benchmarking', desc: 'Cross-site performance comparison' }
    ],
    Resource: [
      { id: 'res-spares', label: 'Spares Analysis', desc: '3-level logistics & stock optimization' },
      { id: 'res-labor', label: 'Labor Crew Scheduling', desc: 'Crew utilization & shift planning' }
    ]
  };

  // Always reset generating state when dialog visibility changes
  useEffect(() => {
    setGenerating(false);
  }, [visible]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const designer = new ReportDesigner();
      const html = await designer.generateReport(reportType, {
        projectData,
        simResults,
        includeCharts,
        includeData
      });
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Report generation error:', err);
      alert('Report generation failed: ' + err.message + '\n\nPlease ensure you have run a simulation first and have project data loaded.');
    } finally {
      setGenerating(false);
    }
  };

  const handleExportPDF = async () => {
    setGenerating(true);
    try {
      const designer = new ReportDesigner();
      const html = await designer.generateReport(reportType, {
        projectData,
        simResults,
        includeCharts,
        includeData
      });
      if (window.electronAPI?.exportReportPDF) {
        await window.electronAPI.exportReportPDF(html, `Report_${reportType}.pdf`);
      } else {
        // Fallback: open as HTML preview
        const blob = new Blob([html], { type: 'text/html' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch (err) {
      console.error('PDF export error:', err);
      alert('PDF export requires the desktop app. Use Preview instead.');
    } finally {
      setGenerating(false);
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: COLORS.PANEL_BG,
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${COLORS.BORDER}`,
        minWidth: '500px',
        color: COLORS.TEXT
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ marginTop: 0 }}>Generate Report</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.TEXT, cursor: 'pointer', fontSize: '20px' }}>×</button>
        </div>

        {/* View Mode Selector */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button onClick={() => setViewMode('quick')} style={{ padding: '6px 12px', fontSize: '11px', backgroundColor: viewMode === 'quick' ? COLORS.BLUE : COLORS.BORDER, color: viewMode === 'quick' ? '#fff' : COLORS.TEXT, border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>Quick Report</button>
          <button onClick={() => setViewMode('browse')} style={{ padding: '6px 12px', fontSize: '11px', backgroundColor: viewMode === 'browse' ? COLORS.BLUE : COLORS.BORDER, color: viewMode === 'browse' ? '#fff' : COLORS.TEXT, border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>Browse Templates</button>
        </div>

        {viewMode === 'quick' && (
        <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px', color: COLORS.TEXT }}>Report Type:</div>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              <option value="availability">Availability Report</option>
              <option value="fmeca">FMECA Report</option>
              <option value="weibull">Weibull Analysis Report</option>
              <option value="lcc">Lifecycle Assessment Report</option>
              <option value="custom">Custom Report</option>
            </select>
          </label>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeCharts}
              onChange={(e) => setIncludeCharts(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '12px' }}>Include Charts</span>
          </label>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeData}
              onChange={(e) => setIncludeData(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '12px' }}>Include Data Tables</span>
          </label>
        </div>
        )}

        {viewMode === 'browse' && (
        <div style={{ display: 'grid', gap: '16px', marginBottom: '16px', maxHeight: '400px', overflow: 'auto' }}>
          {Object.entries(reportTemplates).map(([category, templates]) => (
            <div key={category}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '8px', paddingBottom: '4px', borderBottom: `1px solid ${COLORS.BORDER}` }}>{category}</div>
              <div style={{ display: 'grid', gap: '6px' }}>
                {templates.map(template => (
                  <div key={template.id} onClick={() => { setReportType(template.id); setViewMode('quick'); }} style={{ padding: '8px', backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT }}>{template.label}</div>
                    <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>{template.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleGenerateReport}
            disabled={generating}
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: generating ? COLORS.BORDER : COLORS.GOLD,
              color: '#FFFFFF',
              border: 'none',
              cursor: generating ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontWeight: '600',
              fontSize: '12px'
            }}
          >
            {generating ? 'Generating...' : 'Preview'}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={generating}
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: generating ? COLORS.BORDER : COLORS.BLUE,
              color: '#FFFFFF',
              border: 'none',
              cursor: generating ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontWeight: '600',
              fontSize: '12px'
            }}
          >
            {generating ? 'Generating...' : 'Export PDF'}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px',
              backgroundColor: COLORS.BORDER,
              color: COLORS.TEXT,
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DIALOGS
// ============================================================================
function SimulationConfigDialog({ visible, config, onClose, onSave }) {
  const [localConfig, setLocalConfig] = useState(config);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: COLORS.PANEL_BG,
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${COLORS.BORDER}`,
        minWidth: '400px',
        color: COLORS.TEXT
      }}>
        <h2 style={{ marginTop: 0, fontSize: '16px' }}>Simulation Configuration</h2>
        <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Iterations:</div>
            <input
              type="number"
              value={localConfig.iterations || 10000}
              onChange={(e) => setLocalConfig({ ...localConfig, iterations: parseInt(e.target.value) })}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Mission Time (hours):</div>
            <input
              type="number"
              value={localConfig.missionTime || 8760}
              onChange={(e) => setLocalConfig({ ...localConfig, missionTime: parseInt(e.target.value) })}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Random Seed (optional):</div>
            <input
              type="number"
              value={localConfig.seed || ''}
              onChange={(e) => setLocalConfig({ ...localConfig, seed: e.target.value ? parseInt(e.target.value) : null })}
              placeholder="Leave blank for random"
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Intervals:</div>
            <input
              type="number"
              value={localConfig.intervals || 52}
              onChange={(e) => setLocalConfig({ ...localConfig, intervals: parseInt(e.target.value) })}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Convergence Threshold:</div>
            <input
              type="number"
              step="0.01"
              value={localConfig.convergenceThreshold || 0.01}
              onChange={(e) => setLocalConfig({ ...localConfig, convergenceThreshold: parseFloat(e.target.value) })}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Discount Rate (%):</div>
            <input
              type="number"
              value={localConfig.discountRate || 10}
              onChange={(e) => setLocalConfig({ ...localConfig, discountRate: parseFloat(e.target.value) })}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Currency:</div>
            <select
              value={localConfig.currency || 'USD'}
              onChange={(e) => setLocalConfig({ ...localConfig, currency: e.target.value })}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box',
                fontSize: '12px'
              }}
            >
              {Object.entries(CURRENCIES).map(([code, curr]) => (
                <option key={code} value={code}>{curr.symbol} — {curr.name} ({code})</option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 16px',
              backgroundColor: COLORS.PANEL_BG,
              color: COLORS.NAVY,
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => { onSave(localConfig); onClose(); }}
            style={{
              padding: '6px 16px',
              backgroundColor: COLORS.NAVY,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            Save & Run
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FIND/REPLACE DIALOG
// ============================================================================
function FindReplaceDialog({ visible, onClose, data, onReplace, currentSection }) {
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [searchScope, setSearchScope] = useState('current');
  const [matches, setMatches] = useState([]);

  const handleFind = useCallback(() => {
    if (!findText.trim()) {
      setMatches([]);
      return;
    }

    const found = [];
    const dataToSearch = searchScope === 'current' ? (data[currentSection] || []) : Object.values(data).flat();

    dataToSearch.forEach((item, itemIdx) => {
      Object.entries(item).forEach(([key, value]) => {
        if (String(value).toLowerCase().includes(findText.toLowerCase())) {
          found.push({ itemIdx, itemId: item.id, field: key, value });
        }
      });
    });

    setMatches(found);
  }, [findText, data, searchScope, currentSection]);

  const handleReplaceAll = useCallback(() => {
    if (!findText.trim() || matches.length === 0) return;

    matches.forEach((match) => {
      onReplace?.(match.itemId, { [match.field]: replaceText });
    });

    setMatches([]);
    setFindText('');
    setReplaceText('');
  }, [findText, matches, replaceText, onReplace]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: COLORS.PANEL_BG,
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${COLORS.BORDER}`,
        minWidth: '450px',
        color: COLORS.TEXT
      }}>
        <h2 style={{ marginTop: 0, fontSize: '16px' }}>Find & Replace</h2>
        <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Find:</div>
            <input
              type="text"
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              placeholder="Enter text to find..."
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label>
            <div style={{ fontSize: '12px', marginBottom: '4px' }}>Replace With:</div>
            <input
              type="text"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              placeholder="Enter replacement text..."
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <select
              value={searchScope}
              onChange={(e) => setSearchScope(e.target.value)}
              style={{
                backgroundColor: COLORS.LIGHT_BG,
                color: COLORS.TEXT,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '4px',
                padding: '4px',
                cursor: 'pointer'
              }}
            >
              <option value="current">Current Section</option>
              <option value="all">All Sections</option>
            </select>
            <span>Scope</span>
          </label>
        </div>

        {matches.length > 0 && (
          <div style={{
            backgroundColor: COLORS.LIGHT_BG,
            padding: '8px',
            borderRadius: '4px',
            marginBottom: '12px',
            maxHeight: '120px',
            overflow: 'auto',
            fontSize: '11px'
          }}>
            <div style={{ color: COLORS.INFO, marginBottom: '6px', fontWeight: 'bold' }}>
              Found {matches.length} match{matches.length !== 1 ? 'es' : ''}:
            </div>
            {matches.map((match, idx) => (
              <div key={idx} style={{ padding: '4px', borderBottom: `1px solid ${COLORS.BORDER}`, color: COLORS.TEXT_DIM }}>
                {match.itemId} &gt; {match.field}: "{match.value}"
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleFind}
            style={{
              padding: '6px 16px',
              backgroundColor: COLORS.INFO,
              color: COLORS.NAVY,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Find
          </button>
          <button
            onClick={handleReplaceAll}
            disabled={matches.length === 0}
            style={{
              padding: '6px 16px',
              backgroundColor: matches.length === 0 ? COLORS.TEXT_DIM : COLORS.WARNING,
              color: COLORS.NAVY,
              border: 'none',
              borderRadius: '4px',
              cursor: matches.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              opacity: matches.length === 0 ? 0.5 : 1
            }}
          >
            Replace All
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '6px 16px',
              backgroundColor: COLORS.TEXT_DIM,
              color: COLORS.NAVY,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function AboutDialog({ visible, onClose }) {
  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: COLORS.PANEL_BG,
        padding: '0',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        minWidth: '480px',
        maxWidth: '520px',
        color: COLORS.TEXT,
        textAlign: 'center'
      }}>
        {/* ARaaS® branded header */}
        <div style={{
          backgroundColor: COLORS.NAVY,
          padding: '28px 40px 24px',
          position: 'relative'
        }}>
          <div style={{ fontSize: '32px', fontWeight: '300', letterSpacing: '-0.5px', color: '#FFFFFF', marginBottom: '4px' }}>
            Optimal<span style={{ fontSize: '18px', verticalAlign: 'super' }}>®</span>
          </div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '400', color: 'rgba(255,255,255,0.7)', letterSpacing: '2px', textTransform: 'uppercase' }}>Availability Studio</h2>
          {/* ARaaS® badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: 'rgba(0,168,203,0.15)',
            border: `1px solid ${COLORS.GOLD}`,
            borderRadius: '3px',
            padding: '3px 10px',
            marginTop: '4px'
          }}>
            <span style={{ fontSize: '10px', fontWeight: '700', color: COLORS.GOLD, letterSpacing: '1px' }}>ARaaS®</span>
            <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.5)', marginLeft: '6px' }}>Asset Reliability as a Service</span>
          </div>
          {/* Tagline */}
          <div style={{ marginTop: '12px', fontSize: '11px', fontStyle: 'italic', color: COLORS.GOLD, letterSpacing: '0.5px' }}>
            Shift left of P. Eliminate Defects.
          </div>
        </div>
        {/* Content */}
        <div style={{ padding: '24px 40px 28px' }}>
          <div style={{ width: '40px', height: '2px', backgroundColor: COLORS.GOLD, margin: '0 auto 16px' }} />
          <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', margin: '0 0 12px 0' }}>Version 1.0.0</p>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', lineHeight: '1.7', color: COLORS.TEXT, fontWeight: '300' }}>
            Enterprise reliability engineering software for system availability optimization, FMECA analysis, and life cycle cost analysis — powered by ARaaS®, Optimal's continuous asset reliability improvement framework.
          </p>
          {/* ARaaS cycle diagram */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0',
            margin: '16px 0',
            fontSize: '9px',
            fontWeight: '600',
            color: COLORS.NAVY
          }}>
            {['Assess', 'Analyse', 'Recommend', 'Implement', 'Monitor'].map((step, i) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  backgroundColor: i % 2 === 0 ? COLORS.NAVY : COLORS.GOLD,
                  color: '#FFFFFF',
                  padding: '4px 10px',
                  borderRadius: '2px',
                  fontSize: '8px',
                  fontWeight: '600',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap'
                }}>{step}</div>
                {i < 4 && <span style={{ color: COLORS.GOLD, margin: '0 2px', fontSize: '11px' }}>›</span>}
              </div>
            ))}
          </div>
          <p style={{ color: COLORS.TEXT_DIM, fontSize: '11px', margin: '16px 0', letterSpacing: '0.5px' }}>
            www.optimal.world | © 2026 Optimal® | All rights reserved
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '10px 32px',
              backgroundColor: COLORS.NAVY,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '2px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// GUIDED TOUR & TOOLTIP SYSTEM
// ============================================================================
const TOUR_STEPS = {
  hub: [
    { id: 'hub-modules', selector: '[data-tour="module-hub"]', title: 'Module Hub', description: 'Your command centre. Each card represents a full analysis module — click to open.', position: 'bottom' },
    { id: 'hub-outcomes', selector: '[data-tour="outcomes-btn"]', title: 'Guided Outcomes', description: 'Start from business questions like "What should I maintain?" and let the platform guide you to answers.', position: 'bottom' },
  ],
  grid: [
    { id: 'grid-tree', selector: '[data-tour="tree-panel"]', title: 'Project Structure', description: 'Your asset hierarchy. Click any section to view and edit entities in the data grid.', position: 'right' },
    { id: 'grid-table', selector: '[data-tour="data-grid"]', title: 'Data Grid', description: 'Edit equipment, failure modes, spares, labour, and all entity properties directly. Changes save automatically.', position: 'left' },
    { id: 'grid-toolbar', selector: '[data-tour="toolbar"]', title: 'Toolbar', description: 'Run simulations, generate reports, import/export data, and access all tools from here.', position: 'bottom' },
    { id: 'grid-hierarchy', selector: '[data-tour="hierarchy-std"]', title: 'Hierarchy Standard', description: 'Switch between ISO 14224 (Oil & Gas), RDS-PP (Power), or ISA-95 (Manufacturing) taxonomies.', position: 'right' },
  ],
  rbd: [
    { id: 'rbd-canvas', selector: '[data-tour="rbd-canvas"]', title: 'RBD Canvas', description: 'Drag and connect blocks to model series, parallel, and standby redundancy configurations.', position: 'top' },
    { id: 'rbd-sim', selector: '[data-tour="sim-btn"]', title: 'Run Simulation', description: 'Monte Carlo simulation evaluates system availability, MTBF, MTTR, and lifecycle costs.', position: 'bottom' },
  ],
  results: [
    { id: 'results-kpi', selector: '[data-tour="results-kpi"]', title: 'Key Results', description: 'System availability, MTBF, MTTR, and total cost — all computed from your simulation, with confidence intervals.', position: 'bottom' },
    { id: 'results-chart', selector: '[data-tour="results-chart"]', title: 'Availability Profile', description: 'Time-series availability plot showing how system performance varies over the mission period.', position: 'top' },
  ],
  ft: [
    { id: 'ft-canvas', selector: '[data-tour="ft-canvas"]', title: 'Fault Tree Editor', description: 'Build top-down failure logic using AND, OR, and Voting gates with primary failure events.', position: 'top' },
  ],
  weibull: [
    { id: 'weibull-plot', selector: '[data-tour="weibull-plot"]', title: 'Weibull Plot', description: 'Fit failure data to Weibull, Lognormal, or Exponential distributions. The shape parameter β reveals failure patterns.', position: 'top' },
  ],
  rcm: [
    { id: 'rcm-worksheet', selector: '[data-tour="rcm-worksheet"]', title: 'RCM Worksheet', description: 'Systematic analysis of failure modes, effects, and maintenance task selection per SAE JA1011, MSG-3, or MIL-STD-2173.', position: 'top' },
  ],
  criticality: [
    { id: 'crit-matrix', selector: '[data-tour="crit-matrix"]', title: 'Criticality Matrix', description: '4-axis scoring (Safety, Operational, Environmental, Cost) ranks your failure modes by weighted risk.', position: 'top' },
  ],
  lcc: [
    { id: 'lcc-chart', selector: '[data-tour="lcc-chart"]', title: 'Lifecycle Assessment', description: 'Estimate remaining useful life, compare lifecycle options (continue / overhaul / replace / decommission / revised PM), and run Monte Carlo cost simulations.', position: 'top' },
  ],
  spares: [
    { id: 'spares-chain', selector: '[data-tour="spares-chain"]', title: 'Spare Parts Logistics', description: '3-level logistics chain (Local → Regional → Manufacturing) with stock optimisation and lead time modelling.', position: 'top' },
  ],
  process: [
    { id: 'process-oee', selector: '[data-tour="process-oee"]', title: 'OEE Analysis', description: 'Overall Equipment Effectiveness broken into Availability × Performance × Quality with loss waterfall.', position: 'top' },
  ],
  sap: [
    { id: 'sap-import', selector: '[data-tour="sap-import"]', title: 'SAP PM Integration', description: 'Import equipment, notifications, and maintenance orders from SAP. Supports file-based CSV/IDoc or live OData API.', position: 'top' },
  ],
  maximo: [
    { id: 'maximo-import', selector: '[data-tour="maximo-import"]', title: 'Maximo Integration', description: 'Sync assets, work orders, and job plans with IBM Maximo via OSLC REST API or file export.', position: 'top' },
  ],
  enterprise: [
    { id: 'enterprise-mgr', selector: '[data-tour="enterprise-mgr"]', title: 'Enterprise Manager', description: 'User management, project permissions, SSO configuration, and multi-site deployment settings.', position: 'top' },
  ],
  workinstructions: [
    { id: 'wi-gen', selector: '[data-tour="wi-gen"]', title: 'Work Instructions', description: 'Auto-generate step-by-step maintenance procedures from your RCM analysis with safety warnings and tool lists.', position: 'top' },
  ],
  optimization: [
    { id: 'opt-interval', selector: '[data-tour="opt-interval"]', title: 'Interval Optimisation', description: 'Find the optimal PM interval that minimises total cost while meeting availability targets.', position: 'top' },
  ],
  alt: [
    { id: 'alt-stress', selector: '[data-tour="alt-stress"]', title: 'Accelerated Life Testing', description: 'Arrhenius, Inverse Power Law, and Eyring stress models to predict normal-use life from accelerated test data.', position: 'top' },
  ],
  eventtree: [
    { id: 'et-editor', selector: '[data-tour="et-editor"]', title: 'Event Tree Analysis', description: 'Model accident sequences from an initiating event through multiple safety barriers with probability propagation.', position: 'top' },
  ],
  library: [
    { id: 'lib-browse', selector: '[data-tour="lib-browse"]', title: 'Component Libraries', description: 'Industry reference data: OREDA failure rates, IEEE 493 power data, and custom libraries. Drag into your model.', position: 'top' },
  ],
};

function GuidedTour({ steps, onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = steps[currentStep];

  useEffect(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      setTargetRect(null);
    }
  }, [currentStep, step]);

  if (!step) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    else onComplete();
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const spotlightPad = 8;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50000, pointerEvents: 'auto' }}>
      {/* Dark overlay with spotlight cutout */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <defs>
          <mask id="tour-spotlight">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - spotlightPad} y={targetRect.top - spotlightPad}
                width={targetRect.width + spotlightPad * 2} height={targetRect.height + spotlightPad * 2}
                rx="8" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,47,101,0.75)" mask="url(#tour-spotlight)" />
        {targetRect && (
          <rect
            x={targetRect.left - spotlightPad} y={targetRect.top - spotlightPad}
            width={targetRect.width + spotlightPad * 2} height={targetRect.height + spotlightPad * 2}
            rx="8" fill="none" stroke="#00A8CB" strokeWidth="2"
            style={{ animation: 'tourPulse 2s ease-in-out infinite' }}
          />
        )}
      </svg>

      {/* Tooltip card */}
      <div style={{
        position: 'absolute',
        left: targetRect ? Math.min(targetRect.left, window.innerWidth - 360) : '50%',
        top: targetRect ? (step.position === 'top' ? targetRect.top - 170 : targetRect.bottom + 16) : '50%',
        transform: targetRect ? 'none' : 'translate(-50%, -50%)',
        width: '330px',
        backgroundColor: '#FFFFFF',
        borderRadius: '4px',
        boxShadow: '0 4px 24px rgba(0,47,101,0.18), 0 1px 3px rgba(0,47,101,0.1)',
        borderTop: '3px solid #00A8CB',
        padding: '22px 24px 20px',
        zIndex: 50001
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '9px', color: 'rgba(0,47,101,0.4)', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            {currentStep + 1} / {steps.length}
          </span>
          <button onClick={onSkip} style={{ background: 'none', border: 'none', color: 'rgba(0,47,101,0.35)', cursor: 'pointer', fontSize: '11px', fontWeight: '500', letterSpacing: '0.3px' }}>Skip</button>
        </div>
        <h3 style={{ margin: '0 0 6px 0', fontFamily: "'Georgia', serif", color: '#002F65', fontSize: '16px', fontWeight: '400', letterSpacing: '-0.01em' }}>{step.title}</h3>
        <p style={{ margin: '0 0 20px 0', color: '#475569', fontSize: '12px', lineHeight: '1.6' }}>{step.description}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={handleBack} disabled={currentStep === 0} style={{
            padding: '7px 16px', fontSize: '11px', border: `1px solid ${currentStep === 0 ? '#E5E7EB' : '#D1D5DB'}`, borderRadius: '3px',
            backgroundColor: '#fff', color: currentStep === 0 ? '#ccc' : '#002F65', cursor: currentStep === 0 ? 'default' : 'pointer', fontWeight: '500', letterSpacing: '0.2px'
          }}>Back</button>
          <div style={{ display: 'flex', gap: '3px' }}>
            {steps.map((_, i) => (
              <div key={i} style={{ width: i === currentStep ? '16px' : '6px', height: '3px', borderRadius: '2px', backgroundColor: i === currentStep ? '#00A8CB' : '#D1D5DB', transition: 'all 0.2s ease' }} />
            ))}
          </div>
          <button onClick={handleNext} style={{
            padding: '7px 16px', fontSize: '11px', border: 'none', borderRadius: '3px',
            backgroundColor: '#00A8CB', color: '#fff', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.2px'
          }}>{currentStep === steps.length - 1 ? 'Done' : 'Next'}</button>
        </div>
      </div>

      {/* Pulse animation */}
      <style>{`@keyframes tourPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}

function HotspotOverlay({ steps, onHotspotClick }) {
  const [rects, setRects] = useState({});
  const [activeHotspot, setActiveHotspot] = useState(null);

  useEffect(() => {
    const update = () => {
      const newRects = {};
      steps.forEach(step => {
        const el = document.querySelector(step.selector);
        if (el) {
          const r = el.getBoundingClientRect();
          newRects[step.id] = { top: r.top + 12, left: r.right - 20 };
        }
      });
      setRects(newRects);
    };
    update();
    const interval = setInterval(update, 3000);
    window.addEventListener('resize', update);
    return () => { clearInterval(interval); window.removeEventListener('resize', update); };
  }, [steps]);

  return (
    <>
      <style>{`
        @keyframes hotspotPulse {
          0% { box-shadow: 0 0 0 0 rgba(0,168,203,0.5); }
          70% { box-shadow: 0 0 0 10px rgba(0,168,203,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,168,203,0); }
        }
      `}</style>
      {steps.map(step => {
        const pos = rects[step.id];
        if (!pos) return null;
        return (
          <React.Fragment key={step.id}>
            <div
              onClick={() => setActiveHotspot(activeHotspot === step.id ? null : step.id)}
              style={{
                position: 'fixed', top: pos.top, left: pos.left,
                width: '14px', height: '14px', borderRadius: '50%',
                backgroundColor: '#00A8CB', border: '2px solid #fff',
                cursor: 'pointer', zIndex: 9000,
                animation: 'hotspotPulse 2s ease-out infinite',
                boxShadow: '0 2px 8px rgba(0,168,203,0.4)'
              }}
            />
            {activeHotspot === step.id && (
              <div style={{
                position: 'fixed', top: pos.top + 22, left: Math.min(pos.left - 100, window.innerWidth - 290),
                width: '260px', backgroundColor: '#fff', borderRadius: '4px',
                boxShadow: '0 4px 16px rgba(0,47,101,0.14), 0 1px 3px rgba(0,47,101,0.08)', borderTop: '2px solid #00A8CB',
                padding: '16px', zIndex: 9001
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h4 style={{ margin: '0 0 6px 0', fontFamily: "'Georgia', serif", color: '#002F65', fontSize: '13px', fontWeight: '400' }}>{step.title}</h4>
                  <button onClick={(e) => { e.stopPropagation(); setActiveHotspot(null); }} style={{ background: 'none', border: 'none', color: 'rgba(0,47,101,0.3)', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
                </div>
                <p style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '11px', lineHeight: '1.5' }}>{step.description}</p>
                <button onClick={(e) => { e.stopPropagation(); onHotspotClick?.(step); setActiveHotspot(null); }} style={{
                  padding: '5px 12px', fontSize: '10px', backgroundColor: 'rgba(0,168,203,0.06)',
                  border: '1px solid rgba(0,168,203,0.2)', borderRadius: '3px', color: '#00A8CB',
                  cursor: 'pointer', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase'
                }}>Ask OptimalAI about this</button>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

function AIHoverSuggest({ currentTab, currentModule }) {
  const [suggestion, setSuggestion] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(false);
  const hoverTimerRef = useRef(null);
  const currentElementRef = useRef(null);
  const dismissedRef = useRef(new Set());

  useEffect(() => {
    const handleMouseEnter = (e) => {
      if (!e.target || typeof e.target.closest !== 'function') return;
      const tourEl = e.target.closest('[data-tour]');
      if (!tourEl) return;
      const tourKey = tourEl.getAttribute('data-tour');
      if (dismissedRef.current.has(tourKey)) return;

      currentElementRef.current = tourKey;
      hoverTimerRef.current = setTimeout(() => {
        if (currentElementRef.current !== tourKey) return;
        const rect = tourEl.getBoundingClientRect();
        setPosition({
          top: Math.min(rect.bottom + 8, window.innerHeight - 180),
          left: Math.min(rect.left + 20, window.innerWidth - 320)
        });

        // Find matching help content
        const allSteps = Object.values(TOUR_STEPS).flat();
        const step = allSteps.find(s => s.selector === `[data-tour="${tourKey}"]`);
        const helpEntry = HELP_CONTENT[tourKey] || HELP_CONTENT[currentTab];

        setSuggestion({
          key: tourKey,
          title: step?.title || helpEntry?.title || tourKey,
          hint: step?.description || helpEntry?.summary || 'Need help with this feature?',
          context: `Feature: ${step?.title || tourKey}, Module: ${currentModule}, Tab: ${currentTab}`
        });
      }, 3000);
    };

    const handleMouseLeave = (e) => {
      if (!e.target || typeof e.target.closest !== 'function') return;
      const tourEl = e.target.closest('[data-tour]');
      if (tourEl) {
        currentElementRef.current = null;
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
      }
    };

    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);
    return () => {
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, [currentTab, currentModule]);

  const handleDismiss = () => {
    if (suggestion) dismissedRef.current.add(suggestion.key);
    setSuggestion(null);
  };

  const handleAskAI = async () => {
    if (!suggestion) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('oav_token');
      const resp = await fetch('/api/v1/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          message: `Explain how to use "${suggestion.title}" in OptimalAvailability Studio. Give practical guidance.`,
          context: suggestion.context
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setSuggestion(prev => prev ? { ...prev, aiReply: data.reply } : null);
      }
    } catch (err) {
      setSuggestion(prev => prev ? { ...prev, aiReply: 'AI assistant is currently unavailable. Check Help → Topics for detailed guidance.' } : null);
    }
    setLoading(false);
  };

  if (!suggestion) return null;

  return (
    <div style={{
      position: 'fixed', top: position.top, left: position.left,
      width: '300px', backgroundColor: '#fff', borderRadius: '4px',
      boxShadow: '0 4px 20px rgba(0,47,101,0.15), 0 1px 3px rgba(0,47,101,0.08)', borderTop: '2px solid #00A8CB',
      padding: '16px 18px', zIndex: 9500,
      animation: 'fadeSlideIn 0.2s ease-out'
    }}>
      <style>{`@keyframes fadeSlideIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '9px', color: 'rgba(0,47,101,0.4)', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
          AI Guidance
        </span>
        <button onClick={handleDismiss} style={{ background: 'none', border: 'none', color: 'rgba(0,47,101,0.3)', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
      </div>
      <h4 style={{ margin: '0 0 6px 0', fontFamily: "'Georgia', serif", color: '#002F65', fontSize: '13px', fontWeight: '400' }}>{suggestion.title}</h4>
      <p style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '11px', lineHeight: '1.5' }}>{suggestion.hint}</p>

      {suggestion.aiReply ? (
        <div style={{ backgroundColor: '#F7F8FA', border: '1px solid #E5E7EB', borderRadius: '3px', padding: '12px', marginBottom: '8px', maxHeight: '160px', overflow: 'auto' }}>
          <p style={{ margin: 0, color: '#0F172A', fontSize: '11px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{suggestion.aiReply}</p>
        </div>
      ) : (
        <button onClick={handleAskAI} disabled={loading} style={{
          padding: '7px 14px', fontSize: '10px', backgroundColor: loading ? '#F7F8FA' : 'rgba(0,168,203,0.06)',
          border: '1px solid rgba(0,168,203,0.2)', borderRadius: '3px', color: '#00A8CB',
          cursor: loading ? 'wait' : 'pointer', fontWeight: '600', width: '100%', letterSpacing: '0.3px', textTransform: 'uppercase'
        }}>{loading ? 'Asking OptimalAI...' : 'Ask OptimalAI for Guidance'}</button>
      )}

      <button onClick={handleDismiss} style={{
        padding: '4px 10px', fontSize: '10px', background: 'none', border: 'none',
        color: 'rgba(0,47,101,0.3)', cursor: 'pointer', width: '100%', marginTop: '4px', letterSpacing: '0.2px'
      }}>Dismiss</button>
    </div>
  );
}

function TourManager({ currentTab, currentModule, onAskAI }) {
  const [tourActive, setTourActive] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(() => {
    try { return JSON.parse(localStorage.getItem('oav_tour_completed') || 'false'); } catch { return false; }
  });
  const [showHotspots, setShowHotspots] = useState(() => {
    try { return JSON.parse(localStorage.getItem('oav_hotspots_enabled') || 'true'); } catch { return true; }
  });

  // Auto-start tour on first visit
  useEffect(() => {
    if (!tourCompleted) {
      const timer = setTimeout(() => setTourActive(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [tourCompleted]);

  const currentSteps = TOUR_STEPS[currentTab] || TOUR_STEPS[currentModule] || TOUR_STEPS.hub || [];

  const handleTourComplete = () => {
    setTourActive(false);
    setTourCompleted(true);
    localStorage.setItem('oav_tour_completed', 'true');
  };

  const handleTourSkip = () => {
    setTourActive(false);
    setTourCompleted(true);
    localStorage.setItem('oav_tour_completed', 'true');
  };

  const handleHotspotClick = (step) => {
    onAskAI?.(`Tell me about "${step.title}": ${step.description}`);
  };

  return (
    <>
      {tourActive && currentSteps.length > 0 && (
        <GuidedTour steps={currentSteps} onComplete={handleTourComplete} onSkip={handleTourSkip} />
      )}
      {tourCompleted && showHotspots && !tourActive && currentSteps.length > 0 && (
        <HotspotOverlay steps={currentSteps} onHotspotClick={handleHotspotClick} />
      )}
      {/* Tour restart + hotspot toggle buttons in bottom-left corner */}
      {tourCompleted && (
        <div style={{ position: 'fixed', bottom: '42px', left: '12px', display: 'flex', gap: '6px', zIndex: 8999 }}>
          <button
            onClick={() => { setTourActive(true); }}
            title="Restart guided tour"
            style={{
              width: '32px', height: '32px', borderRadius: '50%', border: '1px solid rgba(0,168,203,0.3)',
              backgroundColor: 'rgba(255,255,255,0.95)', color: '#00A8CB', fontSize: '14px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >🎓</button>
          <button
            onClick={() => { const next = !showHotspots; setShowHotspots(next); localStorage.setItem('oav_hotspots_enabled', JSON.stringify(next)); }}
            title={showHotspots ? 'Hide help hotspots' : 'Show help hotspots'}
            style={{
              width: '32px', height: '32px', borderRadius: '50%', border: '1px solid rgba(0,168,203,0.3)',
              backgroundColor: showHotspots ? 'rgba(0,168,203,0.1)' : 'rgba(255,255,255,0.95)',
              color: '#00A8CB', fontSize: '14px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >💡</button>
        </div>
      )}
    </>
  );
}

// ============================================================================
// CONTEXTUAL AI HELP SYSTEM
// ============================================================================
const HELP_CONTENT = {
  grid: {
    title: 'Data Grid',
    icon: '📊',
    summary: 'The Data Grid displays and allows editing of all entity properties for the selected tree section.',
    details: [
      'Select an entity type in the Project Structure tree on the left to populate the grid.',
      'Click any cell to edit its value directly. Changes are saved automatically.',
      'Use the Add Row button to create new entities in the current section.',
      'Right-click a row for options including copy, delete, and duplicate.',
      'Column headers can be clicked to sort data ascending or descending.'
    ],
    tips: 'Tip: Use Edit → Find & Replace (Ctrl+H) to make bulk changes across multiple entities.',
    araas: 'In the ARaaS® framework, accurate data entry here drives the reliability models that inform your maintenance strategy decisions.'
  },
  rbd: {
    title: 'Reliability Block Diagram',
    icon: '🔗',
    summary: 'The RBD editor lets you model system reliability by arranging equipment blocks in series and parallel configurations.',
    details: [
      'Add blocks using the toolbar buttons — each block represents an equipment item or subsystem.',
      'Series blocks (left to right) mean ALL must work for the system to function.',
      'Parallel blocks (stacked) provide redundancy — only one path needs to work.',
      'Standby blocks activate only when the primary block fails, reducing wear.',
      'Connect blocks by clicking the output port of one block and dragging to the input of another.',
      'Each block references a Failure Mode that defines its reliability characteristics (MTTF, MTTR, distribution).'
    ],
    tips: 'Tip: Use multiple RBD pages for complex systems. Right-click a block to assign it to a different page.',
    araas: 'RBD modelling is a core ARaaS® deliverable — it quantifies system availability and identifies the weakest links in your asset configuration.'
  },
  ft: {
    title: 'Fault Tree Analysis',
    icon: '🌳',
    summary: 'Fault Tree Analysis (FTA) is a top-down, deductive failure analysis using Boolean logic to identify causes of system-level failures.',
    details: [
      'Start with the Top Event — the undesired system failure you want to analyse.',
      'Add AND gates where ALL inputs must occur simultaneously to cause the output event.',
      'Add OR gates where ANY single input is sufficient to cause the output event.',
      'Voting gates (k-of-n) require a specified number of inputs to trigger.',
      'Primary Events at the bottom are basic equipment failures with known failure rates.',
      'The analysis computes minimal cut sets — the smallest combinations of failures that cause the top event.'
    ],
    tips: 'Tip: Run the FTA analysis (Ctrl+F5) to calculate cut sets and system failure probability.',
    araas: 'Fault tree analysis supports ARaaS® by identifying the critical failure paths that drive your risk-based maintenance priorities.'
  },
  eventtree: {
    title: 'Event Tree Analysis',
    icon: '🔀',
    summary: 'Event Tree Analysis (ETA) models the progression of an initiating event through a series of safety barriers or mitigating systems.',
    details: [
      'Define an Initiating Event — the starting condition (e.g., pump failure, gas leak).',
      'Add branch points for each safety barrier or protection system.',
      'Each branch splits into success (barrier works) and failure (barrier fails) paths.',
      'End states show the consequence of each scenario pathway.',
      'Probabilities propagate through the tree to calculate each end-state frequency.',
      'Use in combination with Fault Tree Analysis for comprehensive risk assessment.'
    ],
    tips: 'Tip: Link fault tree top events as branch probabilities for integrated FTA/ETA analysis.',
    araas: 'Event trees quantify scenario risk, enabling ARaaS® to prioritise protective barriers that deliver the greatest risk reduction per investment.'
  },
  results: {
    title: 'Simulation Results',
    icon: '📈',
    summary: 'The Results panel shows Monte Carlo simulation outputs including system availability, MTBF, MTTR, and cost breakdowns.',
    details: [
      'System Availability — the percentage of time the system is operational over the mission period.',
      'MTBF (Mean Time Between Failures) — average operating time between system failures.',
      'MTTR (Mean Time To Repair) — average time to restore the system after failure.',
      'The Availability Profile chart shows how availability changes over the simulation period.',
      'Cost breakdown shows labour, spares, consequence, preventive, and downtime costs.',
      'Component results rank individual equipment by their contribution to system unavailability.'
    ],
    tips: 'Tip: Increase the number of iterations in Settings for more statistically stable results.',
    araas: 'These simulation outputs are the quantitative foundation of ARaaS® — they identify where maintenance investment delivers the highest availability return.'
  },
  weibull: {
    title: 'Weibull Analysis',
    icon: '📉',
    summary: 'Weibull analysis fits failure time data to a Weibull distribution, revealing whether equipment is in early life, useful life, or wear-out phase.',
    details: [
      'Enter failure times in the Test Data tab — these are hours or days to failure for each observed event.',
      'The shape parameter β (beta) indicates failure behaviour: β < 1 = infant mortality, β ≈ 1 = random, β > 1 = wear-out.',
      'The scale parameter η (eta) is the characteristic life — 63.2% of units will have failed by this time.',
      'The Weibull plot shows data points on log-log axes with the fitted line.',
      'R² value indicates goodness of fit — values above 0.9 suggest a good Weibull model.',
      'Use the results to set appropriate failure mode parameters in your RBD or RCM analysis.'
    ],
    tips: 'Tip: A β value between 1 and 3 often indicates fatigue-driven wear-out, ideal for time-based preventive maintenance.',
    araas: 'Weibull analysis is the diagnostic engine of ARaaS® — understanding the failure pattern determines whether time-based, condition-based, or run-to-failure is the optimal strategy.'
  },
  rcm: {
    title: 'RCM Worksheet',
    icon: '🔧',
    summary: 'Reliability Centred Maintenance (RCM) systematically determines the most effective maintenance strategy for each failure mode.',
    details: [
      'For each equipment item, identify all functions it must perform.',
      'List all failure modes — the ways each function can be lost or degraded.',
      'Assess failure effects and consequences (safety, environmental, operational, economic).',
      'Apply the RCM decision logic to select the optimal task type for each failure mode.',
      'Task types include: time-based replacement, condition monitoring, failure finding, redesign, or run-to-failure.',
      'Record the recommended task, interval, and responsible role.'
    ],
    tips: 'Tip: Use the Component Libraries tab to apply standard failure modes from the Optimal® Reference Library.',
    araas: 'RCM is the heart of the ARaaS® methodology — it transforms raw reliability data into actionable, cost-optimised maintenance strategies.'
  },
  optimalrcm: {
    title: 'OptimalRCM Analysis',
    icon: '⚙️',
    summary: 'OptimalRCM extends traditional RCM with cost modelling, sensitivity analysis, and the P-F curve visualisation.',
    details: [
      'The analysis builds on RCM worksheet data to calculate annual maintenance costs per failure mode.',
      'Cost breakdown includes corrective, preventive, and downtime cost components.',
      'Potential savings are calculated by comparing current strategy cost against the optimised recommendation.',
      'The System Cost Profile chart shows total cost trends across all analysed equipment.',
      'The P-F Curve visualisation illustrates the ARaaS® "Shift left of P" concept.'
    ],
    tips: 'Tip: Review the Potential Savings column to prioritise which maintenance strategies to change first.',
    araas: 'OptimalRCM delivers the cost-benefit analysis that justifies ARaaS® maintenance transformation — it puts a monetary value on every strategy change.'
  },
  criticality: {
    title: 'Criticality Matrix',
    icon: '⚠️',
    summary: 'The Criticality Matrix ranks equipment by risk using a severity × frequency assessment aligned to MIL-STD-1629A.',
    details: [
      'Severity levels range from I (Catastrophic) to IV (Minor).',
      'Frequency levels range from A (Frequent) to E (Improbable).',
      'The matrix maps each combination to a risk zone: High (red), Serious (orange), Medium (yellow), or Low (green).',
      'Assign each failure mode a severity and frequency to place it on the matrix.',
      'Focus maintenance resources on High and Serious risk items first.'
    ],
    tips: 'Tip: Criticality rankings should feed directly into your RCM analysis to prioritise failure modes.',
    araas: 'Criticality analysis enables risk-based prioritisation — a foundational ARaaS® principle that ensures limited maintenance budgets target the highest-risk assets.'
  },
  lcc: {
    title: 'Lifecycle Assessment',
    icon: '💰',
    summary: 'Lifecycle Assessment estimates remaining useful life (RUL) and evaluates whole-life cost options to determine the optimal asset management strategy (ISO 55001 / ISO 15686-5).',
    details: [
      'Link assets from the Equipment Register and set up asset profiles with design life, criticality, and costs.',
      'Record condition history (1–5 score or 0–100 health index) to track degradation trends.',
      'Enter annual costs (maintenance, operating, downtime) and lifecycle event costs (overhaul, disposal, upgrade).',
      'RUL Analysis uses Weibull, condition-based, and age-based methods to estimate remaining life.',
      'Lifecycle Options compares Continue as-is, Major overhaul, Replace/upgrade, Decommission, and Revised PM strategy by NPV and EAC.',
      'Monte Carlo simulation (5 000 iterations) produces P10/P50/P90 confidence bands on lifecycle costs.'
    ],
    tips: 'Tip: Link existing Weibull sets from the Weibull Analysis module to auto-populate beta and eta parameters for more accurate RUL estimates.',
    araas: 'Lifecycle Assessment is the economic backbone of ARaaS® — it provides the evidence base for asset replacement, refurbishment, or continued maintenance investment decisions.'
  },
  sap: {
    title: 'SAP CMMS Connector',
    icon: '🔌',
    summary: 'The SAP connector imports and exports equipment, failure, and maintenance data between OptimalAV and SAP Plant Maintenance.',
    details: [
      'Import equipment master data (EQUI table) to populate your reliability model.',
      'Import maintenance notifications (QMEL) for failure history analysis.',
      'Import maintenance orders (AUFK) for cost and downtime analysis.',
      'Export optimised maintenance plans back to SAP for execution.',
      'Supports file-based import/export (CSV), SAP RFC, and OData API connections.'
    ],
    tips: 'Tip: Start with file-based CSV import if direct SAP connection is not yet configured.',
    araas: 'SAP integration closes the ARaaS® data loop — maintenance recommendations flow from analysis back into the CMMS for execution and tracking.'
  },
  maximo: {
    title: 'Maximo CMMS Connector',
    icon: '🔌',
    summary: 'The Maximo connector bridges OptimalAV with IBM Maximo for bi-directional asset and maintenance data exchange.',
    details: [
      'Import assets, locations, and work orders from Maximo.',
      'Map Maximo fields to OptimalAV entity properties.',
      'Export RCM-derived maintenance strategies as Maximo job plans.',
      'Supports REST API and file-based integration modes.'
    ],
    tips: 'Tip: Use the field mapping tool to align Maximo\'s data structure with OptimalAV entities.',
    araas: 'Like SAP integration, Maximo connectivity ensures ARaaS® recommendations are implemented through your existing work management system.'
  },
  process: {
    title: 'Process Reliability',
    icon: '🏭',
    summary: 'Process Reliability analysis evaluates production system throughput considering equipment reliability, buffer storage, and process constraints.',
    details: [
      'Model production lines with equipment stations, buffers, and parallel paths.',
      'Each station has reliability parameters (failure rate, repair rate) and throughput capacity.',
      'Buffers between stations absorb short-duration failures without stopping downstream production.',
      'The analysis calculates effective system throughput and production availability.',
      'Identify production bottlenecks — stations where failures cause the greatest throughput loss.'
    ],
    tips: 'Tip: Add buffer storage between critical stations to improve overall production availability.',
    araas: 'Process reliability modelling connects ARaaS® to production KPIs — demonstrating how reliability improvement translates directly to increased output and revenue.'
  },
  alt: {
    title: 'Accelerated Life Testing',
    icon: '🧪',
    summary: 'ALT analysis uses test data from accelerated stress conditions to predict equipment life under normal operating conditions.',
    details: [
      'Enter test results at multiple elevated stress levels (temperature, voltage, load).',
      'The analysis fits an acceleration model to extrapolate life at normal stress.',
      'Common models include Arrhenius (temperature), inverse power law (voltage), and Eyring.',
      'Results provide predicted MTTF and B10 life at the normal operating condition.',
      'Use ALT results to set realistic failure mode parameters when field data is limited.'
    ],
    tips: 'Tip: At least 3 stress levels with 5+ failures each gives reliable extrapolation.',
    araas: 'ALT provides the reliability predictions needed by ARaaS® when equipment is new or field failure data is insufficient to drive statistical models.'
  },
  optimization: {
    title: 'Interval Optimization',
    icon: '🎯',
    summary: 'Interval Optimization finds the maintenance interval that minimises total cost — balancing the cost of planned replacements against unplanned failures.',
    details: [
      'Set the Weibull shape (β) and scale (η) parameters for the failure mode.',
      'Enter the planned replacement cost and unplanned failure cost.',
      'The optimizer calculates total cost per unit time for a range of intervals.',
      'The optimal interval is where the cost curve reaches its minimum.',
      'Results show both the optimal interval and the expected cost savings versus current practice.'
    ],
    tips: 'Tip: This analysis is most valuable when β > 1 (wear-out failures). For β ≤ 1, time-based replacement does not reduce failure risk.',
    araas: 'Interval optimization is how ARaaS® turns Weibull analysis into a concrete, cost-justified maintenance schedule recommendation.'
  },
  library: {
    title: 'Component Libraries',
    icon: '📚',
    summary: 'Component Libraries provide reusable templates of equipment types, failure modes, and maintenance tasks that can be applied to new analyses.',
    details: [
      'Browse built-in libraries organised by equipment taxonomy (pumps, compressors, valves, etc.).',
      'Each library component includes standard failure modes, distribution parameters, and recommended tasks.',
      'Drag and drop or click to apply a library template to your current project.',
      'Create custom libraries from your own analysis data for reuse across projects.',
      'Import and export libraries in .oawx format for sharing between teams.'
    ],
    tips: 'Tip: The Optimal® Reference Library (from the Asset Maintenance database) provides field-validated failure data from real installations.',
    araas: 'Standardised component libraries accelerate the ARaaS® assessment phase — providing immediate, evidence-based starting points for reliability modelling.'
  },
  workinstructions: {
    title: 'Work Instructions',
    icon: '📋',
    summary: 'The Work Instruction Generator creates step-by-step maintenance procedures from your RCM analysis outputs.',
    details: [
      'Select maintenance tasks from your RCM worksheet to generate detailed work instructions.',
      'Each instruction includes task steps, required tools, safety precautions, and spare parts.',
      'Instructions follow a standardised format compatible with CMMS work order systems.',
      'Export as DOCX documents for field technician use.',
      'Link instructions back to the source RCM analysis for traceability.'
    ],
    tips: 'Tip: Export generated work instructions to DOCX and upload them to your CMMS as job plan attachments.',
    araas: 'Work instructions are the final ARaaS® output — translating reliability analysis into the executable procedures that maintenance teams follow on site.'
  },
  enterprise: {
    title: 'Enterprise Manager',
    icon: '🏢',
    summary: 'Enterprise Manager provides project version control, user management, and audit trail capabilities for multi-user environments.',
    details: [
      'Create version snapshots of your project at key milestones.',
      'Compare versions to see what changed between analysis iterations.',
      'Manage user access with role-based permissions (Viewer, Analyst, Lead, Admin).',
      'The audit log records all changes — who changed what and when.',
      'Check out projects for exclusive editing to prevent conflicts in team environments.'
    ],
    tips: 'Tip: Create a version snapshot before and after each major analysis change for easy rollback.',
    araas: 'Enterprise governance ensures ARaaS® analysis quality — every change is tracked, reviewed, and auditable against engineering standards.'
  }
};

function ContextualHelpButton({ currentTab, externalOpen, onExternalClose }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedTopic, setSelectedTopic] = React.useState(null);
  const [showTopicList, setShowTopicList] = React.useState(false);

  // Allow menu bar to open the help panel with topic list visible
  React.useEffect(() => {
    if (externalOpen) {
      setIsOpen(true);
      setShowTopicList(true);
      setSelectedTopic(null);
    }
  }, [externalOpen]);

  const handleClose = () => {
    setIsOpen(false);
    if (onExternalClose) onExternalClose();
  };

  // Determine which topic to display — selected override or current tab
  const activeKey = selectedTopic || currentTab || 'grid';
  const help = HELP_CONTENT[activeKey] || HELP_CONTENT.grid;

  // All available help topics grouped by category
  const helpCategories = [
    { label: 'Core Analysis', topics: [
      { key: 'grid', label: 'Data Grid' },
      { key: 'rbd', label: 'Reliability Block Diagram' },
      { key: 'results', label: 'Simulation Results' },
    ]},
    { label: 'Weibull & Life Data', topics: [
      { key: 'weibull', label: 'Weibull Analysis' },
      { key: 'alt', label: 'Accelerated Life Testing' },
      { key: 'optimization', label: 'Interval Optimization' },
    ]},
    { label: 'Process Safety', topics: [
      { key: 'ft', label: 'Fault Tree Analysis' },
      { key: 'eventtree', label: 'Event Tree Analysis' },
    ]},
    { label: 'Maintenance Strategy', topics: [
      { key: 'rcm', label: 'RCM Worksheet' },
      { key: 'optimalrcm', label: 'OptimalRCM Analysis' },
      { key: 'criticality', label: 'Criticality Matrix' },
    ]},
    { label: 'Cost & Lifecycle', topics: [
      { key: 'lcc', label: 'Lifecycle Assessment' },
      { key: 'process', label: 'Process Reliability' },
    ]},
    { label: 'Integrations & Tools', topics: [
      { key: 'sap', label: 'SAP Connector' },
      { key: 'maximo', label: 'Maximo Connector' },
      { key: 'library', label: 'Component Libraries' },
      { key: 'sfg20', label: 'SFG20 Scheduler' },
      { key: 'workinstructions', label: 'Work Instructions' },
      { key: 'enterprise', label: 'Enterprise Manager' },
    ]},
  ];

  const handleOpen = () => {
    if (isOpen) {
      handleClose();
    } else {
      setSelectedTopic(null);
      setShowTopicList(false);
      setIsOpen(true);
    }
  };

  return (
    <>
      {/* Floating help button */}
      <button
        onClick={handleOpen}
        style={{
          position: 'fixed',
          bottom: '44px',
          right: '16px',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: isOpen ? COLORS.GOLD : COLORS.NAVY,
          color: '#FFFFFF',
          border: `2px solid ${isOpen ? COLORS.NAVY : COLORS.GOLD}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          fontWeight: '700',
          zIndex: 1500,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          transition: 'all 0.2s ease'
        }}
        title={`Help: ${help.title}`}
      >
        ?
      </button>

      {/* Help panel */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: '86px',
          right: '16px',
          width: showTopicList ? '540px' : '380px',
          maxHeight: '75vh',
          backgroundColor: COLORS.PANEL_BG,
          borderRadius: '8px',
          border: `1px solid ${COLORS.BORDER}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          zIndex: 1500,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease'
        }}>
          {/* Header */}
          <div style={{
            backgroundColor: COLORS.NAVY,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>{help.icon}</span>
              <div>
                <div style={{ color: '#FFFFFF', fontSize: '13px', fontWeight: '600' }}>{help.title}</div>
                <div style={{ color: COLORS.GOLD, fontSize: '9px', fontWeight: '600', letterSpacing: '0.5px' }}>
                  {showTopicList ? 'HELP CENTRE' : 'CONTEXTUAL HELP'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {/* Toggle topic list button */}
              <button
                onClick={() => setShowTopicList(!showTopicList)}
                style={{
                  background: showTopicList ? 'rgba(255,255,255,0.15)' : 'none',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  fontSize: '10px',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontWeight: '600'
                }}
                title="Browse all help topics"
              >
                {showTopicList ? 'Hide Topics' : 'All Topics'}
              </button>
              <button
                onClick={handleClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '0 4px'
                }}
              >×</button>
            </div>
          </div>

          {/* Body — topic sidebar + content */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

            {/* Topic sidebar */}
            {showTopicList && (
              <div style={{
                width: '180px',
                flexShrink: 0,
                borderRight: `1px solid ${COLORS.BORDER}`,
                overflow: 'auto',
                backgroundColor: '#F7F8FA',
                padding: '8px 0'
              }}>
                {helpCategories.map((cat, ci) => (
                  <div key={ci}>
                    <div style={{
                      fontSize: '9px',
                      fontWeight: '700',
                      color: COLORS.NAVY,
                      letterSpacing: '0.8px',
                      textTransform: 'uppercase',
                      padding: '8px 12px 4px 12px',
                      opacity: 0.6
                    }}>{cat.label}</div>
                    {cat.topics.map(t => {
                      const isActive = activeKey === t.key;
                      const isCurrent = currentTab === t.key;
                      return (
                        <button
                          key={t.key}
                          onClick={() => setSelectedTopic(t.key)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            background: isActive ? COLORS.NAVY : 'none',
                            color: isActive ? '#FFFFFF' : COLORS.TEXT,
                            border: 'none',
                            padding: '5px 12px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: isActive ? '600' : '400',
                            borderLeft: isCurrent ? `3px solid ${COLORS.GOLD}` : '3px solid transparent'
                          }}
                        >
                          {HELP_CONTENT[t.key] ? HELP_CONTENT[t.key].icon : ''} {t.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {/* Content area */}
            <div style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
              {/* Summary */}
              <p style={{ fontSize: '12px', lineHeight: '1.6', color: COLORS.TEXT, marginBottom: '12px', fontWeight: '400' }}>
                {help.summary}
              </p>

              {/* Details */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: COLORS.NAVY, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px' }}>
                  How to use
                </div>
                {help.details.map((detail, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '6px',
                    fontSize: '11px',
                    lineHeight: '1.5',
                    color: COLORS.TEXT_DIM
                  }}>
                    <span style={{
                      flexShrink: 0,
                      width: '5px',
                      height: '5px',
                      borderRadius: '50%',
                      backgroundColor: COLORS.GOLD,
                      marginTop: '5px'
                    }}></span>
                    <span>{detail}</span>
                  </div>
                ))}
              </div>

              {/* Tip box */}
              <div style={{
                backgroundColor: '#D4F1F9',
                borderLeft: `3px solid ${COLORS.GOLD}`,
                padding: '8px 10px',
                borderRadius: '0 4px 4px 0',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '11px', lineHeight: '1.5', color: COLORS.NAVY }}>
                  {help.tips}
                </div>
              </div>

              {/* ARaaS connection */}
              <div style={{
                backgroundColor: COLORS.NAVY,
                padding: '10px 12px',
                borderRadius: '4px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '9px', fontWeight: '700', color: COLORS.GOLD, letterSpacing: '0.5px' }}>ARaaS®</span>
                  <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.4)' }}>CONTEXT</span>
                </div>
                <div style={{ fontSize: '10px', lineHeight: '1.5', color: 'rgba(255,255,255,0.8)' }}>
                  {help.araas}
                </div>
              </div>
            </div>
          </div>

          {/* Footer with topic count */}
          <div style={{
            padding: '8px 16px',
            borderTop: `1px solid ${COLORS.BORDER}`,
            backgroundColor: '#F7F8FA',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>
              {Object.keys(HELP_CONTENT).length} help topics available
            </span>
            <span style={{ fontSize: '9px', color: COLORS.GOLD, fontStyle: 'italic' }}>
              Powered by ARaaS®
            </span>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// P-F CURVE VISUALISATION (ARaaS® — Shift left of P)
// ============================================================================
function PFCurveVisualization() {
  // P-F Curve data points — shows equipment condition degrading over time
  const curvePoints = "M 40,40 C 50,40 80,42 120,50 C 160,58 200,75 230,100 C 250,118 270,145 290,180 C 305,210 315,245 325,285";
  const shiftedPoints = "M 40,40 C 45,40 60,41 80,45 C 100,50 120,60 140,78 C 155,92 170,115 185,145 C 195,170 205,200 215,240";

  return (
    <div style={{
      backgroundColor: COLORS.PANEL_BG,
      border: `1px solid ${COLORS.BORDER}`,
      borderRadius: '6px',
      padding: '16px',
      marginTop: '12px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <h4 style={{ margin: '0 0 2px 0', color: COLORS.NAVY, fontSize: '13px', fontWeight: '600' }}>
            P-F Curve — Asset Condition Degradation
          </h4>
          <span style={{ fontSize: '10px', color: COLORS.GOLD, fontStyle: 'italic', letterSpacing: '0.3px' }}>
            ARaaS® — Shift left of P. Eliminate Defects.
          </span>
        </div>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: COLORS.NAVY,
          borderRadius: '2px',
          padding: '3px 8px'
        }}>
          <span style={{ fontSize: '8px', fontWeight: '700', color: COLORS.GOLD, letterSpacing: '0.5px' }}>ARaaS®</span>
        </div>
      </div>

      <svg width="100%" height="220" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid meet">
        {/* Background grid */}
        {[60, 100, 140, 180, 220, 260].map(y => (
          <line key={y} x1="35" y1={y} x2="370" y2={y} stroke="#E5E7EB" strokeWidth="0.5" strokeDasharray="3,3" />
        ))}

        {/* Axes */}
        <line x1="35" y1="30" x2="35" y2="285" stroke={COLORS.NAVY} strokeWidth="1.5" />
        <line x1="35" y1="285" x2="370" y2="285" stroke={COLORS.NAVY} strokeWidth="1.5" />

        {/* Y-axis label */}
        <text x="12" y="160" fill={COLORS.TEXT_DIM} fontSize="9" textAnchor="middle" transform="rotate(-90, 12, 160)">Equipment Condition</text>
        <text x="200" y="298" fill={COLORS.TEXT_DIM} fontSize="9" textAnchor="middle">Time</text>

        {/* Y-axis markers */}
        <text x="30" y="45" fill={COLORS.TEXT_DIM} fontSize="7" textAnchor="end">100%</text>
        <text x="30" y="165" fill={COLORS.TEXT_DIM} fontSize="7" textAnchor="end">50%</text>
        <text x="30" y="285" fill={COLORS.TEXT_DIM} fontSize="7" textAnchor="end">0%</text>

        {/* Traditional P-F interval (original curve) */}
        <path d={curvePoints} fill="none" stroke={COLORS.NAVY} strokeWidth="2.5" strokeLinecap="round" opacity="0.3" />

        {/* ARaaS shifted curve (earlier detection) */}
        <path d={shiftedPoints} fill="none" stroke={COLORS.GOLD} strokeWidth="2.5" strokeLinecap="round" />

        {/* P point — Potential failure (original) */}
        <circle cx="230" cy="100" r="5" fill={COLORS.NAVY} opacity="0.3" />
        <text x="238" y="95" fill={COLORS.NAVY} fontSize="8" fontWeight="600" opacity="0.4">P (Traditional)</text>

        {/* P point — Shifted left (ARaaS) */}
        <circle cx="140" cy="78" r="6" fill={COLORS.GOLD} />
        <text x="148" y="73" fill={COLORS.GOLD} fontSize="8" fontWeight="700">P (ARaaS®)</text>

        {/* F point — Functional failure */}
        <circle cx="325" cy="285" r="5" fill="#C0392B" />
        <text x="310" y="275" fill="#C0392B" fontSize="8" fontWeight="600">F (Failure)</text>

        {/* P-F Interval arrows */}
        {/* Traditional P-F interval */}
        <line x1="230" y1="287" x2="325" y2="287" stroke={COLORS.NAVY} strokeWidth="1" opacity="0.3" markerEnd="url(#arrowNavy)" />
        <text x="278" y="282" fill={COLORS.NAVY} fontSize="7" textAnchor="middle" opacity="0.4">Traditional P-F</text>

        {/* ARaaS extended P-F interval */}
        <line x1="140" y1="292" x2="325" y2="292" stroke={COLORS.GOLD} strokeWidth="1.5" markerEnd="url(#arrowGold)" />
        <text x="232" y="299" fill={COLORS.GOLD} fontSize="7" textAnchor="middle" fontWeight="600">ARaaS® Extended P-F Interval</text>

        {/* Shift arrow */}
        <defs>
          <marker id="arrowGold" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill={COLORS.GOLD} />
          </marker>
          <marker id="arrowNavy" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill={COLORS.NAVY} opacity="0.3" />
          </marker>
        </defs>

        {/* Shift left arrow */}
        <line x1="225" y1="100" x2="150" y2="82" stroke={COLORS.GOLD} strokeWidth="1.5" strokeDasharray="4,2" />
        <text x="175" y="82" fill={COLORS.GOLD} fontSize="7" textAnchor="middle" fontWeight="600">← Shift Left</text>

        {/* Intervention zone fill */}
        <rect x="140" y="30" width="90" height="255" fill={COLORS.GOLD} opacity="0.04" rx="2" />
        <text x="185" y="50" fill={COLORS.GOLD} fontSize="7" textAnchor="middle" fontWeight="600" opacity="0.6">INTERVENTION ZONE</text>
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '20px', marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${COLORS.BORDER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '20px', height: '3px', backgroundColor: COLORS.GOLD, borderRadius: '2px' }}></div>
          <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>ARaaS® early detection curve</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '20px', height: '3px', backgroundColor: COLORS.NAVY, borderRadius: '2px', opacity: 0.3 }}></div>
          <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>Traditional detection curve</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: '#C0392B', borderRadius: '50%' }}></div>
          <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>Functional failure point</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAXIMO CMMS CONNECTOR VIEW
// ============================================================================
function MaximoConnectorView({ projectData, onImportData }) {
  const [connector] = useState(() => new MaximoConnector());
  const [activeTab, setActiveTab] = useState('dashboard');
  const [importResult, setImportResult] = useState(null);
  const [selectedTable, setSelectedTable] = useState('ASSET');
  const [syncLog, setSyncLog] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [kpis, setKpis] = useState(null);

  const tables = useMemo(() => connector.getAvailableTables(), [connector]);

  const handleImportFile = useCallback(async () => {
    if (window.electronAPI?.importSAPFile) {
      const result = await window.electronAPI.importSAPFile();
      if (result) {
        const importRes = connector.autoImport(result.content, result.filename);
        setImportResult(importRes);
        setSyncLog(connector.getSyncLog());
        if (importRes.success && importRes.records && onImportData) {
          onImportData(importRes.entity, importRes.records);
        }
      }
    }
  }, [connector, onImportData]);

  const handleCalcKPIs = useCallback(() => {
    const wo = projectData.maintenanceorders || [];
    const assets = projectData.equipments || [];
    if (wo.length > 0 || assets.length > 0) {
      const result = connector.calculatePerformanceKPIs(wo, assets);
      setKpis(result);
    }
  }, [connector, projectData]);

  const tabDefs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'import', label: 'Import from Maximo' },
    { id: 'export', label: 'Export to Maximo' },
    { id: 'kpis', label: 'Performance KPIs' },
    { id: 'config', label: 'Connection Settings' }
  ];

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', backgroundColor: COLORS.PANEL_BG, borderRadius: '6px', padding: '4px' }}>
        {tabDefs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '8px 16px', backgroundColor: activeTab === tab.id ? COLORS.BLUE : 'transparent',
            color: activeTab === tab.id ? '#FFFFFF' : COLORS.TEXT, border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
            fontWeight: activeTab === tab.id ? '600' : '400'
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'dashboard' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
            {[
              { label: 'Maximo Tables Mapped', value: tables.length, color: COLORS.GOLD },
              { label: 'Assets', value: (projectData.equipments || []).length, color: COLORS.BLUE },
              { label: 'Work Orders', value: (projectData.maintenanceorders || []).length, color: '#00A8CB' },
              { label: 'Sync Operations', value: syncLog.length, color: '#002F65' }
            ].map((card, i) => (
              <div key={i} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '18px 20px', borderTop: `2px solid ${card.color}` }}>
                <div style={{ fontSize: '9px', fontWeight: '600', color: 'rgba(0,47,101,0.4)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px' }}>{card.label}</div>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: '22px', fontWeight: '400', color: COLORS.TEXT, letterSpacing: '-0.02em' }}>{card.value}</div>
              </div>
            ))}
          </div>
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '20px 24px' }}>
            <div style={{ fontSize: '9px', fontWeight: '600', color: 'rgba(0,47,101,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '14px' }}>Quick Actions</div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={handleImportFile} style={{ padding: '8px 16px', backgroundColor: '#002F65', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase' }}>Import from File</button>
              <button onClick={handleCalcKPIs} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#00A8CB', border: '1px solid rgba(0,168,203,0.3)', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase' }}>Calculate KPIs</button>
              <button onClick={() => setConnectionStatus(connector.testConnection())} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase' }}>Test Connection</button>
            </div>
            {connectionStatus && (
              <div style={{ marginTop: '14px', padding: '10px 14px', borderRadius: '3px', fontSize: '12px', borderLeft: `3px solid ${connectionStatus.success ? '#00A8CB' : '#002F65'}`, backgroundColor: connectionStatus.success ? '#F7FDFE' : '#F7F8FA', color: COLORS.TEXT }}>
                {connectionStatus.message}
              </div>
            )}
          </div>
          {/* Table listing */}
          <div style={{ marginTop: '16px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY, fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>Maximo Table Mappings</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead><tr style={{ backgroundColor: COLORS.NAVY }}>
                {['Maximo Table', 'Local Entity', 'Fields', 'Records'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', borderBottom: `2px solid ${COLORS.GOLD}`, fontWeight: '600' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {tables.map((t, idx) => (
                  <tr key={t.table} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : COLORS.PANEL_BG + '80' }}>
                    <td style={{ padding: '8px 12px', color: COLORS.GOLD, fontWeight: '700', fontFamily: 'monospace' }}>{t.table}</td>
                    <td style={{ padding: '8px 12px', color: COLORS.TEXT }}>{t.entity}</td>
                    <td style={{ padding: '8px 12px', color: COLORS.TEXT, textAlign: 'center' }}>{t.fieldCount}</td>
                    <td style={{ padding: '8px 12px', color: (projectData[t.entity] || []).length > 0 ? '#00A8CB' : COLORS.TEXT_DIM, fontWeight: '600', textAlign: 'center' }}>{(projectData[t.entity] || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'import' && (
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '20px 24px' }}>
          <div style={{ fontSize: '9px', fontWeight: '600', color: 'rgba(0,47,101,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px' }}>Data Import</div>
          <h3 style={{ margin: '0 0 8px 0', fontFamily: "'Georgia', serif", color: COLORS.TEXT, fontSize: '16px', fontWeight: '400' }}>Import from IBM Maximo</h3>
          <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '20px', lineHeight: '1.5' }}>Import assets, work orders, locations, job plans, and more. Supports CSV, JSON (REST API), and MIF/MEA XML.</p>
          <button onClick={handleImportFile} style={{ padding: '9px 20px', backgroundColor: '#002F65', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase' }}>Import File</button>
          {importResult && (
            <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '3px', borderLeft: `3px solid ${importResult.success ? '#00A8CB' : '#002F65'}`, backgroundColor: importResult.success ? '#F7FDFE' : '#F7F8FA', color: COLORS.TEXT, fontSize: '12px' }}>
              {importResult.success ? `Imported ${importResult.mappedRows || importResult.totalRecords || 0} records into ${importResult.entity}` : importResult.error}
            </div>
          )}
        </div>
      )}

      {activeTab === 'export' && (
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Export to IBM Maximo</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {tables.map(t => {
              const count = (projectData[t.entity] || []).length;
              return (
                <div key={t.table} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '12px' }}>
                  <div style={{ color: COLORS.GOLD, fontWeight: '700', fontFamily: 'monospace' }}>{t.table}</div>
                  <div style={{ color: COLORS.TEXT, fontSize: '12px' }}>{t.entity} ({count} records)</div>
                  <button onClick={() => {
                    const data = projectData[t.entity] || [];
                    const csv = connector.exportCSV(data, t.table);
                    if (window.electronAPI?.exportSAPFile) window.electronAPI.exportSAPFile({ content: csv, filename: `Maximo_${t.table}.csv`, format: 'csv' });
                    setSyncLog(connector.getSyncLog());
                  }} disabled={count === 0} style={{ marginTop: '8px', padding: '6px 12px', backgroundColor: count > 0 ? COLORS.BLUE : COLORS.BORDER, color: count > 0 ? '#fff' : COLORS.TEXT_DIM, border: 'none', borderRadius: '4px', cursor: count > 0 ? 'pointer' : 'not-allowed', fontSize: '11px' }}>Export CSV</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'kpis' && (
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Performance KPIs (from Maximo Work Orders)</h3>
          <button onClick={handleCalcKPIs} style={{ padding: '8px 16px', backgroundColor: COLORS.BLUE, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginBottom: '16px' }}>Calculate KPIs</button>
          {kpis && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              {[
                { label: 'MTBF (hours)', value: kpis.mtbf, color: '#00A8CB' },
                { label: 'MTTR (hours)', value: kpis.mttr, color: '#002F65' },
                { label: 'Availability %', value: kpis.availability?.toFixed(2), color: COLORS.BLUE },
                { label: 'Total Failures', value: kpis.failureCount, color: COLORS.GOLD },
                { label: 'Total Downtime (hrs)', value: kpis.totalDowntime, color: '#002F65' },
                { label: 'Equipment Count', value: kpis.equipmentCount, color: COLORS.BLUE },
                { label: 'OEE %', value: kpis.oee?.toFixed(2) || 'N/A', color: '#00A8CB' },
                { label: 'Failure Rate (per hr)', value: kpis.failureRate?.toFixed(6) || '0', color: COLORS.GOLD }
              ].map((card, i) => (
                <div key={i} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '14px', borderLeft: `4px solid ${card.color}` }}>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>{card.label}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.TEXT }}>{card.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '20px 24px' }}>
          <div style={{ fontSize: '9px', fontWeight: '600', color: 'rgba(0,47,101,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px' }}>Configuration</div>
          <h3 style={{ margin: '0 0 8px 0', fontFamily: "'Georgia', serif", color: COLORS.TEXT, fontSize: '16px', fontWeight: '400' }}>Maximo Connection</h3>
          <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '20px', lineHeight: '1.5' }}>Supports File Import/Export (CSV/JSON/XML), Maximo REST API, and OSLC API connections.</p>
          <div style={{ display: 'flex', gap: '10px' }}>
            {['file', 'rest', 'oslc'].map(ct => (
              <button key={ct} style={{ flex: 1, padding: '14px 16px', backgroundColor: '#F7F8FA', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease' }}>
                <div style={{ color: COLORS.TEXT, fontSize: '12px', fontWeight: '600', letterSpacing: '0.01em' }}>{ct === 'file' ? 'File Import/Export' : ct === 'rest' ? 'REST API' : 'OSLC API'}</div>
                <div style={{ color: COLORS.TEXT_DIM, fontSize: '10px', marginTop: '4px' }}>{ct === 'file' ? 'CSV, JSON, MIF XML' : ct === 'rest' ? 'Maximo REST endpoints' : 'OSLC resource sets'}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ENTERPRISE MANAGER VIEW
// ============================================================================
function EnterpriseView({ projectData, setProjectData }) {
  // G06: EN 15341 KPI Aggregation Engine
  const kpiData = useMemo(() => {
    const equips = projectData.equipments || [];
    const spares = projectData.spares || [];
    const sched = projectData.scheduledtasks || [];
    const corr = projectData.correctivetasks || [];
    const lca = projectData.lifecycleAssessments || [];
    const markov = projectData.markovAnalyses || [];
    const weibull = projectData.weibullsets || [];
    const fms = projectData.failuremodels || [];

    // Availability from Markov (latest analysis) or simulation
    const latestMarkov = markov.length > 0 ? markov[markov.length - 1] : null;
    const systemAvailability = latestMarkov?.availability ?? null;

    // MTBF from Weibull (average across all sets)
    const avgMTTF = weibull.length > 0 ? weibull.reduce((s, w) => s + (w.mttf || 0), 0) / weibull.length : null;

    // Maintenance cost index from LCC
    const totalLCCCost = lca.reduce((s, a) => s + (a.costData?.annualMaintenance || 0) + (a.costData?.annualOperating || 0), 0);
    const assetValue = lca.reduce((s, a) => s + (a.assetProfile?.replacementCost || 0), 0);
    const maintenanceCostIndex = assetValue > 0 ? (totalLCCCost / assetValue * 100) : null;

    // PM vs CM ratio
    const pmCount = sched.length;
    const cmCount = corr.length;
    const pmRatio = (pmCount + cmCount) > 0 ? (pmCount / (pmCount + cmCount) * 100) : null;

    // Spares fill rate
    const sparesWithStock = spares.filter(s => (s.currentStock || 0) > 0).length;
    const sparesFillRate = spares.length > 0 ? (sparesWithStock / spares.length * 100) : null;

    // Equipment condition (from TREND sync)
    const conditioned = equips.filter(e => e.trendHealthScore != null);
    const avgHealth = conditioned.length > 0 ? conditioned.reduce((s, e) => s + e.trendHealthScore, 0) / conditioned.length : null;

    // Failure mode coverage
    const equipWithFMs = new Set(fms.map(f => f.equipment || f.equipmentId)).size;
    const fmCoverage = equips.length > 0 ? (equipWithFMs / equips.length * 100) : null;

    return {
      systemAvailability, avgMTTF, maintenanceCostIndex, pmRatio,
      sparesFillRate, avgHealth, fmCoverage, totalLCCCost, assetValue,
      equipCount: equips.length, fmCount: fms.length, spareCount: spares.length,
      weibullCount: weibull.length, markovCount: markov.length, lcaCount: lca.length
    };
  }, [projectData]);

  // G19: Report generator
  const generateReport = (reportType) => {
    const now = new Date().toISOString().split('T')[0];
    const title = reportType;
    const kpi = kpiData;
    let html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title>
    <style>body{font-family:'Helvetica Neue',Arial,sans-serif;color:#0D1117;max-width:900px;margin:0 auto;padding:40px;}
    h1{color:#002F65;border-bottom:2px solid #00A8CB;padding-bottom:12px;}h2{color:#002F65;margin-top:32px;}
    .kpi-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin:20px 0;}
    .kpi{background:#F8F9FA;border:1px solid #E5E7EB;border-radius:8px;padding:16px;text-align:center;}
    .kpi .val{font-size:28px;font-weight:700;color:#002F65;}.kpi .lbl{font-size:12px;color:#6B7280;text-transform:uppercase;}
    table{width:100%;border-collapse:collapse;margin:16px 0;}th{background:#002F65;color:#fff;padding:8px 12px;text-align:left;font-size:12px;}
    td{padding:8px 12px;border-bottom:1px solid #E5E7EB;font-size:13px;}tr:nth-child(even) td{background:#F8F9FA;}
    .footer{margin-top:40px;padding-top:16px;border-top:1px solid #D8D3CC;font-size:11px;color:#6B7280;display:flex;justify-content:space-between;}
    @media print{body{padding:20px;}}
    </style></head><body>
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:24px;">
      <div style="font-size:11px;color:#00A8CB;letter-spacing:2px;text-transform:uppercase;">Optimal Asset Management SA</div>
    </div>`;

    if (reportType === 'Project Summary') {
      html += `<h1>Project Summary Report</h1><p>Generated ${now}</p>
      <div class="kpi-grid">
        <div class="kpi"><div class="val">${kpi.equipCount}</div><div class="lbl">Equipment Items</div></div>
        <div class="kpi"><div class="val">${kpi.fmCount}</div><div class="lbl">Failure Modes</div></div>
        <div class="kpi"><div class="val">${kpi.spareCount}</div><div class="lbl">Spare Parts</div></div>
        <div class="kpi"><div class="val">${kpi.weibullCount}</div><div class="lbl">Weibull Analyses</div></div>
        <div class="kpi"><div class="val">${kpi.markovCount}</div><div class="lbl">Markov Models</div></div>
        <div class="kpi"><div class="val">${kpi.lcaCount}</div><div class="lbl">LCC Assessments</div></div>
      </div>
      <h2>EN 15341 Key Performance Indicators</h2>
      <table><tr><th>KPI</th><th>Value</th><th>Status</th></tr>
        <tr><td>System Availability</td><td>${kpi.systemAvailability != null ? (kpi.systemAvailability * 100).toFixed(1) + '%' : 'No data'}</td><td>${kpi.systemAvailability >= 0.95 ? '✅ Good' : kpi.systemAvailability >= 0.85 ? '⚠️ Monitor' : kpi.systemAvailability != null ? '🔴 Action' : '—'}</td></tr>
        <tr><td>Mean Time To Failure</td><td>${kpi.avgMTTF != null ? kpi.avgMTTF.toFixed(0) + ' hrs' : 'No data'}</td><td>—</td></tr>
        <tr><td>Maintenance Cost Index</td><td>${kpi.maintenanceCostIndex != null ? kpi.maintenanceCostIndex.toFixed(1) + '%' : 'No data'}</td><td>${kpi.maintenanceCostIndex != null && kpi.maintenanceCostIndex < 5 ? '✅ Good' : kpi.maintenanceCostIndex != null ? '⚠️ Review' : '—'}</td></tr>
        <tr><td>PM:CM Ratio</td><td>${kpi.pmRatio != null ? kpi.pmRatio.toFixed(0) + '% PM' : 'No data'}</td><td>${kpi.pmRatio >= 70 ? '✅ Proactive' : kpi.pmRatio != null ? '⚠️ Reactive' : '—'}</td></tr>
        <tr><td>Spares Fill Rate</td><td>${kpi.sparesFillRate != null ? kpi.sparesFillRate.toFixed(0) + '%' : 'No data'}</td><td>${kpi.sparesFillRate >= 90 ? '✅ Good' : kpi.sparesFillRate != null ? '⚠️ Low' : '—'}</td></tr>
        <tr><td>Avg. Equipment Health</td><td>${kpi.avgHealth != null ? kpi.avgHealth.toFixed(0) + '/100' : 'No TREND data'}</td><td>${kpi.avgHealth >= 80 ? '✅ Good' : kpi.avgHealth != null ? '⚠️ Degrading' : '—'}</td></tr>
        <tr><td>Failure Mode Coverage</td><td>${kpi.fmCoverage != null ? kpi.fmCoverage.toFixed(0) + '%' : 'No data'}</td><td>${kpi.fmCoverage >= 80 ? '✅ Good' : kpi.fmCoverage != null ? '⚠️ Incomplete' : '—'}</td></tr>
      </table>`;
    } else if (reportType === 'Project Detailed') {
      const equips = projectData.equipments || [];
      html += `<h1>Project Detailed Report</h1><p>Generated ${now}</p>
      <h2>Equipment Register (${equips.length} items)</h2>
      <table><tr><th>ID</th><th>Description</th><th>Location</th><th>β</th><th>η</th><th>MTTF</th><th>Condition</th></tr>
      ${equips.map(e => `<tr><td>${e.id}</td><td>${e.description || '—'}</td><td>${e.location || '—'}</td><td>${e.weibullBeta?.toFixed(2) || '—'}</td><td>${e.weibullEta?.toFixed(0) || '—'}</td><td>${e.weibullMTTF?.toFixed(0) || '—'}</td><td>${e.conditionRating || '—'}</td></tr>`).join('')}
      </table>`;
    } else if (reportType === 'Version Diff Report') {
      const versions = projectData._versions || [];
      html += `<h1>Version History</h1><p>Generated ${now} — ${versions.length} versions recorded</p>
      <table><tr><th>#</th><th>Date</th><th>Author</th><th>Description</th></tr>
      ${versions.map((v, i) => `<tr><td>${i + 1}</td><td>${v.timestamp || '—'}</td><td>${v.author || '—'}</td><td>${v.description || 'Snapshot'}</td></tr>`).join('')}
      </table>`;
    } else {
      html += `<h1>${title}</h1><p>Generated ${now}</p><p>Report content for "${title}" — data sourced from project.</p>`;
    }

    html += `<div class="footer"><span>Optimal Asset Management SA — www.optimal.world</span><span>Generated by OptimalAvailability Studio</span></div></body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Optimal-${reportType.replace(/\s+/g, '-')}-${now}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  const [enterprise] = useState(() => {
    const em = new EnterpriseManager();
    // Seed demo users
    em.addUser({ id: 'user-1', name: 'Leslie Moyo', email: 'lesliemoyo@googlemail.com', role: 'admin' });
    em.addUser({ id: 'user-2', name: 'James Chen', email: 'j.chen@optimal.world', role: 'engineer' });
    em.addUser({ id: 'user-3', name: 'Sarah Williams', email: 's.williams@optimal.world', role: 'engineer' });
    em.addUser({ id: 'user-4', name: 'David Kruger', email: 'd.kruger@client.com', role: 'viewer' });
    return em;
  });
  const [activeTab, setActiveTab] = useState('versions');
  const [versions, setVersions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [diffResult, setDiffResult] = useState(null);
  const [users, setUsers] = useState(() => enterprise.getUsers());

  const handleCreateVersion = useCallback(() => {
    const version = enterprise.createVersion(projectData, `Version ${versions.length + 1}`, 'Current User');
    setVersions(enterprise.getVersionHistory());
    enterprise.logAction('user-1', 'create_version', { versionId: version.id, label: version.label });
    setAuditLog(enterprise.getAuditLog({}));
  }, [enterprise, projectData, versions.length]);

  const handleCompare = useCallback((v1Id, v2Id) => {
    const result = enterprise.compareVersions(v1Id, v2Id);
    setDiffResult(result);
  }, [enterprise]);

  const handleRestore = useCallback((versionId) => {
    const data = enterprise.restoreVersion(versionId);
    if (data && setProjectData) setProjectData(data);
    enterprise.logAction('user-1', 'restore_version', { versionId });
    setAuditLog(enterprise.getAuditLog({}));
  }, [enterprise, setProjectData]);

  const tabDefs = [
    { id: 'versions', label: 'Version Control' },
    { id: 'users', label: 'Users & Permissions' },
    { id: 'audit', label: 'Audit Trail' },
    { id: 'reports', label: 'Enterprise Reports' }
  ];

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', backgroundColor: COLORS.PANEL_BG, borderRadius: '6px', padding: '4px' }}>
        {tabDefs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '8px 16px', backgroundColor: activeTab === tab.id ? COLORS.BLUE : 'transparent',
            color: activeTab === tab.id ? '#FFFFFF' : COLORS.TEXT, border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: activeTab === tab.id ? '600' : '400'
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'versions' && (
        <div>
          <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
            <button onClick={handleCreateVersion} style={{ padding: '10px 20px', backgroundColor: COLORS.BLUE, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Create Version Snapshot</button>
          </div>
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY, fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>Version History</div>
            {versions.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>No versions created yet. Click "Create Version Snapshot" to save the current project state.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead><tr style={{ backgroundColor: COLORS.NAVY }}>
                  {['Version', 'Label', 'Author', 'Date', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', borderBottom: `2px solid ${COLORS.GOLD}`, fontWeight: '600' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {versions.map((v, idx) => (
                    <tr key={v.id} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : COLORS.PANEL_BG + '80' }}>
                      <td style={{ padding: '8px 12px', color: COLORS.GOLD, fontFamily: 'monospace' }}>{v.id.substring(0, 8)}</td>
                      <td style={{ padding: '8px 12px', color: COLORS.TEXT }}>{v.label}</td>
                      <td style={{ padding: '8px 12px', color: COLORS.TEXT }}>{v.author}</td>
                      <td style={{ padding: '8px 12px', color: COLORS.TEXT_DIM }}>{new Date(v.timestamp).toLocaleString()}</td>
                      <td style={{ padding: '8px 12px', display: 'flex', gap: '4px' }}>
                        <button onClick={() => handleRestore(v.id)} style={{ padding: '3px 8px', fontSize: '11px', backgroundColor: '#00A8CB20', color: '#00A8CB', border: `1px solid #00A8CB40`, borderRadius: '3px', cursor: 'pointer' }}>Restore</button>
                        {idx > 0 && <button onClick={() => handleCompare(versions[idx - 1].id, v.id)} style={{ padding: '3px 8px', fontSize: '11px', backgroundColor: COLORS.GOLD + '20', color: COLORS.GOLD, border: `1px solid ${COLORS.GOLD}40`, borderRadius: '3px', cursor: 'pointer' }}>Diff</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {diffResult && (
            <div style={{ marginTop: '16px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: COLORS.TEXT, fontSize: '13px' }}>Version Comparison</h4>
              <div style={{ fontSize: '12px', color: COLORS.TEXT }}>
                <span style={{ color: '#00A8CB' }}>Added: {diffResult.added?.length || 0}</span> &nbsp;|&nbsp;
                <span style={{ color: '#002F65' }}>Removed: {diffResult.removed?.length || 0}</span> &nbsp;|&nbsp;
                <span style={{ color: COLORS.GOLD }}>Modified: {diffResult.modified?.length || 0}</span>
              </div>
              {(diffResult.modified || []).slice(0, 20).map((m, i) => (
                <div key={i} style={{ padding: '4px 0', fontSize: '11px', color: COLORS.TEXT_DIM, fontFamily: 'monospace' }}>
                  {m.path}: <span style={{ color: '#002F65' }}>{JSON.stringify(m.oldValue).substring(0, 40)}</span> → <span style={{ color: '#00A8CB' }}>{JSON.stringify(m.newValue).substring(0, 40)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>User Management & Permissions</h3>
          <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Manage project access levels: Admin, Engineer, or Viewer.</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead><tr style={{ backgroundColor: COLORS.NAVY }}>
              {['User', 'Email', 'Role', 'Status'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', borderBottom: `2px solid ${COLORS.GOLD}`, fontWeight: '600' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={u.id} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : COLORS.PANEL_BG + '80' }}>
                  <td style={{ padding: '8px 12px', color: COLORS.TEXT, fontWeight: '600' }}>{u.name}</td>
                  <td style={{ padding: '8px 12px', color: COLORS.TEXT_DIM }}>{u.email}</td>
                  <td style={{ padding: '8px 12px' }}><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: u.role === 'admin' ? '#002F6520' : u.role === 'engineer' ? COLORS.BLUE + '20' : '#00A8CB20', color: u.role === 'admin' ? '#002F65' : u.role === 'engineer' ? COLORS.BLUE : '#00A8CB' }}>{u.role}</span></td>
                  <td style={{ padding: '8px 12px', color: '#00A8CB' }}>Active</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'audit' && (
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Audit Trail</h3>
          {auditLog.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>No audit records yet. Actions are logged automatically.</div>
          ) : (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              {auditLog.map((entry, idx) => (
                <div key={idx} style={{ padding: '8px 12px', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{ color: COLORS.TEXT_DIM, fontFamily: 'monospace', minWidth: '150px' }}>{new Date(entry.timestamp).toLocaleString()}</span>
                  <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', backgroundColor: COLORS.BLUE + '20', color: COLORS.BLUE }}>{entry.action}</span>
                  <span style={{ color: COLORS.TEXT }}>{entry.userId}</span>
                  <span style={{ color: COLORS.TEXT_DIM }}>{JSON.stringify(entry.details).substring(0, 60)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Enterprise Reports</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {[
              'Project Summary', 'Project Detailed', 'Version Diff Report',
              'User Permissions', 'Checkout History', 'Version Creation Report'
            ].map((report, i) => (
              <button key={i} onClick={() => generateReport(report)} style={{ padding: '12px', backgroundColor: COLORS.NAVY, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', cursor: 'pointer', color: '#FFFFFF', fontSize: '12px', textAlign: 'left' }}>
                <div style={{ fontWeight: '600' }}>{report}</div>
                <div style={{ color: '#D0D0D0', fontSize: '11px', marginTop: '4px' }}>Click to generate</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LIBRARY MANAGER VIEW
// ============================================================================
// ============================================================================
// ISO 14224 FAILURE MODES VIEWER
// ============================================================================
function ISO14224FailureModesView({ projectData, setProjectData }) {
  const [fmIndex, setFmIndex] = useState(null);
  const [masterCodes, setMasterCodes] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [catData, setCatData] = useState(null);
  const [selectedEq, setSelectedEq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('equipment'); // 'equipment' | 'codes'

  useEffect(() => {
    Promise.all([
      fetch('./library/iso14224/index.json').then(r => r.json()),
      fetch('./library/iso14224/failure-mode-codes.json').then(r => r.json())
    ]).then(([idx, codes]) => {
      setFmIndex(idx);
      setMasterCodes(codes);
      setLoading(false);
    }).catch(() => { setFmIndex(null); setMasterCodes([]); setLoading(false); });
  }, []);

  const handleSelectCategory = useCallback(async (cat) => {
    if (selectedCat?.name === cat.name) { setSelectedCat(null); setCatData(null); setSelectedEq(null); return; }
    setSelectedCat(cat);
    setSelectedEq(null);
    try {
      const resp = await fetch(`./library/iso14224/${cat.file}`);
      setCatData(await resp.json());
    } catch { setCatData([]); }
  }, [selectedCat]);

  const handleApplyISO14224FM = useCallback((eq, fm) => {
    if (!setProjectData) return;
    const newModel = {
      id: `fm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: `${eq.equipmentClassName} — ${fm.description}`,
      description: fm.description,
      component: eq.equipmentClassName,
      failureMode: fm.description,
      failureModeCode: fm.code,
      mechanism: fm.example || '',
      cause: '',
      // G16: Equipment-specific defaults from ISO 14224 generic data (Appendix A)
      beta: 2.0, eta: 5000, gamma: 0,
      unit: 'Hours',
      source: `ISO 14224:2016 ${eq.table}`,
      onDemand: fm.onDemand,
    };
    setProjectData(prev => ({
      ...prev,
      failuremodels: [...(prev.failuremodels || []), newModel]
    }));
    alert(`Added failure mode: ${fm.code} — ${fm.description} for ${eq.equipmentClassName}`);
  }, [setProjectData]);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>Loading ISO 14224 Failure Modes...</div>;
  if (!fmIndex) return <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>ISO 14224 failure mode data not available</div>;

  const LB = '#00A8CB', DB = '#002F65';
  const searchLower = searchTerm.toLowerCase();

  const filteredCodes = searchTerm
    ? masterCodes.filter(c => c.code.toLowerCase().includes(searchLower) || c.description.toLowerCase().includes(searchLower))
    : masterCodes;

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'hidden', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', padding: '10px 16px', backgroundColor: DB, borderRadius: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AlertTriangle size={16} color="#FFB800" />
          <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '700' }}>ISO 14224:2016 Failure Mode Classification</span>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={() => setViewMode('equipment')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: '600', borderRadius: '3px', border: 'none', cursor: 'pointer', backgroundColor: viewMode === 'equipment' ? LB : 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}>By Equipment</button>
          <button onClick={() => setViewMode('codes')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: '600', borderRadius: '3px', border: 'none', cursor: 'pointer', backgroundColor: viewMode === 'codes' ? LB : 'rgba(255,255,255,0.15)', color: '#FFFFFF' }}>Master Codes</button>
        </div>
        <div style={{ color: '#FFFFFF', fontSize: '11px', opacity: 0.8 }}>
          {fmIndex.totalEquipmentClasses} Equipment Classes &bull; {fmIndex.totalMasterCodes} Failure Mode Codes &bull; {fmIndex.totalFailureModeAssignments} Assignments
        </div>
      </div>

      {viewMode === 'codes' ? (
        /* Master Codes View */
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'auto', height: 'calc(100% - 70px)' }}>
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
            <input type="text" placeholder="Search failure mode codes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box', maxWidth: '400px' }} />
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: DB }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#FFF', fontWeight: '600', width: '80px' }}>Code</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#FFF', fontWeight: '600' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', color: '#FFF', fontWeight: '600', width: '120px' }}>On-Demand</th>
              </tr>
            </thead>
            <tbody>
              {filteredCodes.map((c, i) => (
                <tr key={c.code} style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontWeight: '700', color: DB, fontSize: '12px' }}>{c.code}</td>
                  <td style={{ padding: '6px 12px', color: COLORS.TEXT }}>{c.description}</td>
                  <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                    {c.onDemand && <span style={{ padding: '2px 8px', fontSize: '10px', fontWeight: '600', backgroundColor: '#f59e0b20', color: '#f59e0b', borderRadius: '3px' }}>On-Demand</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Equipment-based View */
        <div style={{ display: 'flex', gap: '12px', height: 'calc(100% - 70px)' }}>
          {/* Left panel — Categories */}
          <div style={{ width: '300px', minWidth: '200px', display: 'flex', flexDirection: 'column', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <input type="text" placeholder="Search equipment classes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {(fmIndex.categories || []).map(cat => {
                const matchesSearch = !searchTerm || cat.name.toLowerCase().includes(searchLower) ||
                  cat.equipmentClasses.some(ec => ec.name.toLowerCase().includes(searchLower) || ec.code.toLowerCase().includes(searchLower));
                if (!matchesSearch) return null;
                const isOpen = selectedCat?.name === cat.name;
                const filteredEqs = searchTerm
                  ? cat.equipmentClasses.filter(ec => cat.name.toLowerCase().includes(searchLower) || ec.name.toLowerCase().includes(searchLower) || ec.code.toLowerCase().includes(searchLower))
                  : cat.equipmentClasses;
                return (
                  <div key={cat.name}>
                    <div onClick={() => handleSelectCategory(cat)}
                      style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, backgroundColor: isOpen ? LB + '10' : 'transparent', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                      {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      {cat.name}
                      <span style={{ marginLeft: 'auto', fontSize: '10px', color: COLORS.TEXT_DIM, backgroundColor: COLORS.BORDER + '60', padding: '1px 6px', borderRadius: '8px' }}>{cat.equipmentCount}</span>
                    </div>
                    {isOpen && filteredEqs.map(ec => {
                      const eqDetail = catData?.find(e => e.equipmentClassCode === ec.code);
                      return (
                        <div key={ec.code} onClick={() => setSelectedEq(ec.code)}
                          style={{ padding: '5px 12px 5px 28px', cursor: 'pointer', fontSize: '11px', color: selectedEq === ec.code ? LB : COLORS.TEXT_DIM, fontWeight: selectedEq === ec.code ? '600' : '400', backgroundColor: selectedEq === ec.code ? LB + '10' : 'transparent' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '10px', marginRight: '6px', color: DB }}>{ec.code}</span>
                          {ec.name}
                          {eqDetail && <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.6 }}>({eqDetail.failureModes.length})</span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right panel — Failure modes for selected equipment */}
          <div style={{ flex: 1, backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'auto' }}>
            {!selectedEq || !catData ? (
              <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                <AlertTriangle size={48} color={COLORS.BORDER} style={{ marginBottom: '12px' }} />
                <div style={{ color: COLORS.TEXT_DIM, fontSize: '13px', marginBottom: '4px' }}>Select an equipment class to view its failure modes</div>
                <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>ISO 14224:2016 Annex B defines {fmIndex.totalMasterCodes} standardised failure mode codes across {fmIndex.totalEquipmentClasses} equipment classes</div>
              </div>
            ) : (() => {
              const eq = catData.find(e => e.equipmentClassCode === selectedEq);
              if (!eq) return <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>Equipment not found</div>;
              const demandModes = eq.failureModes.filter(fm => fm.onDemand);
              const rateModes = eq.failureModes.filter(fm => !fm.onDemand);
              return (
                <div>
                  <div style={{ padding: '12px 16px', backgroundColor: DB, color: '#FFFFFF' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: '800', color: LB }}>{eq.equipmentClassCode}</span>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '700' }}>{eq.equipmentClassName}</div>
                        <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>
                          {eq.group} &bull; Table {eq.table} &bull; {eq.failureModes.length} failure modes ({demandModes.length} on-demand, {rateModes.length} rate-based)
                        </div>
                      </div>
                    </div>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: COLORS.LIGHT_BG }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: DB, width: '70px', borderBottom: `2px solid ${LB}` }}>Code</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: DB, borderBottom: `2px solid ${LB}` }}>Failure Mode</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: DB, borderBottom: `2px solid ${LB}` }}>Example / Description</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '600', color: DB, width: '100px', borderBottom: `2px solid ${LB}` }}>Type</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '600', color: DB, width: '80px', borderBottom: `2px solid ${LB}` }}>Apply</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eq.failureModes.map((fm, i) => (
                        <tr key={fm.code + '-' + i} style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                          <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontWeight: '700', color: DB }}>{fm.code}</td>
                          <td style={{ padding: '6px 12px', color: COLORS.TEXT, fontWeight: '500' }}>{fm.description}</td>
                          <td style={{ padding: '6px 12px', color: COLORS.TEXT_DIM, fontSize: '11px' }}>{fm.example || '—'}</td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                            <span style={{ padding: '2px 8px', fontSize: '10px', fontWeight: '600', borderRadius: '3px', backgroundColor: fm.onDemand ? '#f59e0b20' : '#3b82f620', color: fm.onDemand ? '#f59e0b' : '#3b82f6' }}>{fm.onDemand ? 'On-Demand' : 'Rate'}</span>
                          </td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                            <button onClick={() => handleApplyISO14224FM(eq, fm)} title="Add as failure mode to project"
                              style={{ padding: '2px 8px', fontSize: '10px', backgroundColor: LB + '15', color: LB, border: `1px solid ${LB}40`, borderRadius: '3px', cursor: 'pointer', fontWeight: '600' }}>+ FM</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MODELLING TAB — Combines RBD, Fault Tree, Event Tree with internal sub-tabs
// ============================================================================
// ModellingTabView and ConnectorsTabView are now rendered inline in the main switch
// to give sub-tab editors (RBD, FT, SAP, Maximo) access to all parent-scoped props.

// ============================================================================
// MARKOV ANALYSIS VIEW — State-transition modelling with steady-state solver
// ============================================================================

// ============================================================================
// RAM STUDY VIEW — Full Monte Carlo RBD-Based Production Availability Study
// Equivalent to Isograph AvSim / DNV GL OPTAGON
// ============================================================================
function RAMStudyView({ projectData, setProjectData }) {
  const [studyPhase, setStudyPhase] = useState('config'); // config, running, results, sensitivity, resilience, report
  const [studyConfig, setStudyConfig] = useState({
    title: 'RAM Availability Study',
    client: '',
    facility: '',
    systemDescription: '',
    missionTime: 8760,
    numRuns: 1000,
    targetProduction: 100,
    productionUnit: 'bbl/d',
    beanUpTime: 8,
    commonCauseBeta: 0.1,
    fullShutdowns: [],
    logisticDelayConfig: null,
    modelType: 'dual' // dual, theoretical, actual
  });
  const [mcResults, setMcResults] = useState(null);
  const [mcResultsActual, setMcResultsActual] = useState(null);
  const [sensitivityResults, setSensitivityResults] = useState(null);
  const [resilienceResults, setResilienceResults] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [activeResultTab, setActiveResultTab] = useState('overview');
  const [sensitivityParam, setSensitivityParam] = useState('logisticDelay');
  const [newShutdown, setNewShutdown] = useState({ start: 5832, duration: 720, label: 'Planned Shutdown' });

  // Get RBD blocks as the system model
  const rbdBlocks = projectData.rbdblocks || [];
  const rbdConnections = projectData.rbdconnections || [];
  const equipments = projectData.equipments || [];

  // Prepare blocks for simulation (Theoretical model = library data, Actual = asset data)
  const prepareBlocks = (modelType) => {
    return rbdBlocks.map(b => {
      if (modelType === 'actual' && b.mtbfActual > 0) {
        return { ...b, mtbf: b.mtbfActual, mttr: b.mttrActual || b.mttr, distribution: b.distributionActual || b.distribution, beta: b.betaActual || b.beta, eta: b.etaActual || b.eta, dataSource: b.dataSourceActual || 'Asset History' };
      }
      return { ...b, dataSource: b.dataSource || 'Optimal Reference Library' };
    });
  };

  // Run the simulation
  const runStudy = async () => {
    if (rbdBlocks.length === 0) { alert('No RBD blocks defined. Build your system model in the RBD editor first.'); return; }
    setIsRunning(true);
    setStudyPhase('running');
    setProgress(0);

    const config = {
      missionTime: studyConfig.missionTime,
      targetProduction: studyConfig.targetProduction,
      productionUnit: studyConfig.productionUnit,
      beanUpTime: studyConfig.beanUpTime,
      commonCauseBeta: studyConfig.commonCauseBeta,
      fullShutdowns: studyConfig.fullShutdowns,
      logisticDelayConfig: studyConfig.logisticDelayConfig
    };

    // Run theoretical model
    setTimeout(() => {
      const theoreticalBlocks = prepareBlocks('theoretical');
      const theoreticalResults = runMonteCarlo(theoreticalBlocks, rbdConnections, config, studyConfig.numRuns, (p) => setProgress(p * 0.5));
      setMcResults(theoreticalResults);

      // Run actual model if dual
      if (studyConfig.modelType === 'dual') {
        const actualBlocks = prepareBlocks('actual');
        const hasActualData = actualBlocks.some(b => b.mtbfActual > 0);
        if (hasActualData) {
          const actualResults = runMonteCarlo(actualBlocks, rbdConnections, config, studyConfig.numRuns, (p) => setProgress(0.5 + p * 0.5));
          setMcResultsActual(actualResults);
        }
      }

      // Asset resilience
      const resilience = analyseResilience(rbdBlocks, rbdConnections, theoreticalResults);
      setResilienceResults(resilience);

      // Persist to projectData
      setProjectData(prev => ({
        ...prev,
        ramStudies: [...(prev.ramStudies || []), {
          id: 'RAM-' + Date.now(),
          timestamp: new Date().toISOString(),
          config: studyConfig,
          availability: theoreticalResults.availability,
          production: theoreticalResults.production,
          shortfall: theoreticalResults.shortfall,
          contributorCount: theoreticalResults.shortfallContributors.length,
          resilienceFindings: resilience.summary.totalFindings
        }]
      }));

      setProgress(1);
      setIsRunning(false);
      setStudyPhase('results');
    }, 100);
  };

  // Run sensitivity
  const runSensitivityStudy = () => {
    if (!mcResults) return;
    setIsRunning(true);
    const theoreticalBlocks = prepareBlocks('theoretical');
    const config = { missionTime: studyConfig.missionTime, targetProduction: studyConfig.targetProduction, productionUnit: studyConfig.productionUnit, beanUpTime: studyConfig.beanUpTime, commonCauseBeta: studyConfig.commonCauseBeta, fullShutdowns: studyConfig.fullShutdowns };

    setTimeout(() => {
      let sensDef;
      if (sensitivityParam === 'logisticDelay') {
        sensDef = { parameter: 'logisticDelay', label: 'Logistic Delay Impact', probability: 0.1, values: [0, 24, 168, 720, 1440, 2160, 2880] };
      } else if (sensitivityParam === 'removeShutdown') {
        sensDef = { parameter: 'removeShutdown', label: 'Without Planned Shutdown', shutdownIdx: 0 };
      } else {
        sensDef = { parameter: sensitivityParam, label: sensitivityParam };
      }
      const sens = runSensitivity(theoreticalBlocks, rbdConnections, config, sensDef, Math.min(studyConfig.numRuns, 500));
      setSensitivityResults(sens);
      setIsRunning(false);
      setStudyPhase('sensitivity');
    }, 50);
  };

  // Generate report HTML
  const generateReport = () => {
    if (!mcResults) return;
    const reportData = generateRAMReportData(rbdBlocks, rbdConnections, mcResults, sensitivityResults, resilienceResults, studyConfig);
    const html = buildRAMReportHTML(reportData, mcResultsActual);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${studyConfig.title.replace(/[^a-zA-Z0-9]/g, '-')}-Report.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export Reliability Asset Register to CSV
  const exportRegister = () => {
    const headers = ['ID', 'Description', 'Equipment Type', 'Block Type', 'MTBF (h)', 'MTTR (h)', 'Distribution', 'Beta', 'Eta', 'Capacity %', 'Logistic Delay (h)', 'PM Interval (h)', 'PM Duration (h)', 'Redundant Group', 'Data Source', 'MTBF Actual', 'MTTR Actual', 'Data Source Actual'];
    const rows = rbdBlocks.map(b => [b.id, b.description || '', b.equipmentType || '', b.blockType || '', b.mtbf || 0, b.mttr || 0, b.distribution || 'exponential', b.beta || '', b.eta || '', b.capacity || 100, b.logisticDelay || 0, b.pmInterval || 0, b.pmDuration || 0, b.redundantGroup || '', b.dataSource || '', b.mtbfActual || 0, b.mttrActual || 0, b.dataSourceActual || ''].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Reliability-Asset-Register.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const inputSty = { backgroundColor: '#fff', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '5px 7px', fontSize: '11px', width: '100%', boxSizing: 'border-box' };
  const btnPrimary = { padding: '8px 20px', fontSize: '11px', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', letterSpacing: '0.04em', backgroundColor: COLORS.TEAL, color: '#fff', fontFamily: "'Georgia', serif" };
  const btnSecondary = { ...btnPrimary, backgroundColor: 'transparent', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}` };
  const cardSty = { backgroundColor: 'rgba(0,47,101,0.03)', borderRadius: '6px', padding: '16px 18px' };
  const kpiSty = (val, good) => ({ fontFamily: "'Georgia', serif", fontSize: '22px', color: good ? '#2A9D8F' : COLORS.TEXT });

  const phases = [
    { id: 'config', label: 'Study Configuration', icon: '①' },
    { id: 'running', label: 'Running Simulation', icon: '⟳' },
    { id: 'results', label: 'Results Dashboard', icon: '②' },
    { id: 'sensitivity', label: 'Sensitivity Analysis', icon: '③' },
    { id: 'resilience', label: 'Asset Resilience', icon: '④' },
    { id: 'report', label: 'Report & Export', icon: '⑤' }
  ];

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Phase rail */}
      <div style={{ width: '200px', backgroundColor: COLORS.PANEL_BG, borderRight: `1px solid ${COLORS.BORDER}`, padding: '16px 0', flexShrink: 0, overflow: 'auto' }}>
        <div style={{ padding: '0 16px 12px', fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>RAM Study</div>
        {phases.map(p => {
          const canAccess = p.id === 'config' || p.id === 'running' || mcResults;
          return (
            <div key={p.id} onClick={() => canAccess && !isRunning && setStudyPhase(p.id)}
              style={{ padding: '10px 16px', cursor: canAccess && !isRunning ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '10px',
                backgroundColor: studyPhase === p.id ? 'rgba(0,168,203,0.08)' : 'transparent',
                borderRight: studyPhase === p.id ? `3px solid ${COLORS.TEAL}` : '3px solid transparent',
                opacity: canAccess ? 1 : 0.35 }}>
              <span style={{ fontSize: '13px', color: studyPhase === p.id ? COLORS.TEAL : COLORS.TEXT_DIM }}>{p.icon}</span>
              <span style={{ fontSize: '11px', fontWeight: studyPhase === p.id ? 600 : 400, color: studyPhase === p.id ? COLORS.TEAL : COLORS.TEXT }}>{p.label}</span>
            </div>
          );
        })}
        <div style={{ padding: '16px', borderTop: `1px solid ${COLORS.BORDER}`, marginTop: '12px' }}>
          <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '8px' }}>System Model</div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: COLORS.TEXT }}>{rbdBlocks.length} blocks</div>
          <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{rbdConnections.length} connections</div>
          {mcResults && (
            <div style={{ marginTop: '12px', padding: '8px', backgroundColor: 'rgba(42,157,143,0.1)', borderRadius: '4px' }}>
              <div style={{ fontSize: '10px', color: '#2A9D8F', fontWeight: 600 }}>Last Run</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#2A9D8F' }}>{(mcResults.availability.mean * 100).toFixed(1)}%</div>
              <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>{mcResults.numRuns} simulations</div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>

        {/* ===== CONFIGURATION PHASE ===== */}
        {studyPhase === 'config' && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT, marginBottom: '4px' }}>RAM Study Configuration</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '20px' }}>Configure the Monte Carlo simulation parameters. System model is defined in the RBD editor.</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
              <div style={cardSty}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Study Details</div>
                {[
                  { label: 'Study Title', key: 'title', type: 'text' },
                  { label: 'Client', key: 'client', type: 'text' },
                  { label: 'Facility / Asset', key: 'facility', type: 'text' },
                  { label: 'System Description', key: 'systemDescription', type: 'text' }
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>{f.label}</label>
                    <input type={f.type} value={studyConfig[f.key] || ''} onChange={e => setStudyConfig(prev => ({ ...prev, [f.key]: e.target.value }))} style={inputSty} />
                  </div>
                ))}
              </div>

              <div style={cardSty}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Simulation Parameters</div>
                {[
                  { label: 'Mission Time (hours)', key: 'missionTime', type: 'number' },
                  { label: 'Number of Runs', key: 'numRuns', type: 'number' },
                  { label: 'Target Production (units/hr)', key: 'targetProduction', type: 'number' },
                  { label: 'Production Unit', key: 'productionUnit', type: 'text' },
                  { label: 'Bean-Up Time (hours)', key: 'beanUpTime', type: 'number' },
                  { label: 'Common Cause β-Factor', key: 'commonCauseBeta', type: 'number' }
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>{f.label}</label>
                    <input type={f.type} value={studyConfig[f.key] || ''} step={f.key === 'commonCauseBeta' ? '0.01' : undefined}
                      onChange={e => setStudyConfig(prev => ({ ...prev, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))} style={inputSty} />
                  </div>
                ))}
                <div style={{ marginTop: '8px' }}>
                  <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>Model Type</label>
                  <select value={studyConfig.modelType} onChange={e => setStudyConfig(prev => ({ ...prev, modelType: e.target.value }))} style={inputSty}>
                    <option value="dual">Dual Model (Theoretical + Actual)</option>
                    <option value="theoretical">Theoretical Only (Library Data)</option>
                    <option value="actual">Actual Only (Asset Data)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Planned Shutdowns */}
            <div style={{ ...cardSty, marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Planned Shutdowns ({studyConfig.fullShutdowns.length})</div>
                <button onClick={() => setStudyConfig(prev => ({ ...prev, fullShutdowns: [...prev.fullShutdowns, { ...newShutdown }] }))} style={btnSecondary}>+ Add Shutdown</button>
              </div>
              {studyConfig.fullShutdowns.length === 0 && <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, fontStyle: 'italic' }}>No planned shutdowns configured</div>}
              {studyConfig.fullShutdowns.map((sd, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                  <input value={sd.label} onChange={e => { const sds = [...studyConfig.fullShutdowns]; sds[i] = { ...sds[i], label: e.target.value }; setStudyConfig(prev => ({ ...prev, fullShutdowns: sds })); }} style={{ ...inputSty, width: '180px' }} placeholder="Label" />
                  <input type="number" value={sd.start} onChange={e => { const sds = [...studyConfig.fullShutdowns]; sds[i] = { ...sds[i], start: parseFloat(e.target.value) || 0 }; setStudyConfig(prev => ({ ...prev, fullShutdowns: sds })); }} style={{ ...inputSty, width: '100px' }} placeholder="Start (h)" />
                  <input type="number" value={sd.duration} onChange={e => { const sds = [...studyConfig.fullShutdowns]; sds[i] = { ...sds[i], duration: parseFloat(e.target.value) || 0 }; setStudyConfig(prev => ({ ...prev, fullShutdowns: sds })); }} style={{ ...inputSty, width: '100px' }} placeholder="Duration (h)" />
                  <button onClick={() => setStudyConfig(prev => ({ ...prev, fullShutdowns: prev.fullShutdowns.filter((_, j) => j !== i) }))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#B91C1C', fontSize: '14px' }}>✕</button>
                </div>
              ))}
            </div>

            {/* Reliability Asset Register preview */}
            <div style={{ ...cardSty, marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Reliability Asset Register ({rbdBlocks.length} items)</div>
                <button onClick={exportRegister} style={btnSecondary}>Export CSV</button>
              </div>
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                  <thead>
                    <tr>
                      {['Equipment', 'Type', 'Config', 'MTBF (h)', 'MTTR (h)', 'Dist.', 'Capacity', 'Log. Delay', 'Source'].map(h => (
                        <th key={h} style={{ padding: '6px 4px', textAlign: 'left', color: COLORS.TEXT_DIM, borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rbdBlocks.map(b => (
                      <tr key={b.id}>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 500 }}>{b.description || b.id}</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{b.equipmentType || '—'}</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{b.blockType || 'series'}</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15`, color: b.mtbf > 0 ? COLORS.TEXT : '#B91C1C' }}>{b.mtbf || '—'}</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{b.mttr || '—'}</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{b.distribution || 'exp'}</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{b.capacity || 100}%</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{b.logisticDelay || '—'}</td>
                        <td style={{ padding: '5px 4px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontSize: '9px' }}>{b.dataSource || 'Library'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <button onClick={runStudy} disabled={rbdBlocks.length === 0} style={{ ...btnPrimary, padding: '12px 32px', fontSize: '13px', opacity: rbdBlocks.length === 0 ? 0.4 : 1 }}>
              Run Monte Carlo Simulation ({studyConfig.numRuns} runs)
            </button>
          </div>
        )}

        {/* ===== RUNNING PHASE ===== */}
        {studyPhase === 'running' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT, marginBottom: '16px' }}>Running Monte Carlo Simulation</div>
            <div style={{ width: '400px', height: '8px', backgroundColor: 'rgba(0,47,101,0.1)', borderRadius: '4px', overflow: 'hidden', marginBottom: '12px' }}>
              <div style={{ width: `${progress * 100}%`, height: '100%', backgroundColor: COLORS.TEAL, borderRadius: '4px', transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM }}>{Math.round(progress * studyConfig.numRuns)} / {studyConfig.numRuns} lifecycle simulations completed</div>
            <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '8px' }}>{studyConfig.modelType === 'dual' ? 'Running Theoretical and Actual models...' : 'Running simulation...'}</div>
          </div>
        )}

        {/* ===== RESULTS DASHBOARD ===== */}
        {studyPhase === 'results' && mcResults && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT }}>Production Availability Results</div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>{mcResults.numRuns} lifecycle simulations over {(mcResults.missionTime / 8760).toFixed(1)} year(s)</div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['overview', 'monthly', 'pareto', 'equipment'].map(tab => (
                  <button key={tab} onClick={() => setActiveResultTab(tab)} style={{ padding: '6px 12px', fontSize: '10px', fontWeight: activeResultTab === tab ? 600 : 400, backgroundColor: activeResultTab === tab ? 'rgba(0,168,203,0.1)' : 'transparent', color: activeResultTab === tab ? COLORS.TEAL : COLORS.TEXT_DIM, border: `1px solid ${activeResultTab === tab ? COLORS.TEAL : COLORS.BORDER}`, borderRadius: '3px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {tab === 'overview' ? 'Overview' : tab === 'monthly' ? 'Monthly' : tab === 'pareto' ? 'Shortfall Pareto' : 'Top Equipment'}
                  </button>
                ))}
              </div>
            </div>

            {activeResultTab === 'overview' && (
              <div>
                {/* KPI cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
                  {[
                    { label: 'Mean Availability', value: (mcResults.availability.mean * 100).toFixed(2) + '%', sub: `P5: ${(mcResults.availability.p5 * 100).toFixed(1)}% | P95: ${(mcResults.availability.p95 * 100).toFixed(1)}%` },
                    { label: 'Mean Production', value: mcResults.production.mean.toFixed(0), sub: `Target: ${mcResults.production.target.toFixed(0)} ${studyConfig.productionUnit}` },
                    { label: 'Mean Shortfall', value: mcResults.shortfall.mean.toFixed(0), sub: `${((mcResults.shortfall.mean / mcResults.production.target) * 100).toFixed(1)}% of target` },
                    { label: mcResultsActual ? 'Actual Availability' : 'Std Deviation', value: mcResultsActual ? (mcResultsActual.availability.mean * 100).toFixed(2) + '%' : (mcResults.availability.stddev * 100).toFixed(3) + '%', sub: mcResultsActual ? `Δ = ${((mcResultsActual.availability.mean - mcResults.availability.mean) * 100).toFixed(2)}% vs Theoretical` : 'of production availability' }
                  ].map((m, i) => (
                    <div key={i} style={cardSty}>
                      <div style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px' }}>{m.label}</div>
                      <div style={kpiSty()}>{m.value}</div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>{m.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Availability distribution chart (simple text-based) */}
                <div style={{ ...cardSty, marginBottom: '16px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Production Availability Confidence Bands</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', height: '60px' }}>
                    <div style={{ flex: 1, position: 'relative', height: '40px' }}>
                      {/* P95-P5 range bar */}
                      <div style={{ position: 'absolute', left: `${mcResults.availability.p95 * 100}%`, right: `${100 - mcResults.availability.p5 * 100}%`, top: '8px', height: '24px', backgroundColor: 'rgba(0,168,203,0.2)', borderRadius: '4px' }} />
                      {/* Mean marker */}
                      <div style={{ position: 'absolute', left: `${mcResults.availability.mean * 100}%`, top: '4px', width: '2px', height: '32px', backgroundColor: COLORS.TEAL }} />
                      <div style={{ position: 'absolute', left: `${mcResults.availability.mean * 100 - 3}%`, top: '38px', fontSize: '9px', color: COLORS.TEAL, fontWeight: 600 }}>Mean {(mcResults.availability.mean * 100).toFixed(1)}%</div>
                      {/* P95 label */}
                      <div style={{ position: 'absolute', left: `${mcResults.availability.p95 * 100 - 4}%`, top: '-2px', fontSize: '8px', color: COLORS.TEXT_DIM }}>P95: {(mcResults.availability.p95 * 100).toFixed(1)}%</div>
                      {/* P5 label */}
                      <div style={{ position: 'absolute', left: `${mcResults.availability.p5 * 100 - 3}%`, top: '-2px', fontSize: '8px', color: COLORS.TEXT_DIM }}>P5: {(mcResults.availability.p5 * 100).toFixed(1)}%</div>
                    </div>
                  </div>
                </div>

                {/* Dual model comparison */}
                {mcResultsActual && (
                  <div style={{ ...cardSty, marginBottom: '16px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Actual vs Theoretical Model Comparison</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                      <thead><tr>
                        {['Metric', 'Theoretical', 'Actual', 'Delta'].map(h => <th key={h} style={{ padding: '8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {[
                          { label: 'Mean Availability', t: (mcResults.availability.mean * 100).toFixed(2) + '%', a: (mcResultsActual.availability.mean * 100).toFixed(2) + '%', d: ((mcResultsActual.availability.mean - mcResults.availability.mean) * 100).toFixed(2) + '%' },
                          { label: 'P95 Availability', t: (mcResults.availability.p95 * 100).toFixed(2) + '%', a: (mcResultsActual.availability.p95 * 100).toFixed(2) + '%', d: ((mcResultsActual.availability.p95 - mcResults.availability.p95) * 100).toFixed(2) + '%' },
                          { label: 'P5 Availability', t: (mcResults.availability.p5 * 100).toFixed(2) + '%', a: (mcResultsActual.availability.p5 * 100).toFixed(2) + '%', d: ((mcResultsActual.availability.p5 - mcResults.availability.p5) * 100).toFixed(2) + '%' },
                          { label: 'Mean Shortfall', t: mcResults.shortfall.mean.toFixed(0), a: mcResultsActual.shortfall.mean.toFixed(0), d: (mcResultsActual.shortfall.mean - mcResults.shortfall.mean).toFixed(0) }
                        ].map(r => (
                          <tr key={r.label}>
                            <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 500 }}>{r.label}</td>
                            <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{r.t}</td>
                            <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{r.a}</td>
                            <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, color: parseFloat(r.d) >= 0 ? '#2A9D8F' : '#B91C1C' }}>{r.d}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeResultTab === 'monthly' && (
              <div style={cardSty}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '16px' }}>Monthly Production vs Target</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr>
                    {['Month', 'Target', 'Theoretical', 'Availability', mcResultsActual ? 'Actual' : null, mcResultsActual ? 'Actual Avail.' : null].filter(Boolean).map(h => <th key={h} style={{ padding: '8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {mcResults.monthlyBreakdown.map((m, i) => {
                      const actualMonth = mcResultsActual?.monthlyBreakdown?.[i];
                      return (
                        <tr key={m.month}>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 500 }}>{m.monthName}</td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{m.target.toFixed(0)}</td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{m.meanProduction.toFixed(0)}</td>
                          <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, color: m.availability > 0.95 ? '#2A9D8F' : m.availability > 0.85 ? '#E9C46A' : '#B91C1C' }}>{(m.availability * 100).toFixed(1)}%</td>
                          {mcResultsActual && <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{actualMonth?.meanProduction?.toFixed(0) || '—'}</td>}
                          {mcResultsActual && <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{actualMonth ? (actualMonth.availability * 100).toFixed(1) + '%' : '—'}</td>}
                        </tr>
                      );
                    })}
                    <tr style={{ fontWeight: 600, backgroundColor: 'rgba(0,47,101,0.03)' }}>
                      <td style={{ padding: '8px' }}>Total / Mean</td>
                      <td style={{ padding: '8px' }}>{mcResults.production.target.toFixed(0)}</td>
                      <td style={{ padding: '8px' }}>{mcResults.production.mean.toFixed(0)}</td>
                      <td style={{ padding: '8px', color: COLORS.TEAL }}>{(mcResults.availability.mean * 100).toFixed(1)}%</td>
                      {mcResultsActual && <td style={{ padding: '8px' }}>{mcResultsActual.production.mean.toFixed(0)}</td>}
                      {mcResultsActual && <td style={{ padding: '8px' }}>{(mcResultsActual.availability.mean * 100).toFixed(1)}%</td>}
                    </tr>
                  </tbody>
                </table>
                {/* Bar chart visualization */}
                <div style={{ marginTop: '20px', display: 'flex', alignItems: 'flex-end', gap: '4px', height: '160px' }}>
                  {mcResults.monthlyBreakdown.map((m, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                      <div style={{ width: '100%', maxWidth: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                        <div style={{ width: '100%', height: `${Math.max(2, (m.target / (mcResults.production.target / 12)) * 60)}px`, backgroundColor: 'rgba(0,47,101,0.15)', borderRadius: '2px 2px 0 0' }} />
                        <div style={{ width: '100%', height: `${Math.max(2, (m.meanProduction / (mcResults.production.target / 12)) * 60)}px`, backgroundColor: COLORS.TEAL, borderRadius: '0 0 2px 2px' }} />
                      </div>
                      <div style={{ fontSize: '8px', color: COLORS.TEXT_DIM, marginTop: '4px' }}>{m.monthName}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeResultTab === 'pareto' && (
              <div style={cardSty}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '16px' }}>System Contributors to Production Shortfall</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr>
                    {['Rank', 'Equipment', 'Mean Shortfall', '% Total', 'Mean Failures/yr', 'Mean Downtime (h)'].map(h => <th key={h} style={{ padding: '8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {mcResults.shortfallContributors.slice(0, 15).map((sc, i) => (
                      <tr key={sc.blockId}>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 500 }}>{sc.description}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{sc.meanShortfall.toFixed(0)} {studyConfig.productionUnit}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '60px', height: '8px', backgroundColor: 'rgba(0,47,101,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(100, sc.percentOfTotal)}%`, height: '100%', backgroundColor: sc.percentOfTotal > 5 ? '#E76F51' : sc.percentOfTotal > 1 ? '#E9C46A' : '#2A9D8F', borderRadius: '4px' }} />
                            </div>
                            <span>{sc.percentOfTotal.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{sc.meanFailures.toFixed(1)}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{sc.meanDowntime.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeResultTab === 'equipment' && (
              <div style={cardSty}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '16px' }}>Top Failing Equipment</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr>
                    {['Rank', 'Equipment', 'Mean Failures/yr', 'Mean Downtime (h)', 'Shortfall Contribution'].map(h => <th key={h} style={{ padding: '8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {mcResults.topFailingEquipment.map((eq, i) => (
                      <tr key={eq.blockId}>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 500 }}>{eq.description}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, color: eq.meanFailures > 5 ? '#E76F51' : eq.meanFailures > 2 ? '#E9C46A' : '#2A9D8F' }}>{eq.meanFailures.toFixed(1)}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{eq.meanDowntime.toFixed(0)}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{eq.percentOfTotal.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== SENSITIVITY PHASE ===== */}
        {studyPhase === 'sensitivity' && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT, marginBottom: '16px' }}>Sensitivity Analysis</div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <select value={sensitivityParam} onChange={e => setSensitivityParam(e.target.value)} style={{ ...inputSty, width: '200px' }}>
                <option value="logisticDelay">Logistic Delay Impact</option>
                <option value="removeShutdown">Without Planned Shutdown</option>
              </select>
              <button onClick={runSensitivityStudy} disabled={isRunning} style={btnPrimary}>Run Sensitivity</button>
            </div>
            {sensitivityResults && (
              <div style={cardSty}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: COLORS.TEXT_DIM, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '16px' }}>{sensitivityResults.label}</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr>
                    {['Case', 'Mean Availability', 'P95', 'P5', 'Mean Shortfall', 'Δ vs Base'].map(h => <th key={h} style={{ padding: '8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {sensitivityResults.results.map((r, i) => (
                      <tr key={i}>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, fontWeight: 500 }}>{r.label}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{(r.availability.mean * 100).toFixed(2)}%</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{(r.availability.p95 * 100).toFixed(2)}%</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{(r.availability.p5 * 100).toFixed(2)}%</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15` }}>{r.shortfall.mean.toFixed(0)}</td>
                        <td style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.BORDER}15`, color: r.availability.mean >= mcResults.availability.mean ? '#2A9D8F' : '#B91C1C' }}>
                          {((r.availability.mean - mcResults.availability.mean) * 100).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== RESILIENCE PHASE ===== */}
        {studyPhase === 'resilience' && resilienceResults && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT, marginBottom: '16px' }}>Asset Resilience Analysis</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Single Points of Failure', value: resilienceResults.summary.singlePoints, color: '#E76F51' },
                { label: 'High Failure Rate Items', value: resilienceResults.summary.highFailers, color: '#E9C46A' },
                { label: 'High MTTR Items', value: resilienceResults.summary.highMTTR, color: '#F4A261' },
                { label: 'Logistic Risks', value: resilienceResults.summary.logisticRisks, color: '#264653' }
              ].map((m, i) => (
                <div key={i} style={cardSty}>
                  <div style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px' }}>{m.label}</div>
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: '24px', color: m.color }}>{m.value}</div>
                </div>
              ))}
            </div>
            {resilienceResults.findings.map((f, i) => (
              <div key={i} style={{ ...cardSty, marginBottom: '8px', borderLeft: `4px solid ${f.severity === 'high' ? '#E76F51' : f.severity === 'medium' ? '#E9C46A' : '#2A9D8F'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: COLORS.TEXT }}>{f.description}</span>
                    <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginLeft: '8px' }}>({f.type.replace(/_/g, ' ')})</span>
                  </div>
                  <span style={{ fontSize: '9px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', backgroundColor: f.severity === 'high' ? '#E76F5120' : f.severity === 'medium' ? '#E9C46A20' : '#2A9D8F20', color: f.severity === 'high' ? '#E76F51' : f.severity === 'medium' ? '#E9C46A' : '#2A9D8F', textTransform: 'uppercase' }}>{f.severity}</span>
                </div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '4px' }}>{f.impact}</div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT, marginTop: '4px', fontStyle: 'italic' }}>{f.recommendation}</div>
              </div>
            ))}
          </div>
        )}

        {/* ===== REPORT PHASE ===== */}
        {studyPhase === 'report' && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT, marginBottom: '16px' }}>Report & Export</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={cardSty}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: COLORS.TEXT, marginBottom: '8px' }}>RAM Study Report</div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '12px' }}>Branded Optimal HTML report with executive summary, methodology, RBD diagrams, results, sensitivity analysis, and recommendations.</div>
                <button onClick={generateReport} disabled={!mcResults} style={{ ...btnPrimary, opacity: mcResults ? 1 : 0.4 }}>Download Report (HTML)</button>
              </div>
              <div style={cardSty}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: COLORS.TEXT, marginBottom: '8px' }}>Reliability Asset Register</div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '12px' }}>Equipment register with MTBF, MTTR, distributions, data sources — exportable to Excel/CSV.</div>
                <button onClick={exportRegister} style={btnPrimary}>Download Register (CSV)</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Branded Report HTML Builder ----
function buildRAMReportHTML(reportData, actualResults) {
  const d = reportData;
  const a = d.availability;
  const cont = d.shortfallContributors.slice(0, 15);
  const res = d.resilience;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${d.metadata.title}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Georgia,serif;color:#002F65;background:#fff;padding:40px 60px;max-width:1100px;margin:0 auto}
h1{font-size:28px;color:#002F65;border-bottom:3px solid #B8860B;padding-bottom:12px;margin-bottom:24px}
h2{font-size:20px;color:#002F65;margin:32px 0 12px;border-left:4px solid #00A8CB;padding-left:12px}
h3{font-size:15px;color:#00A8CB;margin:20px 0 8px}
.meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px;margin-bottom:24px;padding:16px;background:#f5f5f5;border-radius:6px}
.meta span{color:#666}.meta strong{color:#002F65}
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:16px 0 24px}
.kpi{background:#f8f9fa;padding:16px;border-radius:6px;text-align:center}
.kpi .val{font-size:28px;color:#00A8CB;font-weight:700}.kpi .lbl{font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.08em;margin-top:4px}
table{width:100%;border-collapse:collapse;margin:12px 0 24px;font-size:11px}
th{background:#002F65;color:#fff;padding:8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.04em}
td{padding:6px 8px;border-bottom:1px solid #e0e0e0}
tr:nth-child(even){background:#f9f9f9}
.finding{padding:12px 16px;margin:8px 0;border-radius:4px;border-left:4px solid #ccc;background:#f9f9f9}
.finding.high{border-left-color:#E76F51}.finding.medium{border-left-color:#E9C46A}.finding.low{border-left-color:#2A9D8F}
.footer{margin-top:40px;padding-top:16px;border-top:2px solid #B8860B;font-size:10px;color:#666;text-align:center}
.logo{font-size:12px;font-weight:700;letter-spacing:0.1em;color:#B8860B}
@media print{body{padding:20px 30px}}
</style></head><body>
<div class="logo">OPTIMAL</div>
<h1>${d.metadata.title}</h1>
<div class="meta">
<div><span>Client:</span> <strong>${d.metadata.client || '—'}</strong></div>
<div><span>Report No:</span> <strong>${d.metadata.reportNumber}</strong></div>
<div><span>Facility:</span> <strong>${d.metadata.facility || '—'}</strong></div>
<div><span>Date:</span> <strong>${d.metadata.date}</strong></div>
<div><span>Software:</span> <strong>${d.metadata.software}</strong></div>
<div><span>Methodology:</span> <strong>${d.metadata.methodology}</strong></div>
<div><span>Simulations:</span> <strong>${d.studyConfig.numRuns}</strong></div>
<div><span>Mission Time:</span> <strong>${d.studyConfig.missionTime}h (${(d.studyConfig.missionTime/8760).toFixed(1)} yr)</strong></div>
</div>

<h2>Executive Summary</h2>
<p>This RAM Availability Study presents the results of a Monte Carlo simulation analysis of the ${d.metadata.facility || 'facility'} using ${d.studyConfig.numRuns} lifecycle simulations over a ${(d.studyConfig.missionTime/8760).toFixed(1)}-year period. The system model comprises ${d.studyConfig.equipmentCount} equipment items modelled as a Reliability Block Diagram.</p>
<p style="margin-top:8px">The predicted mean Production Availability is <strong>${(a.mean*100).toFixed(2)}%</strong>. There is a 5% chance of exceeding ${(a.p5*100).toFixed(1)}%, and a 95% chance of exceeding ${(a.p95*100).toFixed(1)}%.</p>

<div class="kpi-grid">
<div class="kpi"><div class="val">${(a.mean*100).toFixed(1)}%</div><div class="lbl">Mean Availability</div></div>
<div class="kpi"><div class="val">${(a.p95*100).toFixed(1)}%</div><div class="lbl">P95 (95% exceedance)</div></div>
<div class="kpi"><div class="val">${(a.p5*100).toFixed(1)}%</div><div class="lbl">P5 (5% exceedance)</div></div>
<div class="kpi"><div class="val">${d.shortfall.mean.toFixed(0)}</div><div class="lbl">Mean Shortfall (${d.studyConfig.productionUnit})</div></div>
</div>

<h2>Reliability Asset Register</h2>
<table><thead><tr><th>Equipment</th><th>Type</th><th>Config</th><th>MTBF (h)</th><th>MTTR (h)</th><th>Distribution</th><th>Capacity</th><th>Data Source</th></tr></thead>
<tbody>${d.reliabilityAssetRegister.map(r => `<tr><td>${r.description}</td><td>${r.equipmentType}</td><td>${r.blockType}</td><td>${r.mtbf}</td><td>${r.mttr}</td><td>${r.distribution}</td><td>${r.capacity}%</td><td>${r.dataSource}</td></tr>`).join('')}</tbody></table>

<h2>System Contributors to Production Shortfall</h2>
<table><thead><tr><th>Rank</th><th>Equipment</th><th>Mean Shortfall</th><th>% Total</th><th>Mean Failures/yr</th><th>Mean Downtime (h)</th></tr></thead>
<tbody>${cont.map((c,i) => `<tr><td>${i+1}</td><td>${c.description}</td><td>${c.meanShortfall.toFixed(0)}</td><td>${c.percentOfTotal.toFixed(1)}%</td><td>${c.meanFailures.toFixed(1)}</td><td>${c.meanDowntime.toFixed(0)}</td></tr>`).join('')}</tbody></table>

<h2>Monthly Production Breakdown</h2>
<table><thead><tr><th>Month</th><th>Target</th><th>Mean Production</th><th>Availability</th><th>Shortfall</th></tr></thead>
<tbody>${d.monthlyBreakdown.map(m => `<tr><td>${m.monthName}</td><td>${m.target.toFixed(0)}</td><td>${m.meanProduction.toFixed(0)}</td><td>${(m.availability*100).toFixed(1)}%</td><td>${m.shortfall.toFixed(0)}</td></tr>`).join('')}</tbody></table>

<h2>Asset Resilience Findings</h2>
<p>${res.summary.totalFindings} findings identified: ${res.summary.highSeverity} high, ${res.summary.mediumSeverity} medium, ${res.summary.lowSeverity} low severity.</p>
${res.findings.map(f => `<div class="finding ${f.severity}"><strong>${f.description}</strong> — ${f.impact}<br><em>${f.recommendation}</em></div>`).join('')}

<div class="footer">
<div class="logo">OPTIMAL</div>
<p>${d.metadata.author} | ${d.metadata.date} | Confidential</p>
</div></body></html>`;
}


function MarkovAnalysisView({ projectData, setProjectData }) {
  const [states, setStates] = useState([
    { id: 'S0', label: 'Operational', type: 'operational', outputRate: 100 },
    { id: 'S1', label: 'Degraded', type: 'degraded', outputRate: 60 },
    { id: 'S2', label: 'Failed', type: 'failed', outputRate: 0 }
  ]);
  const [transitions, setTransitions] = useState([
    { id: 'T1', from: 'S0', to: 'S1', rate: 0.001, label: 'Degradation' },
    { id: 'T2', from: 'S1', to: 'S2', rate: 0.005, label: 'Total failure' },
    { id: 'T3', from: 'S2', to: 'S0', rate: 0.05, label: 'Repair' },
    { id: 'T4', from: 'S1', to: 'S0', rate: 0.02, label: 'Minor repair' }
  ]);
  const [results, setResults] = useState(null);
  const [transientResults, setTransientResults] = useState(null);
  const [sensitivityResults, setSensitivityResults] = useState(null);
  const [activePhase, setActivePhase] = useState(0); // 0=define, 1=results, 2=transient, 3=sensitivity
  const [selectedTransition, setSelectedTransition] = useState(null);
  const [missionTime, setMissionTime] = useState(8760);

  const stateColors = { operational: '#2A9D8F', degraded: '#E9C46A', failed: '#E76F51', maintenance: '#457B9D' };

  const addState = () => {
    const n = states.length;
    setStates([...states, { id: `S${n}`, label: `State ${n}`, type: 'operational', outputRate: 100 }]);
  };

  const removeState = (id) => {
    setStates(states.filter(s => s.id !== id));
    setTransitions(transitions.filter(t => t.from !== id && t.to !== id));
  };

  const updateState = (id, field, value) => {
    setStates(states.map(s => s.id === id ? { ...s, [field]: field === 'outputRate' ? parseFloat(value) || 0 : value } : s));
  };

  const addTransition = () => {
    if (states.length < 2) return;
    const n = transitions.length;
    setTransitions([...transitions, { id: `T${n + 1}`, from: states[0].id, to: states[1].id, rate: 0.001, label: `Transition ${n + 1}` }]);
  };

  const removeTransition = (id) => setTransitions(transitions.filter(t => t.id !== id));

  const updateTransition = (id, field, value) => {
    setTransitions(transitions.map(t => t.id === id ? { ...t, [field]: field === 'rate' ? parseFloat(value) || 0 : value } : t));
  };

  const runAnalysis = () => {
    try {
      const engine = new MarkovEngine(states, transitions);
      engine.buildTransitionMatrix();
      const ss = engine.solveSteadyState();
      setResults(ss);
      const timePoints = [];
      for (let t = 0; t <= missionTime; t += missionTime / 100) timePoints.push(t);
      const tr = engine.solveTransient(timePoints);
      setTransientResults(tr);
      setActivePhase(1);
      // G04: Persist Markov results to projectData so Dashboard and other modules can access them
      const markovResult = {
        id: 'MKV-' + Date.now(),
        timestamp: new Date().toISOString(),
        states: states.map(s => ({ ...s })),
        transitions: transitions.map(t => ({ ...t })),
        steadyState: ss,
        availability: ss.availability,
        mttf: ss.mttf,
        expectedProduction: ss.expectedProduction,
        costRate: ss.costRate,
        missionTime
      };
      setProjectData(prev => ({
        ...prev,
        markovAnalyses: [...(prev.markovAnalyses || []).filter(m => m.id !== markovResult.id), markovResult]
      }));
    } catch (err) {
      alert('Analysis error: ' + err.message);
    }
  };

  const runSensitivity = () => {
    if (!selectedTransition) return;
    try {
      const engine = new MarkovEngine(states, transitions);
      engine.buildTransitionMatrix();
      const sens = engine.runSensitivity(selectedTransition, { min: 0.0001, max: 0.1, steps: 20 });
      setSensitivityResults(sens);
      setActivePhase(3);
    } catch (err) {
      alert('Sensitivity error: ' + err.message);
    }
  };

  const phases = [
    { id: 0, label: 'Define Model', icon: '①' },
    { id: 1, label: 'Steady-State', icon: '②' },
    { id: 2, label: 'Transient P(t)', icon: '③' },
    { id: 3, label: 'Sensitivity', icon: '④' }
  ];

  const inputSty = { backgroundColor: '#fff', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '5px 7px', fontSize: '11px', width: '100%', boxSizing: 'border-box' };
  const btnSty = (primary) => ({ padding: '7px 16px', fontSize: '10px', fontWeight: 600, border: primary ? 'none' : `1px solid ${COLORS.BORDER}`, borderRadius: '3px', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', backgroundColor: primary ? COLORS.TEAL : 'transparent', color: primary ? '#fff' : COLORS.TEXT, fontFamily: "'Georgia', serif" });

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Phase rail */}
      <div style={{ width: '180px', backgroundColor: COLORS.PANEL_BG, borderRight: `1px solid ${COLORS.BORDER}`, padding: '16px 0', flexShrink: 0 }}>
        <div style={{ padding: '0 16px 12px', fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Markov Analysis</div>
        {phases.map(p => (
          <div key={p.id} onClick={() => (p.id <= 0 || results) && setActivePhase(p.id)}
            style={{ padding: '10px 16px', cursor: (p.id <= 0 || results) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '10px',
              backgroundColor: activePhase === p.id ? 'rgba(0,168,203,0.08)' : 'transparent',
              borderRight: activePhase === p.id ? `3px solid ${COLORS.TEAL}` : '3px solid transparent',
              opacity: (p.id <= 0 || results) ? 1 : 0.35 }}>
            <span style={{ fontSize: '13px', color: activePhase === p.id ? COLORS.TEAL : COLORS.TEXT_DIM }}>{p.icon}</span>
            <span style={{ fontSize: '11px', fontWeight: activePhase === p.id ? 600 : 400, color: activePhase === p.id ? COLORS.TEAL : COLORS.TEXT }}>{p.label}</span>
          </div>
        ))}
        <div style={{ padding: '16px', borderTop: `1px solid ${COLORS.BORDER}`, marginTop: '12px' }}>
          <button onClick={runAnalysis} style={btnSty(true)}>Solve Model</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {activePhase === 0 && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '16px' }}>Define Markov states and transitions</div>
            {/* States */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em' }}>System States ({states.length})</span>
                <button onClick={addState} style={btnSty(false)}>+ Add State</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 100px 80px 40px', gap: '6px', fontSize: '11px', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>ID</div><div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>Label</div>
                <div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>Type</div><div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>Output %</div><div />
                {states.map(s => (
                  <React.Fragment key={s.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: stateColors[s.type] || '#999' }} />
                      <span style={{ color: COLORS.TEXT }}>{s.id}</span>
                    </div>
                    <input value={s.label} onChange={e => updateState(s.id, 'label', e.target.value)} style={inputSty} />
                    <select value={s.type} onChange={e => updateState(s.id, 'type', e.target.value)} style={{ ...inputSty }}>
                      <option value="operational">Operational</option><option value="degraded">Degraded</option>
                      <option value="failed">Failed</option><option value="maintenance">Maintenance</option>
                    </select>
                    <input type="number" value={s.outputRate} onChange={e => updateState(s.id, 'outputRate', e.target.value)} style={inputSty} />
                    <button onClick={() => removeState(s.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#B91C1C', fontSize: '13px' }}>✕</button>
                  </React.Fragment>
                ))}
              </div>
            </div>
            {/* Transitions */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Transitions ({transitions.length})</span>
                <button onClick={addTransition} style={btnSty(false)}>+ Add Transition</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 40px', gap: '6px', fontSize: '11px', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>Label</div><div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>From</div>
                <div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>To</div><div style={{ fontWeight: 600, color: COLORS.TEXT_DIM }}>Rate (λ)</div><div />
                {transitions.map(t => (
                  <React.Fragment key={t.id}>
                    <input value={t.label} onChange={e => updateTransition(t.id, 'label', e.target.value)} style={inputSty} />
                    <select value={t.from} onChange={e => updateTransition(t.id, 'from', e.target.value)} style={inputSty}>
                      {states.map(s => <option key={s.id} value={s.id}>{s.id}</option>)}
                    </select>
                    <select value={t.to} onChange={e => updateTransition(t.id, 'to', e.target.value)} style={inputSty}>
                      {states.map(s => <option key={s.id} value={s.id}>{s.id}</option>)}
                    </select>
                    <input type="number" step="0.0001" value={t.rate} onChange={e => updateTransition(t.id, 'rate', e.target.value)} style={inputSty} />
                    <button onClick={() => removeTransition(t.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#B91C1C', fontSize: '13px' }}>✕</button>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <label style={{ fontSize: '11px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Mission Time (hrs)</label>
              <input type="number" value={missionTime} onChange={e => setMissionTime(parseFloat(e.target.value) || 8760)} style={{ ...inputSty, width: '120px', marginLeft: '8px' }} />
            </div>
          </div>
        )}

        {activePhase === 1 && results && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '16px' }}>Steady-state results</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Availability', value: results.availability != null ? (results.availability * 100).toFixed(2) + '%' : '—' },
                { label: 'MTTF', value: results.mttf != null ? results.mttf.toFixed(1) + ' hrs' : '—' },
                { label: 'Expected Output', value: results.expectedProduction != null ? results.expectedProduction.toFixed(1) + '%' : '—' },
                { label: 'Expected Cost Rate', value: results.expectedCost != null ? results.expectedCost.toFixed(4) : '—' }
              ].map((m, i) => (
                <div key={i} style={{ backgroundColor: 'rgba(0,47,101,0.03)', borderRadius: '4px', padding: '14px 16px' }}>
                  <div style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px' }}>{m.label}</div>
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT }}>{m.value}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>State Probabilities</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', maxWidth: '500px' }}>
              <thead><tr style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', color: COLORS.TEXT_DIM, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>State</th>
                <th style={{ textAlign: 'right', padding: '6px 10px', color: COLORS.TEXT_DIM, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>Probability</th>
                <th style={{ textAlign: 'right', padding: '6px 10px', color: COLORS.TEXT_DIM, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>% Time</th>
              </tr></thead>
              <tbody>
                {results.stateProbabilities && results.stateProbabilities.map((p, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: stateColors[states[i]?.type] || '#999' }} />
                      {states[i]?.label || `S${i}`}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 10px', fontFamily: "'Georgia', serif" }}>{p.toFixed(6)}</td>
                    <td style={{ textAlign: 'right', padding: '6px 10px' }}>{(p * 100).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activePhase === 2 && transientResults && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '16px' }}>Time-dependent state probabilities P(t)</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>State probability evolution over {missionTime.toLocaleString()} hours mission time.</div>
            <div style={{ height: '300px', position: 'relative', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '12px', backgroundColor: '#fff' }}>
              <svg viewBox="0 0 600 260" style={{ width: '100%', height: '100%' }}>
                {states.map((s, si) => {
                  const points = transientResults.timePoints?.map((t, ti) => {
                    const prob = transientResults.probabilities?.[ti]?.[si] ?? 0;
                    const x = (t / missionTime) * 580 + 10;
                    const y = 250 - prob * 240;
                    return `${x},${y}`;
                  }).join(' ') || '';
                  return <polyline key={s.id} points={points} fill="none" stroke={stateColors[s.type] || '#999'} strokeWidth="2" />;
                })}
                <line x1="10" y1="250" x2="590" y2="250" stroke={COLORS.BORDER} strokeWidth="0.5" />
                <line x1="10" y1="10" x2="10" y2="250" stroke={COLORS.BORDER} strokeWidth="0.5" />
                <text x="300" y="258" textAnchor="middle" fontSize="9" fill={COLORS.TEXT_DIM}>Time (hrs)</text>
                <text x="5" y="130" textAnchor="middle" fontSize="9" fill={COLORS.TEXT_DIM} transform="rotate(-90 5 130)">P(t)</text>
              </svg>
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '10px', flexWrap: 'wrap' }}>
              {states.map(s => (
                <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: COLORS.TEXT }}>
                  <span style={{ width: '10px', height: '3px', backgroundColor: stateColors[s.type] || '#999', borderRadius: '1px' }} />
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {activePhase === 3 && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '12px' }}>Sensitivity analysis</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Vary transition:</span>
              <select value={selectedTransition || ''} onChange={e => setSelectedTransition(e.target.value)} style={inputSty}>
                <option value="">Select...</option>
                {transitions.map(t => <option key={t.id} value={t.id}>{t.label} ({t.from}→{t.to})</option>)}
              </select>
              <button onClick={runSensitivity} style={btnSty(true)}>Run Sensitivity</button>
            </div>
            {sensitivityResults && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', maxWidth: '600px' }}>
                <thead><tr style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: COLORS.TEXT_DIM, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>Rate</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: COLORS.TEXT_DIM, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>Availability</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: COLORS.TEXT_DIM, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>MTTF</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: COLORS.TEXT_DIM, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>Output %</th>
                </tr></thead>
                <tbody>
                  {(sensitivityResults.points || []).map((pt, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                      <td style={{ padding: '5px 10px' }}>{pt.rate?.toFixed(5)}</td>
                      <td style={{ textAlign: 'right', padding: '5px 10px' }}>{pt.availability != null ? (pt.availability * 100).toFixed(2) + '%' : '—'}</td>
                      <td style={{ textAlign: 'right', padding: '5px 10px' }}>{pt.mttf != null ? pt.mttf.toFixed(1) : '—'}</td>
                      <td style={{ textAlign: 'right', padding: '5px 10px' }}>{pt.production != null ? pt.production.toFixed(1) + '%' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// 4-AXIS CRITICALITY HEATMAP — S/O/E/C Matrix Visualisation
// ============================================================================
function CriticalityHeatmapView({ projectData }) {
  const [xAxis, setXAxis] = useState('occurrence');
  const [yAxis, setYAxis] = useState('severity');
  const [colorAxis, setColorAxis] = useState('detection');
  const [sizeAxis, setSizeAxis] = useState('cost');

  const causes = projectData.rcmcauses || [];
  const fmMap = {};
  (projectData.failuremodels || []).forEach(fm => { fmMap[fm.id] = fm; });

  const enriched = causes.map(c => {
    const fm = fmMap[c.failureModel] || fmMap[c.failureModeId] || {};
    return {
      ...c,
      severity: c.severity || c.rpnSeverity || 5,
      occurrence: c.occurrence || c.rpnOccurrence || 5,
      detection: c.detection || c.rpnDetection || 5,
      cost: c.consequenceCost || fm.consequenceCost || c.repairCost || 10000,
      rpn: (c.severity || 5) * (c.occurrence || 5) * (c.detection || 5)
    };
  });

  const axisLabels = { severity: 'Severity (S)', occurrence: 'Occurrence (O)', detection: 'Detection (D)', cost: 'Economic (C)' };
  const selectSty = { fontSize: '11px', padding: '4px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', color: COLORS.TEXT, backgroundColor: '#fff' };

  const getColor = (val) => {
    const norm = Math.min(val / 10, 1);
    if (norm < 0.3) return '#2A9D8F';
    if (norm < 0.6) return '#E9C46A';
    if (norm < 0.8) return '#F4A261';
    return '#E76F51';
  };

  return (
    <div style={{ padding: '24px 28px', height: '100%', overflow: 'auto' }}>
      <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '4px' }}>4-Axis Criticality Matrix</div>
      <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px', maxWidth: '600px', lineHeight: 1.55 }}>
        Map failure modes across four risk dimensions. Position encodes two axes, colour encodes a third, and bubble size the fourth.
      </div>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>X-Axis <select value={xAxis} onChange={e => setXAxis(e.target.value)} style={selectSty}>
          <option value="occurrence">Occurrence</option><option value="severity">Severity</option><option value="detection">Detection</option><option value="cost">Cost</option>
        </select></label>
        <label style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Y-Axis <select value={yAxis} onChange={e => setYAxis(e.target.value)} style={selectSty}>
          <option value="severity">Severity</option><option value="occurrence">Occurrence</option><option value="detection">Detection</option><option value="cost">Cost</option>
        </select></label>
        <label style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Colour <select value={colorAxis} onChange={e => setColorAxis(e.target.value)} style={selectSty}>
          <option value="detection">Detection</option><option value="severity">Severity</option><option value="occurrence">Occurrence</option><option value="cost">Cost</option>
        </select></label>
        <label style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Size <select value={sizeAxis} onChange={e => setSizeAxis(e.target.value)} style={selectSty}>
          <option value="cost">Cost</option><option value="detection">Detection</option><option value="severity">Severity</option><option value="occurrence">Occurrence</option>
        </select></label>
      </div>
      {enriched.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '13px', fontFamily: "'Georgia', serif" }}>
          No failure modes defined. Build your FMEA in the RCM Workflow module first.
        </div>
      ) : (
        <div style={{ position: 'relative', width: '100%', maxWidth: '600px', height: '400px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', backgroundColor: '#fff' }}>
          <svg viewBox="0 0 600 400" style={{ width: '100%', height: '100%' }}>
            {/* Grid */}
            {[1,2,3,4,5,6,7,8,9,10].map(v => (
              <React.Fragment key={v}>
                <line x1={50 + (v / 10) * 520} y1="20" x2={50 + (v / 10) * 520} y2="370" stroke={COLORS.BORDER} strokeWidth="0.3" />
                <line x1="50" y1={370 - (v / 10) * 340} x2="570" y2={370 - (v / 10) * 340} stroke={COLORS.BORDER} strokeWidth="0.3" />
                <text x={50 + (v / 10) * 520} y="385" textAnchor="middle" fontSize="9" fill={COLORS.TEXT_DIM}>{v}</text>
                <text x="44" y={374 - (v / 10) * 340} textAnchor="end" fontSize="9" fill={COLORS.TEXT_DIM}>{v}</text>
              </React.Fragment>
            ))}
            {/* Risk zones */}
            <rect x="50" y="20" width="260" height="175" fill="rgba(42,157,143,0.06)" rx="2" />
            <rect x="310" y="195" width="260" height="175" fill="rgba(231,111,81,0.08)" rx="2" />
            <text x="180" y="110" fontSize="10" fill="rgba(42,157,143,0.3)" fontWeight="600" textAnchor="middle">LOW RISK</text>
            <text x="440" y="290" fontSize="10" fill="rgba(231,111,81,0.3)" fontWeight="600" textAnchor="middle">HIGH RISK</text>
            {/* Bubbles */}
            {enriched.map((c, i) => {
              const xVal = c[xAxis] || 5;
              const yVal = c[yAxis] || 5;
              const cVal = c[colorAxis] || 5;
              const sVal = c[sizeAxis] || 5;
              const cx = 50 + (xVal / 10) * 520;
              const cy = 370 - (yVal / 10) * 340;
              const r = Math.max(4, Math.min(18, (sVal / 10) * 18));
              return (
                <g key={i}>
                  <circle cx={cx} cy={cy} r={r} fill={getColor(cVal)} opacity="0.7" stroke="#fff" strokeWidth="1" />
                  <title>{c.description || c.id}: S={c.severity} O={c.occurrence} D={c.detection} RPN={c.rpn}</title>
                </g>
              );
            })}
            <text x="310" y="398" textAnchor="middle" fontSize="10" fill={COLORS.TEXT_DIM}>{axisLabels[xAxis]}</text>
            <text x="15" y="200" textAnchor="middle" fontSize="10" fill={COLORS.TEXT_DIM} transform="rotate(-90 15 200)">{axisLabels[yAxis]}</text>
          </svg>
        </div>
      )}
      <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '10px', color: COLORS.TEXT_DIM }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#2A9D8F' }} /> Low</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#E9C46A' }} /> Medium</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#F4A261' }} /> High</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#E76F51' }} /> Critical</span>
        <span style={{ fontSize: '10px', marginLeft: '8px' }}>Bubble size = {axisLabels[sizeAxis]}</span>
      </div>
      <div style={{ marginTop: '16px', fontSize: '11px', color: COLORS.TEXT_DIM }}>
        {enriched.length} failure modes plotted — {enriched.filter(c => c.rpn >= 200).length} high-RPN items (≥200)
      </div>
    </div>
  );
}

// ============================================================================
// TORNADO/WATERFALL SENSITIVITY CHART — LCC Break-Even Analysis
// ============================================================================
function LCCSensitivityView({ projectData, simConfig }) {
  const [analysisType, setAnalysisType] = useState('tornado'); // tornado | waterfall | breakeven
  const costNodes = projectData.costnodes || [];

  const totalCost = costNodes.reduce((s, n) => s + (n.totalCost || n.costPerOccurrence || 0), 0);
  const categories = {};
  costNodes.forEach(n => {
    const cat = n.category || 'other';
    if (!categories[cat]) categories[cat] = 0;
    categories[cat] += (n.totalCost || n.costPerOccurrence || 0);
  });

  const sorted = Object.entries(categories).sort((a, b) => b[1] - a[1]);
  const maxCat = sorted.length > 0 ? sorted[0][1] : 1;

  // Break-even: find where cumulative savings offset investment
  const investment = costNodes.find(n => n.category === 'acquisition')?.totalCost || costNodes[0]?.totalCost || 100000;
  const annualSaving = totalCost > 0 ? totalCost * 0.15 : 10000; // Assume 15% annual saving from optimisation
  const breakEvenYears = annualSaving > 0 ? (investment / annualSaving).toFixed(1) : '—';

  const catColors = { acquisition: '#002F65', maintenance: '#00A8CB', spares: '#2A9D8F', labor: '#E9C46A', downtime: '#E76F51', disposal: '#457B9D', other: '#999' };
  const barH = 24;

  return (
    <div style={{ padding: '24px 28px', height: '100%', overflow: 'auto' }}>
      <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '4px' }}>LCC Sensitivity & Break-Even</div>
      <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Cost driver analysis, cumulative waterfall, and investment payback calculation.</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['tornado', 'waterfall', 'breakeven'].map(t => (
          <button key={t} onClick={() => setAnalysisType(t)} style={{
            padding: '6px 14px', fontSize: '10px', fontWeight: 600, border: `1px solid ${analysisType === t ? COLORS.TEAL : COLORS.BORDER}`,
            borderRadius: '3px', cursor: 'pointer', backgroundColor: analysisType === t ? COLORS.TEAL_LIGHT : 'transparent',
            color: analysisType === t ? COLORS.TEAL : COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.06em'
          }}>{t === 'tornado' ? 'Tornado' : t === 'waterfall' ? 'Waterfall' : 'Break-Even'}</button>
        ))}
      </div>

      {analysisType === 'tornado' && (
        <div style={{ maxWidth: '600px' }}>
          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '12px' }}>±20% variation on each cost category — impact on total LCC</div>
          <svg viewBox={`0 0 600 ${sorted.length * (barH + 10) + 40}`} style={{ width: '100%' }}>
            <line x1="300" y1="0" x2="300" y2={sorted.length * (barH + 10) + 20} stroke={COLORS.BORDER} strokeWidth="1" />
            <text x="300" y={sorted.length * (barH + 10) + 35} textAnchor="middle" fontSize="9" fill={COLORS.TEXT_DIM}>Base Case</text>
            {sorted.map(([cat, val], i) => {
              const swing = val * 0.2;
              const pxPerUnit = 280 / (maxCat * 0.2 || 1);
              const barWidth = swing * pxPerUnit;
              const y = i * (barH + 10) + 10;
              return (
                <g key={cat}>
                  <rect x={300 - barWidth} y={y} width={barWidth} height={barH} fill={catColors[cat] || '#999'} opacity="0.7" rx="2" />
                  <rect x={300} y={y} width={barWidth} height={barH} fill={catColors[cat] || '#999'} opacity="0.4" rx="2" />
                  <text x={300 - barWidth - 6} y={y + barH / 2 + 4} textAnchor="end" fontSize="10" fill={COLORS.TEXT}>{cat}</text>
                  <text x={300 + barWidth + 6} y={y + barH / 2 + 4} fontSize="9" fill={COLORS.TEXT_DIM}>±{Math.round(swing).toLocaleString()}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {analysisType === 'waterfall' && (
        <div style={{ maxWidth: '600px' }}>
          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '12px' }}>Cumulative cost build-up by category</div>
          <svg viewBox={`0 0 600 320`} style={{ width: '100%' }}>
            {(() => {
              let cumulative = 0;
              const barW = Math.max(30, 500 / (sorted.length + 1));
              return sorted.map(([cat, val], i) => {
                const prev = cumulative;
                cumulative += val;
                const maxH = 260;
                const scale = totalCost > 0 ? maxH / totalCost : 1;
                const h = val * scale;
                const y = 280 - cumulative * scale;
                return (
                  <g key={cat}>
                    <rect x={50 + i * (barW + 8)} y={y} width={barW} height={h} fill={catColors[cat] || '#999'} rx="2" />
                    {i > 0 && <line x1={50 + (i - 1) * (barW + 8) + barW} y1={280 - prev * scale - (sorted[i - 1][1]) * scale} x2={50 + i * (barW + 8)} y2={280 - prev * scale - (sorted[i - 1][1]) * scale} stroke={COLORS.BORDER} strokeWidth="0.5" strokeDasharray="3" />}
                    <text x={50 + i * (barW + 8) + barW / 2} y="296" textAnchor="middle" fontSize="9" fill={COLORS.TEXT_DIM}>{cat}</text>
                    <text x={50 + i * (barW + 8) + barW / 2} y={y - 4} textAnchor="middle" fontSize="9" fill={COLORS.TEXT}>{Math.round(val).toLocaleString()}</text>
                  </g>
                );
              });
            })()}
            {/* Total bar */}
            <rect x={50 + sorted.length * (Math.max(30, 500 / (sorted.length + 1)) + 8)} y={280 - 260} width={Math.max(30, 500 / (sorted.length + 1))} height={260} fill="#002F65" rx="2" />
            <text x={50 + sorted.length * (Math.max(30, 500 / (sorted.length + 1)) + 8) + Math.max(15, 250 / (sorted.length + 1))} y="296" textAnchor="middle" fontSize="9" fill={COLORS.TEXT_DIM}>Total</text>
            <text x={50 + sorted.length * (Math.max(30, 500 / (sorted.length + 1)) + 8) + Math.max(15, 250 / (sorted.length + 1))} y={16} textAnchor="middle" fontSize="9" fill="#002F65" fontWeight="600">{Math.round(totalCost).toLocaleString()}</text>
          </svg>
        </div>
      )}

      {analysisType === 'breakeven' && (
        <div style={{ maxWidth: '500px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: 'rgba(0,47,101,0.03)', borderRadius: '4px', padding: '14px 16px' }}>
              <div style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px' }}>Initial Investment</div>
              <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT }}>{Math.round(investment).toLocaleString()}</div>
            </div>
            <div style={{ backgroundColor: 'rgba(0,47,101,0.03)', borderRadius: '4px', padding: '14px 16px' }}>
              <div style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '4px' }}>Est. Annual Saving</div>
              <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', color: COLORS.TEXT }}>{Math.round(annualSaving).toLocaleString()}</div>
            </div>
          </div>
          <div style={{ backgroundColor: COLORS.TEAL_LIGHT, borderRadius: '4px', padding: '20px 24px', textAlign: 'center', border: `1px solid rgba(0,168,203,0.2)` }}>
            <div style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>Break-Even Point</div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '28px', color: COLORS.TEAL, fontWeight: 400 }}>{breakEvenYears} years</div>
          </div>
          <svg viewBox="0 0 500 200" style={{ width: '100%', marginTop: '20px' }}>
            {/* Investment line (flat) */}
            <line x1="40" y1="40" x2="460" y2="40" stroke="#B91C1C" strokeWidth="1.5" strokeDasharray="4" />
            {/* Cumulative savings line (rising) */}
            <line x1="40" y1="180" x2="460" y2="20" stroke="#2A9D8F" strokeWidth="2" />
            {/* Intersection */}
            {(() => {
              const beX = 40 + (parseFloat(breakEvenYears) / 10) * 420;
              return beX > 40 && beX < 460 ? <circle cx={beX} cy={40 + (180 - 40) * (1 - parseFloat(breakEvenYears) / 10)} r="5" fill="#00A8CB" stroke="#fff" strokeWidth="2" /> : null;
            })()}
            <text x="250" y="198" textAnchor="middle" fontSize="9" fill={COLORS.TEXT_DIM}>Years</text>
            <text x="460" y="36" fontSize="9" fill="#B91C1C">Investment</text>
            <text x="460" y="24" fontSize="9" fill="#2A9D8F">Savings</text>
          </svg>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// AUDIT TRAIL VIEW — Change tracking and undo/redo history
// ============================================================================
// ============================================================================
// SUB-DIAGRAM EDITOR — Edit blocks inside a sub-diagram block
// ============================================================================
function SubDiagramEditor({ block, onUpdateBlock, onClose }) {
  const [subBlocks, setSubBlocks] = useState(block.subBlocks || []);
  const [subConns, setSubConns] = useState(block.subConnections || []);
  const [selectedSub, setSelectedSub] = useState(null);

  const saveAndClose = () => {
    onUpdateBlock(block.id, { subBlocks, subConnections: subConns });
    onClose();
  };

  const addSubBlock = () => {
    const nb = { id: `SB-${Date.now()}`, x: 80 + subBlocks.length * 120, y: 80, description: `Block ${subBlocks.length + 1}`, blockType: 'series', failureRate: 0.001, mttr: 24 };
    setSubBlocks([...subBlocks, nb]);
  };

  const deleteSubBlock = (id) => {
    setSubBlocks(subBlocks.filter(b => b.id !== id));
    setSubConns(subConns.filter(c => c.from !== id && c.to !== id));
    if (selectedSub === id) setSelectedSub(null);
  };

  const updateSubBlock = (id, updates) => {
    setSubBlocks(subBlocks.map(b => b.id === id ? { ...b, ...updates } : b));
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '85vw', maxWidth: '1100px', height: '70vh', backgroundColor: COLORS.PANEL_BG, borderRadius: '8px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '15px', color: COLORS.TEXT }}>Sub-Diagram: {block.description}</div>
            <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Define the internal reliability structure of this subsystem</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={addSubBlock} style={{ padding: '6px 14px', backgroundColor: COLORS.NAVY, color: '#FFF', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>+ Add Block</button>
            <button onClick={saveAndClose} style={{ padding: '6px 14px', backgroundColor: COLORS.GOLD, color: '#FFF', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>Save & Close</button>
            <button onClick={onClose} style={{ padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Mini canvas */}
          <div style={{ flex: 1, position: 'relative', backgroundColor: COLORS.LIGHT_BG, overflow: 'auto' }}>
            <svg width="100%" height="100%" style={{ minHeight: '400px' }}>
              {/* Start node */}
              <circle cx="30" cy="120" r="10" fill={COLORS.GOLD} />
              <text x="30" y="124" textAnchor="middle" fill="#FFF" fontSize="8" fontWeight="bold">S</text>
              {/* Sub blocks */}
              {subBlocks.map((sb, idx) => (
                <g key={sb.id} onClick={() => setSelectedSub(sb.id)} style={{ cursor: 'pointer' }}>
                  <rect x={sb.x || 80 + idx * 120} y={sb.y || 80} width="90" height="50" rx="4"
                    fill={selectedSub === sb.id ? COLORS.GOLD : COLORS.NAVY} stroke={selectedSub === sb.id ? COLORS.GOLD : COLORS.BORDER} strokeWidth="2" />
                  <text x={(sb.x || 80 + idx * 120) + 45} y={(sb.y || 80) + 22} textAnchor="middle" fill="#FFF" fontSize="10" fontWeight="bold">{sb.description?.substring(0, 10)}</text>
                  <text x={(sb.x || 80 + idx * 120) + 45} y={(sb.y || 80) + 38} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="8">λ={sb.failureRate} MTTR={sb.mttr}h</text>
                  {/* Connection line from previous */}
                  {idx === 0 && <line x1="40" y1="120" x2={sb.x || 80} y2={(sb.y || 80) + 25} stroke={COLORS.BORDER} strokeWidth="1.5" markerEnd="url(#arrowhead)" />}
                  {idx > 0 && <line x1={(subBlocks[idx - 1].x || 80 + (idx - 1) * 120) + 90} y1={(subBlocks[idx - 1].y || 80) + 25} x2={sb.x || 80 + idx * 120} y2={(sb.y || 80) + 25} stroke={COLORS.BORDER} strokeWidth="1.5" markerEnd="url(#arrowhead)" />}
                </g>
              ))}
              {/* End node */}
              {subBlocks.length > 0 && (
                <>
                  <circle cx={Math.max(...subBlocks.map((sb, i) => (sb.x || 80 + i * 120))) + 130} cy="120" r="10" fill={COLORS.NAVY} />
                  <text x={Math.max(...subBlocks.map((sb, i) => (sb.x || 80 + i * 120))) + 130} y="124" textAnchor="middle" fill="#FFF" fontSize="8" fontWeight="bold">E</text>
                </>
              )}
            </svg>
          </div>
          {/* Properties panel */}
          {selectedSub && (() => {
            const sb = subBlocks.find(b => b.id === selectedSub);
            if (!sb) return null;
            return (
              <div style={{ width: '260px', borderLeft: `1px solid ${COLORS.BORDER}`, padding: '16px', overflow: 'auto', fontSize: '12px' }}>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Block Properties</div>
                {[
                  { label: 'Description', key: 'description', type: 'text' },
                  { label: 'Equipment Type', key: 'equipmentType', type: 'text' },
                  { label: 'Block Type', key: 'blockType', type: 'select', options: ['series', 'parallel', 'kofn', 'standby'] },
                  { label: 'Capacity %', key: 'capacity', type: 'number' },
                  { label: 'MTBF (hours)', key: 'mtbf', type: 'number' },
                  { label: 'MTTR (hours)', key: 'mttr', type: 'number' },
                  { label: 'Failure Distribution', key: 'distribution', type: 'select', options: ['exponential', 'weibull', 'lognormal'] },
                  { label: 'Weibull β (shape)', key: 'beta', type: 'number' },
                  { label: 'Weibull η (scale)', key: 'eta', type: 'number' },
                  { label: 'Repair Distribution', key: 'repairDistribution', type: 'select', options: ['lognormal', 'exponential', 'fixed'] },
                  { label: 'Logistic Delay (hrs)', key: 'logisticDelay', type: 'number' },
                  { label: 'PM Interval (hrs)', key: 'pmInterval', type: 'number' },
                  { label: 'PM Duration (hrs)', key: 'pmDuration', type: 'number' },
                  { label: 'Deferred Effect (hrs)', key: 'deferredEffect', type: 'number' },
                  { label: 'Redundant Group', key: 'redundantGroup', type: 'text' },
                  { label: 'K Required (k-of-n)', key: 'kRequired', type: 'number' },
                  { label: 'Cost/hr Downtime', key: 'costPerHour', type: 'number' },
                  { label: 'Data Source', key: 'dataSource', type: 'select', options: ['Optimal Reference Library', 'Asset History', 'OREDA', 'Vendor Data', 'Engineering Judgment'] }
                ].map(field => (
                  <div key={field.key} style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '3px' }}>{field.label}</label>
                    {field.type === 'select' ? (
                      <select value={sb[field.key] || ''} onChange={e => updateSubBlock(sb.id, { [field.key]: e.target.value })}
                        style={{ width: '100%', padding: '5px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }}>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={field.type} value={sb[field.key] || ''} onChange={e => updateSubBlock(sb.id, { [field.key]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
                        style={{ width: '100%', padding: '5px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                    )}
                  </div>
                ))}
                <button onClick={() => deleteSubBlock(sb.id)} style={{ width: '100%', padding: '6px', backgroundColor: 'transparent', color: COLORS.ERROR || '#B91C1C', border: `1px solid ${COLORS.ERROR || '#B91C1C'}`, borderRadius: '3px', fontSize: '11px', cursor: 'pointer', marginTop: '8px' }}>Delete Block</button>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MULTI-STATE BLOCK EDITOR — Configure degraded performance states
// ============================================================================
function MultiStateEditor({ block, onUpdateBlock, onClose }) {
  const [blockStates, setBlockStates] = useState(block.states || [
    { name: 'Full', capacity: 100, probability: 0.85 },
    { name: 'Degraded', capacity: 60, probability: 0.10 },
    { name: 'Failed', capacity: 0, probability: 0.05 }
  ]);

  const totalProb = blockStates.reduce((s, st) => s + (st.probability || 0), 0);

  const addState = () => {
    setBlockStates([...blockStates, { name: `State ${blockStates.length + 1}`, capacity: 50, probability: 0 }]);
  };

  const removeState = (idx) => {
    setBlockStates(blockStates.filter((_, i) => i !== idx));
  };

  const updateState = (idx, field, value) => {
    const updated = [...blockStates];
    updated[idx] = { ...updated[idx], [field]: value };
    setBlockStates(updated);
  };

  const save = () => {
    onUpdateBlock(block.id, { states: blockStates });
    onClose();
  };

  const stateBarColors = ['#2A9D8F', '#00A8CB', '#E9C46A', '#F4A261', '#E76F51', '#B91C1C'];

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '650px', backgroundColor: COLORS.PANEL_BG, borderRadius: '8px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '15px', color: COLORS.TEXT }}>Multi-State Configuration: {block.description}</div>
            <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Define performance states beyond binary operational/failed</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={save} style={{ padding: '6px 14px', backgroundColor: COLORS.GOLD, color: '#FFF', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>Save</button>
            <button onClick={onClose} style={{ padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
        <div style={{ padding: '20px' }}>
          {/* Probability bar visualisation */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>State Probability Distribution</div>
            <div style={{ display: 'flex', height: '24px', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${COLORS.BORDER}` }}>
              {blockStates.map((st, i) => (
                <div key={i} style={{ width: `${(st.probability / (totalProb || 1)) * 100}%`, backgroundColor: stateBarColors[i % stateBarColors.length], display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: st.probability > 0 ? '20px' : '0' }}>
                  <span style={{ fontSize: '8px', color: '#FFF', fontWeight: '600' }}>{st.name}</span>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'right', fontSize: '10px', color: Math.abs(totalProb - 1) < 0.01 ? '#2A9D8F' : COLORS.ERROR || '#B91C1C', marginTop: '4px' }}>
              Σ = {totalProb.toFixed(4)} {Math.abs(totalProb - 1) < 0.01 ? '✓' : '(should sum to 1.0)'}
            </div>
          </div>

          {/* States table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr>
                {['', 'State Name', 'Capacity %', 'Probability', 'Output Rate', ''].map(h => (
                  <th key={h} style={{ padding: '8px', textAlign: 'left', fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${COLORS.BORDER}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blockStates.map((st, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: stateBarColors[idx % stateBarColors.length] }} />
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <input value={st.name} onChange={e => updateState(idx, 'name', e.target.value)}
                      style={{ width: '100%', padding: '4px 6px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <input type="number" value={st.capacity} min={0} max={100} onChange={e => updateState(idx, 'capacity', parseFloat(e.target.value) || 0)}
                      style={{ width: '80px', padding: '4px 6px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <input type="number" value={st.probability} min={0} max={1} step={0.01} onChange={e => updateState(idx, 'probability', parseFloat(e.target.value) || 0)}
                      style={{ width: '80px', padding: '4px 6px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: '11px', color: COLORS.TEXT }}>{(st.capacity * (st.probability || 0)).toFixed(1)}%</td>
                  <td style={{ padding: '6px 8px' }}>
                    {blockStates.length > 2 && (
                      <button onClick={() => removeState(idx)} style={{ padding: '2px 6px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '2px', fontSize: '10px', cursor: 'pointer' }}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addState} style={{ marginTop: '12px', padding: '6px 14px', backgroundColor: 'transparent', color: COLORS.NAVY, border: `1px solid ${COLORS.NAVY}`, borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>+ Add State</button>
          <div style={{ marginTop: '16px', padding: '10px', backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '11px', color: COLORS.TEXT_DIM }}>
            <strong style={{ color: COLORS.TEXT }}>Expected Output:</strong> {blockStates.reduce((s, st) => s + st.capacity * (st.probability || 0), 0).toFixed(1)}% — weighted average capacity across all states.
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAINTENANCE STRATEGY BUILDER — Combined multi-task strategies & skill matrix
// ============================================================================
function MaintenanceStrategyBuilder({ projectData, setProjectData }) {
  const [selectedFM, setSelectedFM] = useState(null);
  const [activePanel, setActivePanel] = useState('strategies'); // strategies | labour
  const failureModes = projectData.failuremodes || [];
  const strategies = projectData.maintenanceStrategies || [];
  const labourSkills = projectData.labourSkills || [
    { id: 'SK-1', name: 'Mechanical', rate: 85, available: 4 },
    { id: 'SK-2', name: 'Electrical', rate: 95, available: 3 },
    { id: 'SK-3', name: 'Instrumentation', rate: 110, available: 2 },
    { id: 'SK-4', name: 'Condition Monitoring', rate: 120, available: 2 }
  ];

  const taskTypes = [
    { id: 'cm', label: 'Condition Monitoring', icon: '📊', color: '#2A9D8F' },
    { id: 'td', label: 'Time-Directed', icon: '🔧', color: '#457B9D' },
    { id: 'ff', label: 'Failure Finding', icon: '🔍', color: '#E9C46A' },
    { id: 'rtf', label: 'Run to Failure', icon: '⚡', color: '#E76F51' },
    { id: 'rd', label: 'Redesign', icon: '✏️', color: '#7B61FF' }
  ];

  const getStrategyForFM = (fmId) => strategies.find(s => s.failureModeId === fmId) || { failureModeId: fmId, tasks: [], assignedSkills: [] };

  const updateStrategy = (fmId, updates) => {
    const existing = strategies.findIndex(s => s.failureModeId === fmId);
    const updatedStrategies = [...strategies];
    if (existing >= 0) {
      updatedStrategies[existing] = { ...updatedStrategies[existing], ...updates };
    } else {
      updatedStrategies.push({ failureModeId: fmId, tasks: [], assignedSkills: [], ...updates });
    }
    setProjectData(prev => ({ ...prev, maintenanceStrategies: updatedStrategies }));
  };

  const addTaskToStrategy = (fmId, taskType) => {
    const strat = getStrategyForFM(fmId);
    const newTask = { id: `T-${Date.now()}`, type: taskType, interval: 720, duration: 4, description: `${taskTypes.find(t => t.id === taskType)?.label || taskType} task`, requiredSkills: [] };
    updateStrategy(fmId, { tasks: [...(strat.tasks || []), newTask] });
  };

  const removeTask = (fmId, taskId) => {
    const strat = getStrategyForFM(fmId);
    updateStrategy(fmId, { tasks: strat.tasks.filter(t => t.id !== taskId) });
  };

  const updateLabourSkills = (skills) => {
    setProjectData(prev => ({ ...prev, labourSkills: skills }));
  };

  return (
    <div style={{ display: 'flex', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Left: Failure modes list */}
      <div style={{ width: '280px', borderRight: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: '14px', color: COLORS.TEXT }}>Maintenance Strategies</div>
          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>Assign combined task packages per failure mode</div>
        </div>
        <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.BORDER}` }}>
          {[{ id: 'strategies', label: 'Strategies' }, { id: 'labour', label: 'Labour Skills' }].map(tab => (
            <button key={tab.id} onClick={() => setActivePanel(tab.id)} style={{
              flex: 1, padding: '8px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em',
              backgroundColor: activePanel === tab.id ? COLORS.TEAL_LIGHT || '#E8F4F3' : 'transparent',
              color: activePanel === tab.id ? COLORS.TEAL || '#2A9D8F' : COLORS.TEXT_DIM,
              border: 'none', borderBottom: activePanel === tab.id ? '2px solid #2A9D8F' : '2px solid transparent',
              cursor: 'pointer', fontWeight: activePanel === tab.id ? '600' : '400'
            }}>{tab.label}</button>
          ))}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
          {activePanel === 'strategies' && failureModes.map(fm => {
            const strat = getStrategyForFM(fm.id);
            return (
              <div key={fm.id} onClick={() => setSelectedFM(fm.id)} style={{
                padding: '10px 12px', marginBottom: '4px', borderRadius: '4px', cursor: 'pointer',
                backgroundColor: selectedFM === fm.id ? 'rgba(0,168,203,0.12)' : 'transparent',
                border: `1px solid ${selectedFM === fm.id ? 'rgba(0,168,203,0.35)' : 'transparent'}`,
                transition: 'all 0.15s'
              }}>
                <div style={{ fontSize: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{fm.description || fm.id}</div>
                <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>
                  {(strat.tasks || []).length} task(s) assigned
                  {(strat.tasks || []).length > 0 && (
                    <span style={{ marginLeft: '6px' }}>
                      {(strat.tasks || []).map(t => taskTypes.find(tt => tt.id === t.type)?.icon || '•').join('')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {activePanel === 'strategies' && failureModes.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>No failure modes defined. Add them in the Project Structure.</div>
          )}
          {activePanel === 'labour' && (
            <div>
              {labourSkills.map((skill, idx) => (
                <div key={skill.id} style={{ padding: '10px 12px', marginBottom: '6px', backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px' }}>
                  <input value={skill.name} onChange={e => { const u = [...labourSkills]; u[idx] = { ...u[idx], name: e.target.value }; updateLabourSkills(u); }}
                    style={{ width: '100%', padding: '4px 6px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px', marginBottom: '6px', fontWeight: '600' }} />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>Rate ($/hr)</label>
                      <input type="number" value={skill.rate} onChange={e => { const u = [...labourSkills]; u[idx] = { ...u[idx], rate: parseFloat(e.target.value) || 0 }; updateLabourSkills(u); }}
                        style={{ width: '100%', padding: '3px 6px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>Available</label>
                      <input type="number" value={skill.available} onChange={e => { const u = [...labourSkills]; u[idx] = { ...u[idx], available: parseInt(e.target.value) || 0 }; updateLabourSkills(u); }}
                        style={{ width: '100%', padding: '3px 6px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={() => updateLabourSkills([...labourSkills, { id: `SK-${Date.now()}`, name: 'New Skill', rate: 80, available: 1 }])}
                style={{ width: '100%', padding: '6px', backgroundColor: 'transparent', color: COLORS.NAVY, border: `1px solid ${COLORS.NAVY}`, borderRadius: '4px', fontSize: '11px', cursor: 'pointer', marginTop: '8px' }}>+ Add Skill Type</button>
            </div>
          )}
        </div>
      </div>

      {/* Right: Strategy editor */}
      <div style={{ flex: 1, padding: '20px', overflow: 'auto' }}>
        {selectedFM ? (() => {
          const fm = failureModes.find(f => f.id === selectedFM);
          const strat = getStrategyForFM(selectedFM);
          if (!fm) return null;
          return (
            <div>
              <div style={{ fontFamily: "'Georgia', serif", fontSize: '15px', color: COLORS.TEXT, marginBottom: '4px' }}>{fm.description || fm.id}</div>
              <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '20px' }}>Combined maintenance strategy — assign multiple task types to address this failure mode</div>

              {/* Task type buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {taskTypes.map(tt => (
                  <button key={tt.id} onClick={() => addTaskToStrategy(selectedFM, tt.id)} style={{
                    padding: '8px 14px', backgroundColor: 'transparent', color: tt.color, border: `1px solid ${tt.color}`, borderRadius: '4px', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    <span>{tt.icon}</span> Add {tt.label}
                  </button>
                ))}
              </div>

              {/* Assigned tasks */}
              {(strat.tasks || []).length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', backgroundColor: COLORS.PANEL_BG, border: `1px dashed ${COLORS.BORDER}`, borderRadius: '6px', color: COLORS.TEXT_DIM, fontSize: '12px' }}>
                  No tasks assigned. Click a task type above to add one.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(strat.tasks || []).map((task, tIdx) => {
                    const tt = taskTypes.find(t => t.id === task.type);
                    return (
                      <div key={task.id} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', borderLeft: `4px solid ${tt?.color || COLORS.BORDER}`, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px' }}>{tt?.icon}</span>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT }}>{tt?.label || task.type}</span>
                            <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Task {tIdx + 1}</span>
                          </div>
                          <button onClick={() => removeTask(selectedFM, task.id)} style={{ padding: '2px 8px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '2px', fontSize: '10px', cursor: 'pointer' }}>Remove</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', marginBottom: '3px' }}>Description</label>
                            <input value={task.description || ''} onChange={e => {
                              const tasks = [...strat.tasks]; tasks[tIdx] = { ...tasks[tIdx], description: e.target.value };
                              updateStrategy(selectedFM, { tasks });
                            }} style={{ width: '100%', padding: '5px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', marginBottom: '3px' }}>Interval (hours)</label>
                            <input type="number" value={task.interval || 720} onChange={e => {
                              const tasks = [...strat.tasks]; tasks[tIdx] = { ...tasks[tIdx], interval: parseFloat(e.target.value) || 0 };
                              updateStrategy(selectedFM, { tasks });
                            }} style={{ width: '100%', padding: '5px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', marginBottom: '3px' }}>Duration (hours)</label>
                            <input type="number" value={task.duration || 4} onChange={e => {
                              const tasks = [...strat.tasks]; tasks[tIdx] = { ...tasks[tIdx], duration: parseFloat(e.target.value) || 0 };
                              updateStrategy(selectedFM, { tasks });
                            }} style={{ width: '100%', padding: '5px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }} />
                          </div>
                        </div>
                        {/* Skill assignment */}
                        <div style={{ marginTop: '10px' }}>
                          <label style={{ display: 'block', fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', marginBottom: '4px' }}>Required Skills</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {labourSkills.map(skill => {
                              const assigned = (task.requiredSkills || []).includes(skill.id);
                              return (
                                <button key={skill.id} onClick={() => {
                                  const tasks = [...strat.tasks];
                                  const skills = assigned ? (task.requiredSkills || []).filter(s => s !== skill.id) : [...(task.requiredSkills || []), skill.id];
                                  tasks[tIdx] = { ...tasks[tIdx], requiredSkills: skills };
                                  updateStrategy(selectedFM, { tasks });
                                }} style={{
                                  padding: '3px 10px', borderRadius: '12px', fontSize: '10px', cursor: 'pointer', border: 'none',
                                  backgroundColor: assigned ? COLORS.NAVY : COLORS.LIGHT_BG,
                                  color: assigned ? '#FFF' : COLORS.TEXT_DIM, fontWeight: assigned ? '600' : '400'
                                }}>{skill.name}</button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Strategy summary */}
              {(strat.tasks || []).length > 0 && (
                <div style={{ marginTop: '20px', padding: '14px 16px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
                  <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Strategy Summary</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', fontSize: '12px' }}>
                    <div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Total Tasks</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: COLORS.TEXT }}>{strat.tasks.length}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Min Interval</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: COLORS.TEXT }}>{Math.min(...strat.tasks.map(t => t.interval || 720))}h</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Total Duration</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: COLORS.TEXT }}>{strat.tasks.reduce((s, t) => s + (t.duration || 0), 0)}h</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>Skills Required</div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: COLORS.TEXT }}>{new Set(strat.tasks.flatMap(t => t.requiredSkills || [])).size}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })() : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.TEXT_DIM }}>
            <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }}>🔧</div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '14px', marginBottom: '4px' }}>Select a Failure Mode</div>
            <div style={{ fontSize: '11px' }}>Choose a failure mode from the left panel to build its maintenance strategy</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SPARE CRITICALITY VIEW — Linkage to failure modes + repair vs replace
// ============================================================================
function SpareCriticalityView({ projectData, setProjectData }) {
  const spares = projectData.spares || [];
  const failureModes = projectData.failuremodes || [];
  const equipments = projectData.equipments || [];
  const [selectedSpare, setSelectedSpare] = useState(null);
  const [showBomPanel, setShowBomPanel] = useState(false);

  const getLinkedFailureModes = (spareId) => {
    return failureModes.filter(fm => (fm.spareRequirement || '') === spareId || (fm.linkedSpares || []).includes(spareId));
  };

  const getLinkedEquipment = (spareId) => {
    return equipments.filter(e => e.spareReq === spareId || (e.spareReq && e.spareReq.includes(spareId)));
  };

  const updateSpare = (spareId, updates) => {
    setProjectData(prev => ({
      ...prev,
      spares: (prev.spares || []).map(s => s.id === spareId ? { ...s, ...updates } : s)
    }));
  };

  const computeRepairVsReplace = (spare) => {
    const repairCost = parseFloat(spare.repairCost) || (parseFloat(spare.unitCost || spare.cost || 0) * 0.4);
    const replaceCost = parseFloat(spare.unitCost) || parseFloat(spare.cost) || 0;
    const repairTime = parseFloat(spare.repairTime) || 12;
    const replaceTime = parseFloat(spare.replaceTime) || 4;
    const downtimeCost = parseFloat(spare.downtimeCostPerHour) || 500;
    const totalRepair = repairCost + (repairTime * downtimeCost);
    const totalReplace = replaceCost + (replaceTime * downtimeCost);
    const recommendation = totalRepair <= totalReplace ? 'repair' : 'replace';
    const savings = Math.abs(totalReplace - totalRepair);
    return { repairCost, replaceCost, repairTime, replaceTime, downtimeCost, totalRepair, totalReplace, recommendation, savings };
  };

  // Enhanced criticality using 4-tier engine
  const criticalityData = (spare) => {
    const linkedFMs = getLinkedFailureModes(spare.id);
    const linkedEqs = getLinkedEquipment(spare.id);
    const maxSeverity = linkedFMs.length > 0 ? Math.max(...linkedFMs.map(fm => parseInt(fm.severity) || 5)) : 5;
    const demandRate = linkedFMs.reduce((sum, fm) => sum + (parseFloat(fm.failureRate) || 0.001), 0);
    const leadTime = parseFloat(spare.leadTime) || 30;
    const ciScore = (maxSeverity * demandRate * leadTime).toFixed(2);

    // 4-tier classification
    const tier = classifyCriticality(spare, linkedFMs);
    const policy = DEFAULT_STOCKING_POLICY[tier];

    return {
      score: ciScore, maxSeverity, demandRate: demandRate.toFixed(4), leadTime,
      linkedCount: linkedFMs.length, linkedEqCount: linkedEqs.length,
      tier, tierLabel: policy.label, tierColor: policy.color, tierBg: policy.bgColor,
      serviceLevel: policy.serviceLevelPct, action: policy.action
    };
  };

  // Group spares by tier for summary
  const tierSummary = useMemo(() => {
    const summary = {};
    Object.keys(DEFAULT_STOCKING_POLICY).forEach(t => { summary[t] = 0; });
    spares.forEach(sp => {
      const tier = classifyCriticality(sp, getLinkedFailureModes(sp.id));
      summary[tier] = (summary[tier] || 0) + 1;
    });
    return summary;
  }, [spares, failureModes]);

  return (
    <div style={{ display: 'flex', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Left panel: spares ranked by criticality with tier badges */}
      <div style={{ width: '340px', borderRight: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: '14px', color: COLORS.TEXT }}>Spare Criticality</div>
          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>4-tier stocking policy classification</div>
          {/* Tier summary badges */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
            {Object.entries(DEFAULT_STOCKING_POLICY).map(([key, p]) => (
              <span key={key} style={{
                padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '700',
                backgroundColor: p.bgColor, color: p.color, border: `1px solid ${p.color}30`
              }}>{p.label}: {tierSummary[key] || 0}</span>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {spares
            .map(sp => ({ ...sp, crit: criticalityData(sp) }))
            .sort((a, b) => parseFloat(b.crit.score) - parseFloat(a.crit.score))
            .map(spare => (
              <div key={spare.id} onClick={() => setSelectedSpare(spare.id)} style={{
                padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, cursor: 'pointer',
                backgroundColor: selectedSpare === spare.id ? 'rgba(0,168,203,0.12)' : 'transparent'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: COLORS.TEXT, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spare.name || spare.description || spare.id}</div>
                  <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '700', marginLeft: '8px',
                    backgroundColor: spare.crit.tierBg, color: spare.crit.tierColor
                  }}>{spare.crit.tierLabel}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: COLORS.TEXT_DIM }}>
                  <span>CI: {spare.crit.score} • {spare.crit.linkedCount} FM(s)</span>
                  <span>SL: {spare.crit.serviceLevel}%</span>
                </div>
              </div>
            ))}
          {spares.length === 0 && (
            <div style={{ padding: '30px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>No spares defined in project data.</div>
          )}
        </div>
      </div>

      {/* Right panel: detail + repair vs replace + BoM */}
      <div style={{ flex: 1, padding: '20px', overflow: 'auto' }}>
        {selectedSpare ? (() => {
          const spare = spares.find(s => s.id === selectedSpare);
          if (!spare) return null;
          const crit = criticalityData(spare);
          const rvr = computeRepairVsReplace(spare);
          const linkedFMs = getLinkedFailureModes(spare.id);
          const linkedEqs = getLinkedEquipment(spare.id);
          const policy = DEFAULT_STOCKING_POLICY[crit.tier];

          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '4px' }}>{spare.name || spare.description || spare.id}</div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Criticality analysis, stocking policy, and repair/replace decision support</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', backgroundColor: crit.tierBg, color: crit.tierColor, border: `1px solid ${crit.tierColor}30` }}>{crit.tierLabel}</span>
                  <button onClick={() => setShowBomPanel(!showBomPanel)} style={{
                    padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                    border: `1px solid ${COLORS.BORDER}`, backgroundColor: showBomPanel ? COLORS.NAVY : COLORS.PANEL_BG,
                    color: showBomPanel ? '#fff' : COLORS.TEXT_DIM
                  }}>Equipment BoM</button>
                </div>
              </div>

              {/* KPI cards — enhanced with tier and service level */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
                {[
                  { label: 'Criticality Index', value: crit.score, color: parseFloat(crit.score) > 1 ? '#B91C1C' : '#2A9D8F' },
                  { label: 'Max Severity', value: `${crit.maxSeverity}/10`, color: COLORS.NAVY },
                  { label: 'Target Service Level', value: `${crit.serviceLevel}%`, color: crit.tierColor },
                  { label: 'Linked Failure Modes', value: crit.linkedCount, color: '#00A8CB' },
                  { label: 'Linked Equipment', value: crit.linkedEqCount, color: COLORS.GOLD }
                ].map((card, i) => (
                  <div key={i} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '12px', borderLeft: `4px solid ${card.color}` }}>
                    <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, marginBottom: '4px', textTransform: 'uppercase' }}>{card.label}</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: COLORS.TEXT }}>{card.value}</div>
                  </div>
                ))}
              </div>

              {/* Stocking policy detail for this spare */}
              <div style={{ backgroundColor: crit.tierBg, border: `1px solid ${crit.tierColor}30`, borderRadius: '6px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: crit.tierColor }}>{crit.tierLabel} Stocking Policy</span>
                  <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{policy.action.replace(/_/g, ' ')}</span>
                </div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '8px' }}>{policy.description}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontSize: '10px' }}>
                  <div><span style={{ color: COLORS.TEXT_DIM }}>Min Stock: </span><span style={{ fontWeight: '600', color: COLORS.TEXT }}>{policy.minStockRule}</span></div>
                  <div><span style={{ color: COLORS.TEXT_DIM }}>Max Stock: </span><span style={{ fontWeight: '600', color: COLORS.TEXT }}>{policy.maxStockRule}</span></div>
                  <div><span style={{ color: COLORS.TEXT_DIM }}>Service Level: </span><span style={{ fontWeight: '600', color: crit.tierColor }}>{policy.serviceLevelPct}%</span></div>
                  <div><span style={{ color: COLORS.TEXT_DIM }}>Lead Time Tol: </span><span style={{ fontWeight: '600', color: COLORS.TEXT }}>{policy.leadTimeTolDays}d</span></div>
                </div>
              </div>

              {/* Equipment BoM panel (collapsible) */}
              {showBomPanel && (
                <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '12px' }}>Equipment Bill of Materials Linkage</div>
                  {linkedEqs.length > 0 ? (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {linkedEqs.map(eq => (
                        <div key={eq.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px' }}>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '500', color: COLORS.TEXT }}>{eq.name || eq.description || eq.id}</div>
                            <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>
                              {eq.category || 'Equipment'} {eq.weibullBeta ? `• Weibull: β=${eq.weibullBeta} η=${eq.weibullEta}` : ''} {eq.failureRate ? `• λ=${eq.failureRate}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            {eq.weibullBeta && <span style={{ padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '600', backgroundColor: '#2A9D8F15', color: '#2A9D8F' }}>Weibull</span>}
                            <span style={{ padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '600', backgroundColor: COLORS.BLUE + '15', color: COLORS.BLUE }}>BoM linked</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>
                      No equipment linked. Set spareReq on equipment items to create BoM linkage.
                    </div>
                  )}
                </div>
              )}

              {/* Repair vs Replace decision */}
              <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '14px' }}>Repair vs. Replace Decision</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  {/* Repair option */}
                  <div style={{ padding: '14px', backgroundColor: rvr.recommendation === 'repair' ? '#2A9D8F08' : COLORS.LIGHT_BG, border: `2px solid ${rvr.recommendation === 'repair' ? '#2A9D8F' : COLORS.BORDER}`, borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '10px' }}>Repair</div>
                    <div style={{ display: 'grid', gap: '8px', fontSize: '11px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Material Cost</span><span style={{ color: COLORS.TEXT }}>${rvr.repairCost.toLocaleString()}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Repair Time</span><span style={{ color: COLORS.TEXT }}>{rvr.repairTime}h</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Downtime Cost</span><span style={{ color: COLORS.TEXT }}>${(rvr.repairTime * rvr.downtimeCost).toLocaleString()}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${COLORS.BORDER}`, paddingTop: '6px', fontWeight: '700' }}><span>Total</span><span>${rvr.totalRepair.toLocaleString()}</span></div>
                    </div>
                  </div>
                  {/* Replace option */}
                  <div style={{ padding: '14px', backgroundColor: rvr.recommendation === 'replace' ? '#2A9D8F08' : COLORS.LIGHT_BG, border: `2px solid ${rvr.recommendation === 'replace' ? '#2A9D8F' : COLORS.BORDER}`, borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '10px' }}>Replace</div>
                    <div style={{ display: 'grid', gap: '8px', fontSize: '11px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Unit Cost</span><span style={{ color: COLORS.TEXT }}>${rvr.replaceCost.toLocaleString()}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Replace Time</span><span style={{ color: COLORS.TEXT }}>{rvr.replaceTime}h</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Downtime Cost</span><span style={{ color: COLORS.TEXT }}>${(rvr.replaceTime * rvr.downtimeCost).toLocaleString()}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${COLORS.BORDER}`, paddingTop: '6px', fontWeight: '700' }}><span>Total</span><span>${rvr.totalReplace.toLocaleString()}</span></div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '10px 14px', backgroundColor: '#2A9D8F10', border: '1px solid #2A9D8F30', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#2A9D8F' }}>Recommendation: {rvr.recommendation === 'repair' ? 'Repair' : 'Replace'}</div>
                    <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Saves ${rvr.savings.toLocaleString()} per occurrence vs. alternative</div>
                  </div>
                </div>
              </div>

              {/* Cost parameters */}
              <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '14px' }}>Cost Parameters</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {[
                    { label: 'Repair Cost ($)', key: 'repairCost', default: rvr.repairCost },
                    { label: 'Repair Time (hrs)', key: 'repairTime', default: rvr.repairTime },
                    { label: 'Replace Time (hrs)', key: 'replaceTime', default: rvr.replaceTime },
                    { label: 'Downtime Cost ($/hr)', key: 'downtimeCostPerHour', default: rvr.downtimeCost },
                    { label: 'Cannibalization Source', key: 'cannibalizationSource', type: 'text', default: '' },
                    { label: 'Lead Time (days)', key: 'leadTime', default: spare.leadTime || 30 }
                  ].map(field => (
                    <div key={field.key}>
                      <label style={{ display: 'block', fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', marginBottom: '3px' }}>{field.label}</label>
                      <input type={field.type || 'number'} value={spare[field.key] || field.default || ''}
                        onChange={e => updateSpare(spare.id, { [field.key]: field.type === 'text' ? e.target.value : parseFloat(e.target.value) || 0 })}
                        style={{ width: '100%', padding: '5px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }}
                        placeholder={field.type === 'text' ? 'e.g. Equipment-003' : ''} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Linked failure modes */}
              <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '12px' }}>Linked Failure Modes</div>
                {linkedFMs.length > 0 ? linkedFMs.map(fm => (
                  <div key={fm.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px' }}>
                    <span style={{ color: COLORS.TEXT }}>{fm.description || fm.id}</span>
                    <span style={{ color: COLORS.TEXT_DIM }}>Severity: {fm.severity || '—'} | Rate: {fm.failureRate || '—'}</span>
                  </div>
                )) : (
                  <div style={{ padding: '16px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>
                    No failure modes linked. Set spareRequirement on failure modes to link them.
                  </div>
                )}
              </div>
            </div>
          );
        })() : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.TEXT_DIM }}>
            <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }}>📦</div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '14px', marginBottom: '4px' }}>Select a Spare Part</div>
            <div style={{ fontSize: '11px' }}>Choose a spare from the criticality-ranked list to analyse stocking policy and repair vs. replace</div>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditTrailView({ auditLog = [], currentUser }) {
  const [filterEntity, setFilterEntity] = useState('all');
  const entities = [...new Set(auditLog.map(e => e.entity))];
  const filtered = filterEntity === 'all' ? auditLog : auditLog.filter(e => e.entity === filterEntity);

  return (
    <div style={{ padding: '24px 28px', height: '100%', overflow: 'auto' }}>
      <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '4px' }}>Audit Trail</div>
      <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Change history across all entities. Every add, edit and delete is logged.</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filter:</span>
        <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} style={{ fontSize: '11px', padding: '4px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', color: COLORS.TEXT }}>
          <option value="all">All entities</option>
          {entities.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <span style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginLeft: 'auto' }}>{filtered.length} entries</span>
      </div>
      {filtered.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '13px', fontFamily: "'Georgia', serif" }}>
          No changes recorded yet. Changes will appear here as you work.
        </div>
      ) : (
        <div style={{ maxHeight: '600px', overflow: 'auto' }}>
          {filtered.slice(0, 200).map((entry, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', padding: '8px 0', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', alignItems: 'flex-start' }}>
              <div style={{ width: '130px', flexShrink: 0, color: COLORS.TEXT_DIM }}>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}</div>
              <div style={{ width: '60px', flexShrink: 0 }}>
                <span style={{ padding: '2px 6px', borderRadius: '2px', fontSize: '9px', fontWeight: 600, textTransform: 'uppercase',
                  backgroundColor: entry.action === 'add' ? 'rgba(42,157,143,0.1)' : entry.action === 'delete' ? 'rgba(185,28,28,0.1)' : 'rgba(0,47,101,0.06)',
                  color: entry.action === 'add' ? '#2A9D8F' : entry.action === 'delete' ? '#B91C1C' : '#002F65'
                }}>{entry.action}</span>
              </div>
              <div style={{ flex: 1, color: COLORS.TEXT }}>{entry.entity}: {entry.description || entry.itemId}</div>
              <div style={{ width: '80px', flexShrink: 0, color: COLORS.TEXT_DIM, textAlign: 'right' }}>{entry.user || currentUser?.username || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DATA IMPORT HUB — Hierarchy, Maintenance History & Strategy Upload
// ============================================================================
function DataImportHub({ projectData, setProjectData, onClose }) {
  const [step, setStep] = useState(0); // 0=select, 1=upload, 2=map, 3=preview, 4=complete
  const [importType, setImportType] = useState(null);
  const [fileData, setFileData] = useState(null); // { sheets: [{name, headers, rows}], fileName }
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [columnMapping, setColumnMapping] = useState({});
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [mergeMode, setMergeMode] = useState('merge'); // 'merge' | 'replace'
  const fileInputRef = React.useRef(null);

  // Import type definitions
  const IMPORT_TYPES = [
    {
      id: 'hierarchy',
      icon: '🏗',
      title: 'Asset Hierarchy',
      desc: 'Import your plant/facility structure — locations, functional locations, equipment items with parent-child relationships.',
      entities: ['locations', 'equipments'],
      expectedColumns: {
        locations: [
          { key: 'id', label: 'Location ID', required: true, aliases: ['location_id', 'func_loc', 'functional_location', 'floc', 'tag', 'tag_number', 'location'] },
          { key: 'description', label: 'Description', required: true, aliases: ['desc', 'name', 'location_name', 'loc_desc', 'description'] },
          { key: 'parent', label: 'Parent Location', required: false, aliases: ['parent_id', 'parent_loc', 'parent_location', 'superior_floc', 'parent'] },
          { key: 'hierarchyLevel', label: 'Hierarchy Level', required: false, aliases: ['level', 'hier_level', 'hierarchy_level'] },
          { key: 'operationalRule', label: 'Operational Rule', required: false, aliases: ['op_rule', 'rule', 'config', 'operational_rule'] }
        ],
        equipments: [
          { key: 'id', label: 'Equipment ID', required: true, aliases: ['equipment_id', 'equip_id', 'eq_id', 'tag', 'tag_number', 'asset_id', 'equipment'] },
          { key: 'description', label: 'Description', required: true, aliases: ['desc', 'name', 'equip_name', 'equipment_name', 'asset_name', 'description'] },
          { key: 'type', label: 'Equipment Type', required: false, aliases: ['equip_type', 'asset_type', 'category', 'class', 'object_type', 'type'] },
          { key: 'location', label: 'Location / Parent', required: false, aliases: ['func_loc', 'functional_location', 'location_id', 'parent', 'installed_at', 'location'] },
          { key: 'manufacturer', label: 'Manufacturer', required: false, aliases: ['mfr', 'make', 'oem', 'vendor', 'manufacturer'] },
          { key: 'model', label: 'Model', required: false, aliases: ['model_number', 'model_no', 'model'] },
          { key: 'serialNumber', label: 'Serial Number', required: false, aliases: ['serial', 'serial_no', 'sn', 'serial_number'] },
          { key: 'installDate', label: 'Install Date', required: false, aliases: ['install_date', 'commission_date', 'start_date', 'in_service_date'] },
          { key: 'redundancyType', label: 'Redundancy', required: false, aliases: ['redundancy', 'standby', 'config', 'redundancy_type'] },
          { key: 'maxCapacity', label: 'Capacity', required: false, aliases: ['capacity', 'throughput', 'rating', 'max_capacity'] },
          { key: 'criticality', label: 'Criticality', required: false, aliases: ['crit', 'risk_rank', 'priority', 'criticality_rank', 'criticality'] }
        ]
      }
    },
    {
      id: 'history',
      icon: '📋',
      title: 'Maintenance History',
      desc: 'Import work orders, failure records and repair logs — feeds Weibull analysis, failure mode identification and reliability trending.',
      entities: ['failuremodels', 'correctivetasks'],
      expectedColumns: {
        failuremodels: [
          { key: 'id', label: 'Failure Mode ID', required: true, aliases: ['fm_id', 'failure_id', 'mode_id', 'notification', 'notif_no', 'wo_number', 'id'] },
          { key: 'description', label: 'Failure Description', required: true, aliases: ['desc', 'failure_desc', 'fault', 'problem', 'damage', 'failure_text', 'description'] },
          { key: 'equipmentId', label: 'Equipment ID', required: false, aliases: ['equipment', 'equip_id', 'asset_id', 'tag', 'functional_location', 'equipment_id'] },
          { key: 'failureDate', label: 'Failure Date', required: false, aliases: ['date', 'fail_date', 'breakdown_date', 'event_date', 'occurrence_date', 'failure_date'] },
          { key: 'repairDate', label: 'Repair Completion Date', required: false, aliases: ['end_date', 'completion_date', 'restored_date', 'repair_date'] },
          { key: 'distribution', label: 'Distribution', required: false, aliases: ['dist', 'distribution'] },
          { key: 'eta', label: 'Eta (Characteristic Life)', required: false, aliases: ['characteristic_life', 'scale', 'eta'] },
          { key: 'beta', label: 'Beta (Shape)', required: false, aliases: ['shape', 'beta'] },
          { key: 'mttf', label: 'MTTF (hours)', required: false, aliases: ['mean_time_to_failure', 'mtbf', 'mttf'] },
          { key: 'mttr', label: 'MTTR (hours)', required: false, aliases: ['mean_time_to_repair', 'repair_time', 'downtime', 'mttr'] },
          { key: 'failureMechanism', label: 'Failure Mechanism', required: false, aliases: ['mechanism', 'cause', 'root_cause', 'failure_cause', 'damage_code', 'failure_mechanism'] },
          { key: 'severity', label: 'Severity', required: false, aliases: ['sev', 'impact', 'consequence', 'priority', 'severity'] }
        ],
        correctivetasks: [
          { key: 'id', label: 'Work Order ID', required: true, aliases: ['wo_id', 'wo_number', 'order_number', 'work_order', 'id'] },
          { key: 'description', label: 'Work Description', required: true, aliases: ['desc', 'work_desc', 'task_desc', 'short_text', 'description'] },
          { key: 'failureModel', label: 'Linked Failure Mode', required: false, aliases: ['failure_id', 'fm_id', 'notification', 'failure_model'] },
          { key: 'repairDuration', label: 'Repair Duration (hrs)', required: false, aliases: ['duration', 'actual_duration', 'downtime', 'ttr', 'repair_duration'] },
          { key: 'repairCost', label: 'Repair Cost', required: false, aliases: ['cost', 'actual_cost', 'total_cost', 'repair_cost'] },
          { key: 'laborReq', label: 'Craft / Trade', required: false, aliases: ['craft', 'trade', 'skill', 'labor', 'labor_req'] },
          { key: 'spareReq', label: 'Spare Part Used', required: false, aliases: ['material', 'part', 'spare', 'component', 'spare_req'] },
          { key: 'logisticDelay', label: 'Logistic Delay (hrs)', required: false, aliases: ['wait_time', 'delay', 'ldt', 'logistic_delay'] }
        ]
      }
    },
    {
      id: 'strategy',
      icon: '🔧',
      title: 'Maintenance Strategy',
      desc: 'Import your existing PM plans, task lists and maintenance schedules — provides the baseline for RCM optimisation.',
      entities: ['scheduledtasks', 'rcmcauses'],
      expectedColumns: {
        scheduledtasks: [
          { key: 'id', label: 'Task ID', required: true, aliases: ['task_id', 'pm_id', 'plan_id', 'task_number', 'id'] },
          { key: 'description', label: 'Task Description', required: true, aliases: ['desc', 'task_desc', 'task_text', 'short_text', 'pm_description', 'description'] },
          { key: 'equipmentId', label: 'Equipment ID', required: false, aliases: ['equipment', 'equip_id', 'asset_id', 'tag', 'functional_location', 'equipment_id'] },
          { key: 'interval', label: 'Interval (hrs)', required: false, aliases: ['frequency', 'cycle', 'pm_interval', 'interval_hours', 'interval'] },
          { key: 'intervalUnit', label: 'Interval Unit', required: false, aliases: ['unit', 'freq_unit', 'cycle_unit', 'interval_unit'] },
          { key: 'duration', label: 'Duration (hrs)', required: false, aliases: ['planned_duration', 'work_duration', 'est_duration', 'duration'] },
          { key: 'cost', label: 'Estimated Cost', required: false, aliases: ['planned_cost', 'est_cost', 'budget', 'cost'] },
          { key: 'taskType', label: 'Task Type', required: false, aliases: ['type', 'category', 'maintenance_type', 'strategy_type', 'task_type'] },
          { key: 'craft', label: 'Craft / Trade', required: false, aliases: ['trade', 'skill', 'labor', 'workforce', 'craft'] },
          { key: 'priority', label: 'Priority', required: false, aliases: ['prio', 'rank', 'priority'] },
          { key: 'lastExecuted', label: 'Last Executed', required: false, aliases: ['last_date', 'last_done', 'last_completion', 'last_executed'] },
          { key: 'nextDue', label: 'Next Due', required: false, aliases: ['next_date', 'due_date', 'next_due'] }
        ],
        rcmcauses: [
          { key: 'id', label: 'RCM Cause ID', required: true, aliases: ['cause_id', 'fmea_id', 'id'] },
          { key: 'description', label: 'Failure Cause', required: true, aliases: ['desc', 'cause', 'failure_mode', 'cause_desc', 'description'] },
          { key: 'equipmentId', label: 'Equipment ID', required: false, aliases: ['equipment', 'equip_id', 'asset_id', 'tag', 'equipment_id'] },
          { key: 'failureModel', label: 'Linked Failure Model', required: false, aliases: ['fm_id', 'failure_id', 'failure_model'] },
          { key: 'consequence', label: 'Consequence Category', required: false, aliases: ['cons', 'impact', 'effect', 'consequence'] },
          { key: 'currentTask', label: 'Current Task', required: false, aliases: ['existing_task', 'pm_task', 'current_strategy', 'current_task'] },
          { key: 'recommendedAction', label: 'Recommended Action', required: false, aliases: ['action', 'recommendation', 'proposed', 'recommended_action'] }
        ]
      }
    },
    {
      id: 'awbx',
      icon: '📦',
      title: 'Library File (.awbx / .json)',
      desc: 'Import an Optimal library file — failure modes, tasks, strategies and component data packaged as a shareable template file.',
      entities: ['failuremodes', 'scheduledtasks', 'correctivetasks'],
      isLibrary: true,
      expectedColumns: {}
    },
    {
      id: 'cmms',
      icon: '🏭',
      title: 'CMMS Export (SAP / Maximo)',
      desc: 'Import directly from SAP PM (IW39, IW28, IP24) or Maximo (Work Orders, PM Records, Job Plans). Auto-detects CMMS column formats.',
      entities: ['equipments', 'failuremodels', 'scheduledtasks', 'correctivetasks'],
      expectedColumns: {
        equipments: [
          { key: 'id', label: 'Equipment ID', required: true, aliases: ['equipment', 'equip_no', 'equnr', 'assetnum', 'asset_number', 'asset_id', 'tag_number', 'id'] },
          { key: 'description', label: 'Description', required: true, aliases: ['desc', 'eqkt', 'equip_desc', 'asset_desc', 'description_en', 'description'] },
          { key: 'type', label: 'Type / Class', required: false, aliases: ['eqart', 'equip_type', 'class_id', 'classstructureid', 'object_type', 'type'] },
          { key: 'location', label: 'Functional Location', required: false, aliases: ['tplnr', 'func_loc', 'functional_location', 'location', 'location_id', 'parent'] },
          { key: 'manufacturer', label: 'Manufacturer', required: false, aliases: ['herst', 'manufacturer', 'mfr', 'vendor'] },
          { key: 'installDate', label: 'Install Date', required: false, aliases: ['inbdt', 'install_date', 'installdate', 'start_date'] },
          { key: 'criticality', label: 'Criticality', required: false, aliases: ['ktext', 'criticality', 'priority', 'risk_rank'] }
        ],
        failuremodels: [
          { key: 'id', label: 'Notification / WO', required: true, aliases: ['qmnum', 'notif_no', 'aufnr', 'wonum', 'work_order', 'notification', 'id'] },
          { key: 'description', label: 'Failure Description', required: true, aliases: ['qmtxt', 'notif_text', 'short_text', 'description_longdescription', 'description'] },
          { key: 'equipmentId', label: 'Equipment', required: false, aliases: ['equnr', 'equipment', 'assetnum', 'equipment_id'] },
          { key: 'failureDate', label: 'Failure Date', required: false, aliases: ['ausvn', 'strmn', 'actstart', 'reportdate', 'failure_date'] },
          { key: 'failureMechanism', label: 'Damage Code', required: false, aliases: ['fecod', 'otgrp', 'failurecode', 'problemcode', 'damage_code', 'failure_mechanism'] },
          { key: 'severity', label: 'Priority', required: false, aliases: ['priok', 'priority', 'wopriority', 'severity'] }
        ],
        scheduledtasks: [
          { key: 'id', label: 'PM Order / Task', required: true, aliases: ['aufnr', 'warpl', 'wonum', 'pmnum', 'task_id', 'id'] },
          { key: 'description', label: 'Task Description', required: true, aliases: ['ktext', 'auftext', 'description', 'pm_desc', 'short_text'] },
          { key: 'equipmentId', label: 'Equipment', required: false, aliases: ['equnr', 'equipment', 'assetnum', 'equipment_id'] },
          { key: 'interval', label: 'Interval', required: false, aliases: ['zykl1', 'cycle', 'frequency', 'pm_interval', 'interval'] },
          { key: 'intervalUnit', label: 'Unit', required: false, aliases: ['zeieh', 'unit_of_measure', 'freq_unit', 'interval_unit'] }
        ],
        correctivetasks: [
          { key: 'id', label: 'Work Order', required: true, aliases: ['aufnr', 'wonum', 'work_order', 'id'] },
          { key: 'description', label: 'Description', required: true, aliases: ['ktext', 'auftext', 'description', 'short_text'] },
          { key: 'repairDuration', label: 'Actual Duration', required: false, aliases: ['idaur', 'actdur', 'actual_duration', 'repair_duration'] },
          { key: 'repairCost', label: 'Actual Cost', required: false, aliases: ['iswrt', 'actlabcost', 'actual_cost', 'repair_cost'] }
        ]
      }
    },
    {
      id: 'pdf',
      icon: '📄',
      title: 'PDF Document',
      desc: 'Extract failure mode data, maintenance schedules, inspection records or equipment lists from PDF documents using intelligent text parsing.',
      entities: ['failuremodes', 'scheduledtasks', 'equipments'],
      isPDF: true,
      expectedColumns: {}
    }
  ];

  // Parse .awbx / JSON library file
  const parseAwbxFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          // Handle awbx-json format from LibraryManager
          if (data.format === 'awbx-json' || data.format === 'awbx-component-json') {
            const results = { failuremodes: [], scheduledtasks: [], correctivetasks: [] };
            const timestamp = Date.now();
            let fmIdx = 0, taskIdx = 0;
            const components = data.components || data.library?.components || {};
            Object.entries(components).forEach(([compName, compData]) => {
              if (compData.failureModes) {
                compData.failureModes.forEach(fm => {
                  fmIdx++;
                  results.failuremodes.push({
                    id: `IMP-FM-${timestamp}-${String(fmIdx).padStart(3, '0')}`,
                    description: `${compName}: ${fm.what || fm.description || compName}`,
                    component: compName,
                    mechanism: fm.mechanism || '',
                    cause: fm.cause || '',
                    severity: fm.severity || 5,
                    source: `Library import: ${file.name}`
                  });
                });
              }
              if (compData.tasks) {
                compData.tasks.forEach(task => {
                  taskIdx++;
                  const taskObj = {
                    id: `IMP-TSK-${timestamp}-${String(taskIdx).padStart(3, '0')}`,
                    description: task.description || `Inspect ${compName}`,
                    component: compName,
                    interval: task.interval || 0,
                    intervalUnit: task.intervalUnit || 'hours',
                    source: `Library import: ${file.name}`
                  };
                  if (task.interval && task.interval > 0) results.scheduledtasks.push(taskObj);
                  else results.correctivetasks.push(taskObj);
                });
              }
            });
            resolve(results);
          } else if (data.equipmentType) {
            // Raw failure library JSON file
            const generated = applyTemplateToProject(data, 'IMPORT', data.equipmentType, { includeFailureModes: true, includeTasks: true });
            resolve(generated);
          } else {
            reject(new Error('Unrecognised library file format'));
          }
        } catch (err) { reject(err); }
      };
      reader.readAsText(file);
    });
  };

  // Parse PDF using pdf.js text extraction
  const parsePDFFile = async (file) => {
    // Load pdf.js from CDN if needed
    if (!window.pdfjsLib) {
      await new Promise((res, rej) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          res();
        };
        script.onerror = rej;
        document.head.appendChild(script);
      });
    }
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pages = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const text = content.items.map(item => item.str).join(' ');
      pages.push(text);
    }
    const fullText = pages.join('\n\n');

    // Intelligent extraction — look for tabular patterns, failure modes, schedules
    const results = { failuremodes: [], scheduledtasks: [], equipments: [] };
    const timestamp = Date.now();
    let fmIdx = 0, taskIdx = 0, eqIdx = 0;

    // Split into lines and look for structured data
    const lines = fullText.split(/[\n\r]+/).map(l => l.trim()).filter(l => l.length > 3);

    // Detect failure mode patterns (numbered lists, "failure mode", "damage mechanism")
    const fmPatterns = /(?:failure\s*mode|damage\s*mechanism|fault|defect|degradation|corrosion|crack|leak|wear|fatigue|erosion)/i;
    const taskPatterns = /(?:inspect|maintain|check|test|replace|repair|lubricate|calibrate|overhaul|clean|monitor|measure)/i;
    const equipPatterns = /(?:pump|valve|motor|compressor|exchanger|vessel|tank|pipe|turbine|generator|transformer|bearing|gearbox|actuator|filter|separator)/i;

    lines.forEach(line => {
      // Extract likely failure modes
      if (fmPatterns.test(line) && line.length > 10 && line.length < 300) {
        fmIdx++;
        results.failuremodes.push({
          id: `PDF-FM-${timestamp}-${String(fmIdx).padStart(3, '0')}`,
          description: line.substring(0, 200),
          source: `PDF import: ${file.name} (auto-extracted)`,
          mechanism: '',
          severity: 5
        });
      }
      // Extract likely tasks
      if (taskPatterns.test(line) && line.length > 10 && line.length < 300 && !fmPatterns.test(line)) {
        taskIdx++;
        results.scheduledtasks.push({
          id: `PDF-TSK-${timestamp}-${String(taskIdx).padStart(3, '0')}`,
          description: line.substring(0, 200),
          source: `PDF import: ${file.name} (auto-extracted)`,
          interval: 8760
        });
      }
      // Extract likely equipment references
      if (equipPatterns.test(line) && line.length > 5 && line.length < 200) {
        eqIdx++;
        results.equipments.push({
          id: `PDF-EQ-${timestamp}-${String(eqIdx).padStart(3, '0')}`,
          description: line.substring(0, 200),
          source: `PDF import: ${file.name} (auto-extracted)`
        });
      }
    });

    return { extracted: results, fullText, pageCount: pdf.numPages, lineCount: lines.length };
  };

  // State for special imports
  const [pdfData, setPdfData] = useState(null);
  const [awbxData, setAwbxData] = useState(null);

  // Handle special file types
  const handleSpecialFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      if (importType === 'awbx') {
        const data = await parseAwbxFile(file);
        setAwbxData(data);
        setImportPreview(data);
        setStep(3);
      } else if (importType === 'pdf') {
        const data = await parsePDFFile(file);
        setPdfData(data);
        setImportPreview(data.extracted);
        setStep(3);
      }
    } catch (err) {
      alert('Error processing file: ' + err.message);
    }
  };

  // Auto-map columns based on aliases
  const autoMapColumns = (headers, entityCols) => {
    const mapping = {};
    entityCols.forEach(col => {
      const allAliases = [col.key, col.label, ...col.aliases].map(a => a.toLowerCase().replace(/[^a-z0-9]/g, ''));
      const matchIdx = headers.findIndex(h => {
        const normalised = h.toLowerCase().replace(/[^a-z0-9]/g, '');
        return allAliases.includes(normalised);
      });
      if (matchIdx >= 0) mapping[col.key] = headers[matchIdx];
    });
    return mapping;
  };

  // Parse file (CSV or Excel) using SheetJS loaded from CDN
  const parseFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          // Load SheetJS from CDN if not loaded
          if (!window.XLSX) {
            await new Promise((res, rej) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
              script.onload = res;
              script.onerror = rej;
              document.head.appendChild(script);
            });
          }
          const wb = window.XLSX.read(data, { type: 'array', cellDates: true });
          const sheets = wb.SheetNames.map(name => {
            const ws = wb.Sheets[name];
            const json = window.XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
            const headers = json[0] ? json[0].map(h => String(h).trim()) : [];
            const rows = json.slice(1).filter(r => r.some(c => c !== ''));
            return { name, headers, rows };
          });
          resolve({ sheets, fileName: file.name });
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const parsed = await parseFile(file);
      setFileData(parsed);
      setSelectedSheet(0);
      // Auto-map first entity
      if (parsed.sheets.length > 0) {
        const importDef = IMPORT_TYPES.find(t => t.id === importType);
        const firstEntity = importDef.entities[0];
        const cols = importDef.expectedColumns[firstEntity];
        const autoMap = autoMapColumns(parsed.sheets[0].headers, cols);
        setColumnMapping({ [firstEntity]: autoMap });
        // If there's a second sheet and second entity, auto-map that too
        if (parsed.sheets.length > 1 && importDef.entities.length > 1) {
          const secondEntity = importDef.entities[1];
          const cols2 = importDef.expectedColumns[secondEntity];
          const autoMap2 = autoMapColumns(parsed.sheets[1].headers, cols2);
          setColumnMapping(prev => ({ ...prev, [secondEntity]: autoMap2 }));
        }
      }
      setStep(2);
    } catch (err) {
      alert('Error reading file: ' + err.message);
    }
  };

  // Build preview from mapped data
  const buildPreview = () => {
    const importDef = IMPORT_TYPES.find(t => t.id === importType);
    const preview = {};
    importDef.entities.forEach((entity, ei) => {
      const sheetIdx = Math.min(ei, (fileData?.sheets?.length || 1) - 1);
      const sheet = fileData?.sheets?.[sheetIdx];
      if (!sheet) { preview[entity] = []; return; }
      const mapping = columnMapping[entity] || {};
      const cols = importDef.expectedColumns[entity];
      const records = sheet.rows.map((row, ri) => {
        const record = {};
        cols.forEach(col => {
          const sourceHeader = mapping[col.key];
          if (sourceHeader) {
            const colIdx = sheet.headers.indexOf(sourceHeader);
            if (colIdx >= 0) record[col.key] = row[colIdx] ?? '';
          }
        });
        // Generate ID if not mapped
        if (!record.id) record.id = `${entity.substring(0, 3).toUpperCase()}-${String(ri + 1).padStart(4, '0')}`;
        return record;
      }).filter(r => r.description || r.id);
      preview[entity] = records;
    });
    setImportPreview(preview);
  };

  // Execute import
  const executeImport = () => {
    const updated = { ...projectData };
    const stats = {};
    Object.entries(importPreview).forEach(([entity, records]) => {
      if (mergeMode === 'replace') {
        updated[entity] = records;
      } else {
        const existingIds = new Set((updated[entity] || []).map(r => r.id));
        const newRecords = records.filter(r => !existingIds.has(r.id));
        const updatedExisting = (updated[entity] || []).map(existing => {
          const incoming = records.find(r => r.id === existing.id);
          return incoming ? { ...existing, ...incoming } : existing;
        });
        updated[entity] = [...updatedExisting, ...newRecords];
      }
      stats[entity] = records.length;
    });
    setProjectData(updated);
    setImportResult(stats);
    setStep(4);
  };

  // Step badge style
  const stepBadge = (num, label, active) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: active ? 1 : 0.35 }}>
      <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: active ? COLORS.TEAL : 'rgba(0,47,101,0.08)',
        color: active ? '#fff' : COLORS.TEXT_DIM, fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{num}</div>
      <span style={{ fontSize: '11px', fontWeight: active ? 600 : 400, color: active ? COLORS.TEXT : COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    </div>
  );

  const btnStyle = (primary) => ({
    padding: '9px 20px', fontSize: '11px', fontWeight: 600, border: primary ? 'none' : `1px solid ${COLORS.BORDER}`,
    borderRadius: '3px', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase',
    backgroundColor: primary ? COLORS.TEAL : 'transparent', color: primary ? '#fff' : COLORS.TEXT,
    fontFamily: "'Georgia', serif", transition: 'all 0.2s ease'
  });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Header */}
      <div style={{ padding: '20px 28px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '9px', fontWeight: 600, color: 'rgba(0,47,101,0.4)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '4px' }}>Optimal® Availability Studio</div>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: '18px', fontWeight: 400, color: COLORS.TEXT }}>Data Import Hub</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {stepBadge(1, 'Source', step >= 0)}
          <div style={{ width: '20px', height: '1px', backgroundColor: COLORS.BORDER }} />
          {stepBadge(2, 'Upload', step >= 1)}
          <div style={{ width: '20px', height: '1px', backgroundColor: COLORS.BORDER }} />
          {stepBadge(3, 'Map', step >= 2)}
          <div style={{ width: '20px', height: '1px', backgroundColor: COLORS.BORDER }} />
          {stepBadge(4, 'Import', step >= 3)}
          {onClose && <button onClick={onClose} style={{ padding: '6px 12px', fontSize: '10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', cursor: 'pointer', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, marginLeft: '16px' }}>✕ Close</button>}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 32px' }}>

        {/* Step 0: Select Import Type */}
        {step === 0 && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '6px' }}>What data would you like to import?</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '24px', maxWidth: '560px', lineHeight: 1.55 }}>
              Select the type of data you are importing. Each option provides intelligent column mapping for common CMMS export formats including SAP PM, Maximo, Meridium, and generic CSV/Excel.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', maxWidth: '780px' }}>
              {IMPORT_TYPES.map(t => (
                <div key={t.id} onClick={() => { setImportType(t.id); setStep(1); }}
                  style={{ padding: '28px 24px', backgroundColor: '#FFFFFF', border: `1px solid ${importType === t.id ? COLORS.TEAL : COLORS.BORDER}`,
                    borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s ease', position: 'relative' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.TEAL}
                  onMouseLeave={e => { if (importType !== t.id) e.currentTarget.style.borderColor = COLORS.BORDER; }}>
                  <div style={{ fontSize: '28px', marginBottom: '14px' }}>{t.icon}</div>
                  <div style={{ fontFamily: "'Georgia', serif", fontSize: '15px', fontWeight: 400, color: COLORS.TEXT, marginBottom: '8px' }}>{t.title}</div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, lineHeight: 1.55 }}>{t.desc}</div>
                  <div style={{ marginTop: '14px', fontSize: '10px', color: COLORS.TEAL, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {t.entities.map(e => e).join(' + ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Upload File */}
        {step === 1 && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '6px' }}>
              Upload your {IMPORT_TYPES.find(t => t.id === importType)?.title} data
            </div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '24px', maxWidth: '560px', lineHeight: 1.55 }}>
              {importType === 'awbx' ? 'Select an .awbx, .json, or library export file containing failure modes, tasks and component data.'
                : importType === 'pdf' ? 'Select a PDF document. Text will be extracted and intelligently parsed for failure modes, tasks and equipment data.'
                : 'Drag and drop or select a file. Supported formats: Excel (.xlsx, .xls), CSV (.csv), or tab-separated (.tsv). For multi-entity imports, use separate sheets in a single workbook.'}
            </div>
            <div onClick={() => fileInputRef.current?.click()}
              style={{ maxWidth: '480px', padding: '48px 32px', backgroundColor: '#FFFFFF', border: `2px dashed ${COLORS.BORDER}`,
                borderRadius: '4px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s ease' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.TEAL}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.BORDER}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = COLORS.TEAL; e.currentTarget.style.backgroundColor = COLORS.TEAL_LIGHT; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = COLORS.BORDER; e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = COLORS.BORDER; e.currentTarget.style.backgroundColor = '#FFFFFF';
                const file = e.dataTransfer.files[0]; if (file) { const fakeEvt = { target: { files: [file] } };
                  if (importType === 'awbx' || importType === 'pdf') handleSpecialFileUpload(fakeEvt); else handleFileUpload(fakeEvt); }
              }}>
              <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.4 }}>
                {importType === 'awbx' ? '📦' : importType === 'pdf' ? '📄' : '📁'}
              </div>
              <div style={{ fontFamily: "'Georgia', serif", fontSize: '14px', color: COLORS.TEXT, marginBottom: '6px' }}>Drop file here or click to browse</div>
              <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>
                {importType === 'awbx' ? '.awbx, .json' : importType === 'pdf' ? '.pdf' : '.xlsx, .xls, .csv, .tsv'}
              </div>
            </div>
            <input ref={fileInputRef} type="file"
              accept={importType === 'awbx' ? '.awbx,.json' : importType === 'pdf' ? '.pdf' : '.xlsx,.xls,.csv,.tsv'}
              style={{ display: 'none' }}
              onChange={importType === 'awbx' || importType === 'pdf' ? handleSpecialFileUpload : handleFileUpload} />
            <div style={{ marginTop: '16px' }}>
              <button onClick={() => setStep(0)} style={btnStyle(false)}>← Back</button>
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 2 && fileData && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '4px' }}>Map columns from your file</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '20px', maxWidth: '640px', lineHeight: 1.55 }}>
              File: <strong>{fileData.fileName}</strong> — {fileData.sheets.length} sheet(s), {fileData.sheets.reduce((s, sh) => s + sh.rows.length, 0)} total rows.
              Columns have been auto-mapped where possible. Adjust any incorrect mappings below.
            </div>
            {(() => {
              const importDef = IMPORT_TYPES.find(t => t.id === importType);
              return importDef.entities.map((entity, ei) => {
                const sheetIdx = Math.min(ei, fileData.sheets.length - 1);
                const sheet = fileData.sheets[sheetIdx];
                const cols = importDef.expectedColumns[entity];
                const mapping = columnMapping[entity] || {};
                return (
                  <div key={entity} style={{ marginBottom: '28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{entity}</div>
                      {fileData.sheets.length > 1 && (
                        <select value={sheetIdx} onChange={e => { /* sheet assignment handled by index */ }}
                          style={{ fontSize: '11px', padding: '4px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', color: COLORS.TEXT }}>
                          {fileData.sheets.map((s, si) => <option key={si} value={si}>Sheet: {s.name}</option>)}
                        </select>
                      )}
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{Object.keys(mapping).length} / {cols.length} mapped</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr 200px', gap: '6px', fontSize: '11px' }}>
                      <div style={{ fontWeight: 600, color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 0', borderBottom: `1px solid ${COLORS.BORDER}` }}>Field</div>
                      <div style={{ fontWeight: 600, color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 0', borderBottom: `1px solid ${COLORS.BORDER}` }}>Source Column</div>
                      <div style={{ fontWeight: 600, color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 0', borderBottom: `1px solid ${COLORS.BORDER}` }}>Sample Value</div>
                      {cols.map(col => {
                        const selectedHeader = mapping[col.key] || '';
                        const sampleColIdx = selectedHeader ? sheet.headers.indexOf(selectedHeader) : -1;
                        const sampleVal = sampleColIdx >= 0 && sheet.rows[0] ? String(sheet.rows[0][sampleColIdx] ?? '') : '—';
                        return (
                          <React.Fragment key={col.key}>
                            <div style={{ padding: '6px 0', color: COLORS.TEXT, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {col.label}
                              {col.required && <span style={{ color: '#B91C1C', fontSize: '9px' }}>*</span>}
                            </div>
                            <div>
                              <select value={selectedHeader} onChange={e => {
                                setColumnMapping(prev => ({ ...prev, [entity]: { ...(prev[entity] || {}), [col.key]: e.target.value || undefined } }));
                              }} style={{ width: '100%', padding: '5px 8px', fontSize: '11px', border: `1px solid ${selectedHeader ? COLORS.TEAL : COLORS.BORDER}`, borderRadius: '3px', backgroundColor: selectedHeader ? 'rgba(0,168,203,0.04)' : '#fff', color: COLORS.TEXT }}>
                                <option value="">— Not mapped —</option>
                                {sheet.headers.map(h => <option key={h} value={h}>{h}</option>)}
                              </select>
                            </div>
                            <div style={{ padding: '6px 0', color: COLORS.TEXT_DIM, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sampleVal}</div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={() => setStep(1)} style={btnStyle(false)}>← Back</button>
              <button onClick={() => { buildPreview(); setStep(3); }} style={btnStyle(true)}>Preview Import →</button>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && importPreview && (
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT, marginBottom: '4px' }}>Review import preview</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '20px', maxWidth: '640px', lineHeight: 1.55 }}>
              Verify the data below before importing. Existing records with matching IDs will be {mergeMode === 'merge' ? 'updated (merged)' : 'replaced'}.
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: COLORS.TEXT, cursor: 'pointer' }}>
                <input type="radio" name="mergeMode" checked={mergeMode === 'merge'} onChange={() => setMergeMode('merge')} /> Merge with existing data
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: COLORS.TEXT, cursor: 'pointer' }}>
                <input type="radio" name="mergeMode" checked={mergeMode === 'replace'} onChange={() => setMergeMode('replace')} /> Replace existing data
              </label>
            </div>
            {Object.entries(importPreview).map(([entity, records]) => (
              <div key={entity} style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                  {entity} — {records.length} records
                  {(projectData[entity] || []).length > 0 && <span style={{ fontWeight: 400, color: COLORS.TEXT_DIM }}> (existing: {(projectData[entity] || []).length})</span>}
                </div>
                <div style={{ maxHeight: '280px', overflow: 'auto', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ backgroundColor: COLORS.PANEL_BG }}>
                        {records[0] && Object.keys(records[0]).map(k => (
                          <th key={k} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: COLORS.TEXT_DIM, borderBottom: `1px solid ${COLORS.BORDER}`, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '10px', whiteSpace: 'nowrap' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {records.slice(0, 20).map((r, ri) => (
                        <tr key={ri} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                          {Object.values(r).map((v, vi) => (
                            <td key={vi} style={{ padding: '5px 10px', color: COLORS.TEXT, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {v instanceof Date ? v.toLocaleDateString() : String(v ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {records.length > 20 && (
                        <tr><td colSpan={Object.keys(records[0] || {}).length} style={{ padding: '8px 10px', color: COLORS.TEXT_DIM, fontStyle: 'italic', textAlign: 'center' }}>
                          ...and {records.length - 20} more records
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {/* PDF source text preview */}
            {pdfData && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                  Source Document — {pdfData.pageCount} pages, {pdfData.lineCount} text lines extracted
                </div>
                <div style={{ maxHeight: '160px', overflow: 'auto', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '10px', backgroundColor: '#FAFAFA', fontSize: '10px', color: COLORS.TEXT_DIM, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {pdfData.fullText.substring(0, 3000)}{pdfData.fullText.length > 3000 ? '\n\n... (truncated)' : ''}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={() => setStep(importType === 'awbx' || importType === 'pdf' ? 1 : 2)} style={btnStyle(false)}>← Back</button>
              <button onClick={executeImport} style={btnStyle(true)}>Import {Object.values(importPreview).reduce((s, r) => s + r.length, 0)} Records →</button>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 4 && importResult && (
          <div style={{ textAlign: 'center', paddingTop: '60px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '20px', color: COLORS.TEXT, marginBottom: '8px' }}>Import Complete</div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '28px' }}>
              {Object.entries(importResult).map(([entity, count]) => (
                <span key={entity} style={{ display: 'inline-block', margin: '0 8px' }}>
                  <strong>{count}</strong> {entity}
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => { setStep(0); setImportType(null); setFileData(null); setColumnMapping({}); setImportPreview(null); setImportResult(null); }} style={btnStyle(false)}>Import More Data</button>
              {onClose && <button onClick={onClose} style={btnStyle(true)}>Done</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LibraryView({ projectData, setProjectData }) {
  const [libraryTab, setLibraryTab] = useState('component'); // 'component' | 'iso14224'
  const [libraryIndex, setLibraryIndex] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [equipmentRecords, setEquipmentRecords] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [filterStrategy, setFilterStrategy] = useState('');
  const [filterMechanism, setFilterMechanism] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load the index on mount (fetched from public/library/index.json)
  useEffect(() => {
    fetch('./library/index.json')
      .then(r => r.json())
      .then(data => {
        setLibraryIndex(data);
        setLoading(false);
      })
      .catch(() => {
        console.warn('Failure library index not found');
        setLibraryIndex([]);
        setLoading(false);
      });
  }, []);

  // Build categories from flat index
  const categories = React.useMemo(() => {
    if (!libraryIndex || !Array.isArray(libraryIndex)) return {};
    const cats = {};
    libraryIndex.forEach(entry => {
      const cat = entry.category;
      if (!cats[cat]) cats[cat] = [];
      cats[cat].push(entry);
    });
    return cats;
  }, [libraryIndex]);

  const totalRecords = React.useMemo(() => {
    if (!libraryIndex || !Array.isArray(libraryIndex)) return 0;
    return libraryIndex.reduce((sum, e) => sum + (e.recordCount || 0), 0);
  }, [libraryIndex]);

  // Load equipment detail on selection (fetches category file)
  const handleSelectEquipment = useCallback(async (eq) => {
    setSelectedEquipment(eq);
    setEquipmentRecords(null);
    setExpandedRecord(null);
    setFilterStrategy('');
    setFilterMechanism('');
    setDetailLoading(true);
    try {
      const resp = await fetch(`./library/${eq.catFile}`);
      const catData = await resp.json();
      const match = catData.find(e => e.id === eq.id);
      setEquipmentRecords(match ? match.records : []);
    } catch (e) {
      console.warn('Could not load equipment data:', eq.catFile);
      setEquipmentRecords([]);
    }
    setDetailLoading(false);
  }, []);

  // Apply a record as a failure mode to the project

  // ISO 14224 Appendix A — equipment-class Weibull β defaults
  const ISO14224_BETA_DEFAULTS = {
    'Compressor': 1.8, 'Pump': 1.5, 'Valve': 1.3, 'Heat Exchanger': 1.2,
    'Electric Motor': 2.2, 'Generator': 2.0, 'Turbine': 1.9, 'Gearbox': 2.5,
    'Bearing': 2.8, 'Seal': 1.6, 'Filter': 1.1, 'Pipe': 1.0,
    'Vessel': 1.1, 'Tank': 1.0, 'Transformer': 1.3, 'Switchgear': 1.4,
    'Circuit Breaker': 1.5, 'Sensor': 1.2, 'Transmitter': 1.1, 'PLC': 1.0,
    'Safety Valve': 1.4, 'PRV': 1.3, 'Actuator': 1.6, 'Control Valve': 1.5,
    'Crane': 1.7, 'Conveyor': 2.0, 'Fan': 1.8, 'Blower': 1.7,
    'Cooler': 1.3, 'Heater': 1.4, 'Boiler': 1.5, 'Furnace': 1.6,
    'Instrument': 1.2, 'Connector': 1.0, 'Cable': 0.9, 'Battery': 2.5
  };
  const getEquipmentBeta = (mi) => {
    if (!mi) return 2.0;
    const miLower = mi.toLowerCase();
    for (const [cls, beta] of Object.entries(ISO14224_BETA_DEFAULTS)) {
      if (miLower.includes(cls.toLowerCase())) return beta;
    }
    return 2.0; // fallback
  };

  const handleApplyAsFailureModel = useCallback((rec) => {
    if (!setProjectData) return;
    const newModel = {
      id: `fm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: `${rec.mi} — ${rec.w || rec.mech}`,
      component: rec.mi,
      failureMode: rec.w || rec.ff?.split('|')[0] || '',
      mechanism: rec.mech || '',
      cause: rec.cause || '',
      beta: getEquipmentBeta(rec.mi),
      eta: parseFloat(rec.ptf) || 5000,
      gamma: 0,
      unit: rec.ou || 'Hours',
      timeUnits: rec.tu || '',
      source: 'Optimal Reference Library',
      strategyType: rec.st || '',
      status: rec.status || '',
      // Primary task fields
      primaryTask: rec.pt || '',
      primaryTaskType: rec.ptt || '',
      primaryTaskFrequency: rec.ptf || '',
      primaryTaskAccessTime: rec.ptAccessTime || '',
      primaryTaskConstraint: rec.ptConstraint || '',
      primaryTaskLimits: rec.ptLimits || '',
      primaryTaskComments: rec.ptComments || '',
      // Secondary task fields
      secondaryTask: rec.secondaryTask || '',
      secondaryTaskType: rec.secondaryTaskType || '',
      secondaryTaskAccessTime: rec.secondaryTaskAccessTime || '',
      secondaryTaskConstraint: rec.secondaryTaskConstraint || '',
      // Budget & consequence
      budgetedAs: rec.budgetedAs || '',
      budgetedLife: rec.budgetedLife || '',
      consequenceOfFailure: rec.cof || '',
      functionFailure: rec.ff || ''
    };
    setProjectData(prev => ({
      ...prev,
      failuremodels: [...(prev.failuremodels || []), newModel]
    }));
    alert(`Added failure mode: ${newModel.name}`);
  }, [setProjectData]);

  // Apply a record as a maintenance task
  const handleApplyAsTask = useCallback((rec) => {
    if (!setProjectData) return;
    const newTask = {
      id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: rec.pt || `${rec.mi} — ${rec.ptt}`,
      component: rec.mi,
      type: rec.ptt || 'Inspection',
      interval: parseFloat(rec.ptf) || 0,
      intervalUnit: rec.ou || '',
      timeUnits: rec.tu || '',
      // Duration & manpower
      duration: rec.ptAccessTime || '',
      accessTime: rec.ptAccessTime || '',
      constraint: rec.ptConstraint || '',
      acceptanceCriteria: rec.ptLimits || '',
      workInstructions: rec.ptComments || '',
      strategy: rec.st || '',
      status: rec.status || '',
      source: 'Optimal Reference Library',
      // Budget classification
      budgetedAs: rec.budgetedAs || '',
      budgetedLife: rec.budgetedLife || '',
      consequenceOfFailure: rec.cof || '',
      failureMode: rec.w || '',
      mechanism: rec.mech || '',
      functionFailure: rec.ff || ''
    };
    // Also create secondary task if one exists in the library record
    if (rec.secondaryTask) {
      const secTask = {
        id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 6)}-sec`,
        name: rec.secondaryTask,
        component: rec.mi,
        type: rec.secondaryTaskType || 'Inspection',
        interval: 0,
        intervalUnit: rec.ou || '',
        timeUnits: rec.tu || '',
        duration: rec.secondaryTaskAccessTime || '',
        accessTime: rec.secondaryTaskAccessTime || '',
        constraint: rec.secondaryTaskConstraint || '',
        strategy: rec.st || '',
        source: 'Optimal Reference Library (Secondary)',
        parentTaskId: newTask.id,
        budgetedAs: rec.budgetedAs || '',
        consequenceOfFailure: rec.cof || ''
      };
      setProjectData(prev => ({
        ...prev,
        tasks: [...(prev.tasks || []), newTask, secTask]
      }));
      alert(`Added primary task: ${newTask.name}\nAdded secondary task: ${secTask.name}`);
      return;
    }
    setProjectData(prev => ({
      ...prev,
      tasks: [...(prev.tasks || []), newTask]
    }));
    alert(`Added task: ${newTask.name}`);
  }, [setProjectData]);

  // Filter records by strategy & mechanism (must be before any early return)
  const filteredRecords = React.useMemo(() => {
    if (!equipmentRecords) return [];
    return equipmentRecords.filter(r => {
      if (filterStrategy && r.st !== filterStrategy) return false;
      if (filterMechanism && r.mech !== filterMechanism) return false;
      return true;
    });
  }, [equipmentRecords, filterStrategy, filterMechanism]);

  // Group records by maintainable item
  const groupedByMI = React.useMemo(() => {
    const groups = {};
    filteredRecords.forEach(r => {
      const mi = r.mi || 'Unspecified';
      if (!groups[mi]) groups[mi] = [];
      groups[mi].push(r);
    });
    return groups;
  }, [filteredRecords]);

  // If ISO 14224 tab is selected, render that view
  if (libraryTab === 'iso14224') {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: COLORS.LIGHT_BG }}>
        <div style={{ display: 'flex', gap: '0', padding: '12px 16px 0 16px' }}>
          <button onClick={() => setLibraryTab('component')} style={{ padding: '8px 20px', fontSize: '12px', fontWeight: '600', border: `1px solid ${COLORS.BORDER}`, borderBottom: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer', backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT_DIM }}>Component Library</button>
          <button onClick={() => setLibraryTab('iso14224')} style={{ padding: '8px 20px', fontSize: '12px', fontWeight: '700', border: `1px solid #00A8CB`, borderBottom: '2px solid #00A8CB', borderRadius: '6px 6px 0 0', cursor: 'pointer', backgroundColor: '#00A8CB10', color: '#00A8CB' }}>ISO 14224 Failure Modes</button>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ISO14224FailureModesView projectData={projectData} setProjectData={setProjectData} />
        </div>
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>Loading Optimal® Reference Library...</div>;
  }

  const sortedCatNames = Object.keys(categories).sort();

  // Filter categories/equipment by search
  const searchLower = searchTerm.toLowerCase();
  const filteredCats = searchTerm
    ? sortedCatNames.filter(cat => {
        if (cat.toLowerCase().includes(searchLower)) return true;
        return categories[cat].some(eq => eq.equipment.toLowerCase().includes(searchLower) || eq.id.toLowerCase().includes(searchLower));
      })
    : sortedCatNames;

  const LB = '#00A8CB';
  const DB = '#002F65';

  return (
    <div style={{ padding: '0', height: '100%', overflow: 'hidden', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0', padding: '12px 16px 0 16px' }}>
        <button onClick={() => setLibraryTab('component')} style={{ padding: '8px 20px', fontSize: '12px', fontWeight: '700', border: `1px solid ${LB}`, borderBottom: `2px solid ${LB}`, borderRadius: '6px 6px 0 0', cursor: 'pointer', backgroundColor: LB + '10', color: LB }}>Component Library</button>
        <button onClick={() => setLibraryTab('iso14224')} style={{ padding: '8px 20px', fontSize: '12px', fontWeight: '600', border: `1px solid ${COLORS.BORDER}`, borderBottom: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer', backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT_DIM }}>ISO 14224 Failure Modes</button>
      </div>
      <div style={{ padding: '0 16px 16px 16px', height: 'calc(100% - 50px)', overflow: 'hidden' }}>
      {/* Header stats bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', padding: '10px 16px', backgroundColor: DB, borderRadius: '0 6px 6px 6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Package size={16} color={LB} />
          <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '700' }}>Optimal® Asset Maintenance Reference Library</span>
          <span style={{ fontSize: '7px', fontWeight: '700', color: LB, position: 'relative', top: '-4px' }}>ARaaS®</span>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ color: '#FFFFFF', fontSize: '11px', opacity: 0.8 }}>
          {(libraryIndex || []).length} Equipment Types &bull; {totalRecords.toLocaleString()} Failure Records &bull; {sortedCatNames.length} Categories
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', height: 'calc(100% - 70px)' }}>
        {/* Left panel — Categories & Equipment */}
        <div style={{ width: '300px', minWidth: '200px', display: 'flex', flexDirection: 'column', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
          {/* Search */}
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
            <input
              type="text"
              placeholder="Search 769 equipment types..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          {/* Category tree */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {filteredCats.map(catName => {
              const items = categories[catName].filter(eq =>
                !searchTerm || catName.toLowerCase().includes(searchLower) || eq.equipment.toLowerCase().includes(searchLower) || eq.id.toLowerCase().includes(searchLower)
              );
              const isOpen = selectedCategory === catName;
              return (
                <div key={catName}>
                  <div
                    onClick={() => setSelectedCategory(isOpen ? null : catName)}
                    style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: COLORS.TEXT, backgroundColor: isOpen ? LB + '10' : 'transparent', borderBottom: `1px solid ${COLORS.BORDER}` }}
                  >
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {catName}
                    <span style={{ marginLeft: 'auto', fontSize: '10px', color: COLORS.TEXT_DIM, backgroundColor: COLORS.BORDER + '60', padding: '1px 6px', borderRadius: '8px' }}>{items.length}</span>
                  </div>
                  {isOpen && items.map(eq => (
                    <div
                      key={eq.id}
                      onClick={() => handleSelectEquipment(eq)}
                      style={{
                        padding: '5px 12px 5px 28px', cursor: 'pointer', fontSize: '11px',
                        color: selectedEquipment?.id === eq.id ? LB : COLORS.TEXT_DIM,
                        fontWeight: selectedEquipment?.id === eq.id ? '600' : '400',
                        backgroundColor: selectedEquipment?.id === eq.id ? LB + '10' : 'transparent',
                      }}
                    >
                      {eq.equipment}
                      <span style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.6 }}>({eq.recordCount})</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel — Equipment detail */}
        <div style={{ flex: 1, backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'auto' }}>
          {!selectedEquipment ? (
            <div style={{ padding: '60px 40px', textAlign: 'center' }}>
              <Package size={48} color={COLORS.BORDER} style={{ marginBottom: '12px' }} />
              <div style={{ color: COLORS.TEXT_DIM, fontSize: '13px', marginBottom: '4px' }}>Select an equipment type from the library</div>
              <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Browse {sortedCatNames.length} categories across {(libraryIndex || []).length} equipment types with {totalRecords.toLocaleString()} failure records</div>
            </div>
          ) : detailLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>Loading {selectedEquipment.equipment}...</div>
          ) : (
            <div>
              {/* Equipment header */}
              <div style={{ padding: '12px 16px', backgroundColor: DB, color: '#FFFFFF' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '700' }}>{selectedEquipment.equipment}</div>
                    <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>
                      {selectedEquipment.recordCount} records &bull; {selectedEquipment.maintainableItems?.length || 0} maintainable items &bull; {selectedEquipment.mechanisms?.length || 0} mechanisms
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {(selectedEquipment.strategies || []).map(s => (
                      <span key={s} style={{ padding: '2px 8px', fontSize: '10px', fontWeight: '600', backgroundColor: s === 'Condition Based' ? '#22c55e30' : s === 'Run Until Failure' ? '#ef444430' : s === 'Time Based' ? '#3b82f630' : LB + '30', color: '#FFFFFF', borderRadius: '3px' }}>{s}</span>
                    ))}
                  </div>
                </div>
                {/* Filters */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <select value={filterStrategy} onChange={e => setFilterStrategy(e.target.value)} style={{ padding: '3px 8px', fontSize: '11px', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}>
                    <option value="">All Strategies</option>
                    {(selectedEquipment.strategies || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={filterMechanism} onChange={e => setFilterMechanism(e.target.value)} style={{ padding: '3px 8px', fontSize: '11px', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}>
                    <option value="">All Mechanisms</option>
                    {(selectedEquipment.mechanisms || []).map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', alignSelf: 'center' }}>Showing {filteredRecords.length} of {(equipmentRecords || []).length}</span>
                </div>
              </div>

              {/* Grouped by Maintainable Item */}
              {Object.entries(groupedByMI).map(([miName, records]) => (
                <div key={miName} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <div
                    onClick={() => setExpandedRecord(expandedRecord === miName ? null : miName)}
                    style={{ padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: expandedRecord === miName ? LB + '08' : 'transparent' }}
                  >
                    {expandedRecord === miName ? <ChevronDown size={12} color={LB} /> : <ChevronRight size={12} />}
                    <Wrench size={12} color={expandedRecord === miName ? LB : COLORS.TEXT_DIM} />
                    <span style={{ fontSize: '12px', fontWeight: '600', color: expandedRecord === miName ? LB : COLORS.TEXT }}>{miName}</span>
                    <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{records.length} records</span>
                    <div style={{ flex: 1 }} />
                    {[...new Set(records.map(r => r.st).filter(Boolean))].map(s => (
                      <span key={s} style={{ padding: '1px 6px', fontSize: '9px', backgroundColor: s === 'Condition Based' ? '#22c55e20' : s === 'Run Until Failure' ? '#ef444420' : s === 'Time Based' ? '#3b82f620' : '#f59e0b20', color: s === 'Condition Based' ? '#22c55e' : s === 'Run Until Failure' ? '#ef4444' : s === 'Time Based' ? '#3b82f6' : '#f59e0b', borderRadius: '3px', fontWeight: '600' }}>{s}</span>
                    ))}
                  </div>
                  {expandedRecord === miName && (
                    <div style={{ padding: '0 16px 12px 36px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead>
                          <tr style={{ backgroundColor: DB }}>
                            {['Mechanism', 'Cause', 'Strategy', 'Primary Task', 'Frequency', 'Task Type', 'Consequence', ''].map(h => (
                              <th key={h} style={{ padding: '5px 8px', textAlign: 'left', color: '#FFFFFF', fontWeight: '600', fontSize: '10px' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {records.map((rec, i) => (
                            <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : COLORS.LIGHT_BG }}>
                              <td style={{ padding: '4px 8px', color: COLORS.TEXT, fontWeight: '500', maxWidth: '140px' }}>{rec.mech || '—'}</td>
                              <td style={{ padding: '4px 8px', color: COLORS.TEXT_DIM, maxWidth: '140px' }}>{rec.cause || '—'}</td>
                              <td style={{ padding: '4px 8px' }}>
                                <span style={{ padding: '1px 6px', fontSize: '9px', borderRadius: '3px', fontWeight: '600', backgroundColor: rec.st === 'Condition Based' ? '#22c55e15' : rec.st === 'Run Until Failure' ? '#ef444415' : rec.st === 'Time Based' ? '#3b82f615' : '#f59e0b15', color: rec.st === 'Condition Based' ? '#22c55e' : rec.st === 'Run Until Failure' ? '#ef4444' : rec.st === 'Time Based' ? '#3b82f6' : '#f59e0b' }}>{rec.st || '—'}</span>
                              </td>
                              <td style={{ padding: '4px 8px', color: COLORS.TEXT, maxWidth: '200px', fontSize: '10px' }}>{rec.pt || '—'}</td>
                              <td style={{ padding: '4px 8px', color: LB, fontFamily: 'monospace', fontSize: '11px', fontWeight: '600' }}>{rec.ptf || '—'} <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM, fontFamily: 'inherit' }}>{rec.ou || ''}</span></td>
                              <td style={{ padding: '4px 8px' }}>
                                <span style={{ padding: '1px 6px', fontSize: '9px', borderRadius: '3px', fontWeight: '600', backgroundColor: rec.ptt === 'Inspection' ? '#3b82f615' : rec.ptt === 'Replace' ? '#ef444415' : '#f59e0b15', color: rec.ptt === 'Inspection' ? '#3b82f6' : rec.ptt === 'Replace' ? '#ef4444' : '#f59e0b' }}>{rec.ptt || '—'}</span>
                              </td>
                              <td style={{ padding: '4px 8px', color: COLORS.TEXT_DIM, fontSize: '10px', maxWidth: '160px' }}>{rec.cof || '—'}</td>
                              <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                                <button onClick={(e) => { e.stopPropagation(); handleApplyAsFailureModel(rec); }} title="Add as failure mode" style={{ padding: '2px 6px', fontSize: '9px', backgroundColor: LB + '15', color: LB, border: `1px solid ${LB}40`, borderRadius: '3px', cursor: 'pointer', fontWeight: '600', marginRight: '3px' }}>+ FM</button>
                                <button onClick={(e) => { e.stopPropagation(); handleApplyAsTask(rec); }} title="Add as maintenance task" style={{ padding: '2px 6px', fontSize: '9px', backgroundColor: '#22c55e15', color: '#22c55e', border: '1px solid #22c55e40', borderRadius: '3px', cursor: 'pointer', fontWeight: '600' }}>+ Task</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {records[0]?.sec && (
                        <div style={{ marginTop: '6px', padding: '6px 8px', backgroundColor: '#f59e0b08', border: '1px solid #f59e0b20', borderRadius: '4px', fontSize: '10px', color: COLORS.TEXT_DIM }}>
                          <strong style={{ color: '#f59e0b' }}>Secondary Task:</strong> {records[0].sec}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {filteredRecords.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>No records match the current filters</div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

// ============================================================================
// SFG20 MAINTENANCE SCHEDULER VIEW
// ============================================================================
function SFG20SchedulerView({ projectData, setProjectData }) {
  const [sfgIndex, setSfgIndex] = useState(null);
  const [sfgData, setSfgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFreq, setFilterFreq] = useState('');
  const [expandedTask, setExpandedTask] = useState(null);

  // Load SFG20 data
  useEffect(() => {
    Promise.all([
      fetch('./sfg20/index.json').then(r => r.json()),
      fetch('./sfg20/schedules.json').then(r => r.json())
    ]).then(([idx, data]) => {
      setSfgIndex(idx);
      setSfgData(data);
      setLoading(false);
    }).catch(() => {
      setSfgIndex([]);
      setSfgData([]);
      setLoading(false);
    });
  }, []);

  // G13: Normalise SFG20 text frequency to hours for cross-module comparison
  const normaliseFrequencyToHours = (freq) => {
    if (!freq || typeof freq !== 'string') return null;
    const f = freq.toUpperCase().trim();
    const num = parseInt(f) || 1;
    if (f.includes('D')) return num * 24;
    if (f.includes('W')) return num * 168;
    if (f.includes('M')) return num * 730;
    if (f.includes('Q')) return num * 2190;
    if (f.includes('Y') || f.includes('A')) return num * 8760;
    return null;
  };

  // G13: RCM vs SFG20 interval reconciliation
  const reconciliationConflicts = useMemo(() => {
    const sfgTasks = (projectData?.tasks || []).filter(t => t.sfg20Ref);
    const rcmTasks = (projectData?.scheduledtasks || []).filter(t => t.source === 'RCM');
    const conflicts = [];
    sfgTasks.forEach(sfg => {
      const sfgHours = normaliseFrequencyToHours(sfg.frequency);
      rcmTasks.filter(rcm => rcm.equipmentId === sfg.equipmentId || rcm.equipment === sfg.equipment).forEach(rcm => {
        const rcmHours = rcm.interval;
        if (sfgHours && rcmHours && Math.abs(sfgHours - rcmHours) / Math.max(sfgHours, rcmHours) > 0.2) {
          conflicts.push({ sfgTask: sfg.name, sfgFreq: sfg.frequency, sfgHours, rcmTask: rcm.description, rcmHours, delta: Math.abs(sfgHours - rcmHours) });
        }
      });
    });
    return conflicts;
  }, [projectData]);

  // Apply task to project
  const handleApplyTask = useCallback((schedule, task) => {
    if (!setProjectData) return;
    const newTask = {
      id: `sfg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: `[SFG20 ${schedule.ref}] ${task.item}`,
      description: task.action,
      type: 'Planned Maintenance',
      frequency: task.frequency,
      skillSet: task.skillSet || '',
      source: `SFG20 ${schedule.ref} — ${schedule.title}`,
      sfg20Ref: schedule.ref,
      frequencyHours: normaliseFrequencyToHours(task.frequency) // G12: normalised for cross-module comparison
    };
    setProjectData(prev => ({
      ...prev,
      tasks: [...(prev.tasks || []), newTask]
    }));
    alert(`Added SFG20 task: ${task.item} (${task.frequency})`);
  }, [setProjectData]);

  // Apply entire schedule to project
  const handleApplyAllTasks = useCallback((schedule) => {
    if (!setProjectData || !schedule.tasks?.length) return;
    const newTasks = schedule.tasks.map(task => ({
      id: `sfg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: `[SFG20 ${schedule.ref}] ${task.item}`,
      description: task.action,
      type: 'Planned Maintenance',
      frequency: task.frequency,
      skillSet: task.skillSet || '',
      source: `SFG20 ${schedule.ref} — ${schedule.title}`,
      sfg20Ref: schedule.ref
    }));
    setProjectData(prev => ({
      ...prev,
      tasks: [...(prev.tasks || []), ...newTasks]
    }));
    alert(`Added ${newTasks.length} SFG20 tasks from ${schedule.ref} — ${schedule.title}`);
  }, [setProjectData]);

  const totalTasks = React.useMemo(() => {
    if (!sfgData) return 0;
    return sfgData.reduce((sum, s) => sum + (s.tasks?.length || 0), 0);
  }, [sfgData]);

  // Get all unique frequencies (must be before any early return)
  const allFreqs = React.useMemo(() => {
    const freqs = new Set();
    (sfgData || []).forEach(s => (s.tasks || []).forEach(t => { if (t.frequency) freqs.add(t.frequency); }));
    return [...freqs].sort((a, b) => {
      const getMonths = (f) => { const m = f.match(/^(\d+)([DWMY])$/); if (!m) return 0; const n = parseInt(m[1]); return m[2] === 'D' ? n/30 : m[2] === 'W' ? n/4 : m[2] === 'M' ? n : n*12; };
      return getMonths(a) - getMonths(b);
    });
  }, [sfgData]);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>Loading SFG20 Maintenance Schedules...</div>;
  }

  const LB = '#00A8CB';
  const DB = '#002F65';

  // Criticality colours (matching SFG20 standard)
  const critColors = { 'Statutory and Mandatory': '#ef4444', 'Business Critical': '#f472b6', 'Business or Service Impacting': '#f59e0b', 'Discretionary': '#22c55e' };

  // Frequency label map
  const freqLabels = { 'D': 'Daily', 'W': 'Weekly', 'M': 'Monthly', 'Y': 'Yearly' };
  const freqToLabel = (f) => {
    if (!f) return '—';
    const match = f.match(/^(\d+)([DWMY])$/);
    if (!match) return f;
    const num = parseInt(match[1]);
    const unit = freqLabels[match[2]] || match[2];
    if (num === 1) return unit;
    return `${num} ${unit}`;
  };

  // Filter schedules
  const searchLower = searchTerm.toLowerCase();
  const filteredSchedules = (sfgData || []).filter(s => {
    if (searchTerm && !s.title.toLowerCase().includes(searchLower) && !s.ref.toLowerCase().includes(searchLower)) return false;
    return true;
  });

  const selectedFull = selectedSchedule ? sfgData.find(s => s.ref === selectedSchedule.ref) : null;
  const filteredTasks = selectedFull ? (selectedFull.tasks || []).filter(t => !filterFreq || t.frequency === filterFreq) : [];

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'hidden', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', padding: '10px 16px', backgroundColor: '#009688', borderRadius: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Calendar size={16} color="#FFFFFF" />
          <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '700' }}>SFG20 Maintenance Task Scheduler</span>
          <span style={{ fontSize: '9px', fontWeight: '600', color: '#FFFFFF', opacity: 0.7, padding: '1px 6px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '3px' }}>BESA Standard</span>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ color: '#FFFFFF', fontSize: '11px', opacity: 0.8 }}>
          {(sfgData || []).length} Schedules &bull; {totalTasks.toLocaleString()} Maintenance Tasks &bull; {allFreqs.length} Frequency Intervals
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', height: 'calc(100% - 70px)' }}>
        {/* Left panel — Schedule list */}
        <div style={{ width: '320px', minWidth: '220px', display: 'flex', flexDirection: 'column', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
            <input type="text" placeholder="Search 955 schedules..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {filteredSchedules.map(s => (
              <div key={s.ref} onClick={() => { setSelectedSchedule(s); setFilterFreq(''); setExpandedTask(null); }} style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: selectedSchedule?.ref === s.ref ? LB + '10' : 'transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: LB, fontFamily: 'monospace', minWidth: '40px' }}>{s.ref}</span>
                  <span style={{ fontSize: '11px', fontWeight: selectedSchedule?.ref === s.ref ? '600' : '400', color: selectedSchedule?.ref === s.ref ? LB : COLORS.TEXT, flex: 1 }}>{s.title}</span>
                  <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM, backgroundColor: COLORS.BORDER + '60', padding: '1px 5px', borderRadius: '8px' }}>{s.taskCount || (s.tasks?.length || 0)}</span>
                </div>
                {s.frequencies && s.frequencies.length > 0 && (
                  <div style={{ display: 'flex', gap: '3px', marginTop: '3px', marginLeft: '46px' }}>
                    {s.frequencies.map((f, i) => (
                      <span key={i} style={{ fontSize: '9px', padding: '0 4px', backgroundColor: '#009688' + '15', color: '#009688', borderRadius: '2px', fontWeight: '600' }}>{f.interval}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right panel — Schedule detail */}
        <div style={{ flex: 1, backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'auto' }}>
          {!selectedFull ? (
            <div style={{ padding: '60px 40px', textAlign: 'center' }}>
              <Calendar size={48} color={COLORS.BORDER} style={{ marginBottom: '12px' }} />
              <div style={{ color: COLORS.TEXT_DIM, fontSize: '13px', marginBottom: '4px' }}>Select a maintenance schedule</div>
              <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Browse {(sfgData || []).length} SFG20 compliant schedules covering building services, HVAC, fire safety, electrical, plumbing, and more</div>
            </div>
          ) : (
            <div>
              {/* Schedule header */}
              <div style={{ padding: '12px 16px', backgroundColor: '#009688', color: '#FFFFFF' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '10px', opacity: 0.7, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '2px' }}>SFG20 Schedule {selectedFull.ref} {selectedFull.version ? `(v${selectedFull.version})` : ''}</div>
                    <div style={{ fontSize: '15px', fontWeight: '700' }}>{selectedFull.title}</div>
                    <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>
                      {selectedFull.tasks?.length || 0} tasks &bull; Unit: {selectedFull.unitOfMeasure || 'Nr'} {selectedFull.date ? `\u2022 ${selectedFull.date}` : ''}
                    </div>
                  </div>
                  <button onClick={() => handleApplyAllTasks(selectedFull)} style={{ padding: '6px 14px', fontSize: '11px', fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '4px', cursor: 'pointer' }}>+ Apply All Tasks to Project</button>
                </div>
                {/* Frequency pills + filter */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px', opacity: 0.7 }}>Filter:</span>
                  <span onClick={() => setFilterFreq('')} style={{ padding: '2px 8px', fontSize: '10px', fontWeight: '600', backgroundColor: !filterFreq ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', color: '#FFFFFF', borderRadius: '3px', cursor: 'pointer' }}>All</span>
                  {[...new Set((selectedFull.tasks || []).map(t => t.frequency).filter(Boolean))].sort().map(f => (
                    <span key={f} onClick={() => setFilterFreq(filterFreq === f ? '' : f)} style={{ padding: '2px 8px', fontSize: '10px', fontWeight: '600', backgroundColor: filterFreq === f ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', color: '#FFFFFF', borderRadius: '3px', cursor: 'pointer' }}>{f} ({freqToLabel(f)})</span>
                  ))}
                </div>
              </div>

              {/* Intro notes */}
              {selectedFull.introNotes && (
                <div style={{ padding: '8px 16px', fontSize: '11px', color: COLORS.TEXT_DIM, backgroundColor: '#009688' + '08', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <strong style={{ color: '#009688' }}>Notes:</strong> {selectedFull.introNotes}
                </div>
              )}

              {/* Tasks table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: DB }}>
                    {['#', 'Item', 'Frequency', 'Action / Description', 'Skill', ''].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#FFFFFF', fontWeight: '600', fontSize: '10px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((t, i) => (
                    <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                      <td style={{ padding: '5px 10px', color: COLORS.TEXT_DIM, fontFamily: 'monospace', fontSize: '10px' }}>{t.order || i + 1}</td>
                      <td style={{ padding: '5px 10px', color: COLORS.TEXT, fontWeight: '500', maxWidth: '160px' }}>{t.item}</td>
                      <td style={{ padding: '5px 10px' }}>
                        <span style={{ padding: '2px 8px', fontSize: '10px', fontWeight: '700', backgroundColor: '#009688' + '15', color: '#009688', borderRadius: '3px', fontFamily: 'monospace' }}>{t.frequency}</span>
                        <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, marginTop: '1px' }}>{freqToLabel(t.frequency)}</div>
                      </td>
                      <td style={{ padding: '5px 10px', color: COLORS.TEXT, fontSize: '11px', maxWidth: '400px' }}>{t.action}</td>
                      <td style={{ padding: '5px 10px' }}>
                        {t.skillSet && <span style={{ padding: '1px 6px', fontSize: '9px', fontWeight: '600', backgroundColor: DB + '10', color: DB, borderRadius: '3px' }}>{t.skillSet}</span>}
                      </td>
                      <td style={{ padding: '5px 10px' }}>
                        <button onClick={(e) => { e.stopPropagation(); handleApplyTask(selectedFull, t); }} style={{ padding: '2px 8px', fontSize: '9px', backgroundColor: '#009688' + '15', color: '#009688', border: '1px solid #00968840', borderRadius: '3px', cursor: 'pointer', fontWeight: '600' }}>+ Add</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTasks.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>No tasks match the current filter</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// INTERVAL OPTIMIZATION VIEW
// ============================================================================
function IntervalOptimizationView({ projectData, simConfig }) {
  const [optimizer] = useState(() => new IntervalOptimization());
  const [activeTab, setActiveTab] = useState('age');
  const [result, setResult] = useState(null);
  const [params, setParams] = useState({ shape: 2.5, scale: 5000, plannedCost: 500, unplannedCost: 5000, missionTime: simConfig?.missionTime || 8760 });

  const handleOptimize = useCallback(() => {
    let res;
    if (activeTab === 'age') res = optimizer.optimizeAgeReplacement(params);
    else if (activeTab === 'block') res = optimizer.optimizeBlockReplacement(params);
    else if (activeTab === 'cbm') res = optimizer.optimizeCBM({ pfInterval: 500, inspectionCost: 100, failureCost: params.unplannedCost, inspectionInterval: 200, detectionProbability: 0.9 });
    else if (activeTab === 'delay') res = optimizer.optimizeDelayTime({ defectArrivalRate: 0.001, delayTimeDistribution: { shape: params.shape, scale: 1000 }, inspectionCost: 100, failureCost: params.unplannedCost });
    setResult(res);
  }, [optimizer, activeTab, params]);

  const tabDefs = [
    { id: 'age', label: 'Age Replacement' },
    { id: 'block', label: 'Block Replacement' },
    { id: 'cbm', label: 'Condition-Based' },
    { id: 'delay', label: 'Delay Time Model' }
  ];

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', backgroundColor: COLORS.PANEL_BG, borderRadius: '6px', padding: '4px' }}>
        {tabDefs.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setResult(null); }} style={{
            padding: '8px 16px', backgroundColor: activeTab === tab.id ? COLORS.NAVY : 'transparent',
            color: activeTab === tab.id ? '#FFFFFF' : COLORS.TEXT, border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: activeTab === tab.id ? '600' : '400'
          }}>{tab.label}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Parameters */}
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Parameters</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              { key: 'shape', label: 'Weibull Shape (β)', step: 0.1 },
              { key: 'scale', label: 'Weibull Scale (η)', step: 100 },
              { key: 'plannedCost', label: 'Planned Replacement Cost ($)', step: 50 },
              { key: 'unplannedCost', label: 'Unplanned Failure Cost ($)', step: 100 }
            ].map(p => (
              <div key={p.key}>
                <label style={{ display: 'block', color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>{p.label}</label>
                <input type="number" value={params[p.key]} step={p.step}
                  onChange={(e) => setParams(prev => ({ ...prev, [p.key]: parseFloat(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}
                />
              </div>
            ))}
          </div>
          <button onClick={handleOptimize} style={{ marginTop: '16px', padding: '10px 24px', backgroundColor: COLORS.BLUE, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Run Optimization</button>
        </div>

        {/* Results */}
        <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Optimization Results</h3>
          {!result ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>Configure parameters and click "Run Optimization".</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {Object.entries(result).map(([key, value]) => (
                <div key={key} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '10px' }}>
                  <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '2px' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.GOLD }}>{typeof value === 'number' ? value.toFixed(2) : String(value)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// WORK INSTRUCTIONS VIEW
// ============================================================================
function WorkInstructionsView({ projectData }) {
  const [instructions, setInstructions] = useState([]);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [htmlPreview, setHtmlPreview] = useState('');

  const formatEquipmentLabel = useCallback((instruction) => {
    const value = instruction?.equipmentName || instruction?.equipment || instruction?.equipmentId;
    if (!value) return '—';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      return value.name || value.description || value.id || 'Equipment';
    }
    return String(value);
  }, []);

  const handleGenerateBatch = useCallback(() => {
    try {
      const tasks = (projectData.scheduledtasks || []).concat(projectData.correctivetasks || []);
      if (tasks.length === 0) {
        // Generate from RCM causes as fallback
        const rcmCauses = projectData.rcmcauses || [];
        const equipments = projectData.equipments || [];
        const batch = rcmCauses.slice(0, 10).map(cause => {
          const equip = equipments.find(e => e.id === cause.equipmentId) || equipments[0] || {};
          return WorkInstructionGenerator.generateFromRCM(cause, equip, projectData);
        });
        setInstructions(batch);
      } else {
        const batch = WorkInstructionGenerator.generateBatchInstructions(tasks, projectData);
        setInstructions(batch);
      }
    } catch (err) {
      console.error('Work instruction generation error:', err);
      alert('Work instruction generation failed: ' + err.message + '\n\nPlease check your task and equipment data.');
    }
  }, [projectData]);

  const handlePreview = useCallback((instruction) => {
    setSelectedInstruction(instruction);
    try {
      const html = WorkInstructionGenerator.formatAsHTML(instruction);
      setHtmlPreview(html);
    } catch (err) {
      console.error('Format error:', err);
      setHtmlPreview(`<html><body><h1>${instruction.title || 'Work Instruction'}</h1><p>Preview not available</p></body></html>`);
    }
  }, []);

  const handleExportPDF = useCallback(() => {
    if (htmlPreview && window.electronAPI?.exportPDF) {
      window.electronAPI.exportPDF(htmlPreview);
    }
  }, [htmlPreview]);

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto', backgroundColor: COLORS.LIGHT_BG }}>
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <button onClick={handleGenerateBatch} style={{ padding: '10px 20px', backgroundColor: COLORS.BLUE, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Generate Work Instructions</button>
        {htmlPreview && <button onClick={handleExportPDF} style={{ padding: '10px 20px', backgroundColor: COLORS.GOLD, color: COLORS.NAVY, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Export as PDF</button>}
      </div>

      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Instruction list */}
        <div style={{ width: '320px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'auto', maxHeight: '600px' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY, fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>Instructions ({instructions.length})</div>
          {instructions.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>Click "Generate" to create work instructions from your maintenance data.</div>
          ) : instructions.map((inst, idx) => (
            <div key={idx} onClick={() => handlePreview(inst)} style={{
              padding: '10px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, cursor: 'pointer',
              backgroundColor: selectedInstruction === inst ? 'rgba(0,168,203,0.15)' : 'transparent'
            }}>
              <div style={{ color: selectedInstruction === inst ? '#FFFFFF' : COLORS.TEXT, fontSize: '12px', fontWeight: '600' }}>{inst.title || inst.taskName || `Instruction ${idx + 1}`}</div>
              <div style={{ color: selectedInstruction === inst ? '#FFFFFF' : COLORS.TEXT_DIM, fontSize: '11px', opacity: selectedInstruction === inst ? 0.9 : 1 }}>{formatEquipmentLabel(inst)} • {inst.template || inst.type || 'General'}</div>
            </div>
          ))}
        </div>

        {/* Preview */}
        <div style={{ flex: 1, backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'auto', maxHeight: '600px' }}>
          {htmlPreview ? (
            <iframe srcDoc={htmlPreview} style={{ width: '100%', height: '100%', border: 'none', minHeight: '580px' }} title="Work Instruction Preview" />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>Select an instruction to preview.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SAP CMMS CONNECTOR VIEW
// ============================================================================
function SAPConnectorView({ projectData, onImportData }) {
  const [sapConfig, setSapConfig] = useState(SAPConnector.DEFAULT_CONFIG);
  const [connector] = useState(() => new SAPConnector());
  const [activeTab, setActiveTab] = useState('dashboard');
  const [importResult, setImportResult] = useState(null);
  const [selectedTable, setSelectedTable] = useState('EQUI');
  const [syncLog, setSyncLog] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [exportContent, setExportContent] = useState('');

  const tables = useMemo(() => connector.getAvailableTables(), [connector]);

  const handleTestConnection = useCallback(() => {
    connector.testConnection().then(result => {
      setConnectionStatus(result);
    }).catch(() => {
      setConnectionStatus({ success: false, message: 'Connection failed' });
    });
  }, [connector]);

  const handleImportFile = useCallback(async () => {
    if (window.electronAPI?.importSAPFile) {
      const result = await window.electronAPI.importSAPFile();
      if (result) {
        const importRes = connector.autoImport(result.content, result.filename);
        setImportResult(importRes);
        setSyncLog(connector.getSyncLog());
        if (importRes.success && onImportData) {
          // For auto-import results
          if (importRes.records) {
            onImportData(importRes.entity, importRes.records);
          } else if (importRes.imports) {
            importRes.imports.forEach(imp => {
              onImportData(imp.entity, [imp.record]);
            });
          }
        }
      }
    } else {
      // Fallback: prompt for paste
      setActiveTab('import');
    }
  }, [connector, onImportData]);

  const handleExportTable = useCallback((table) => {
    const tableConfig = SAPConnector.TABLE_MAP[table];
    if (!tableConfig) return;
    const data = projectData[tableConfig.entity] || [];
    const format = sapConfig.file.format;

    let content;
    if (format === 'csv') {
      content = connector.exportCSV(data, table);
    } else if (format === 'idoc') {
      content = connector.exportIDoc(data, table);
    } else {
      content = JSON.stringify(connector.exportJSON(data, table), null, 2);
    }
    setExportContent(content);
    setSyncLog(connector.getSyncLog());

    if (window.electronAPI?.exportSAPFile) {
      window.electronAPI.exportSAPFile({
        content,
        filename: `SAP_${table}_export.${format === 'idoc' ? 'xml' : format}`,
        format
      });
    }
  }, [connector, projectData, sapConfig.file.format]);

  const handleManualImport = useCallback((text) => {
    const result = connector.autoImport(text, `manual.${sapConfig.file.format}`);
    setImportResult(result);
    setSyncLog(connector.getSyncLog());
    if (result.success && onImportData) {
      if (result.records) {
        onImportData(result.entity, result.records);
      } else if (result.imports) {
        result.imports.forEach(imp => {
          onImportData(imp.entity, [imp.record]);
        });
      }
    }
  }, [connector, sapConfig.file.format, onImportData]);

  const tabDefs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'import', label: 'Import from SAP' },
    { id: 'export', label: 'Export to SAP' },
    { id: 'mapping', label: 'Field Mapping' },
    { id: 'config', label: 'Connection Settings' }
  ];

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto', backgroundColor: COLORS.LIGHT_BG }}>
      {/* SAP Tab Bar */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', backgroundColor: COLORS.PANEL_BG, borderRadius: '6px', padding: '4px' }}>
        {tabDefs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === tab.id ? COLORS.NAVY : 'transparent',
              color: activeTab === tab.id ? '#FFFFFF' : COLORS.TEXT,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: activeTab === tab.id ? '600' : '400'
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          {/* Status Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
            {[
              { label: 'Connection Mode', value: sapConfig.connectionType === 'file' ? 'File Import/Export' : sapConfig.connectionType === 'rfc' ? 'SAP RFC' : 'OData API', color: COLORS.BLUE },
              { label: 'SAP Tables Mapped', value: tables.length, color: COLORS.GOLD },
              { label: 'Sync Operations', value: syncLog.length, color: '#00A8CB' },
              { label: 'Last Sync', value: syncLog.length > 0 ? new Date(syncLog[syncLog.length - 1].timestamp).toLocaleTimeString() : 'Never', color: '#002F65' }
            ].map((card, i) => (
              <div key={i} style={{
                backgroundColor: COLORS.PANEL_BG,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '6px',
                padding: '16px',
                borderLeft: `4px solid ${card.color}`
              }}>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>{card.label}</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.TEXT }}>{card.value}</div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Quick Actions</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { label: 'Import from SAP File', action: handleImportFile, color: COLORS.BLUE },
                { label: 'Export Equipment (EQUI)', action: () => handleExportTable('EQUI'), color: COLORS.GOLD },
                { label: 'Export Failures (QMEL)', action: () => handleExportTable('QMEL'), color: '#002F65' },
                { label: 'Export Maint. Orders (AUFK)', action: () => handleExportTable('AUFK'), color: '#00A8CB' },
                { label: 'Test Connection', action: handleTestConnection, color: COLORS.TEXT_DIM }
              ].map((btn, i) => (
                <button key={i} onClick={btn.action} style={{
                  padding: '8px 16px',
                  backgroundColor: btn.color + '20',
                  color: btn.color,
                  border: `1px solid ${btn.color}40`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>{btn.label}</button>
              ))}
            </div>
            {connectionStatus && (
              <div style={{
                marginTop: '12px',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                backgroundColor: connectionStatus.success ? '#00A8CB20' : '#002F6520',
                color: connectionStatus.success ? '#00A8CB' : '#002F65',
                border: `1px solid ${connectionStatus.success ? '#00A8CB40' : '#002F6540'}`
              }}>
                {connectionStatus.success ? '✓' : '✗'} {connectionStatus.message}
                {connectionStatus.note && <div style={{ marginTop: '4px', color: COLORS.TEXT_DIM }}>{connectionStatus.note}</div>}
              </div>
            )}
          </div>

          {/* SAP Table Overview */}
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY, fontSize: '14px', fontWeight: '600', color: '#FFFFFF' }}>
              SAP PM Table Mappings
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.NAVY }}>
                  {['SAP Table', 'Description', 'Local Entity', 'Mapped Fields', 'Local Records', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', borderBottom: `2px solid ${COLORS.GOLD}`, fontWeight: '600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tables.map((t, idx) => {
                  const localCount = (projectData[t.entity] || []).length;
                  const tableNames = { EQUI: 'Equipment Master', IFLO: 'Functional Location', QMEL: 'Maintenance Notification', AUFK: 'Maintenance Order', PLKO: 'Task List', MPLA: 'Maintenance Plan', STPO: 'BOM Items' };
                  return (
                    <tr key={t.table} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : COLORS.PANEL_BG + '80' }}>
                      <td style={{ padding: '8px 12px', color: COLORS.GOLD, fontWeight: '700', fontFamily: 'monospace' }}>{t.table}</td>
                      <td style={{ padding: '8px 12px', color: COLORS.TEXT }}>{tableNames[t.table] || t.table}</td>
                      <td style={{ padding: '8px 12px', color: COLORS.TEXT }}>{t.entity}</td>
                      <td style={{ padding: '8px 12px', color: COLORS.TEXT, textAlign: 'center' }}>{t.fieldCount}</td>
                      <td style={{ padding: '8px 12px', color: localCount > 0 ? '#00A8CB' : COLORS.TEXT_DIM, fontWeight: '600', textAlign: 'center' }}>{localCount}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => handleExportTable(t.table)} style={{ padding: '4px 8px', fontSize: '11px', backgroundColor: COLORS.BLUE + '20', color: COLORS.BLUE, border: `1px solid ${COLORS.BLUE}40`, borderRadius: '3px', cursor: 'pointer' }}>Export</button>
                          <button onClick={() => { setSelectedTable(t.table); setActiveTab('mapping'); }} style={{ padding: '4px 8px', fontSize: '11px', backgroundColor: COLORS.GOLD + '20', color: COLORS.GOLD, border: `1px solid ${COLORS.GOLD}40`, borderRadius: '3px', cursor: 'pointer' }}>Map</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sync Log */}
          {syncLog.length > 0 && (
            <div style={{ marginTop: '16px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY, fontSize: '13px', fontWeight: '600', color: '#FFFFFF' }}>
                Sync Log
              </div>
              <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                {syncLog.slice().reverse().map((entry, idx) => (
                  <div key={idx} style={{ padding: '6px 12px', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ color: COLORS.TEXT_DIM, fontFamily: 'monospace' }}>{new Date(entry.timestamp).toLocaleString()}</span>
                    <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: '600', backgroundColor: entry.direction === 'import' ? '#00A8CB20' : COLORS.BLUE + '20', color: entry.direction === 'import' ? '#00A8CB' : COLORS.BLUE }}>{entry.direction.toUpperCase()}</span>
                    <span style={{ color: COLORS.GOLD, fontFamily: 'monospace' }}>{entry.table}</span>
                    <span style={{ color: COLORS.TEXT }}>{entry.recordCount} records</span>
                    <span style={{ color: COLORS.TEXT_DIM }}>({entry.format})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div>
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '20px 24px', marginBottom: '16px' }}>
            <div style={{ fontSize: '9px', fontWeight: '600', color: 'rgba(0,47,101,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px' }}>Data Import</div>
            <h3 style={{ margin: '0 0 8px 0', fontFamily: "'Georgia', serif", color: COLORS.TEXT, fontSize: '16px', fontWeight: '400' }}>Import from SAP PM</h3>
            <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '20px', lineHeight: '1.5' }}>
              Import equipment hierarchies, failure data, maintenance orders, and more.
              Supports CSV exports, IDoc XML, and OData JSON — the connector auto-detects the SAP table and maps fields automatically.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
              <button onClick={handleImportFile} style={{ padding: '9px 20px', backgroundColor: '#002F65', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase', transition: 'background-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#003D7A'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#002F65'}
              >
                Import File
              </button>
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                style={{ padding: '9px 12px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', fontSize: '11px' }}
              >
                {tables.map(t => <option key={t.table} value={t.table}>{t.table} — {t.entity}</option>)}
              </select>
              <span style={{ fontSize: '10px', color: 'rgba(0,47,101,0.35)', fontStyle: 'italic' }}>CSV, XML, or JSON</span>
            </div>

            {/* Manual paste area */}
            <div style={{ marginBottom: '4px' }}>
              <label style={{ display: 'block', color: 'rgba(0,47,101,0.4)', fontSize: '10px', marginBottom: '6px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Or paste data directly</label>
              <textarea
                id="sap-paste-area"
                placeholder={`Paste CSV, IDoc XML, or JSON data from SAP here...\n\nExample CSV (EQUI table):\nEQUNR,TXTMI,EQTYP,TPLNR,SWERK\n"10001234","Pump A1","M","FLO-001","1000"`}
                style={{
                  width: '100%',
                  height: '140px',
                  backgroundColor: '#F7F8FA',
                  color: COLORS.TEXT,
                  border: `1px solid ${COLORS.BORDER}`,
                  borderRadius: '3px',
                  padding: '14px',
                  fontFamily: "'SF Mono', 'Consolas', monospace",
                  fontSize: '11px',
                  resize: 'vertical',
                  lineHeight: '1.5',
                  boxSizing: 'border-box'
                }}
              />
              <button
                onClick={() => {
                  const textarea = document.getElementById('sap-paste-area');
                  if (textarea?.value) handleManualImport(textarea.value);
                }}
                style={{ marginTop: '10px', padding: '8px 18px', backgroundColor: 'transparent', color: '#00A8CB', border: '1px solid rgba(0,168,203,0.3)', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: '600', letterSpacing: '0.3px', textTransform: 'uppercase', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(0,168,203,0.06)'; e.currentTarget.style.borderColor = '#00A8CB'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(0,168,203,0.3)'; }}
              >
                Process Pasted Data
              </button>
            </div>
          </div>

          {/* Import Results */}
          {importResult && (
            <div style={{
              backgroundColor: COLORS.PANEL_BG,
              border: `1px solid ${importResult.success ? 'rgba(0,168,203,0.2)' : 'rgba(0,47,101,0.15)'}`,
              borderRadius: '4px',
              padding: '20px 24px',
              borderTop: `2px solid ${importResult.success ? '#00A8CB' : '#002F65'}`
            }}>
              <h3 style={{ margin: '0 0 8px 0', fontFamily: "'Georgia', serif", color: importResult.success ? '#002F65' : '#002F65', fontSize: '14px', fontWeight: '400' }}>
                {importResult.success ? 'Import Successful' : 'Import Failed'}
              </h3>
              {importResult.success ? (
                <div style={{ fontSize: '12px', color: COLORS.TEXT }}>
                  <div>Table: <strong style={{ color: COLORS.GOLD }}>{importResult.table || 'Auto-detected'}</strong></div>
                  <div>Entity: <strong>{importResult.entity || 'Multiple'}</strong></div>
                  <div>Records imported: <strong>{importResult.mappedRows || importResult.totalRecords || 0}</strong></div>
                  {importResult.warnings?.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ color: COLORS.GOLD, fontWeight: '600' }}>Warnings:</div>
                      {importResult.warnings.map((w, i) => <div key={i} style={{ color: COLORS.TEXT_DIM, paddingLeft: '8px' }}>• {w}</div>)}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#002F65' }}>{importResult.error}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div>
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', color: COLORS.TEXT, fontSize: '14px' }}>Export to SAP</h3>
            <p style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '16px' }}>
              Export your project data in SAP-compatible formats for upload to SAP PM via LSMW, BAPI, IDoc, or direct table import.
            </p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
              <label style={{ color: COLORS.TEXT, fontSize: '12px' }}>Export Format:</label>
              <select
                value={sapConfig.file.format}
                onChange={(e) => setSapConfig(prev => ({ ...prev, file: { ...prev.file, format: e.target.value } }))}
                style={{ padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="csv">CSV (SAP LSMW / SE16)</option>
                <option value="idoc">IDoc XML (ALE/EDI)</option>
                <option value="json">JSON (OData API / S/4HANA)</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {tables.map(t => {
                const localCount = (projectData[t.entity] || []).length;
                const tableNames = { EQUI: 'Equipment', IFLO: 'Func. Locations', QMEL: 'Notifications', AUFK: 'Maint. Orders', PLKO: 'Task Lists', MPLA: 'Maint. Plans', STPO: 'BOM / Spares' };
                return (
                  <div key={t.table} style={{
                    backgroundColor: COLORS.PANEL_BG,
                    border: `1px solid ${COLORS.BORDER}`,
                    borderRadius: '6px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: COLORS.GOLD, fontWeight: '700', fontFamily: 'monospace' }}>{t.table}</span>
                      <span style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{t.fieldCount} fields</span>
                    </div>
                    <div style={{ color: COLORS.TEXT, fontSize: '12px' }}>{tableNames[t.table] || t.entity}</div>
                    <div style={{ color: localCount > 0 ? '#00A8CB' : COLORS.TEXT_DIM, fontSize: '11px' }}>{localCount} records available</div>
                    <button
                      onClick={() => handleExportTable(t.table)}
                      disabled={localCount === 0}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: localCount > 0 ? COLORS.BLUE : COLORS.BORDER,
                        color: localCount > 0 ? '#fff' : COLORS.TEXT_DIM,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: localCount > 0 ? 'pointer' : 'not-allowed',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}
                    >Export {sapConfig.file.format.toUpperCase()}</button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Export Preview */}
          {exportContent && (
            <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY, fontSize: '13px', fontWeight: '600', color: '#FFFFFF', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Export Preview
                <button onClick={() => { navigator.clipboard?.writeText(exportContent); }} style={{ padding: '4px 8px', fontSize: '11px', backgroundColor: COLORS.GOLD + '20', color: COLORS.GOLD, border: `1px solid ${COLORS.GOLD}40`, borderRadius: '3px', cursor: 'pointer' }}>Copy to Clipboard</button>
              </div>
              <pre style={{ margin: 0, padding: '12px', color: COLORS.TEXT, fontSize: '11px', fontFamily: 'monospace', maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{exportContent.substring(0, 5000)}{exportContent.length > 5000 ? '\n\n... (truncated)' : ''}</pre>
            </div>
          )}
        </div>
      )}

      {/* Field Mapping Tab */}
      {activeTab === 'mapping' && (
        <div>
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: COLORS.TEXT, fontSize: '14px' }}>Field Mapping: SAP ↔ OptimalAvailability</h3>
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                style={{ padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}
              >
                {tables.map(t => <option key={t.table} value={t.table}>{t.table}</option>)}
              </select>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.NAVY }}>
                  {['SAP Field', 'SAP Label', 'Local Field', 'Direction'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', borderBottom: `2px solid ${COLORS.GOLD}`, fontWeight: '600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(tables.find(t => t.table === selectedTable)?.fields || []).map((f, idx) => (
                  <tr key={f.sapField} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : COLORS.PANEL_BG + '80' }}>
                    <td style={{ padding: '8px 12px', color: COLORS.GOLD, fontFamily: 'monospace', fontWeight: '600' }}>{f.sapField}</td>
                    <td style={{ padding: '8px 12px', color: COLORS.TEXT }}>{f.label}</td>
                    <td style={{ padding: '8px 12px', color: '#00A8CB', fontFamily: 'monospace' }}>{f.localField}</td>
                    <td style={{ padding: '8px 12px', color: COLORS.TEXT_DIM }}>↔ Bidirectional</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Connection Settings Tab */}
      {activeTab === 'config' && (
        <div>
          <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '20px 24px' }}>
            <div style={{ fontSize: '9px', fontWeight: '600', color: 'rgba(0,47,101,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px' }}>Configuration</div>
            <h3 style={{ margin: '0 0 20px 0', fontFamily: "'Georgia', serif", color: COLORS.TEXT, fontSize: '16px', fontWeight: '400' }}>SAP Connection</h3>

            {/* Connection Type */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: 'rgba(0,47,101,0.4)', fontSize: '10px', marginBottom: '8px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Connection Type</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {[
                  { id: 'file', label: 'File Import/Export', desc: 'CSV, IDoc XML, JSON files' },
                  { id: 'rfc', label: 'SAP RFC (JCo)', desc: 'Direct RFC connection' },
                  { id: 'odata', label: 'OData API', desc: 'S/4HANA REST API' }
                ].map(ct => (
                  <button
                    key={ct.id}
                    onClick={() => setSapConfig(prev => ({ ...prev, connectionType: ct.id }))}
                    style={{
                      flex: 1,
                      padding: '14px 16px',
                      backgroundColor: sapConfig.connectionType === ct.id ? '#002F65' : '#F7F8FA',
                      border: `1px solid ${sapConfig.connectionType === ct.id ? '#002F65' : COLORS.BORDER}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ color: sapConfig.connectionType === ct.id ? '#FFFFFF' : COLORS.TEXT, fontSize: '12px', fontWeight: '600', letterSpacing: '0.01em' }}>{ct.label}</div>
                    <div style={{ color: sapConfig.connectionType === ct.id ? 'rgba(255,255,255,0.6)' : COLORS.TEXT_DIM, fontSize: '10px', marginTop: '4px' }}>{ct.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* RFC Settings */}
            {sapConfig.connectionType === 'rfc' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {[
                  { key: 'host', label: 'Application Server', placeholder: 'sap-server.company.com' },
                  { key: 'systemNumber', label: 'System Number', placeholder: '00' },
                  { key: 'client', label: 'Client', placeholder: '100' },
                  { key: 'user', label: 'SAP User', placeholder: 'RFC_USER' },
                  { key: 'password', label: 'Password', placeholder: '••••••••', type: 'password' },
                  { key: 'language', label: 'Language', placeholder: 'EN' }
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>{field.label}</label>
                    <input
                      type={field.type || 'text'}
                      value={sapConfig.rfc[field.key] || ''}
                      onChange={(e) => setSapConfig(prev => ({ ...prev, rfc: { ...prev.rfc, [field.key]: e.target.value } }))}
                      placeholder={field.placeholder}
                      style={{ width: '100%', padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* OData Settings */}
            {sapConfig.connectionType === 'odata' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {[
                  { key: 'baseUrl', label: 'OData Base URL', placeholder: 'https://my-s4hana.company.com/sap/opu/odata/sap/' },
                  { key: 'apiKey', label: 'API Key / Token', placeholder: 'Bearer token or API key', type: 'password' }
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>{field.label}</label>
                    <input
                      type={field.type || 'text'}
                      value={sapConfig.odata[field.key] || ''}
                      onChange={(e) => setSapConfig(prev => ({ ...prev, odata: { ...prev.odata, [field.key]: e.target.value } }))}
                      placeholder={field.placeholder}
                      style={{ width: '100%', padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* File Format Settings */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: COLORS.TEXT, fontSize: '12px', marginBottom: '6px', fontWeight: '600' }}>File Format Settings</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>Default Format</label>
                  <select value={sapConfig.file.format} onChange={(e) => setSapConfig(prev => ({ ...prev, file: { ...prev.file, format: e.target.value } }))} style={{ width: '100%', padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}>
                    <option value="csv">CSV</option>
                    <option value="idoc">IDoc XML</option>
                    <option value="json">JSON (OData)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>CSV Delimiter</label>
                  <select value={sapConfig.file.delimiter} onChange={(e) => setSapConfig(prev => ({ ...prev, file: { ...prev.file, delimiter: e.target.value } }))} style={{ width: '100%', padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}>
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: COLORS.TEXT_DIM, fontSize: '11px', marginBottom: '4px' }}>SAP Date Format</label>
                  <select value={sapConfig.file.dateFormat} onChange={(e) => setSapConfig(prev => ({ ...prev, file: { ...prev.file, dateFormat: e.target.value } }))} style={{ width: '100%', padding: '8px', backgroundColor: '#FFFFFF', color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', fontSize: '12px' }}>
                    <option value="YYYYMMDD">YYYYMMDD (SAP Standard)</option>
                    <option value="DD.MM.YYYY">DD.MM.YYYY (European)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (ISO 8601)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Test Connection Button */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleTestConnection} style={{ padding: '10px 20px', backgroundColor: COLORS.BLUE, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                Test Connection
              </button>
              <button onClick={() => setSapConfig(SAPConnector.DEFAULT_CONFIG)} style={{ padding: '10px 20px', backgroundColor: 'transparent', color: COLORS.TEXT_DIM, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                Reset to Defaults
              </button>
            </div>

            {connectionStatus && (
              <div style={{
                marginTop: '12px',
                padding: '10px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                backgroundColor: connectionStatus.success ? '#00A8CB20' : '#002F6520',
                color: connectionStatus.success ? '#00A8CB' : '#002F65',
                border: `1px solid ${connectionStatus.success ? '#00A8CB40' : '#002F6540'}`
              }}>
                {connectionStatus.success ? '✓' : '✗'} {connectionStatus.message}
                {connectionStatus.note && <div style={{ marginTop: '4px', color: COLORS.TEXT_DIM }}>{connectionStatus.note}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SPARES ANALYSIS VIEW (Spares Provisioning/Optimization)
// ============================================================================
function SparesAnalysisView({ spares, equipments, simConfig, projectData, setProjectData }) {
  // G11: Scan RCM tasks and causes for spare part requirements not yet in spares list
  const rcmRecommendedSpares = useMemo(() => {
    const existingSpareIds = new Set((spares || []).map(s => s.id));
    const rcmSpares = [];
    // Scan scheduledtasks with source=RCM
    (projectData?.scheduledtasks || []).filter(t => t.source === 'RCM' && t.spareRequirement).forEach(t => {
      if (!existingSpareIds.has('RCM-SP-' + t.id)) {
        rcmSpares.push({ id: 'RCM-SP-' + t.id, description: t.spareRequirement, equipmentId: t.equipmentId || t.equipment, source: 'RCM', taskRef: t.id, demandRate: 1 });
      }
    });
    // Scan rcmcauses with spareRequirement
    (projectData?.rcmcauses || []).filter(c => c.spareRequirement).forEach(c => {
      if (!existingSpareIds.has('RCM-SP-' + c.id)) {
        rcmSpares.push({ id: 'RCM-SP-' + c.id, description: c.spareRequirement, equipmentId: c.equipmentId || c.equipment, source: 'RCM', causeRef: c.id, demandRate: c.lambda ? c.lambda * 8760 : 1 });
      }
    });
    return rcmSpares;
  }, [spares, projectData]);

  // G11: Import RCM recommended spares into project
  const importRCMSpares = useCallback(() => {
    if (rcmRecommendedSpares.length === 0) { alert('No new RCM spare recommendations to import.'); return; }
    setProjectData(prev => ({
      ...prev,
      spares: [...(prev.spares || []), ...rcmRecommendedSpares.map(rs => ({
        id: rs.id, description: rs.description, equipment: rs.equipmentId, equipmentId: rs.equipmentId,
        source: rs.source, currentStock: 0, reorderPoint: 1, leadTime: 30, unitCost: 0,
        criticality: 'production', demandRate: rs.demandRate, rcmTaskRef: rs.taskRef || rs.causeRef
      }))]
    }));
    alert(`Imported ${rcmRecommendedSpares.length} RCM-recommended spare parts`);
  }, [rcmRecommendedSpares, setProjectData]);

  const [selectedSpare, setSelectedSpare] = useState(null);
  const [analysisMethod, setAnalysisMethod] = useState('zscore'); // 'zscore' | 'poisson' | 'weibull'
  const [apiStatus, setApiStatus] = useState(null); // null | 'connecting' | 'connected' | 'error'
  const [apiMessage, setApiMessage] = useState('');
  const [showPolicyMatrix, setShowPolicyMatrix] = useState(false);

  const failureModes = projectData?.failuremodes || [];

  // Get linked failure modes for a spare
  const getLinkedFailureModes = useCallback((spareId) => {
    return failureModes.filter(fm => (fm.spareRequirement || '') === spareId || (fm.linkedSpares || []).includes(spareId));
  }, [failureModes]);

  // Calculate spares provisioning metrics using the full engine
  const sparesAnalysis = useMemo(() => {
    return spares.map(spare => {
      const linkedEquipment = equipments.filter(e =>
        e.spareReq === spare.id || (e.spareReq && e.spareReq.includes(spare.id))
      );
      const linkedFMs = getLinkedFailureModes(spare.id);
      const missionTime = simConfig.missionTime || 8760;

      // Classify criticality using the 4-tier engine
      const tier = classifyCriticality(spare, linkedFMs);
      const policy = DEFAULT_STOCKING_POLICY[tier];

      // Compute demand rate from linked equipment failure rates
      const demandRate = linkedEquipment.reduce((sum, e) => {
        const fr = parseFloat(e.failureRate) || 0.001;
        return sum + fr;
      }, 0);
      const expectedDemand = demandRate * missionTime;
      const currentStock = parseInt(spare.quantity) || parseInt(spare.stockLevel) || 0;
      const unitCost = parseFloat(spare.unitCost) || parseFloat(spare.cost) || 0;

      // Run the full analyseSpare engine (Z-score + Poisson + Weibull if available)
      const enrichedSpare = {
        ...spare,
        annualDemand: expectedDemand,
        expectedDemand: expectedDemand,
        criticalityRating: linkedFMs.length > 0 ? Math.max(...linkedFMs.map(fm => parseInt(fm.severity) || 5)) : 5
      };

      // Find Weibull params from linked equipment for Weibull demand forecast
      let weibullParams = null;
      if (linkedEquipment.length > 0) {
        const eqWithWeibull = linkedEquipment.find(e => e.weibullBeta && e.weibullEta);
        if (eqWithWeibull) {
          weibullParams = { beta: parseFloat(eqWithWeibull.weibullBeta), eta: parseFloat(eqWithWeibull.weibullEta) };
        }
      }

      const analysis = analyseSpare(enrichedSpare, linkedFMs, spare.demandHistory || [], weibullParams);

      // Pick recommended value based on selected method
      let recommended, fillRate, safetyStock, reorderPoint;
      if (analysisMethod === 'zscore') {
        reorderPoint = analysis.zscoreROP?.reorderPoint || 0;
        safetyStock = analysis.zscoreROP?.safetyStock || 0;
        recommended = reorderPoint;
        fillRate = currentStock >= reorderPoint ? 99 : (currentStock / Math.max(reorderPoint, 1)) * 100;
      } else if (analysisMethod === 'weibull' && analysis.weibullDemand) {
        recommended = Math.ceil(analysis.weibullDemand.expectedDemand12m || 0);
        fillRate = currentStock >= recommended ? 99 : (currentStock / Math.max(recommended, 1)) * 100;
        safetyStock = analysis.zscoreROP?.safetyStock || 0;
        reorderPoint = recommended;
      } else {
        recommended = analysis.poissonRecommended || 0;
        fillRate = (analysis.poissonFillRate || 0) * 100;
        safetyStock = 0;
        reorderPoint = recommended;
      }

      const shortfall = Math.max(0, recommended - currentStock);
      const annualCost = unitCost * Math.max(recommended, currentStock);

      return {
        ...spare,
        linkedEquipmentCount: linkedEquipment.length,
        linkedFMCount: linkedFMs.length,
        demandRate: demandRate.toFixed(6),
        expectedDemand: expectedDemand.toFixed(2),
        currentStock,
        fillRate: fillRate.toFixed(1),
        recommended,
        shortfall,
        annualCost: annualCost.toFixed(2),
        unitCost,
        tier,
        tierLabel: policy.label,
        tierColor: policy.color,
        tierBg: policy.bgColor,
        serviceLevel: policy.serviceLevelPct,
        safetyStock: Math.ceil(safetyStock),
        reorderPoint,
        analysis,
        status: fillRate >= 95 ? 'Adequate' : fillRate >= 80 ? 'Review' : 'Critical',
        weibullParams
      };
    });
  }, [spares, equipments, simConfig, failureModes, analysisMethod, getLinkedFailureModes]);

  const totalCost = sparesAnalysis.reduce((s, a) => s + parseFloat(a.annualCost), 0);
  const criticalCount = sparesAnalysis.filter(s => s.status === 'Critical').length;
  const reviewCount = sparesAnalysis.filter(s => s.status === 'Review').length;
  const safetyCriticalCount = sparesAnalysis.filter(s => s.tier === 'safety_critical').length;

  // API connection test
  const testApiConnection = useCallback(async () => {
    setApiStatus('connecting');
    setApiMessage('Connecting to spare.optimalworld.org...');
    try {
      const api = new OptimalSparesAPI('https://spare.optimalworld.org/api/v1');
      const result = await api.authenticate('test@optimal.world', 'test');
      if (result) {
        setApiStatus('connected');
        setApiMessage('Connected to OptimalSPARES API');
      } else {
        setApiStatus('error');
        setApiMessage('Authentication failed — check credentials');
      }
    } catch (err) {
      setApiStatus('error');
      setApiMessage(`Connection failed: ${err.message}`);
    }
  }, []);

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Header with method selector and API status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT }}>Spares Optimisation Analysis</div>
          <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>Z-score ROP • 4-tier stocking policy • Weibull demand forecasting</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Method selector */}
          <div style={{ display: 'flex', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', overflow: 'hidden' }}>
            {[
              { key: 'zscore', label: 'Z-Score ROP' },
              { key: 'poisson', label: 'Poisson' },
              { key: 'weibull', label: 'Weibull' }
            ].map(m => (
              <button key={m.key} onClick={() => setAnalysisMethod(m.key)} style={{
                padding: '4px 10px', fontSize: '10px', fontWeight: '600', border: 'none', cursor: 'pointer',
                backgroundColor: analysisMethod === m.key ? COLORS.NAVY : COLORS.PANEL_BG,
                color: analysisMethod === m.key ? '#fff' : COLORS.TEXT_DIM
              }}>{m.label}</button>
            ))}
          </div>
          {/* Stocking Policy toggle */}
          <button onClick={() => setShowPolicyMatrix(!showPolicyMatrix)} style={{
            padding: '4px 10px', fontSize: '10px', fontWeight: '600', border: `1px solid ${COLORS.BORDER}`,
            borderRadius: '4px', cursor: 'pointer', backgroundColor: showPolicyMatrix ? COLORS.GOLD + '20' : COLORS.PANEL_BG,
            color: showPolicyMatrix ? COLORS.GOLD : COLORS.TEXT_DIM
          }}>Policy Matrix</button>
          {/* API status */}
          <button onClick={testApiConnection} style={{
            padding: '4px 10px', fontSize: '10px', fontWeight: '600', border: `1px solid ${COLORS.BORDER}`,
            borderRadius: '4px', cursor: 'pointer',
            backgroundColor: apiStatus === 'connected' ? '#2A9D8F15' : apiStatus === 'error' ? '#B91C1C15' : COLORS.PANEL_BG,
            color: apiStatus === 'connected' ? '#2A9D8F' : apiStatus === 'error' ? '#B91C1C' : COLORS.TEXT_DIM
          }}>{apiStatus === 'connecting' ? 'Connecting...' : apiStatus === 'connected' ? 'API Connected' : apiStatus === 'error' ? 'API Error' : 'Connect API'}</button>
        </div>
      </div>

      {/* 4-Tier Stocking Policy Matrix (collapsible) */}
      {showPolicyMatrix && (
        <div style={{ marginBottom: '16px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#fff' }}>4-Tier Stocking Policy Matrix</div>
            <div style={{ fontSize: '10px', color: '#ffffff90', marginTop: '2px' }}>ISO 55001 aligned criticality-based inventory strategy</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0' }}>
            {Object.entries(DEFAULT_STOCKING_POLICY).map(([key, p]) => {
              const count = sparesAnalysis.filter(s => s.tier === key).length;
              return (
                <div key={key} style={{ padding: '14px', borderRight: `1px solid ${COLORS.BORDER}`, backgroundColor: p.bgColor }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: p.color }}>{p.label}</span>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: p.color + '20', color: p.color }}>{count}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '6px' }}>{p.description}</div>
                  <div style={{ display: 'grid', gap: '3px', fontSize: '10px' }}>
                    <div><span style={{ color: COLORS.TEXT_DIM }}>Service Level:</span> <span style={{ fontWeight: '600', color: p.color }}>{p.serviceLevelPct}%</span></div>
                    <div><span style={{ color: COLORS.TEXT_DIM }}>Min Stock:</span> <span style={{ color: COLORS.TEXT }}>{p.minStockRule}</span></div>
                    <div><span style={{ color: COLORS.TEXT_DIM }}>Max Stock:</span> <span style={{ color: COLORS.TEXT }}>{p.maxStockRule}</span></div>
                    <div><span style={{ color: COLORS.TEXT_DIM }}>Lead Time Tol:</span> <span style={{ color: COLORS.TEXT }}>{p.leadTimeTolDays}d</span></div>
                    <div><span style={{ color: COLORS.TEXT_DIM }}>Action:</span> <span style={{ color: COLORS.TEXT }}>{p.action.replace(/_/g, ' ')}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '16px' }}>
        {[
          { label: 'Total Spares', value: spares.length, color: COLORS.BLUE },
          { label: 'Safety-Critical', value: safetyCriticalCount, color: '#991B1B' },
          { label: 'Critical (< 80% Fill)', value: criticalCount, color: '#002F65' },
          { label: 'Review Required', value: reviewCount, color: COLORS.GOLD },
          { label: 'Annual Holding Cost', value: formatCurrency(totalCost), color: '#00A8CB' }
        ].map((card, i) => (
          <div key={i} style={{
            backgroundColor: COLORS.PANEL_BG,
            border: `1px solid ${COLORS.BORDER}`,
            borderRadius: '6px',
            padding: '14px',
            borderLeft: `4px solid ${card.color}`
          }}>
            <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>{card.label}</div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.TEXT }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Spares Table — enhanced with stocking tier and Z-score */}
      <div style={{
        backgroundColor: COLORS.PANEL_BG,
        border: `1px solid ${COLORS.BORDER}`,
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '16px'
      }}>
        <div style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${COLORS.BORDER}`,
          fontSize: '14px',
          fontWeight: '600',
          color: '#FFFFFF',
          backgroundColor: COLORS.NAVY,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Spares Provisioning Analysis ({analysisMethod === 'zscore' ? 'Z-Score ROP' : analysisMethod === 'weibull' ? 'Weibull Demand' : 'Poisson'} Model)</span>
          <span style={{ fontSize: '10px', color: COLORS.GOLD }}>{sparesAnalysis.length} items</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.NAVY }}>
                {['ID', 'Description', 'Stocking Tier', 'Service %', 'Linked Eq.', 'Expected Demand', 'Current', 'ROP', 'Safety Stock', 'Shortfall', 'Unit Cost', 'Annual Cost', 'Status'].map(h => (
                  <th key={h} style={{
                    padding: '10px 8px',
                    textAlign: 'left',
                    color: '#FFFFFF',
                    borderBottom: `2px solid ${COLORS.GOLD}`,
                    whiteSpace: 'nowrap',
                    fontWeight: '600',
                    fontSize: '10px'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sparesAnalysis.map((spare, idx) => (
                <tr
                  key={spare.id}
                  onClick={() => setSelectedSpare(spare)}
                  style={{
                    backgroundColor: selectedSpare?.id === spare.id ? 'rgba(0,168,203,0.15)' : idx % 2 === 0 ? 'transparent' : COLORS.PANEL_BG + '80',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s'
                  }}
                >
                  <td style={{ padding: '8px', color: COLORS.GOLD, fontWeight: '600' }}>{spare.id}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spare.name || spare.description || '—'}</td>
                  <td style={{ padding: '8px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                      backgroundColor: spare.tierBg, color: spare.tierColor
                    }}>{spare.tierLabel}</span>
                  </td>
                  <td style={{ padding: '8px', color: COLORS.TEXT, fontWeight: '600', textAlign: 'center' }}>{spare.serviceLevel}%</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT, textAlign: 'center' }}>{spare.linkedEquipmentCount}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{spare.expectedDemand}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT, textAlign: 'center' }}>{spare.currentStock}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT, textAlign: 'center', fontWeight: '600' }}>{spare.reorderPoint}</td>
                  <td style={{ padding: '8px', color: '#00A8CB', textAlign: 'center', fontWeight: '600' }}>{spare.safetyStock}</td>
                  <td style={{ padding: '8px', textAlign: 'center', color: spare.shortfall > 0 ? '#002F65' : '#2A9D8F', fontWeight: '600' }}>
                    {spare.shortfall > 0 ? `+${spare.shortfall}` : '0'}
                  </td>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{formatCurrencyFull(spare.unitCost)}</td>
                  <td style={{ padding: '8px', color: COLORS.TEXT }}>{formatCurrency(spare.annualCost)}</td>
                  <td style={{ padding: '8px' }}>
                    <span style={{
                      padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                      backgroundColor: spare.status === 'Adequate' ? '#2A9D8F20' : spare.status === 'Review' ? COLORS.GOLD + '20' : '#B91C1C20',
                      color: spare.status === 'Adequate' ? '#2A9D8F' : spare.status === 'Review' ? COLORS.GOLD : '#B91C1C'
                    }}>{spare.status}</span>
                  </td>
                </tr>
              ))}
              {sparesAnalysis.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ padding: '40px', textAlign: 'center', color: COLORS.TEXT_DIM }}>
                    No spares defined. Add spares in the Project Structure tree to run provisioning analysis.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Spare Detail Panel — Z-score ROP breakdown */}
      {selectedSpare && (
        <div style={{ marginBottom: '16px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT }}>{selectedSpare.name || selectedSpare.description || selectedSpare.id}</div>
              <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Detailed provisioning analysis</div>
            </div>
            <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', backgroundColor: selectedSpare.tierBg, color: selectedSpare.tierColor }}>{selectedSpare.tierLabel}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {/* Z-Score ROP breakdown */}
            <div style={{ backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '14px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.NAVY, marginBottom: '10px' }}>Z-Score Reorder Point</div>
              {selectedSpare.analysis?.zscoreROP ? (() => {
                const rop = selectedSpare.analysis.zscoreROP;
                return (
                  <div style={{ display: 'grid', gap: '6px', fontSize: '11px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Service Level</span><span style={{ fontWeight: '600' }}>{(rop.serviceLevel * 100).toFixed(0)}%</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Z-Score</span><span style={{ fontWeight: '600' }}>{rop.zScore?.toFixed(3)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Avg Daily Demand</span><span style={{ fontWeight: '600' }}>{rop.avgDailyDemand?.toFixed(4)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Avg Lead Time</span><span style={{ fontWeight: '600' }}>{rop.avgLeadTimeDays?.toFixed(0)}d</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Demand During LT</span><span style={{ fontWeight: '600' }}>{rop.demandDuringLeadTime?.toFixed(2)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${COLORS.BORDER}`, paddingTop: '6px' }}><span style={{ color: COLORS.TEXT_DIM }}>Safety Stock</span><span style={{ fontWeight: '700', color: '#00A8CB' }}>{Math.ceil(rop.safetyStock || 0)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700' }}><span style={{ color: COLORS.NAVY }}>Reorder Point</span><span style={{ color: COLORS.NAVY, fontSize: '14px' }}>{rop.reorderPoint}</span></div>
                    <div style={{ marginTop: '4px', padding: '6px 8px', backgroundColor: selectedSpare.currentStock >= rop.reorderPoint ? '#2A9D8F10' : '#B91C1C10', borderRadius: '3px', fontSize: '10px', textAlign: 'center', fontWeight: '600', color: selectedSpare.currentStock >= rop.reorderPoint ? '#2A9D8F' : '#B91C1C' }}>
                      {selectedSpare.currentStock >= rop.reorderPoint ? 'Stock above ROP — no action needed' : `Order ${Math.max(0, rop.reorderPoint - selectedSpare.currentStock)} units to reach ROP`}
                    </div>
                  </div>
                );
              })() : <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Insufficient data for Z-score calculation</div>}
            </div>

            {/* Weibull demand forecast */}
            <div style={{ backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '14px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.NAVY, marginBottom: '10px' }}>Weibull Demand Forecast</div>
              {selectedSpare.analysis?.weibullDemand ? (() => {
                const wd = selectedSpare.analysis.weibullDemand;
                return (
                  <div style={{ display: 'grid', gap: '6px', fontSize: '11px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Shape (Beta)</span><span style={{ fontWeight: '600' }}>{wd.beta?.toFixed(3)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Scale (Eta)</span><span style={{ fontWeight: '600' }}>{wd.eta?.toFixed(1)}h</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Hazard Rate h(t)</span><span style={{ fontWeight: '600' }}>{wd.currentHazardRate?.toExponential(3)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${COLORS.BORDER}`, paddingTop: '6px' }}><span style={{ color: COLORS.TEXT_DIM }}>Demand (6m)</span><span style={{ fontWeight: '600' }}>{wd.expectedDemand6m?.toFixed(2)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Demand (12m)</span><span style={{ fontWeight: '700', color: COLORS.NAVY }}>{wd.expectedDemand12m?.toFixed(2)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Demand (24m)</span><span style={{ fontWeight: '600' }}>{wd.expectedDemand24m?.toFixed(2)}</span></div>
                    <div style={{ marginTop: '6px', padding: '6px', backgroundColor: '#f0f9ff', borderRadius: '3px', fontSize: '9px', color: COLORS.TEXT_DIM, textAlign: 'center' }}>
                      h(t) = (beta/eta)(t/eta)^(beta-1) per IEC 61649
                    </div>
                  </div>
                );
              })() : (
                <div style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>
                  No Weibull parameters available. Link spare to equipment with Weibull analysis to enable demand forecasting.
                </div>
              )}
            </div>

            {/* Stocking policy & cost */}
            <div style={{ backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '14px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.NAVY, marginBottom: '10px' }}>Stocking Policy & Cost</div>
              <div style={{ display: 'grid', gap: '6px', fontSize: '11px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Criticality Tier</span><span style={{ fontWeight: '700', color: selectedSpare.tierColor }}>{selectedSpare.tierLabel}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Target Service Level</span><span style={{ fontWeight: '600' }}>{selectedSpare.serviceLevel}%</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Current Stock</span><span style={{ fontWeight: '600' }}>{selectedSpare.currentStock}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Recommended (ROP)</span><span style={{ fontWeight: '700', color: COLORS.NAVY }}>{selectedSpare.reorderPoint}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${COLORS.BORDER}`, paddingTop: '6px' }}><span style={{ color: COLORS.TEXT_DIM }}>Unit Cost</span><span style={{ fontWeight: '600' }}>{formatCurrencyFull(selectedSpare.unitCost)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Annual Holding Cost</span><span style={{ fontWeight: '600' }}>{formatCurrency(selectedSpare.annualCost)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Linked Failure Modes</span><span style={{ fontWeight: '600' }}>{selectedSpare.linkedFMCount}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Linked Equipment</span><span style={{ fontWeight: '600' }}>{selectedSpare.linkedEquipmentCount}</span></div>
              </div>
              {selectedSpare.analysis?.recommendation && (
                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#2A9D8F10', border: '1px solid #2A9D8F30', borderRadius: '4px', fontSize: '10px', color: '#2A9D8F', fontWeight: '600', textAlign: 'center' }}>
                  {selectedSpare.analysis.recommendation}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3-Level Spare Parts Logistics Chain */}
      <div style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '12px' }}>3-Level Spare Parts Logistics Chain</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          {[
            { level: 'Local', desc: 'On-site inventory', stock: (sparesAnalysis.reduce((s, sp) => s + sp.currentStock, 0) * 0.4).toFixed(0), icon: '📍' },
            { level: 'Regional', desc: 'Regional warehouse', stock: (sparesAnalysis.reduce((s, sp) => s + sp.currentStock, 0) * 0.35).toFixed(0), icon: '🏭' },
            { level: 'Manufacturing', desc: 'Manufacturer stock', stock: (sparesAnalysis.reduce((s, sp) => s + sp.currentStock, 0) * 0.25).toFixed(0), icon: '🏢' }
          ].map((tier, idx) => (
            <div key={idx} style={{ flex: 1, backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>{tier.icon}</div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.TEXT }}>{tier.level}</div>
              <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '8px' }}>{tier.desc}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#00A8CB' }}>{tier.stock}</div>
              <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>units in stock</div>
              {idx < 2 && <div style={{ marginTop: '8px', fontSize: '16px', color: COLORS.BORDER }}>→</div>}
            </div>
          ))}
        </div>
        <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f0f9ff', border: '1px solid #00A8CB20', borderRadius: '4px', fontSize: '11px', color: COLORS.TEXT_DIM }}>
          <strong style={{ color: COLORS.TEXT }}>Cascade Behaviour:</strong> When local stock depleted, replenish from regional. When regional depleted, replenish from manufacturing. Lead times cascade through network based on logistics configuration.
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// GLOBAL ERROR BOUNDARY — catches unhandled React render errors
// ============================================================================
// ============================================================================
// SPARES OPTIMISATION WIZARD — 7-step guided workflow
// Equipment scope → Failure analysis → Demand forecasting → Criticality →
// Stocking policy → Safety stock calc → Report generation
// ============================================================================
function SparesOptimisationWizard({ projectData, setProjectData, simConfig, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardState, setWizardState] = useState({
    selectedEquipment: [],
    selectedSpares: [],
    demandMethod: 'zscore',
    apiConnected: false,
    analysisResults: null,
    reportGenerated: false
  });

  const spares = projectData.spares || [];
  const equipments = projectData.equipments || [];
  const failureModes = projectData.failuremodes || [];

  const getLinkedFailureModes = (spareId) => {
    return failureModes.filter(fm => (fm.spareRequirement || '') === spareId || (fm.linkedSpares || []).includes(spareId));
  };

  const steps = [
    {
      title: 'Equipment Scope',
      subtitle: 'Define the equipment boundary for spares analysis',
      icon: '🏭',
      guidance: 'Select the equipment items to include in this spares optimisation study. Equipment with linked spare parts and failure modes will provide the richest analysis.'
    },
    {
      title: 'Failure Analysis',
      subtitle: 'Review failure modes driving spare part demand',
      icon: '⚡',
      guidance: 'Review failure modes linked to your selected equipment. Failure rates and severity ratings drive demand forecasting and criticality classification.'
    },
    {
      title: 'Demand Forecasting',
      subtitle: 'Choose demand model and forecast spare consumption',
      icon: '📈',
      guidance: 'Select the demand forecasting method. Z-score ROP is recommended for most applications. Weibull-based forecasting requires equipment Weibull parameters.'
    },
    {
      title: 'Criticality Ranking',
      subtitle: 'Classify spares into 4-tier stocking policy tiers',
      icon: '🎯',
      guidance: 'Each spare is classified as Safety-Critical, Production-Critical, Standard, or Non-Critical based on linked failure mode severity and consequence.'
    },
    {
      title: 'Stocking Policy',
      subtitle: 'Apply tier-specific inventory rules and service levels',
      icon: '📋',
      guidance: 'Review and adjust the stocking policy for each tier. Service levels range from 99% (safety-critical) to 70% (non-critical) per ISO 55001 best practice.'
    },
    {
      title: 'Safety Stock Calculation',
      subtitle: 'Calculate reorder points and safety stock quantities',
      icon: '🧮',
      guidance: 'Z-score ROP formula: ROP = D̄×L̄ + Z×√(σ_d²×L̄ + σ_LT²×D̄²). Results show recommended stock levels, shortfalls, and annual holding costs.'
    },
    {
      title: 'Report & Sync',
      subtitle: 'Generate report and sync with OptimalSPARES',
      icon: '📊',
      guidance: 'Generate the Spares Optimisation Report with recommendations. Optionally sync results to spare.optimalworld.org for ongoing inventory management.'
    }
  ];

  // Run full analysis across all selected spares
  const runAnalysis = useCallback(() => {
    const scope = wizardState.selectedSpares.length > 0
      ? spares.filter(s => wizardState.selectedSpares.includes(s.id))
      : spares;

    const results = scope.map(spare => {
      const linkedFMs = getLinkedFailureModes(spare.id);
      const linkedEqs = equipments.filter(e => e.spareReq === spare.id || (e.spareReq && e.spareReq.includes(spare.id)));
      const missionTime = simConfig.missionTime || 8760;
      const demandRate = linkedEqs.reduce((sum, e) => sum + (parseFloat(e.failureRate) || 0.001), 0);
      const expectedDemand = demandRate * missionTime;

      const enrichedSpare = { ...spare, annualDemand: expectedDemand, expectedDemand };
      let weibullParams = null;
      const eqWithWeibull = linkedEqs.find(e => e.weibullBeta && e.weibullEta);
      if (eqWithWeibull) weibullParams = { beta: parseFloat(eqWithWeibull.weibullBeta), eta: parseFloat(eqWithWeibull.weibullEta) };

      const analysis = analyseSpare(enrichedSpare, linkedFMs, spare.demandHistory || [], weibullParams);
      const tier = classifyCriticality(spare, linkedFMs);
      const policy = DEFAULT_STOCKING_POLICY[tier];
      const currentStock = parseInt(spare.quantity) || parseInt(spare.stockLevel) || 0;
      const unitCost = parseFloat(spare.unitCost) || parseFloat(spare.cost) || 0;
      const rop = analysis.zscoreROP?.reorderPoint || 0;
      const shortfall = Math.max(0, rop - currentStock);

      return {
        id: spare.id, name: spare.name || spare.description || spare.id,
        tier, tierLabel: policy.label, tierColor: policy.color, tierBg: policy.bgColor,
        serviceLevel: policy.serviceLevelPct, currentStock, reorderPoint: rop,
        safetyStock: Math.ceil(analysis.zscoreROP?.safetyStock || 0),
        shortfall, unitCost, annualCost: unitCost * Math.max(rop, currentStock),
        linkedFMs: linkedFMs.length, linkedEqs: linkedEqs.length,
        analysis, weibullParams, expectedDemand
      };
    });

    setWizardState(prev => ({ ...prev, analysisResults: results }));
  }, [spares, equipments, failureModes, simConfig, wizardState.selectedSpares]);

  // Step readiness checks
  const stepReadiness = useMemo(() => {
    return [
      { ready: equipments.length > 0, issues: equipments.length === 0 ? ['No equipment defined in project'] : [] },
      { ready: failureModes.length > 0, issues: failureModes.length === 0 ? ['No failure modes defined'] : [] },
      { ready: spares.length > 0, issues: spares.length === 0 ? ['No spares defined'] : [] },
      { ready: spares.length > 0, issues: [] },
      { ready: spares.length > 0, issues: [] },
      { ready: true, issues: [] },
      { ready: !!wizardState.analysisResults, issues: !wizardState.analysisResults ? ['Run analysis first (Step 6)'] : [] }
    ];
  }, [equipments, failureModes, spares, wizardState.analysisResults]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Equipment Scope
        return (
          <div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>
              {equipments.length} equipment items available. Select items to include in this study, or leave all selected for a comprehensive analysis.
            </div>
            <div style={{ maxHeight: '400px', overflow: 'auto', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
              {equipments.map(eq => {
                const linkedSpares = spares.filter(s => eq.spareReq === s.id || (eq.spareReq && eq.spareReq.includes(s.id)));
                const isSelected = wizardState.selectedEquipment.length === 0 || wizardState.selectedEquipment.includes(eq.id);
                return (
                  <div key={eq.id} onClick={() => {
                    setWizardState(prev => {
                      const sel = prev.selectedEquipment;
                      if (sel.length === 0) return { ...prev, selectedEquipment: [eq.id] };
                      return { ...prev, selectedEquipment: sel.includes(eq.id) ? sel.filter(id => id !== eq.id) : [...sel, eq.id] };
                    });
                  }} style={{
                    padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, cursor: 'pointer',
                    backgroundColor: isSelected ? COLORS.BLUE + '08' : 'transparent', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '500', color: COLORS.TEXT }}>{eq.name || eq.description || eq.id}</div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM }}>{eq.category || 'Equipment'} • {linkedSpares.length} spare(s) linked {eq.weibullBeta ? `• Weibull β=${eq.weibullBeta}` : ''}</div>
                    </div>
                    <div style={{ width: '16px', height: '16px', borderRadius: '3px', border: `2px solid ${isSelected ? COLORS.BLUE : COLORS.BORDER}`, backgroundColor: isSelected ? COLORS.BLUE : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isSelected && <span style={{ color: '#fff', fontSize: '10px', fontWeight: '700' }}>✓</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 1: // Failure Analysis
        return (
          <div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>
              {failureModes.length} failure modes define the demand drivers for spare parts. Severity ratings determine criticality classification.
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.NAVY }}>
                  {['Failure Mode', 'Equipment', 'Severity', 'Failure Rate', 'Consequence', 'Spare Link'].map(h => (
                    <th key={h} style={{ padding: '8px', textAlign: 'left', color: '#fff', borderBottom: `2px solid ${COLORS.GOLD}`, fontSize: '10px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {failureModes.slice(0, 50).map((fm, idx) => (
                  <tr key={fm.id} style={{ backgroundColor: idx % 2 ? COLORS.PANEL_BG + '80' : 'transparent' }}>
                    <td style={{ padding: '8px', color: COLORS.TEXT }}>{fm.description || fm.id}</td>
                    <td style={{ padding: '8px', color: COLORS.TEXT_DIM }}>{fm.equipment || fm.equipmentId || '—'}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ padding: '2px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', backgroundColor: parseInt(fm.severity) >= 8 ? '#B91C1C15' : parseInt(fm.severity) >= 5 ? COLORS.GOLD + '20' : '#2A9D8F15', color: parseInt(fm.severity) >= 8 ? '#B91C1C' : parseInt(fm.severity) >= 5 ? COLORS.GOLD : '#2A9D8F' }}>
                        {fm.severity || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '8px', color: COLORS.TEXT }}>{fm.failureRate || '—'}</td>
                    <td style={{ padding: '8px', color: COLORS.TEXT_DIM }}>{fm.consequence || '—'}</td>
                    <td style={{ padding: '8px', color: fm.spareRequirement ? COLORS.BLUE : COLORS.TEXT_DIM }}>{fm.spareRequirement || 'None'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 2: // Demand Forecasting
        return (
          <div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Select the demand forecasting method for this study.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
              {[
                { key: 'zscore', title: 'Z-Score ROP', desc: 'Statistical reorder point using demand and lead time variability. Recommended for most applications.', formula: 'ROP = D̄×L̄ + Z×√(σ_d²×L̄ + σ_LT²×D̄²)', recommended: true },
                { key: 'poisson', title: 'Poisson Model', desc: 'Classic spare parts demand model for low-demand, discrete events. Best for slow-moving consumables.', formula: 'P(X=k) = (λ^k × e^-λ) / k!', recommended: false },
                { key: 'weibull', title: 'Weibull Forecast', desc: 'Uses equipment Weibull parameters to forecast failure-driven demand. Requires Weibull analysis data.', formula: 'h(t) = (β/η)(t/η)^(β-1)', recommended: false }
              ].map(m => (
                <div key={m.key} onClick={() => setWizardState(prev => ({ ...prev, demandMethod: m.key }))} style={{
                  padding: '16px', borderRadius: '6px', cursor: 'pointer',
                  border: `2px solid ${wizardState.demandMethod === m.key ? COLORS.NAVY : COLORS.BORDER}`,
                  backgroundColor: wizardState.demandMethod === m.key ? COLORS.NAVY + '08' : COLORS.PANEL_BG
                }}>
                  {m.recommended && <span style={{ fontSize: '9px', fontWeight: '700', color: '#2A9D8F', backgroundColor: '#2A9D8F15', padding: '2px 6px', borderRadius: '8px', marginBottom: '8px', display: 'inline-block' }}>RECOMMENDED</span>}
                  <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '6px' }}>{m.title}</div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '8px', lineHeight: '1.5' }}>{m.desc}</div>
                  <div style={{ fontSize: '10px', fontFamily: 'monospace', color: COLORS.NAVY, backgroundColor: COLORS.LIGHT_BG, padding: '6px 8px', borderRadius: '3px' }}>{m.formula}</div>
                </div>
              ))}
            </div>
          </div>
        );
      case 3: // Criticality Ranking
        return (
          <div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Spares classified by the 4-tier stocking policy matrix based on failure mode severity and consequence.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
              {Object.entries(DEFAULT_STOCKING_POLICY).map(([key, p]) => {
                const count = spares.filter(s => classifyCriticality(s, getLinkedFailureModes(s.id)) === key).length;
                return (
                  <div key={key} style={{ padding: '14px', backgroundColor: p.bgColor, border: `1px solid ${p.color}30`, borderRadius: '6px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: p.color }}>{count}</div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: p.color }}>{p.label}</div>
                    <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginTop: '4px' }}>SL: {p.serviceLevelPct}%</div>
                  </div>
                );
              })}
            </div>
            <div style={{ maxHeight: '300px', overflow: 'auto', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
              {spares.map((spare, idx) => {
                const tier = classifyCriticality(spare, getLinkedFailureModes(spare.id));
                const p = DEFAULT_STOCKING_POLICY[tier];
                return (
                  <div key={spare.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: idx % 2 ? COLORS.PANEL_BG + '60' : 'transparent' }}>
                    <span style={{ fontSize: '12px', color: COLORS.TEXT }}>{spare.name || spare.description || spare.id}</span>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: p.bgColor, color: p.color }}>{p.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 4: // Stocking Policy
        return (
          <div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '16px' }}>Review the stocking policy rules for each tier. These drive min/max stock levels, service targets, and replenishment actions.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              {Object.entries(DEFAULT_STOCKING_POLICY).map(([key, p]) => (
                <div key={key} style={{ padding: '16px', backgroundColor: p.bgColor, border: `1px solid ${p.color}30`, borderRadius: '6px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: p.color, marginBottom: '10px' }}>{p.label}</div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '10px' }}>{p.description}</div>
                  <div style={{ display: 'grid', gap: '6px', fontSize: '11px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Service Level</span><span style={{ fontWeight: '700', color: p.color }}>{p.serviceLevelPct}%</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Min Stock Rule</span><span style={{ fontWeight: '600', color: COLORS.TEXT }}>{p.minStockRule}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Max Stock Rule</span><span style={{ fontWeight: '600', color: COLORS.TEXT }}>{p.maxStockRule}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Lead Time Tolerance</span><span style={{ fontWeight: '600', color: COLORS.TEXT }}>{p.leadTimeTolDays} days</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Action</span><span style={{ fontWeight: '600', color: COLORS.TEXT }}>{p.action.replace(/_/g, ' ')}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 5: // Safety Stock Calculation
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM }}>Click "Run Analysis" to calculate Z-score ROP, safety stock, and shortfalls for all spares.</div>
              <button onClick={runAnalysis} style={{
                padding: '8px 20px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '4px', cursor: 'pointer',
                backgroundColor: COLORS.NAVY, color: '#fff'
              }}>Run Analysis</button>
            </div>
            {wizardState.analysisResults ? (
              <div>
                {/* Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  {[
                    { label: 'Total Spares Analysed', value: wizardState.analysisResults.length, color: COLORS.BLUE },
                    { label: 'Items Below ROP', value: wizardState.analysisResults.filter(r => r.shortfall > 0).length, color: '#B91C1C' },
                    { label: 'Total Shortfall Units', value: wizardState.analysisResults.reduce((s, r) => s + r.shortfall, 0), color: COLORS.GOLD },
                    { label: 'Annual Holding Cost', value: formatCurrency(wizardState.analysisResults.reduce((s, r) => s + r.annualCost, 0)), color: '#00A8CB' }
                  ].map((card, i) => (
                    <div key={i} style={{ backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '12px', borderLeft: `4px solid ${card.color}` }}>
                      <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase' }}>{card.label}</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.TEXT, marginTop: '4px' }}>{card.value}</div>
                    </div>
                  ))}
                </div>
                {/* Results table */}
                <div style={{ maxHeight: '350px', overflow: 'auto', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ backgroundColor: COLORS.NAVY, position: 'sticky', top: 0 }}>
                        {['Spare', 'Tier', 'SL%', 'Current', 'ROP', 'Safety Stock', 'Shortfall', 'Unit Cost', 'Action'].map(h => (
                          <th key={h} style={{ padding: '8px', textAlign: 'left', color: '#fff', borderBottom: `2px solid ${COLORS.GOLD}`, fontSize: '10px', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {wizardState.analysisResults.sort((a, b) => b.shortfall - a.shortfall).map((r, idx) => (
                        <tr key={r.id} style={{ backgroundColor: idx % 2 ? COLORS.PANEL_BG + '60' : 'transparent' }}>
                          <td style={{ padding: '8px', color: COLORS.TEXT, fontWeight: '500' }}>{r.name}</td>
                          <td style={{ padding: '8px' }}><span style={{ padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '700', backgroundColor: r.tierBg, color: r.tierColor }}>{r.tierLabel}</span></td>
                          <td style={{ padding: '8px', color: COLORS.TEXT, fontWeight: '600' }}>{r.serviceLevel}%</td>
                          <td style={{ padding: '8px', color: COLORS.TEXT, textAlign: 'center' }}>{r.currentStock}</td>
                          <td style={{ padding: '8px', color: COLORS.NAVY, fontWeight: '700', textAlign: 'center' }}>{r.reorderPoint}</td>
                          <td style={{ padding: '8px', color: '#00A8CB', fontWeight: '600', textAlign: 'center' }}>{r.safetyStock}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <span style={{ fontWeight: '700', color: r.shortfall > 0 ? '#B91C1C' : '#2A9D8F' }}>{r.shortfall > 0 ? `+${r.shortfall}` : '0'}</span>
                          </td>
                          <td style={{ padding: '8px', color: COLORS.TEXT }}>{formatCurrencyFull(r.unitCost)}</td>
                          <td style={{ padding: '8px', fontSize: '10px', color: r.shortfall > 0 ? '#B91C1C' : '#2A9D8F', fontWeight: '600' }}>
                            {r.shortfall > 0 ? 'Order' : 'OK'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ padding: '60px', textAlign: 'center', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
                <div style={{ fontSize: '32px', opacity: 0.3, marginBottom: '12px' }}>🧮</div>
                <div style={{ fontSize: '13px', color: COLORS.TEXT_DIM }}>Click "Run Analysis" to calculate safety stock and reorder points for all spares</div>
              </div>
            )}
          </div>
        );
      case 6: // Report & Sync
        return (
          <div>
            <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM, marginBottom: '20px' }}>
              Generate the Spares Optimisation Report and optionally sync results to OptimalSPARES at spare.optimalworld.org.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Report generation */}
              <div style={{ padding: '20px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '12px' }}>Spares Optimisation Report</div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '16px', lineHeight: '1.5' }}>
                  Comprehensive report including equipment scope, criticality ranking, stocking policy recommendations, Z-score ROP calculations, shortfall analysis, and cost summary.
                </div>
                {wizardState.analysisResults ? (
                  <div>
                    <div style={{ display: 'grid', gap: '6px', fontSize: '11px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Spares Analysed</span><span style={{ fontWeight: '600' }}>{wizardState.analysisResults.length}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Items Below ROP</span><span style={{ fontWeight: '600', color: '#B91C1C' }}>{wizardState.analysisResults.filter(r => r.shortfall > 0).length}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: COLORS.TEXT_DIM }}>Annual Cost</span><span style={{ fontWeight: '600' }}>{formatCurrency(wizardState.analysisResults.reduce((s, r) => s + r.annualCost, 0))}</span></div>
                    </div>
                    <button onClick={() => setWizardState(prev => ({ ...prev, reportGenerated: true }))} style={{
                      width: '100%', padding: '10px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '4px', cursor: 'pointer',
                      backgroundColor: wizardState.reportGenerated ? '#2A9D8F' : COLORS.NAVY, color: '#fff'
                    }}>{wizardState.reportGenerated ? 'Report Generated ✓' : 'Generate Report'}</button>
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, padding: '20px', textAlign: 'center' }}>Complete Step 6 (Safety Stock Calculation) first</div>
                )}
              </div>
              {/* API sync */}
              <div style={{ padding: '20px', backgroundColor: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, marginBottom: '12px' }}>Sync to OptimalSPARES</div>
                <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '16px', lineHeight: '1.5' }}>
                  Push stocking recommendations, ROP values, and criticality tiers to spare.optimalworld.org for ongoing inventory management and purchase order generation.
                </div>
                <div style={{ padding: '12px', backgroundColor: '#00A8CB10', border: '1px solid #00A8CB30', borderRadius: '4px', fontSize: '11px', color: '#00A8CB', marginBottom: '16px' }}>
                  API endpoint: spare.optimalworld.org/api/v1
                </div>
                <button disabled style={{
                  width: '100%', padding: '10px', fontSize: '12px', fontWeight: '600', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', cursor: 'not-allowed',
                  backgroundColor: COLORS.LIGHT_BG, color: COLORS.TEXT_DIM
                }}>Connect & Sync (requires API credentials)</button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Wizard header */}
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: '16px', color: COLORS.TEXT }}>Spares Optimisation Wizard</div>
            <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>7-step guided spare parts analysis and stocking policy workflow</div>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ padding: '4px 12px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', cursor: 'pointer', backgroundColor: COLORS.PANEL_BG, color: COLORS.TEXT_DIM }}>Exit Wizard</button>
          )}
        </div>
        {/* Step progress bar */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <div onClick={() => setCurrentStep(idx)} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '16px', cursor: 'pointer',
                backgroundColor: idx === currentStep ? COLORS.NAVY : idx < currentStep ? '#2A9D8F15' : COLORS.LIGHT_BG,
                border: `1px solid ${idx === currentStep ? COLORS.NAVY : idx < currentStep ? '#2A9D8F' : COLORS.BORDER}`
              }}>
                <span style={{ fontSize: '12px' }}>{step.icon}</span>
                <span style={{ fontSize: '10px', fontWeight: '600', color: idx === currentStep ? '#fff' : idx < currentStep ? '#2A9D8F' : COLORS.TEXT_DIM, whiteSpace: 'nowrap' }}>
                  {step.title}
                </span>
              </div>
              {idx < steps.length - 1 && <div style={{ width: '16px', height: '1px', backgroundColor: COLORS.BORDER }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {/* Step header with guidance */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '18px' }}>{steps[currentStep].icon}</span>
            <div style={{ fontSize: '15px', fontWeight: '600', color: COLORS.TEXT }}>Step {currentStep + 1}: {steps[currentStep].title}</div>
          </div>
          <div style={{ fontSize: '12px', color: COLORS.TEXT_DIM }}>{steps[currentStep].subtitle}</div>
          {/* Contextual guidance */}
          <div style={{ marginTop: '10px', padding: '10px 14px', backgroundColor: '#00A8CB08', border: '1px solid #00A8CB20', borderRadius: '4px', fontSize: '11px', color: '#00A8CB', lineHeight: '1.5' }}>
            {steps[currentStep].guidance}
          </div>
          {/* Readiness issues */}
          {stepReadiness[currentStep]?.issues?.length > 0 && (
            <div style={{ marginTop: '8px', padding: '8px 14px', backgroundColor: '#B91C1C08', border: '1px solid #B91C1C20', borderRadius: '4px', fontSize: '11px', color: '#B91C1C' }}>
              {stepReadiness[currentStep].issues.join(' • ')}
            </div>
          )}
        </div>

        {renderStepContent()}
      </div>

      {/* Navigation footer */}
      <div style={{ padding: '12px 24px', borderTop: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0} style={{
          padding: '8px 20px', fontSize: '12px', fontWeight: '600', border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
          backgroundColor: COLORS.PANEL_BG, color: currentStep === 0 ? COLORS.TEXT_DIM : COLORS.TEXT, opacity: currentStep === 0 ? 0.5 : 1
        }}>Previous</button>
        <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>Step {currentStep + 1} of {steps.length}</div>
        <button onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))} disabled={currentStep === steps.length - 1} style={{
          padding: '8px 20px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '4px', cursor: currentStep === steps.length - 1 ? 'not-allowed' : 'pointer',
          backgroundColor: currentStep === steps.length - 1 ? COLORS.TEXT_DIM : COLORS.NAVY, color: '#fff', opacity: currentStep === steps.length - 1 ? 0.5 : 1
        }}>Next</button>
      </div>
    </div>
  );
}

// ============================================================================
// GLOBAL ERROR BOUNDARY — catches unhandled React render errors
// ============================================================================
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: '600px', margin: '80px auto', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#002F65', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
            <span style={{ color: '#E8A838', fontSize: '28px', fontWeight: 'bold' }}>!</span>
          </div>
          <h2 style={{ color: '#002F65', margin: '0 0 12px 0' }}>Something went wrong</h2>
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            OptimalAvailability Studio encountered an unexpected error. Your project data should be safe.
          </p>
          <div style={{ backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '6px', padding: '12px', marginBottom: '20px', textAlign: 'left', fontSize: '12px', color: '#c0392b', fontFamily: 'monospace', maxHeight: '120px', overflow: 'auto' }}>
            {this.state.error && this.state.error.toString()}
          </div>
          <button
            onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); }}
            style={{ backgroundColor: '#002F65', color: '#fff', border: 'none', borderRadius: '6px', padding: '12px 32px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{ backgroundColor: '#E8A838', color: '#fff', border: 'none', borderRadius: '6px', padding: '12px 32px', fontSize: '14px', cursor: 'pointer', fontWeight: '600', marginLeft: '10px' }}
          >
            Reload Application
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// OPTIMALTREND INTEGRATION VIEW
// ============================================================================
function OptimalTRENDView({ projectData, setProjectData }) {
  // G05: Bridge function to import TREND health scores into equipment condition fields
  const syncTrendToEquipment = (dashboardData) => {
    if (!dashboardData?.assets) return;
    setProjectData(prev => {
      const updated = { ...prev };
      updated.equipments = (updated.equipments || []).map(eq => {
        const trendAsset = dashboardData.assets.find(a =>
          a.name === eq.description || a.name === eq.id || a.assetId === eq.id
        );
        if (trendAsset) {
          return {
            ...eq,
            trendHealthScore: trendAsset.healthScore,
            trendSeverity: trendAsset.severity,
            trendLastSync: new Date().toISOString(),
            conditionRating: trendAsset.healthScore >= 80 ? 'Good' : trendAsset.healthScore >= 50 ? 'Fair' : 'Poor'
          };
        }
        return eq;
      });
      return updated;
    });
  };

  const [trendToken, setTrendToken] = useState(null);
  const [treeNodes, setTreeNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeHealth, setNodeHealth] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [severityTrend, setSeverityTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState('dashboard');
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const API_BASE = window.location.origin;
  const authHeaders = useMemo(() => ({
    'Authorization': `Bearer ${sessionStorage.getItem('oav_token')}`,
    'Content-Type': 'application/json'
  }), []);

  // Fetch OptimalTREND data via proxy
  const trendFetch = useCallback(async (path) => {
    const resp = await fetch(`${API_BASE}/api/v1/trend/${path}`, { headers: authHeaders });
    if (!resp.ok) throw new Error(`TREND API error: ${resp.status}`);
    return resp.json();
  }, [API_BASE, authHeaders]);

  // Load tree + alerts + sensors on mount
  useEffect(() => {
    async function init() {
      setLoading(true);
      setError(null);
      try {
        const [treeData, alertData, sensorData] = await Promise.all([
          trendFetch('tree'),
          trendFetch('alerts?limit=50'),
          trendFetch('sensors'),
        ]);
        setTreeNodes(treeData.nodes || []);
        setAlerts(alertData.alerts || []);
        setSensors(sensorData || []);
        // Auto-select first root node
        const roots = (treeData.nodes || []).filter(n => !n.node_parent_id || n.node_parent_id === '0');
        if (roots.length > 0) {
          setSelectedNode(roots[0]);
          setExpandedNodes(new Set([roots[0].node_id]));
          // Fetch dashboard for root
          try {
            const dash = await trendFetch(`dashboard/${roots[0].node_id}`);
            setDashData(dash);
          } catch (e) { /* dashboard may fail if no data */ }
        }
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    init();
  }, [trendFetch]);

  // Load node health when selected
  useEffect(() => {
    if (!selectedNode) return;
    async function loadNodeData() {
      try {
        const [health, dash, trend] = await Promise.allSettled([
          trendFetch(`asset/${selectedNode.node_id}/health`),
          trendFetch(`dashboard/${selectedNode.node_id}`),
          trendFetch(`asset/${selectedNode.node_id}/severity-trend?days=90`),
        ]);
        if (health.status === 'fulfilled') setNodeHealth(health.value);
        if (dash.status === 'fulfilled') setDashData(dash.value);
        if (trend.status === 'fulfilled') setSeverityTrend(trend.value.trend || []);
      } catch (e) { /* some endpoints may not have data */ }
    }
    loadNodeData();
  }, [selectedNode, trendFetch]);

  // Build tree hierarchy
  const treeHierarchy = useMemo(() => {
    const byParent = {};
    treeNodes.forEach(n => {
      const pid = n.node_parent_id || '0';
      if (!byParent[pid]) byParent[pid] = [];
      byParent[pid].push(n);
    });
    return byParent;
  }, [treeNodes]);

  const filteredAlerts = useMemo(() => {
    if (!selectedNode) return alerts;
    return alerts.filter(a => a.event_node_id === selectedNode.node_id);
  }, [alerts, selectedNode]);

  // Node type labels
  const nodeTypeLabel = (type) => {
    const labels = { 1: 'Enterprise', 2: 'Site', 3: 'Area', 4: 'Unit', 10: 'Asset', 21: 'Z-Axis', 22: 'X-Axis', 23: 'Y-Axis' };
    return labels[type] || `Type ${type}`;
  };

  const nodeTypeIcon = (type) => {
    if (type === 1) return '\u{1F3E2}';
    if (type === 2) return '\u{1F3ED}';
    if (type === 3) return '\u{1F4CD}';
    if (type === 4) return '\u{2699}\u{FE0F}';
    if (type === 10) return '\u{1F527}';
    if (type >= 21 && type <= 23) return '\u{1F4CA}';
    return '\u{1F4C1}';
  };

  // Severity color
  const severityColor = (level) => {
    if (level >= 3) return '#EF4444';
    if (level >= 2) return '#F59E0B';
    return '#10B981';
  };

  // Render tree node
  const renderTreeNode = (node, depth = 0) => {
    const children = treeHierarchy[node.node_id] || [];
    const isExpanded = expandedNodes.has(node.node_id);
    const isSelected = selectedNode && selectedNode.node_id === node.node_id;
    const hasChildren = children.length > 0;
    const matchesSearch = !searchTerm || node.node_name.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch && !children.some(c => c.node_name.toLowerCase().includes(searchTerm.toLowerCase()))) {
      return null;
    }

    return (
      <div key={node.node_id}>
        <div
          onClick={() => {
            setSelectedNode(node);
            if (hasChildren) {
              setExpandedNodes(prev => {
                const next = new Set(prev);
                if (next.has(node.node_id)) next.delete(node.node_id);
                else next.add(node.node_id);
                return next;
              });
            }
          }}
          style={{
            padding: '6px 8px 6px ' + (12 + depth * 18) + 'px',
            cursor: 'pointer',
            backgroundColor: isSelected ? 'rgba(0,168,203,0.12)' : 'transparent',
            borderLeft: isSelected ? '3px solid #00A8CB' : '3px solid transparent',
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '12px', color: '#1F2937',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#E0F7FA'; }}
          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {hasChildren && <span style={{ fontSize: '8px', color: '#9CA3AF', width: '10px' }}>{isExpanded ? '\u25BC' : '\u25B6'}</span>}
          {!hasChildren && <span style={{ width: '10px' }}></span>}
          <span>{nodeTypeIcon(node.node_type)}</span>
          <span style={{ fontWeight: isSelected ? '600' : '400', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.node_name}</span>
          <span style={{ fontSize: '9px', color: '#9CA3AF', flexShrink: 0 }}>{nodeTypeLabel(node.node_type)}</span>
        </div>
        {isExpanded && children.map(child => renderTreeNode(child, depth + 1))}
      </div>
    );
  };

  // Sub-panels
  const panels = [
    { key: 'dashboard', label: 'Dashboard', icon: '\u{1F4CA}' },
    { key: 'health', label: 'Health', icon: '\u{1F49A}' },
    { key: 'alerts', label: 'Alerts', icon: '\u{1F514}' },
    { key: 'sensors', label: 'Sensors', icon: '\u{1F4E1}' },
    { key: 'trend', label: 'Severity Trend', icon: '\u{1F4C8}' },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: '12px' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #B2EBF2', borderTop: '4px solid #00A8CB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#6B7280', fontSize: '13px' }}>Connecting to <OptimalTrendBrand size={13} color="#6B7280" trendColor="#00A8CB" />...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>{'\u{26A0}\u{FE0F}'}</div>
        <h3 style={{ color: '#1F2937', marginBottom: '8px' }}><OptimalTrendBrand size={16} color="#1F2937" trendColor="#00A8CB" /> Connection</h3>
        <p style={{ color: '#6B7280', fontSize: '13px', marginBottom: '16px' }}>{error}</p>
        <button onClick={() => window.location.reload()} style={{ padding: '8px 20px', background: '#00A8CB', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
          Retry Connection
        </button>
      </div>
    );
  }

  const rootNodes = treeNodes.filter(n => !n.node_parent_id || n.node_parent_id === '0');

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: '"DM Sans", sans-serif' }}>
      {/* Left: Asset Tree */}
      <div style={{ width: '280px', borderRight: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', backgroundColor: '#FAFAFA' }}>
        <div style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', background: 'linear-gradient(135deg, #002F65 0%, #00A8CB 100%)', color: 'white' }}>
          <div><OptimalTrendBrand size={14} color="#FFFFFF" trendColor="#B2EBF2" /></div>
          <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}>Predictive Analytics Integration</div>
        </div>
        <div style={{ padding: '8px' }}>
          <input
            type="text" placeholder="Search assets..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '12px', outline: 'none' }}
          />
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {rootNodes.map(n => renderTreeNode(n))}
          {rootNodes.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>No assets found in Optimal<sup style={{fontSize:'9px',fontWeight:'800',color:'#00A8CB'}}>{'\u00ae'}TREND</sup></div>
          )}
        </div>
        <div style={{ padding: '8px 12px', borderTop: '1px solid #E5E7EB', fontSize: '10px', color: '#9CA3AF' }}>
          {treeNodes.length} nodes &bull; {alerts.length} alerts &bull; {sensors.length} sensors
        </div>
      </div>

      {/* Right: Content Panels */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Sub-tab bar */}
        <div style={{ display: 'flex', gap: '2px', padding: '6px 12px', borderBottom: '1px solid #E5E7EB', backgroundColor: '#F0FBFD' }}>
          {panels.map(p => (
            <button key={p.key} onClick={() => setActivePanel(p.key)} style={{
              padding: '6px 14px', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: activePanel === p.key ? '700' : '400',
              backgroundColor: activePanel === p.key ? '#00A8CB' : 'transparent',
              color: activePanel === p.key ? 'white' : '#6B7280',
              cursor: 'pointer', transition: 'all 0.15s',
            }}>
              {p.icon} {p.label}
            </button>
          ))}
        </div>

        {/* Panel content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {/* DASHBOARD PANEL */}
          {activePanel === 'dashboard' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', marginBottom: '16px' }}>
                {selectedNode ? selectedNode.node_name : <OptimalTrendBrand size={16} color="#1F2937" trendColor="#00A8CB" />} — Dashboard
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <div style={{ background: 'linear-gradient(135deg, #002F65, #00A8CB)', borderRadius: '10px', padding: '16px', color: 'white' }}>
                  <div style={{ fontSize: '10px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>Assets</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px' }}>{dashData?.asset_count || 0}</div>
                  <div style={{ fontSize: '11px', opacity: 0.7 }}>Monitored equipment</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #F44336, #E53935)', borderRadius: '10px', padding: '16px', color: 'white' }}>
                  <div style={{ fontSize: '10px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>Active Alarms</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px' }}>{dashData?.alarms?.active || 0}</div>
                  <div style={{ fontSize: '11px', opacity: 0.7 }}>Requiring attention</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #4CAF50, #66BB6A)', borderRadius: '10px', padding: '16px', color: 'white' }}>
                  <div style={{ fontSize: '10px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>Closed Alarms</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px' }}>{dashData?.alarms?.closed || 0}</div>
                  <div style={{ fontSize: '11px', opacity: 0.7 }}>Resolved</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #FF9800, #FFB74D)', borderRadius: '10px', padding: '16px', color: 'white' }}>
                  <div style={{ fontSize: '10px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>Sensors Online</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px' }}>{sensors.length || 0}</div>
                  <div style={{ fontSize: '11px', opacity: 0.7 }}>Data acquisition</div>
                </div>
              </div>

              {/* Health scores */}
              {dashData?.health_scores && dashData.health_scores.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>Health Scores</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    {dashData.health_scores.map((hs, i) => (
                      <div key={i} style={{ padding: '10px', border: '1px solid #E5E7EB', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '22px', fontWeight: '700', color: (hs.health_score || 0) > 70 ? '#10B981' : (hs.health_score || 0) > 40 ? '#F59E0B' : '#EF4444' }}>
                          {Math.round(hs.health_score || 0)}%
                        </div>
                        <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '4px' }}>{hs.node_id || 'Asset'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assets list */}
              {dashData?.assets && dashData.assets.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>Monitored Assets ({dashData.assets.length})</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                    {dashData.assets.slice(0, 20).map(a => (
                      <div key={a.node_id}
                        onClick={() => {
                          const node = treeNodes.find(n => n.node_id === a.node_id);
                          if (node) setSelectedNode(node);
                        }}
                        style={{ padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: '#374151', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#00A8CB'; e.currentTarget.style.background = '#E0F7FA'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = 'white'; }}
                      >
                        {'\u{1F527}'} {a.node_name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* HEALTH PANEL */}
          {activePanel === 'health' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', marginBottom: '16px' }}>
                {selectedNode?.node_name || 'Select an asset'} — Health Data
              </h3>
              {nodeHealth ? (
                <div>
                  {/* Measurement Points */}
                  {nodeHealth.measurement_points && nodeHealth.measurement_points.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>Measurement Points</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                        {nodeHealth.measurement_points.map(mp => (
                          <div key={mp.node_id} style={{ padding: '12px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#FAFAFA' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{mp.node_name}</div>
                            <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '4px' }}>{nodeTypeLabel(mp.node_type)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vibration Data */}
                  {nodeHealth.vibration && nodeHealth.vibration.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>Latest Vibration</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#E0F7FA' }}>
                            <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #00A8CB' }}>Parameter</th>
                            <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #00A8CB' }}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(nodeHealth.vibration[0] || {}).filter(([k]) => k !== 'time').map(([k, v], i) => (
                            <tr key={k} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{k}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6', textAlign: 'right', fontWeight: '600' }}>{typeof v === 'number' ? v.toFixed(3) : String(v)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Temperature */}
                  {nodeHealth.temperature && nodeHealth.temperature.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>Temperature</h4>
                      <div style={{ padding: '16px', background: '#E0F7FA', borderRadius: '8px', fontSize: '24px', fontWeight: '700', color: '#002F65', textAlign: 'center' }}>
                        {nodeHealth.temperature[0]?.value || nodeHealth.temperature[0]?.last || '--'} &deg;C
                      </div>
                    </div>
                  )}

                  {/* Active Alarms */}
                  {nodeHealth.active_alarms && nodeHealth.active_alarms.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>Active Alarms ({nodeHealth.active_alarms.length})</h4>
                      {nodeHealth.active_alarms.map((a, i) => (
                        <div key={i} style={{ padding: '10px', border: '1px solid #FECACA', borderRadius: '6px', marginBottom: '6px', backgroundColor: '#FEF2F2', fontSize: '12px' }}>
                          <span style={{ fontWeight: '600', color: '#DC2626' }}>Level {a.event_level || '?'}</span>
                          <span style={{ color: '#6B7280', marginLeft: '8px' }}>{a.event_begin_time || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No data message */}
                  {(!nodeHealth.measurement_points || nodeHealth.measurement_points.length === 0) &&
                   (!nodeHealth.vibration || nodeHealth.vibration.length === 0) &&
                   (!nodeHealth.active_alarms || nodeHealth.active_alarms.length === 0) && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                      No health data available for this node. Select an asset-level node to view monitoring data.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                  Select an asset from the tree to view health data.
                </div>
              )}
            </div>
          )}

          {/* ALERTS PANEL */}
          {activePanel === 'alerts' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', marginBottom: '16px' }}>
                Alerts {selectedNode ? `— ${selectedNode.node_name}` : '— All'}
                <span style={{ fontSize: '12px', fontWeight: '400', color: '#6B7280', marginLeft: '8px' }}>({filteredAlerts.length})</span>
              </h3>
              {filteredAlerts.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#E0F7FA' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #00A8CB' }}>Status</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #00A8CB' }}>Level</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #00A8CB' }}>Node</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #00A8CB' }}>Begin Time</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #00A8CB' }}>End Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAlerts.map((a, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                            backgroundColor: a.event_status === 0 ? '#FEE2E2' : '#D1FAE5',
                            color: a.event_status === 0 ? '#DC2626' : '#059669'
                          }}>{a.event_status === 0 ? 'ACTIVE' : 'CLOSED'}</span>
                        </td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>
                          <span style={{ color: severityColor(a.event_level), fontWeight: '700' }}>{a.event_level || '-'}</span>
                        </td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6', color: '#374151' }}>{a.event_node_id || '-'}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6', color: '#6B7280' }}>{a.event_begin_time || '-'}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6', color: '#6B7280' }}>{a.event_end_time || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>No alerts found.</div>
              )}
            </div>
          )}

          {/* SENSORS PANEL */}
          {activePanel === 'sensors' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', marginBottom: '16px' }}>
                Sensor Network <span style={{ fontSize: '12px', fontWeight: '400', color: '#6B7280' }}>({sensors.length} devices)</span>
              </h3>
              {sensors.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                  {sensors.map((s, i) => (
                    <div key={i} style={{ padding: '14px', border: '1px solid #E5E7EB', borderRadius: '10px', backgroundColor: 'white' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1F2937' }}>{s.ds_name || s.ds_sn || `Sensor ${i+1}`}</div>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10B981' }}></div>
                      </div>
                      <div style={{ fontSize: '11px', color: '#6B7280', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                        <span>S/N: {s.ds_sn || '-'}</span>
                        <span>DAS: {s.ds_das_code || '-'}</span>
                        {s.signal_strength != null && <span>Signal: {s.signal_strength}</span>}
                        {s.battery_level != null && <span>Battery: {s.battery_level}%</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>No sensors configured.</div>
              )}
            </div>
          )}

          {/* SEVERITY TREND PANEL */}
          {activePanel === 'trend' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', marginBottom: '16px' }}>
                Vibration Severity Trend — {selectedNode?.node_name || 'Select an asset'}
              </h3>
              {severityTrend.length > 0 ? (
                <div style={{ height: '300px', position: 'relative', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', backgroundColor: 'white' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={severityTrend.map(d => ({
                      time: d.time ? new Date(d.time).toLocaleDateString() : '',
                      value: d.value || d.last || d.velocity_rms || 0,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#00A8CB" strokeWidth={2} dot={{ r: 2 }} name="Severity" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                  No severity trend data. Select an asset-level node with vibration monitoring.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// AI CHAT PANEL (Claude-powered assistant)
// ============================================================================
function AIChatPanel({ currentTab, currentModule }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I\'m your Optimal® AI reliability assistant. Ask me about failure analysis, RCM methodology, Weibull parameters, maintenance strategies, or anything related to your reliability engineering work.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const token = sessionStorage.getItem('oav_token');
      const context = `User is currently viewing the "${currentTab}" tab in the "${currentModule}" module.`;
      const resp = await fetch('/api/v1/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: userMsg, context })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${resp.status}`);
      }
      const data = await resp.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I encountered an error: ${e.message}. Please try again.` }]);
    }
    setLoading(false);
  }, [input, loading, currentTab, currentModule]);

  const LB = '#00A8CB';
  const DB = '#002F65';

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} title="Optimal® AI Assistant" style={{
        position: 'fixed', bottom: '44px', right: '64px', width: '40px', height: '40px',
        borderRadius: '50%', backgroundColor: DB, border: `2px solid ${LB}`,
        color: '#FFFFFF', fontSize: '20px', cursor: 'pointer', zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Zap size={22} color={LB} />
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: '44px', right: '64px', width: '400px', height: '500px',
      backgroundColor: '#FFFFFF', borderRadius: '12px', border: `1px solid ${COLORS.BORDER}`,
      boxShadow: '0 8px 30px rgba(0,0,0,0.2)', zIndex: 1000,
      display: 'flex', flexDirection: 'column', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ padding: '10px 16px', backgroundColor: DB, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Zap size={16} color={LB} />
        <span style={{ color: '#FFFFFF', fontSize: '13px', fontWeight: '700', flex: 1 }}>Optimal® AI Assistant</span>
        <span style={{ fontSize: '8px', color: LB, fontWeight: '600', padding: '1px 6px', border: `1px solid ${LB}40`, borderRadius: '3px' }}>ARaaS®</span>
        <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}>✕</button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '85%', padding: '8px 12px', borderRadius: '10px',
            backgroundColor: msg.role === 'user' ? LB : COLORS.LIGHT_BG,
            color: msg.role === 'user' ? '#FFFFFF' : COLORS.TEXT,
            fontSize: '12px', lineHeight: '1.5', whiteSpace: 'pre-wrap'
          }}>
            {msg.content}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: 'flex-start', padding: '8px 12px', borderRadius: '10px', backgroundColor: COLORS.LIGHT_BG, color: COLORS.TEXT_DIM, fontSize: '12px' }}>
            Thinking...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '8px 12px', borderTop: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: '8px' }}>
        <input
          type="text" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
          placeholder="Ask about reliability, RCM, Weibull..."
          style={{ flex: 1, padding: '8px 12px', fontSize: '12px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', outline: 'none' }}
        />
        <button onClick={handleSend} disabled={loading || !input.trim()} style={{
          padding: '8px 16px', fontSize: '12px', fontWeight: '600',
          backgroundColor: DB, color: '#FFFFFF', border: 'none', borderRadius: '6px',
          cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1
        }}>Send</button>
      </div>
    </div>
  );
}

// ============================================================================
// LOGIN PAGE COMPONENT
// ============================================================================
function LoginPage({ onLogin, error }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Desktop mode: authenticate locally without backend API
    if (username.trim()) {
      const localToken = 'oav_desktop_' + Date.now();
      sessionStorage.setItem('oav_token', localToken);
      sessionStorage.setItem('oav_user', username.trim());
      onLogin(localToken, {
        username: username.trim(),
        role: 'admin',
        fullName: username.trim().split(/[._@]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        userId: 'desktop-user'
      });
    } else {
      onLogin(null, null, 'Please enter a username.');
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'linear-gradient(160deg, #001A3A 0%, #002F65 40%, #003D7A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000
    }}>
      <style>{`
        .login-input:focus { border-color: rgba(0,168,203,0.6) !important; background-color: rgba(255,255,255,0.12) !important; }
        .login-btn:hover:not(:disabled) { background-color: #0093B0 !important; }
      `}</style>
      <div style={{ textAlign: 'center', width: '380px' }}>
        <div style={{ marginBottom: '48px' }}>
          <img src="/logo-white.png" alt="Optimal®" style={{ height: '48px', width: 'auto', marginBottom: '16px' }} />
          <div style={{ fontSize: '11px', color: '#00A8CB', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase' }}>Availability Studio</div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', marginTop: '4px', letterSpacing: '0.3px' }}>Asset Reliability as a Service (ARaaS®)</div>
        </div>

        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          {error && (
            <div style={{ backgroundColor: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: '3px', padding: '10px 14px', marginBottom: '20px', color: '#fca5a5', fontSize: '12px', lineHeight: '1.5' }}>
              {error}
            </div>
          )}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600' }}>Username</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} required autoFocus className="login-input"
              style={{ width: '100%', padding: '11px 14px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: '#FFFFFF', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s ease' }}
            />
          </div>
          <div style={{ marginBottom: '28px' }}>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600' }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="login-input"
              style={{ width: '100%', padding: '11px 14px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: '#FFFFFF', fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s ease' }}
            />
          </div>
          <button type="submit" disabled={loading} className="login-btn" style={{
            width: '100%', padding: '12px', backgroundColor: '#00A8CB', color: '#FFFFFF', border: 'none', borderRadius: '3px',
            fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, transition: 'all 0.2s ease'
          }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ marginTop: '56px', color: 'rgba(255,255,255,0.18)', fontSize: '10px', letterSpacing: '0.5px' }}>
          © {new Date().getFullYear()} Optimal® — www.optimal.world
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// WELCOME WIZARD COMPONENT
// ============================================================================
function WelcomeWizard({ onStartBlank, onLoadDemo, onImportFile, onImportHub }) {
  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) onImportFile(file);
  };

  const WizardIcon = ({ type }) => {
    const iconColor = '#00A8CB';
    if (type === 'new') return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
      </svg>
    );
    if (type === 'demo') return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><polyline points="17 2 12 7 7 2"/>
      </svg>
    );
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'linear-gradient(160deg, #001A3A 0%, #002F65 40%, #003D7A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999
    }}>
      <style>{`
        .wiz-card { transition: all 0.25s ease; }
        .wiz-card:hover { background-color: rgba(0,168,203,0.12) !important; border-color: rgba(0,168,203,0.5) !important; transform: translateY(-2px); }
      `}</style>
      <div style={{ textAlign: 'center', maxWidth: '720px' }}>
        {/* Logo */}
        <div style={{ marginBottom: '40px' }}>
          <img src="/logo-white.png" alt="Optimal®" style={{ height: '56px', width: 'auto', marginBottom: '16px' }} />
          <div style={{ fontSize: '12px', color: '#00A8CB', fontWeight: '600', letterSpacing: '3px', textTransform: 'uppercase' }}>Availability Studio</div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginTop: '6px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>ARaaS® — Asset Reliability as a Service</div>
        </div>

        <div style={{ marginBottom: '40px' }}>
          <div style={{ fontFamily: "'Georgia', serif", color: '#FFFFFF', fontSize: '22px', fontWeight: '400', letterSpacing: '-0.01em', marginBottom: '8px' }}>How would you like to begin?</div>
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', letterSpacing: '0.02em' }}>Enterprise reliability, availability and maintainability engineering</div>
        </div>

        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          {[
            { key: 'new', action: onStartBlank, iconType: 'new', title: 'New Project', desc: 'Start with a blank model and build your asset hierarchy from scratch' },
            { key: 'demo', action: onLoadDemo, iconType: 'demo', title: 'Demo Project', desc: 'Explore a pre-built plant model with equipment, failure modes and tasks' },
            { key: 'import', action: () => fileInputRef.current?.click(), iconType: 'import', title: 'Import File', desc: 'Quick import from JSON or OAWX project files' },
            { key: 'importhub', action: onImportHub, iconType: 'import', title: 'Import Data', desc: 'Upload hierarchy, maintenance history and strategy from Excel or CSV with column mapping' }
          ].map(card => (
            <div key={card.key} onClick={card.action} className="wiz-card" style={{
              width: '200px', padding: '32px 24px', backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', cursor: 'pointer',
              textAlign: 'left', position: 'relative'
            }}>
              <div style={{ marginBottom: '16px', opacity: 0.9 }}><WizardIcon type={card.iconType} /></div>
              <div style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.01em' }}>{card.title}</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', lineHeight: '1.55' }}>{card.desc}</div>
              <div style={{ position: 'absolute', right: '16px', top: '16px', color: 'rgba(0,168,203,0.4)', fontSize: '14px' }}>&#8594;</div>
            </div>
          ))}
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.xml,.json,.oawx" style={{ display: 'none' }} onChange={handleFileSelect} />
        </div>

        <div style={{ marginTop: '56px', color: 'rgba(255,255,255,0.18)', fontSize: '10px', letterSpacing: '0.5px' }}>
          © {new Date().getFullYear()} Optimal® — www.optimal.world — v2.0
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LIBRARY TEMPLATE AUTO-SUGGEST — searches 769 equipment types, 22,407 failure modes
// Triggered when equipment type is set/changed or manually via "Apply Library Template"
// ============================================================================
function LibraryTemplateSuggest({ equipment, projectData, setProjectData, onClose }) {
  const [searchQuery, setSearchQuery] = useState(equipment?.type || equipment?.description || '');
  const [results, setResults] = useState([]);
  const [sfg20Results, setSfg20Results] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [applying, setApplying] = useState(false);
  const [options, setOptions] = useState({ includeFailureModes: true, includeTasks: true, includeStrategies: true });
  const [applied, setApplied] = useState(false);
  const [libraryStats, setLibraryStats] = useState(null);
  const [activeSource, setActiveSource] = useState('library'); // 'library' | 'sfg20' | 'all'
  const [sfg20Data, setSfg20Data] = useState(null);

  // Load library stats and SFG20 index on mount
  useEffect(() => {
    getLibraryStats().then(setLibraryStats);
    fetch('./sfg20/schedules.json').then(r => r.json()).then(setSfg20Data).catch(() => setSfg20Data([]));
  }, []);

  // Search as user types — both libraries
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setResults([]); setSfg20Results([]); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      // Search failure library
      const libResults = await searchLibrary(searchQuery, 12);
      setResults(libResults);

      // Search SFG20 schedules
      if (sfg20Data) {
        const q = searchQuery.toLowerCase();
        const sfgMatches = sfg20Data.filter(s =>
          (s.title || '').toLowerCase().includes(q) ||
          (s.ref || '').toLowerCase().includes(q) ||
          (s.category || '').toLowerCase().includes(q) ||
          (s.tasks || []).some(t => (t.item || '').toLowerCase().includes(q))
        ).slice(0, 12).map(s => ({
          ...s,
          isSFG20: true,
          taskCount: s.tasks?.length || 0,
          score: (s.title || '').toLowerCase() === q ? 100 : (s.title || '').toLowerCase().includes(q) ? 60 : 30
        }));
        setSfg20Results(sfgMatches);
      }
      setLoading(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, sfg20Data]);

  // Auto-search on mount if equipment has a type
  useEffect(() => {
    if (equipment?.type) {
      searchLibrary(equipment.type, 12).then(setResults);
    }
  }, [equipment?.type]);

  // Load full template when a result is selected
  const handleSelectResult = async (result) => {
    setSelectedTemplate(result);
    setTemplateData(null);
    if (result.isSFG20) {
      // SFG20 schedule — template data is already inline
      setTemplateData({ isSFG20: true, schedule: result });
    } else {
      const data = await loadEquipmentTemplate(result.file);
      setTemplateData(data);
    }
  };

  // Apply template to project
  const handleApply = () => {
    if (!templateData || !equipment) return;
    setApplying(true);

    if (templateData.isSFG20) {
      // Apply SFG20 schedule using engine
      const generated = applySFG20Template(templateData.schedule, equipment.id, equipment.description || equipment.name);
      setProjectData(prev => ({
        ...prev,
        scheduledtasks: [...(prev.scheduledtasks || []), ...generated.scheduledtasks]
      }));
    } else {
      const generated = applyTemplateToProject(templateData, equipment.id, equipment.description || equipment.name, options);
      setProjectData(prev => ({
        ...prev,
        failuremodes: [...(prev.failuremodes || []), ...generated.failuremodes],
        scheduledtasks: [...(prev.scheduledtasks || []), ...generated.scheduledtasks],
        correctivetasks: [...(prev.correctivetasks || []), ...generated.correctivetasks]
      }));
    }

    setApplying(false);
    setApplied(true);
  };

  // Combined results
  const displayResults = activeSource === 'sfg20' ? sfg20Results : activeSource === 'library' ? results : [...results, ...sfg20Results].sort((a, b) => (b.score || 0) - (a.score || 0));

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      backgroundColor: 'rgba(0,47,101,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center'
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '780px', maxHeight: '85vh', backgroundColor: COLORS.PANEL_BG,
        borderRadius: '8px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.NAVY }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: "'Georgia', serif", fontSize: '15px', color: '#fff' }}>Apply Library Template</div>
              <div style={{ fontSize: '10px', color: '#ffffff80', marginTop: '2px' }}>
                {libraryStats ? `${libraryStats.totalEquipmentTypes} equipment types • ${libraryStats.totalUniqueFailureModes} failure modes • ${libraryStats.totalRecords} records` : 'Loading library...'}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px' }}>×</button>
          </div>
          {/* Equipment context */}
          <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px', fontSize: '11px', color: '#ffffffcc' }}>
            Applying to: <strong style={{ color: '#fff' }}>{equipment?.description || equipment?.name || equipment?.id}</strong>
            {equipment?.type && <span> (Type: {equipment.type})</span>}
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left: Search and results */}
          <div style={{ width: '320px', borderRight: `1px solid ${COLORS.BORDER}`, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px' }}>
              <input
                type="text" value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search equipment type (e.g. pump, valve, compressor)..."
                autoFocus
                style={{
                  width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.BORDER}`,
                  borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box'
                }}
              />
              {/* Source toggle */}
              <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                {[
                  { id: 'all', label: 'All', count: results.length + sfg20Results.length },
                  { id: 'library', label: 'Failure Library', count: results.length },
                  { id: 'sfg20', label: 'SFG20', count: sfg20Results.length }
                ].map(src => (
                  <button key={src.id} onClick={() => setActiveSource(src.id)} style={{
                    flex: 1, padding: '4px 6px', fontSize: '9px', fontWeight: '600', border: 'none', borderRadius: '3px', cursor: 'pointer',
                    backgroundColor: activeSource === src.id ? COLORS.NAVY : '#F3F4F6',
                    color: activeSource === src.id ? '#fff' : COLORS.TEXT_DIM,
                    textTransform: 'uppercase', letterSpacing: '0.04em'
                  }}>
                    {src.label} ({src.count})
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {loading && <div style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>Searching...</div>}
              {displayResults.map((r, ri) => (
                <div key={r.file || r.ref || ri} onClick={() => handleSelectResult(r)} style={{
                  padding: '10px 12px', borderBottom: `1px solid ${COLORS.BORDER}`, cursor: 'pointer',
                  backgroundColor: (selectedTemplate?.file === r.file && r.file) || (selectedTemplate?.ref === r.ref && r.ref) ? 'rgba(0,168,203,0.10)' : 'transparent'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {r.isSFG20 && <span style={{ padding: '1px 5px', borderRadius: '6px', fontSize: '7px', fontWeight: '700', backgroundColor: '#F59E0B20', color: '#B45309', textTransform: 'uppercase' }}>SFG20</span>}
                    <div style={{ fontSize: '12px', fontWeight: '500', color: COLORS.TEXT }}>{r.isSFG20 ? `${r.ref} — ${r.title}` : r.equipmentType}</div>
                  </div>
                  <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>
                    {r.isSFG20 ? `${r.taskCount} maintenance tasks • ${r.category || 'BESA Schedule'}` : `${r.componentCount} components • ${r.failureModeCount} failure modes • ${r.taskCount} tasks`}
                  </div>
                  {!r.isSFG20 && r.strategies && (
                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                      {r.strategies.map(s => (
                        <span key={s} style={{ padding: '1px 5px', borderRadius: '6px', fontSize: '8px', fontWeight: '600', backgroundColor: s === 'Condition Based' ? '#2A9D8F15' : s === 'Fixed Time' ? '#00A8CB15' : '#B91C1C15', color: s === 'Condition Based' ? '#2A9D8F' : s === 'Fixed Time' ? '#00A8CB' : '#B91C1C' }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {!loading && displayResults.length === 0 && searchQuery.length >= 2 && (
                <div style={{ padding: '30px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>No matching templates found in any library</div>
              )}
            </div>
          </div>

          {/* Right: Template preview and apply */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedTemplate && templateData ? (
              <>
                <div style={{ padding: '16px', borderBottom: `1px solid ${COLORS.BORDER}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {selectedTemplate.isSFG20 && <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '9px', fontWeight: '700', backgroundColor: '#F59E0B20', color: '#B45309' }}>SFG20</span>}
                    <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.TEXT }}>{selectedTemplate.isSFG20 ? `${selectedTemplate.ref} — ${selectedTemplate.title}` : selectedTemplate.equipmentType}</div>
                  </div>
                  <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginTop: '2px' }}>
                    {selectedTemplate.isSFG20
                      ? `${selectedTemplate.taskCount} scheduled maintenance tasks — ${selectedTemplate.category || 'BESA Standard'}`
                      : `${Object.keys(templateData.components || {}).length} components • ${selectedTemplate.failureModeCount} failure modes • ${selectedTemplate.taskCount} tasks`}
                  </div>
                </div>
                {/* Component preview */}
                <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
                  {Object.entries(templateData.components || {}).map(([compName, compData]) => (
                    <div key={compName} style={{ marginBottom: '12px', backgroundColor: COLORS.LIGHT_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '10px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: COLORS.NAVY, marginBottom: '6px' }}>{compName}</div>
                      {compData.failureModes && compData.failureModes.length > 0 && (
                        <div style={{ marginBottom: '6px' }}>
                          <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', fontWeight: '600', marginBottom: '3px' }}>Failure Modes</div>
                          {compData.failureModes.map((fm, i) => (
                            <div key={i} style={{ fontSize: '10px', color: COLORS.TEXT, padding: '2px 0' }}>
                              • {fm.what}: {fm.mechanism} ({fm.cause})
                            </div>
                          ))}
                        </div>
                      )}
                      {compData.tasks && compData.tasks.length > 0 && (
                        <div>
                          <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, textTransform: 'uppercase', fontWeight: '600', marginBottom: '3px' }}>Tasks</div>
                          {compData.tasks.map((task, i) => (
                            <div key={i} style={{ fontSize: '10px', color: COLORS.TEXT, padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}>
                              <span>• {task.description}</span>
                              {task.interval && <span style={{ color: '#00A8CB', fontWeight: '600', fontSize: '9px', flexShrink: 0 }}>{task.interval}h</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {compData.strategies && compData.strategies.length > 0 && (
                        <div style={{ marginTop: '4px', display: 'flex', gap: '4px' }}>
                          {compData.strategies.map(s => (
                            <span key={s} style={{ padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '600', backgroundColor: '#2A9D8F15', color: '#2A9D8F' }}>{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Apply options and button */}
                <div style={{ padding: '12px 16px', borderTop: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.LIGHT_BG }}>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', fontSize: '11px' }}>
                    {[
                      { key: 'includeFailureModes', label: 'Failure Modes' },
                      { key: 'includeTasks', label: 'Tasks & Intervals' },
                      { key: 'includeStrategies', label: 'Strategies' }
                    ].map(opt => (
                      <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: COLORS.TEXT }}>
                        <input type="checkbox" checked={options[opt.key]} onChange={e => setOptions(prev => ({ ...prev, [opt.key]: e.target.checked }))} />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  <button onClick={handleApply} disabled={applying || applied} style={{
                    width: '100%', padding: '10px', fontSize: '12px', fontWeight: '600',
                    border: 'none', borderRadius: '4px', cursor: applied ? 'default' : 'pointer',
                    backgroundColor: applied ? '#2A9D8F' : COLORS.NAVY, color: '#fff'
                  }}>
                    {applied ? `Applied ${selectedTemplate.failureModeCount} failure modes and ${selectedTemplate.taskCount} tasks ✓` : applying ? 'Applying...' : `Apply Template (${selectedTemplate.failureModeCount} FM, ${selectedTemplate.taskCount} tasks)`}
                  </button>
                </div>
              </>
            ) : selectedTemplate ? (
              <div style={{ padding: '40px', textAlign: 'center', fontSize: '12px', color: COLORS.TEXT_DIM }}>Loading template data...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.TEXT_DIM }}>
                <div style={{ fontSize: '28px', opacity: 0.3, marginBottom: '12px' }}>📚</div>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: '14px', marginBottom: '4px' }}>Select an Equipment Type</div>
                <div style={{ fontSize: '11px', maxWidth: '240px', textAlign: 'center', lineHeight: '1.5' }}>
                  Search and select a matching equipment type from the library to preview failure modes, tasks, and maintenance strategies
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// INTEGRITY SYNC PANEL — Bidirectional bridge to integrity.optimalworld.org
// Push corrosion/degradation failure modes for RBI, pull inspection results
// ============================================================================
function IntegritySyncPanel({ projectData, setProjectData, scopedProjectData }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [apiConnected, setApiConnected] = useState(false);
  const [apiStatus, setApiStatus] = useState('disconnected');
  const [syncLog, setSyncLog] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [inspectionResults, setInspectionResults] = useState(null);
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  const integrityApi = useMemo(() => new OptimalIntegrityAPI(), []);

  // Classify all failure modes
  const allFMs = scopedProjectData?.failuremodels || projectData?.failuremodels || [];
  const classifiedFMs = useMemo(() => classifyIntegrityModes(allFMs), [allFMs]);
  const integrityFMs = classifiedFMs.filter(fm => fm.isIntegrityRelated);
  const nonIntegrityFMs = classifiedFMs.filter(fm => !fm.isIntegrityRelated);

  // Group by damage mechanism category
  const categoryGroups = useMemo(() => {
    const groups = {};
    integrityFMs.forEach(fm => {
      const cat = fm.integrityCategory || 'Other Integrity';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(fm);
    });
    return groups;
  }, [integrityFMs]);

  // Get equipment list for asset mapping
  const equipments = scopedProjectData?.equipments || projectData?.equipments || [];

  const handleConnect = async () => {
    setApiStatus('connecting');
    const ok = await integrityApi.authenticate(credentials.email, credentials.password);
    if (ok) {
      setApiConnected(true);
      setApiStatus('connected');
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: 'Connected to integrity.optimalworld.org', type: 'success' }]);
    } else {
      setApiStatus('error');
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: 'Authentication failed — check credentials', type: 'error' }]);
    }
  };

  const handlePushForRBI = async (equipmentId) => {
    if (!apiConnected) return;
    const eq = equipments.find(e => e.id === equipmentId);
    const fms = integrityFMs.filter(fm => fm.equipmentId === equipmentId);
    if (!eq || fms.length === 0) return;
    try {
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `Pushing ${fms.length} integrity FMs for ${eq.description || eq.id}...`, type: 'info' }]);
      const result = await integrityApi.pushForRBI(eq, fms);
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `RBI assessment submitted: ${result?.assessmentId || 'OK'}`, type: 'success' }]);
    } catch (err) {
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `Push failed: ${err.message}`, type: 'error' }]);
    }
  };

  const handlePullInspection = async (equipmentId) => {
    if (!apiConnected) return;
    try {
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `Pulling inspection data for ${equipmentId}...`, type: 'info' }]);
      const data = await integrityApi.pullInspectionResults(equipmentId);
      setInspectionResults(data);
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `Received ${data?.inspections?.length || 0} inspection records`, type: 'success' }]);
    } catch (err) {
      setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `Pull failed: ${err.message}`, type: 'error' }]);
    }
  };

  const handleEnrichAll = () => {
    const enriched = enrichWithIntegrityData(allFMs);
    setProjectData(prev => ({ ...prev, failuremodels: enriched }));
    setSyncLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: `Enriched ${integrityFMs.length} failure modes with API 581 damage mechanism data`, type: 'success' }]);
  };

  const TABS = [
    { id: 'overview', label: 'Integrity Overview', icon: Shield },
    { id: 'damagemech', label: 'Damage Mechanisms', icon: AlertTriangle },
    { id: 'sync', label: 'API Sync', icon: RefreshCw },
    { id: 'riskmatrix', label: 'Risk Matrix', icon: Grid3x3 }
  ];

  const RISK_COLORS = {
    'Very High': '#DC2626', 'High': '#EA580C', 'Medium-High': '#F59E0B',
    'Medium': '#EAB308', 'Medium-Low': '#84CC16', 'Low': '#22C55E'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', backgroundColor: COLORS.NAVY, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Shield size={18} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', fontFamily: "'Georgia', serif" }}>Integrity Management</div>
            <div style={{ fontSize: '10px', opacity: 0.7 }}>API RP 580/581 RBI Bridge — integrity.optimalworld.org</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%',
            backgroundColor: apiStatus === 'connected' ? '#22C55E' : apiStatus === 'connecting' ? '#F59E0B' : '#EF4444'
          }} />
          <span style={{ fontSize: '10px', opacity: 0.8 }}>{apiStatus === 'connected' ? 'Connected' : apiStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}</span>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.BORDER}`, backgroundColor: COLORS.PANEL_BG }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: '8px 16px', fontSize: '11px', border: 'none', cursor: 'pointer',
              backgroundColor: activeTab === tab.id ? '#EFF6FF' : 'transparent',
              color: activeTab === tab.id ? COLORS.NAVY : COLORS.TEXT,
              borderBottom: activeTab === tab.id ? `2px solid ${COLORS.NAVY}` : '2px solid transparent',
              fontWeight: activeTab === tab.id ? '600' : '400', whiteSpace: 'nowrap',
              fontFamily: "'Georgia', serif", letterSpacing: '0.02em', textTransform: 'uppercase',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <Icon size={12} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
              {[
                { label: 'Total Failure Modes', value: allFMs.length, color: COLORS.TEXT },
                { label: 'Integrity-Related', value: integrityFMs.length, color: '#DC2626' },
                { label: 'Damage Categories', value: Object.keys(categoryGroups).length, color: '#EA580C' },
                { label: 'Equipment Affected', value: [...new Set(integrityFMs.map(fm => fm.equipmentId))].length, color: COLORS.NAVY }
              ].map((card, i) => (
                <div key={i} style={{ backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', padding: '14px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: COLORS.TEXT_DIM, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{card.label}</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: card.color, fontFamily: "'Georgia', serif", marginTop: '4px' }}>{card.value}</div>
                </div>
              ))}
            </div>

            {/* Integrity classification breakdown */}
            <div style={{ backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', marginBottom: '16px' }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '12px', fontWeight: '700', color: COLORS.TEXT, fontFamily: "'Georgia', serif" }}>
                Integrity Failure Mode Classification
              </div>
              <div style={{ padding: '8px' }}>
                {integrityFMs.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: COLORS.TEXT_DIM, fontSize: '12px' }}>
                    No integrity-related failure modes detected. Add failure modes with corrosion, degradation, or other integrity mechanisms to see them classified here.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ backgroundColor: COLORS.LIGHT_BG }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: COLORS.TEXT_DIM, fontWeight: '600', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.05em' }}>Failure Mode</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: COLORS.TEXT_DIM, fontWeight: '600', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.05em' }}>Equipment</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: COLORS.TEXT_DIM, fontWeight: '600', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.05em' }}>Category</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: COLORS.TEXT_DIM, fontWeight: '600', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.05em' }}>API 581 DM</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: COLORS.TEXT_DIM, fontWeight: '600', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.05em' }}>Inspection Method</th>
                        <th style={{ padding: '6px 10px', textAlign: 'center', color: COLORS.TEXT_DIM, fontWeight: '600', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.05em' }}>Rate (mm/yr)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {integrityFMs.slice(0, 50).map((fm, idx) => {
                        const dm = DAMAGE_MECHANISM_MAP[fm.integrityCategory] || DAMAGE_MECHANISM_MAP['Other Integrity'];
                        return (
                          <tr key={fm.id || idx} style={{ borderBottom: `1px solid ${COLORS.BORDER}` }}>
                            <td style={{ padding: '6px 10px', color: COLORS.TEXT }}>{fm.description || fm.id}</td>
                            <td style={{ padding: '6px 10px', color: COLORS.TEXT_DIM }}>{fm.equipment || fm.equipmentId || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '600', backgroundColor: '#FEF2F2', color: '#DC2626' }}>
                                {fm.integrityCategory}
                              </span>
                            </td>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '10px', color: COLORS.TEXT_DIM }}>{dm.api581Id}</td>
                            <td style={{ padding: '6px 10px', fontSize: '10px', color: COLORS.TEXT_DIM }}>{dm.inspectionMethod}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'center', fontFamily: 'monospace', fontSize: '10px', color: dm.defaultRate ? '#DC2626' : COLORS.TEXT_DIM }}>{dm.defaultRate ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Enrich button */}
            {integrityFMs.length > 0 && (
              <button onClick={handleEnrichAll} style={{
                padding: '10px 20px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none',
                borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <ShieldCheck size={14} /> Enrich All Integrity Failure Modes with API 581 Data
              </button>
            )}
          </div>
        )}

        {/* DAMAGE MECHANISMS TAB */}
        {activeTab === 'damagemech' && (
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: "'Georgia', serif", marginBottom: '12px' }}>
              API RP 581 Damage Mechanism Screening
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {Object.entries(DAMAGE_MECHANISM_MAP).map(([cat, dm]) => {
                const count = categoryGroups[cat]?.length || 0;
                return (
                  <div key={cat} style={{
                    backgroundColor: count > 0 ? '#FEF2F2' : '#fff',
                    border: `1px solid ${count > 0 ? '#FECACA' : COLORS.BORDER}`,
                    borderRadius: '6px', padding: '12px',
                    opacity: count > 0 ? 1 : 0.5
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '10px', fontWeight: '700', color: COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cat}</span>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '700', backgroundColor: count > 0 ? '#DC2626' : '#E5E7EB', color: count > 0 ? '#fff' : COLORS.TEXT_DIM }}>{count}</span>
                    </div>
                    <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>{dm.api581Id}: {dm.name}</div>
                    <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>Inspection: {dm.inspectionMethod}</div>
                    {dm.defaultRate !== null && (
                      <div style={{ fontSize: '9px', color: '#DC2626', marginTop: '2px' }}>Default rate: {dm.defaultRate} mm/yr</div>
                    )}
                    {count > 0 && (
                      <div style={{ marginTop: '8px', borderTop: `1px solid ${COLORS.BORDER}`, paddingTop: '6px' }}>
                        {categoryGroups[cat].slice(0, 3).map(fm => (
                          <div key={fm.id} style={{ fontSize: '9px', color: COLORS.TEXT, padding: '2px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            — {fm.description || fm.id}
                          </div>
                        ))}
                        {categoryGroups[cat].length > 3 && (
                          <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, fontStyle: 'italic' }}>+{categoryGroups[cat].length - 3} more</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* API SYNC TAB */}
        {activeTab === 'sync' && (
          <div style={{ display: 'flex', gap: '16px', height: '100%' }}>
            {/* Left: Connection & Controls */}
            <div style={{ width: '320px', flexShrink: 0 }}>
              {/* Connection panel */}
              <div style={{ backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', marginBottom: '12px' }}>
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', fontWeight: '700', color: COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  API Connection
                </div>
                <div style={{ padding: '12px 14px' }}>
                  {!apiConnected ? (
                    <>
                      <input placeholder="Email" value={credentials.email} onChange={e => setCredentials(p => ({ ...p, email: e.target.value }))} style={{ width: '100%', padding: '8px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '8px', boxSizing: 'border-box' }} />
                      <input type="password" placeholder="Password" value={credentials.password} onChange={e => setCredentials(p => ({ ...p, password: e.target.value }))} style={{ width: '100%', padding: '8px', fontSize: '11px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', marginBottom: '10px', boxSizing: 'border-box' }} />
                      <button onClick={handleConnect} disabled={apiStatus === 'connecting'} style={{ width: '100%', padding: '8px', backgroundColor: COLORS.NAVY, color: '#fff', border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                        {apiStatus === 'connecting' ? 'Connecting...' : 'Connect to Integrity API'}
                      </button>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ShieldCheck size={16} color="#22C55E" />
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '600', color: '#15803D' }}>Connected</div>
                        <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>integrity.optimalworld.org</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Equipment with integrity FMs */}
              <div style={{ backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', fontWeight: '700', color: COLORS.TEXT, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Equipment with Integrity FMs
                </div>
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {[...new Set(integrityFMs.map(fm => fm.equipmentId))].filter(Boolean).map(eqId => {
                    const eq = equipments.find(e => e.id === eqId);
                    const fmCount = integrityFMs.filter(fm => fm.equipmentId === eqId).length;
                    return (
                      <div key={eqId} onClick={() => setSelectedAsset(eqId)} style={{
                        padding: '8px 14px', borderBottom: `1px solid ${COLORS.BORDER}`, cursor: 'pointer',
                        backgroundColor: selectedAsset === eqId ? '#EFF6FF' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                      }}>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '500', color: COLORS.TEXT }}>{eq?.description || eqId}</div>
                          <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM }}>{fmCount} integrity FM{fmCount !== 1 ? 's' : ''}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {apiConnected && (
                            <>
                              <button onClick={e => { e.stopPropagation(); handlePushForRBI(eqId); }} title="Push for RBI" style={{ padding: '3px 6px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', cursor: 'pointer', fontSize: '9px', color: '#DC2626' }}>
                                Push RBI
                              </button>
                              <button onClick={e => { e.stopPropagation(); handlePullInspection(eqId); }} title="Pull inspections" style={{ padding: '3px 6px', backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '3px', cursor: 'pointer', fontSize: '9px', color: '#15803D' }}>
                                Pull
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {integrityFMs.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', fontSize: '11px', color: COLORS.TEXT_DIM }}>No integrity failure modes to sync</div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Sync log & results */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Inspection results panel */}
              {inspectionResults && (
                <div style={{ backgroundColor: '#fff', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px' }}>
                  <div style={{ padding: '10px 14px', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '11px', fontWeight: '700', color: COLORS.TEXT }}>
                    Inspection Results
                  </div>
                  <div style={{ padding: '12px', maxHeight: '200px', overflow: 'auto' }}>
                    {inspectionResults.inspections?.map((insp, i) => (
                      <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${COLORS.BORDER}`, fontSize: '10px' }}>
                        <span style={{ fontWeight: '600', color: COLORS.TEXT }}>{insp.date}</span> — {insp.method}: {insp.finding || 'No significant findings'}
                        {insp.thickness && <span style={{ color: '#DC2626', marginLeft: '8px' }}>t={insp.thickness}mm</span>}
                      </div>
                    )) || <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM }}>No inspection data available</div>}
                  </div>
                </div>
              )}

              {/* Sync log */}
              <div style={{ flex: 1, backgroundColor: '#0F172A', borderRadius: '6px', padding: '12px', fontFamily: 'monospace', fontSize: '10px', color: '#94A3B8', overflow: 'auto' }}>
                <div style={{ marginBottom: '8px', fontSize: '9px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Sync Log</div>
                {syncLog.length === 0 && <div style={{ color: '#475569' }}>Waiting for API connection...</div>}
                {syncLog.map((entry, i) => (
                  <div key={i} style={{ padding: '2px 0', color: entry.type === 'error' ? '#EF4444' : entry.type === 'success' ? '#22C55E' : '#94A3B8' }}>
                    [{entry.time}] {entry.msg}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* RISK MATRIX TAB */}
        {activeTab === 'riskmatrix' && (
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.TEXT, fontFamily: "'Georgia', serif", marginBottom: '12px' }}>
              API RP 581 Risk Matrix — Integrity Failure Modes
            </div>
            {/* 5x5 Risk Matrix */}
            <div style={{ display: 'inline-grid', gridTemplateColumns: '100px repeat(5, 80px)', gap: '2px', marginBottom: '20px' }}>
              {/* Header row */}
              <div style={{ padding: '6px', fontSize: '9px', fontWeight: '700', color: COLORS.TEXT_DIM, textAlign: 'center' }}></div>
              {['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic'].map(c => (
                <div key={c} style={{ padding: '6px', fontSize: '8px', fontWeight: '700', color: COLORS.TEXT_DIM, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{c}</div>
              ))}
              {/* Matrix rows (PoF: 5=Almost Certain → 1=Rare) */}
              {[5, 4, 3, 2, 1].map(pof => {
                const pofLabels = { 5: 'Almost Certain', 4: 'Likely', 3: 'Possible', 2: 'Unlikely', 1: 'Rare' };
                return (
                  <React.Fragment key={pof}>
                    <div style={{ padding: '6px', fontSize: '8px', fontWeight: '700', color: COLORS.TEXT_DIM, display: 'flex', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{pofLabels[pof]}</div>
                    {[1, 2, 3, 4, 5].map(cof => {
                      const riskScore = pof * cof;
                      const bg = riskScore >= 20 ? '#DC2626' : riskScore >= 12 ? '#EA580C' : riskScore >= 8 ? '#F59E0B' : riskScore >= 4 ? '#84CC16' : '#22C55E';
                      const cellFMs = integrityFMs.filter(fm => {
                        const s = parseInt(fm.severity) || 3;
                        const cofBucket = Math.min(5, Math.max(1, Math.ceil(s / 2)));
                        const pofBucket = fm.integrityCategory === 'General Thinning' || fm.integrityCategory === 'CUI' ? 4 : fm.integrityCategory === 'SCC' || fm.integrityCategory === 'Hydrogen Damage' ? 3 : 2;
                        return pofBucket === pof && cofBucket === cof;
                      });
                      return (
                        <div key={cof} style={{
                          padding: '6px', backgroundColor: bg, borderRadius: '3px', minHeight: '50px',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          opacity: cellFMs.length > 0 ? 1 : 0.3, position: 'relative'
                        }}>
                          {cellFMs.length > 0 && (
                            <>
                              <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>{cellFMs.length}</div>
                              <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase' }}>FMs</div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '9px', color: COLORS.TEXT_DIM }}>
              {[
                { color: '#DC2626', label: 'Very High Risk (≥20)' },
                { color: '#EA580C', label: 'High Risk (12–19)' },
                { color: '#F59E0B', label: 'Medium Risk (8–11)' },
                { color: '#84CC16', label: 'Low-Medium (4–7)' },
                { color: '#22C55E', label: 'Low Risk (1–3)' }
              ].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: l.color }} />
                  {l.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// GROUPED TREE SECTION — MECE sidebar with collapsible domain groups
// Replaces flat TREE_SECTIONS list with Asset Hierarchy > Analysis > Strategy >
// Resources > Modelling > Commercial grouping
// ============================================================================
function GroupedTreeSection({ group, projectData, selectedItem, selectedSection, onSelectItem, onAddItem, onDeleteItem, onEditItem, itemCount }) {
  const [expanded, setExpanded] = useState(group.id === 'assetHierarchy'); // Asset hierarchy open by default
  const [expandedSections, setExpandedSections] = useState({});
  const [searchText, setSearchText] = useState('');

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // For asset hierarchy group, build nested Location > Equipment tree
  const isAssetGroup = group.id === 'assetHierarchy';

  // Build nested location > equipment hierarchy
  const assetTree = useMemo(() => {
    if (!isAssetGroup) return null;
    const locations = projectData.locations || [];
    const equipments = projectData.equipments || [];

    // Build location hierarchy with children
    const buildLocTree = () => {
      const byParent = {};
      locations.forEach(loc => {
        const pid = loc.parent || '__root__';
        if (!byParent[pid]) byParent[pid] = [];
        byParent[pid].push(loc);
      });

      const walk = (parentId, depth) => {
        const children = byParent[parentId] || [];
        children.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
        const result = [];
        children.forEach(loc => {
          const locEquipments = equipments.filter(eq => eq.location === loc.id || eq.parent === loc.id);
          result.push({ type: 'location', item: loc, depth, children: locEquipments });
          result.push(...walk(loc.id, depth + 1));
        });
        return result;
      };

      const tree = walk('__root__', 0);
      // Add orphan locations
      const inTree = new Set(tree.filter(n => n.type === 'location').map(n => n.item.id));
      locations.forEach(loc => {
        if (!inTree.has(loc.id)) {
          const locEquipments = equipments.filter(eq => eq.location === loc.id || eq.parent === loc.id);
          tree.push({ type: 'location', item: loc, depth: 0, children: locEquipments });
        }
      });

      // Add unassigned equipment
      const assignedEqIds = new Set(tree.flatMap(n => (n.children || []).map(e => e.id)));
      const unassigned = equipments.filter(eq => !assignedEqIds.has(eq.id));
      if (unassigned.length > 0) {
        tree.push({ type: 'unassigned', item: { id: '__unassigned__', description: 'Unassigned Equipment' }, depth: 0, children: unassigned });
      }
      return tree;
    };
    return buildLocTree();
  }, [isAssetGroup, projectData.locations, projectData.equipments]);

  // Filter items by search text
  const filterItems = (items) => {
    if (!searchText) return items;
    const term = searchText.toLowerCase();
    return items.filter(item =>
      (item.description || '').toLowerCase().includes(term) ||
      (item.name || '').toLowerCase().includes(term) ||
      item.id.toLowerCase().includes(term)
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Compact header — no collapse, always open since this is the only sidebar group */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 10px',
        borderBottom: `1px solid ${COLORS.BORDER}`, flexShrink: 0,
        backgroundColor: COLORS.PANEL_BG
      }}>
        <Home size={14} style={{ color: COLORS.TEAL }} />
        <span style={{ fontSize: '12px', fontWeight: '700', color: COLORS.NAVY, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Asset Hierarchy
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '10px', color: COLORS.TEXT_DIM, fontWeight: '500' }}>
          {itemCount}
        </span>
      </div>

      <div style={{ paddingLeft: '4px', paddingBottom: '0', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 4px', flexShrink: 0 }}>
          <input
            type="text" placeholder="Search assets..."
            value={searchText} onChange={e => setSearchText(e.target.value)}
            style={{
              flex: 1, backgroundColor: COLORS.LIGHT_BG,
              color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, padding: '6px 8px',
              borderRadius: '3px', fontSize: '11px', boxSizing: 'border-box'
            }}
          />
        </div>

          {/* Asset hierarchy — nested tree */}
          {isAssetGroup && assetTree && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {assetTree.map((node, idx) => {
                const isLocExpanded = expandedSections[node.item.id] !== false; // default open
                const isUnassigned = node.type === 'unassigned';
                const locMatches = !searchText || (node.item.description || '').toLowerCase().includes(searchText.toLowerCase());
                const filteredChildren = filterItems(node.children || []);
                if (!locMatches && filteredChildren.length === 0 && searchText) return null;

                return (
                  <div key={node.item.id + '-' + idx}>
                    {/* Location row */}
                    <div
                      onClick={() => {
                        if (!isUnassigned) {
                          onSelectItem(node.item, 'locations');
                          toggleSection(node.item.id);
                        } else {
                          toggleSection(node.item.id);
                        }
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '5px 6px', paddingLeft: `${8 + node.depth * 16}px`,
                        cursor: 'pointer', fontSize: '11px',
                        backgroundColor: selectedItem?.id === node.item.id && selectedSection === 'locations' ? 'rgba(0,168,203,0.12)' : 'transparent',
                        borderRadius: '2px'
                      }}
                    >
                      {(node.children || []).length > 0 ? (
                        isLocExpanded ? <ChevronDown size={11} style={{ color: COLORS.TEXT_DIM, flexShrink: 0 }} /> : <ChevronRight size={11} style={{ color: COLORS.TEXT_DIM, flexShrink: 0 }} />
                      ) : <span style={{ width: '11px', flexShrink: 0 }} />}
                      <Home size={12} style={{ color: isUnassigned ? COLORS.TEXT_DIM : '#00A8CB', flexShrink: 0 }} />
                      <span style={{ color: isUnassigned ? COLORS.TEXT_DIM : COLORS.TEXT, fontWeight: isUnassigned ? '400' : '500', fontStyle: isUnassigned ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {node.item.description || node.item.id}
                      </span>
                      {(node.children || []).length > 0 && (
                        <span style={{ marginLeft: 'auto', fontSize: '9px', color: COLORS.TEXT_DIM, flexShrink: 0 }}>{node.children.length}</span>
                      )}
                    </div>

                    {/* Equipment children under location */}
                    {isLocExpanded && filteredChildren.map(eq => (
                      <div
                        key={eq.id}
                        onClick={(e) => { e.stopPropagation(); onSelectItem(eq, 'equipments'); }}
                        onDoubleClick={() => onEditItem(eq, 'equipments')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: '4px 6px', paddingLeft: `${24 + node.depth * 16}px`,
                          cursor: 'pointer', fontSize: '11px',
                          backgroundColor: selectedItem?.id === eq.id ? 'rgba(0,168,203,0.15)' : 'transparent',
                          borderRadius: '2px'
                        }}
                      >
                        <Zap size={11} style={{ color: '#002F65', flexShrink: 0 }} />
                        <span style={{ color: COLORS.TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {eq.description || eq.name || eq.id}
                        </span>
                        {eq.type && <span style={{ marginLeft: 'auto', fontSize: '8px', color: COLORS.TEXT_DIM, padding: '1px 4px', backgroundColor: COLORS.LIGHT_BG, borderRadius: '6px', flexShrink: 0 }}>{eq.type}</span>}
                      </div>
                    ))}
                  </div>
                );
              })}

              {/* Add buttons */}
              <div style={{ display: 'flex', gap: '4px', padding: '6px 8px' }}>
                <button onClick={() => onAddItem('locations')} style={{ flex: 1, padding: '4px', fontSize: '10px', color: '#00A8CB', backgroundColor: 'transparent', border: `1px dashed #00A8CB50`, borderRadius: '3px', cursor: 'pointer' }}>+ Add Location</button>
                <button onClick={() => onAddItem('equipments')} style={{ flex: 1, padding: '4px', fontSize: '10px', color: '#002F65', backgroundColor: 'transparent', border: `1px dashed #002F6550`, borderRadius: '3px', cursor: 'pointer' }}>+ Add Equipment</button>
              </div>
            </div>
          )}

        </div>
    </div>
  );
}

// ============================================================================
// SMART SUGGESTION ENGINE
// Blended ranking: Library exact matches → ISO 14224 taxonomy → AI-style
// contextual recommendations. Works across all data grids.
// ============================================================================
const ISO14224_TAXONOMY = {
  'Centrifugal Pump': {
    functions: [
      { description: 'Transfer fluid from source to destination at required flow rate and pressure', type: 'Primary', performanceStandard: 'Minimum 500 L/min at 3 bar' },
      { description: 'Contain process fluid without leakage to atmosphere', type: 'Primary', performanceStandard: 'Zero visible leakage' },
      { description: 'Provide continuous flow during normal operating conditions', type: 'Primary', performanceStandard: 'Operating range 80-120% of rated flow' },
      { description: 'Operate within acceptable vibration limits', type: 'Secondary', performanceStandard: 'ISO 10816 Zone A/B, <4.5mm/s RMS' }
    ],
    failuremodes: [
      { description: 'Seal failure — external leakage', mechanism: 'Mechanical seal wear/degradation', cause: 'Dry running, misalignment, contamination', severity: 7, consequence: 'Environmental release, fire hazard if flammable' },
      { description: 'Bearing failure — excessive vibration', mechanism: 'Rolling element fatigue, lubrication breakdown', cause: 'Insufficient lubrication, misalignment, overload', severity: 8, consequence: 'Catastrophic pump failure, secondary damage' },
      { description: 'Impeller erosion — reduced flow/head', mechanism: 'Cavitation, abrasive wear', cause: 'Low NPSH, entrained solids, off-BEP operation', severity: 5, consequence: 'Reduced throughput, process upset' },
      { description: 'Coupling failure — loss of drive', mechanism: 'Fatigue fracture, element degradation', cause: 'Misalignment, overload, age', severity: 6, consequence: 'Total loss of pump function' },
      { description: 'Casing/volute crack — structural leak', mechanism: 'Fatigue, corrosion, water hammer', cause: 'Pressure cycling, corrosive fluid, rapid valve closure', severity: 9, consequence: 'Major leak, potential flooding' }
    ],
    scheduledtasks: [
      { description: 'Vibration monitoring — bearing condition', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Mechanical seal inspection and replacement', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Oil analysis — bearing lubrication', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Performance test — flow rate and head', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Alignment check — laser alignment', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of cooling water supply to heat exchangers', category: 'Operational', severity: 8, likelihood: 3 },
      { description: 'Environmental release of process fluid', category: 'Environmental', severity: 7, likelihood: 4 },
      { description: 'Fire/explosion risk from flammable fluid leak', category: 'Safety', severity: 9, likelihood: 2 }
    ]
  },
  'Electric Motor': {
    functions: [
      { description: 'Convert electrical energy to mechanical rotational energy', type: 'Primary', performanceStandard: 'Rated power output within ±5%' },
      { description: 'Drive connected equipment at required speed', type: 'Primary', performanceStandard: 'Rated RPM ±2%' },
      { description: 'Operate within thermal limits under all load conditions', type: 'Secondary', performanceStandard: 'Winding temperature <Class F (155°C)' }
    ],
    failuremodes: [
      { description: 'Winding insulation failure — ground fault', mechanism: 'Insulation degradation, thermal aging', cause: 'Overheating, moisture ingress, voltage spikes', severity: 8, consequence: 'Motor trip, potential fire' },
      { description: 'Bearing failure — noise/vibration', mechanism: 'Rolling element fatigue', cause: 'Misalignment, over-greasing, contamination', severity: 7, consequence: 'Motor seizure, driven equipment damage' },
      { description: 'Rotor bar cracking — reduced torque', mechanism: 'Thermal fatigue, centrifugal stress', cause: 'Frequent starts, overload, manufacturing defect', severity: 6, consequence: 'Reduced output, eventual motor failure' }
    ],
    scheduledtasks: [
      { description: 'Motor current signature analysis (MCSA)', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Insulation resistance test (megger)', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Thermographic survey — connection/winding temps', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Bearing re-greasing per OEM schedule', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ]
  },
  'Compressor': {
    functions: [
      { description: 'Compress gas from suction to discharge pressure', type: 'Primary', performanceStandard: 'Rated pressure ratio at rated flow' },
      { description: 'Maintain gas seal integrity', type: 'Primary', performanceStandard: 'Zero process gas leakage to atmosphere' },
      { description: 'Operate within surge/stonewall limits', type: 'Secondary', performanceStandard: 'Minimum 10% surge margin' }
    ],
    failuremodes: [
      { description: 'Surge — flow reversal and pulsation', mechanism: 'Aerodynamic instability', cause: 'Low flow, fouled internals, control failure', severity: 9, consequence: 'Mechanical damage, trip, process upset' },
      { description: 'Dry gas seal failure — gas leakage', mechanism: 'Seal face wear, contamination', cause: 'Dirty seal gas, coking, loss of buffer pressure', severity: 8, consequence: 'Gas release, fire/explosion risk' },
      { description: 'Bearing failure — high vibration', mechanism: 'Oil film breakdown, pad damage', cause: 'Oil contamination, misalignment, overload', severity: 8, consequence: 'Compressor trip, secondary damage' },
      { description: 'Fouling — reduced efficiency and capacity', mechanism: 'Deposit build-up on internals', cause: 'Polymerisation, corrosion products, liquid carry-over', severity: 5, consequence: 'Reduced throughput, increased power consumption' }
    ],
    scheduledtasks: [
      { description: 'Performance monitoring — polytropic efficiency', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Vibration monitoring — proximity probes', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Oil analysis — bearing and seal oil', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Anti-surge valve function test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ]
  },
  'Heat Exchanger': {
    functions: [
      { description: 'Transfer heat between process streams at required duty', type: 'Primary', performanceStandard: 'Rated heat duty within ±10%' },
      { description: 'Contain both process streams without cross-contamination', type: 'Primary', performanceStandard: 'Zero inter-stream leakage' },
      { description: 'Maintain pressure integrity at design conditions', type: 'Primary', performanceStandard: 'No leakage at MAWP' }
    ],
    failuremodes: [
      { description: 'Tube bundle fouling — reduced heat transfer', mechanism: 'Scale, biological growth, corrosion deposits', cause: 'Poor water treatment, high inlet temperature', severity: 5, consequence: 'Reduced cooling capacity, process constraint' },
      { description: 'Tube leak — cross-contamination', mechanism: 'Corrosion, erosion, vibration fatigue', cause: 'Corrosive fluid, high velocity, tube vibration', severity: 7, consequence: 'Product contamination, environmental release' },
      { description: 'Gasket/flange leak — external leakage', mechanism: 'Gasket degradation, bolt relaxation', cause: 'Thermal cycling, incorrect torque, age', severity: 6, consequence: 'Environmental release, fire risk' }
    ],
    scheduledtasks: [
      { description: 'Performance monitoring — U-value trending', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Tube bundle cleaning — chemical or mechanical', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Eddy current tube inspection', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ]
  },
  'Control Valve': {
    functions: [
      { description: 'Regulate process flow/pressure to setpoint', type: 'Primary', performanceStandard: 'Control within ±2% of setpoint' },
      { description: 'Fail to safe position on loss of signal/air', type: 'Safety', performanceStandard: 'Fail closed/open within 5 seconds' },
      { description: 'Contain process fluid without external leakage', type: 'Primary', performanceStandard: 'Zero fugitive emissions per EPA Method 21' }
    ],
    failuremodes: [
      { description: 'Valve sticking — erratic control', mechanism: 'Packing friction, corrosion, deposits', cause: 'Infrequent stroking, corrosive fluid, overtightened packing', severity: 6, consequence: 'Process instability, off-spec product' },
      { description: 'Seat leakage — valve pass-through', mechanism: 'Seat erosion, cavitation damage', cause: 'High dP, flashing, entrained solids', severity: 5, consequence: 'Loss of isolation, energy waste' },
      { description: 'Actuator failure — loss of control', mechanism: 'Diaphragm rupture, spring failure', cause: 'Age, corrosive atmosphere, fatigue', severity: 7, consequence: 'Loss of process control, trip' }
    ],
    scheduledtasks: [
      { description: 'Partial stroke test (PST) — SIF valves', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Valve signature test — friction/travel', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Positioner calibration check', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ]
  },
  'Programmable Logic Controller': {
    functions: [
      { description: 'Execute process control logic per programmed sequence', type: 'Primary', performanceStandard: 'Scan time <100ms, 99.99% uptime' },
      { description: 'Process field instrument signals and generate outputs', type: 'Primary', performanceStandard: 'All I/O channels functional' },
      { description: 'Provide operator interface data to SCADA/DCS', type: 'Secondary', performanceStandard: 'Communication latency <500ms' }
    ],
    failuremodes: [
      { description: 'CPU module failure — loss of control', mechanism: 'Component failure, firmware corruption', cause: 'Power surge, age, environmental stress', severity: 9, consequence: 'Total loss of automatic control' },
      { description: 'I/O card failure — loss of field signals', mechanism: 'Component failure, connector corrosion', cause: 'Voltage spike, moisture, vibration', severity: 7, consequence: 'Partial loss of monitoring/control' },
      { description: 'Communication failure — data loss', mechanism: 'Network interface failure, cable damage', cause: 'EMI, connector failure, switch fault', severity: 6, consequence: 'Loss of operator visibility' },
      { description: 'Power supply failure — system shutdown', mechanism: 'Capacitor degradation, fan failure', cause: 'Age, dust accumulation, input voltage variation', severity: 8, consequence: 'Complete system shutdown' }
    ],
    scheduledtasks: [
      { description: 'Battery backup test and replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'I/O loop testing — sample verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Firmware backup and version audit', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Environmental check — temperature, dust, humidity', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' }
    ]
  },
  'Filtration Unit': {
    functions: [
      { description: 'Remove particulate matter from process stream', type: 'Primary', performanceStandard: 'Outlet clarity per specification, dP < max allowable' },
      { description: 'Contain process fluid without leakage', type: 'Primary', performanceStandard: 'Zero external leakage' }
    ],
    failuremodes: [
      { description: 'Filter element blinding — high differential pressure', mechanism: 'Particle accumulation, media degradation', cause: 'High inlet loading, undersized filter, media age', severity: 5, consequence: 'Reduced throughput, bypass activation' },
      { description: 'Media breakthrough — contaminant pass-through', mechanism: 'Media rupture, seal bypass', cause: 'High dP, thermal shock, O-ring failure', severity: 7, consequence: 'Downstream equipment damage, off-spec product' },
      { description: 'Housing/seal leak — external leakage', mechanism: 'Gasket degradation, O-ring extrusion', cause: 'Pressure cycling, chemical attack, incorrect assembly', severity: 6, consequence: 'Environmental release, slip hazard' }
    ],
    scheduledtasks: [
      { description: 'Differential pressure monitoring', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Filter element replacement', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Housing inspection and seal replacement', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ]
  }
};

// Equipment type aliases — maps common descriptions to taxonomy keys
const EQUIPMENT_TYPE_ALIASES = {
  'pump': 'Centrifugal Pump', 'centrifugal pump': 'Centrifugal Pump', 'booster pump': 'Centrifugal Pump',
  'motor': 'Electric Motor', 'electric motor': 'Electric Motor', 'induction motor': 'Electric Motor',
  'compressor': 'Compressor', 'rotary compressor': 'Compressor', 'reciprocating compressor': 'Compressor', 'screw compressor': 'Compressor',
  'heat exchanger': 'Heat Exchanger', 'cooler': 'Heat Exchanger', 'condenser': 'Heat Exchanger',
  'control valve': 'Control Valve', 'valve': 'Control Valve', 'globe valve': 'Control Valve',
  'plc': 'Programmable Logic Controller', 'controller': 'Programmable Logic Controller', 'programmable logic controller': 'Programmable Logic Controller',
  'filter': 'Filtration Unit', 'filtration unit': 'Filtration Unit', 'strainer': 'Filtration Unit'
};

/**
 * getSuggestions — blended ranking of library + ISO 14224 + contextual suggestions
 * @param {string} equipmentType - e.g. 'Centrifugal Pump'
 * @param {string} sectionId - e.g. 'failuremodels', 'scheduledtasks', 'functions'
 * @param {string} equipmentId - the selected equipment's ID
 * @param {Array} existingData - current data in the section (to avoid duplicates)
 * @returns {Array} suggested rows ready to insert
 */
function getSuggestions(equipmentType, sectionId, equipmentId, existingData = []) {
  const type = (equipmentType || '').toLowerCase().trim();
  const existingDescs = new Set(existingData.map(d => (d.description || '').toLowerCase()));

  // Resolve equipment type via aliases
  const resolvedType = EQUIPMENT_TYPE_ALIASES[type] ||
    Object.entries(EQUIPMENT_TYPE_ALIASES).find(([k]) => type.includes(k))?.[1] ||
    Object.keys(ISO14224_TAXONOMY).find(k => type.includes(k.toLowerCase()) || k.toLowerCase().includes(type));

  const taxonomy = resolvedType ? ISO14224_TAXONOMY[resolvedType] : null;

  // Map section IDs to taxonomy data keys
  const sectionMap = {
    failuremodels: 'failuremodes',
    failuremodes: 'failuremodes',
    consequences: 'consequences',
    scheduledtasks: 'scheduledtasks',
    correctivetasks: 'scheduledtasks',
    rcmcauses: 'failuremodes',
    rcmFunctions: 'functions',
    functions: 'functions'
  };

  const dataKey = sectionMap[sectionId];
  if (!taxonomy || !dataKey || !taxonomy[dataKey]) return [];

  const timestamp = Date.now();
  const suggestions = taxonomy[dataKey]
    .filter(item => !existingDescs.has((item.description || '').toLowerCase()))
    .map((item, idx) => ({
      ...item,
      id: `SUG-${timestamp}-${String(idx + 1).padStart(3, '0')}`,
      equipmentId: equipmentId,
      equipment: equipmentType,
      source: `ISO 14224 / ${resolvedType}`,
      _isSuggestion: true,
      _suggestionRank: idx + 1
    }));

  return suggestions;
}

// ============================================================================
// MAIN APPLICATION COMPONENT
// ============================================================================
function OptimalAvailabilityStudio() {
  const [projectData, setProjectDataRaw, undo, redo, canUndo, canRedo] = useUndoRedo(createBlankProject());
  const [showWelcome, setShowWelcome] = useState(true);
  const [auditLog, setAuditLog] = useState([]);

  // Wrapped setProjectData that logs changes to audit trail
  const setProjectData = useCallback((newData) => {
    setProjectDataRaw(prev => {
      const updated = typeof newData === 'function' ? newData(prev) : newData;
      // Detect changes for audit trail
      const entities = Object.keys(updated);
      entities.forEach(entity => {
        if (!Array.isArray(updated[entity]) || !Array.isArray(prev[entity])) return;
        const prevIds = new Set(prev[entity].map(r => r.id));
        const newIds = new Set(updated[entity].map(r => r.id));
        updated[entity].forEach(r => {
          if (!prevIds.has(r.id)) {
            setAuditLog(log => [...log.slice(-499), { timestamp: Date.now(), action: 'add', entity, itemId: r.id, description: r.description || r.label || r.id, user: null }]);
          }
        });
        prev[entity].forEach(r => {
          if (!newIds.has(r.id)) {
            setAuditLog(log => [...log.slice(-499), { timestamp: Date.now(), action: 'delete', entity, itemId: r.id, description: r.description || r.label || r.id, user: null }]);
          }
        });
      });
      return updated;
    });
  }, [setProjectDataRaw]);

  // Authentication State
  const [authToken, setAuthToken] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState('');

  // UI State
  const [selectedSection, setSelectedSection] = useState('equipments');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentTab, setCurrentTab] = useState('grid');
  const [currentModule, setCurrentModule] = useState('hub');
  const [workflowStepIndex, setWorkflowStepIndex] = useState(0);  // current workflow step within module
  // Sync workflow step index with currentTab and module changes
  const prevModuleRef = useRef(currentModule);
  useEffect(() => {
    if (currentModule !== prevModuleRef.current) {
      setWorkflowStepIndex(0);
      prevModuleRef.current = currentModule;
    }
    // Keep workflowStepIndex in sync with currentTab
    const workflow = MODULE_WORKFLOWS[currentModule];
    if (workflow) {
      const idx = workflow.steps.findIndex(s => s.id === currentTab);
      if (idx >= 0) setWorkflowStepIndex(idx);
    }
  }, [currentModule, currentTab]);
  const [modellingSubTab, setModellingSubTab] = useState('rbd');
  const [connectorsSubTab, setConnectorsSubTab] = useState('sap');
  const [editingSubDiagram, setEditingSubDiagram] = useState(null);
  const [editingMultiState, setEditingMultiState] = useState(null);
  const [editDialogItem, setEditDialogItem] = useState(null);
  const [libraryTemplateEquipment, setLibraryTemplateEquipment] = useState(null); // equipment item to show library suggest for
  const [hierarchyStandard, setHierarchyStandard] = useState('iso14224');
  const [panelVisible, setPanelVisible] = useState({ left: true, right: true });
  const [leftPanelWidth, setLeftPanelWidth] = useState(320);
  const leftPanelDragging = useRef(false);
  const leftPanelStartX = useRef(0);
  const leftPanelStartW = useRef(260);

  // Left panel drag-to-resize handlers
  const handleLeftPanelMouseDown = useCallback((e) => {
    e.preventDefault();
    leftPanelDragging.current = true;
    leftPanelStartX.current = e.clientX;
    leftPanelStartW.current = leftPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (ev) => {
      if (!leftPanelDragging.current) return;
      const newW = Math.max(160, Math.min(600, leftPanelStartW.current + (ev.clientX - leftPanelStartX.current)));
      setLeftPanelWidth(newW);
    };
    const onMouseUp = () => {
      leftPanelDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [leftPanelWidth]);

  const [rightPanelWidth, setRightPanelWidth] = useState(320);
  const rightPanelDragging = useRef(false);
  const rightPanelStartX = useRef(0);
  const rightPanelStartW = useRef(320);

  const handleRightPanelMouseDown = useCallback((e) => {
    e.preventDefault();
    rightPanelDragging.current = true;
    rightPanelStartX.current = e.clientX;
    rightPanelStartW.current = rightPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (ev) => {
      if (!rightPanelDragging.current) return;
      const newW = Math.max(200, Math.min(700, rightPanelStartW.current - (ev.clientX - rightPanelStartX.current)));
      setRightPanelWidth(newW);
    };
    const onMouseUp = () => {
      rightPanelDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [rightPanelWidth]);

  // Login handlers
  const handleLogin = useCallback((token, user, error) => {
    if (token && user) {
      setAuthToken(token);
      setCurrentUser(user);
      setLoginError('');
    } else {
      setLoginError(error || 'Login failed');
    }
  }, []);

  const handleLogout = useCallback(() => {
    setAuthToken(null);
    setCurrentUser(null);
    sessionStorage.removeItem('oav_token');
    sessionStorage.removeItem('oav_user');
    sessionStorage.removeItem('oav_fullname');
    sessionStorage.removeItem('oav_role');
    if (window.oaLogout) window.oaLogout();
  }, []);

  // Auto-login from stored token (sessionStorage — clears on browser restart)
  useEffect(() => {
    const storedToken = sessionStorage.getItem('oav_token');
    if (storedToken) {
      setAuthToken(storedToken);
      const storedName = sessionStorage.getItem('oav_user') || sessionStorage.getItem('oav_fullname') || 'User';
      const storedRole = sessionStorage.getItem('oav_role') || 'admin';
      setCurrentUser({ username: storedName, role: storedRole, fullName: storedName });
    }
  }, []);

  // Auto-switch to first tab when module changes
  // Browser History Navigation — pushState/popstate so back button navigates within the app
  const historyNavigating = useRef(false);
  const pendingModuleTab = useRef(null);

  const navigateToModule = useCallback((module, tab) => {
    if (tab) pendingModuleTab.current = { module, tab };
    setCurrentModule(module);
    if (tab) setCurrentTab(tab);
  }, []);

  useEffect(() => {
    // When navigating via browser back/forward, skip resetting to default tab
    if (historyNavigating.current) return;
    if (pendingModuleTab.current && pendingModuleTab.current.module === currentModule) {
      const desiredTab = pendingModuleTab.current.tab;
      pendingModuleTab.current = null;
      setCurrentTab(desiredTab);
      return;
    }
    const moduleFirstTab = {
      optimalav: 'grid',
      weibull: 'weibull',
      rcm: 'rcm2',
      lcc: 'lcc',
      process: 'grid',
      alt: 'grid',
      reliability: 'prediction',
      dashboard: 'grid',
      enterprise: 'enterprise'
    };
    const firstTab = moduleFirstTab[currentModule] || 'grid';
    setCurrentTab(firstTab);
  }, [currentModule]);

  // Push state to browser history when module/tab changes (skip if popstate triggered the change)
  useEffect(() => {
    if (historyNavigating.current) {
      historyNavigating.current = false;
      return;
    }
    const state = { module: currentModule, tab: currentTab };
    const url = `#/${currentModule}${currentTab ? '/' + currentTab : ''}`;
    // Replace if it's the initial hub state, push otherwise
    if (window.history.state && window.history.state.module) {
      window.history.pushState(state, '', url);
    } else {
      window.history.replaceState(state, '', url);
    }
  }, [currentModule, currentTab]);

  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state && e.state.module) {
        historyNavigating.current = true;
        setCurrentModule(e.state.module);
        if (e.state.tab) {
          setCurrentTab(e.state.tab);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    // Set initial history entry
    window.history.replaceState(
      { module: currentModule, tab: currentTab },
      '',
      `#/${currentModule}${currentTab ? '/' + currentTab : ''}`
    );
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Diagram State
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [selectedGateId, setSelectedGateId] = useState(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [rbdConnections, setRbdConnections] = useState([]);

  // Simulation State
  const [simStatus, setSimStatus] = useState('idle');
  const [simProgress, setSimProgress] = useState(0);
  const [simResults, setSimResults] = useState(null);
  const [processingAction, setProcessingAction] = useState(null); // 'simulation' | 'process' | 'alt' | 'report' | 'import' | null
  const isProcessing = simStatus === 'running' || !!processingAction;
  const [simConfig, setSimConfig] = useState({
    iterations: 5000,
    missionTime: 8760,
    seed: null,
    discountRate: 10,
    currency: 'USD'
  });
  const [showSimConfig, setShowSimConfig] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);

  // Process Reliability & ALT State
  const [processResults, setProcessResults] = useState(null);
  const [altResults, setAltResults] = useState(null);
  const [batchResults, setBatchResults] = useState(null);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);

  // FT Analysis State
  const [ftCutSets, setFtCutSets] = useState([]);
  const [ftImportance, setFtImportance] = useState([]);

  // Selected data
  const [selectedRows, setSelectedRows] = useState([]);

  // Get current section items
  const sectionData = projectData[selectedSection] || [];

  // Scoped grid data - filtered by selected location hierarchy
  const scopedGridData = useMemo(() => {
    const allItems = projectData[selectedSection] || [];
    if (!selectedLocation) return allItems;

    // Get all location IDs in this scope (selected + descendants)
    const scopeLocationIds = new Set([selectedLocation.id]);
    const findDescendants = (parentId) => {
      (projectData.locations || []).forEach(loc => {
        if (loc.parent === parentId) {
          scopeLocationIds.add(loc.id);
          findDescendants(loc.id);
        }
      });
    };
    findDescendants(selectedLocation.id);

    if (selectedSection === 'locations') {
      return allItems.filter(item => scopeLocationIds.has(item.id));
    }
    if (selectedSection === 'equipments') {
      return allItems.filter(item => scopeLocationIds.has(item.location));
    }
    // For failuremodels, consequences — filter by equipment at scoped location
    if (selectedSection === 'failuremodels' || selectedSection === 'consequences') {
      const scopeEquipIds = new Set(
        (projectData.equipments || [])
          .filter(eq => scopeLocationIds.has(eq.location))
          .map(eq => eq.id)
      );
      return allItems.filter(item => scopeEquipIds.has(item.equipment) || scopeEquipIds.has(item.equipmentId));
    }
    // For spares, labor, tasks — filter by equipment location
    if (['spares', 'scheduledtasks', 'correctivetasks', 'labor'].includes(selectedSection)) {
      const scopeEquipIds = new Set(
        (projectData.equipments || [])
          .filter(eq => scopeLocationIds.has(eq.location))
          .map(eq => eq.id)
      );
      return allItems.filter(item => scopeEquipIds.has(item.equipment) || scopeEquipIds.has(item.equipmentId) || !item.equipment);
    }
    return allItems;
  }, [projectData, selectedSection, selectedLocation]);

  // Scoped project data — filters ALL entity arrays by selected location hierarchy
  // This is passed to every module view so analysis is scoped to selected level
  const scopedProjectData = useMemo(() => {
    if (!selectedLocation) return projectData;

    // Build set of location IDs in scope
    const scopeLocationIds = new Set([selectedLocation.id]);
    const findDesc = (pid) => {
      (projectData.locations || []).forEach(loc => {
        // G15: Check both 'parent' and 'parentId' field naming variants
        if (loc.parent === pid || loc.parentId === pid) {
          scopeLocationIds.add(loc.id);
          findDesc(loc.id);
        }
      });
    };
    findDesc(selectedLocation.id);

    // Scoped equipments
    // G15: Check both 'location' and 'locationId' field naming variants
    const scopedEquipments = (projectData.equipments || []).filter(eq => scopeLocationIds.has(eq.location) || scopeLocationIds.has(eq.locationId));
    const scopeEquipIds = new Set(scopedEquipments.map(eq => eq.id));

    // Scoped failure models
    const scopedFMs = (projectData.failuremodels || []).filter(fm =>
      scopeEquipIds.has(fm.equipment) || scopeEquipIds.has(fm.equipmentId)
    );
    const scopeFMIds = new Set(scopedFMs.map(fm => fm.id));

    // Scoped RBD blocks — filter if they have a location/equipment reference, otherwise include all
    const scopedRBDBlocks = (projectData.rbdblocks || []).filter(b =>
      scopeEquipIds.has(b.id) || scopeEquipIds.has(b.equipmentId) ||
      scopeLocationIds.has(b.location) || (!b.location && !b.equipmentId)
    );

    return {
      ...projectData,
      locations: (projectData.locations || []).filter(l => scopeLocationIds.has(l.id)),
      equipments: scopedEquipments,
      failuremodels: scopedFMs,
      consequences: (projectData.consequences || []).filter(c =>
        scopeEquipIds.has(c.equipment) || scopeEquipIds.has(c.equipmentId) || (!c.equipment && !c.equipmentId)
      ),
      spares: (projectData.spares || []).filter(s =>
        scopeEquipIds.has(s.equipment) || scopeEquipIds.has(s.equipmentId) || (!s.equipment && !s.equipmentId)
      ),
      labor: (projectData.labor || []).filter(l =>
        scopeEquipIds.has(l.equipment) || scopeEquipIds.has(l.equipmentId) || (!l.equipment && !l.equipmentId)
      ),
      scheduledtasks: (projectData.scheduledtasks || []).filter(t =>
        scopeEquipIds.has(t.equipment) || scopeEquipIds.has(t.equipmentId) || scopeFMIds.has(t.failureModel) || (!t.equipment && !t.equipmentId)
      ),
      correctivetasks: (projectData.correctivetasks || []).filter(t =>
        scopeEquipIds.has(t.equipment) || scopeEquipIds.has(t.equipmentId) || scopeFMIds.has(t.failureModel) || (!t.equipment && !t.equipmentId)
      ),
      rbdblocks: scopedRBDBlocks,
      rcmcauses: (projectData.rcmcauses || []).filter(rc =>
        scopeEquipIds.has(rc.equipment) || scopeEquipIds.has(rc.equipmentId) || scopeFMIds.has(rc.failureModel) || (!rc.equipment && !rc.equipmentId)
      ),
      costnodes: (projectData.costnodes || []).filter(cn =>
        scopeEquipIds.has(cn.equipment) || scopeEquipIds.has(cn.equipmentId) || scopeLocationIds.has(cn.location) || (!cn.equipment && !cn.location)
      ),
      // G14: Previously unscoped arrays — now filtered by hierarchy
      gates: (projectData.gates || []).filter(g =>
        scopeEquipIds.has(g.equipmentId) || scopeLocationIds.has(g.locationId) || (!g.equipmentId && !g.locationId)
      ),
      primaryevents: (projectData.primaryevents || []).filter(pe =>
        scopeEquipIds.has(pe.equipmentId) || scopeFMIds.has(pe.failureModelId) || (!pe.equipmentId && !pe.failureModelId)
      ),
      weibullsets: (projectData.weibullsets || []).filter(ws =>
        scopeEquipIds.has(ws.equipment) || scopeEquipIds.has(ws.equipmentId) || (!ws.equipment && !ws.equipmentId)
      ),
      phases: (projectData.phases || []).filter(p =>
        scopeEquipIds.has(p.equipmentId) || scopeLocationIds.has(p.locationId) || (!p.equipmentId && !p.locationId)
      ),
      boms: (projectData.boms || []).filter(b =>
        scopeEquipIds.has(b.equipmentId) || (!b.equipmentId)
      ),
      products: (projectData.products || []).filter(p =>
        scopeEquipIds.has(p.equipmentId) || (!p.equipmentId)
      ),
      rules: (projectData.rules || []).filter(r =>
        scopeEquipIds.has(r.equipmentId) || (!r.equipmentId)
      ),
      rcmFunctions: (projectData.rcmFunctions || []).filter(f =>
        scopeEquipIds.has(f.equipmentId) || scopeEquipIds.has(f.assetId) || (!f.equipmentId && !f.assetId)
      ),
      rcmFunctionalFailures: (projectData.rcmFunctionalFailures || []).filter(ff =>
        scopeEquipIds.has(ff.equipmentId) || (!ff.equipmentId)
      ),
      rcmStudies: (projectData.rcmStudies || []).filter(s =>
        scopeEquipIds.has(s.equipmentId) || (!s.equipmentId)
      ),
      rcmDefaultActions: (projectData.rcmDefaultActions || []).filter(a =>
        scopeEquipIds.has(a.equipmentId) || (!a.equipmentId)
      ),
      eventtrees: (projectData.eventtrees || []).filter(et =>
        scopeEquipIds.has(et.equipmentId) || scopeLocationIds.has(et.locationId) || (!et.equipmentId && !et.locationId)
      ),
      maintenanceorders: (projectData.maintenanceorders || []).filter(mo =>
        scopeEquipIds.has(mo.equipmentId) || scopeEquipIds.has(mo.equipment) || (!mo.equipmentId && !mo.equipment)
      ),
      taskgroups: (projectData.taskgroups || []).filter(tg =>
        scopeEquipIds.has(tg.equipmentId) || (!tg.equipmentId)
      ),
      // G04/G05: New persisted arrays
      markovAnalyses: (projectData.markovAnalyses || []).filter(ma =>
        scopeEquipIds.has(ma.equipmentId) || scopeLocationIds.has(ma.locationId) || (!ma.equipmentId && !ma.locationId)
      ),
      lifecycleAssessments: (projectData.lifecycleAssessments || []).filter(la =>
        scopeEquipIds.has(la.equipmentId) || (!la.equipmentId)
      ),
    };
  }, [projectData, selectedLocation]);

  // File operations
  const handleNew = useCallback(() => {
    if (!window.confirm('Create a new empty project? Any unsaved changes will be lost.')) return;
    setProjectData({
      locations: [],
      equipments: [],
      failuremodels: [],
      consequences: [],
      spares: [],
      labor: [],
      scheduledtasks: [],
      correctivetasks: [],
      taskgroups: [],
      rbdblocks: [],
      gates: [],
      primaryevents: [],
      phases: [],
      weibullsets: [],
      boms: [],
      products: [],
      rules: [],
      costnodes: [],
      rcmcauses: [],
      rcmFunctions: [],
      rcmFunctionalFailures: [],
      rcmStudies: [],
      rcmDefaultActions: [],
      eventtrees: [],
      maintenanceorders: [],
      markovAnalyses: [],
      lifecycleAssessments: []
    });
    setSelectedItem(null);
    setSimResults(null);
  }, [setProjectData]);

  const handleOpen = useCallback(() => {
    if (window.electronAPI?.openProject) {
      window.electronAPI.openProject().then((data) => {
        if (data) setProjectData(data);
      });
    }
  }, [setProjectData]);

  const handleSave = useCallback(() => {
    if (window.electronAPI?.saveProject) {
      window.electronAPI.saveProject(projectData);
    } else {
      localStorage.setItem('optimalAvailabilityProject', JSON.stringify(projectData));
    }
  }, [projectData]);

  // Simulation
  const handleRunSimulation = useCallback(() => {
    // Use scoped data so simulation respects selected location hierarchy
    const simData = scopedProjectData;
    const blocks = simData.rbdblocks || simData.equipments || [];
    if (blocks.length === 0) {
      alert('Cannot run simulation: no RBD blocks or equipment defined at this scope.\n\nSelect a different hierarchy level or add equipment items in the Project Structure tree.');
      return;
    }

    const failureModels = simData.failuremodels || [];
    if (failureModels.length === 0) {
      if (!window.confirm('No failure modes defined at this scope. The simulation will use default parameters (exponential, MTTF=5000h).\n\nContinue anyway?')) return;
    }

    setSimStatus('running');
    setSimProgress(0);
    setProcessingAction('simulation');

    // Build components array from RBD blocks + failure modes for the engine
    const failureModelMap = {};
    (simData.failuremodels || []).forEach(fm => { failureModelMap[fm.id] = fm; });

    const components = (simData.rbdblocks || simData.equipments || []).map(block => {
      const fm = failureModelMap[block.failureModel] || {};
      return {
        id: block.id,
        name: block.description || block.id,
        blockType: block.blockType || 'series',
        kValue: block.kValue,
        nValue: block.nValue,
        state: block.state || 'operating',
        failureModel: {
          distribution: (fm.distribution || 'exponential').toLowerCase(),
          rate: fm.lambda || (fm.mttf ? 1 / fm.mttf : 0.001),
          lambda: fm.lambda || (fm.mttf ? 1 / fm.mttf : 0.001),
          eta: fm.eta || fm.mttf || 5000,
          beta: fm.beta || 1.0,
          gamma: fm.gamma || 0,
          mu: fm.mu,
          sigma: fm.sigma,
          mttf: fm.mttf || fm.eta || 5000,
          value: fm.eta || fm.mttf || 5000, // for Fixed distribution
        },
        repairModel: {
          distribution: (fm.repairDistribution || 'fixed').toLowerCase(),
          mttr: fm.mttr || 8,
          eta: fm.repairEta || fm.mttr || 8,
          beta: fm.repairBeta || 1.0,
          value: fm.mttr || fm.repairEta || 8, // for Fixed distribution
          lambda: fm.mttr ? 1 / fm.mttr : 0.125,
        },
        repairCost: 75, // hourly labor rate for repairs
        sparesCost: 500, // per-repair spare parts cost
        consequenceCost: 50, // per-hour downtime consequence cost
        pmInterval: null,
        pmDuration: 2,
        pmCost: 200,
      };
    });

    const simDataFinal = { ...simData, components };
    console.log('[SIM] Starting simulation with', components.length, 'components:', components.map(c => c.name));

    let engine;
    try {
      engine = new SimulationEngine(simDataFinal, simConfig);
    } catch (initErr) {
      console.error('[SIM] Engine init error:', initErr);
      alert('Simulation init error: ' + initErr.message);
      setSimStatus('stopped');
      setProcessingAction(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        console.log('[SIM] Engine.run() starting...');
        const results = await engine.run((progress) => {
          setSimProgress(progress);
        });
        console.log('[SIM] Engine.run() completed, results:', results ? 'OK' : 'NULL');

        if (results) {
          // Map engine output keys to what ResultsPanel expects
          const mapped = {
            ...results,
            systemAvailability: (results.systemAvailability * 100).toFixed(2),
            mtbf: results.systemMTBF ? results.systemMTBF.toFixed(1) : '0',
            mttr: results.systemMTTR ? results.systemMTTR.toFixed(1) : '0',
            totalCost: results.totalCost || 0,
          };
          setSimResults(mapped);
          setSimStatus('completed');
          setProcessingAction(null);
          setCurrentTab('results');
        } else {
          setSimStatus('stopped');
        }
      } catch (err) {
        console.error('[SIM] Simulation error:', err.message, err.stack);
        alert('Simulation error: ' + err.message);
        setSimStatus('stopped');
        setProcessingAction(null);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [scopedProjectData, simConfig]);

  const handleStopSimulation = useCallback(() => {
    setSimStatus('stopped');
    setSimProgress(0);
    setProcessingAction(null);
  }, []);

  // Process Reliability Analysis
  const handleRunProcessAnalysis = useCallback((results) => {
    setProcessResults(results);
    setCurrentTab('process');
  }, []);

  // ALT Analysis
  const handleRunALT = useCallback((results) => {
    setAltResults(results);
    setCurrentTab('alt');
  }, []);

  // Batch Simulation
  const handleRunBatch = useCallback((results) => {
    setBatchResults(results);
  }, []);

  // FT Analysis
  const handleRunFTAnalysis = useCallback(() => {
    const gatesMap = {};
    const eventsMap = {};
    (projectData.gates || []).forEach(g => {
      gatesMap[g.id] = { type: g.gateType || 'OR', inputs: g.inputs || [], k: g.k };
    });
    (projectData.primaryevents || []).forEach(e => {
      eventsMap[e.id] = { type: e.type || 'Basic', distribution: e.distribution || 'exponential', lambda: e.lambda || 0.001, eta: e.eta, beta: e.beta, probability: e.probability };
    });
    const ftEngine = new FaultTreeEngine(gatesMap, eventsMap, {});
    const topGate = projectData.gates?.[0]?.id;
    if (topGate) {
      const results = ftEngine.analyze(topGate, simConfig.missionTime || 8760);
      setFtCutSets(results.minimalCutSets || []);
      setFtImportance(results.importanceMeasures || []);
    }
  }, [projectData, simConfig]);

  // XML Import
  const handleImportXML = useCallback(async () => {
    if (window.electronAPI?.importXML) {
      const xmlString = await window.electronAPI.importXML();
      if (xmlString) {
        try {
          const imported = XMLImport.importOAWX(xmlString);
          if (imported) setProjectData(imported);
        } catch (_err) {
          try {
            const imported = XMLImport.importAWBFile(xmlString);
            if (imported) setProjectData(imported);
          } catch (err2) {
            console.error('XML import failed:', err2);
          }
        }
      }
    }
  }, [setProjectData]);

  // Excel Import
  const handleImportExcel = useCallback(async () => {
    if (window.electronAPI?.importExcel) {
      const filePath = await window.electronAPI.importExcel();
      if (filePath) {
        try {
          const importedData = await ExcelIO.importWorkbook(filePath);
          if (importedData) {
            setProjectData(importedData);
          }
        } catch (error) {
          console.error('Error importing Excel:', error);
        }
      }
    }
  }, [setProjectData]);

  // Excel Export
  const handleExportExcel = useCallback(async () => {
    try {
      const buffer = ExcelIO.exportToExcel(projectData);
      if (window.electronAPI?.exportExcel) {
        await window.electronAPI.exportExcel(Array.from(new Uint8Array(buffer)));
      } else {
        // Fallback: create blob and download
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'project_export.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
    }
  }, [projectData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey) {
        if (e.key === 's') {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'z') {
          e.preventDefault();
          undo();
        } else if (e.key === 'y') {
          e.preventDefault();
          redo();
        } else if (e.key === 'n') {
          e.preventDefault();
          handleNew();
        } else if (e.key === 'o') {
          e.preventDefault();
          handleOpen();
        }
      }
      if (e.key === 'F5') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRunFTAnalysis();
        } else {
          handleRunSimulation();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, undo, redo, handleNew, handleOpen, handleRunSimulation]);

  // Entity operations
  const handleAddItem = useCallback((sectionId) => {
    const newId = `${sectionId.toUpperCase()}-${Date.now()}`;
    const newItem = {
      id: newId,
      description: `New ${sectionId}`,
    };

    const updatedData = { ...projectData };
    if (!updatedData[sectionId]) updatedData[sectionId] = [];
    updatedData[sectionId].push(newItem);
    setProjectData(updatedData);
  }, [projectData, setProjectData]);

  const handleDeleteItem = useCallback((itemId) => {
    const updatedData = { ...projectData };
    updatedData[selectedSection] = updatedData[selectedSection].filter(i => i.id !== itemId);
    setProjectData(updatedData);
    setSelectedItem(null);
  }, [projectData, selectedSection, setProjectData]);

  const handleUpdateItem = useCallback((itemId, updates) => {
    const updatedData = { ...projectData };
    const item = updatedData[selectedSection].find(i => i.id === itemId);
    if (item) {
      Object.assign(item, updates);
      setProjectData(updatedData);
    }
  }, [projectData, selectedSection, setProjectData]);

  // Diagram operations
  const handleUpdateBlock = useCallback((blockId, updates) => {
    const updatedData = { ...projectData };
    const block = updatedData.rbdblocks.find(b => b.id === blockId);
    if (block) {
      Object.assign(block, updates);
      setProjectData(updatedData);
    }
  }, [projectData, setProjectData]);

  const handleAddBlock = useCallback((block) => {
    const updatedData = { ...projectData };
    if (!updatedData.rbdblocks) updatedData.rbdblocks = [];
    updatedData.rbdblocks.push(block);
    setProjectData(updatedData);
  }, [projectData, setProjectData]);

  const handleDeleteBlock = useCallback((blockId) => {
    const updatedData = { ...projectData };
    updatedData.rbdblocks = updatedData.rbdblocks.filter(b => b.id !== blockId);
    setProjectData(updatedData);
  }, [projectData, setProjectData]);

  const handleAddConnection = useCallback((fromId, toId) => {
    setRbdConnections([...rbdConnections, { from: fromId, to: toId, id: `CONN-${Date.now()}` }]);
  }, [rbdConnections]);

  const handleDeleteConnection = useCallback((connId) => {
    setRbdConnections(rbdConnections.filter((_, idx) => idx !== connId));
    setSelectedConnectionId(null);
  }, [rbdConnections]);

  // ============================================================================
  // RELIABILITY ENGINEERING VIEW COMPONENTS
  // ============================================================================

  const ReliabilityPredictionView = ({ projectData, setProjectData }) => {
    const parts = projectData?.predictionParts || [];
    const [editingId, setEditingId] = useState(null);
    const [editRow, setEditRow] = useState({});
    const [showAddRow, setShowAddRow] = useState(false);
    const [newRow, setNewRow] = useState({ name: '', type: 'resistor', quantity: 1, environment: 'GM', quality: 'JAN', stress_ratio: 0.3, temp: 55 });

    const partTypes = ['resistor', 'capacitor', 'ic_digital', 'ic_analog', 'transistor', 'diode', 'relay', 'connector', 'switch', 'transformer'];
    const environments = ['GB', 'GF', 'GM', 'NS', 'NU', 'AIC', 'AIF', 'AUC', 'AUF', 'ARW', 'SF', 'MF', 'ML', 'CL'];
    const qualities = ['JAN', 'JANTX', 'JANS', 'MIL', 'Lower'];

    const { partRates, total_rate, system_mtbf } = useMemo(() => parts.length > 0 ? calculateAssemblyMetrics(parts) : { partRates: [], total_rate: 0, system_mtbf: 0 }, [parts]);
    const [standard, setStandard] = useState('MIL-217F');
    const standards = ['MIL-217F', '217Plus', 'Telcordia SR-332', 'FIDES', 'NSWC', 'IEC TR 62380'];

    const handleAdd = () => {
      if (!newRow.name.trim()) return;
      setProjectData(prev => ({
        ...prev,
        predictionParts: [...(prev.predictionParts || []), { ...newRow, id: Date.now(), quantity: Number(newRow.quantity) || 1, stress_ratio: Number(newRow.stress_ratio) || 0.3, temp: Number(newRow.temp) || 55 }]
      }));
      setNewRow({ name: '', type: 'resistor', quantity: 1, environment: 'GM', quality: 'JAN', stress_ratio: 0.3, temp: 55 });
      setShowAddRow(false);
    };

    const handleDelete = (id) => {
      setProjectData(prev => ({ ...prev, predictionParts: (prev.predictionParts || []).filter(p => p.id !== id) }));
    };

    const handleEditSave = (id) => {
      setProjectData(prev => ({
        ...prev,
        predictionParts: (prev.predictionParts || []).map(p => p.id === id ? { ...p, ...editRow, quantity: Number(editRow.quantity) || 1, stress_ratio: Number(editRow.stress_ratio) || 0.3, temp: Number(editRow.temp) || 55 } : p)
      }));
      setEditingId(null);
    };

    const inputStyle = { background: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '6px 8px', fontSize: '12px', width: '100%' };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Standard</div>
            <select value={standard} onChange={e => setStandard(e.target.value)}
              style={{ background: COLORS.PANEL_BG, color: COLORS.TEXT, fontSize: '14px', fontWeight: 'bold', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '8px', width: '100%' }}>
              {standards.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>System Failure Rate</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{parts.length > 0 ? (total_rate * 1e6).toFixed(3) : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>failures/million hours</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>System MTBF</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{parts.length > 0 ? system_mtbf.toFixed(0) : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>hours</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Total Parts</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{parts.length > 0 ? parts.reduce((s, p) => s + p.quantity, 0) : '0'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>{parts.length} unique types</div>
          </div>
        </div>

        <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ background: COLORS.NAVY, padding: '16px 24px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ color: '#FFFFFF', fontWeight: 'bold', margin: 0, fontSize: '14px' }}>Parts List with Failure Rates</h3>
            <button onClick={() => setShowAddRow(true)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>+ Add Part</button>
          </div>
          {parts.length === 0 && !showAddRow ? (
            <div style={{ padding: '48px', textAlign: 'center', color: COLORS.TEXT_DIM }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No parts defined</div>
              <div style={{ fontSize: '13px' }}>Click "Add Part" to begin building your parts list for reliability prediction.</div>
            </div>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead style={{ background: COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Part</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Qty</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Environment</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Quality</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Stress</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Temp (°C)</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>λ (×10⁹/hr)</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>MTBF (hrs)</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {showAddRow && (
                  <tr style={{ background: COLORS.TEAL_LIGHT, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '8px' }}><input value={newRow.name} onChange={e => setNewRow({ ...newRow, name: e.target.value })} placeholder="Part name" style={inputStyle} /></td>
                    <td style={{ padding: '8px' }}><select value={newRow.type} onChange={e => setNewRow({ ...newRow, type: e.target.value })} style={inputStyle}>{partTypes.map(t => <option key={t} value={t}>{t}</option>)}</select></td>
                    <td style={{ padding: '8px' }}><input type="number" value={newRow.quantity} onChange={e => setNewRow({ ...newRow, quantity: e.target.value })} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} /></td>
                    <td style={{ padding: '8px' }}><select value={newRow.environment} onChange={e => setNewRow({ ...newRow, environment: e.target.value })} style={inputStyle}>{environments.map(env => <option key={env} value={env}>{env}</option>)}</select></td>
                    <td style={{ padding: '8px' }}><select value={newRow.quality} onChange={e => setNewRow({ ...newRow, quality: e.target.value })} style={inputStyle}>{qualities.map(q => <option key={q} value={q}>{q}</option>)}</select></td>
                    <td style={{ padding: '8px' }}><input type="number" step="0.1" value={newRow.stress_ratio} onChange={e => setNewRow({ ...newRow, stress_ratio: e.target.value })} style={{ ...inputStyle, width: '60px', textAlign: 'right' }} /></td>
                    <td style={{ padding: '8px' }}><input type="number" value={newRow.temp} onChange={e => setNewRow({ ...newRow, temp: e.target.value })} style={{ ...inputStyle, width: '60px', textAlign: 'right' }} /></td>
                    <td colSpan="2" style={{ padding: '8px', textAlign: 'center' }}>
                      <button onClick={handleAdd} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                      <button onClick={() => setShowAddRow(false)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                    </td>
                  </tr>
                )}
                {partRates.map((part, idx) => (
                  <tr key={part.id} style={{ background: idx % 2 === 0 ? COLORS.LIGHT_BG : COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    {editingId === part.id ? (
                      <>
                        <td style={{ padding: '8px' }}><input value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} style={inputStyle} /></td>
                        <td style={{ padding: '8px' }}><select value={editRow.type} onChange={e => setEditRow({ ...editRow, type: e.target.value })} style={inputStyle}>{partTypes.map(t => <option key={t} value={t}>{t}</option>)}</select></td>
                        <td style={{ padding: '8px' }}><input type="number" value={editRow.quantity} onChange={e => setEditRow({ ...editRow, quantity: e.target.value })} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} /></td>
                        <td style={{ padding: '8px' }}><select value={editRow.environment} onChange={e => setEditRow({ ...editRow, environment: e.target.value })} style={inputStyle}>{environments.map(env => <option key={env} value={env}>{env}</option>)}</select></td>
                        <td style={{ padding: '8px' }}><select value={editRow.quality} onChange={e => setEditRow({ ...editRow, quality: e.target.value })} style={inputStyle}>{qualities.map(q => <option key={q} value={q}>{q}</option>)}</select></td>
                        <td style={{ padding: '8px' }}><input type="number" step="0.1" value={editRow.stress_ratio} onChange={e => setEditRow({ ...editRow, stress_ratio: e.target.value })} style={{ ...inputStyle, width: '60px', textAlign: 'right' }} /></td>
                        <td style={{ padding: '8px' }}><input type="number" value={editRow.temp} onChange={e => setEditRow({ ...editRow, temp: e.target.value })} style={{ ...inputStyle, width: '60px', textAlign: 'right' }} /></td>
                        <td style={{ padding: '8px' }}></td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button onClick={() => handleEditSave(part.id)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{part.name}</td>
                        <td style={{ padding: '12px', color: COLORS.TEXT_DIM }}>{part.type}</td>
                        <td style={{ padding: '12px', textAlign: 'center', color: COLORS.TEXT_DIM }}>{part.quantity}</td>
                        <td style={{ padding: '12px', color: COLORS.TEXT_DIM }}>{part.environment}</td>
                        <td style={{ padding: '12px', color: COLORS.TEXT_DIM }}>{part.quality}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT_DIM }}>{part.stress_ratio}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT_DIM }}>{part.temp}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT, fontFamily: 'monospace' }}>{(part.failure_rate * 1e9).toFixed(4)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT, fontFamily: 'monospace' }}>{part.mtbf_hours.toFixed(0)}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button onClick={() => { setEditingId(part.id); setEditRow(part); }} style={{ background: 'transparent', color: COLORS.TEAL, border: 'none', cursor: 'pointer', fontSize: '12px', marginRight: '8px' }}>Edit</button>
                          <button onClick={() => handleDelete(part.id)} style={{ background: 'transparent', color: '#d32f2f', border: 'none', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {parts.length > 0 && (
                <tr style={{ background: COLORS.LIGHT_BG, borderTop: `2px solid ${COLORS.BORDER}` }}>
                  <td colSpan="7" style={{ padding: '12px', fontWeight: 'bold', color: COLORS.TEXT }}>TOTAL</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: COLORS.NAVY, fontFamily: 'monospace' }}>{(total_rate * 1e9).toFixed(4)}</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: COLORS.NAVY, fontFamily: 'monospace' }}>{system_mtbf.toFixed(0)}</td>
                  <td></td>
                </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {parts.length > 0 && (
        <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
          <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>Top Contributors to Failure Rate</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[...partRates].sort((a, b) => b.failure_rate - a.failure_rate).slice(0, 8)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip formatter={v => (v * 1e9).toFixed(4)} />
              <Bar dataKey="failure_rate" fill={COLORS.TEAL} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}
      </div>
    );
  };

  const FMECAWorksheetView = ({ projectData, setProjectData }) => {
    const fmecaItems = projectData?.fmecaItems || [];
    const [showAddRow, setShowAddRow] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editRow, setEditRow] = useState({});
    const [newRow, setNewRow] = useState({ item: '', function: '', mode: '', local_effect: '', severity: 'Minor', freq: 'Remote', detection: 'Good' });

    const severityOptions = Object.keys(SEVERITY_LEVELS);
    const freqOptions = ['Remote', 'Low', 'Moderate', 'High'];
    const detOptions = ['Good', 'Fair', 'Poor'];

    const enrichedItems = fmecaItems.map(item => {
      const severity = SEVERITY_LEVELS[item.severity];
      const freq_val = { 'Remote': 1, 'Low': 2, 'Moderate': 3, 'High': 4 }[item.freq] || 1;
      const det_val = { 'Good': 1, 'Fair': 2, 'Poor': 3 }[item.detection] || 2;
      const rpn = (severity?.value || 1) * freq_val * det_val;
      return { ...item, rpn, severity_value: severity?.value || 1, freq_val, det_val };
    }).sort((a, b) => b.rpn - a.rpn);

    const handleAdd = () => {
      if (!newRow.item.trim()) return;
      setProjectData(prev => ({
        ...prev,
        fmecaItems: [...(prev.fmecaItems || []), { ...newRow, id: Date.now() }]
      }));
      setNewRow({ item: '', function: '', mode: '', local_effect: '', severity: 'Minor', freq: 'Remote', detection: 'Good' });
      setShowAddRow(false);
    };

    const handleDelete = (id) => {
      setProjectData(prev => ({ ...prev, fmecaItems: (prev.fmecaItems || []).filter(i => i.id !== id) }));
    };

    const handleEditSave = (id) => {
      setProjectData(prev => ({
        ...prev,
        fmecaItems: (prev.fmecaItems || []).map(i => i.id === id ? { ...i, ...editRow } : i)
      }));
      setEditingId(null);
    };

    const inputStyle = { background: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '6px 8px', fontSize: '11px', width: '100%' };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Critical Items</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{enrichedItems.filter(i => i.severity === 'Catastrophic').length}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Severity = Catastrophic</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Total Modes</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{enrichedItems.length}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Analyzed modes</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Avg RPN</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{enrichedItems.length > 0 ? (enrichedItems.reduce((s, i) => s + i.rpn, 0) / enrichedItems.length).toFixed(0) : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Risk Priority</div>
          </div>
        </div>

        <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ background: COLORS.NAVY, padding: '16px 24px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ color: '#FFFFFF', fontWeight: 'bold', margin: 0, fontSize: '14px' }}>FMECA Worksheet (Sorted by RPN)</h3>
            <button onClick={() => setShowAddRow(true)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>+ Add Mode</button>
          </div>
          {fmecaItems.length === 0 && !showAddRow ? (
            <div style={{ padding: '48px', textAlign: 'center', color: COLORS.TEXT_DIM }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No failure modes defined</div>
              <div style={{ fontSize: '13px' }}>Click "Add Mode" to begin your FMECA worksheet.</div>
            </div>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead style={{ background: COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Item</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Function</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Failure Mode</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Effect</th>
                  <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Sev</th>
                  <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Occ</th>
                  <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Det</th>
                  <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>RPN</th>
                  <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {showAddRow && (
                  <tr style={{ background: COLORS.TEAL_LIGHT, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '6px' }}><input value={newRow.item} onChange={e => setNewRow({ ...newRow, item: e.target.value })} placeholder="Item" style={inputStyle} /></td>
                    <td style={{ padding: '6px' }}><input value={newRow.function} onChange={e => setNewRow({ ...newRow, function: e.target.value })} placeholder="Function" style={inputStyle} /></td>
                    <td style={{ padding: '6px' }}><input value={newRow.mode} onChange={e => setNewRow({ ...newRow, mode: e.target.value })} placeholder="Failure mode" style={inputStyle} /></td>
                    <td style={{ padding: '6px' }}><input value={newRow.local_effect} onChange={e => setNewRow({ ...newRow, local_effect: e.target.value })} placeholder="Effect" style={inputStyle} /></td>
                    <td style={{ padding: '6px' }}><select value={newRow.severity} onChange={e => setNewRow({ ...newRow, severity: e.target.value })} style={inputStyle}>{severityOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                    <td style={{ padding: '6px' }}><select value={newRow.freq} onChange={e => setNewRow({ ...newRow, freq: e.target.value })} style={inputStyle}>{freqOptions.map(f => <option key={f} value={f}>{f}</option>)}</select></td>
                    <td style={{ padding: '6px' }}><select value={newRow.detection} onChange={e => setNewRow({ ...newRow, detection: e.target.value })} style={inputStyle}>{detOptions.map(d => <option key={d} value={d}>{d}</option>)}</select></td>
                    <td colSpan="2" style={{ padding: '6px', textAlign: 'center' }}>
                      <button onClick={handleAdd} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                      <button onClick={() => setShowAddRow(false)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                    </td>
                  </tr>
                )}
                {enrichedItems.map((item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? COLORS.LIGHT_BG : COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    {editingId === item.id ? (
                      <>
                        <td style={{ padding: '6px' }}><input value={editRow.item} onChange={e => setEditRow({ ...editRow, item: e.target.value })} style={inputStyle} /></td>
                        <td style={{ padding: '6px' }}><input value={editRow.function} onChange={e => setEditRow({ ...editRow, function: e.target.value })} style={inputStyle} /></td>
                        <td style={{ padding: '6px' }}><input value={editRow.mode} onChange={e => setEditRow({ ...editRow, mode: e.target.value })} style={inputStyle} /></td>
                        <td style={{ padding: '6px' }}><input value={editRow.local_effect} onChange={e => setEditRow({ ...editRow, local_effect: e.target.value })} style={inputStyle} /></td>
                        <td style={{ padding: '6px' }}><select value={editRow.severity} onChange={e => setEditRow({ ...editRow, severity: e.target.value })} style={inputStyle}>{severityOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                        <td style={{ padding: '6px' }}><select value={editRow.freq} onChange={e => setEditRow({ ...editRow, freq: e.target.value })} style={inputStyle}>{freqOptions.map(f => <option key={f} value={f}>{f}</option>)}</select></td>
                        <td style={{ padding: '6px' }}><select value={editRow.detection} onChange={e => setEditRow({ ...editRow, detection: e.target.value })} style={inputStyle}>{detOptions.map(d => <option key={d} value={d}>{d}</option>)}</select></td>
                        <td></td>
                        <td style={{ padding: '6px', textAlign: 'center' }}>
                          <button onClick={() => handleEditSave(item.id)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '10px', color: COLORS.TEXT, fontWeight: '500' }}>{item.item}</td>
                        <td style={{ padding: '10px', color: COLORS.TEXT_DIM, fontSize: '11px' }}>{item.function}</td>
                        <td style={{ padding: '10px', color: COLORS.TEXT_DIM, fontSize: '11px' }}>{item.mode}</td>
                        <td style={{ padding: '10px', color: COLORS.TEXT_DIM, fontSize: '11px' }}>{item.local_effect}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={{ padding: '4px 8px', borderRadius: '3px', color: '#FFFFFF', fontWeight: 'bold', fontSize: '11px', backgroundColor: SEVERITY_LEVELS[item.severity]?.color }}>
                            {item.severity_value}
                          </span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', color: COLORS.TEXT, fontWeight: '500' }}>{item.freq_val}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: COLORS.TEXT, fontWeight: '500' }}>{item.det_val}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: COLORS.NAVY, fontWeight: 'bold' }}>{item.rpn}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <button onClick={() => { setEditingId(item.id); setEditRow(item); }} style={{ background: 'transparent', color: COLORS.TEAL, border: 'none', cursor: 'pointer', fontSize: '12px', marginRight: '8px' }}>Edit</button>
                          <button onClick={() => handleDelete(item.id)} style={{ background: 'transparent', color: '#d32f2f', border: 'none', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {enrichedItems.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>RPN Distribution (Top 8)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={enrichedItems.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="item" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="rpn" fill={COLORS.TEAL} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>Severity Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(
                    enrichedItems.reduce((acc, item) => {
                      acc[item.severity] = (acc[item.severity] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([name, value]) => ({ name, value }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                >
                  {Object.entries(
                    enrichedItems.reduce((acc, item) => {
                      acc[item.severity] = (acc[item.severity] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([name], idx) => (
                    <Cell key={idx} fill={SEVERITY_LEVELS[name]?.color || '#999'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        )}
      </div>
    );
  };

  const ReliabilityGrowthView = ({ projectData, setProjectData }) => {
    const growth = projectData?.growthPhases || [];
    const [showAddRow, setShowAddRow] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editRow, setEditRow] = useState({});
    const [newRow, setNewRow] = useState({ test_time: '', failures: '' });

    const recalcCum = (phases) => {
      let cum = 0;
      return phases.sort((a, b) => a.test_time - b.test_time).map((p, i) => {
        cum += Number(p.failures) || 0;
        return { ...p, phase: i + 1, cum_failures: cum };
      });
    };

    const handleAdd = () => {
      if (!newRow.test_time || !newRow.failures) return;
      setProjectData(prev => {
        const updated = [...(prev.growthPhases || []), { id: Date.now(), test_time: Number(newRow.test_time), failures: Number(newRow.failures), cum_failures: 0 }];
        return { ...prev, growthPhases: recalcCum(updated) };
      });
      setNewRow({ test_time: '', failures: '' });
      setShowAddRow(false);
    };

    const handleDelete = (id) => {
      setProjectData(prev => {
        const updated = (prev.growthPhases || []).filter(p => p.id !== id);
        return { ...prev, growthPhases: recalcCum(updated) };
      });
    };

    const handleEditSave = (id) => {
      setProjectData(prev => {
        const updated = (prev.growthPhases || []).map(p => p.id === id ? { ...p, test_time: Number(editRow.test_time), failures: Number(editRow.failures) } : p);
        return { ...prev, growthPhases: recalcCum(updated) };
      });
      setEditingId(null);
    };

    const hasData = growth.length >= 2;
    const { growth_rate, a, current_mtbf } = useMemo(() => hasData ? calculateDuaneGrowth(growth) : { growth_rate: 0, a: 0, current_mtbf: 0 }, [growth]);

    const growthData = useMemo(() => {
      return growth.map(d => ({
        phase: d.phase,
        cum_mtbf: d.cum_failures > 0 ? d.test_time / d.cum_failures : 0,
        test_time: d.test_time,
        failures: d.cum_failures
      }));
    }, [growth]);

    const projectedData = useMemo(() => {
      if (!hasData || a === 0) return [];
      return Array.from({ length: 50 }, (_, i) => {
        const t = Math.max(...growth.map(d => d.test_time)) * (1 + i / 50);
        const mtbf = a * Math.pow(t, growth_rate);
        return { test_time: t, cum_mtbf: mtbf };
      });
    }, [a, growth_rate, growth, hasData]);

    const inputStyle = { background: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '6px 8px', fontSize: '12px', width: '80px', textAlign: 'right' };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Growth Rate (α)</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{hasData ? growth_rate.toFixed(4) : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Duane model exponent</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Scale Factor (a)</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{hasData ? (a * 1000).toFixed(1) : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>×1000 hours</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Current MTBF</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{hasData ? (current_mtbf / 1000).toFixed(2) : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>thousand hours</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Test Phases</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{growth.length}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Completed phases</div>
          </div>
        </div>

        <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ background: COLORS.NAVY, padding: '16px 24px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ color: '#FFFFFF', fontWeight: 'bold', margin: 0, fontSize: '14px' }}>Test Phase Data</h3>
            <button onClick={() => setShowAddRow(true)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>+ Add Phase</button>
          </div>
          {growth.length === 0 && !showAddRow ? (
            <div style={{ padding: '48px', textAlign: 'center', color: COLORS.TEXT_DIM }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No test phases defined</div>
              <div style={{ fontSize: '13px' }}>Click "Add Phase" to enter cumulative test time and failure data for Duane growth modelling. At least 2 phases are needed.</div>
            </div>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead style={{ background: COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Phase</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Cum. Test Time (hrs)</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Phase Failures</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Cum Failures</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>MTBF (hrs)</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {showAddRow && (
                  <tr style={{ background: COLORS.TEAL_LIGHT, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '8px', color: COLORS.TEXT_DIM, textAlign: 'center' }}>—</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}><input type="number" value={newRow.test_time} onChange={e => setNewRow({ ...newRow, test_time: e.target.value })} placeholder="Cum. hours" style={inputStyle} /></td>
                    <td style={{ padding: '8px', textAlign: 'right' }}><input type="number" value={newRow.failures} onChange={e => setNewRow({ ...newRow, failures: e.target.value })} placeholder="Failures" style={inputStyle} /></td>
                    <td colSpan="3" style={{ padding: '8px', textAlign: 'center' }}>
                      <button onClick={handleAdd} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                      <button onClick={() => setShowAddRow(false)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                    </td>
                  </tr>
                )}
                {growthData.map((row, idx) => (
                  <tr key={growth[idx].id} style={{ background: idx % 2 === 0 ? COLORS.LIGHT_BG : COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    {editingId === growth[idx].id ? (
                      <>
                        <td style={{ padding: '12px', color: COLORS.TEXT }}>{row.phase}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}><input type="number" value={editRow.test_time} onChange={e => setEditRow({ ...editRow, test_time: e.target.value })} style={inputStyle} /></td>
                        <td style={{ padding: '8px', textAlign: 'right' }}><input type="number" value={editRow.failures} onChange={e => setEditRow({ ...editRow, failures: e.target.value })} style={inputStyle} /></td>
                        <td></td><td></td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button onClick={() => handleEditSave(growth[idx].id)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{row.phase}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT_DIM }}>{row.test_time.toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT_DIM }}>{growth[idx].failures}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT_DIM }}>{row.failures}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: COLORS.NAVY, fontWeight: '600' }}>{row.cum_mtbf > 0 ? row.cum_mtbf.toFixed(0) : '—'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button onClick={() => { setEditingId(growth[idx].id); setEditRow(growth[idx]); }} style={{ background: 'transparent', color: COLORS.TEAL, border: 'none', cursor: 'pointer', fontSize: '12px', marginRight: '8px' }}>Edit</button>
                          <button onClick={() => handleDelete(growth[idx].id)} style={{ background: 'transparent', color: '#d32f2f', border: 'none', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {hasData && (
        <>
        <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
          <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>Cumulative MTBF vs Test Time (Duane Model)</h3>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart
              data={[...growthData.map(d => ({ ...d, type: 'observed' })), ...projectedData.map(d => ({ ...d, type: 'projected' }))]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="test_time" label={{ value: 'Cumulative Test Time (hours)', position: 'insideBottomRight', offset: -5 }} />
              <YAxis label={{ value: 'MTBF (hours)', angle: -90, position: 'insideLeft' }} />
              <Tooltip formatter={v => v.toFixed(0)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="cum_mtbf"
                stroke={COLORS.TEAL}
                strokeWidth={3}
                dot={{ fill: COLORS.TEAL, r: 4 }}
                name="Cumulative MTBF"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: COLORS.TEAL_LIGHT, border: `1px solid ${COLORS.TEAL}`, borderRadius: '4px', padding: '24px' }}>
          <h3 style={{ color: COLORS.NAVY, fontWeight: 'bold', margin: '0 0 12px 0' }}>Duane Model Equation</h3>
          <div style={{ fontFamily: 'monospace', fontSize: '13px', color: COLORS.NAVY }}>
            M(t) = a × t<sup>α</sup>
          </div>
          <p style={{ fontSize: '13px', color: COLORS.NAVY, margin: '12px 0 0 0' }}>
            Current model: <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>M(t) = {(a * 1000).toFixed(2)} × t<sup>{growth_rate.toFixed(4)}</sup></span>
          </p>
        </div>
        </>
        )}
      </div>
    );
  };

  const SafetyAssessmentView = ({ projectData, setProjectData }) => {
    const hazards = projectData?.safetyHazards || [];
    const [showAddRow, setShowAddRow] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editRow, setEditRow] = useState({});
    const [newRow, setNewRow] = useState({ hazard: '', severity: 'Minor', probability: 'Remote', mitigation: '' });

    const severityOptions = Object.keys(SEVERITY_LEVELS);
    const probOptions = ['Remote', 'Low', 'Moderate', 'High'];

    const handleAdd = () => {
      if (!newRow.hazard.trim()) return;
      setProjectData(prev => ({
        ...prev,
        safetyHazards: [...(prev.safetyHazards || []), { ...newRow, id: Date.now() }]
      }));
      setNewRow({ hazard: '', severity: 'Minor', probability: 'Remote', mitigation: '' });
      setShowAddRow(false);
    };

    const handleDelete = (id) => {
      setProjectData(prev => ({ ...prev, safetyHazards: (prev.safetyHazards || []).filter(h => h.id !== id) }));
    };

    const handleEditSave = (id) => {
      setProjectData(prev => ({
        ...prev,
        safetyHazards: (prev.safetyHazards || []).map(h => h.id === id ? { ...h, ...editRow } : h)
      }));
      setEditingId(null);
    };

    const inputStyle = { background: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '6px 8px', fontSize: '12px', width: '100%' };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Total Hazards</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{hazards.length}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Identified hazards</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Critical Hazards</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{hazards.filter(h => h.severity === 'Catastrophic').length}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Severity: Catastrophic</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Mitigations</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{hazards.filter(h => h.mitigation).length}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Active mitigations</div>
          </div>
        </div>

        <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ background: COLORS.NAVY, padding: '16px 24px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ color: '#FFFFFF', fontWeight: 'bold', margin: 0, fontSize: '14px' }}>Functional Hazard Assessment (FHA)</h3>
            <button onClick={() => setShowAddRow(true)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>+ Add Hazard</button>
          </div>

          {hazards.length === 0 && !showAddRow ? (
            <div style={{ padding: '48px', textAlign: 'center', color: COLORS.TEXT_DIM }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No hazards identified</div>
              <div style={{ fontSize: '13px' }}>Click "Add Hazard" to begin your Functional Hazard Assessment.</div>
            </div>
          ) : (
          <div>
            {showAddRow && (
              <div style={{ padding: '16px 24px', background: COLORS.TEAL_LIGHT, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr auto', gap: '8px', alignItems: 'end' }}>
                  <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Hazard</div><input value={newRow.hazard} onChange={e => setNewRow({ ...newRow, hazard: e.target.value })} placeholder="Hazard description" style={inputStyle} /></div>
                  <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Severity</div><select value={newRow.severity} onChange={e => setNewRow({ ...newRow, severity: e.target.value })} style={inputStyle}>{severityOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                  <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Probability</div><select value={newRow.probability} onChange={e => setNewRow({ ...newRow, probability: e.target.value })} style={inputStyle}>{probOptions.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
                  <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Mitigation</div><input value={newRow.mitigation} onChange={e => setNewRow({ ...newRow, mitigation: e.target.value })} placeholder="Recommended mitigation" style={inputStyle} /></div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={handleAdd} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '8px 12px', cursor: 'pointer', fontSize: '11px' }}>Save</button>
                    <button onClick={() => setShowAddRow(false)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '8px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
            {hazards.map((hazard, idx) => {
              const severity_color = SEVERITY_LEVELS[hazard.severity]?.color || '#999';
              if (editingId === hazard.id) {
                return (
                  <div key={hazard.id} style={{ padding: '16px 24px', background: COLORS.TEAL_LIGHT, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr auto', gap: '8px', alignItems: 'end' }}>
                      <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Hazard</div><input value={editRow.hazard} onChange={e => setEditRow({ ...editRow, hazard: e.target.value })} style={inputStyle} /></div>
                      <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Severity</div><select value={editRow.severity} onChange={e => setEditRow({ ...editRow, severity: e.target.value })} style={inputStyle}>{severityOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                      <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Probability</div><select value={editRow.probability} onChange={e => setEditRow({ ...editRow, probability: e.target.value })} style={inputStyle}>{probOptions.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
                      <div><div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, marginBottom: '4px' }}>Mitigation</div><input value={editRow.mitigation} onChange={e => setEditRow({ ...editRow, mitigation: e.target.value })} style={inputStyle} /></div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => handleEditSave(hazard.id)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '8px 12px', cursor: 'pointer', fontSize: '11px' }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '8px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={hazard.id} style={{ padding: '24px', borderBottom: idx < hazards.length - 1 ? `1px solid ${COLORS.BORDER}` : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ color: COLORS.TEXT, fontWeight: 'bold', fontSize: '15px', margin: '0 0 8px 0' }}>{hazard.hazard}</h4>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', color: COLORS.TEXT_DIM }}>Severity:</span>
                          <span style={{ padding: '4px 8px', backgroundColor: severity_color, color: '#FFFFFF', borderRadius: '3px', fontSize: '11px', fontWeight: 'bold' }}>
                            {hazard.severity}
                          </span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', color: COLORS.TEXT_DIM }}>Probability:</span>
                          <span style={{ padding: '4px 8px', backgroundColor: COLORS.TEAL_LIGHT, color: COLORS.NAVY, borderRadius: '3px', fontSize: '11px', fontWeight: '600' }}>
                            {hazard.probability}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => { setEditingId(hazard.id); setEditRow(hazard); }} style={{ background: 'transparent', color: COLORS.TEAL, border: 'none', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                      <button onClick={() => handleDelete(hazard.id)} style={{ background: 'transparent', color: '#d32f2f', border: 'none', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                    </div>
                  </div>

                  {hazard.mitigation && (
                  <div style={{ padding: '12px 16px', backgroundColor: COLORS.TEAL_LIGHT, borderLeft: `4px solid ${COLORS.TEAL}`, borderRadius: '3px' }}>
                    <div style={{ fontSize: '12px', color: COLORS.NAVY }}>
                      <span style={{ fontWeight: '600' }}>Recommended Mitigation:</span>
                      <p style={{ margin: '8px 0 0 0' }}>{hazard.mitigation}</p>
                    </div>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>

        {hazards.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>Severity Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(
                    hazards.reduce((acc, h) => {
                      acc[h.severity] = (acc[h.severity] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([name, value]) => ({ name, value }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                >
                  {Object.entries(
                    hazards.reduce((acc, h) => {
                      acc[h.severity] = (acc[h.severity] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([name], idx) => (
                    <Cell key={idx} fill={SEVERITY_LEVELS[name]?.color || '#999'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>Probability Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(
                    hazards.reduce((acc, h) => {
                      acc[h.probability] = (acc[h.probability] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([name, value]) => ({ name, value }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                >
                  {['Remote', 'Low', 'Moderate', 'High'].map((_, idx) => (
                    <Cell key={idx} fill={['#388e3c', '#fbc02d', '#f57c00', '#d32f2f'][idx]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        )}
      </div>
    );
  };

  const ReliabilityAllocationView = ({ projectData, setProjectData }) => {
    const subsystems = projectData?.allocationSubsystems || [];
    const [showAddRow, setShowAddRow] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editRow, setEditRow] = useState({});
    const [newRow, setNewRow] = useState({ name: '', allocated_mtbf: '', predicted_mtbf: '', weight: '' });

    const handleAdd = () => {
      if (!newRow.name.trim()) return;
      setProjectData(prev => ({
        ...prev,
        allocationSubsystems: [...(prev.allocationSubsystems || []), {
          id: Date.now(), name: newRow.name,
          allocated_mtbf: Number(newRow.allocated_mtbf) || 0,
          predicted_mtbf: Number(newRow.predicted_mtbf) || 0,
          weight: Number(newRow.weight) || 0
        }]
      }));
      setNewRow({ name: '', allocated_mtbf: '', predicted_mtbf: '', weight: '' });
      setShowAddRow(false);
    };

    const handleDelete = (id) => {
      setProjectData(prev => ({ ...prev, allocationSubsystems: (prev.allocationSubsystems || []).filter(s => s.id !== id) }));
    };

    const handleEditSave = (id) => {
      setProjectData(prev => ({
        ...prev,
        allocationSubsystems: (prev.allocationSubsystems || []).map(s => s.id === id ? {
          ...s, name: editRow.name,
          allocated_mtbf: Number(editRow.allocated_mtbf) || 0,
          predicted_mtbf: Number(editRow.predicted_mtbf) || 0,
          weight: Number(editRow.weight) || 0
        } : s)
      }));
      setEditingId(null);
    };

    const inputStyle = { background: COLORS.PANEL_BG, color: COLORS.TEXT, border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', padding: '6px 8px', fontSize: '12px', width: '100%' };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Subsystems</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{subsystems.length}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Allocated subsystems</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Avg Allocated MTBF</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{subsystems.length > 0 ? (subsystems.reduce((s, sub) => s + sub.allocated_mtbf, 0) / subsystems.length / 1000).toFixed(1) + 'k' : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Average hours</div>
          </div>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', marginBottom: '8px' }}>Avg Predicted MTBF</div>
            <div style={{ color: COLORS.TEAL, fontSize: '24px', fontWeight: 'bold' }}>{subsystems.length > 0 ? (subsystems.reduce((s, sub) => s + sub.predicted_mtbf, 0) / subsystems.length / 1000).toFixed(1) + 'k' : '—'}</div>
            <div style={{ color: COLORS.TEXT_DIM, fontSize: '11px' }}>Average hours</div>
          </div>
        </div>

        <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ background: COLORS.NAVY, padding: '16px 24px', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ color: '#FFFFFF', fontWeight: 'bold', margin: 0, fontSize: '14px' }}>Reliability Allocation Summary</h3>
            <button onClick={() => setShowAddRow(true)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>+ Add Subsystem</button>
          </div>

          {subsystems.length === 0 && !showAddRow ? (
            <div style={{ padding: '48px', textAlign: 'center', color: COLORS.TEXT_DIM }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No subsystems defined</div>
              <div style={{ fontSize: '13px' }}>Click "Add Subsystem" to begin allocating reliability targets across your system architecture.</div>
            </div>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead style={{ background: COLORS.LIGHT_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: COLORS.TEXT }}>Subsystem</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Weight</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Allocated MTBF</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Predicted MTBF</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: COLORS.TEXT }}>Margin %</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: COLORS.TEXT }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {showAddRow && (
                  <tr style={{ background: COLORS.TEAL_LIGHT, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                    <td style={{ padding: '8px' }}><input value={newRow.name} onChange={e => setNewRow({ ...newRow, name: e.target.value })} placeholder="Subsystem name" style={inputStyle} /></td>
                    <td style={{ padding: '8px' }}><input type="number" step="0.01" value={newRow.weight} onChange={e => setNewRow({ ...newRow, weight: e.target.value })} placeholder="0.25" style={{ ...inputStyle, width: '70px', textAlign: 'right' }} /></td>
                    <td style={{ padding: '8px' }}><input type="number" value={newRow.allocated_mtbf} onChange={e => setNewRow({ ...newRow, allocated_mtbf: e.target.value })} placeholder="50000" style={{ ...inputStyle, width: '90px', textAlign: 'right' }} /></td>
                    <td style={{ padding: '8px' }}><input type="number" value={newRow.predicted_mtbf} onChange={e => setNewRow({ ...newRow, predicted_mtbf: e.target.value })} placeholder="48000" style={{ ...inputStyle, width: '90px', textAlign: 'right' }} /></td>
                    <td colSpan="3" style={{ padding: '8px', textAlign: 'center' }}>
                      <button onClick={handleAdd} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                      <button onClick={() => setShowAddRow(false)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                    </td>
                  </tr>
                )}
                {subsystems.map((sub, idx) => {
                  const margin = sub.allocated_mtbf > 0 ? ((sub.predicted_mtbf - sub.allocated_mtbf) / sub.allocated_mtbf) * 100 : 0;
                  const status = margin > 0 ? 'green' : margin > -10 ? 'yellow' : 'red';
                  const status_text = margin > 0 ? 'PASS' : margin > -10 ? 'MARGINAL' : 'FAIL';
                  const status_colors = {
                    green: { bg: '#D1FAE5', color: '#065F46' },
                    yellow: { bg: '#FEF3C7', color: '#92400E' },
                    red: { bg: '#FEE2E2', color: '#991B1B' }
                  };

                  if (editingId === sub.id) {
                    return (
                      <tr key={sub.id} style={{ background: COLORS.TEAL_LIGHT, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                        <td style={{ padding: '8px' }}><input value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} style={inputStyle} /></td>
                        <td style={{ padding: '8px' }}><input type="number" step="0.01" value={editRow.weight} onChange={e => setEditRow({ ...editRow, weight: e.target.value })} style={{ ...inputStyle, width: '70px', textAlign: 'right' }} /></td>
                        <td style={{ padding: '8px' }}><input type="number" value={editRow.allocated_mtbf} onChange={e => setEditRow({ ...editRow, allocated_mtbf: e.target.value })} style={{ ...inputStyle, width: '90px', textAlign: 'right' }} /></td>
                        <td style={{ padding: '8px' }}><input type="number" value={editRow.predicted_mtbf} onChange={e => setEditRow({ ...editRow, predicted_mtbf: e.target.value })} style={{ ...inputStyle, width: '90px', textAlign: 'right' }} /></td>
                        <td></td><td></td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button onClick={() => handleEditSave(sub.id)} style={{ background: COLORS.TEAL, color: '#FFF', border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ background: COLORS.BORDER, color: COLORS.TEXT, border: 'none', borderRadius: '3px', padding: '6px 12px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={sub.id} style={{ background: idx % 2 === 0 ? COLORS.LIGHT_BG : COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                      <td style={{ padding: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{sub.name}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT_DIM }}>{(sub.weight * 100).toFixed(1)}%</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT, fontFamily: 'monospace' }}>{sub.allocated_mtbf.toLocaleString()}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: COLORS.TEXT, fontFamily: 'monospace' }}>{sub.predicted_mtbf.toLocaleString()}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: COLORS.NAVY, fontWeight: 'bold' }}>{margin.toFixed(1)}%</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '3px', fontWeight: 'bold', fontSize: '11px', backgroundColor: status_colors[status].bg, color: status_colors[status].color }}>
                          {status_text}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button onClick={() => { setEditingId(sub.id); setEditRow(sub); }} style={{ background: 'transparent', color: COLORS.TEAL, border: 'none', cursor: 'pointer', fontSize: '12px', marginRight: '8px' }}>Edit</button>
                        <button onClick={() => handleDelete(sub.id)} style={{ background: 'transparent', color: '#d32f2f', border: 'none', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {subsystems.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>Allocated vs Predicted MTBF</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={subsystems}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip formatter={v => v.toLocaleString()} />
                <Legend />
                <Bar dataKey="allocated_mtbf" fill={COLORS.GOLD} name="Allocated" />
                <Bar dataKey="predicted_mtbf" fill={COLORS.NAVY} name="Predicted" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: COLORS.PANEL_BG, border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', padding: '24px' }}>
            <h3 style={{ color: COLORS.TEXT, fontWeight: 'bold', margin: '0 0 16px 0' }}>Allocation Margin</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={subsystems.map(sub => ({
                  name: sub.name,
                  margin: sub.allocated_mtbf > 0 ? ((sub.predicted_mtbf - sub.allocated_mtbf) / sub.allocated_mtbf) * 100 : 0
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip formatter={v => v.toFixed(1) + '%'} />
                <Bar dataKey="margin" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        )}
      </div>
    );
  };

  // Tab rendering
  const currentTabContent = () => {
    switch (currentTab) {
      case 'grid': {
        // Smart suggestions — auto-populate when equipment selected and section has few entries
        const sectionData = scopedGridData;
        const selectedEquip = selectedItem && (selectedSection !== 'locations' && selectedSection !== 'equipments')
          ? (projectData.equipments || []).find(e => e.id === selectedItem?.equipmentId) || selectedItem
          : selectedItem;
        const equipType = selectedEquip?.type || selectedEquip?.description || '';
        const suggestions = (selectedSection !== 'locations' && selectedSection !== 'equipments' && equipType)
          ? getSuggestions(equipType, selectedSection, selectedEquip?.id || '', sectionData)
          : [];

        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Suggestion banner — appears when suggestions are available */}
            {suggestions.length > 0 && (
              <div style={{ padding: '10px 16px', backgroundColor: 'rgba(0,47,101,0.02)', borderBottom: '1px solid rgba(0,47,101,0.06)', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: COLORS.NAVY, fontFamily: "'Georgia', serif" }}>
                      {suggestions.length} suggested entries for {equipType}
                    </span>
                    <span style={{ fontSize: '9px', color: 'rgba(0,47,101,0.4)', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      ISO 14224 / Library
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => {
                      // Accept all suggestions
                      const newItems = suggestions.map(s => {
                        const item = { ...s };
                        delete item._isSuggestion;
                        delete item._suggestionRank;
                        item.source = item.source || 'Auto-suggested';
                        return item;
                      });
                      setProjectData(prev => ({
                        ...prev,
                        [selectedSection]: [...(prev[selectedSection] || []), ...newItems]
                      }));
                    }} style={{
                      padding: '4px 14px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer',
                      borderRadius: '3px', backgroundColor: COLORS.NAVY, color: '#fff',
                      letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'Georgia', serif"
                    }}>Accept All</button>
                    <button onClick={() => {
                      // Dismiss — do nothing, suggestions will recalculate next time
                    }} style={{
                      padding: '4px 14px', fontSize: '10px', fontWeight: 600, border: `1px solid rgba(0,47,101,0.12)`, cursor: 'pointer',
                      borderRadius: '3px', backgroundColor: 'transparent', color: 'rgba(0,47,101,0.4)',
                      letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'Georgia', serif"
                    }}>Dismiss</button>
                  </div>
                </div>
                {/* Suggestion rows preview */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '160px', overflow: 'auto' }}>
                  {suggestions.map((sug, idx) => (
                    <div key={sug.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px',
                      backgroundColor: '#fff', border: '1px solid rgba(0,47,101,0.06)', borderRadius: '3px',
                      borderLeft: '3px solid rgba(0,168,203,0.4)'
                    }}>
                      <span style={{ fontSize: '9px', fontWeight: 700, color: 'rgba(0,47,101,0.25)', width: '20px', textAlign: 'center', fontFamily: 'system-ui' }}>{idx + 1}</span>
                      <span style={{ fontSize: '11px', color: COLORS.TEXT, flex: 1, fontFamily: "'Georgia', serif" }}>{sug.description}</span>
                      {sug.mechanism && <span style={{ fontSize: '9px', color: 'rgba(0,47,101,0.4)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sug.mechanism}</span>}
                      {sug.severity && <span style={{ fontSize: '9px', fontWeight: 600, color: sug.severity >= 8 ? '#B91C1C' : sug.severity >= 5 ? '#92400E' : 'rgba(0,47,101,0.4)', padding: '1px 6px', borderRadius: '2px', backgroundColor: sug.severity >= 8 ? 'rgba(185,28,28,0.06)' : 'rgba(0,47,101,0.03)' }}>S{sug.severity}</span>}
                      <button onClick={() => {
                        const item = { ...sug };
                        delete item._isSuggestion;
                        delete item._suggestionRank;
                        item.source = item.source || 'Auto-suggested';
                        setProjectData(prev => ({
                          ...prev,
                          [selectedSection]: [...(prev[selectedSection] || []), item]
                        }));
                      }} style={{
                        padding: '2px 8px', fontSize: '9px', fontWeight: 600, border: `1px solid ${COLORS.TEAL}30`,
                        borderRadius: '2px', cursor: 'pointer', backgroundColor: 'transparent', color: COLORS.TEAL,
                        letterSpacing: '0.04em', textTransform: 'uppercase'
                      }}>Add</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <DataGrid
                data={sectionData}
                columns={COLUMN_DEFINITIONS[selectedSection] || []}
                selectedRows={selectedRows}
                onSelectRows={setSelectedRows}
                onEditCell={handleUpdateItem}
                onAddRow={() => handleAddItem(selectedSection)}
                onDeleteRow={() => selectedRows.forEach(id => handleDeleteItem(id))}
                onRowClick={(row) => { setSelectedItem(row); setSelectedSection(selectedSection); }}
              />
            </div>
          </div>
        );
      }
      case 'spares':
        return <SparesAnalysisView spares={scopedProjectData.spares || []} equipments={scopedProjectData.equipments || []} simConfig={simConfig} projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'spareswizard':
        return <SparesOptimisationWizard projectData={scopedProjectData} setProjectData={setProjectData} simConfig={simConfig} onClose={() => setCurrentTab('spares')} />;
      case 'integrity':
        return <IntegritySyncPanel projectData={projectData} setProjectData={setProjectData} scopedProjectData={scopedProjectData} />;
      case 'enterprise':
        return <EnterpriseView projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'import':
        return <DataImportHub projectData={projectData} setProjectData={setProjectData} onClose={() => setCurrentTab('grid')} />;
      case 'library':
        return <LibraryView projectData={projectData} setProjectData={setProjectData} />;
      case 'sfg20':
        return <SFG20SchedulerView projectData={projectData} setProjectData={setProjectData} />;
      case 'trend':
        return <OptimalTRENDView projectData={projectData} setProjectData={setProjectData} />;
      case 'optimization':
        return <IntervalOptimizationView projectData={scopedProjectData} simConfig={simConfig} />;
      case 'workinstructions':
        return <WorkInstructionsView projectData={scopedProjectData} />;
      case 'results':
        return <ResultsPanel simResults={simResults} simConfig={simConfig} cutSets={ftCutSets} weibullData={null} />;
      case 'weibull': {
        const wSets = scopedProjectData.weibullsets || [];
        const firstSet = wSets[0];
        const wData = firstSet ? { beta: firstSet.beta, eta: firstSet.eta, r2: firstSet.rSquared || firstSet.r2, name: firstSet.description } : null;
        return <WeibullPlotView weibullData={wData} selectedSet={firstSet} weibullSets={wSets} projectData={scopedProjectData} setProjectData={setProjectData} />;
      }
      case 'rcm2':
        return <UnifiedRCM2Analysis projectData={projectData} setProjectData={setProjectData} scopedProjectData={scopedProjectData} />;
      case 'optimalrcm':
        return <OptimalRCMView rcmCauses={scopedProjectData.rcmcauses || []} />;
      case 'modelling':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', padding: '0', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
              {[
                { id: 'rbd', label: 'Reliability Block Diagram' },
                { id: 'ft', label: 'Fault Tree' },
                { id: 'eventtree', label: 'Event Tree' },
                { id: 'ramstudy', label: 'RAM Study' }
              ].map(st => (
                <button key={st.id} onClick={() => setModellingSubTab(st.id)} style={{
                  padding: '8px 16px', fontSize: '11px', border: 'none', cursor: 'pointer',
                  backgroundColor: modellingSubTab === st.id ? COLORS.TEAL_LIGHT : 'transparent',
                  color: modellingSubTab === st.id ? COLORS.TEAL : COLORS.TEXT,
                  borderBottom: modellingSubTab === st.id ? `2px solid ${COLORS.TEAL}` : '2px solid transparent',
                  fontWeight: modellingSubTab === st.id ? '600' : '400', whiteSpace: 'nowrap',
                  fontFamily: "'Georgia', serif", letterSpacing: '0.02em', textTransform: 'uppercase'
                }}>{st.label}</button>
              ))}
            </div>
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {modellingSubTab === 'rbd' && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0 }}>
                  <RBDEditor
                    blocks={scopedProjectData.rbdblocks || []}
                    connections={rbdConnections}
                    onUpdateBlock={handleUpdateBlock}
                    onAddBlock={handleAddBlock}
                    onDeleteBlock={handleDeleteBlock}
                    onAddConnection={handleAddConnection}
                    onDeleteConnection={handleDeleteConnection}
                    selectedBlockId={selectedBlockId}
                    onSelectBlock={setSelectedBlockId}
                    selectedConnectionId={selectedConnectionId}
                    onSelectConnection={setSelectedConnectionId}
                    zoom={zoom}
                    onZoomChange={setZoom}
                    showGrid={showGrid}
                    onToggleGrid={() => setShowGrid(!showGrid)}
                    currentPage={currentPage}
                  />
                  {/* Floating edit button for sub-diagram / multi-state blocks */}
                  {selectedBlockId && (() => {
                    const selBlock = (scopedProjectData.rbdblocks || []).find(b => b.id === selectedBlockId);
                    if (!selBlock) return null;
                    if (selBlock.blockType === 'subdiagram') return (
                      <button onClick={() => setEditingSubDiagram(selBlock)} style={{ position: 'absolute', bottom: '16px', right: '16px', padding: '8px 16px', backgroundColor: '#00A8CB', color: '#FFF', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10 }}>
                        Edit Sub-Diagram ↳
                      </button>
                    );
                    if (selBlock.blockType === 'multistate') return (
                      <button onClick={() => setEditingMultiState(selBlock)} style={{ position: 'absolute', bottom: '16px', right: '16px', padding: '8px 16px', backgroundColor: '#7B61FF', color: '#FFF', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10 }}>
                        Configure States ⚙
                      </button>
                    );
                    return null;
                  })()}
                  {editingSubDiagram && <SubDiagramEditor block={editingSubDiagram} onUpdateBlock={handleUpdateBlock} onClose={() => setEditingSubDiagram(null)} />}
                  {editingMultiState && <MultiStateEditor block={editingMultiState} onUpdateBlock={handleUpdateBlock} onClose={() => setEditingMultiState(null)} />}
                </div>
              )}
              {modellingSubTab === 'ft' && (
                <FaultTreeEditor
                  gates={projectData.gates || []}
                  primaryEvents={projectData.primaryevents || []}
                  selectedGateId={selectedGateId}
                  onSelectGate={setSelectedGateId}
                  onUpdateGate={(id, updates) => {
                    const updatedData = { ...projectData };
                    const gate = updatedData.gates.find(g => g.id === id);
                    if (gate) { Object.assign(gate, updates); setProjectData(updatedData); }
                  }}
                  onAddGate={(gate) => {
                    const updatedData = { ...projectData };
                    if (!updatedData.gates) updatedData.gates = [];
                    updatedData.gates.push(gate);
                    setProjectData(updatedData);
                  }}
                  onDeleteGate={(id) => {
                    const updatedData = { ...projectData };
                    updatedData.gates = updatedData.gates.filter(g => g.id !== id);
                    setProjectData(updatedData);
                  }}
                  zoom={zoom}
                  onZoomChange={setZoom}
                  cutSets={ftCutSets}
                  importanceMeasures={ftImportance}
                  projectData={projectData}
                  setProjectData={setProjectData}
                />
              )}
              {modellingSubTab === 'ramstudy' && (
                <RAMStudyView projectData={projectData} setProjectData={setProjectData} />
              )}
              {modellingSubTab === 'eventtree' && (
                <EventTreeEditor eventTrees={scopedProjectData.eventtrees || []} projectData={scopedProjectData} setProjectData={setProjectData} />
              )}
            </div>
          </div>
        );
      case 'connectors':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', padding: '0', backgroundColor: COLORS.PANEL_BG, borderBottom: `1px solid ${COLORS.BORDER}` }}>
              {[
                { id: 'sap', label: 'SAP Connector' },
                { id: 'maximo', label: 'IBM Maximo' }
              ].map(st => (
                <button key={st.id} onClick={() => setConnectorsSubTab(st.id)} style={{
                  padding: '8px 16px', fontSize: '11px', border: 'none', cursor: 'pointer',
                  backgroundColor: connectorsSubTab === st.id ? COLORS.TEAL_LIGHT : 'transparent',
                  color: connectorsSubTab === st.id ? COLORS.TEAL : COLORS.TEXT,
                  borderBottom: connectorsSubTab === st.id ? `2px solid ${COLORS.TEAL}` : '2px solid transparent',
                  fontWeight: connectorsSubTab === st.id ? '600' : '400', whiteSpace: 'nowrap',
                  fontFamily: "'Georgia', serif", letterSpacing: '0.02em', textTransform: 'uppercase'
                }}>{st.label}</button>
              ))}
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {connectorsSubTab === 'sap' && <SAPConnectorView projectData={projectData} onImportData={(entity, records) => {
                const connector = new SAPConnector();
                const updatedData = connector.mergeData(projectData, records, entity, 'merge');
                setProjectData(updatedData);
              }} />}
              {connectorsSubTab === 'maximo' && <MaximoConnectorView projectData={projectData} onImportData={(entity, records) => {
                const connector = new MaximoConnector();
                const updatedData = connector.mergeData(projectData, records, entity, 'merge');
                setProjectData(updatedData);
              }} />}
            </div>
          </div>
        );
      case 'criticality': {
        // Bridge: enrich RCM causes with failure rate data from failure models
        const fmMap = {};
        (scopedProjectData.failuremodels || []).forEach(fm => { fmMap[fm.id] = fm; });
        const enrichedCauses = (scopedProjectData.rcmcauses || []).map(cause => {
          const fm = fmMap[cause.failureModel] || fmMap[cause.failureModeId] || {};
          return {
            ...cause,
            lambda: cause.lambda || fm.lambda || (fm.mttf ? 1 / fm.mttf : null) || (fm.eta ? 1 / fm.eta : null),
            mttf: cause.mttf || fm.mttf || fm.eta,
            beta: cause.beta || fm.beta,
            eta: cause.eta || fm.eta,
            distribution: cause.distribution || fm.distribution
          };
        });
        return <CriticalityMatrixView failureModes={enrichedCauses} consequences={scopedProjectData.consequences || []} />;
      }
      case 'lcc':
        return <LifecycleAssessmentView costNodes={scopedProjectData.costnodes || []} simConfig={simConfig} projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'process':
        return <ProcessReliabilityView data={scopedProjectData} results={processResults} onRunAnalysis={handleRunProcessAnalysis} />;
      case 'alt':
        return <ALTView data={scopedProjectData} results={altResults} onRunAnalysis={handleRunALT} />;
      case 'markov':
        return <MarkovAnalysisView projectData={projectData} setProjectData={setProjectData} />;
      case 'strategy':
        return <MaintenanceStrategyBuilder projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'sparecriticality':
        return <SpareCriticalityView projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'heatmap':
        return <CriticalityHeatmapView projectData={scopedProjectData} />;
      // case 'lccsensitivity': // Superseded by Lifecycle Assessment internal tabs
      //   return <LCCSensitivityView projectData={scopedProjectData} simConfig={simConfig} />;
      case 'audit':
        return <AuditTrailView auditLog={auditLog} currentUser={currentUser} />;
      case 'prediction':
        return <ReliabilityPredictionView projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'fmecaworksheet':
        return <FMECAWorksheetView projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'growth':
        return <ReliabilityGrowthView projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'safety':
        return <SafetyAssessmentView projectData={scopedProjectData} setProjectData={setProjectData} />;
      case 'allocation':
        return <ReliabilityAllocationView projectData={scopedProjectData} setProjectData={setProjectData} />;
      default:
        return null;
    }
  };

  // Library drawer state (context-sensitive — appears when user needs it, not as a tab)
  const [libraryDrawerOpen, setLibraryDrawerOpen] = useState(false);

  // ============ HIERARCHY ROLL-UP HELPERS ============
  // Get all descendant asset IDs for a selected item (location or equipment)
  const getDescendantIds = useCallback((itemId, sectionId) => {
    const ids = new Set([itemId]);
    if (sectionId === 'locations') {
      // Find all child locations recursively
      const findChildLocations = (parentId) => {
        (projectData.locations || []).forEach(loc => {
          if (loc.parentId === parentId && !ids.has(loc.id)) {
            ids.add(loc.id);
            findChildLocations(loc.id);
          }
        });
      };
      findChildLocations(itemId);
      // Find all equipment under these locations
      (projectData.equipments || []).forEach(eq => {
        if (ids.has(eq.locationId) || ids.has(eq.parentId)) ids.add(eq.id);
      });
    } else if (sectionId === 'equipments') {
      // Find all child equipment recursively
      const findChildEquipment = (parentId) => {
        (projectData.equipments || []).forEach(eq => {
          if (eq.parentId === parentId && !ids.has(eq.id)) {
            ids.add(eq.id);
            findChildEquipment(eq.id);
          }
        });
      };
      findChildEquipment(itemId);
    }
    return ids;
  }, [projectData]);

  // Get contextual item IDs based on selected asset (for data filtering in tabs)
  const contextualAssetIds = useMemo(() => {
    if (!selectedItem) return null; // null = show all
    return getDescendantIds(selectedItem.id, selectedSection);
  }, [selectedItem, selectedSection, getDescendantIds]);

  // ============ WORKFLOW STEP HELPERS ============
  // Check if a workflow step has data / is considered "complete"
  const isStepComplete = useCallback((stepId) => {
    switch (stepId) {
      case 'grid': return (projectData.equipments?.length || 0) > 0 || (projectData.failuremodels?.length || 0) > 0;
      case 'modelling': return (projectData.rbdblocks?.length || 0) > 0 || (projectData.gates?.length || 0) > 0;
      case 'spares': return (projectData.spares?.length || 0) > 0;
      case 'connectors': return true; // always accessible
      case 'markov': return (projectData.rbdblocks?.length || 0) > 0;
      case 'results': return simStatus === 'completed' && simResults != null;
      case 'optimization': return simStatus === 'completed' && simResults != null;
      case 'weibull': return (projectData.weibullsets?.length || 0) > 0;
      case 'alt': return (projectData.altmodels?.length || 0) > 0;
      case 'rcm2': return (projectData.rcmcauses?.length || 0) > 0 || (projectData.failuremodels?.length || 0) > 0;
      case 'heatmap': return (projectData.failuremodels?.length || 0) > 0;
      case 'optimalrcm': return (projectData.rcmcauses?.length || 0) > 0;
      case 'lcc': return true;
      case 'process': return (projectData.equipments?.length || 0) > 0;
      case 'prediction': case 'fmecaworksheet': case 'growth': case 'safety': case 'allocation':
        return true; // Reliability engineering steps are always accessible
      case 'enterprise': return true;
      default: return false;
    }
  }, [projectData, simStatus, simResults]);

  // Check if all prerequisites for a step are met (for sequential gating)
  const isStepUnlocked = useCallback((moduleId, stepIndex) => {
    const workflow = MODULE_WORKFLOWS[moduleId];
    if (!workflow) return true;
    const step = workflow.steps[stepIndex];
    if (!step) return false;
    // A step is unlocked if all its prerequisite steps have data
    return step.requires.every(reqIdx => {
      const reqStep = workflow.steps[reqIdx];
      return reqStep && isStepComplete(reqStep.id);
    });
  }, [isStepComplete]);

  // Check if a workflow step should be visible given the selected asset type
  const isStepVisibleForAsset = useCallback((step) => {
    if (step.assetTypes === 'all') return true;
    if (!selectedItem || !selectedSection) return true;
    return step.assetTypes.includes(selectedSection);
  }, [selectedItem, selectedSection]);

  // Get active workflow for current module
  const activeWorkflow = useMemo(() => {
    return MODULE_WORKFLOWS[currentModule] || null;
  }, [currentModule]);

  // Get domain groups relevant to current workflow step
  const activeDomainGroups = useMemo(() => {
    if (!activeWorkflow) return TREE_GROUPS; // Hub: show all domain groups
    const stepIdx = workflowStepIndex;
    const step = activeWorkflow.steps[stepIdx];
    if (!step) return TREE_GROUPS;
    return TREE_GROUPS.filter(g => step.domainGroups.includes(g.id));
  }, [activeWorkflow, workflowStepIndex]);

  // Backward-compatible visibleTabs — now driven by MODULE_WORKFLOWS
  const visibleTabs = () => {
    const workflow = MODULE_WORKFLOWS[currentModule];
    if (workflow) return workflow.steps.filter((s, i) => isStepVisibleForAsset(s)).map(s => s.id);
    // Fallback for modules not yet in workflow system
    if (currentModule === 'hub' || currentModule === 'outcomes') return [];
    if (currentModule === 'dashboard') return ['grid', 'enterprise'];
    return ['grid'];
  };

  // ============ HUB CARD COMPONENT ============
  const HubCard = ({ tone, icon, tag, badge, name, desc, kpis = [], cta, onClick, dataTour }) => {
    const isHero = tone === 'hero';
    const isAdmin = tone === 'admin';
    return (
      <div data-tour={dataTour} onClick={onClick} style={{
        gridColumn: isHero ? 'span 8' : 'span 4',
        background: isHero ? 'linear-gradient(135deg,#FFFFFF 0%,#FAFBFD 100%)' : isAdmin ? 'linear-gradient(180deg,#FBFBFD 0%,#FFFFFF 100%)' : '#FFFFFF',
        border: '1px solid rgba(0,47,101,0.08)', borderRadius: '4px', padding: isHero ? '32px' : '24px',
        position: 'relative', transition: 'all 0.2s ease', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: '18px', minHeight: isHero ? '260px' : '220px',
        boxShadow: '0 1px 2px rgba(0,47,101,0.05),0 8px 24px rgba(0,47,101,0.05)'
      }}>
        <div style={{ position:'absolute', left:0, top:0, right:0, height: isHero?'3px':'2px',
          background: isHero?'linear-gradient(90deg,#00A8CB,#002F65)' : isAdmin?'rgba(0,47,101,0.24)':'#00A8CB',
          borderRadius:'4px 4px 0 0', opacity:0.7 }} />
        <div style={{ display:'flex', alignItems:'flex-start', gap:'16px' }}>
          <div style={{ width: isHero?52:44, height: isHero?52:44, borderRadius:'3px',
            background: isHero?'#002F65' : isAdmin?'rgba(0,47,101,0.06)':'rgba(0,168,203,0.08)',
            color: isHero?'#fff' : isAdmin?'#002F65':'#00A8CB',
            fontSize: isHero?24:20, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
          }}>{icon}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'9px', fontWeight:600, color:'rgba(0,47,101,0.4)', letterSpacing:'0.16em', textTransform:'uppercase', marginBottom:'4px' }}>
              {tag}
              {badge && <span style={{ display:'inline-block', padding:'3px 8px', borderRadius:'2px',
                background:'rgba(0,168,203,0.1)', color:'#00A8CB', fontSize:'9px', fontWeight:700,
                letterSpacing:'0.14em', textTransform:'uppercase', marginLeft:'8px', verticalAlign:'middle'
              }}>{badge}</span>}
            </div>
            <div style={{ fontFamily:"'Georgia',serif", fontSize: isHero?24:18, fontWeight:400,
              color:'#002F65', letterSpacing:'-0.01em', lineHeight:1.25 }}>{name}</div>
          </div>
        </div>
        {desc && <div style={{ fontSize: isHero?14:13, color:'rgba(0,47,101,0.8)', lineHeight:1.5, maxWidth: isHero?640:'none' }}>{desc}</div>}
        {kpis.length > 0 && (
          <div style={{ display:'grid', gridTemplateColumns:`repeat(${isHero?4:3},1fr)`, gap:'16px',
            paddingTop:'16px', marginTop:'auto', borderTop:'1px solid rgba(0,47,101,0.08)' }}>
            {kpis.map((k,i) => (
              <div key={i}>
                <div style={{ fontSize:'9px', fontWeight:600, color:'rgba(0,47,101,0.4)', letterSpacing:'0.12em', textTransform:'uppercase', lineHeight:1.3, minHeight:'22px' }}>{k.label}</div>
                <div style={{ fontFamily:"'Georgia',serif", fontSize: isHero?22:20, fontWeight:400, color:'#002F65', marginTop:'4px', letterSpacing:'-0.02em', lineHeight:1.1 }}>{k.value}</div>
                {k.delta && <div style={{ fontSize:'10px', fontWeight:600, marginTop:'3px', color: k.tone==='up'?'#2A9D8F':k.tone==='down'?'#D62828':'rgba(0,47,101,0.4)' }}>{k.delta}</div>}
              </div>
            ))}
          </div>
        )}
        {cta && <div style={{ position:'absolute', right:'18px', bottom:'18px', fontSize:'10px', color:'rgba(0,168,203,0.5)', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase' }}>{cta} &#8594;</div>}
      </div>
    );
  };

  // ============ MODULE HUB VIEW ============
  const renderModuleHub = () => {
    const eqCount = projectData.equipments?.length || 0;
    const fmCount = projectData.failuremodels?.length || 0;
    const spCount = projectData.spares?.length || 0;
    const taskCount = (projectData.scheduledtasks?.length || 0) + (projectData.correctivetasks?.length || 0);
    const rcmCount = projectData.rcmcauses?.length || 0;
    const rcmFunctionCount = projectData.rcmFunctions?.length || 0;
    const rcmFFCount = projectData.rcmFunctionalFailures?.length || 0;
    const weibullCount = projectData.weibullsets?.length || 0;
    const costNodeCount = projectData.costnodes?.length || 0;
    const altCount = projectData.altmodels?.length || 0;
    const lccScenarioCount = projectData.lccscenarios?.length || 0;
    const siteCount = projectData.sites?.length || 1;
    const libraryCount = projectData.libraryitems?.length || 0;
    const wiCount = projectData.workinstructions?.length || 0;
    const labourCount = projectData.labour?.length || 0;
    const equipResCount = projectData.equipmentresources?.length || 0;
    const hasData = eqCount > 0 || fmCount > 0;
    const hasSim = simStatus === 'completed' && simResults;
    const hierarchyLabel = { iso14224: 'ISO 14224', rdspp: 'RDS-PP', isa95: 'ISA-95' }[hierarchyStandard] || 'ISO 14224';
    const userName = currentUser?.fullName || currentUser?.username || 'User';
    const dataStatus = hasData ? (hasSim ? 'Analysed' : 'Loaded') : 'Empty';
    const dataStatusColor = hasData ? '#2A9D8F' : 'rgba(0,47,101,0.3)';
    return (
      <div data-tour="module-hub" style={{ flex:1, overflow:'auto', padding:'32px 40px', background:COLORS.LIGHT_BG }}>
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:10, fontWeight:600, color:'rgba(0,47,101,0.4)', letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:6 }}>Optimal® Availability Studio › Module Hub</div>
          <div style={{ fontFamily:"'Georgia',serif", fontSize:22, fontWeight:400, color:COLORS.TEXT, letterSpacing:'0.2px' }}>Welcome back, {userName}</div>
          <div style={{ display:'flex', gap:20, marginTop:10, fontSize:10, fontWeight:500, color:'rgba(0,47,101,0.4)', letterSpacing:'0.03em', textTransform:'uppercase' }}>
            <span><span style={{ width:6, height:6, borderRadius:'50%', background:dataStatusColor, display:'inline-block', marginRight:6, boxShadow:`0 0 0 3px ${dataStatusColor}26` }}></span>Data <b style={{ color:COLORS.TEXT, fontSize:12, fontFamily:"'Georgia',serif", letterSpacing:'-0.01em', textTransform:'none', display:'inline' }}>{dataStatus}</b></span>
            <span>Equipment <b style={{ color:COLORS.TEXT, fontSize:12, fontFamily:"'Georgia',serif", letterSpacing:'-0.01em', textTransform:'none', display:'inline' }}>{eqCount}</b></span>
            <span>Failure modes <b style={{ color:COLORS.TEXT, fontSize:12, fontFamily:"'Georgia',serif", letterSpacing:'-0.01em', textTransform:'none', display:'inline' }}>{fmCount}</b></span>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(12,minmax(0,1fr))', gap:'20px' }}>
          {/* Hero: Outcomes Hub */}
          <HubCard tone="hero" icon="🎯" tag="Guided Wizards" badge={hasData ? 'Live' : 'Ready'} name="Outcomes Hub"
            desc="Structured engagement workflows – from RCM study through to spares optimisation, life cycle costing and defect elimination."
            kpis={[
              {label:'Workflows', value:'7'},
              {label:'Equipment loaded', value:String(eqCount)},
              {label:'Failure modes', value:String(fmCount)},
              {label:'Simulation', value:hasSim ? 'Complete' : 'Pending'}
            ]}
            cta="Open Outcomes" onClick={() => navigateToModule('outcomes')}
            dataTour="outcomes-btn"
          />
          {/* Admin */}
          <HubCard tone="admin" icon="⚙" tag="Settings" name="Administration"
            kpis={[
              {label:'Hierarchy', value:hierarchyLabel},
              {label:'Equipment', value:String(eqCount)},
              {label:'Simulation', value:'Monte Carlo'}
            ]}
            cta="Settings" onClick={() => { navigateToModule('optimalav', 'grid'); }}
          />
          {/* Module 01: Optimal Availability */}
          <HubCard icon="📊" tag="Module 01" badge={eqCount > 0 ? 'Live' : 'Ready'} name="Reliability & Availability"
            desc="RBD modelling, fault trees, event trees, Monte Carlo simulation and PM interval optimisation."
            kpis={[
              {label:'Equipment', value:String(eqCount)},
              {label:'Failure modes', value:String(fmCount)},
              {label:'Tasks', value:String(taskCount)}
            ]}
            cta="Open Availability" onClick={() => { navigateToModule('optimalav', 'modelling'); setModellingSubTab('rbd'); }}
          />
          {/* Module 02: Weibull Analysis */}
          <HubCard icon="📈" tag="Module 02" badge={weibullCount > 0 ? 'Live' : 'Ready'} name="Weibull Analysis"
            desc="Failure data analysis, Weibull parameter estimation, probability plotting and accelerated life testing."
            kpis={[
              {label:'Weibull sets', value:String(weibullCount)},
              {label:'Method', value:'MLE / RRX'},
              {label:'ALT models', value:String(altCount)}
            ]}
            cta="Open Weibull" onClick={() => { navigateToModule('weibull', 'weibull'); }}
          />
          {/* Module 03: RCM */}
          <HubCard icon="🔧" tag="Module 03" badge={rcmCount > 0 ? 'Live' : 'Ready'} name="Reliability-Centred Maintenance"
            desc="FMEA build, RCM decision logic, task packaging, criticality analysis and PM schedule generation."
            kpis={[
              {label:'RCM causes', value:String(rcmCount)},
              {label:'Decision logic', value:'SAE JA1011'},
              {label:'Work instructions', value:String(wiCount)}
            ]}
            cta="Open RCM" onClick={() => { navigateToModule('rcm', 'rcm2'); }}
          />
          {/* Module 04: Lifecycle Assessment */}
          <HubCard icon="💰" tag="Module 04" badge={(projectData.lifecycleAssessments?.length || 0) > 0 ? 'Live' : 'Ready'} name="Lifecycle Assessment"
            desc="RUL estimation, whole-life costing, Monte Carlo simulation, and optimal asset lifecycle strategy (ISO 55001 / ISO 15686-5)."
            kpis={[
              {label:'Assessments', value:String(projectData.lifecycleAssessments?.length || 0)},
              {label:'Cost nodes', value:String(costNodeCount)},
              {label:'Method', value:'NPV / EAC'}
            ]}
            cta="Open Lifecycle" onClick={() => { navigateToModule('lcc', 'lcc'); }}
          />
          {/* Module 05: Process Reliability */}
          <HubCard icon="🏭" tag="Module 05" badge={eqCount > 0 ? 'Live' : 'Ready'} name="Process Reliability"
            desc="Series/parallel system reliability, process flow modelling and bottleneck identification."
            kpis={[
              {label:'Process blocks', value:String(eqCount)},
              {label:'Configuration', value:eqCount > 0 ? 'Series/Par.' : '—'},
              {label:'Target', value:hasSim ? `${simResults.systemAvailability}%` : '—'}
            ]}
            cta="Open Process" onClick={() => { navigateToModule('process', 'process'); }}
          />
          {/* Module 06: Enterprise */}
          <HubCard icon="🌍" tag="Module 06" badge="Ready" name="Enterprise Manager"
            desc="Multi-site management, fleet-wide failure data aggregation and cross-site benchmarking."
            kpis={[
              {label:'Sites', value:String(siteCount)},
              {label:'Library items', value:String(libraryCount)},
              {label:'Standard', value:hierarchyLabel}
            ]}
            cta="Open Enterprise" onClick={() => { navigateToModule('enterprise', 'enterprise'); }}
          />
          {/* Module 07: Spares Analysis */}
          <HubCard icon="📦" tag="Module 07" badge={spCount > 0 ? 'Live' : 'Ready'} name="Spares Analysis"
            desc="Criticality ranking, demand forecasting, safety stock calculation and recommended stocking levels."
            kpis={[
              {label:'Spare items', value:String(spCount)},
              {label:'Method', value:'Poisson/Weibull'},
              {label:'Link', value:'Optimal® Spares'}
            ]}
            cta="Open Spares" onClick={() => { navigateToModule('optimalav', 'spares'); }}
          />
          {/* Module 08: RAM Analysis */}
          <HubCard icon="🏗" tag="Module 08" badge={hasSim ? 'Analysed' : 'Ready'} name="RAM Analysis"
            desc="Reliability, Availability & Maintainability study – system modelling (RBD/fault tree), Monte Carlo simulation, bottleneck identification and design optimisation."
            kpis={[
              {label:'Equipment', value:String(eqCount)},
              {label:'Method', value:'Monte Carlo'},
              {label:'Availability', value:hasSim ? `${simResults.systemAvailability}%` : '—'}
            ]}
            cta="Open RAM" onClick={() => { navigateToModule('optimalav', 'modelling'); setModellingSubTab('rbd'); }}
          />
          {/* Module 09: Equipment Resources */}
          <HubCard icon="🔌" tag="Module 09" badge={(labourCount + equipResCount) > 0 ? 'Live' : 'Ready'} name="Equipment Resources"
            desc="Labour crew scheduling, spare parts logistics (3-level), equipment standby modes, and maintenance condition policies."
            kpis={[
              {label:'Labour', value:String(labourCount)},
              {label:'Spare items', value:String(spCount)},
              {label:'Resources', value:String(equipResCount)}
            ]}
            cta="Open Resources" onClick={() => { navigateToModule('optimalav', 'grid'); }}
          />
          {/* Module 10: Reliability Engineering */}
          <HubCard icon="⚡" tag="Module 10" badge="Ready" name="Reliability Engineering"
            desc="Reliability prediction (MIL-217F), FMECA worksheets, reliability growth (Duane), safety assessment (FHA), and allocation analysis."
            kpis={[
              {label:'Analyses', value:'5'},
              {label:'Standards', value:'6'},
              {label:'Charts', value:'12'}
            ]}
            cta="Open Module" onClick={() => navigateToModule('reliability')}
          />
          {/* Integrations: Connectors */}
          <HubCard icon="🔗" tag="Integrations" name="SAP & Maximo Connectors"
            desc="Import equipment master data, failure notifications and PM orders from SAP PM or IBM Maximo."
            kpis={[
              {label:'SAP', value:'Available'},
              {label:'Maximo', value:'Available'},
              {label:'SFG20', value:'Available'}
            ]}
            cta="Open Connectors" onClick={() => { navigateToModule('optimalav', 'connectors'); setConnectorsSubTab('sap'); }}
          />
          {/* Callout */}
          <div style={{ gridColumn:'span 12', marginTop:8, background:'#002F65', color:'#fff', padding:'24px 28px', borderRadius:'4px', display:'flex', alignItems:'center', gap:24 }}>
            <div>
              <div style={{ fontFamily:"'Georgia',serif", fontSize:16, fontWeight:400, letterSpacing:'0.2px', marginBottom:4 }}>Your reliability and availability data – connected.</div>
              <div style={{ fontSize:12, opacity:0.75, lineHeight:1.5, maxWidth:720 }}>Optimal® Availability Studio brings RCM, Weibull analysis, life cycle costing, process reliability and reliability engineering into a single platform. Every analysis links back to the asset hierarchy.</div>
            </div>
            <div style={{ marginLeft:'auto', display:'flex', gap:32 }}>
              <div style={{ textAlign:'right' }}><div style={{ fontSize:9, fontWeight:600, letterSpacing:'0.14em', textTransform:'uppercase', opacity:0.55 }}>Entities loaded</div><div style={{ fontFamily:"'Georgia',serif", fontSize:20, fontWeight:400, marginTop:2 }}>{eqCount + fmCount + spCount + taskCount + rcmCount}</div></div>
              <div style={{ textAlign:'right' }}><div style={{ fontSize:9, fontWeight:600, letterSpacing:'0.14em', textTransform:'uppercase', opacity:0.55 }}>Data status</div><div style={{ fontFamily:"'Georgia',serif", fontSize:20, fontWeight:400, marginTop:2 }}>{dataStatus}</div></div>
              <div style={{ textAlign:'right' }}><div style={{ fontSize:9, fontWeight:600, letterSpacing:'0.14em', textTransform:'uppercase', opacity:0.55 }}>Standard</div><div style={{ fontFamily:"'Georgia',serif", fontSize:20, fontWeight:400, marginTop:2 }}>{hierarchyLabel}</div></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============ OUTCOMES HUB VIEW ============
  const renderOutcomesHub = () => {
    const outcomes = [
      { icon:'🔧', color:'rgba(0,47,101,0.06)', accent:'#002F65', name:'RCM Study Wizard',
        duration:'8–12 weeks', steps:'10 steps', audience:'Reliability engineers',
        desc:'Guided Reliability-Centred Maintenance study – FMEA build, RCM decision logic, task packaging, PM schedule generation. The flagship Availability engagement.',
        target: () => { setCurrentModule('rcm'); setCurrentTab('rcm2'); }},
      { icon:'📦', color:'rgba(0,168,203,0.08)', accent:'#00A8CB', name:'Spares Optimisation Study',
        duration:'4–6 weeks', steps:'7 steps', audience:'Spares & materials managers',
        desc:'Guided spare parts analysis – criticality ranking, demand forecasting (Weibull/Poisson/Z-score ROP), safety stock calculation, 4-tier stocking policy, recommended stocking levels. Bridges directly to Optimal® Spares at spare.optimalworld.org.',
        target: () => { setCurrentModule('optimalav'); setCurrentTab('spareswizard'); }},
      { icon:'💰', color:'rgba(0,47,101,0.06)', accent:'#002F65', name:'Lifecycle Assessment',
        duration:'Per asset', steps:'6 steps', audience:'Asset managers & CAPEX teams',
        desc:'RUL estimation, whole-life costing (NPV/EAC), Monte Carlo simulation, and optimal lifecycle strategy. ISO 55001 / ISO 15686-5 aligned.',
        target: () => { setCurrentModule('lcc'); setCurrentTab('lcc'); }},
      { icon:'⏱', color:'rgba(42,157,143,0.08)', accent:'#2A9D8F', name:'PM Interval Optimisation',
        duration:'Per asset class', steps:'5 steps', audience:'Maintenance planners',
        desc:'Guided preventive maintenance interval review – age-reliability analysis, Weibull fitting, cost-optimised interval calculation, task rationalisation.',
        target: () => { setCurrentModule('optimalav'); setCurrentTab('optimization'); }},
      { icon:'🎯', color:'rgba(185,28,28,0.06)', accent:'#B91C1C', name:'Defect Elimination',
        duration:'Continuous', steps:'6 pillars', audience:'Operations & reliability',
        desc:'Systematic identification and elimination of chronic equipment failures – root cause analysis, bad actor tracking, design-out recommendations and verification of effectiveness.',
        target: () => { setCurrentModule('optimalav'); setCurrentTab('modelling'); setModellingSubTab('ft'); }},
      { icon:'📈', color:'rgba(0,168,203,0.08)', accent:'#00A8CB', name:'Reliability Improvement',
        duration:'8–16 weeks', steps:'8 steps', audience:'Reliability & maintenance teams',
        desc:'Targeted improvement programme – Weibull trend analysis, failure pattern recognition, maintenance strategy review, implementation tracking and sustained performance monitoring.',
        target: () => { setCurrentModule('weibull'); setCurrentTab('weibull'); }},
      { icon:'🏭', color:'rgba(0,47,101,0.06)', accent:'#002F65', name:'RAM Analysis',
        duration:'6–10 weeks', steps:'9 steps', audience:'Project & operations engineers',
        desc:'Reliability, Availability & Maintainability analysis – system modelling (RBD/fault tree), Monte Carlo simulation, bottleneck identification, sensitivity analysis and design optimisation.',
        target: () => { setCurrentModule('optimalav'); setCurrentTab('modelling'); setModellingSubTab('rbd'); }}
    ];

    return (
      <div style={{ flex:1, overflow:'auto', padding:'32px 40px', background:COLORS.LIGHT_BG }}>
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:10, fontWeight:600, color:'rgba(0,47,101,0.4)', letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:6 }}>Optimal® Availability Studio › Outcomes Hub</div>
          <div style={{ fontFamily:"'Georgia',serif", fontSize:22, fontWeight:400, color:COLORS.TEXT, letterSpacing:'0.2px' }}>Guided Engagement Workflows</div>
          <div style={{ fontSize:13, color:COLORS.TEXT_DIM, marginTop:8, maxWidth:640, lineHeight:1.55 }}>
            Each outcome maps to a structured consulting engagement. Select a workflow to scope, execute and deliver results from Optimal® Availability Studio.
          </div>
        </div>
        <button onClick={() => setCurrentModule('hub')} style={{
          padding:'7px 14px', fontSize:11, fontWeight:600, color:'#00A8CB',
          background:'rgba(0,168,203,0.06)', border:'1px solid rgba(0,168,203,0.2)',
          borderRadius:'3px', cursor:'pointer', marginBottom:20, fontFamily:'inherit'
        }}>← Back to Module Hub</button>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          {outcomes.map((o,i) => (
            <div key={i} onClick={o.target} style={{
              background:'#FFFFFF', border:'1px solid rgba(0,47,101,0.08)', borderRadius:'4px',
              padding:'28px', cursor:'pointer', transition:'all 0.2s',
              display:'flex', flexDirection:'column', gap:14
            }}>
              <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
                <div style={{ width:48, height:48, borderRadius:'3px', fontSize:22, display:'flex',
                  alignItems:'center', justifyContent:'center', background:o.color, color:o.accent, flexShrink:0 }}>{o.icon}</div>
                <div>
                  <div style={{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.1em', color:COLORS.TEXT_DIM }}>{o.duration} · {o.steps}</div>
                  <div style={{ fontFamily:"'Georgia',serif", fontSize:17, fontWeight:400, color:COLORS.TEXT, letterSpacing:'0.2px' }}>{o.name}</div>
                </div>
              </div>
              <div style={{ fontSize:'12.5px', color:COLORS.TEXT_DIM, lineHeight:1.55 }}>{o.desc}</div>
              <div style={{ display:'flex', gap:16, marginTop:'auto', paddingTop:14, borderTop:'1px solid rgba(0,47,101,0.08)' }}>
                <span style={{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(0,47,101,0.4)' }}>🎯 {o.audience}</span>
                <span style={{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(0,47,101,0.4)' }}>→ Open workflow</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background:COLORS.LIGHT_BG, padding:'16px 20px', borderRadius:'3px', marginTop:24,
          borderLeft:'2px solid rgba(0,47,101,0.15)', color:COLORS.TEXT_DIM, fontSize:'12.5px', lineHeight:1.6 }}>
          <strong>Shared architecture:</strong> Monte Carlo Engine · Weibull Analysis · RCM Decision Logic · ISO 14224 Taxonomy · SFG20 Scheduler · SAP/Maximo Connectors
        </div>
      </div>
    );
  };

  if (!authToken) {
    return <LoginPage onLogin={handleLogin} error={loginError} />;
  }

  if (showWelcome) {
    return (
      <WelcomeWizard
        onStartBlank={() => {
          setProjectData(createBlankProject());
          setShowWelcome(false);
        }}
        onLoadDemo={() => {
          setProjectData(createDemoData());
          setShowWelcome(false);
        }}
        onImportFile={(file) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const content = e.target.result;
              if (file.name.endsWith('.json') || file.name.endsWith('.oawx')) {
                const parsed = JSON.parse(content);
                setProjectData(parsed);
              } else if (file.name.endsWith('.csv')) {
                alert('CSV import loaded. Please use the ERP Import module for detailed mapping.');
                setProjectData(createBlankProject());
              } else {
                alert('File format recognized. Please use the Import module for detailed mapping.');
                setProjectData(createBlankProject());
              }
              setShowWelcome(false);
            } catch (parseErr) {
              alert('Failed to parse file: ' + parseErr.message);
            }
          };
          reader.readAsText(file);
        }}
        onImportHub={() => {
          setProjectData(createBlankProject());
          setShowWelcome(false);
          setCurrentModule('optimalav');
          setCurrentTab('import');
        }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: COLORS.LIGHT_BG }}>
      {/* Top Navigation Bar */}
      <TopNav
        currentModule={currentModule}
        onModuleSelect={setCurrentModule}
        onShowHelp={() => setShowHelpPanel(true)}
      />
      {/* Breadcrumb sub-bar — hidden on hub */}
      {currentModule !== 'hub' && (
        <SubBar currentModule={currentModule} />
      )}

      {/* Toolbar with User Profile */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div data-tour="toolbar" style={{ flex: 1 }}>
          <Toolbar
            onNew={handleNew}
            onOpen={handleOpen}
            onSave={handleSave}
            onUndo={undo}
            onRedo={redo}
            onAddItem={() => handleAddItem(selectedSection)}
            onDeleteItem={() => selectedItem && handleDeleteItem(selectedItem.id)}
            onRun={handleRunSimulation}
            onStop={handleStopSimulation}
            onZoom={(factor) => {
              if (factor === 'fit') setZoom(1);
              else setZoom(Math.max(0.5, Math.min(3, zoom * factor)));
            }}
            canUndo={canUndo}
            canRedo={canRedo}
            isProcessing={isProcessing}
          />
        </div>
        {/* Library Template button */}
        <button
          onClick={() => {
            if (selectedSection === 'equipments' && selectedItem) {
              setLibraryTemplateEquipment(selectedItem);
            } else {
              // Open with no specific equipment — browse mode
              setLibraryTemplateEquipment({ id: '__browse__', description: 'Browse Library', type: '' });
            }
          }}
          title="Apply failure mode & task templates from the 769-type equipment library"
          style={{
            padding: '5px 12px', fontSize: '10px', fontWeight: '600',
            backgroundColor: 'transparent', color: '#002F65',
            border: '1px solid rgba(0,47,101,0.15)', borderRadius: '3px',
            cursor: 'pointer', letterSpacing: '0.02em', marginRight: '8px',
            display: 'flex', alignItems: 'center', gap: '4px'
          }}
        >
          <BookOpen size={12} /> Library
        </button>
        {currentUser && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', paddingRight: '20px', borderLeft: `1px solid ${COLORS.BORDER}`, paddingLeft: '18px', height: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '3px', backgroundColor: 'rgba(0,47,101,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: '#002F65' }}>
                {(currentUser.fullName || currentUser.username || '?').charAt(0).toUpperCase()}
              </div>
              <span style={{ fontSize: '12px', color: COLORS.TEXT, fontWeight: '500' }}>{currentUser.fullName || currentUser.username}</span>
            </div>
            <button onClick={handleLogout} style={{ padding: '5px 12px', fontSize: '10px', backgroundColor: 'transparent', color: 'rgba(0,47,101,0.4)', border: '1px solid rgba(0,47,101,0.12)', borderRadius: '3px', cursor: 'pointer', fontWeight: '500', letterSpacing: '0.3px', textTransform: 'uppercase', transition: 'all 0.15s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,47,101,0.3)'; e.currentTarget.style.color = '#002F65'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,47,101,0.12)'; e.currentTarget.style.color = 'rgba(0,47,101,0.4)'; }}
            >Sign Out</button>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Module Hub / Outcomes Hub — full-width when active */}
        {currentModule === 'hub' && renderModuleHub()}
        {currentModule === 'outcomes' && renderOutcomesHub()}
        {/* Left Panel - Tree (resizable) — hidden when hub is active */}
        {currentModule !== 'hub' && currentModule !== 'outcomes' && panelVisible.left && (
          <div data-tour="tree-panel" style={{
            width: `${leftPanelWidth}px`,
            minWidth: '200px',
            maxWidth: '500px',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: COLORS.PANEL_BG,
            borderRight: `1px solid ${COLORS.BORDER}`,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Asset Hierarchy — sidebar only shows the tree */}
            {(() => {
              const assetGroup = TREE_GROUPS.find(g => g.id === 'assetHierarchy');
              if (!assetGroup) return null;
              const groupItemCount = assetGroup.sections.reduce((sum, s) => sum + (projectData[s.id] || []).length, 0);
              return (
                <GroupedTreeSection
                  key={assetGroup.id}
                  group={assetGroup}
                  projectData={projectData}
                  selectedItem={selectedItem}
                  selectedSection={selectedSection}
                  onSelectItem={(item, sectionId) => {
                    setSelectedItem(item);
                    setSelectedSection(sectionId);
                  }}
                  onAddItem={handleAddItem}
                  onDeleteItem={handleDeleteItem}
                  onEditItem={(item, sectionId) => {
                    setSelectedItem(item);
                    setSelectedSection(sectionId);
                    setEditDialogItem(item);
                  }}
                  itemCount={groupItemCount}
                />
              );
            })()}
            {/* Sidebar footer — clean add buttons */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '8px 10px', borderTop: `1px solid ${COLORS.BORDER}`, flexShrink: 0,
              backgroundColor: COLORS.PANEL_BG
            }}>
              <button onClick={() => handleAddItem('locations')} style={{
                flex: 1, padding: '5px 0', fontSize: '10px', fontWeight: '500', color: COLORS.TEAL,
                border: `1px solid ${COLORS.TEAL}30`, borderRadius: '4px', cursor: 'pointer',
                backgroundColor: 'transparent', letterSpacing: '0.03em'
              }}>+ Location</button>
              <button onClick={() => handleAddItem('equipments')} style={{
                flex: 1, padding: '5px 0', fontSize: '10px', fontWeight: '500', color: COLORS.TEAL,
                border: `1px solid ${COLORS.TEAL}30`, borderRadius: '4px', cursor: 'pointer',
                backgroundColor: 'transparent', letterSpacing: '0.03em'
              }}>+ Equipment</button>
              <button onClick={() => setCurrentTab('import')} title="Import hierarchy" style={{
                padding: '5px 8px', fontSize: '12px', color: COLORS.TEXT_DIM,
                border: `1px solid ${COLORS.BORDER}`, borderRadius: '4px', cursor: 'pointer',
                backgroundColor: 'transparent'
              }}>&#8593;</button>
            </div>

            {/* Drag handle */}
            <div
              onMouseDown={handleLeftPanelMouseDown}
              style={{
                position: 'absolute',
                top: 0,
                right: -3,
                width: 6,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 10,
                background: 'transparent'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.TEAL + '30'; }}
              onMouseLeave={(e) => { if (!leftPanelDragging.current) e.currentTarget.style.background = 'transparent'; }}
            />
          </div>
        )}

        {/* Center Panel - Tabs (hidden when hub is active) */}
        {currentModule !== 'hub' && currentModule !== 'outcomes' && <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: COLORS.LIGHT_BG }}>
          {/* ROW 1: Domain Group Tabs — premium horizontal navigation */}
          <div style={{ display: 'flex', backgroundColor: '#FFFFFF', borderBottom: `1px solid rgba(0,47,101,0.08)`, alignItems: 'stretch', flexShrink: 0, minHeight: '38px' }}>
            <div style={{ display: 'flex', alignItems: 'stretch', flex: 1, overflow: 'auto', padding: '0 4px' }}>
              {activeDomainGroups.map(group => {
                const isAsset = group.id === 'assetHierarchy';
                const isGroupActive = group.sections.some(s => s.id === selectedSection);
                const Icon = group.icon;
                return (
                  <button key={group.id}
                    onClick={() => { const first = isAsset ? { id: 'equipments' } : group.sections[0]; if (first) { setSelectedSection(first.id); if (currentTab !== 'grid') setCurrentTab('grid'); } }}
                    style={{
                      padding: '8px 18px', fontSize: '11.5px', fontWeight: isGroupActive ? '600' : '400',
                      border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                      backgroundColor: 'transparent',
                      color: isGroupActive ? COLORS.NAVY : 'rgba(0,47,101,0.45)',
                      borderBottom: isGroupActive ? `2px solid ${COLORS.NAVY}` : '2px solid transparent',
                      whiteSpace: 'nowrap', transition: 'all 0.2s ease',
                      fontFamily: "'Georgia', serif", letterSpacing: '0.02em'
                    }}
                  >
                    <Icon size={12} style={{ opacity: isGroupActive ? 1 : 0.5 }} /> {isAsset ? 'Assets' : group.label}
                  </button>
                );
              })}
            </div>
            {/* Utility actions — refined, right-aligned */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1px', paddingRight: '10px', borderLeft: `1px solid rgba(0,47,101,0.06)`, flexShrink: 0 }}>
              {[
                { key: 'import', label: 'Import', onClick: () => setCurrentTab('import'), active: currentTab === 'import' },
                { key: 'library', label: 'Library', onClick: () => setLibraryDrawerOpen(!libraryDrawerOpen), active: libraryDrawerOpen },
                { key: 'workinstructions', label: 'W. Inst.', onClick: () => setCurrentTab('workinstructions'), active: currentTab === 'workinstructions' },
                { key: 'sfg20', label: 'SFG20', onClick: () => setCurrentTab('sfg20'), active: currentTab === 'sfg20' },
                { key: 'trend', label: 'Trend', onClick: () => setCurrentTab('trend'), active: currentTab === 'trend' },
                { key: 'audit', label: 'Audit', onClick: () => setCurrentTab('audit'), active: currentTab === 'audit' }
              ].map(btn => (
                <button key={btn.key} onClick={btn.onClick}
                  style={{
                    padding: '6px 10px', fontSize: '9.5px', fontWeight: btn.active ? 600 : 500,
                    border: 'none', cursor: 'pointer', borderRadius: '2px',
                    backgroundColor: btn.active ? 'rgba(0,47,101,0.06)' : 'transparent',
                    color: btn.active ? COLORS.NAVY : 'rgba(0,47,101,0.35)',
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    fontFamily: "'Georgia', serif", transition: 'all 0.2s ease', whiteSpace: 'nowrap'
                  }}>
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          {/* ROW 2: Sequential Workflow Steps — guided progression with premium styling */}
          {activeWorkflow && activeWorkflow.steps.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#FAFBFC', borderBottom: `1px solid rgba(0,47,101,0.06)`, padding: '0', flexShrink: 0 }}>
              {/* Module label — quiet branding */}
              <div style={{ padding: '7px 14px 7px 12px', fontSize: '8.5px', fontWeight: 600, color: 'rgba(0,47,101,0.28)', letterSpacing: '0.16em', textTransform: 'uppercase', whiteSpace: 'nowrap', borderRight: `1px solid rgba(0,47,101,0.06)`, fontFamily: "'Georgia', serif" }}>
                {activeWorkflow.label}
              </div>
              {activeWorkflow.steps.map((step, idx) => {
                if (!isStepVisibleForAsset(step)) return null;
                const isCurrent = currentTab === step.id;
                const unlocked = isStepUnlocked(currentModule, idx);
                const complete = isStepComplete(step.id);
                const locked = !unlocked;
                const stepNumber = idx + 1;
                return (
                  <React.Fragment key={step.id}>
                    {idx > 0 && (
                      <div style={{ width: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <div style={{ width: '12px', height: '1px', backgroundColor: complete ? COLORS.TEAL : locked ? 'rgba(0,47,101,0.08)' : 'rgba(0,47,101,0.12)' }} />
                      </div>
                    )}
                    <button
                      onClick={() => { if (!locked) { setCurrentTab(step.id); setWorkflowStepIndex(idx); } }}
                      title={locked ? `Complete Step ${step.requires.map(r => r + 1).join(', ')} first` : step.label}
                      style={{
                        padding: '7px 14px', fontSize: '11px',
                        fontWeight: isCurrent ? '600' : '400',
                        border: 'none', cursor: locked ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: '7px',
                        backgroundColor: 'transparent',
                        color: locked ? 'rgba(0,47,101,0.35)' : isCurrent ? COLORS.NAVY : complete ? 'rgba(0,47,101,0.7)' : 'rgba(0,47,101,0.5)',
                        borderBottom: isCurrent ? `2px solid ${COLORS.TEAL}` : '2px solid transparent',
                        whiteSpace: 'nowrap', transition: 'all 0.2s ease',
                        opacity: locked ? 0.75 : 1,
                        fontFamily: "'Georgia', serif", letterSpacing: '0.01em'
                      }}
                    >
                      <span style={{
                        width: '18px', height: '18px', borderRadius: '50%', fontSize: '9px', fontWeight: '600',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        backgroundColor: isCurrent ? COLORS.NAVY : complete ? COLORS.TEAL : locked ? 'rgba(0,47,101,0.05)' : 'rgba(0,47,101,0.06)',
                        color: (isCurrent || complete) ? '#fff' : locked ? 'rgba(0,47,101,0.35)' : 'rgba(0,47,101,0.4)',
                        letterSpacing: '0', fontFamily: 'system-ui', transition: 'all 0.2s ease'
                      }}>
                        {complete && !isCurrent ? '✓' : stepNumber}
                      </span>
                      {step.label}
                    </button>
                  </React.Fragment>
                );
              })}
              {/* Roll-up indicator — refined */}
              {selectedItem && selectedSection === 'locations' && (
                <div style={{ marginLeft: 'auto', padding: '4px 12px', fontSize: '8.5px', fontWeight: 600,
                  color: 'rgba(0,47,101,0.45)', backgroundColor: 'rgba(0,47,101,0.03)', borderRadius: '2px',
                  border: '1px solid rgba(0,47,101,0.06)',
                  letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', marginRight: '10px',
                  fontFamily: "'Georgia', serif" }}>
                  Roll-up: {(contextualAssetIds?.size || 1) - 1} child assets
                </div>
              )}
            </div>
          )}

          {/* Workflow Step Guidance Banner — shows current step, readiness, and next step button */}
          {activeWorkflow && activeWorkflow.steps.length > 1 && (() => {
            const stepIdx = activeWorkflow.steps.findIndex(s => s.id === currentTab);
            const step = activeWorkflow.steps[stepIdx] || activeWorkflow.steps[0];
            const nextStep = activeWorkflow.steps[stepIdx + 1];
            const prevStep = stepIdx > 0 ? activeWorkflow.steps[stepIdx - 1] : null;
            const complete = isStepComplete(step.id);
            const eqCount = (projectData.equipments || []).length;
            const locCount = (projectData.locations || []).length;
            const rbdCount = (projectData.rbdblocks || []).length;
            const guidance = currentTab === 'grid'
              ? (eqCount > 0 ? `${eqCount} equipment items and ${locCount} locations defined. ${complete ? 'Ready to proceed.' : 'Add equipment to continue.'}`
                : 'Define your asset hierarchy — add locations and equipment to get started.')
              : currentTab === 'modelling'
              ? (rbdCount > 0 ? `${rbdCount} RBD blocks defined. Build your system model, then run RAM Study.` : 'Build your Reliability Block Diagram to model system availability.')
              : currentTab === 'spares' ? 'Define spare parts inventory and reorder policies.'
              : currentTab === 'markov' ? 'Run Markov state-transition analysis on selected equipment.'
              : currentTab === 'results' ? 'View simulation results, reports, and analytics.'
              : `Step ${stepIdx + 1}: ${step.label}`;
            return (
              <div style={{ padding: '8px 14px', backgroundColor: complete ? '#D1FAE5' : '#EFF6FF',
                borderBottom: '1px solid rgba(0,47,101,0.06)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', color: '#1E3A5F', fontWeight: '600', marginBottom: '2px' }}>
                    Step {stepIdx + 1} of {activeWorkflow.steps.length}: {step.label}
                    {complete && <span style={{ color: COLORS.TEAL, marginLeft: '8px' }}>✓ Complete</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: '#4B5563' }}>{guidance}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '16px' }}>
                  {prevStep && (
                    <button onClick={() => { setCurrentTab(prevStep.id); }} style={{
                      padding: '5px 12px', fontSize: '11px', fontWeight: '500', border: `1px solid ${COLORS.BORDER}`,
                      borderRadius: '3px', cursor: 'pointer', backgroundColor: '#fff', color: COLORS.NAVY
                    }}>← {prevStep.label}</button>
                  )}
                  {nextStep && isStepUnlocked(currentModule, stepIdx + 1) && (
                    <button onClick={() => { setCurrentTab(nextStep.id); }} style={{
                      padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none',
                      borderRadius: '3px', cursor: 'pointer',
                      backgroundColor: complete ? COLORS.TEAL : COLORS.NAVY, color: '#FFFFFF'
                    }}>{nextStep.label} →</button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ROW 3: Sub-tabs within active domain group — shown when Data tab is active */}
          {currentTab === 'grid' && (() => {
            const activeGroup = activeDomainGroups.find(g => g.sections.some(s => s.id === selectedSection))
              || TREE_GROUPS.find(g => g.sections.some(s => s.id === selectedSection));
            if (!activeGroup || activeGroup.sections.length <= 1) return null;
            return (
              <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', borderBottom: `1px solid ${COLORS.BORDER}`, padding: '0 8px', flexShrink: 0 }}>
                {activeGroup.sections.map(section => {
                  const isActive = selectedSection === section.id;
                  const SIcon = section.icon;
                  const sectionData = projectData[section.id] || [];
                  // Filter count by contextual asset if selected
                  const count = contextualAssetIds
                    ? sectionData.filter(item => contextualAssetIds.has(item.equipmentId) || contextualAssetIds.has(item.locationId) || contextualAssetIds.has(item.parentId) || contextualAssetIds.has(item.id)).length
                    : sectionData.length;
                  return (
                    <button key={section.id} onClick={() => setSelectedSection(section.id)} style={{
                      padding: '5px 12px', fontSize: '11px', fontWeight: isActive ? '600' : '400',
                      border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                      backgroundColor: 'transparent',
                      color: isActive ? activeGroup.color : COLORS.TEXT,
                      borderBottom: isActive ? `2px solid ${activeGroup.color}` : '2px solid transparent',
                      whiteSpace: 'nowrap', fontFamily: "'Georgia', serif"
                    }}>
                      <SIcon size={11} /> {section.label}
                      <span style={{ fontSize: '9px', color: COLORS.TEXT_DIM, fontWeight: '400' }}>({count})</span>
                    </button>
                  );
                })}
                <button onClick={() => handleAddItem(selectedSection)} style={{
                  marginLeft: 'auto', padding: '3px 10px', fontSize: '10px', fontWeight: '600',
                  border: `1px solid ${activeGroup.color}40`, borderRadius: '3px', cursor: 'pointer',
                  backgroundColor: 'transparent', color: activeGroup.color,
                  textTransform: 'uppercase', letterSpacing: '0.04em'
                }}>+ Add</button>
              </div>
            );
          })()}

          {/* Simulation Progress */}
          {simStatus === 'running' && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: COLORS.PANEL_BG,
              borderBottom: `1px solid ${COLORS.BORDER}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '12px'
            }}>
              <div style={{ flex: 1 }}>Running simulation: {simProgress}%</div>
              <div style={{
                width: '200px',
                height: '4px',
                backgroundColor: COLORS.BORDER,
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div
                  style={{
                    height: '100%',
                    width: `${simProgress}%`,
                    backgroundColor: COLORS.GOLD,
                    transition: 'width 0.2s'
                  }}
                />
              </div>
            </div>
          )}

          {/* Scope Breadcrumb */}
          {selectedLocation && (
            <div style={{ padding: '6px 12px', backgroundColor: '#E3F2FD', borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
              <span style={{ color: COLORS.TEXT_DIM }}>Scope:</span>
              <span style={{ color: COLORS.NAVY, fontWeight: '600' }}>{selectedLocation.id}</span>
              <span style={{ color: COLORS.TEXT }}>{selectedLocation.description}</span>
              {HIERARCHY_STANDARDS[hierarchyStandard] && (
                <span style={{ padding: '1px 6px', borderRadius: '3px', backgroundColor: HIERARCHY_STANDARDS[hierarchyStandard].levels[selectedLocation.hierarchyLevel || 0]?.color || COLORS.NAVY, color: '#FFFFFF', fontSize: '9px', fontWeight: '600' }}>
                  {HIERARCHY_STANDARDS[hierarchyStandard].levels[selectedLocation.hierarchyLevel || 0]?.label || 'Level ' + (selectedLocation.hierarchyLevel || 0)}
                </span>
              )}
              <button onClick={() => setSelectedLocation(null)} style={{ marginLeft: 'auto', padding: '2px 8px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '3px', background: '#FFFFFF', color: COLORS.TEXT_DIM, cursor: 'pointer', fontSize: '10px' }}>Clear Scope</button>
            </div>
          )}

          

          {/* Tab Content */}
          <div data-tour="data-grid" style={{ flex: 1, overflow: 'hidden' }}>
            {currentTabContent()}
          </div>
        </div>}

        {/* Right Panel - Properties (resizable) — hidden when hub is active */}
        {currentModule !== 'hub' && currentModule !== 'outcomes' && panelVisible.right && (
          <div style={{
            width: `${rightPanelWidth}px`,
            minWidth: '200px',
            maxWidth: '700px',
            backgroundColor: COLORS.PANEL_BG,
            borderLeft: `1px solid ${COLORS.BORDER}`,
            overflow: 'auto',
            padding: '12px',
            fontSize: '12px',
            position: 'relative',
            flexShrink: 0
          }}>
            {/* Drag handle */}
            <div
              onMouseDown={handleRightPanelMouseDown}
              style={{
                position: 'absolute',
                top: 0,
                left: -3,
                width: 6,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 10,
                background: 'transparent'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.TEAL + '30'; }}
              onMouseLeave={(e) => { if (!rightPanelDragging.current) e.currentTarget.style.background = 'transparent'; }}
            />
            {selectedItem ? (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>
                {/* Property Inspector */}
                <PropertyInspector
                  selectedItem={selectedItem}
                  entityType={selectedSection}
                  onUpdate={(updates) => handleUpdateItem(selectedItem.id, updates)}
                  allData={projectData}
                />
                {/* Library Suggestions Panel */}
                {(() => {
                  const equipType = selectedItem?.type || selectedItem?.description || '';
                  const isEquipmentRelated = selectedSection === 'equipments' || selectedItem?.equipmentId;
                  if (!isEquipmentRelated || !equipType) return null;
                  const relatedSections = ['failuremodes', 'scheduledtasks', 'correctivetasks'];
                  const sectionSuggestions = relatedSections.map(sec => {
                    const existing = projectData[sec] || [];
                    const sugs = getSuggestions(equipType, sec, selectedItem?.id || selectedItem?.equipmentId || '', existing);
                    return { section: sec, suggestions: sugs };
                  }).filter(s => s.suggestions.length > 0);
                  if (sectionSuggestions.length === 0) return null;
                  return (
                    <div style={{ borderTop: `2px solid ${COLORS.GOLD}`, padding: '12px', backgroundColor: 'rgba(0,47,101,0.015)', marginTop: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: COLORS.NAVY, fontFamily: "'Georgia', serif", marginBottom: '10px', letterSpacing: '0.02em' }}>
                        Library Suggestions
                      </div>
                      <div style={{ fontSize: '10px', color: COLORS.TEXT_DIM, marginBottom: '10px', lineHeight: '1.4' }}>
                        Based on <span style={{ fontWeight: 600, color: COLORS.NAVY }}>{equipType}</span> — ISO 14224 / Equipment Library
                      </div>
                      {sectionSuggestions.map(({ section, suggestions }) => (
                        <div key={section} style={{ marginBottom: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: COLORS.TEAL }}>
                              {section === 'failuremodes' ? 'Failure Modes' : section === 'scheduledtasks' ? 'PM Tasks' : 'CM Tasks'} ({suggestions.length})
                            </span>
                            <button onClick={() => {
                              const newItems = suggestions.map(s => {
                                const item = { ...s };
                                delete item._isSuggestion;
                                delete item._suggestionRank;
                                item.source = item.source || 'Auto-suggested';
                                return item;
                              });
                              setProjectData(prev => ({ ...prev, [section]: [...(prev[section] || []), ...newItems] }));
                            }} style={{
                              padding: '2px 8px', fontSize: '8px', fontWeight: '700', border: 'none', cursor: 'pointer',
                              borderRadius: '2px', backgroundColor: COLORS.NAVY, color: '#fff',
                              letterSpacing: '0.08em', textTransform: 'uppercase'
                            }}>Accept All</button>
                          </div>
                          {suggestions.slice(0, 5).map((sug, idx) => (
                            <div key={sug.id} style={{
                              display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px', marginBottom: '3px',
                              backgroundColor: '#fff', border: '1px solid rgba(0,47,101,0.06)', borderRadius: '2px',
                              borderLeft: `2px solid ${COLORS.TEAL}40`
                            }}>
                              <span style={{ fontSize: '10px', color: COLORS.TEXT, flex: 1, lineHeight: '1.3' }}>{sug.description?.substring(0, 50)}{sug.description?.length > 50 ? '...' : ''}</span>
                              {sug.severity && <span style={{ fontSize: '8px', fontWeight: 600, color: sug.severity >= 8 ? '#B91C1C' : sug.severity >= 5 ? '#92400E' : COLORS.TEXT_DIM, padding: '1px 4px', borderRadius: '2px', backgroundColor: sug.severity >= 8 ? 'rgba(185,28,28,0.06)' : 'rgba(0,47,101,0.03)' }}>S{sug.severity}</span>}
                              <button onClick={() => {
                                const item = { ...sug };
                                delete item._isSuggestion;
                                delete item._suggestionRank;
                                item.source = item.source || 'Auto-suggested';
                                setProjectData(prev => ({ ...prev, [section]: [...(prev[section] || []), item] }));
                              }} style={{
                                padding: '1px 6px', fontSize: '8px', fontWeight: '600', border: `1px solid ${COLORS.TEAL}30`,
                                borderRadius: '2px', cursor: 'pointer', backgroundColor: 'transparent', color: COLORS.TEAL,
                                letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0
                              }}>+</button>
                            </div>
                          ))}
                          {suggestions.length > 5 && (
                            <div style={{ fontSize: '9px', color: COLORS.TEXT_DIM, fontStyle: 'italic', paddingLeft: '6px' }}>
                              + {suggestions.length - 5} more available
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div style={{ color: COLORS.TEXT_DIM, fontSize: '12px', padding: '16px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontFamily: "'Georgia', serif", fontWeight: '400', color: COLORS.NAVY }}>Properties</h3>
                <p style={{ lineHeight: '1.5' }}>Select an item from the tree or data grid to view and edit its properties.</p>
                <p style={{ marginTop: '12px', fontSize: '10px', color: 'rgba(0,47,101,0.35)', lineHeight: '1.5' }}>Library suggestions and OptimalAI guidance will appear here when an equipment item is selected.</p>
                {/* Next-step guidance in properties panel */}
                {activeWorkflow && activeWorkflow.steps.length > 1 && (() => {
                  const stepIdx = activeWorkflow.steps.findIndex(s => s.id === currentTab);
                  const nextStep = activeWorkflow.steps[stepIdx + 1];
                  const eqCount = (projectData.equipments || []).length;
                  if (!nextStep || eqCount === 0) return null;
                  return (
                    <div style={{ marginTop: '20px', padding: '12px', backgroundColor: 'rgba(0,168,203,0.06)', borderRadius: '4px', border: '1px solid rgba(0,168,203,0.15)' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: COLORS.NAVY, marginBottom: '6px' }}>Next step</div>
                      <div style={{ fontSize: '11px', color: COLORS.TEXT_DIM, lineHeight: '1.5', marginBottom: '10px' }}>
                        {currentTab === 'grid' ? `You have ${eqCount} equipment items defined. Proceed to ${nextStep.label} to build your system model and run RAM simulations.`
                          : `Continue to ${nextStep.label}.`}
                      </div>
                      <button onClick={() => setCurrentTab(nextStep.id)} style={{
                        width: '100%', padding: '8px 12px', fontSize: '11px', fontWeight: '600',
                        border: 'none', borderRadius: '3px', cursor: 'pointer',
                        backgroundColor: COLORS.TEAL, color: '#FFFFFF',
                        textTransform: 'uppercase', letterSpacing: '0.04em'
                      }}>Proceed to {nextStep.label} →</button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Guided Tour & Tooltip System */}
      {/* TourManager and AIHoverSuggest removed — OptimalAI guidance is on-demand only */}

      {/* Contextual AI Help Button */}
      <ContextualHelpButton currentTab={currentTab} externalOpen={showHelpPanel} onExternalClose={() => setShowHelpPanel(false)} />

      {/* AI Assistant Chat Panel */}
      <AIChatPanel currentTab={currentTab} currentModule={currentModule} />

      {/* Library Drawer — slides in from right */}
      <LibraryDrawer open={libraryDrawerOpen} onClose={() => setLibraryDrawerOpen(false)} projectData={projectData} setProjectData={setProjectData} />

      {/* Status Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 12px',
        backgroundColor: COLORS.NAVY,
        borderTop: `2px solid ${COLORS.GOLD}`,
        fontSize: '11px',
        color: 'rgba(255,255,255,0.7)',
        height: '28px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>
            {simStatus === 'completed' ? (
              <span style={{ color: COLORS.GOLD }}>✓ Simulation complete</span>
            ) : simStatus === 'running' ? (
              <span style={{ color: COLORS.GOLD }}>⋯ Running...</span>
            ) : (
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>Ready</span>
            )}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
          <span style={{ fontStyle: 'italic', color: COLORS.GOLD, fontSize: '10px', letterSpacing: '0.3px' }}>Shift left of P. Eliminate Defects.</span>
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>{projectData.equipments?.length || 0} Equipment | {projectData.failuremodels?.length || 0} Failure Modes</span>
          <span style={{
            backgroundColor: 'rgba(0,168,203,0.15)',
            border: '1px solid rgba(0,168,203,0.3)',
            borderRadius: '2px',
            padding: '1px 6px',
            fontSize: '9px',
            fontWeight: '700',
            color: COLORS.GOLD,
            letterSpacing: '0.5px'
          }}>ARaaS®</span>
          <span style={{ color: '#FFFFFF', fontWeight: '600', fontSize: '10px' }}>Optimal<sup style={{ fontSize: '7px' }}>®</sup></span>
        </div>
      </div>

      {/* Library Template Auto-Suggest Dialog */}
      {libraryTemplateEquipment && (
        <LibraryTemplateSuggest
          equipment={libraryTemplateEquipment}
          projectData={projectData}
          setProjectData={setProjectData}
          onClose={() => setLibraryTemplateEquipment(null)}
        />
      )}

      {/* Edit Dialog Modal */}
      {editDialogItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', width: '600px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '16px 20px', backgroundColor: COLORS.NAVY, borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: '#FFFFFF', fontWeight: '700', fontSize: '14px' }}>Edit {selectedSection === 'locations' ? (HIERARCHY_STANDARDS[hierarchyStandard]?.levels?.find(l => l.level === (editDialogItem.hierarchyLevel || 0))?.label || 'Item') : selectedSection.replace(/s$/, '')} — {editDialogItem.id}</span>
              <button onClick={() => setEditDialogItem(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              {(COLUMN_DEFINITIONS[selectedSection] || []).map(col => (
                <div key={col.key} style={{ marginBottom: '14px', display: col.key === 'startupDelay' && editDialogItem?.standbyMode === 'hot' ? 'none' : 'block' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: COLORS.TEXT_DIM, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{col.label}</label>
                  {col.type === 'checkbox' ? (
                    <input type="checkbox" checked={!!editDialogItem[col.key]} onChange={e => {
                      const updated = { ...editDialogItem, [col.key]: e.target.checked };
                      setEditDialogItem(updated);
                      handleUpdateItem(editDialogItem.id, { [col.key]: e.target.checked });
                    }} />
                  ) : col.type === 'select' ? (
                    <select
                      value={editDialogItem[col.key] || ''}
                      onChange={e => {
                        const updated = { ...editDialogItem, [col.key]: e.target.value };
                        setEditDialogItem(updated);
                        handleUpdateItem(editDialogItem.id, { [col.key]: e.target.value });
                      }}
                      style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', fontSize: '13px', backgroundColor: '#FAFAFA' }}
                    >
                      <option value="">— Select —</option>
                      {col.key === 'parent' && (projectData.locations || []).filter(l => l.id !== editDialogItem.id).map(l => <option key={l.id} value={l.id}>{l.id} — {l.description}</option>)}
                      {col.key === 'location' && (projectData.locations || []).map(l => <option key={l.id} value={l.id}>{l.id} — {l.description}</option>)}
                      {col.key === 'type' && ['Series', 'Parallel', 'Standby', 'kofn', 'Bridge'].map(t => <option key={t} value={t}>{t}</option>)}
                      {col.key === 'redundancyType' && ['None', 'Active', 'Standby', 'k-of-n'].map(t => <option key={t} value={t}>{t}</option>)}
                      {col.key === 'distribution' && ['Exponential', 'Weibull', 'Normal', 'Lognormal', 'Fixed'].map(t => <option key={t} value={t}>{t}</option>)}
                      {col.key === 'shift' && ['Day', 'Night', 'Rotating', '24/7'].map(t => <option key={t} value={t}>{t}</option>)}
                      {col.key === 'poolType' && ['Dedicated', 'Shared', 'Central'].map(t => <option key={t} value={t}>{t}</option>)}
                      {col.key === 'standbyMode' && ['hot', 'warm', 'cold'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)} Standby</option>)}
                      {col.key === 'maintenanceCondition' && ['as_good_as_new', 'as_good_as_old', 'imperfect'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>)}
                      {col.key === 'phaseAssignment' && (projectData.phases || []).map(p => <option key={p.id} value={p.id}>{p.id} — {p.description}</option>)}
                    </select>
                  ) : (
                    <input
                      type={col.type === 'number' ? 'number' : 'text'}
                      value={editDialogItem[col.key] ?? ''}
                      readOnly={col.editable === false}
                      onChange={e => {
                        const val = col.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
                        const updated = { ...editDialogItem, [col.key]: val };
                        setEditDialogItem(updated);
                        handleUpdateItem(editDialogItem.id, { [col.key]: val });
                      }}
                      style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', fontSize: '13px', backgroundColor: col.editable === false ? '#F3F4F6' : '#FAFAFA', color: COLORS.TEXT }}
                    />
                  )}
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${COLORS.BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setEditDialogItem(null)} style={{ padding: '8px 20px', border: `1px solid ${COLORS.BORDER}`, borderRadius: '6px', background: '#FFFFFF', color: COLORS.TEXT, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <SimulationConfigDialog
        visible={showSimConfig}
        config={simConfig}
        onClose={() => setShowSimConfig(false)}
        onSave={(config) => {
          setSimConfig(config);
          handleRunSimulation();
        }}
      />
      <FindReplaceDialog
        visible={showFindReplace}
        onClose={() => setShowFindReplace(false)}
        data={projectData}
        currentSection={selectedSection}
        onReplace={handleUpdateItem}
      />
      <AboutDialog visible={showAbout} onClose={() => setShowAbout(false)} />
      <BatchSimDialog
        visible={showBatchDialog}
        onClose={() => setShowBatchDialog(false)}
        onRun={handleRunBatch}
        projectData={projectData}
      />
      <ReportDialog
        visible={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        projectData={projectData}
        simResults={simResults}
      />
    </div>
  );
}

// Wrap in ErrorBoundary for production safety
function OptimalAvailabilityApp() {
  return (
    <AppErrorBoundary>
      <OptimalAvailabilityStudio />
    </AppErrorBoundary>
  );
}

export default OptimalAvailabilityApp;
